/*
  LessonTemplate.js - Integrated with Modular Component System and WorksheetModule
  Preserves all original UI and functionality while adding comprehensive worksheet support
*/
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Download, Upload, Eye, EyeOff, Edit3, Save, Plus, Video, Image, Music, CreditCard, X, Settings, ChevronDown, ChevronRight, GripVertical, Trash2, Copy, ClipboardPaste, Scissors, Paintbrush, Presentation, FileText, List, AlertCircle, CheckCircle, AlertTriangle, Play, Pause, Clock, HelpCircle, ChevronUp, Maximize2, Minimize2, Calendar, FolderOpen, Search, MoreHorizontal, ArrowUp, ArrowDown, ArrowRight, Sun, Moon, Keyboard, RefreshCw, FolderPlus } from 'lucide-react';
import { LogoProvider, useLogo } from './LogoContext';
import SchoolLogoSettings from './SchoolLogoSettings';

// Import CSS styles
import './LessonTemplate.css';

// Enhanced Tiptap imports - including all new extensions

// Import extracted components and utilities
import { ContentBlock } from './ContentBlocks';
import { normalizeAudioPlayerStyle } from './ContentBlocks/AudioPlayer';
import AddBlockMenu from './ContentBlocks/Section';
import { RichTextEditor, HeadlineEditor } from './components/LazyEditors';
import TemplateManager from './components/TemplateManager';
import {
  createAccordionModal,
  addAccordionItem,
  removeAccordionItem,
  updateAccordionItem,
  reorderAccordionItems,
  generateAccordionHTML,
  saveAccordionBlock
} from './utils/accordion';

// Phase 1 Utility Imports - Content Utils
import {
  generateId,
  extractVideoId,
  generateAudioCitation,
  htmlToText,
  formatTime,
  formatFileSize,
  detectPlatform,
  validateContent
} from './utils/contentUtils.js';

// Import worksheet builder and processor.
import {
  WorksheetComponent,
  WorksheetBuilder,
  processWorksheetData,
  WORKSHEET_QUESTION_TYPES,
  WORKSHEET_LAYOUTS,
  normalizeQuestion
} from './WorksheetModule';

// Phase 1 Utility Imports - Constants
import {
  CONTENT_TYPES,
  BOPPPS_SECTIONS,
  VIDEO_PLATFORMS,
  CARD_LAYOUTS,
  CARD_STYLES,
  IMAGE_SIZES,
  GALLERY_COLUMNS,
  AUTO_SAVE_CONFIG,
  PRESET_LAYOUTS
} from './utils/constants.js';

// The export writers load on demand — see utils/lazyExporters.js. They are only
// reachable from the Export menu, and statically imported they put the HTML,
// PowerPoint and PDF writers in front of everyone who merely opens a lesson.
import {
  loadHtmlExporter,
  loadLlmTemplate,
  loadPdfExporter,
  loadPptxExporter
} from './utils/lazyExporters.js';
import SlideAspectGuides from './components/SlideAspectGuides.js';
import SlideDesigner from './components/SlideDesigner.js';
import {
  createTimelineBlockData,
  createTimelineStep,
  sanitizeTimelineStep,
  buildTimelineSamplePayload,
  downloadTimelineJson,
  TIMELINE_EXPORT_TYPE
} from './ContentBlocks/renderers/TimelineRenderer';
import ColorSwatchPicker, { DEFAULT_TEXT_SWATCHES } from './components/ColorSwatchPicker.js';
import CodeEditorField from './components/CodeEditorField.js';
import BackgroundImagePicker from './components/BackgroundImagePicker.js';
import ImageLinkInput from './components/ImageLinkInput.js';
import AudioLinkInput from './components/AudioLinkInput.js';
import { isAudioDataUrl } from './utils/audioLink.js';
import TransferContentModal from './components/TransferContentModal';
import { buildSlideSelectionName, buildBlockSelectionName } from './utils/selectionExportNames';
import { parseImageEmbed, embedCitationDefaults, embedFrameStyle, imageBlockHasContent, directImageFileName } from './utils/imageEmbed.js';
import {
  DEFAULT_PLAYLIST_ACCENT,
  PLAYLIST_LAYOUTS,
  formatDuration,
  normalizePlaylistItem,
  normalizePlaylistItems,
  parseVideoIdsFromText,
  parseYouTubePlaylist,
  playlistPageUrl,
  playlistWatchUrl,
  videoThumbnailUrl
} from './utils/youtubePlaylist.js';

// Phase 1 Utility Imports - Validation Utils
import {
  validateImageFile,
  validateAudioFile,
  validateVideoUrl,
  validateFormData
} from './utils/validationUtils.js';
import { ensureSlideBreaksInSections, normalizeLectureJson, normalizeSectionsDividerContent, safeString, sanitizeForExport, prepareJsonForDownload } from './utils/lectureNormalization';
import { sanitizeDeckHtml } from './utils/sanitizeHtml';
import { withSectionMembership } from './utils/drawerSectionExpansion';
import { resolveSlideJump, sanitizeSlideJumpInput } from './utils/slideJump';
import { planSlideMove, shiftSelection } from './utils/slideMoves';
import { planSectionSplit, trailingSlidesAfter } from './utils/sectionSplit';
import { findBlockShortcut } from './utils/blockShortcuts';
import { isDeckScopedCustomHtml, prepareCustomHtmlBlock } from './utils/customCss';
import { CUSTOM_HTML_BRIEF_FILENAME, buildCustomHtmlBrief } from './utils/customHtmlBrief';
import { CUSTOM_HTML_EXAMPLES_FILENAME, buildCustomHtmlExamples } from './utils/customHtmlExamples';
import {
  buildCustomHtmlLayoutFile,
  customHtmlLayoutFilename,
  hasCustomHtmlContent
} from './utils/customHtmlLayoutFile';
import { normalizeCitationOverlayPos } from './utils/customHtmlCitationOverlay';
import {
  getSlideHeaderParts,
  getStudentFriendlyTitle,
  resolveSlideName
} from './utils/slideHeader';
import { firstSlideIdOf, hasLostFirstSlide, segmentBlocksIntoSlides } from './utils/slideSegmentation';
import { SLIDE_VARIANTS, createSlideStarterBlocks } from './utils/slideStarters';
import { isDialogOpen, isTypingTarget } from './utils/keyboardTargets';
import { createSlideTemplate, downloadTemplate } from './utils/slideTemplate';
import { migrateDeckTo400 } from './utils/boppps/migrateDeckTo400';
import { repairDeck } from './utils/boppps/repairDeck';
import { validateDeck } from './utils/boppps/validateDeck';
import {
  buildStableLessonJson,
  migrateStableLessonJson,
  stableLessonFilename,
  stableLessonToInternal,
  validateStableLessonJson,
  STABLE_FORMAT,
  STABLE_SCHEMA_VERSION,
  APP_VERSION
} from './utils/stableLessonFormat';
import { applyScopedSlideBackground } from './utils/slideBackgroundScope';
import { prepareDeckForSave, prepareLoadedDeck } from './utils/deckImages';
import { auditExportedHtml, formatAssetAudit, shouldInlineForExport } from './utils/assetSources';
import {
  BACKGROUND_PRESETS,
  getBackgroundStyles,
  getImageBackgroundPresetEntries,
  normalizeBackgroundMapForPersistence,
  normalizeBackgroundPersistenceValue,
  resolveBackgroundPreset
} from './utils/presetTemplates';
import { resolveTitleCardBackgroundStyles, resolveTitleCardImageSource } from './utils/titleCardBackground';
import {
  registerLiveSlideDeckExportPayloadResponder,
  resolveExportPayloadAtClick
} from './utils/exportPayloadSync';
import { frameForBlock } from './utils/slideFrames';
import { pastedImageUpdates, applyPastedImageUpdates } from './utils/pastedImageCitations';
import { loadRulerPref, saveRulerPref } from './utils/slideGuides.js';
import { HIDDEN_FILE_INPUT_CLASS, JSON_ACCEPT, openFileInput, pickFile } from './utils/filePicker';
import {
  IMPORT_ACCEPT,
  importWouldReplace,
  readImportedFiles
} from './utils/customHtmlImport';
import ConfirmReplaceDialog from './components/ConfirmReplaceDialog';
import { applySlideFormat, captureSlideFormat, cloneSlideWithFreshIds, deepClone } from './utils/slideClipboard';
import ToolbarDrawer from './components/ToolbarDrawer';
import TemplateControlsPage from './components/TemplateControlsPage';
import InteractiveContentTools from './components/InteractiveContentTools';
import SlideThumbnailPreview from './components/SlideThumbnailPreview';
import SlideLiveThumbnail from './components/SlideLiveThumbnail';
import SlideFlowStage from './components/SlideFlowStage';
import SectionReorderModal from './components/SectionReorderModal';
import SlideReorderModal from './components/SlideReorderModal';
import EmptiedSectionPrompt from './components/EmptiedSectionPrompt';
import CreateSectionModal from './components/CreateSectionModal';
import { isLiveThumbnailMode } from './utils/thumbnailMode';
import HotkeysModal from './components/HotkeysModal';
import {
  openPresentationFile,
  parsePresentationText,
  describeLoadError,
  incompleteDeckError,
  OpenPresentationErrorType
} from './features/openPresentation';
import PresentationLoadErrorScreen from './components/PresentationLoadErrorScreen';
import SaveProblemDialog from './components/SaveProblemDialog';
import OverwriteVersionDialog from './components/OverwriteVersionDialog';
import SaveConfirmation from './components/SaveConfirmation';
import useSaveLocation, { SaveMethod } from './hooks/useSaveLocation';
import { describeSaveProblem } from './utils/saveLocation';
import {
  openedFileNameFor,
  parseVersionedFileName,
  rememberOpenedFileName,
  resolveOpenedDeckVersion,
  versionForSave
} from './utils/deckVersioning';
import {
  buildImageSourceMeta,
  buildMediaNote,
  buildReferenceContent,
  buildVideoSourceMeta,
  collectSourceMetasFromSections,
  dedupeSourcesByReference,
  formatInText,
  formatInTextNarrative,
  formatMultiInText,
  formatReference,
  formatTableLabel,
  inferReferenceCategory,
  mapCitationToSourceMeta
} from './lib/apaSlideFormatter';
import { IMAGE_REFERENCE_CATEGORIES, MEDIUM_OPTIONS } from './lib/apaImageReference';
import { LICENSE_PRESETS, licenseWarning } from './lib/apaLicense';
import ApaSegments from './components/ApaSegments';
import {
  getCitationAuthorParts,
  getApaCitationKey,
  hasApaCitationContent
} from './utils/apaCitationUtils';
import {
  APA_CITATION_LIBRARY_KEYS,
  readSavedApaCitations,
  subscribeToApaLibraryUpdates,
  writeSavedApaCitations
} from './utils/apaLibraryUtils';
import {
  collectReferenceListMetas,
  ensureReferenceListBlockExists,
  hasReferenceListBlock,
  syncReferenceListPages
} from './utils/apaReferenceListUtils';
import {
  buildReferenceReview,
  describeOrigin,
  diffReferenceReview,
  setSourceInclusion
} from './utils/referenceListReview';
import {
  buildImageCitationEnvelope,
  buildImageCitationExample,
  buildImageCitationPrompt,
  citationEntryToFormFields,
  formFieldsToCitationEntry,
  parseImageCitationJson
} from './utils/imageCitationJson';

// The two shapes the audio block can take on a slide. Kept here beside the
// modal that offers them; what each one draws lives in AudioPlayer.
const AUDIO_PLAYER_CHOICES = [
  {
    value: 'button',
    label: 'Play button',
    description: 'A small pill the presenter clicks once. Best for a short clip beside other content.'
  },
  {
    value: 'waveform',
    label: 'Scrubbable timeline',
    description: 'A full-width waveform you can click or drag to move around inside the clip.'
  }
];

const IMAGE_BACKGROUND_PRESET_OPTIONS = getImageBackgroundPresetEntries();

const downloadTextFile = (filename, text, type = 'text/plain;charset=utf-8') => {
  const blob = new Blob([text], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const downloadJsonFile = (filename, data) =>
  downloadTextFile(filename, JSON.stringify(data, null, 2), 'application/json');

const downloadCustomHtmlBrief = (options) =>
  downloadTextFile(
    CUSTOM_HTML_BRIEF_FILENAME,
    buildCustomHtmlBrief(options),
    'text/markdown;charset=utf-8'
  );

const downloadCustomHtmlExamples = (options) =>
  downloadTextFile(
    CUSTOM_HTML_EXAMPLES_FILENAME,
    buildCustomHtmlExamples(options),
    'text/markdown;charset=utf-8'
  );

const downloadCustomHtmlLayout = (options) =>
  downloadTextFile(
    customHtmlLayoutFilename(options),
    buildCustomHtmlLayoutFile(options),
    'text/html;charset=utf-8'
  );

const MULTI_COLUMN_CARD_CLIPBOARD_KEY = 'boppps_multi_column_card_clipboard';
const MULTI_COLUMN_CARD_GLOBAL_KEY = '__bopppsMultiColumnCardClipboard';
let multiColumnClipboardCache = null;
const cloneMultiColumnCardData = (card) => JSON.parse(JSON.stringify(card || {}));

const readMultiColumnCardClipboard = () => {
  if (typeof window === 'undefined') return null;
  try {
    if (multiColumnClipboardCache) {
      return cloneMultiColumnCardData(multiColumnClipboardCache);
    }
    if (window[MULTI_COLUMN_CARD_GLOBAL_KEY]) {
      return cloneMultiColumnCardData(window[MULTI_COLUMN_CARD_GLOBAL_KEY]);
    }
    const raw = window.localStorage.getItem(MULTI_COLUMN_CARD_CLIPBOARD_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    console.warn('[LessonTemplate] Unable to read multi-column card clipboard', error);
    return null;
  }
};

const writeMultiColumnCardClipboard = (card) => {
  if (typeof window === 'undefined') return;
  try {
    if (card === null || card === undefined) {
      multiColumnClipboardCache = null;
      delete window[MULTI_COLUMN_CARD_GLOBAL_KEY];
      window.localStorage.removeItem(MULTI_COLUMN_CARD_CLIPBOARD_KEY);
    } else {
      multiColumnClipboardCache = cloneMultiColumnCardData(card);
      window[MULTI_COLUMN_CARD_GLOBAL_KEY] = cloneMultiColumnCardData(card);
      window.localStorage.setItem(MULTI_COLUMN_CARD_CLIPBOARD_KEY, JSON.stringify(card));
    }
  } catch (error) {
    console.warn('[LessonTemplate] Unable to write multi-column card clipboard', error);
  }
};

const buildCitationAuthorLabel = (citation = {}) => {
  if (citation.author) return citation.author;
  const parts = getCitationAuthorParts(citation);
  if (parts.lastName && parts.firstName) return `${parts.lastName}, ${parts.firstName}`;
  return parts.lastName || parts.firstName || '';
};
const buildReferenceDisplay = (citation = {}) => {
  const meta = mapCitationToSourceMeta(citation);
  const text = formatReference(meta) || '';
  const link = meta.doi ? `https://doi.org/${meta.doi}` : meta.url;
  if (!link || !text.includes(link)) {
    return { before: text, link, after: '', inline: false };
  }
  const [before, after = ''] = text.split(link);
  return { before, link, after, inline: true };
};

const sortCitationsByMeta = (citations = []) => (
  [...(citations || [])].sort((a, b) => {
    const aMeta = mapCitationToSourceMeta(a);
    const bMeta = mapCitationToSourceMeta(b);
    const aKey = (aMeta.author || aMeta.publisher || aMeta.title || '').toLowerCase();
    const bKey = (bMeta.author || bMeta.publisher || bMeta.title || '').toLowerCase();
    return aKey.localeCompare(bKey);
  })
);

const CONTENT_BLOCK_GUIDE = [
  {
    type: 'text',
    label: 'Text Block',
    purpose: 'General rich text with inline formatting for paragraphs or short explanations.',
    requiredFields: ['content'],
    structure: {
      type: 'text',
      content: '<p>Write in HTML. Preserve semantic tags for lists, emphasis, and links.</p>'
    },
    tips: [
      'Keep paragraphs concise and break long ideas into multiple blocks for readability.',
      'Use semantic HTML (e.g., <strong>, <em>, <a>) instead of plain text styling keywords.'
    ]
  },
  {
    type: 'heading',
    label: 'Heading (Rich Text)',
    purpose: 'Short headings or sub-headings inside sections.',
    requiredFields: ['content'],
    structure: { type: 'heading', content: '<h3>Sub-heading</h3>' },
    tips: ['Use heading levels that respect document hierarchy.']
  },
  {
    type: 'list',
    label: 'List',
    purpose: 'Bulleted or numbered lists for steps and checkpoints.',
    requiredFields: ['content'],
    structure: { type: 'list', content: '<ul><li>Item one</li><li>Item two</li></ul>' },
    tips: ['Prefer short bullets with action verbs.']
  },
  {
    type: 'headline',
    label: 'Headline',
    purpose: 'Large hero-style titles for slides or section openers.',
    requiredFields: ['content'],
    structure: { type: 'headline', content: '<h1>Big Idea</h1>' },
    tips: ['Keep to one or two lines and avoid inline images.']
  },
  {
    type: 'divider',
    label: 'Divider',
    purpose: 'Visual separation between ideas.',
    requiredFields: ['content'],
    structure: {
      type: 'divider',
      content: { style: 'solid', thickness: 2, color: '#cbd5e1', spacing: 'medium', width: 'full' }
    },
    tips: ['Use between major beats; do not overuse inside short sections.']
  },
  {
    type: 'html',
    label: 'Code Block',
    purpose: 'Syntax-highlighted code or raw HTML/CSS snippets.',
    requiredFields: ['content'],
    structure: {
      type: 'html',
      content: '<!-- HTML, CSS, or JS allowed; avoid scripts unless necessary -->',
      language: 'html',
      mode: 'displayCode',
      options: { allowScripts: false, softWrap: true, showLineNumbers: true },
      customCss: ''
    },
    tips: ['Prefer safe embeds; avoid untrusted scripts.']
  },
  {
    type: 'embedded-html',
    label: 'Embedded HTML',
    purpose: 'Trusted embed snippets (maps, charts, iframes).',
    requiredFields: ['content'],
    structure: { type: 'embedded-html', content: '<div>Embed code</div>' },
    tips: ['Use only vetted embed codes; keep aspect ratios responsive.']
  },
  {
    type: 'custom-html',
    label: 'Custom HTML/CSS',
    purpose: 'Hand-written markup and styles that lay out the body of one slide.',
    requiredFields: ['html'],
    structure: {
      type: 'custom-html',
      html: '<div class="lp-grid"><h2>Custom layout</h2><p>Markup for this slide.</p></div>',
      css: '.lp-grid { display: grid; gap: 1rem; }',
      cssScope: 'slide'
    },
    tips: [
      'Set cssScope to "slide" (default) to keep the styles on this slide, or "deck" to apply them to every slide.',
      'Scripts, iframes and form controls are stripped — this is layout markup, not an app.'
    ]
  },
  {
    type: 'info-box',
    label: 'Info Box',
    purpose: 'Callout for notes, reminders, or definitions.',
    requiredFields: ['content'],
    structure: { type: 'info-box', content: '<p>Key note or definition.</p>' },
    tips: ['Use sparingly to avoid alert fatigue.']
  },
  {
    type: 'exercise-box',
    label: 'Exercise Box',
    purpose: 'Prompts for learner tasks or practice.',
    requiredFields: ['content'],
    structure: { type: 'exercise-box', content: '<ol><li>Try this...</li></ol>' },
    tips: ['Include clear steps and expected outputs.']
  },
  {
    type: 'warning-box',
    label: 'Warning Box',
    purpose: 'Risks, misconceptions, or compliance reminders.',
    requiredFields: ['content'],
    structure: { type: 'warning-box', content: '<p>Avoid these pitfalls...</p>' },
    tips: ['Keep concise and action-oriented.']
  },
  {
    type: 'video',
    label: 'Video',
    purpose: 'Embed external videos.',
    requiredFields: ['platform', 'videoId'],
    structure: {
      type: 'video',
      platform: 'youtube',
      videoId: 'VIDEO_ID',
      aspectRatio: '16-9',
      startTime: 0,
      title: 'Clip title'
    },
    tips: ['Prefer platform + ID over raw iframe; keep start times under five seconds for focus.']
  },
  {
    type: 'youtube-playlist',
    label: 'YouTube Playlist',
    purpose: 'A whole YouTube playlist as one shareable card: player, running order, and the link to hand out.',
    requiredFields: ['playlistId'],
    structure: {
      type: 'youtube-playlist',
      playlistId: 'PLAYLIST_ID',
      startVideoId: 'VIDEO_ID',
      startIndex: 1,
      title: 'Playlist title',
      channel: 'Channel or curator',
      description: 'What to watch for.',
      itemCount: 14,
      layout: 'split',
      theme: 'light',
      accentColor: '#dc2626',
      aspectRatio: '16-9',
      items: [{ videoId: 'VIDEO_ID', title: 'Video title', duration: '0:14' }],
      videoAuthor: 'Channel or curator',
      videoSource: 'YouTube',
      videoDate: 'YYYY-MM-DD'
    },
    tips: [
      'playlistId is the list= value from any YouTube address; startVideoId sets which video the card opens on.',
      'layout: split (player + list), stacked, player, or list for a handout.',
      'Citation fields use the video block names (videoAuthor/videoSource/videoDate), so the playlist joins the reference list.'
    ]
  },
  {
    type: 'image',
    label: 'Image',
    purpose: 'Single image with optional caption. Either a picture file (src) or a link embedded from its source site (imageEmbedSrc).',
    requiredFields: ['src'],
    structure: {
      type: 'image',
      src: 'https://example.com/image.png',
      caption: 'Describe the visual for accessibility.',
      size: IMAGE_SIZES.medium?.label || 'Medium'
    },
    tips: [
      'Provide descriptive captions; favor hosted URLs when exporting slim JSON.',
      'A link straight to a picture file (https://…/photo.png) belongs in src — it renders and scales like any other image.',
      'For sites that only allow embedding (Pinterest, Flickr, Instagram), leave src empty and set imageEmbedSrc to the provider iframe URL, imageEmbedUrl to the page URL, plus imageEmbedProvider/imageEmbedWidth/imageEmbedHeight. Citation fields work the same either way.'
    ]
  },
  {
    type: 'gallery',
    label: 'Image Gallery',
    purpose: 'Multiple related images.',
    requiredFields: ['items'],
    structure: {
      type: 'gallery',
      columns: GALLERY_COLUMNS['3']?.label || '3 Columns',
      items: [
        { src: 'https://example.com/image-1.png', caption: 'Label A' },
        { src: 'https://example.com/image-2.png', caption: 'Label B' }
      ]
    },
    tips: ['Group images by theme and keep captions short.']
  },
  {
    type: 'audio',
    label: 'Audio',
    purpose: 'Audio clips with citations.',
    requiredFields: ['src'],
    structure: { type: 'audio', src: 'https://example.com/audio.mp3', title: 'Clip title', citation: 'Source details' },
    tips: ['Link transcripts in the surrounding text for accessibility.']
  },
  {
    type: 'accordion',
    label: 'Accordion',
    purpose: 'Progressive disclosure of concepts or FAQs.',
    requiredFields: ['items'],
    structure: {
      type: 'accordion',
      settings: { allowMultipleOpen: false, defaultOpenFirst: true },
      items: [
        { id: 'item-1', title: 'Question', content: '<p>Answer...</p>' },
        { id: 'item-2', title: 'Next Question', content: '<p>Answer...</p>' }
      ]
    },
    tips: ['Use for 3–6 items; avoid deeply nested accordions.']
  },
  {
    type: 'cards',
    label: 'Cards',
    purpose: 'Grid of spotlight items.',
    requiredFields: ['items'],
    structure: {
      type: 'cards',
      layout: CARD_LAYOUTS['2x2'] ? '2x2' : '2x1',
      style: 'info',
      items: [
        { title: 'Key Idea', content: '<p>Supporting detail.</p>' },
        { title: 'Example', content: '<p>Concrete instance.</p>' }
      ]
    },
    tips: ['Balance text across cards; keep each under 60 words.']
  },
  {
    type: 'multi-column-cards',
    label: 'Multi-Column Cards',
    purpose: 'Columns with nested cards for comparisons or milestones.',
    requiredFields: ['columns'],
    structure: {
      type: 'multi-column-cards',
      columns: [
        { title: 'Column A', width: '50%', cards: [{ title: 'Point', content: '<p>Detail.</p>' }] },
        { title: 'Column B', width: '50%', cards: [{ title: 'Point', content: '<p>Detail.</p>' }] }
      ]
    },
    tips: ['Use equal widths for comparisons; stagger colors lightly for readability.']
  },
  {
    type: 'stacked-cards',
    label: 'Stacked Cards',
    purpose: 'Vertical sequence of cards (timelines, processes).',
    requiredFields: ['items'],
    structure: {
      type: 'stacked-cards',
      items: [
        { title: 'Step 1', content: '<p>Do this first.</p>', accentColor: '#0ea5e9' },
        { title: 'Step 2', content: '<p>Then this.</p>', accentColor: '#6366f1' }
      ]
    },
    tips: ['Limit to 4–7 steps to keep the stack scannable.']
  },
  {
    type: 'worksheet',
    label: 'Worksheet',
    purpose: 'Structured questions for practice or assessment.',
    requiredFields: ['questions'],
    structure: {
      type: 'worksheet',
      layout: 'single-column',
      questions: [
        { id: 'q1', type: 'short-answer', prompt: 'Question text', answer: 'Expected answer', points: 1 },
        { id: 'q2', type: 'multiple-choice', prompt: 'Question text', options: ['A', 'B', 'C'], answer: 'A', points: 1 }
      ]
    },
    tips: ['Map question types to supported WORKSHEET_QUESTION_TYPES; include answers when generating instructor keys.']
  }
];




// Platforms /api/video-meta.php can actually describe. Panopto is left out on
// purpose: it answers with a placeholder title rather than the session's own,
// so offering the button there would overwrite good details with a stub.
const CITATION_FETCHABLE_PLATFORMS = ['youtube', 'vimeo', 'disney'];

const generateVideoEmbed = (platform, videoId, embedCode, aspectRatio) => {
  const aspectRatioValue = {
    '16-9': '16 / 9',
    '4-3': '4 / 3',
    '1-1': '1 / 1',
    '21-9': '21 / 9'
  }[aspectRatio] || '16 / 9';

  switch (platform) {
    case 'youtube':
      return `<div class="relative w-full overflow-hidden rounded-xl bg-gray-900 shadow-lg" style="aspect-ratio: ${aspectRatioValue};"><iframe src="https://www.youtube.com/embed/${videoId}" class="w-full h-full border-0" allowfullscreen allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" referrerpolicy="strict-origin-when-cross-origin"></iframe></div>`;
    case 'vimeo':
      return `<div class="relative w-full overflow-hidden rounded-xl bg-gray-900 shadow-lg" style="aspect-ratio: ${aspectRatioValue};"><iframe src="https://player.vimeo.com/video/${videoId}" class="w-full h-full border-0" allowfullscreen allow="autoplay; fullscreen; picture-in-picture"></iframe></div>`;
    case 'panopto':
      return `<div class="relative w-full overflow-hidden rounded-xl bg-gray-900 shadow-lg" style="aspect-ratio: ${aspectRatioValue};"><iframe src="${videoId}" class="w-full h-full border-0" allowfullscreen></iframe></div>`;
    // Disney serves a bare 16:9 player at /embed/<id> — the page an author
    // copies (/watch/<slug>-<id>) is the whole site around it, so the id is
    // swapped onto the player address the same way YouTube's is.
    case 'disney':
      return `<div class="relative w-full overflow-hidden rounded-xl bg-gray-900 shadow-lg" style="aspect-ratio: ${aspectRatioValue};"><iframe src="https://video.disney.com/embed/${videoId}" class="w-full h-full border-0" allowfullscreen allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" referrerpolicy="strict-origin-when-cross-origin"></iframe></div>`;
    case 'embed':
      return `<div class="relative w-full overflow-hidden rounded-xl bg-gray-900 shadow-lg" style="aspect-ratio: ${aspectRatioValue};">${embedCode}</div>`;
    default:
      return '';
  }
};

const generateAPACitation = (title, author, date, source, url) => {
  let citation = '';
  if (author) citation += `<strong>${author}.</strong> `;
  if (date) {
    const videoDate = new Date(date + 'T00:00:00');
    citation += `(${videoDate.getFullYear()}). `;
  } else if (author || title || source || url) {
    citation += `(n.d.). `;
  }
  if (title) citation += `<em>${title}</em> [Video]. `;
  if (source) citation += `${source}. `;
  if (url) citation += `<a href="${url}" target="_blank" class="text-blue-600 hover:text-blue-800">${url}</a>`;
  return citation || '';
};

// Auto-save functionality
const useAutoSave = (data, key, interval = 30000) => {
  const [lastSaved, setLastSaved] = useState(new Date());
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  useEffect(() => {
    setHasUnsavedChanges(true);
  }, [data]);

  useEffect(() => {
    const autoSaveInterval = setInterval(() => {
      if (hasUnsavedChanges) {
        try {
          const sanitizedData = sanitizeForExport(data);
          localStorage.setItem(key, JSON.stringify({
            ...sanitizedData,
            timestamp: new Date().toISOString(),
            autoSaved: true
          }));
          setLastSaved(new Date());
          setHasUnsavedChanges(false);
        } catch (error) {
          console.error('Auto-save failed:', error);
        }
      }
    }, interval);

    return () => clearInterval(autoSaveInterval);
  }, [data, hasUnsavedChanges, key, interval]);

  const loadAutoSavedData = useCallback(() => {
    try {
      const saved = localStorage.getItem(key);
      if (saved) {
        const parsedData = JSON.parse(saved);
        if (parsedData.autoSaved) {
          return parsedData;
        }
      }
    } catch (error) {
      console.error('Failed to load auto-saved data:', error);
    }
    return null;
  }, [key]);

  const clearAutoSavedData = useCallback(() => {
    localStorage.removeItem(key);
  }, [key]);

  return { lastSaved, hasUnsavedChanges, loadAutoSavedData, clearAutoSavedData };
};

// Save Indicator Component
const SaveIndicator = ({ message, type, show }) => (
  <div className={`fixed top-6 right-6 px-4 py-3 rounded-lg text-white text-sm font-medium z-50 transition-all duration-300 shadow-lg ${show ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-20'
    } ${type === 'saving' ? 'bg-amber-500' : type === 'error' ? 'bg-red-500' : 'bg-emerald-500'
    }`}>
    <div className="flex items-center gap-2">
      {type === 'saving' ? (
        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
      ) : type === 'error' ? (
        <AlertTriangle size={16} />
      ) : (
        <CheckCircle size={16} />
      )}
      {message}
    </div>
  </div>
);

// Mode Banner - sits at top of slide body to show current mode. `rightControls`
// lets the slide inject compact utility buttons (e.g. the canvas guide toggle
// and slide-settings gear) into this row so they don't crowd the title bar.
const SlideModeBanner = ({ isEditMode, onToggle, rightControls = null }) => (
  <div
    className={`slide-mode-banner ${isEditMode ? 'slide-mode-banner--edit' : 'slide-mode-banner--preview'}`}
    onClick={onToggle}
    role="button"
    tabIndex={0}
    onKeyDown={(e) => {
      // Only toggle when the banner itself has focus, so Enter/Space on an
      // injected utility button (guide toggle, gear) doesn't also flip modes.
      if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); onToggle(); }
    }}
  >
    <div className="slide-mode-banner__left">
      <span className={`slide-mode-banner__dot ${isEditMode ? 'slide-mode-banner__dot--edit' : 'slide-mode-banner__dot--preview'}`} />
      <span className="slide-mode-banner__label">
        {isEditMode ? 'Edit Mode \u2014 click elements to modify' : 'Preview Mode \u2014 read-only'}
      </span>
    </div>
    <div className="slide-mode-banner__right">
      {rightControls}
      <span
        className={`slide-mode-banner__btn ${isEditMode ? 'slide-mode-banner__btn--to-preview' : 'slide-mode-banner__btn--to-edit'}`}
        role="button"
        tabIndex={-1}
      >
        {isEditMode ? 'Preview' : 'Edit'}
        <span className={`slide-mode-banner__shortcut ${isEditMode ? 'slide-mode-banner__shortcut--edit' : 'slide-mode-banner__shortcut--preview'}`}>E</span>
      </span>
    </div>
  </div>
);

// Floating Edit Button - appears on hover in preview mode, bottom-right of slide
const FloatingEditButton = ({ onToggle }) => (
  <div className="slide-floating-edit">
    <button
      className="slide-floating-edit__btn"
      onClick={(e) => { e.stopPropagation(); onToggle(); }}
      type="button"
    >
      <Edit3 size={14} />
      Edit Slide
      <span className="slide-mode-banner__shortcut slide-mode-banner__shortcut--preview">E</span>
    </button>
  </div>
);

// Mode Toast - bottom-center notification on mode switch
const ModeToast = ({ message, isEdit, visible }) => (
  <div
    className={`slide-mode-toast ${visible ? 'slide-mode-toast--visible' : ''} ${isEdit ? 'slide-mode-toast--edit' : 'slide-mode-toast--preview'}`}
  >
    {message}
  </div>
);

// Auto-save Recovery Modal
const AutoSaveRecoveryModal = ({ isOpen, onRecover, onDiscard, timestamp }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
        <div className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
              <Clock className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Auto-saved Content Found</h3>
              <p className="text-sm text-gray-600">
                Last saved: {new Date(timestamp).toLocaleString()}
              </p>
            </div>
          </div>
          <p className="text-gray-700 mb-6">
            We found auto-saved changes from your previous session. Would you like to recover them?
          </p>
          <div className="flex gap-3">
            <button
              onClick={onRecover}
              className="flex-1 px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors font-medium"
            >
              Recover Changes
            </button>
            <button
              onClick={onDiscard}
              className="flex-1 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors font-medium"
            >
              Discard
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const getSectionHeaderLabel = (section = {}, sectionNumberMap) => {
  const studentTitle = getStudentFriendlyTitle(section);
  const baseTitle = studentTitle ? `${section.title} (${studentTitle})` : section.title;
  const sectionNumber = section.sectionNumber ?? sectionNumberMap?.[section.id];
  return sectionNumber ? `${sectionNumber}. ${baseTitle}` : baseTitle;
};

const getSectionExportLabel = (section = {}) =>
  getStudentFriendlyTitle(section) || section.title;

/** Titles are plain text; exports interpolate them into markup. */
/**
 * The corner logo's authored width as a share of the card.
 *
 * The app draws it in --title-card-px — one authored pixel expressed against
 * the 1280x720 slide — which every export has to reproduce in its own units.
 * Here that is a percentage of the card's width, and a matching cap on its
 * height so a tall mark stays inside the corner it was put in.
 */
const titleCardCornerLogoWidthPercent = (size) => (
  ((Number(size) || 120) / 1280 * 100).toFixed(3)
);

const titleCardCornerLogoHeightPercent = (size) => (
  ((Number(size) || 120) / 720 * 100).toFixed(3)
);

const escapeHtmlText = (value = '') => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;');

const DEFAULT_TITLE_CARD_SETTINGS = {
  enabled: true,
  title: 'Main Title',
  subtitle: 'Description of Presentation',
  image: '',
  imageSize: 120,
  imageCitation: '',
  showCourseInfo: true,
  showLogoOnTitle: true,
  showTextOverlay: false,
  textOverlayOpacity: 0.5,
  textPlacement: 'below',
  // Light background with dark-grey text so title cards stay readable by
  // default — including when a light background image is added
  backgroundColor: '#f4f6f9',
  textColor: '#1f2937',
  titlePosition: 'center',
  backgroundImage: '',
  // Font sizes (px) for the individual title-card text items, and whether the
  // course/instructor block stacks vertically or flows in one bottom row.
  titleFontSize: 48,
  subtitleFontSize: 20,
  metaFontSize: 16,
  instructorLayout: 'stacked',
  // A crest or wordmark pinned in one of the card's top corners. Separate from
  // the deck's school logo, which sits in the meta row along the bottom: a
  // title slide usually wants its institution's mark up top, at a size the
  // author picks, and often a different mark from the one on content slides.
  cornerLogo: '',
  cornerLogoPosition: 'top-right',
  cornerLogoSize: 120
};

/**
 * The corner logo's width, in authored pixels against the 1280px slide.
 *
 * Bounded rather than free: below the floor a crest is a smudge nobody can
 * read, and above the ceiling it stops being a corner mark and starts being
 * the slide.
 */
const TITLE_CARD_CORNER_LOGO_MIN_PX = 40;
const TITLE_CARD_CORNER_LOGO_MAX_PX = 480;
const TITLE_CARD_CORNER_LOGO_POSITIONS = ['top-left', 'top-right'];

const DEFAULT_SECTION_TITLE_CARD_SETTINGS = {
  ...DEFAULT_TITLE_CARD_SETTINGS,
  enabled: false,
  title: 'Section Title',
  subtitle: '',
  showCourseInfo: false,
  showLogoOnTitle: false
};

const SECTION_TITLE_CARD_PREFIX = 'section:';

const DEFAULT_END_TITLE_CARD_SETTINGS = {
  ...DEFAULT_TITLE_CARD_SETTINGS,
  enabled: false,
  title: 'End Title',
  subtitle: 'Thanks for reviewing',
  showCourseInfo: false,
  showLogoOnTitle: false
};

const normalizeTitleCardSettings = (settings = {}, defaults = DEFAULT_TITLE_CARD_SETTINGS) => {
  const imageSize = Number(settings.imageSize);
  const allowCourseInfo = settings.showCourseInfo !== false;
  const allowLogoOnTitle = settings.showLogoOnTitle !== false;
  const showTextOverlay = settings.showTextOverlay === true;
  const rawOpacity = Number(settings.textOverlayOpacity);
  const textOverlayOpacity = Number.isFinite(rawOpacity) ? Math.max(0, Math.min(1, rawOpacity)) : defaults.textOverlayOpacity;
  const allowedPlacements = new Set(['above', 'below']);
  const textPlacement = allowedPlacements.has(settings.textPlacement)
    ? settings.textPlacement
    : defaults.textPlacement;
  const allowedPositions = new Set(['top', 'center', 'bottom']);
  const titlePosition = allowedPositions.has(settings.titlePosition)
    ? settings.titlePosition
    : defaults.titlePosition;
  // Clamp font sizes to a sensible range, falling back to the defaults.
  const clampFontSize = (raw, fallback) => {
    const value = Number(raw);
    return Number.isFinite(value) ? Math.max(8, Math.min(160, value)) : fallback;
  };
  const allowedLayouts = new Set(['stacked', 'row']);
  const instructorLayout = allowedLayouts.has(settings.instructorLayout)
    ? settings.instructorLayout
    : (defaults.instructorLayout || 'stacked');
  const cornerLogoPosition = TITLE_CARD_CORNER_LOGO_POSITIONS.includes(settings.cornerLogoPosition)
    ? settings.cornerLogoPosition
    : (defaults.cornerLogoPosition || 'top-right');
  const rawCornerLogoSize = Number(settings.cornerLogoSize);
  const cornerLogoSize = Number.isFinite(rawCornerLogoSize)
    ? Math.max(TITLE_CARD_CORNER_LOGO_MIN_PX, Math.min(TITLE_CARD_CORNER_LOGO_MAX_PX, rawCornerLogoSize))
    : (defaults.cornerLogoSize ?? 120);
  // Same rule as the card's picture: a blob: URL is a handle on this page's
  // memory, so a deck that has been reloaded would point at nothing.
  const cornerLogo = typeof settings.cornerLogo === 'string' && !settings.cornerLogo.startsWith('blob:')
    ? settings.cornerLogo
    : '';
  return {
    ...defaults,
    ...settings,
    backgroundImage: normalizeBackgroundPersistenceValue(settings.backgroundImage || ''),
    image: typeof settings.image === 'string' && settings.image.startsWith('blob:') ? '' : settings.image,
    imageSize: Number.isFinite(imageSize) ? imageSize : defaults.imageSize,
    showCourseInfo: allowCourseInfo,
    showLogoOnTitle: allowLogoOnTitle,
    showTextOverlay,
    textOverlayOpacity,
    textPlacement,
    titlePosition,
    titleFontSize: clampFontSize(settings.titleFontSize, defaults.titleFontSize ?? 48),
    subtitleFontSize: clampFontSize(settings.subtitleFontSize, defaults.subtitleFontSize ?? 20),
    metaFontSize: clampFontSize(settings.metaFontSize, defaults.metaFontSize ?? 16),
    instructorLayout,
    cornerLogo,
    cornerLogoPosition,
    cornerLogoSize
  };
};

const getBackgroundSelectionState = (background) => {
  const resolved = resolveBackgroundPreset(background);
  if (!resolved) {
    return {
      selectedId: '',
      selectedLabel: '',
      selectedPreview: ''
    };
  }

  const presetEntry = IMAGE_BACKGROUND_PRESET_OPTIONS.find((entry) => entry.key === resolved.id);
  return {
    selectedId: presetEntry ? presetEntry.key : 'custom',
    selectedLabel: resolved.name || 'Custom Image',
    selectedPreview: resolved.preview || resolved.value || ''
  };
};

// Block types the slide designer scales as one object rather than letting them
// flow: a picture, its caption and its citation shrink together to fit their
// frame. Mirrors SCALED_MEDIA_TYPES in SlideDesigner, and the export's fit
// script treats these the same way.
const EXPORT_SCALED_MEDIA_TYPES = new Set(['image', 'video', 'youtube-playlist', 'gallery', 'embedded-html', 'project-timeline']);

/* A canvas slide is laid out entirely in percentages — every frame, and (via
   cqw units) every point of text. A picture is the one thing that is not: an
   <img> narrower than its frame renders at its natural pixel size, which does
   not change when the slide does. So the same deck lays out differently at
   every stage width. Measured on one slide of a real deck, a 360x270 photo
   took 49% of the stage's height at 1104px and 34% at 1600px:

       stage 1104x551   photo @21.0-70.0%   collides with the logo strip
       stage 1220x609   photo @21.0-65.4%   clear
       stage 1600x798   photo @21.0-54.8%   clear

   The app draws the slide at whatever width its window gives it, while the
   export caps the deck at max-w-6xl (1104px), so the two disagreed and the
   export's pictures ran into the blocks below them. Nothing about the frames
   was wrong, which is why capping the picture's width could never fix it.

   Restate the natural width against the canonical slide the pptx export
   already measures pictures against — SLIDE_W (13.33in) at 96 CSS px/in — in
   container-query units of the card. 1cqw is 1% of the card's width, so a
   360px picture becomes 28.125cqw: exactly 360px when the card is 1280 wide,
   and the same share of the slide at every other width. The frame cap still
   applies on top, so a picture wider than its frame is untouched — those were
   already scale-invariant, being sized by the frame rather than by pixels. */
const CANVAS_CANONICAL_WIDTH_PX = 13.33 * 96;

const measureNaturalWidth = (src) => new Promise((resolve) => {
  if (typeof document === 'undefined' || !src || !/^data:image\//i.test(src)) { resolve(0); return; }
  let settled = false;
  const finish = (width) => { if (!settled) { settled = true; resolve(width); } };
  // A picture that never decodes must not hold the export open. Environments
  // that do not rasterize at all (jsdom under test) fire neither event, and an
  // unmeasured picture simply keeps the size it has today.
  const timer = setTimeout(() => finish(0), 2000);
  // document.createElement, not `new Image()`: this module imports lucide's
  // Image icon at the top, which shadows the DOM constructor for the whole
  // file. `new Image()` therefore threw "is not a constructor" and took the
  // export down with it — and a `typeof Image` guard cannot catch that,
  // because the icon is very much defined.
  const img = document.createElement('img');
  img.onload = () => { clearTimeout(timer); finish(img.naturalWidth || 0); };
  img.onerror = () => { clearTimeout(timer); finish(0); };
  img.src = src;
});

const pinCanvasImageWidths = async (html) => {
  if (typeof document === 'undefined' || !html || !html.includes('<img')) return html;
  const host = document.createElement('div');
  host.innerHTML = html;
  const images = Array.from(host.querySelectorAll('img'));
  let changed = false;
  for (const image of images) {
    // The full-width preset already states its size as a share of the frame,
    // so it scales with the slide and must be left exactly as it is.
    if (image.style.width) continue;
    const naturalWidth = await measureNaturalWidth(image.getAttribute('src') || '');
    if (!(naturalWidth > 0)) continue;
    const cqw = (naturalWidth / CANVAS_CANONICAL_WIDTH_PX) * 100;
    image.style.width = `${Number(cqw.toFixed(4))}cqw`;
    changed = true;
  }
  return changed ? host.innerHTML : html;
};

const stripHtml = (value = '') =>
  value.replace(/<[^>]*>/g, ' ').replace(/&nbsp;/gi, ' ').trim();

const sanitizeHtmlString = (value = '') => stripHtml(String(value || '')).trim();

const getContrastingTextColor = (backgroundColor, lightColor = '#ffffff', darkColor = '#0f172a') => {
  if (typeof backgroundColor !== 'string') return darkColor;
  const hex = backgroundColor.replace('#', '').trim();

  if (![3, 6].includes(hex.length) || /[^a-fA-F0-9]/.test(hex)) {
    return darkColor;
  }

  const normalizedHex = hex.length === 3
    ? hex.split('').map((char) => char + char).join('')
    : hex;

  const r = parseInt(normalizedHex.slice(0, 2), 16) / 255;
  const g = parseInt(normalizedHex.slice(2, 4), 16) / 255;
  const b = parseInt(normalizedHex.slice(4, 6), 16) / 255;

  const luminance = [r, g, b].map((channel) =>
    channel <= 0.03928
      ? channel / 12.92
      : Math.pow((channel + 0.055) / 1.055, 2.4)
  ).reduce((acc, val, idx) => acc + [0.2126, 0.7152, 0.0722][idx] * val, 0);

  return luminance > 0.45 ? darkColor : lightColor;
};

const getBlockPreview = (block = {}) => {
  const base = block.content || block.title || '';
  if (sanitizeHtmlString(base)) return sanitizeHtmlString(base);

  switch (block.type) {
    case 'custom-html': {
      // The markup is on `html`, not `content`, so the base check above misses
      // it and the block would list as "No preview available".
      const text = sanitizeHtmlString(block.html || '');
      if (text) return text;
      return (block.css || '').trim() ? 'Custom CSS' : '';
    }
    case 'cards':
    case 'stacked-cards': {
      const firstCard = (block.items || []).find((item) =>
        sanitizeHtmlString(item?.title || '') || sanitizeHtmlString(item?.content || '') || sanitizeHtmlString(item?.sideText || '')
      );
      if (firstCard) {
        return sanitizeHtmlString(firstCard.title || firstCard.content || firstCard.sideText || '');
      }
      break;
    }
    case 'multi-column-cards': {
      const blockTitleEnabled = typeof block.blockTitleEnabled === 'boolean'
        ? block.blockTitleEnabled
        : Boolean(block.multiColumnData?.blockTitleEnabled);
      const blockTitle = sanitizeHtmlString(block.blockTitle) || sanitizeHtmlString(block.multiColumnData?.blockTitle);
      if (blockTitleEnabled && blockTitle) {
        return blockTitle;
      }

      for (const column of block.columns || []) {
        const firstCard = (column.cards || []).find((card) =>
          sanitizeHtmlString(card?.title || '')
          || sanitizeHtmlString(card?.content || '')
          || sanitizeHtmlString(card?.sideText || '')
          || sanitizeHtmlString(card?.description || '')
        );
        if (firstCard) {
          return sanitizeHtmlString(firstCard.title || firstCard.content || firstCard.sideText || firstCard.description || '');
        }
      }
      break;
    }
    case 'gallery': {
      const firstItem = (block.items || []).find((item) => item?.title || item?.description);
      if (firstItem) {
        return sanitizeHtmlString(firstItem.title || firstItem.description || '');
      }
      break;
    }
    case 'link': {
      const firstItem = (block.items || []).find((item) => item?.title || item?.description || item?.url);
      if (firstItem) {
        return sanitizeHtmlString(firstItem.title || firstItem.description || firstItem.url || '');
      }
      break;
    }
    case 'project-timeline': {
      const firstStep = (block.steps || []).find((step) =>
        sanitizeHtmlString(step?.title || '') || sanitizeHtmlString(step?.text || '') || sanitizeHtmlString(step?.timeSpan || '')
      );
      if (firstStep) {
        return sanitizeHtmlString(firstStep.title || firstStep.timeSpan || firstStep.text || '');
      }
      break;
    }
    case 'worksheet': {
      const firstQuestion = (block.questions || [])[0];
      if (firstQuestion?.prompt) return sanitizeHtmlString(firstQuestion.prompt);
      break;
    }
    case 'youtube-playlist': {
      const label = sanitizeHtmlString(block.videoTitle || block.channel || block.videoAuthor || '');
      if (label) return label;
      const firstEntry = (block.items || []).find((item) => sanitizeHtmlString(item?.title || ''));
      if (firstEntry) return sanitizeHtmlString(firstEntry.title);
      if (block.playlistId) return 'YouTube playlist';
      break;
    }
    default:
      break;
  }

  return 'No content provided';
};

const DEFAULT_DIVIDER_CONTENT = {
  style: 'solid',
  thickness: 2,
  color: '#d1d5db',
  spacing: 'medium',
  width: 'full'
};

const isEmptyBlock = (block) => {
  if (!block) return true;
  switch (block.type) {
    case 'divider':
      return true;
    case 'apa-reference-list':
      // A reference list is intentional content that auto-populates from the
      // deck's citations, so it is never an "empty / missing content" block.
      return false;
    case 'apa-citation':
      return !(Array.isArray(block.citations) && block.citations.length > 0);
    case 'apa-quote':
      return !(
        sanitizeHtmlString(block.content || block.title || '')
        || block.author
        || (Array.isArray(block.sources) && block.sources.length > 0)
      );
    case 'text':
    case 'heading':
    case 'list':
    case 'headline':
    case 'info-box':
    case 'exercise-box':
    case 'warning-box':
    case 'html':
      return sanitizeHtmlString(block.content || '').length === 0;
    case 'custom-html':
      // CSS alone is a legitimate use — a deck-scoped block with no markup is
      // how you restyle every slide from one place.
      return !((block.html || '').trim() || (block.css || '').trim());
    case 'image':
      return !imageBlockHasContent(block);
    case 'video':
    case 'audio':
      return !block.src;
    case 'youtube-playlist':
      return !block.playlistId;
    case 'gallery':
      return !(Array.isArray(block.items) && block.items.some((item) => item?.src));
    case 'cards':
      return !(Array.isArray(block.items) && block.items.some((item) =>
        sanitizeHtmlString(item?.content || '') || sanitizeHtmlString(item?.title || '')
      ));
    case 'stacked-cards':
      return !(Array.isArray(block.items) && block.items.some((item) =>
        sanitizeHtmlString(item?.content || '') || sanitizeHtmlString(item?.title || '') || sanitizeHtmlString(item?.sideText || '')
      ));
    case 'multi-column-cards':
      return !(Array.isArray(block.columns) && block.columns.some((column) =>
        Array.isArray(column.cards) && column.cards.some((card) =>
          sanitizeHtmlString(card?.title || '') || sanitizeHtmlString(card?.content || '') ||
          card?.image?.src || card?.video?.src ||
          (card?.isChecklist && Array.isArray(card.checklistItems) && card.checklistItems.some(item => sanitizeHtmlString(item?.text || '')))
        )
      ));
    case 'link':
      return !(Array.isArray(block.items) && block.items.some((item) =>
        sanitizeHtmlString(item?.title || '') || sanitizeHtmlString(item?.description || '') || item?.url || item?.pageInfo || item?.notes
      ));
    case 'worksheet':
      return !(Array.isArray(block.questions) && block.questions.length > 0);
    case 'project-timeline':
      return !(
        sanitizeHtmlString(block.title || '')
        || (Array.isArray(block.steps) && block.steps.some((step) =>
          sanitizeHtmlString(step?.title || '') || sanitizeHtmlString(step?.text || '')
          || sanitizeHtmlString(step?.timeSpan || '') || sanitizeHtmlString(step?.icon || '')
        ))
      );
    default: {
      console.warn('Unknown block type encountered:', block.type);
      return !sanitizeHtmlString(block.content || block.title || '');
    }
  }
};

const isEmptySlide = (slide) => {
  if (!slide) return true;
  if (slide.type === 'title-card' || slide.type === 'end-title-card' || slide.type === 'section-title-card') {
    return !sanitizeHtmlString(slide.title || '')
      && !sanitizeHtmlString(slide.subtitle || '')
      && !slide.image;
  }
  const blocks = slide.blocks || [];
  return blocks.length === 0 || blocks.every(isEmptyBlock);
};

const formatBlockTypeLabel = (blockType) => {
  if (!blockType) return 'EMPTY';
  return blockType.replace(/-/g, ' ').toUpperCase();
};

const formatSlideDurationLabel = (minutesValue) => {
  const minutes = Number(minutesValue);
  if (!Number.isFinite(minutes) || minutes <= 0) return null;
  return `${minutes} min`;
};

const hasBlockContent = (block) => {
  if (!block) return false;
  switch (block.type) {
    case 'divider':
      return false;
    case 'text':
    case 'heading':
    case 'list':
    case 'headline':
    case 'info-box':
    case 'exercise-box':
    case 'warning-box':
    case 'html':
      return stripHtml(block.content || '').length > 0;
    case 'custom-html':
      return Boolean((block.html || '').trim() || (block.css || '').trim());
    case 'image':
      return imageBlockHasContent(block);
    case 'video':
    case 'audio':
      return Boolean(block.src);
    case 'youtube-playlist':
      return Boolean(block.playlistId);
    case 'gallery':
      return Array.isArray(block.items) && block.items.some((item) => item?.src);
    case 'cards':
      return Array.isArray(block.items) && block.items.some((item) => stripHtml(item?.content || '') || stripHtml(item?.title || ''));
    case 'stacked-cards':
      return Array.isArray(block.items) && block.items.some((item) => stripHtml(item?.content || '') || stripHtml(item?.title || '') || stripHtml(item?.sideText || ''));
    case 'multi-column-cards':
      return Array.isArray(block.columns) && block.columns.some((column) =>
        Array.isArray(column.cards) && column.cards.some((card) =>
          stripHtml(card?.title || '') || stripHtml(card?.content || '') || card?.image?.src || card?.video?.src ||
          (card?.isChecklist && Array.isArray(card.checklistItems) && card.checklistItems.some(item => stripHtml(item?.text || '')))
        )
      );
    case 'link':
      return Array.isArray(block.items) && block.items.some((item) =>
        stripHtml(item?.title || '') || stripHtml(item?.description || '') || item?.url || item?.pageInfo || item?.notes
      );
    case 'worksheet':
      return Array.isArray(block.questions) && block.questions.length > 0;
    case 'project-timeline':
      return Boolean(stripHtml(block.title || '')) || (Array.isArray(block.steps) && block.steps.some((step) =>
        stripHtml(step?.title || '') || stripHtml(step?.text || '') || stripHtml(step?.timeSpan || '') || stripHtml(step?.icon || '')
      ));
    default:
      return Boolean(stripHtml(block.content || ''));
  }
};

const splitSectionSlides = (section, { includeEmpty = true, includeMeta = false } = {}) => {
  const blocks = section.blocks || [];
  const segmented = segmentBlocksIntoSlides(blocks, { includeEmpty });

  if (!segmented.length) {
    if (!includeEmpty) return [];
    const slideId = buildSectionSlideId(section.id || 'section', 0);
    return includeMeta ? [{ slideId, blocks: [] }] : [[]];
  }

  const mapped = segmented.map(({ blocks: slideBlocks, slideId }, index) => {
    const resolvedId = resolveSlideIdFromBlocks(slideBlocks, slideId || buildSectionSlideId(section.id, index));
    return includeMeta ? { slideId: resolvedId, blocks: slideBlocks } : slideBlocks;
  });

  return mapped;
};

const SECTION_ORDER = [
  'bridge-in',
  'outcomes',
  'pre-assessment',
  'participatory-learning',
  'post-assessment',
  'summary',
  'resources'
];

const NAV_ORDER = ['title-card', ...SECTION_ORDER, 'end-title-card'];

const buildSectionNumberMap = (sections = []) => {
  const sectionList = Array.isArray(sections) ? sections : Object.values(sections || {});
  const ordered = [
    ...SECTION_ORDER.map((id) => sectionList.find((section) => section.id === id)).filter(Boolean),
    ...sectionList.filter((section) => !SECTION_ORDER.includes(section.id))
  ];

  const map = {};
  ordered.forEach((section, index) => {
    if (section?.id) {
      map[section.id] = index + 1;
    }
  });

  return map;
};

const sortSectionsForNav = (sections = []) => {
  const prioritized = sections.map((section, index) => ({
    section,
    index,
    priority: SECTION_ORDER.indexOf(section.id)
  }));

  const fallbackPriority = SECTION_ORDER.indexOf('resources') === -1
    ? SECTION_ORDER.length
    : SECTION_ORDER.indexOf('resources');

  return prioritized
    .sort((a, b) => {
      const aPriority = a.priority === -1 ? fallbackPriority : a.priority;
      const bPriority = b.priority === -1 ? fallbackPriority : b.priority;
      if (aPriority !== bPriority) return aPriority - bPriority;
      return a.index - b.index;
    })
    .map(({ section }) => section);
};

const sortNavItems = (items = []) => {
  const prioritized = items.map((item, index) => ({
    item,
    index,
    priority: NAV_ORDER.indexOf(item.id)
  }));

  const fallbackPriority = NAV_ORDER.indexOf('resources') === -1
    ? NAV_ORDER.length
    : NAV_ORDER.indexOf('resources');

  return prioritized
    .sort((a, b) => {
      const aPriority = a.priority === -1 ? fallbackPriority : a.priority;
      const bPriority = b.priority === -1 ? fallbackPriority : b.priority;
      if (aPriority !== bPriority) return aPriority - bPriority;
      return a.index - b.index;
    })
    .map(({ item }) => item);
};

const buildSectionSlideId = (sectionId, slideIndex) => `${sectionId}-slide-${slideIndex}`;
const buildSectionTitleCardId = (sectionId) => `${sectionId}-title-card`;

const getTitleCardPreviewStyles = (card = {}, titleCardBackground = null) => {
  return resolveTitleCardBackgroundStyles({
    cardBackgroundImage: card.backgroundImage,
    deckTitleCardBackground: titleCardBackground,
    fallbackColor: card.backgroundColor || '#545f7d'
  });
};

const createSlideDivider = (slideId) => ({
  id: generateId(),
  type: 'divider',
  content: {
    style: 'solid',
    thickness: 2,
    color: '#e5e7eb',
    spacing: 'medium',
    width: 'full',
    isSlideBreak: true,
    slideId
  }
});

const collectSlideIds = (blocks = []) => {
  const ids = new Set();
  blocks.forEach((block) => {
    if (block?.slideId) ids.add(block.slideId);
    if (block?.content?.slideId) ids.add(block.content.slideId);
  });
  return ids;
};

const resolveNextAvailableSlideId = (sectionId, usedIds, preferredIndex = 0) => {
  let index = preferredIndex;
  let candidate = buildSectionSlideId(sectionId || 'section', index);
  while (usedIds.has(candidate)) {
    index += 1;
    candidate = buildSectionSlideId(sectionId || 'section', index);
  }
  usedIds.add(candidate);
  return candidate;
};

const ensureSectionSlideIds = (section) => {
  if (!section || !Array.isArray(section.blocks) || section.blocks.length === 0) {
    return { updated: false, blocks: section?.blocks || [] };
  }

  const blocks = section.blocks;
  const nextBlocks = [...blocks];
  const assignedIds = new Set();
  const usedIds = collectSlideIds(blocks);
  let updated = false;
  let currentBlocks = [];
  let currentIndices = [];
  let nextSlideId = null;
  let lastDividerIndex = null;
  let segmentIndex = 0;

  const ensureBlockSlideId = (block, slideId) => {
    let nextBlock = block;
    let changed = false;
    if (block?.slideId !== slideId) {
      nextBlock = { ...nextBlock, slideId };
      changed = true;
    }
    if (block?.content && typeof block.content === 'object' && !Array.isArray(block.content)) {
      if (block.content.slideId !== slideId) {
        nextBlock = { ...nextBlock, content: { ...nextBlock.content, slideId } };
        changed = true;
      }
    }
    return { nextBlock, changed };
  };

  const ensureDividerSlideId = (dividerIndex, slideId) => {
    if (dividerIndex === null || dividerIndex === undefined) return;
    const divider = nextBlocks[dividerIndex];
    if (!divider || divider.type !== 'divider' || !divider.content?.isSlideBreak) return;
    const needsContentUpdate = divider.content?.slideId !== slideId;
    const needsSlideUpdate = divider.slideId !== slideId;
    if (!needsContentUpdate && !needsSlideUpdate) return;
    const updatedDivider = {
      ...divider,
      slideId,
      content: { ...divider.content, slideId }
    };
    nextBlocks[dividerIndex] = updatedDivider;
    updated = true;
  };

  const finalizeSegment = (forceEmpty = false) => {
    if (!currentBlocks.length && !forceEmpty) return;
    const preferredId = nextSlideId || buildSectionSlideId(section.id || 'section', segmentIndex);
    let resolvedId = preferredId;
    if (assignedIds.has(resolvedId)) {
      resolvedId = resolveNextAvailableSlideId(section.id, usedIds, segmentIndex);
    } else {
      assignedIds.add(resolvedId);
    }
    if (!assignedIds.has(resolvedId)) assignedIds.add(resolvedId);
    if (!usedIds.has(resolvedId)) usedIds.add(resolvedId);

    currentBlocks.forEach((block, idx) => {
      const blockIndex = currentIndices[idx];
      const { nextBlock, changed } = ensureBlockSlideId(block, resolvedId);
      if (changed) {
        nextBlocks[blockIndex] = nextBlock;
        updated = true;
      }
    });

    ensureDividerSlideId(lastDividerIndex, resolvedId);

    currentBlocks = [];
    currentIndices = [];
    nextSlideId = null;
    lastDividerIndex = null;
    segmentIndex += 1;
  };

  blocks.forEach((block, index) => {
    const isSlideBreak = block?.type === 'divider' && block?.content?.isSlideBreak === true;
    if (isSlideBreak) {
      if (currentBlocks.length > 0) {
        finalizeSegment();
      } else if (nextSlideId !== null) {
        finalizeSegment(true);
      }
      nextSlideId = block.slideId || block.content?.slideId || null;
      lastDividerIndex = index;
      return;
    }

    if (!currentBlocks.length && nextSlideId === null && (block?.slideId || block?.content?.slideId)) {
      nextSlideId = block.slideId || block.content?.slideId;
    }

    currentBlocks.push(block);
    currentIndices.push(index);
  });

  if (currentBlocks.length > 0) {
    finalizeSegment();
  } else if (nextSlideId !== null) {
    finalizeSegment(true);
  }

  return { updated, blocks: nextBlocks };
};

const resolveSlideIdFromBlocks = (blocks, fallbackId) => {
  if (!Array.isArray(blocks)) return fallbackId;
  for (const block of blocks) {
    if (block?.slideId) return block.slideId;
    if (block?.content?.slideId) return block.content.slideId;
  }
  return fallbackId;
};

const flattenSlidesToBlocks = (slides = [], sectionIdForFallback = '') => {
  if (!Array.isArray(slides)) return [];
  return slides.reduce((acc, slide, index) => {
    const blocks = Array.isArray(slide) ? slide : slide?.blocks || [];
    const inferredId = !Array.isArray(slide)
      ? slide?.slideId
      : resolveSlideIdFromBlocks(blocks, null);
    const fallbackId = sectionIdForFallback
      ? buildSectionSlideId(sectionIdForFallback, index)
      : `slide-${index}`;
    const slideId = inferredId || fallbackId;
    const taggedBlocks = blocks.map((block) => (
      block?.slideId === slideId
        ? block
        : { ...block, slideId }
    ));

    if (index > 0) {
      acc.push(createSlideDivider(slideId));
    }
    acc.push(...taggedBlocks);
    return acc;
  }, []);
};

const buildSlidesFromJson = ({
  slides: explicitSlides,
  sections = [],
  titleCard,
  endTitleCard,
  includeEmptySections = false,
  sectionNumberMap: providedSectionNumberMap
}) => {
  const sectionList = Array.isArray(sections) ? sections : Object.values(sections || {});
  const orderedSections = sectionList;
  const sectionNumberMap = providedSectionNumberMap || buildSectionNumberMap(sectionList);
  const normalized = [];
  if (Array.isArray(explicitSlides) && explicitSlides.length > 0) {
    explicitSlides.forEach((slide, index) => {
      const fallbackId = slide.section?.id && Number.isFinite(slide.slideIndex)
        ? buildSectionSlideId(slide.section.id, slide.slideIndex)
        : `slide-${index}`;
      const slideId = slide.slideId || slide.id || fallbackId;
      const sectionId = slide.section?.id;
      const sectionNumber = sectionId ? sectionNumberMap[sectionId] : undefined;
      const sectionWithNumber = sectionId
        ? { ...slide.section, sectionNumber }
        : slide.section;

      normalized.push({
        ...slide,
        section: sectionWithNumber,
        slideId,
        slideIndex: slide.slideIndex ?? index,
        slideOrderIndex: index
      });
    });
    const filtered = includeEmptySections ? normalized : normalized.filter((slide) => !isEmptySlide(slide));
    if (!includeEmptySections) {
      console.log(`Built ${filtered.length} slides from JSON`);
      if (filtered.some(isEmptySlide)) {
        console.warn('Empty slides detected after normalization.');
      }
    }
    return filtered;
  }

  if (titleCard?.enabled) {
    const titleSlide = {
      type: 'title-card',
      slideId: 'title-card',
      title: titleCard.title,
      subtitle: titleCard.subtitle,
      image: titleCard.image,
      imageSize: titleCard.imageSize,
      imageCitation: titleCard.imageCitation || '',
      showCourseInfo: titleCard.showCourseInfo !== false,
      showLogoOnTitle: titleCard.showLogoOnTitle !== false,
      showTextOverlay: titleCard.showTextOverlay === true,
      textOverlayOpacity: titleCard.textOverlayOpacity ?? DEFAULT_TITLE_CARD_SETTINGS.textOverlayOpacity,
      textPlacement: titleCard.textPlacement || DEFAULT_TITLE_CARD_SETTINGS.textPlacement,
      backgroundColor: titleCard.backgroundColor || '#545f7d',
      textColor: titleCard.textColor || DEFAULT_TITLE_CARD_SETTINGS.textColor,
      metaTextColor: titleCard.metaTextColor || '',
      titlePosition: titleCard.titlePosition || 'center',
      backgroundImage: titleCard.backgroundImage || '',
      titleFontSize: titleCard.titleFontSize ?? DEFAULT_TITLE_CARD_SETTINGS.titleFontSize,
      subtitleFontSize: titleCard.subtitleFontSize ?? DEFAULT_TITLE_CARD_SETTINGS.subtitleFontSize,
      metaFontSize: titleCard.metaFontSize ?? DEFAULT_TITLE_CARD_SETTINGS.metaFontSize,
      instructorLayout: titleCard.instructorLayout || DEFAULT_TITLE_CARD_SETTINGS.instructorLayout,
      cornerLogo: titleCard.cornerLogo || '',
      cornerLogoPosition: titleCard.cornerLogoPosition || DEFAULT_TITLE_CARD_SETTINGS.cornerLogoPosition,
      cornerLogoSize: titleCard.cornerLogoSize ?? DEFAULT_TITLE_CARD_SETTINGS.cornerLogoSize
    };
    if (!isEmptySlide(titleSlide)) {
      normalized.push(titleSlide);
    }
  }

  orderedSections.forEach((section) => {
    const sectionSlides = splitSectionSlides(section, { includeEmpty: includeEmptySections, includeMeta: true });
    const processedSlides = sectionSlides
      .map(({ slideId, blocks }) => {
        const filteredBlocks = includeEmptySections ? blocks : blocks.filter((block) => !isEmptyBlock(block));
        return { slideId, blocks: filteredBlocks };
      })
      .filter(({ blocks }) => includeEmptySections ? true : blocks.length > 0);

    const sectionNumber = sectionNumberMap[section.id];
    const sectionWithNumber = sectionNumber
      ? { ...section, sectionNumber }
      : section;
    const rawSectionTitleCard = section.sectionTitleCard;
    if (rawSectionTitleCard?.enabled) {
      const sectionTitleCard = normalizeTitleCardSettings(
        rawSectionTitleCard,
        DEFAULT_SECTION_TITLE_CARD_SETTINGS
      );
      const sectionTitleSlide = {
        type: 'section-title-card',
        slideId: sectionTitleCard.slideId || buildSectionTitleCardId(section.id),
        section: sectionWithNumber,
        title: sectionTitleCard.title,
        subtitle: sectionTitleCard.subtitle,
        image: sectionTitleCard.image,
        imageSize: sectionTitleCard.imageSize,
        imageCitation: sectionTitleCard.imageCitation || '',
        showCourseInfo: sectionTitleCard.showCourseInfo !== false,
        showLogoOnTitle: sectionTitleCard.showLogoOnTitle !== false,
        showTextOverlay: sectionTitleCard.showTextOverlay === true,
        textOverlayOpacity: sectionTitleCard.textOverlayOpacity ?? DEFAULT_SECTION_TITLE_CARD_SETTINGS.textOverlayOpacity,
        textPlacement: sectionTitleCard.textPlacement || DEFAULT_SECTION_TITLE_CARD_SETTINGS.textPlacement,
        backgroundColor: sectionTitleCard.backgroundColor || DEFAULT_SECTION_TITLE_CARD_SETTINGS.backgroundColor,
        textColor: sectionTitleCard.textColor || DEFAULT_SECTION_TITLE_CARD_SETTINGS.textColor,
        metaTextColor: sectionTitleCard.metaTextColor || '',
        titlePosition: sectionTitleCard.titlePosition || DEFAULT_SECTION_TITLE_CARD_SETTINGS.titlePosition,
        backgroundImage: sectionTitleCard.backgroundImage || '',
        titleFontSize: sectionTitleCard.titleFontSize ?? DEFAULT_SECTION_TITLE_CARD_SETTINGS.titleFontSize,
        subtitleFontSize: sectionTitleCard.subtitleFontSize ?? DEFAULT_SECTION_TITLE_CARD_SETTINGS.subtitleFontSize,
        metaFontSize: sectionTitleCard.metaFontSize ?? DEFAULT_SECTION_TITLE_CARD_SETTINGS.metaFontSize,
        instructorLayout: sectionTitleCard.instructorLayout || DEFAULT_SECTION_TITLE_CARD_SETTINGS.instructorLayout,
        cornerLogo: sectionTitleCard.cornerLogo || '',
        cornerLogoPosition: sectionTitleCard.cornerLogoPosition || DEFAULT_SECTION_TITLE_CARD_SETTINGS.cornerLogoPosition,
        cornerLogoSize: sectionTitleCard.cornerLogoSize ?? DEFAULT_SECTION_TITLE_CARD_SETTINGS.cornerLogoSize
      };
      if (!isEmptySlide(sectionTitleSlide)) {
        normalized.push(sectionTitleSlide);
      }
    }

    if (!processedSlides.length) {
      if (!includeEmptySections) {
        console.warn('Section contains no content and was skipped:', section.id);
      }
      return;
    }

    processedSlides.forEach(({ slideId: rawSlideId, blocks: slideBlocks }, index) => {
      normalized.push({
        type: 'section',
        section: sectionWithNumber,
        blocks: slideBlocks,
        slideIndex: index,
        slidesInSection: processedSlides.length,
        slideId: rawSlideId || buildSectionSlideId(section.id, index)
      });
    });
  });

  if (endTitleCard?.enabled) {
    const endTitleSlide = {
      type: 'end-title-card',
      slideId: 'end-title-card',
      title: endTitleCard.title,
      subtitle: endTitleCard.subtitle,
      image: endTitleCard.image,
      imageSize: endTitleCard.imageSize,
      imageCitation: endTitleCard.imageCitation || '',
      showCourseInfo: endTitleCard.showCourseInfo !== false,
      showLogoOnTitle: endTitleCard.showLogoOnTitle !== false,
      showTextOverlay: endTitleCard.showTextOverlay === true,
      textOverlayOpacity: endTitleCard.textOverlayOpacity ?? DEFAULT_END_TITLE_CARD_SETTINGS.textOverlayOpacity,
      textPlacement: endTitleCard.textPlacement || DEFAULT_END_TITLE_CARD_SETTINGS.textPlacement,
      backgroundColor: endTitleCard.backgroundColor || DEFAULT_END_TITLE_CARD_SETTINGS.backgroundColor,
      textColor: endTitleCard.textColor || DEFAULT_END_TITLE_CARD_SETTINGS.textColor,
      metaTextColor: endTitleCard.metaTextColor || '',
      titlePosition: endTitleCard.titlePosition || DEFAULT_END_TITLE_CARD_SETTINGS.titlePosition,
      backgroundImage: endTitleCard.backgroundImage || '',
      titleFontSize: endTitleCard.titleFontSize ?? DEFAULT_END_TITLE_CARD_SETTINGS.titleFontSize,
      subtitleFontSize: endTitleCard.subtitleFontSize ?? DEFAULT_END_TITLE_CARD_SETTINGS.subtitleFontSize,
      metaFontSize: endTitleCard.metaFontSize ?? DEFAULT_END_TITLE_CARD_SETTINGS.metaFontSize,
      instructorLayout: endTitleCard.instructorLayout || DEFAULT_END_TITLE_CARD_SETTINGS.instructorLayout,
      cornerLogo: endTitleCard.cornerLogo || '',
      cornerLogoPosition: endTitleCard.cornerLogoPosition || DEFAULT_END_TITLE_CARD_SETTINGS.cornerLogoPosition,
      cornerLogoSize: endTitleCard.cornerLogoSize ?? DEFAULT_END_TITLE_CARD_SETTINGS.cornerLogoSize
    };
    if (!isEmptySlide(endTitleSlide)) {
      normalized.push(endTitleSlide);
    }
  }

  const withIds = normalized.map((slide, index) => ({
    ...slide,
    slideId: slide.slideId || `slide-${index}`,
    slideOrderIndex: index
  }));

  const filtered = includeEmptySections ? withIds : withIds.filter((slide) => !isEmptySlide(slide));
  if (!includeEmptySections) {
    console.log(`Built ${filtered.length} slides from JSON`);
    if (filtered.some(isEmptySlide)) {
      console.warn('Empty slides detected after normalization.');
    }
  }
  return filtered;
};

const renumberSlides = (slides = [], sectionNumberMap = {}) => {
  const sectionsToSlides = {};

  slides.forEach((slide) => {
    if (slide.type !== 'section' || !slide.section?.id) return;
    const sectionId = slide.section.id;
    if (!sectionsToSlides[sectionId]) {
      sectionsToSlides[sectionId] = [];
    }
    sectionsToSlides[sectionId].push(slide);
  });

  return slides.map((slide) => {
    if (slide.type !== 'section' || !slide.section?.id) return slide;
    const sectionId = slide.section.id;
    const sectionSlides = sectionsToSlides[sectionId] || [];
    const slideIndex = Math.max(sectionSlides.indexOf(slide), 0);
    const slidesInSection = sectionSlides.length || slide.slidesInSection || 1;
    const sectionNumber = sectionNumberMap[sectionId] ?? slide.section.sectionNumber;

    return {
      ...slide,
      slideIndex,
      slidesInSection,
      section: sectionNumber ? { ...slide.section, sectionNumber } : slide.section
    };
  });
};

const buildNavItemsFromSlides = (slides, sectionNumberMap = {}) => {
  const items = [];
  const seenSections = new Set();
  slides.forEach((slide, index) => {
    if (slide.type === 'title-card') {
      if (!items.find((item) => item.type === 'title-card')) {
        items.push({ type: 'title-card', id: 'title-card', label: 'Title', slideIndex: index });
      }
      return;
    }
    if (slide.type === 'end-title-card') {
      if (!items.find((item) => item.type === 'end-title-card')) {
        items.push({ type: 'end-title-card', id: 'end-title-card', label: 'End Title', slideIndex: index });
      }
      return;
    }
    if ((slide.type === 'section' || slide.type === 'section-title-card') && slide.section?.id && !seenSections.has(slide.section.id)) {
      seenSections.add(slide.section.id);
      items.push({
        type: 'section',
        id: slide.section.id,
        label: getSectionHeaderLabel(slide.section, sectionNumberMap),
        slideIndex: index
      });
    }
  });
  return items;
};

const buildNavItemsFromSections = (sections, titleCardSettings, endTitleCardSettings, sectionNumberMap = {}) => {
  const items = [];

  if (titleCardSettings?.enabled) {
    items.push({ type: 'title-card', id: 'title-card', label: 'Title', slideIndex: 0 });
  }

  (sections || []).forEach((section) => {
    items.push({
      type: 'section',
      id: section.id,
      label: getSectionHeaderLabel(section, sectionNumberMap),
      slideIndex: -1
    });
  });

  if (endTitleCardSettings?.enabled) {
    items.push({ type: 'end-title-card', id: 'end-title-card', label: 'End Title', slideIndex: -1 });
  }

  return items;
};

const SectionBlocks = ({ section, blocksOverride, onUpdate, isEditMode, onBlockEdit, htmlModes, toggleHtmlMode, onAddBlockBelow, onAddBlockAtTop, handleWorksheetJsonImport, handleWorksheetJsonPaste, onPresetImageRequest, onPresetVideoRequest, autoApaCitations }) => {
  const [draggedBlock, setDraggedBlock] = useState(null);
  const blocks = blocksOverride || section.blocks || [];

  const handleBlockDragStart = (e, blockId) => {
    setDraggedBlock(blockId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleBlockDrop = (e, targetIndex) => {
    e.preventDefault();
    if (draggedBlock === null) return;

    const sourceIndex = blocks.findIndex(b => b.id === draggedBlock);
    if (sourceIndex === -1 || sourceIndex === targetIndex) return;

    const newBlocks = [...blocks];
    const [movedBlock] = newBlocks.splice(sourceIndex, 1);
    newBlocks.splice(targetIndex, 0, movedBlock);

    onUpdate({ ...section, blocks: newBlocks });
    setDraggedBlock(null);
  };

  const handleBlockUpdate = (updatedBlock) => {
    const newBlocks = blocks.map(b => (b.id === updatedBlock.id ? updatedBlock : b));
    onUpdate({ ...section, blocks: newBlocks });
  };

  const handleBlockEdit = (blockId) => {
    const blockToEdit = blocks.find(b => b.id === blockId);
    if (blockToEdit) {
      onBlockEdit(blockToEdit, section.id);
    }
  };

  const handleBlockDelete = (blockId) => {
    if (window.confirm('Are you sure you want to delete this content?')) {
      const newBlocks = blocks.filter(b => b.id !== blockId);
      onUpdate({ ...section, blocks: newBlocks });
    }
  };

  const handleBlockMove = (blockId, direction) => {
    const index = blocks.findIndex(b => b.id === blockId);
    if (index === -1) return;

    const newBlocks = [...blocks];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;

    if (targetIndex < 0 || targetIndex >= newBlocks.length) return;

    // Swap elements
    [newBlocks[index], newBlocks[targetIndex]] = [newBlocks[targetIndex], newBlocks[index]];

    onUpdate({ ...section, blocks: newBlocks });
  };

  return (
    <div className="p-8">
      {isEditMode && blocks.length > 0 && (
        <div className="mb-6 flex justify-center no-print sticky top-0 z-10">
          <AddBlockMenu
            onAddContent={(type) => onAddBlockAtTop(section.id, type)}
            label="Add Block to Top"
            variant="top"
          />
        </div>
      )}

      {blocks.map((block, index) => (
        <ContentBlock
          key={block.id}
          block={block}
          isEditMode={isEditMode}
          onEdit={() => handleBlockEdit(block.id)}
          onDelete={() => handleBlockDelete(block.id)}
          onBlockUpdate={handleBlockUpdate}
          onDragStart={(e) => handleBlockDragStart(e, block.id)}
          onDrop={(e) => handleBlockDrop(e, index)}
          onDragOver={(e) => e.preventDefault()}
          onMoveUp={() => handleBlockMove(block.id, 'up')}
          onMoveDown={() => handleBlockMove(block.id, 'down')}
          isFirst={index === 0}
          isLast={index === blocks.length - 1}
          htmlModes={htmlModes}
          toggleHtmlMode={toggleHtmlMode}
          onAddBlockBelow={onAddBlockBelow}
          sectionId={section.id}
          handleWorksheetJsonImport={handleWorksheetJsonImport}
          handleWorksheetJsonPaste={handleWorksheetJsonPaste}
          onPresetImageRequest={onPresetImageRequest}
          onPresetVideoRequest={onPresetVideoRequest}
          autoApaCitations={autoApaCitations}
        />
      ))}

    </div>
  );
};

// Section Component
const Section = ({ section, onUpdate, isEditMode, onAddContent, onDeleteSection, onBlockEdit, isOpen, onToggle, htmlModes, toggleHtmlMode, onAddBlockBelow, onAddBlockAtTop, handleWorksheetJsonImport, handleWorksheetJsonPaste, onPresetImageRequest, onPresetVideoRequest, sectionNumberMap, autoApaCitations }) => {
  const sectionColors = {
    'bridge-in': { bg: 'bg-red-500', hover: 'hover:bg-red-600' },
    'outcomes': { bg: 'bg-emerald-500', hover: 'hover:bg-emerald-600' },
    'pre-assessment': { bg: 'bg-amber-500', hover: 'hover:bg-amber-600' },
    'participatory-learning': { bg: 'bg-blue-500', hover: 'hover:bg-blue-600' },
    'post-assessment': { bg: 'bg-purple-500', hover: 'hover:bg-purple-600' },
    'summary': { bg: 'bg-indigo-500', hover: 'hover:bg-indigo-600' },
    'resources': { bg: 'bg-gray-600', hover: 'hover:bg-gray-700' }
  };

  const colorConfig = sectionColors[section.id] || { bg: 'bg-slate-600', hover: 'hover:bg-slate-700' };

  const headerLabel = getSectionHeaderLabel(section, sectionNumberMap);

  return (
    <div
      id={section.id}
      className="bg-white rounded-2xl shadow-sm border border-gray-200 mb-8 overflow-hidden transition-all duration-200 hover:shadow-md print-break-inside-avoid"
    >
      <div
        className={`${colorConfig.bg} ${colorConfig.hover} text-white px-8 py-6 cursor-pointer flex justify-between items-center relative overflow-hidden transition-colors no-print`}
        onClick={onToggle}
      >
        <h2 className="text-xl font-semibold text-white">
          {headerLabel}
        </h2>

        <div className="flex items-center gap-3">
          {isEditMode &&
            section.id !== 'overview' &&
            section.id !== 'resources' && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (
                    window.confirm(
                      `Are you sure you want to delete the entire "${section.title}" section?`
                    )
                  ) {
                    onDeleteSection(section.id);
                  }
                }}
                className="w-8 h-8 bg-red-500 text-white rounded-lg flex items-center justify-center hover:bg-red-600 transition-colors"
              >
                <Trash2 size={16} />
              </button>
            )}
          <div className={`transition-transform duration-300 ${isOpen ? 'rotate-90' : ''}`}>
            <ChevronRight size={20} />
          </div>
        </div>
      </div>

      <div className={`accordion-content-wrapper ${isOpen ? 'is-open' : ''}`}>
        {/* This inner div is essential for the grid animation to work correctly */}
        <div>
          <SectionBlocks
            section={section}
            onUpdate={onUpdate}
            isEditMode={isEditMode}
            onAddContent={onAddContent}
            onBlockEdit={onBlockEdit}
            htmlModes={htmlModes}
            toggleHtmlMode={toggleHtmlMode}
            onAddBlockBelow={onAddBlockBelow}
            onAddBlockAtTop={onAddBlockAtTop}
            handleWorksheetJsonImport={handleWorksheetJsonImport}
            handleWorksheetJsonPaste={handleWorksheetJsonPaste}
            onPresetImageRequest={onPresetImageRequest}
            onPresetVideoRequest={onPresetVideoRequest}
            autoApaCitations={autoApaCitations}
          />
        </div>
      </div>
    </div>
  );
};

const SlideNav = ({ currentIndex, totalSlides, onPrev, onNext, onJump, placement = 'bottom', showJump = true }) => (
  <div className={`${placement === 'top' ? 'mb-6' : 'mt-6'} flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between`}>
    <div className="flex items-center gap-3">
      <button
        type="button"
        onClick={onPrev}
        disabled={currentIndex === 0}
        className="px-4 py-2 rounded-lg border border-slate-500 bg-slate-700 text-slate-100 font-semibold disabled:opacity-50 disabled:text-slate-400 disabled:border-slate-600 disabled:cursor-not-allowed hover:bg-slate-600 hover:border-slate-400 transition-colors"
      >
        Previous
      </button>
      <button
        type="button"
        onClick={onNext}
        disabled={currentIndex >= totalSlides - 1}
        className="px-4 py-2 rounded-lg border border-slate-500 bg-slate-700 text-slate-100 font-semibold disabled:opacity-50 disabled:text-slate-400 disabled:border-slate-600 disabled:cursor-not-allowed hover:bg-slate-600 hover:border-slate-400 transition-colors"
      >
        Next
      </button>
    </div>
    <div className="flex items-center gap-4">
      <span className="text-sm text-gray-500 font-medium">
        {currentIndex + 1} / {totalSlides}
      </span>
      {showJump && (
        <label className="text-sm text-gray-500 flex items-center gap-2">
          Jump to slide
          <select
            value={currentIndex}
            onChange={(e) => onJump(Number(e.target.value))}
            className="p-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-slate-500 focus:border-transparent"
          >
            {Array.from({ length: totalSlides }, (_, index) => (
              <option key={index} value={index}>
                {index + 1}
              </option>
            ))}
          </select>
        </label>
      )}
    </div>
  </div>
);

const SlideDeckNameBadge = ({ value, onChange, isEditMode, className = '', positionClass = 'left-6 bottom-4' }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');
  const canEdit = Boolean(onChange);

  useEffect(() => {
    setDraft(value || '');
  }, [value]);

  const handleSave = () => {
    const trimmed = draft.trim();
    onChange?.(trimmed);
    setIsEditing(false);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleSave();
    } else if (event.key === 'Escape') {
      setDraft(value || '');
      setIsEditing(false);
    }
  };

  const badgeTextColor = '#6b7280';
  const badgeStyle = {
    color: badgeTextColor,
    borderColor: `${badgeTextColor}4d`
  };

  const label = value?.trim() || (isEditMode ? 'Click to name this deck' : '');

  if (!isEditing && !label && !isEditMode) {
    return null;
  }

  const badgeBaseClass = `absolute ${positionClass} z-10 ${className}`;

  if (isEditing && canEdit) {
    return (
      <div className={badgeBaseClass}>
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={handleSave}
          onKeyDown={handleKeyDown}
          className="slide-deck-name-badge__control px-3 py-1.5 rounded-full border bg-white/95 shadow-sm text-sm font-semibold text-gray-600 placeholder:text-gray-400 focus:ring-2 focus:ring-slate-600 focus:border-transparent"
          style={badgeStyle}
          placeholder="Add slide deck name"
          autoFocus
        />
      </div>
    );
  }

  return (
    <div className={badgeBaseClass}>
      <button
        type="button"
        onClick={() => canEdit && setIsEditing(true)}
        className={`slide-deck-name-badge__control px-3 py-1.5 rounded-full border bg-white/90 shadow-sm text-sm font-semibold text-gray-600 transition ${canEdit ? 'hover:shadow' : 'cursor-default'}`}
        style={badgeStyle}
      >
        {label}
      </button>
    </div>
  );
};

// Optional slide number, bottom-right of every slide. Deliberately plain —
// the figure and nothing else — and sized in cqw like the logo badge, so it
// stays in proportion whether the slide is a thumbnail in the editor or fills
// a projector. A slide left out of the deck gets no number, which is what
// keeps the run the audience sees unbroken: 1, 2, 3 with no gaps.
const SlideNumberBadge = ({ number, variant = 'content' }) => {
  if (!Number.isFinite(number)) return null;
  return (
    <div
      className={`slide-number-badge slide-number-badge--${variant}`}
      aria-hidden="true"
    >
      {number}
    </div>
  );
};

const TitleCardSlide = ({
  titleCard,
  headerData = {},
  isEditMode,
  onCustomize,
  isSlideEnabled,
  onToggleSlideEnabled,
  onImageInsert,
  slideDeckName,
  onSlideDeckNameChange,
  variant = 'start',
  slideId = 'title-card',
  showDeckNameBadge = true,
  slideNumber = null,
  titleCardBackground = null,
  onHeaderDataChange
}) => {
  const { logo, logoSize } = useLogo();
  const imageInputRef = useRef(null);
  const textColor = titleCard.textColor || getContrastingTextColor(titleCard.backgroundColor || '#545f7d');
  // Lower-right course/instructor text can use its own color; falls back to
  // the main title color when the user has not set a separate one.
  const metaTextColor = titleCard.metaTextColor || textColor;
  const backgroundColor = titleCard.backgroundColor || '#545f7d';
  const [isCourseInfoModalOpen, setIsCourseInfoModalOpen] = useState(false);
  const textPlacement = titleCard.textPlacement || 'below';
  const showCourseInfo = titleCard.showCourseInfo !== false;
  const showLogoOnTitle = titleCard.showLogoOnTitle !== false;
  // Per-item font sizes (px) and the instructor block's layout.
  const titleFontSize = Number(titleCard.titleFontSize) || 48;
  const subtitleFontSize = Number(titleCard.subtitleFontSize) || 20;
  const metaFontSize = Number(titleCard.metaFontSize) || 16;
  const instructorRow = titleCard.instructorLayout === 'row';
  // The corner mark. Its width is authored against the 1280px slide and drawn
  // in --title-card-px, so it grows and shrinks with the card like everything
  // else on it rather than staying one fixed size in every view.
  const cornerLogo = titleCard.cornerLogo || '';
  const cornerLogoPosition = titleCard.cornerLogoPosition === 'top-left' ? 'top-left' : 'top-right';
  const cornerLogoSize = Number(titleCard.cornerLogoSize) || 120;

  // Build title card styles - use background image if provided, otherwise use color
  //
  // The per-item sizes travel as custom properties rather than inline
  // font-size values. The card is a fluid 16:9 box, so the stylesheet turns
  // each authored pixel into a share of the card's width (see --title-card-px):
  // at a phone's width, fixed pixels made a 48px title and 64px of padding
  // collide inside a 240px-tall card.
  const titleCardStyle = useMemo(() => {
    return {
      color: textColor,
      '--title-card-text-color': textColor,
      '--title-card-title-px': titleFontSize,
      '--title-card-subtitle-px': subtitleFontSize,
      '--title-card-meta-px': metaFontSize,
      ...resolveTitleCardBackgroundStyles({
        cardBackgroundImage: titleCard.backgroundImage,
        deckTitleCardBackground: titleCardBackground,
        fallbackColor: backgroundColor
      })
    };
  }, [
    titleCard.backgroundImage,
    titleCardBackground,
    backgroundColor,
    textColor,
    titleFontSize,
    subtitleFontSize,
    metaFontSize
  ]);
  const courseInfo = headerData.courseTopic?.trim();
  const instructorName = headerData.instructorName?.trim();
  const instructorEmail = headerData.instructorEmail?.trim();
  const hasCourseInfo = Boolean(courseInfo);
  const hasInstructorInfo = Boolean(instructorName || instructorEmail);
  const hasLogo = Boolean(logo);
  const shouldShowMeta = (showLogoOnTitle && hasLogo) || (showCourseInfo && (hasCourseInfo || hasInstructorInfo));

  const handleTitleCardImageUpload = (event) => {
    const file = event.target.files?.[0];
    if (file && onImageInsert) {
      onImageInsert(file, true, variant);
    }
    event.target.value = '';
  };

  const showTextOverlay = titleCard.showTextOverlay === true;
  const textOverlayOpacity = titleCard.textOverlayOpacity ?? 0.5;
  const imageCitation = titleCard.imageCitation?.trim();

  // Overlay styles with dynamic opacity - use white for frosted glass effect
  const overlayStyle = {
    backgroundColor: `rgba(255, 255, 255, ${textOverlayOpacity})`
  };

  const renderTitleCardImage = () => (
    <div className="title-card-image-group">
      <img
        src={titleCard.image}
        alt=""
        className="title-card-image"
        style={{ '--title-card-image-px': titleCard.imageSize || 120 }}
      />
      {imageCitation && (
        <p className="title-card-image-citation">{imageCitation}</p>
      )}
    </div>
  );

  const titleDoubleClick = isEditMode && onCustomize
    ? (e) => { e.stopPropagation(); onCustomize(); }
    : undefined;
  const titleContent = (
    <>
      <h2
        className="title-card-title"
        onDoubleClick={titleDoubleClick}
        title={isEditMode ? 'Double-click to edit the title' : undefined}
        style={isEditMode ? { cursor: 'text' } : undefined}
      >
        {titleCard.title}
      </h2>
      {titleCard.subtitle && (
        <p
          className="title-card-subtitle"
          onDoubleClick={titleDoubleClick}
          title={isEditMode ? 'Double-click to edit the subtitle' : undefined}
          style={isEditMode ? { cursor: 'text' } : undefined}
        >
          {titleCard.subtitle}
        </p>
      )}
    </>
  );
  const isEndCard = variant === 'end';
  const isSectionCard = variant === 'section';
  const customizeLabel = isEndCard
    ? 'Customize End Title Card'
    : isSectionCard
      ? 'Customize Section Title Slide'
      : 'Customize Title Card';
  const includeLabel = isEndCard
    ? 'Include end title slide in export'
    : 'Include in export';
  const helperCopy = isEndCard
    ? 'Customize the closing title and image anytime in the End Title Card options.'
    : isSectionCard
      ? 'Customize the section title slide anytime to introduce the next section.'
      : 'Customize the title and image anytime in the Customize Title Card options.';
  return (
    <div className="space-y-2">
      {isCourseInfoModalOpen && onHeaderDataChange && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[120] p-4"
          onClick={() => setIsCourseInfoModalOpen(false)}
        >
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold text-gray-900">Course &amp; Instructor Info</h3>
              <button onClick={() => setIsCourseInfoModalOpen(false)} className="text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Course topic</label>
                <input
                  type="text"
                  value={headerData.courseTopic || ''}
                  onChange={(e) => onHeaderDataChange('courseTopic', e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-slate-900 focus:ring-2 focus:ring-blue-500"
                  placeholder="Course topic"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Instructor name</label>
                <input
                  type="text"
                  value={headerData.instructorName || ''}
                  onChange={(e) => onHeaderDataChange('instructorName', e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-slate-900 focus:ring-2 focus:ring-blue-500"
                  placeholder="Instructor name"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Instructor email</label>
                <input
                  type="text"
                  value={headerData.instructorEmail || ''}
                  onChange={(e) => onHeaderDataChange('instructorEmail', e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-slate-900 focus:ring-2 focus:ring-blue-500"
                  placeholder="name@institution.edu"
                />
              </div>
              <p className="text-xs text-gray-500">These fields also appear in the top header bar and all exports.</p>
            </div>
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setIsCourseInfoModalOpen(false)}
                className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
      {isEditMode && (
        <p className="text-xs text-gray-500 text-center">
          {helperCopy}
        </p>
      )}
      <div className="slide-card overflow-hidden rounded-3xl shadow-lg border border-gray-200 relative">
        <div
          className={`title-card title-card--${titleCard.titlePosition || 'center'} relative pb-24`}
          style={titleCardStyle}
        >
          {/* The corner mark, before everything else so it never joins the
              centred stack: it is pinned to a corner, not laid out with the
              title. */}
          {cornerLogo && (
            <img
              src={cornerLogo}
              alt=""
              className={`title-card-corner-logo title-card-corner-logo--${cornerLogoPosition}`}
              style={{ '--title-card-corner-logo-px': cornerLogoSize }}
            />
          )}
          {/* Main content - optionally wrapped in overlay */}
          {showTextOverlay ? (
            <div className="title-card-text-overlay" style={overlayStyle}>
              {textPlacement === 'above' && titleContent}
              {titleCard.image && (
                renderTitleCardImage()
              )}
              {textPlacement === 'below' && titleContent}
            </div>
          ) : (
            <>
              {textPlacement === 'above' && titleContent}
              {titleCard.image && (
                renderTitleCardImage()
              )}
              {textPlacement === 'below' && titleContent}
            </>
          )}
          {/* Meta section - optionally wrapped in overlay */}
          {shouldShowMeta && (
            <div
              className={`title-card-meta ${showTextOverlay ? 'title-card-meta-overlay' : ''}`}
              style={showTextOverlay ? overlayStyle : undefined}
            >
              <div className="title-card-meta-left">
                {showLogoOnTitle && hasLogo && (
                  <img
                    src={logo}
                    alt="School Logo"
                    className="title-card-meta-logo"
                    style={{ '--title-card-logo-px': logoSize || 80 }}
                  />
                )}
              </div>
              {showCourseInfo && (hasCourseInfo || hasInstructorInfo) && (
                <div
                  className={`title-card-meta-right ${instructorRow ? 'title-card-meta-right--row' : ''}`}
                  style={{ color: metaTextColor, ...(isEditMode && onHeaderDataChange ? { cursor: 'text' } : {}) }}
                  onDoubleClick={isEditMode && onHeaderDataChange
                    ? (e) => { e.stopPropagation(); setIsCourseInfoModalOpen(true); }
                    : undefined}
                  title={isEditMode && onHeaderDataChange ? 'Double-click to edit course & instructor info' : undefined}
                >
                  {hasCourseInfo && <p className="title-card-meta-course">{courseInfo}</p>}
                  {hasInstructorInfo && (
                    <>
                      {instructorName && <p className="title-card-meta-instructor">{instructorName}</p>}
                      {instructorEmail && <p className="title-card-meta-email">{instructorEmail}</p>}
                    </>
                  )}
                </div>
              )}
            </div>
          )}
          {showDeckNameBadge && (
            <SlideDeckNameBadge
              value={slideDeckName}
              onChange={onSlideDeckNameChange}
              isEditMode={isEditMode}
              className="pointer-events-auto"
            />
          )}
          {/* A title card can carry any background the author likes, so the
              number follows the card's own text colour rather than the black
              used on content slides — a black figure on a navy card is not a
              number, it is a smudge. */}
          <SlideNumberBadge number={slideNumber} variant="title" />
        </div>
        {isEditMode && (
          <>
            {!titleCard.image && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <button
                  type="button"
                  onClick={() => imageInputRef.current?.click()}
                  className="pointer-events-auto inline-flex items-center gap-2 rounded-full bg-white/90 px-4 py-2 text-sm font-semibold text-slate-700 shadow-lg transition hover:bg-white backdrop-blur-sm"
                >
                  <Image size={16} />
                  Insert Title Image
                </button>
                <input
                  ref={imageInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleTitleCardImageUpload}
                />
              </div>
            )}
            <div className="absolute bottom-4 left-0 right-0 flex items-center justify-center gap-3">
              <button
                type="button"
                onClick={onCustomize}
                className="inline-flex items-center gap-2 rounded-full bg-white/90 px-4 py-2 text-sm font-semibold text-slate-700 shadow-lg transition hover:bg-white backdrop-blur-sm"
              >
                <Settings size={16} />
                {customizeLabel}
              </button>
              {typeof isSlideEnabled === 'boolean' && (
                <label className="inline-flex items-center gap-2 rounded-full bg-white/90 px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-lg backdrop-blur-sm">
                  <input
                    type="checkbox"
                    checked={isSlideEnabled}
                    onChange={(e) => onToggleSlideEnabled?.(slideId, e.target.checked)}
                  />
                  {includeLabel}
                </label>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const SlideSection = ({
  slide,
  slideId,
  isSlideEnabled,
  onToggleSlideEnabled,
  headerColor,
  onUpdateHeaderColor,
  headerData,
  onHeaderDataChange,
  isEditMode,
  onToggleEditMode,
  onUpdate,
  onAddContent,
  onBlockEdit,
  onAddSlideAfter,
  onAddSectionTitleSlide,
  onRemoveSlide,
  onClearSlideBlocks,
  onCustomizeTitleCard,
  onCustomizeEndTitleCard,
  onCustomizeSectionTitleCard,
  onTitleCardImageChange,
  onEndTitleCardImageChange,
  onSectionTitleCardImageChange,
  onApplySlideBackgroundScope,
  htmlModes,
  toggleHtmlMode,
  onAddBlockBelow,
  onAddBlockAtTop,
  handleWorksheetJsonImport,
  handleWorksheetJsonPaste,
  onPresetImageRequest,
  onPresetVideoRequest,
  onCanvasImageDrop,
  onCanvasImagePaste,
  slideNames,
  onUpdateSlideName,
  slideSubtitles,
  onUpdateSlideSubtitle,
  slideDeckName,
  onSlideDeckNameChange,
  bodyBackground,
  slideBackgrounds,
  titleCardBackground,
  autoApaCitations,
  showAspectGuides,
  slideDesignMode = 'flow',
  deckNameOnContentSlides = true,
  deckNameBadgeEnabled = true,
  slideNumber = null,
  // Full screen can collapse every overlay control, including this slide's
  // mode banner, so the slide alone fills the screen.
  hideModeBanner = false
}) => {
  const { hasLogo, logo, logoSize } = useLogo();
  const bodyBackgroundStyles = useMemo(() => getBackgroundStyles(bodyBackground), [bodyBackground]);
  // On-screen height of the bottom-left logo badge, expressed in cqw so it
  // scales with the slide exactly as it does in the PowerPoint export
  // (logoHeight inches / 13.33in slide width * 100).
  const slideLogoHeightCqw = useMemo(() => {
    const px = Number(logoSize) > 0 ? Number(logoSize) : 120;
    const inches = Math.min(1.2, Math.max(0.3, (px / 120) * 0.6));
    return (inches / 13.33) * 100;
  }, [logoSize]);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [isEditingSlideName, setIsEditingSlideName] = useState(false);
  const [isAddSlideMenuOpen, setIsAddSlideMenuOpen] = useState(false);
  const [isSlideSettingsOpen, setIsSlideSettingsOpen] = useState(false);
  // Canvas ruler/guide visibility lives here (lifted out of SlideDesigner) so
  // its toggle can sit in the mode banner alongside the settings gear, keeping
  // both off the slide's title bar. Persisted per browser.
  const [showRulers, setShowRulers] = useState(loadRulerPref);
  const toggleRulers = useCallback(() => {
    setShowRulers((prev) => {
      const next = !prev;
      saveRulerPref(next);
      return next;
    });
  }, []);
  const addSlideMenuRef = useRef(null);
  const isEndTitleCard = slide.type === 'end-title-card';
  const isSectionTitleCard = slide.type === 'section-title-card';
  const isTitleCard = isEndTitleCard || slide.type === 'title-card' || isSectionTitleCard;
  const section = slide.section || { id: '', title: '' };
  const blocks = slide.blocks || [];
  const slideIndex = slide.slideIndex ?? 0;
  const slidesInSection = slide.slidesInSection ?? 1;
  const [tempTitle, setTempTitle] = useState(section.title);
  const [tempStudentTitle, setTempStudentTitle] = useState(getStudentFriendlyTitle(section));
  const [tempSlideName, setTempSlideName] = useState(slideNames?.[slideId] || '');
  const [tempSlideSubtitle, setTempSlideSubtitle] = useState(slideSubtitles?.[slideId] || '');
  const headerTextColor = useMemo(() => getContrastingTextColor(headerColor), [headerColor]);
  const sectionBlocks = section.blocks || [];
  const slideBlockIds = useMemo(() => new Set(blocks.map((block) => block.id)), [blocks]);
  const firstBlockId = blocks[0]?.id;
  const lastBlockId = blocks[blocks.length - 1]?.id;
  const currentSlideBackground = slideId ? slideBackgrounds?.[slideId] : null;
  const [backgroundScope, setBackgroundScope] = useState('current');
  const [pendingSlideBackground, setPendingSlideBackground] = useState(currentSlideBackground || '');
  const pendingSlideBackgroundSelection = getBackgroundSelectionState(pendingSlideBackground);

  useEffect(() => {
    if (!isSlideSettingsOpen) return;
    setBackgroundScope('current');
    setPendingSlideBackground(currentSlideBackground || '');
  }, [isSlideSettingsOpen, currentSlideBackground]);

  // A background chosen here is staged, then written when the dialog closes —
  // by the X, the backdrop or Done, all of which commit. The one way it used to
  // vanish without a word: a slide with no id of its own, where there is
  // nothing to key the background to. That now says so instead of discarding
  // the choice in silence.
  const applySlideBackground = useCallback((background) => {
    if (!slideId || !onApplySlideBackgroundScope) {
      console.warn(
        '[slide background] This slide has no id yet, so a background cannot be attached to it. '
        + 'Reopening the deck assigns one.',
        { slideId, hasHandler: Boolean(onApplySlideBackgroundScope) }
      );
      return false;
    }
    onApplySlideBackgroundScope({ slideId, background, scope: backgroundScope });
    return true;
  }, [backgroundScope, onApplySlideBackgroundScope, slideId]);

  const commitSlideBackgroundSelection = useCallback(
    () => applySlideBackground(pendingSlideBackground),
    [applySlideBackground, pendingSlideBackground]
  );

  const handleSlideBackgroundUpload = useCallback((file) => {
    if (!file) {
      setPendingSlideBackground('');
      return;
    }
    const reader = new FileReader();
    reader.onload = (event) => {
      setPendingSlideBackground(event.target.result || '');
    };
    reader.readAsDataURL(file);
  }, []);

  useEffect(() => {
    if (!isAddSlideMenuOpen) return undefined;
    const handleClickOutside = (event) => {
      if (addSlideMenuRef.current && !addSlideMenuRef.current.contains(event.target)) {
        setIsAddSlideMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isAddSlideMenuOpen]);

  // Calculate beforeFirstBlockId - the block to insert after when adding to top
  let beforeFirstIndex = -1;
  let beforeFirstBlockId = null;

  if (firstBlockId) {
    // Normal case: slide has blocks, find the block before the first one
    beforeFirstIndex = sectionBlocks.findIndex((block) => block.id === firstBlockId) - 1;
    beforeFirstBlockId = beforeFirstIndex >= 0 ? sectionBlocks[beforeFirstIndex].id : null;
  } else if (slideIndex > 0) {
    // Empty slide case: find the divider that created this slide
    // Count backwards through dividers to find the one for this slideIndex
    let dividerCount = 0;
    for (let i = 0; i < sectionBlocks.length; i++) {
      const block = sectionBlocks[i];
      const isSlideBreak = block.type === 'divider' && block.content?.isSlideBreak;
      if (isSlideBreak) {
        dividerCount++;
        if (dividerCount === slideIndex) {
          // This divider created our empty slide
          beforeFirstBlockId = block.id;
          beforeFirstIndex = i;
          console.log('[SlideSection] Found divider for empty slide:', { slideIndex, dividerId: block.id, dividerIndex: i });
          break;
        }
      }
    }
  }
  const sectionColors = {
    'bridge-in': { bg: 'from-red-500 to-red-400', text: 'text-red-600' },
    'outcomes': { bg: 'from-emerald-500 to-emerald-400', text: 'text-emerald-600' },
    'pre-assessment': { bg: 'from-amber-500 to-amber-400', text: 'text-amber-600' },
    'participatory-learning': { bg: 'from-blue-500 to-blue-400', text: 'text-blue-600' },
    'post-assessment': { bg: 'from-purple-500 to-purple-400', text: 'text-purple-600' },
    'summary': { bg: 'from-indigo-500 to-indigo-400', text: 'text-indigo-600' },
    'resources': { bg: 'from-gray-600 to-gray-500', text: 'text-gray-600' }
  };

  const colorConfig = sectionColors[section.id] || { bg: 'from-slate-600 to-slate-500', text: 'text-slate-700' };
  useEffect(() => {
    setTempTitle(section.title);
    setTempStudentTitle(getStudentFriendlyTitle(section));
  }, [section.title, section.studentFriendlyTitle, section.id]);

  // The name box needs the same resync the title boxes get. Its initial value
  // is read once, from a `useState` default, so on a deck loaded after this
  // component mounted — or on any slide the component is reused for — the box
  // showed a name belonging to some other slide. It also reads through
  // `resolveSlideName`, so a name the app generated from a slide id
  // ("Section 1 Slide 7") shows as the empty box it really is rather than
  // inviting the author to edit an internal id.
  // Keyed on this slide's own stored name rather than the whole map, so saving
  // a name on another slide cannot reset a box being typed into here.
  const storedSlideName = slideNames?.[slideId];
  useEffect(() => {
    setTempSlideName(resolveSlideName(storedSlideName, slideId));
  }, [storedSlideName, slideId]);

  const storedSlideSubtitle = slideSubtitles?.[slideId];
  useEffect(() => {
    setTempSlideSubtitle(String(storedSlideSubtitle || ''));
  }, [storedSlideSubtitle, slideId]);

  const handleTitleSave = () => {
    const newTitle = tempTitle.trim() || section.title;
    const currentStudentTitle = getStudentFriendlyTitle(section);
    const newStudentTitle = tempStudentTitle.trim();
    const updates = {};

    if (newTitle !== section.title) {
      updates.title = newTitle;
    }
    if (newStudentTitle !== currentStudentTitle) {
      updates.studentFriendlyTitle = newStudentTitle;
    }
    if (Object.keys(updates).length > 0) {
      onUpdate({ ...section, ...updates });
    }
  };

  // A name generated from the slide's id is not a name. Older decks have
  // "Section 1 Slide 7" stored for `section-1-slide-7`, and the header shows
  // the slide's name as its title — so without this every unnamed slide in
  // those decks is titled with its own internal id.
  const slideName = resolveSlideName(slideNames?.[slideId], slideId);
  const slideSubtitle = String(slideSubtitles?.[slideId] || '');
  const slideHeaderParts = getSlideHeaderParts(section, slideName, slideSubtitle);
  const headerLabel = isTitleCard ? (slideDeckName || 'Title') : slideHeaderParts.title;
  const headerSubtitle = isTitleCard ? '' : slideHeaderParts.subtitle;
  const headerAriaLabel = headerSubtitle ? `${headerLabel} — ${headerSubtitle}` : headerLabel;

  const handleSlideNameSave = () => {
    if (onUpdateSlideName && slideId) {
      onUpdateSlideName(slideId, tempSlideName.trim());
    }
    setIsEditingSlideName(false);
  };

  // The header of this slide and nothing else. Editing the bar used to write
  // to the section's title and student-friendly line, so renaming one slide's
  // header renamed every slide in the section — including their subtitles.
  const handleSlideHeaderSave = () => {
    if (slideId) {
      if (onUpdateSlideName) onUpdateSlideName(slideId, tempSlideName.trim());
      if (onUpdateSlideSubtitle) onUpdateSlideSubtitle(slideId, tempSlideSubtitle.trim());
    }
    setIsEditingTitle(false);
  };

  const handleSlideHeaderKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleSlideHeaderSave();
    } else if (event.key === 'Escape') {
      setTempSlideName(slideName);
      setTempSlideSubtitle(slideSubtitle);
      setIsEditingTitle(false);
    }
  };

  // The Slide Settings dialog edits three things that live in two places: the
  // title and subtitle belong to the section, the name belongs to the slide.
  // Every way out of the dialog has to commit all of them.
  //
  // It used to commit only the section fields, so the Slide Name box was a
  // dead end — you could type in it, press Done, and nothing happened. That
  // was the whole header going stale, not just one field: a named slide's
  // header bar *is* its slide name (`getSlideHeaderParts` returns
  // `name || sectionTitle`), so on any deck whose slides were already named —
  // anything reopened from an export — the one field that drives the header
  // was the one field the dialog threw away.
  const commitSlideSettings = () => {
    commitSlideBackgroundSelection();
    // Section fields first, then this slide's own header, because the two are
    // separate scopes: the section boxes rename every slide that has no header
    // of its own, the slide boxes rename this one.
    handleTitleSave();
    handleSlideHeaderSave();
    setIsSlideSettingsOpen(false);
  };

  const handleSlideNameKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSlideNameSave();
    } else if (e.key === 'Escape') {
      setTempSlideName(slideName);
      setIsEditingSlideName(false);
    }
  };

  const handleSlideUpdate = (updatedSection) => {
    const updatedBlocks = updatedSection.blocks || [];
    const rebuilt = [];
    let inserted = false;

    sectionBlocks.forEach((block) => {
      if (slideBlockIds.has(block.id)) {
        if (!inserted) {
          rebuilt.push(...updatedBlocks);
          inserted = true;
        }
        return;
      }
      rebuilt.push(block);
    });

    if (!inserted) {
      rebuilt.push(...updatedBlocks);
    }

    onUpdate({ ...section, blocks: rebuilt });
  };

  const handleAddBlockAtTopInSlide = (_sectionId, contentType) => {
    let targetInsertBlockId = beforeFirstBlockId;

    // Fallback: when an empty slide has no direct divider reference (e.g., after clearing blocks
    // or immediately after creating a new slide), find the last block from the previous slide so
    // we always have a stable insertion point within the current section.
    if (!targetInsertBlockId && slideIndex > 0) {
      const sectionSlides = splitSectionSlides(section, { includeEmpty: true });
      const previousSlides = sectionSlides.slice(0, slideIndex);
      const lastBlockOfPreviousSlide = previousSlides.flat().pop();
      targetInsertBlockId = lastBlockOfPreviousSlide?.id || null;
    }

    if (targetInsertBlockId) {
      onAddBlockBelow(targetInsertBlockId, contentType, section.id);
      return;
    }
    onAddBlockAtTop(section.id, contentType);
  };

  const handleAddBlockAtBottomInSlide = (_sectionId, contentType) => {
    if (lastBlockId) {
      onAddBlockBelow(lastBlockId, contentType, section.id);
      return;
    }
    handleAddBlockAtTopInSlide(section.id, contentType);
  };

  const handleAddSlideAfter = () => {
    if (!onAddSlideAfter || !section.id) return;
    onAddSlideAfter(section.id, lastBlockId);
    setIsAddSlideMenuOpen(false);
  };

  const handleAddSectionTitleSlide = () => {
    if (!onAddSectionTitleSlide || !section.id) return;
    onAddSectionTitleSlide(section.id);
    setIsAddSlideMenuOpen(false);
  };

  const handleRemoveSlide = () => {
    if (!onRemoveSlide || !section.id) return;
    onRemoveSlide(section.id, blocks, slideId);
  };

  const handleClearBlocks = () => {
    if (!onClearSlideBlocks || !section.id || !blocks.length) return;
    if (window.confirm('Are you sure you want to clear all blocks from this slide? This action cannot be undone.')) {
      onClearSlideBlocks(section.id, blocks);
    }
  };

  if (isTitleCard) {
    return (
      <>
        {onToggleEditMode && !hideModeBanner && (
          <SlideModeBanner isEditMode={isEditMode} onToggle={onToggleEditMode} />
        )}
        <TitleCardSlide
          titleCard={slide}
          headerData={headerData}
          isEditMode={isEditMode}
          onCustomize={isEndTitleCard
            ? onCustomizeEndTitleCard
            : isSectionTitleCard
              ? () => onCustomizeSectionTitleCard?.(section.id)
              : onCustomizeTitleCard}
          isSlideEnabled={isSlideEnabled}
          onToggleSlideEnabled={onToggleSlideEnabled}
          onImageInsert={isEndTitleCard
            ? (file, openCustomize) => onEndTitleCardImageChange?.(file, openCustomize, 'end')
            : isSectionTitleCard
              ? (file, openCustomize) => onSectionTitleCardImageChange?.(section.id, file, openCustomize)
              : (file, openCustomize) => onTitleCardImageChange?.(file, openCustomize, 'start')}
          slideDeckName={slideDeckName}
          onSlideDeckNameChange={onSlideDeckNameChange}
          variant={isEndTitleCard ? 'end' : isSectionTitleCard ? 'section' : 'start'}
          slideId={slideId || (isEndTitleCard ? 'end-title-card' : 'title-card')}
          showDeckNameBadge={deckNameBadgeEnabled && !isSectionTitleCard}
          slideNumber={slideNumber}
          onHeaderDataChange={onHeaderDataChange}
        />
      </>
    );
  }

  const isCanvasMode = slideDesignMode === 'canvas';

  // Compact header for canvas mode: matches the exported header bar's
  // proportions so what you position on the stage lines up with the export.
  const canvasHeader = (
    <div
      className="slide-canvas-header"
      style={{ backgroundColor: headerColor, color: headerTextColor }}
    >
      <h2
        className="slide-canvas-header__title"
        data-slide-title
        tabIndex="-1"
        aria-label={headerAriaLabel}
        title={isEditMode ? "Click to edit this slide's header" : undefined}
        onClick={() => {
          if (isEditMode) {
            setTempTitle(section.title);
            setTempStudentTitle(getStudentFriendlyTitle(section));
            setTempSlideName(slideName);
            setTempSlideSubtitle(slideSubtitle);
            setIsSlideSettingsOpen(true);
          }
        }}
      >
        <span className="slide-canvas-header__main">{headerLabel}</span>
        {headerSubtitle && (
          <span className="slide-canvas-header__subtitle">{headerSubtitle}</span>
        )}
      </h2>
      {/* The settings gear and guide toggle now live in the mode banner above
          (see bannerRightControls) so they never overlap this title. */}
    </div>
  );

  // Compact utility buttons injected into the mode banner for canvas edit mode:
  // the ruler/guide toggle and the slide-settings gear. Kept here (rather than
  // on the title bar) so a long slide title has the whole header to itself.
  const bannerRightControls = (isCanvasMode && isEditMode && !isTitleCard) ? (
    <>
      <button
        type="button"
        className={`slide-mode-banner__util ${showRulers ? 'is-active' : ''}`}
        onClick={(e) => { e.stopPropagation(); toggleRulers(); }}
        title={`Rulers & guides: ${showRulers ? 'On' : 'Off'} — click to toggle. Guides never export.`}
        aria-label={`Rulers and guides ${showRulers ? 'on' : 'off'}`}
        aria-pressed={showRulers}
      >
        📐
      </button>
      <button
        type="button"
        className="slide-mode-banner__util"
        onClick={(e) => { e.stopPropagation(); setIsSlideSettingsOpen(true); }}
        title="Slide settings"
        aria-label="Slide settings"
      >
        <Settings size={14} />
      </button>
    </>
  ) : null;

  const modeBanner = (onToggleEditMode && !hideModeBanner)
    ? <SlideModeBanner isEditMode={isEditMode} onToggle={onToggleEditMode} rightControls={bannerRightControls} />
    : null;

  const slideSettingsModal = isSlideSettingsOpen && (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[120] p-4" onClick={() => { commitSlideSettings(); }}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-bold text-gray-900">Slide Settings</h3>
          <button onClick={() => { commitSlideSettings(); }} className="text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>
        <div className="space-y-4">
          {/* Two scopes, each in its own box and labelled with what it touches.
              They used to be one list of three fields where the top two wrote
              to the section — so an author editing "Slide Title" renamed the
              header of every slide in the section and wondered why. */}
          <fieldset className="rounded-xl border border-blue-200 bg-blue-50/60 p-4">
            <legend className="px-1 text-sm font-bold text-blue-900">This slide only</legend>
            <div className="space-y-3">
              <div>
                <label htmlFor={`slide-header-title-${slideId}`} className="block text-sm font-semibold text-gray-700 mb-1">Header Title</label>
                <input
                  id={`slide-header-title-${slideId}`}
                  type="text"
                  value={tempSlideName}
                  onChange={(e) => setTempSlideName(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-slate-900 focus:ring-2 focus:ring-blue-500"
                  placeholder={section.title || 'e.g., "Introduction", "Activity 1"'}
                />
                <p className="text-xs text-gray-600 mt-1">
                  The big line in this slide's header bar, and its label in navigation and exports.
                  Leave empty to fall back to the section name below.
                </p>
              </div>
              <div>
                <label htmlFor={`slide-header-subtitle-${slideId}`} className="block text-sm font-semibold text-gray-700 mb-1">Sub-title</label>
                <input
                  id={`slide-header-subtitle-${slideId}`}
                  type="text"
                  value={tempSlideSubtitle}
                  onChange={(e) => setTempSlideSubtitle(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-slate-900 focus:ring-2 focus:ring-blue-500"
                  placeholder={getStudentFriendlyTitle(section) || 'Optional second line'}
                />
                <p className="text-xs text-gray-600 mt-1">
                  The smaller line under it. Leave empty to fall back to the section subtitle below.
                </p>
              </div>
            </div>
          </fieldset>

          <fieldset className="rounded-xl border border-amber-200 bg-amber-50/60 p-4">
            <legend className="px-1 text-sm font-bold text-amber-900">
              Whole section — every slide in it
            </legend>
            <div className="space-y-3">
              <div>
                <label htmlFor={`section-name-${section.id}`} className="block text-sm font-semibold text-gray-700 mb-1">Section name</label>
                <input
                  id={`section-name-${section.id}`}
                  type="text"
                  value={tempTitle}
                  onChange={(e) => setTempTitle(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-slate-900 focus:ring-2 focus:ring-blue-500"
                  placeholder="Section name"
                />
                <p className="text-xs text-gray-600 mt-1">
                  Used as the header title of every slide in this section that has none of its own.
                </p>
              </div>
              <div>
                <label htmlFor={`section-subtitle-${section.id}`} className="block text-sm font-semibold text-gray-700 mb-1">Section subtitle (student-friendly)</label>
                <input
                  id={`section-subtitle-${section.id}`}
                  type="text"
                  value={tempStudentTitle}
                  onChange={(e) => setTempStudentTitle(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-slate-900 focus:ring-2 focus:ring-blue-500"
                  placeholder="Optional subtitle"
                />
                <p className="text-xs text-gray-600 mt-1">
                  The fallback second line for every slide in this section.
                </p>
              </div>
            </div>
          </fieldset>
          {onUpdateHeaderColor && section.id && (
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Header color</label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={/^#[0-9a-fA-F]{6}$/.test(headerColor || '') ? headerColor : '#545f7d'}
                  onChange={(e) => onUpdateHeaderColor(section.id, e.target.value)}
                  className="h-10 w-14 border border-gray-300 rounded cursor-pointer"
                  aria-label="Slide header color"
                />
                <span className="text-sm text-gray-600">Color of this slide's header bar</span>
              </div>
            </div>
          )}
          {onApplySlideBackgroundScope && slideId && (
            <>
              <BackgroundImagePicker
                label="Slide background image"
                description="Choose a reusable image preset, upload a custom background for this slide, or link one that is already hosted."
                presets={IMAGE_BACKGROUND_PRESET_OPTIONS}
                selectedId={pendingSlideBackgroundSelection.selectedId}
                selectedLabel={pendingSlideBackgroundSelection.selectedLabel || 'Inherit deck background'}
                selectedPreview={pendingSlideBackgroundSelection.selectedPreview}
                onSelectPreset={(preset) => setPendingSlideBackground(preset?.key || '')}
                onUploadImage={handleSlideBackgroundUpload}
                // A linked background is the same string as an uploaded one,
                // minus the megabytes: a URL survives normalisation and
                // resolves to a "Custom Image" preset exactly as a data URL does.
                //
                // It applies at once rather than waiting for the dialog to
                // close, because the link field reports "Linked — the deck
                // stores the address" the moment it resolves. Staging it left
                // that message describing something that had not happened yet,
                // and the deck-wide and title-card pickers both apply on the
                // spot — this one was the odd one out.
                onUseLink={(url) => { setPendingSlideBackground(url); applySlideBackground(url); }}
                onClear={() => setPendingSlideBackground('')}
                uploadLabel="Upload slide image"
                replaceLabel="Replace slide image"
                clearLabel="Remove slide image"
                emptyLabel="Inherit deck background"
              />
              <div>
                <p className="block text-sm font-semibold text-gray-700 mb-2">Background scope</p>
                <div role="radiogroup" aria-label="Background scope" className="space-y-2">
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="radio"
                      name={`background-scope-${slideId}`}
                      value="current"
                      checked={backgroundScope === 'current'}
                      onChange={() => setBackgroundScope('current')}
                    />
                    This slide only
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="radio"
                      name={`background-scope-${slideId}`}
                      value="all"
                      checked={backgroundScope === 'all'}
                      onChange={() => setBackgroundScope('all')}
                    />
                    All regular slides
                  </label>
                </div>
                {backgroundScope === 'all' && (
                  <p className="text-xs text-amber-700 mt-2">Applies this background change to every existing regular slide. Title cards are not changed.</p>
                )}
              </div>
            </>
          )}
          {typeof isSlideEnabled === 'boolean' && (
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={isSlideEnabled}
                onChange={(e) => onToggleSlideEnabled?.(slideId, e.target.checked)}
              />
              Include this slide in exports
            </label>
          )}
          <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-100">
            <button type="button" onClick={() => { commitSlideBackgroundSelection(); handleAddSlideAfter(); setIsSlideSettingsOpen(false); }} className="px-3 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold flex items-center gap-1.5">
              <Plus size={14} /> Add slide
            </button>
            <button type="button" onClick={() => { commitSlideBackgroundSelection(); handleAddSectionTitleSlide(); setIsSlideSettingsOpen(false); }} className="px-3 py-2 rounded-lg bg-slate-600 hover:bg-slate-700 text-white text-sm font-semibold">
              Add title slide
            </button>
            {blocks.length > 0 && (
              <button type="button" onClick={() => { commitSlideBackgroundSelection(); handleClearBlocks(); setIsSlideSettingsOpen(false); }} className="px-3 py-2 rounded-lg bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold flex items-center gap-1.5">
                <X size={14} /> Clear blocks
              </button>
            )}
            {slidesInSection > 1 && (
              <button type="button" onClick={() => { commitSlideBackgroundSelection(); handleRemoveSlide(); setIsSlideSettingsOpen(false); }} className="px-3 py-2 rounded-lg bg-red-500 hover:bg-red-600 text-white text-sm font-semibold flex items-center gap-1.5">
                <Trash2 size={14} /> Remove slide
              </button>
            )}
          </div>
        </div>
        <div className="mt-6 flex justify-end">
          <button onClick={commitSlideSettings} className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold">
            Save
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <>
    {modeBanner}
    <div className="slide-card bg-white rounded-3xl shadow-lg border border-gray-200 overflow-hidden relative">
      {slideSettingsModal}
      {isCanvasMode ? canvasHeader : (
      <div className="px-4 pt-4 pb-4 sm:px-8 sm:pt-6 sm:pb-6 relative" style={{ backgroundColor: headerColor, color: headerTextColor }}>
        {/* Stacks on a phone: side by side, a long section title was squeezed
            into a column a couple of words wide. */}
        <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
          <div>
            {!isEditMode || !isEditingTitle ? (
              <h2
                className="mt-2 slide-header__heading"
                tabIndex="-1"
                data-slide-title
                aria-label={headerAriaLabel}
                title={isEditMode ? "Click to edit this slide's header" : undefined}
                onClick={() => {
                  if (isEditMode) {
                    // This slide's own header, not the section's — see
                    // handleSlideHeaderSave.
                    setTempSlideName(slideName);
                    setTempSlideSubtitle(slideSubtitle);
                    setIsEditingTitle(true);
                  }
                }}
              >
                <span className="slide-header__title text-lg sm:text-2xl font-semibold">
                  {headerLabel}
                </span>
                {headerSubtitle && (
                  <span className="slide-header__subtitle">{headerSubtitle}</span>
                )}
              </h2>
            ) : (
              <div
                className="mt-2 space-y-2"
                onBlur={(event) => {
                  if (!event.currentTarget.contains(event.relatedTarget)) {
                    handleSlideHeaderSave();
                  }
                }}
              >
                <input
                  type="text"
                  value={tempSlideName}
                  onChange={(e) => setTempSlideName(e.target.value)}
                  onKeyDown={handleSlideHeaderKeyDown}
                  className="w-full max-w-md rounded-lg px-3 py-2 text-slate-900"
                  placeholder={section.title || 'Header title'}
                  autoFocus
                />
                <input
                  type="text"
                  value={tempSlideSubtitle}
                  onChange={(e) => setTempSlideSubtitle(e.target.value)}
                  onKeyDown={handleSlideHeaderKeyDown}
                  className="w-full max-w-md rounded-lg px-3 py-2 text-slate-900"
                  placeholder={getStudentFriendlyTitle(section) || 'Optional sub-title'}
                />
                <p className="text-xs opacity-90" style={{ color: headerTextColor }}>
                  This slide's header only. Leave a box empty to fall back to the section's.
                  Rename the section in Slide settings.
                </p>
              </div>
            )}
            {isEditMode && (slidesInSection > 1 || slideIndex === 0) && (
              <div className="mt-3">
                {!isEditingSlideName ? (
                  <div
                    onClick={() => {
                      setTempSlideName(slideName);
                      setIsEditingSlideName(true);
                    }}
                    className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/20 hover:bg-white/30 rounded-lg cursor-pointer transition"
                  >
                    <Edit3 size={14} />
                    <span className="text-sm font-medium">
                      {slideName ? `Slide name: "${slideName}"` : 'Click to name this slide'}
                    </span>
                  </div>
                ) : (
                  <div
                    className="space-y-1"
                    onBlur={(event) => {
                      if (!event.currentTarget.contains(event.relatedTarget)) {
                        handleSlideNameSave();
                      }
                    }}
                  >
                    <input
                      type="text"
                      value={tempSlideName}
                      onChange={(e) => setTempSlideName(e.target.value)}
                      onKeyDown={handleSlideNameKeyDown}
                      className="w-full max-w-md rounded-lg px-3 py-2 text-slate-900"
                      placeholder={`e.g., "Introduction", "Activity 1", etc.`}
                      autoFocus
                    />
                    <p className="text-xs opacity-90" style={{ color: headerTextColor }}>
                      This name will appear in navigation and exports instead of "Slide X of Y"
                    </p>
                  </div>
                )}
              </div>
            )}
            <p className="text-sm mt-2" style={{ color: headerTextColor }}>
              {slidesInSection > 1
                ? 'Use the "Add slide" button to split this section into slides. Divider blocks add horizontal lines within a slide.'
                : 'Edit and build this section as a single slide. Use "Add slide" to create another page.'}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            {isEditMode && (
              <>
                <div className="relative" ref={addSlideMenuRef}>
                  <button
                    type="button"
                    onClick={() => setIsAddSlideMenuOpen((prev) => !prev)}
                    className="slide-header-btn slide-header-btn--primary"
                    aria-haspopup="menu"
                    aria-expanded={isAddSlideMenuOpen}
                  >
                    <Plus size={14} />
                    Add slide
                    <ChevronDown size={14} />
                  </button>
                  {isAddSlideMenuOpen && (
                    <div
                      className="absolute right-0 mt-2 w-44 rounded-xl border border-gray-200 bg-white shadow-lg z-20 overflow-hidden"
                      role="menu"
                    >
                      <button
                        type="button"
                        onClick={handleAddSlideAfter}
                        className="w-full px-4 py-2 text-left text-xs font-semibold text-slate-700 hover:bg-gray-50"
                        role="menuitem"
                      >
                        Add slide
                      </button>
                      <button
                        type="button"
                        onClick={handleAddSectionTitleSlide}
                        className="w-full px-4 py-2 text-left text-xs font-semibold text-slate-700 hover:bg-gray-50"
                        role="menuitem"
                      >
                        Add title slide
                      </button>
                    </div>
                  )}
                </div>
                {blocks.length > 0 && (
                  <button
                    type="button"
                    onClick={handleClearBlocks}
                    className="slide-header-btn slide-header-btn--warning"
                    title="Clear all blocks from this slide"
                  >
                    <X size={14} />
                    Clear blocks
                  </button>
                )}
                {slidesInSection > 1 && (
                  <button
                    type="button"
                    onClick={handleRemoveSlide}
                    className="slide-header-btn slide-header-btn--danger"
                    title="Remove this slide"
                  >
                    <Trash2 size={14} />
                    Remove slide
                  </button>
                )}
              </>
            )}
            {typeof isSlideEnabled === 'boolean' && (
              <label className="slide-header-btn slide-header-btn--toggle">
                <input
                  type="checkbox"
                  checked={isSlideEnabled}
                  onChange={(e) => onToggleSlideEnabled?.(slideId, e.target.checked)}
                />
                Include in export
              </label>
            )}
          </div>
        </div>
      </div>
      )}
      <div className={`slide-body relative ${isCanvasMode ? 'slide-body--designer' : 'pb-16'}`} style={bodyBackgroundStyles}>
        {slideDesignMode === 'canvas' ? (
          <SlideDesigner
            blocks={blocks}
            isEditMode={isEditMode}
            showRulers={showRulers}
            onToggleRulers={toggleRulers}
            onUpdateBlocks={(newBlocks) => handleSlideUpdate({ ...section, blocks: newBlocks })}
            onEditBlock={(blockId) => {
              const target = blocks.find((b) => b.id === blockId);
              if (target) onBlockEdit(target, section.id);
            }}
            onDeleteBlock={(blockId) => {
              if (window.confirm('Delete this block?')) {
                handleSlideUpdate({ ...section, blocks: blocks.filter((b) => b.id !== blockId) });
              }
            }}
            onCanvasImageDrop={(files) => onCanvasImageDrop?.({ files, sectionId: section.id, slideId })}
            onCanvasImagePaste={(pastedBlocks) => onCanvasImagePaste?.({ blocks: pastedBlocks, sectionId: section.id, slideId })}
            renderBlock={(block) => (
              <ContentBlock
                block={block}
                // Read-only on the canvas on purpose: the designer owns moving
                // and resizing, and a block that also edited itself in place
                // would fight it.
                isEditMode={false}
                // The one exception. A layout's citation note is not part of
                // the designer's object model — it lives inside the block, on
                // top of it — so the only place it can be moved from is the
                // block itself. Nothing else about the block becomes editable.
                canMoveCitation={isEditMode}
                onBlockUpdate={(updated) => handleSlideUpdate({
                  ...section,
                  blocks: blocks.map((entry) => (entry.id === updated.id ? updated : entry))
                })}
                htmlModes={htmlModes}
                toggleHtmlMode={toggleHtmlMode}
                autoApaCitations={autoApaCitations}
              />
            )}
          />
        ) : (
          <SlideFlowStage>
          <SectionBlocks
            section={section}
            blocksOverride={blocks}
            onUpdate={handleSlideUpdate}
            isEditMode={isEditMode}
            onBlockEdit={onBlockEdit}
            htmlModes={htmlModes}
            toggleHtmlMode={toggleHtmlMode}
            onAddBlockBelow={onAddBlockBelow}
            onAddBlockAtTop={handleAddBlockAtTopInSlide}
            handleWorksheetJsonImport={handleWorksheetJsonImport}
            handleWorksheetJsonPaste={handleWorksheetJsonPaste}
            onPresetImageRequest={onPresetImageRequest}
            onPresetVideoRequest={onPresetVideoRequest}
            autoApaCitations={autoApaCitations}
          />
          </SlideFlowStage>
        )}
        {deckNameBadgeEnabled && deckNameOnContentSlides && (
          <SlideDeckNameBadge
            value={slideDeckName}
            onChange={onSlideDeckNameChange}
            isEditMode={isEditMode}
            className="pointer-events-auto"
            // Centered along the bottom so it clears the bottom-left school logo
            positionClass="left-1/2 -translate-x-1/2 bottom-4"
          />
        )}
        {hasLogo && logo && (
          <div className="slide-corner-logo" style={{ height: `${slideLogoHeightCqw}cqw` }}>
            <img src={logo} alt="School logo" />
          </div>
        )}
        <SlideNumberBadge number={slideNumber} />
        {showAspectGuides && slideDesignMode !== 'canvas' && <SlideAspectGuides />}
      </div>
    </div>
    {/* Under the slide rather than floating over its bottom-right corner. The
        card clips its own overflow, so a button below it has to be a sibling
        of the card — and out here it cannot cover the corner where a layout's
        citation note and the slide number both live. */}
    {isEditMode && (
      <div className="slide-floating-add no-print">
        <AddBlockMenu
          onAddContent={(type) => handleAddBlockAtBottomInSlide(section.id, type)}
          label="Add"
        />
      </div>
    )}
    </>
  );
};

const SlideDeck = ({
  slides,
  currentIndex,
  headerColorMap,
  defaultHeaderColor,
  onUpdateHeaderColor,
  onHeaderDataChange,
  slideGlobalCss,
  slideVisibility,
  onToggleSlideVisibility,
  onChangeIndex,
  headerData,
  isEditMode,
  onToggleEditMode,
  onUpdate,
  onAddContent,
  onBlockEdit,
  onAddSlideAfter,
  onAddSectionTitleSlide,
  onRemoveSlide,
  onClearSlideBlocks,
  onCustomizeTitleCard,
  onCustomizeEndTitleCard,
  onCustomizeSectionTitleCard,
  onTitleCardImageChange,
  onEndTitleCardImageChange,
  onSectionTitleCardImageChange,
  onApplySlideBackgroundScope,
  htmlModes,
  toggleHtmlMode,
  onAddBlockBelow,
  onAddBlockAtTop,
  handleWorksheetJsonImport,
  handleWorksheetJsonPaste,
  onPresetImageRequest,
  onPresetVideoRequest,
  onCanvasImageDrop,
  onCanvasImagePaste,
  slideNames,
  onUpdateSlideName,
  slideSubtitles,
  onUpdateSlideSubtitle,
  slideDeckName,
  onSlideDeckNameChange,
  bodyBackground,
  slideBackgrounds,
  titleCardBackground,
  autoApaCitations,
  lightMode = false,
  showAspectGuides = false,
  slideDesignMode = 'flow',
  deckNameOnContentSlides = true,
  deckNameBadgeEnabled = true,
  isFullScreen = false,
  hideChrome = false,
  onNudge = null,
  slideNumbersEnabled = true
}) => {
  const totalSlides = slides.length;
  const currentSlide = slides[currentIndex];
  const slideTitleRef = useRef(null);

  // One running count for the whole deck — title cards included — so the
  // numbers read 1, 2, 3 straight through. Slides switched out of the deck are
  // skipped rather than numbered, which keeps the printed run matching the
  // exported file instead of leaving a hole where a hidden slide used to be.
  const slideNumbers = useMemo(() => {
    let counter = 0;
    return slides.map((slide) => {
      const id = slide.slideId;
      const included = id ? slideVisibility?.[id] !== false : true;
      return included ? ++counter : null;
    });
  }, [slides, slideVisibility]);

  useEffect(() => {
    const titleElement = slideTitleRef.current?.querySelector('[data-slide-title]');
    if (titleElement) {
      titleElement.focus({ preventScroll: true });
    }
  }, [currentIndex]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      const target = event.target;
      const tagName = target?.tagName?.toLowerCase();
      if (target?.isContentEditable || ['input', 'textarea', 'select'].includes(tagName)) return;

      // A focused button normally keeps the deck keys, so Enter/Space still
      // work the control the user just clicked. In a full-screen preview that
      // is wrong: after clicking a drawer arrow the button holds focus and
      // Space re-triggers it instead of advancing the deck. Full-screen
      // preview is a presentation surface, so the deck keys win there and the
      // button gives up focus.
      const presenting = isFullScreen && !isEditMode;
      if (tagName === 'button' && !presenting) return;

      if (event.ctrlKey || event.metaKey || event.altKey) return;

      const step = (delta) => {
        event.preventDefault();
        if (presenting && tagName === 'button') target.blur?.();
        onChangeIndex(Math.min(Math.max(currentIndex + delta, 0), totalSlides - 1));
      };

      const jumpTo = (index) => {
        event.preventDefault();
        if (presenting && tagName === 'button') target.blur?.();
        onChangeIndex(Math.min(Math.max(index, 0), totalSlides - 1));
      };

      const nudge = (direction) => {
        event.preventDefault();
        if (presenting && tagName === 'button') target.blur?.();
        onNudge(direction);
      };

      // The vertical arrows change slides, as they do in PowerPoint: Down
      // advances, Up goes back. The horizontal pair nudges the slide sideways
      // under the frame instead — the move a presenter wants after zooming
      // past fit. PageDown/PageUp are what a USB slide clicker sends, so they
      // drive the deck too, and Shift+Space steps back, as in every other
      // presentation tool.
      if (event.key === ' ') {
        step(event.shiftKey ? -1 : 1);
      } else if (event.key === 'ArrowDown' || event.key === 'PageDown') {
        step(1);
      } else if (event.key === 'ArrowUp' || event.key === 'PageUp') {
        step(-1);
      } else if (onNudge && (event.key === 'ArrowLeft' || event.key === 'ArrowRight')) {
        nudge(event.key === 'ArrowLeft' ? -1 : 1);
      } else if (presenting && event.key === 'Home') {
        jumpTo(0);
      } else if (presenting && event.key === 'End') {
        jumpTo(totalSlides - 1);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex, onChangeIndex, onNudge, totalSlides, isFullScreen, isEditMode]);

  // Double-click to enter edit mode from preview
  const handleDoubleClick = (e) => {
    if (!isEditMode && onToggleEditMode) {
      // Don't trigger if double-clicking an interactive element
      const target = e.target;
      const tagName = target?.tagName?.toLowerCase();
      if (['button', 'a', 'input', 'textarea', 'select'].includes(tagName)) return;
      onToggleEditMode();
    }
  };

  if (!currentSlide) return null;
  const headerColor = currentSlide.type === 'section'
    ? headerColorMap?.[currentSlide.section.id] || defaultHeaderColor
    : defaultHeaderColor;
  const slideId = currentSlide.slideId;
  const isSlideEnabled = slideId ? slideVisibility?.[slideId] !== false : true;
  const effectiveBodyBackground = (slideId && slideBackgrounds?.[slideId]) ? slideBackgrounds[slideId] : bodyBackground;

  return (
    <div className="no-print">
      {slideGlobalCss && (
        <style>{slideGlobalCss}</style>
      )}
      <div
        className={`slide-deck-wrapper ${isEditMode ? 'slide-deck-wrapper--edit' : 'slide-deck-wrapper--preview'} ${lightMode ? 'slide-deck-wrapper--light' : 'slide-deck-wrapper--dark'}`}
        onDoubleClick={handleDoubleClick}
      >
        <div ref={slideTitleRef}>
          <SlideSection
            slide={currentSlide}
            slideId={slideId}
            isSlideEnabled={isSlideEnabled}
            onToggleSlideEnabled={onToggleSlideVisibility}
            headerColor={headerColor}
            onUpdateHeaderColor={onUpdateHeaderColor}
            headerData={headerData}
            onHeaderDataChange={onHeaderDataChange}
            isEditMode={isEditMode}
            onToggleEditMode={onToggleEditMode}
            onUpdate={onUpdate}
            onAddContent={onAddContent}
            onBlockEdit={onBlockEdit}
            onAddSlideAfter={onAddSlideAfter}
            onAddSectionTitleSlide={onAddSectionTitleSlide}
            onRemoveSlide={onRemoveSlide}
            onClearSlideBlocks={onClearSlideBlocks}
            onCustomizeTitleCard={onCustomizeTitleCard}
            onCustomizeEndTitleCard={onCustomizeEndTitleCard}
            onCustomizeSectionTitleCard={onCustomizeSectionTitleCard}
            onTitleCardImageChange={onTitleCardImageChange}
            onEndTitleCardImageChange={onEndTitleCardImageChange}
            onSectionTitleCardImageChange={onSectionTitleCardImageChange}
            onApplySlideBackgroundScope={onApplySlideBackgroundScope}
            htmlModes={htmlModes}
            toggleHtmlMode={toggleHtmlMode}
            onAddBlockBelow={onAddBlockBelow}
            onAddBlockAtTop={onAddBlockAtTop}
            handleWorksheetJsonImport={handleWorksheetJsonImport}
            handleWorksheetJsonPaste={handleWorksheetJsonPaste}
            onPresetImageRequest={onPresetImageRequest}
            onPresetVideoRequest={onPresetVideoRequest}
            onCanvasImageDrop={onCanvasImageDrop}
            onCanvasImagePaste={onCanvasImagePaste}
            slideNames={slideNames}
            onUpdateSlideName={onUpdateSlideName}
            slideSubtitles={slideSubtitles}
            onUpdateSlideSubtitle={onUpdateSlideSubtitle}
            slideDeckName={slideDeckName}
            onSlideDeckNameChange={onSlideDeckNameChange}
            bodyBackground={effectiveBodyBackground}
            titleCardBackground={titleCardBackground}
            autoApaCitations={autoApaCitations}
            showAspectGuides={showAspectGuides}
            slideDesignMode={slideDesignMode}
            deckNameOnContentSlides={deckNameOnContentSlides}
            deckNameBadgeEnabled={deckNameBadgeEnabled}
            slideNumber={slideNumbersEnabled ? slideNumbers[currentIndex] : null}
            hideModeBanner={hideChrome}
          />
        </div>
        {!isEditMode && onToggleEditMode && !hideChrome && (
          <FloatingEditButton onToggle={onToggleEditMode} />
        )}
      </div>
    </div>
  );
};

const PrintSections = ({ sections, onUpdate, onAddContent, onBlockEdit, htmlModes, toggleHtmlMode, onAddBlockBelow, onAddBlockAtTop, handleWorksheetJsonImport, handleWorksheetJsonPaste, onPresetImageRequest, onPresetVideoRequest, sectionNumberMap, autoApaCitations }) => (
  <div className="hidden print:block">
    {sections.map((section) => (
      <div
        key={section.id}
        id={`${section.id}-print`}
        className="bg-white rounded-2xl border border-gray-200 mb-8 overflow-hidden print-break-inside-avoid"
      >
        <div className="px-8 py-6 border-b border-gray-200 bg-gray-50">
          <h2 className="text-xl font-semibold text-gray-900">
            {getSectionHeaderLabel(section, sectionNumberMap)}
          </h2>
        </div>
        <SectionBlocks
          section={section}
          onUpdate={onUpdate}
          isEditMode={false}
          onAddContent={onAddContent}
          onBlockEdit={onBlockEdit}
          htmlModes={htmlModes}
          toggleHtmlMode={toggleHtmlMode}
          onAddBlockBelow={onAddBlockBelow}
          onAddBlockAtTop={onAddBlockAtTop}
          handleWorksheetJsonImport={handleWorksheetJsonImport}
          handleWorksheetJsonPaste={handleWorksheetJsonPaste}
          onPresetImageRequest={onPresetImageRequest}
          onPresetVideoRequest={onPresetVideoRequest}
          autoApaCitations={autoApaCitations}
        />
      </div>
    ))}
  </div>
);

// Control Panel Component
const ControlPanel = ({
  onExportPPTX,
  isEditMode,
  onToggleEditMode,
  onExportPDF,
  onExportHTML,
  onExportOptimizedHTML,
  onExportSlimHTML,
  onExportPathHTML,
  onExportSlimJson,
  onSave,
  onLoad,
  onAddSection,
  onAddContent,
  showAdvancedExports,
  setShowAdvancedExports,
  week,
  onWeekChange,
  date,
  onDateChange,
  isOpen,
  onToggle,
  onOpenLogoSettings,
  hasUnsavedChanges,
  lastSaved,
  sections,
  defaultSection,
  onDefaultSectionChange,
  instructorName,
  instructorEmail,
  onHeaderDataChange,
  headerData,
  bodyLayout,
  onBodyLayoutChange,
  bodyBackground,
  onBodyBackgroundChange,
  slideHeaderMode,
  slideHeaderColor,
  slideHeaderColors,
  onSlideHeaderModeChange,
  onSlideHeaderColorChange,
  onSlideHeaderSectionColorChange,
  slideGlobalCss,
  onSlideGlobalCssChange,
  titleCardSettings,
  onTitleCardUpdate,
  onTitleCardImageChange,
  endTitleCardSettings,
  onEndTitleCardUpdate,
  onEndTitleCardImageChange,
  slideDeckName,
  onSlideDeckNameChange,
  exportFileName,
  onExportFileNameChange,
  htmlExportTitle,
  onHtmlExportTitleChange,
  exportVersion,
  onDownloadScopedCssExample,
  onExportLlmTemplate,
  onExportStableJson,
  sectionNumberMap,
}) => (
  <div className={`fixed top-24 right-6 z-50 transition-all duration-300 no-print ${isOpen ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'}`}>
    <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 min-w-80 max-w-[420px]">
      <div className="bg-gradient-to-r from-sky-600 to-blue-700 text-white p-5 rounded-t-2xl flex items-center justify-between">
        <div>
          <p className="text-[11px] uppercase tracking-[0.08em] text-white/80 font-semibold">Template settings</p>
          <h3 className="font-semibold text-lg">Template Controls</h3>
        </div>
        <button onClick={onToggle} className="text-white hover:text-gray-100 transition-colors rounded-full p-1 bg-white/10">
          <X size={20} />
        </button>
      </div>

      <div className="p-6 max-h-[75vh] overflow-y-auto space-y-6">
        {hasUnsavedChanges && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <div className="flex items-center gap-2 text-amber-700">
              <Clock size={16} />
              <span className="text-sm font-medium">Auto-saving...</span>
            </div>
            <p className="text-xs text-amber-600 mt-1">
              Last saved: {lastSaved.toLocaleTimeString()}
            </p>
          </div>
        )}

        <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-4 shadow-inner space-y-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h4 className="text-sm font-semibold text-slate-900">Session details</h4>
              <p className="text-xs text-slate-600">Set the week, date, and instructor information.</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Week</label>
              <select
                value={week}
                onChange={(e) => onWeekChange(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-transparent"
              >
                {Array.from({ length: 15 }, (_, i) => (
                  <option key={i + 1} value={i + 1}>Week {i + 1}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Date</label>
              <input
                type="date"
                value={date}
                onChange={(e) => onDateChange(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-transparent"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Instructor Name</label>
              <input
                type="text"
                value={instructorName}
                onChange={e => onHeaderDataChange('instructorName', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Instructor Email</label>
              <input
                type="email"
                value={instructorEmail}
                onChange={e => onHeaderDataChange('instructorEmail', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-transparent"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Course Title</label>
            <input
              type="text"
              value={headerData.courseTopic}
              onChange={e => onHeaderDataChange('courseTopic', e.target.value)}
              placeholder="Enter course title"
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-transparent"
            />
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm space-y-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h4 className="text-sm font-semibold text-slate-900">Layout & Slides</h4>
              <p className="text-xs text-slate-600">Choose how you want to present the lesson content.</p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Body Layout</label>
            <select
              value={bodyLayout}
              onChange={(e) => onBodyLayoutChange(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-transparent"
            >
              <option value="accordion">Accordion</option>
              <option value="slides">Slides</option>
            </select>
            <p className="text-xs text-gray-500 mt-2">Switch between the traditional accordion view and the slide deck experience.</p>
          </div>

          {bodyLayout === 'slides' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Slide deck name (lower-left label)</label>
                <input
                  type="text"
                  value={slideDeckName}
                  onChange={(e) => onSlideDeckNameChange(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg text-gray-700 placeholder:text-gray-400 focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                  placeholder="e.g., Capstone Production Schedule"
                />
                <p className="text-xs text-gray-500 mt-2">Appears on every slide and can be edited directly on the slide itself.</p>
              </div>

              <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-3">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-semibold text-gray-700">Slide Header Color</label>
                  <span className="text-[11px] uppercase tracking-wide text-gray-500">Design</span>
                </div>
                <select
                  value={slideHeaderMode}
                  onChange={(e) => onSlideHeaderModeChange(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                >
                  <option value="single">Use one color for all slides</option>
                  <option value="per-section">Different colors per section</option>
                </select>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={slideHeaderColor}
                    onChange={(e) => onSlideHeaderColorChange(e.target.value)}
                    className="h-10 w-14 border border-gray-300 rounded"
                    aria-label="Default slide header color"
                  />
                  <span className="text-sm text-gray-600">Default header color</span>
                </div>
                {slideHeaderMode === 'per-section' && (
                  <div className="grid grid-cols-1 gap-3">
                    {sections.map((section) => (
                      <div key={section.id} className="flex items-center justify-between gap-3 bg-white p-2.5 rounded-lg border border-gray-100">
                        <span className="text-sm text-gray-700">{section.title}</span>
                        <input
                          type="color"
                          value={slideHeaderColors[section.id] || slideHeaderColor}
                          onChange={(e) => onSlideHeaderSectionColorChange(section.id, e.target.value)}
                          className="h-8 w-12 border border-gray-300 rounded"
                          aria-label={`Header color for ${section.title}`}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-3">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-semibold text-gray-700">Slide Background</label>
                  <span className="text-[11px] uppercase tracking-wide text-gray-500">Design</span>
                </div>

                {/* Colors */}
                <div>
                  <p className="text-xs text-gray-500 mb-2">Solid Colors</p>
                  <div className="grid grid-cols-4 gap-2">
                    {Object.entries(BACKGROUND_PRESETS).filter(([_, p]) => p.type === 'color').map(([key, preset]) => (
                      <button
                        key={key}
                        onClick={() => onBodyBackgroundChange(key)}
                        className={`h-10 rounded-lg border-2 transition-all ${
                          bodyBackground === key
                            ? 'border-blue-500 ring-2 ring-blue-200'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        style={getBackgroundStyles(preset)}
                        title={preset.name}
                      />
                    ))}
                  </div>
                </div>

                {/* Gradients */}
                <div>
                  <p className="text-xs text-gray-500 mb-2">Gradients</p>
                  <div className="grid grid-cols-5 gap-2">
                    {Object.entries(BACKGROUND_PRESETS).filter(([_, p]) => p.type === 'gradient').map(([key, preset]) => (
                      <button
                        key={key}
                        onClick={() => onBodyBackgroundChange(key)}
                        className={`h-10 rounded-lg border-2 transition-all ${
                          bodyBackground === key
                            ? 'border-blue-500 ring-2 ring-blue-200'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        style={getBackgroundStyles(preset)}
                        title={preset.name}
                      />
                    ))}
                  </div>
                </div>

                {/* Patterns */}
                <details className="text-sm">
                  <summary className="cursor-pointer text-gray-600 hover:text-gray-800 text-xs">Patterns</summary>
                  <div className="grid grid-cols-5 gap-2 mt-2">
                    {Object.entries(BACKGROUND_PRESETS).filter(([_, p]) => p.type === 'pattern').map(([key, preset]) => (
                      <button
                        key={key}
                        onClick={() => onBodyBackgroundChange(key)}
                        className={`h-10 rounded-lg border-2 transition-all ${
                          bodyBackground === key
                            ? 'border-blue-500 ring-2 ring-blue-200'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        style={getBackgroundStyles(preset)}
                        title={preset.name}
                      />
                    ))}
                  </div>
                </details>

                {/* Background Images */}
                <div>
                  <p className="text-xs text-gray-500 mb-2">Background Images</p>
                  <div className="grid grid-cols-3 gap-2">
                    {Object.entries(BACKGROUND_PRESETS).filter(([_, p]) => p.type === 'image').map(([key, preset]) => (
                      <button
                        key={key}
                        onClick={() => onBodyBackgroundChange(key)}
                        className={`aspect-video rounded-lg border-2 transition-all overflow-hidden ${
                          bodyBackground === key
                            ? 'border-blue-500 ring-2 ring-blue-200'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        title={preset.name}
                      >
                        <img
                          src={preset.preview}
                          alt={preset.name}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                </div>

                {/* Custom Image — uploaded, or linked so the file stays out of the deck */}
                <div>
                  <p className="text-xs text-gray-500 mb-2">Custom Image</p>
                  <div className="flex items-center gap-2">
                    <label className="flex-1 cursor-pointer">
                      <div className={`flex items-center justify-center gap-2 p-2 rounded-lg border-2 border-dashed transition-all ${
                        typeof bodyBackground === 'object' && bodyBackground?.type === 'image'
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-300 hover:border-gray-400 hover:bg-gray-100'
                      }`}>
                        <Upload size={16} className="text-gray-500" />
                        <span className="text-xs text-gray-600">Upload image</span>
                      </div>
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            const reader = new FileReader();
                            reader.onload = (event) => {
                              onBodyBackgroundChange({
                                id: 'custom',
                                name: file.name,
                                type: 'image',
                                value: event.target.result,
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                                backgroundRepeat: 'no-repeat'
                              });
                            };
                            reader.readAsDataURL(file);
                          }
                          e.target.value = '';
                        }}
                      />
                    </label>
                    {typeof bodyBackground === 'object' && bodyBackground?.type === 'image' && (
                      <button
                        onClick={() => onBodyBackgroundChange('white')}
                        className="p-2 text-gray-500 hover:text-red-500 transition-colors"
                        title="Remove custom image"
                      >
                        <X size={16} />
                      </button>
                    )}
                  </div>
                  <div className="mt-3">
                    <ImageLinkInput
                      label="Or use a linked image"
                      onUseLink={(url, result) => onBodyBackgroundChange({
                        id: 'custom',
                        name: result?.name || 'Linked image',
                        type: 'image',
                        value: url,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat'
                      })}
                    />
                  </div>
                  {typeof bodyBackground === 'object' && bodyBackground?.type === 'image' && (
                    <div className="mt-2 aspect-video rounded-lg overflow-hidden border border-blue-200">
                      <img
                        src={bodyBackground.value}
                        alt="Custom background"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                </div>

                <p className="text-xs text-gray-500">
                  Selected: {typeof bodyBackground === 'object'
                    ? (bodyBackground?.name || 'Custom Image')
                    : (BACKGROUND_PRESETS[bodyBackground]?.name || 'Default')}
                </p>
              </div>

              <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-3">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-semibold text-gray-700">Title Card</label>
                  <span className="text-[11px] uppercase tracking-wide text-gray-500">First slide</span>
                </div>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={titleCardSettings.enabled}
                    onChange={(e) => onTitleCardUpdate({ enabled: e.target.checked })}
                  />
                  Add a title card slide at the start
                </label>
                <div className="space-y-3">
                  <input
                    type="text"
                    value={titleCardSettings.title}
                    onChange={(e) => onTitleCardUpdate({ title: e.target.value })}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                    placeholder="Title card title"
                  />
                  <input
                    type="text"
                    value={titleCardSettings.subtitle}
                    onChange={(e) => onTitleCardUpdate({ subtitle: e.target.value })}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                    placeholder="Title card subtitle"
                  />
                  <div className="flex flex-wrap items-center gap-3">
                    <label className="flex items-center gap-2 text-sm text-gray-700">
                      <input
                        type="color"
                        value={titleCardSettings.backgroundColor}
                        onChange={(e) => onTitleCardUpdate({ backgroundColor: e.target.value })}
                        className="h-10 w-14 border border-gray-300 rounded"
                        aria-label="Title card background color"
                      />
                      <span className="text-sm text-gray-600">Background color</span>
                    </label>
                  </div>
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={titleCardSettings.showCourseInfo !== false}
                      onChange={(e) => onTitleCardUpdate({ showCourseInfo: e.target.checked })}
                    />
                    Show course/instructor info on the title slide
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={titleCardSettings.showLogoOnTitle !== false}
                      onChange={(e) => onTitleCardUpdate({ showLogoOnTitle: e.target.checked })}
                    />
                    Show school logo on the title slide
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={titleCardSettings.showTextOverlay === true}
                      onChange={(e) => onTitleCardUpdate({ showTextOverlay: e.target.checked })}
                    />
                    Add transparent overlay behind text (improves readability on background images)
                  </label>
                  <div className="grid grid-cols-1 gap-4">
                    <div className="min-w-0 space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-600 mb-1">Optional image</label>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => onTitleCardImageChange(e.target.files?.[0] || null)}
                          className="w-full min-w-0 p-2 border border-gray-300 rounded-lg"
                        />
                      </div>
                      <ImageLinkInput
                        label="Or use a linked image"
                        onUseLink={(url) => onTitleCardUpdate({ image: url })}
                      />
                    </div>
                    <div className="min-w-0">
                      <label className="block text-sm font-medium text-gray-600 mb-2">Image size</label>
                      <div className="flex flex-wrap items-center gap-3">
                        <input
                          type="range"
                          min="60"
                          max="800"
                          step="10"
                          value={titleCardSettings.imageSize || 120}
                          onChange={(e) => onTitleCardUpdate({ imageSize: Number(e.target.value) })}
                          className="flex-1"
                        />
                        <input
                          type="number"
                          min="60"
                          max="800"
                          step="10"
                          value={titleCardSettings.imageSize || 120}
                          onChange={(e) => onTitleCardUpdate({ imageSize: Number(e.target.value) })}
                          className="w-20 p-2 border border-gray-300 rounded-lg text-sm"
                        />
                        <span className="text-xs text-gray-500">px</span>
                      </div>
                    </div>
                    <div className="min-w-0">
                      <label className="block text-sm font-medium text-gray-600 mb-2">Image citation</label>
                      <textarea
                        value={titleCardSettings.imageCitation || ''}
                        onChange={(e) => onTitleCardUpdate({ imageCitation: e.target.value })}
                        rows={3}
                        className="w-full p-2 border border-gray-300 rounded-lg text-sm"
                        placeholder="Add an image citation (optional)"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-2">Text placement</label>
                    <div className="flex flex-wrap items-center gap-2">
                      {[{ value: 'above', label: 'Above image' }, { value: 'below', label: 'Below image' }].map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => onTitleCardUpdate({ textPlacement: option.value })}
                          className={`px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
                            (titleCardSettings.textPlacement || 'below') === option.value
                              ? 'border-slate-700 bg-slate-700 text-white'
                              : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-100'
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Choose whether the title sits above or below the optional image.</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-2">Title alignment</label>
                    <div className="flex flex-wrap items-center gap-2">
                      {[
                        { value: 'top', label: 'Top' },
                        { value: 'center', label: 'Middle' },
                        { value: 'bottom', label: 'Bottom' }
                      ].map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => onTitleCardUpdate({ titlePosition: option.value })}
                          className={`px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
                            (titleCardSettings.titlePosition || 'center') === option.value
                              ? 'border-slate-700 bg-slate-700 text-white'
                              : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-100'
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>
              </div>
            </div>

            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-3">
              <div className="flex items-center justify-between">
                <label className="text-sm font-semibold text-gray-700">End Title Card</label>
                <span className="text-[11px] uppercase tracking-wide text-gray-500">Last slide</span>
              </div>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={endTitleCardSettings.enabled}
                  onChange={(e) => onEndTitleCardUpdate({ enabled: e.target.checked })}
                />
                Add an end title slide
              </label>
              <div className="space-y-3">
                <input
                  type="text"
                  value={endTitleCardSettings.title}
                  onChange={(e) => onEndTitleCardUpdate({ title: e.target.value })}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                  placeholder="End title"
                />
                <input
                  type="text"
                  value={endTitleCardSettings.subtitle}
                  onChange={(e) => onEndTitleCardUpdate({ subtitle: e.target.value })}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                  placeholder="End subtitle"
                />
                <div className="flex flex-wrap items-center gap-3">
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="color"
                      value={endTitleCardSettings.backgroundColor}
                      onChange={(e) => onEndTitleCardUpdate({ backgroundColor: e.target.value })}
                      className="h-10 w-14 border border-gray-300 rounded"
                      aria-label="End title card background color"
                    />
                    <span className="text-sm text-gray-600">Background color</span>
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="color"
                      value={endTitleCardSettings.textColor || '#ffffff'}
                      onChange={(e) => onEndTitleCardUpdate({ textColor: e.target.value })}
                      className="h-10 w-14 border border-gray-300 rounded"
                      aria-label="End title card text color"
                    />
                    <span className="text-sm text-gray-600">Text color</span>
                  </label>
                </div>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={endTitleCardSettings.showCourseInfo !== false}
                    onChange={(e) => onEndTitleCardUpdate({ showCourseInfo: e.target.checked })}
                  />
                  Show course/instructor info on the end slide
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={endTitleCardSettings.showLogoOnTitle !== false}
                    onChange={(e) => onEndTitleCardUpdate({ showLogoOnTitle: e.target.checked })}
                  />
                  Show school logo on the end slide
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={endTitleCardSettings.showTextOverlay === true}
                    onChange={(e) => onEndTitleCardUpdate({ showTextOverlay: e.target.checked })}
                  />
                  Add transparent overlay behind text (improves readability on background images)
                </label>
                <div className="grid grid-cols-1 gap-4">
                  <div className="min-w-0">
                    <label className="block text-sm font-medium text-gray-600 mb-1">Optional image</label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => onEndTitleCardImageChange(e.target.files?.[0] || null, false, 'end')}
                      className="w-full min-w-0 p-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                  <div className="min-w-0">
                    <label className="block text-sm font-medium text-gray-600 mb-2">Image size</label>
                    <div className="flex flex-wrap items-center gap-3">
                      <input
                        type="range"
                        min="60"
                        max="800"
                        step="10"
                        value={endTitleCardSettings.imageSize || 120}
                        onChange={(e) => onEndTitleCardUpdate({ imageSize: Number(e.target.value) })}
                        className="flex-1"
                      />
                      <input
                        type="number"
                        min="60"
                        max="800"
                        step="10"
                        value={endTitleCardSettings.imageSize || 120}
                        onChange={(e) => onEndTitleCardUpdate({ imageSize: Number(e.target.value) })}
                        className="w-20 p-2 border border-gray-300 rounded-lg text-sm"
                      />
                      <span className="text-xs text-gray-500">px</span>
                    </div>
                  </div>
                  <div className="min-w-0">
                    <label className="block text-sm font-medium text-gray-600 mb-2">Image citation</label>
                    <textarea
                      value={endTitleCardSettings.imageCitation || ''}
                      onChange={(e) => onEndTitleCardUpdate({ imageCitation: e.target.value })}
                      rows={3}
                      className="w-full p-2 border border-gray-300 rounded-lg text-sm"
                      placeholder="Add an image citation (optional)"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-2">Text placement</label>
                  <div className="flex flex-wrap items-center gap-2">
                    {[{ value: 'above', label: 'Above image' }, { value: 'below', label: 'Below image' }].map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => onEndTitleCardUpdate({ textPlacement: option.value })}
                        className={`px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
                          (endTitleCardSettings.textPlacement || 'below') === option.value
                            ? 'border-slate-700 bg-slate-700 text-white'
                            : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Choose whether the title sits above or below the optional image.</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-2">Title alignment</label>
                  <div className="flex flex-wrap items-center gap-2">
                    {[
                      { value: 'top', label: 'Top' },
                      { value: 'center', label: 'Middle' },
                      { value: 'bottom', label: 'Bottom' }
                    ].map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => onEndTitleCardUpdate({ titlePosition: option.value })}
                        className={`px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
                          (endTitleCardSettings.titlePosition || 'center') === option.value
                            ? 'border-slate-700 bg-slate-700 text-white'
                            : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-3">
              <div className="flex items-center justify-between">
                <label className="text-sm font-semibold text-gray-700">Global Slide CSS</label>
                  <span className="text-[11px] uppercase tracking-wide text-gray-500">Advanced</span>
                </div>
                <textarea
                  value={slideGlobalCss}
                  onChange={(e) => onSlideGlobalCssChange(e.target.value)}
                  className="w-full h-32 p-3 border border-gray-300 rounded-lg font-mono text-xs focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                  placeholder=".slide-card { border-radius: 32px; }"
                />
                <p className="text-xs text-gray-500">
                  Scope styles to the slide deck (e.g. <code>.slide-deck .slide-card</code>) so they don't affect the rest of the page. Exported HTML uses the same scoped CSS.
                </p>
                <button
                  type="button"
                  onClick={onDownloadScopedCssExample}
                  className="inline-flex items-center gap-2 text-xs font-semibold text-slate-700 hover:text-slate-900"
                >
                  <Download size={14} />
                  Download scoped CSS example
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm space-y-3">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h4 className="text-sm font-semibold text-slate-900">Mode & Branding</h4>
              <p className="text-xs text-slate-600">Toggle editing and adjust logo settings.</p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <button
              onClick={onToggleEditMode}
              className={`w-full p-3 rounded-xl font-semibold transition-all flex items-center justify-center gap-3 ${isEditMode
                ? 'bg-emerald-500 hover:bg-emerald-600 text-white shadow-md'
                : 'bg-slate-700 hover:bg-slate-800 text-white shadow-md'
                }`}
            >
              {isEditMode ? <Eye size={18} /> : <Edit3 size={18} />}
              {isEditMode ? 'Preview Mode' : 'Edit Mode'}
            </button>

            <button
              onClick={onOpenLogoSettings}
              className="w-full p-3 bg-slate-600 hover:bg-slate-700 text-white rounded-xl font-medium flex items-center justify-center gap-2 transition-colors shadow-sm"
            >
              <Settings size={16} />
              School Logo Settings
            </button>
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm space-y-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h4 className="text-sm font-semibold text-slate-900">Export & Save</h4>
              <p className="text-xs text-slate-600">Name your files and choose how to export.</p>
            </div>
            <span className="text-[11px] uppercase tracking-wide text-slate-500">v{exportVersion}</span>
          </div>
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-2">Export file name</label>
              <input
                type="text"
                value={exportFileName}
                onChange={(e) => onExportFileNameChange(e.target.value)}
                placeholder="Week1_Lecture_2025_01_09"
                className="w-full p-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-slate-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-2">HTML export title</label>
              <input
                type="text"
                value={htmlExportTitle}
                onChange={(e) => onHtmlExportTitleChange(e.target.value)}
                placeholder="Lesson Title for HTML exports"
                className="w-full p-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-slate-500 focus:border-transparent"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <button onClick={onExportPDF} className="w-full p-3 bg-orange-500 hover:bg-orange-600 text-white rounded-xl font-medium flex items-center justify-center gap-2 transition-colors shadow-sm">
              <Download size={16} />
              Export PDF
            </button>

            <button onClick={onExportPPTX} className="w-full p-3 bg-rose-600 hover:bg-rose-700 text-white rounded-xl font-medium flex items-center justify-center gap-2 transition-colors shadow-sm">
              <Download size={16} />
              PowerPoint (.pptx)
            </button>

            <button onClick={onExportHTML} className="w-full p-3 bg-purple-500 hover:bg-purple-600 text-white rounded-xl font-medium flex items-center justify-center gap-2 transition-colors shadow-sm">
              <Download size={16} />
              Export Locked HTML
            </button>

            <button onClick={onExportOptimizedHTML} className="w-full p-3 bg-green-500 hover:bg-green-600 text-white rounded-xl font-medium flex items-center justify-center gap-2 transition-colors shadow-sm">
              <Download size={16} />
              Optimized HTML
            </button>

            <button onClick={onExportSlimHTML} className="w-full p-3 bg-teal-700 hover:bg-teal-800 text-white rounded-xl font-medium flex items-center justify-center gap-2 transition-colors shadow-sm">
              <Download size={16} />
              Slim HTML (no embedded images)
            </button>

            <button onClick={onExportPathHTML} className="w-full p-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-medium flex items-center justify-center gap-2 transition-colors shadow-sm">
              <Download size={16} />
              HTML using image paths
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <button onClick={onSave} className="w-full p-3 bg-blue-500 hover:bg-blue-600 text-white rounded-xl font-medium flex items-center justify-center gap-2 transition-colors shadow-sm">
              <Save size={16} />
              Export JSON (Full – includes images)
            </button>

            <button onClick={onExportSlimJson} className="w-full p-3 bg-teal-600 hover:bg-teal-700 text-white rounded-xl font-medium flex items-center justify-center gap-2 transition-colors shadow-sm">
              <Download size={16} />
              Export JSON (Slim – no embedded images)
            </button>
        </div>
        <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
          <div>
            <p className="text-sm font-semibold text-slate-800">Advanced exports</p>
            <p className="text-xs text-slate-600">Show Stable JSON (LLM) in export menus when enabled.</p>
          </div>
          <label className="inline-flex items-center gap-2 text-sm font-medium text-slate-800">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
              checked={showAdvancedExports}
              onChange={(e) => setShowAdvancedExports(e.target.checked)}
            />
            Enable
          </label>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {showAdvancedExports && (
            <button
              onClick={onExportStableJson}
              className="w-full p-3 bg-blue-700 hover:bg-blue-800 text-white rounded-xl font-medium flex items-center justify-center gap-2 transition-colors shadow-sm"
              title="Download Stable JSON (LLM) with schema v1.0.0 to share lesson meaning safely with AI"
            >
              <Download size={16} />
              Export Stable JSON (LLM)
            </button>
          )}

          <button
            onClick={onExportLlmTemplate}
            className="w-full p-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-medium flex items-center justify-center gap-2 transition-colors shadow-sm"
            title="Download the LLM template guide (JSON) showing all block types, layouts, and tips for AI-generated presentations"
          >
            <Download size={16} />
            Export LLM block template
          </button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <input
            type="file"
            accept={JSON_ACCEPT}
              onChange={onLoad}
              className={HIDDEN_FILE_INPUT_CLASS}
              id="loadFile"
            />
            <button
              onClick={() => openFileInput(document.getElementById('loadFile'))}
              className="w-full p-3 bg-gray-500 hover:bg-gray-600 text-white rounded-xl font-medium flex items-center justify-center gap-2 transition-colors shadow-sm"
            >
              <Upload size={16} />
              Load Content
            </button>
            <div className="hidden sm:block" />
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm space-y-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h4 className="text-sm font-semibold text-slate-900">Content Builder</h4>
              <p className="text-xs text-slate-600">Add sections and drop content into the right place.</p>
            </div>
          </div>

          <button onClick={onAddSection} className="w-full p-3 bg-slate-700 hover:bg-slate-800 text-white rounded-xl font-medium flex items-center justify-center gap-2 transition-colors shadow-sm">
            <Plus size={16} />
            Add Section
          </button>

          <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-3">
            <label className="block text-sm font-semibold text-gray-700 mb-1">Add Content to Section</label>
            <p className="text-xs text-gray-500">New content cards will drop into the section you pick here.</p>
            <select
              value={defaultSection}
              onChange={(e) => onDefaultSectionChange(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-transparent"
            >
              {sections.map((section) => {
                const label = getSectionHeaderLabel(section, sectionNumberMap);

                return (
                  <option key={section.id} value={section.id}>
                    {label}
                  </option>
                );
              })}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <button onClick={() => onAddContent(defaultSection, 'video')} className="p-3 bg-red-500 hover:bg-red-600 text-white rounded-lg font-medium flex items-center justify-center gap-2 text-sm transition-colors">
              <Video size={14} />
              Video
            </button>
            <button onClick={() => onAddContent(defaultSection, 'image')} className="p-3 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium flex items-center justify-center gap-2 text-sm transition-colors">
              <Image size={14} />
              Image
            </button>
            <button onClick={() => onAddContent(defaultSection, 'audio')} className="p-3 bg-purple-500 hover:bg-purple-600 text-white rounded-lg font-medium flex items-center justify-center gap-2 text-sm transition-colors">
              <Music size={14} />
              Audio
            </button>
            <button onClick={() => onAddContent(defaultSection, 'cards')} className="p-3 bg-yellow-500 hover:bg-yellow-600 text-white rounded-lg font-medium flex items-center justify-center gap-2 text-sm transition-colors">
              <CreditCard size={14} />
              Cards
            </button>
            <button onClick={() => onAddContent(defaultSection, 'stacked-cards')} className="p-3 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-medium flex items-center justify-center gap-2 text-sm transition-colors">
              <CreditCard size={14} />
              Stacked Cards
            </button>
            <button onClick={() => onAddContent(defaultSection, 'worksheet')} className="p-3 bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg font-medium flex items-center justify-center gap-2 text-sm transition-colors">
              <FileText size={14} />
              Worksheet
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
);

// Helper component for individual image metadata forms, memoized to prevent unnecessary re-renders
const ImageMetadataForm = React.memo(({
  index,
  fileName,
  isMultiple,
  formData,
  onFieldChange,
  onRemove,
  savedCitations,
  onApplySavedCitation,
  onSaveCitationToLibrary,
  onFetchCitationFromUrl,
  isFetchingCitationUrl,
  totalImages = 1,
  onApplyCitationFields
}) => {
  const [citationTab, setCitationTab] = useState('create');
  const [saveFeedbackActive, setSaveFeedbackActive] = useState(false);
  const [citationJsonText, setCitationJsonText] = useState('');
  const [citationJsonError, setCitationJsonError] = useState('');
  const [citationJsonStatus, setCitationJsonStatus] = useState('');
  const [promptCopied, setPromptCopied] = useState(false);
  const citationFileInputRef = useRef(null);
  const baseFieldName = isMultiple ? `image_${index}` : 'image';
  const sourceType = formData[`${baseFieldName}_sourceType`] || 'image';
  const previewMeta = buildImageSourceMeta({
    imageSourceType: sourceType,
    imageAuthor: formData[`${baseFieldName}_author`] || '',
    imageTitle: formData[`${baseFieldName}_title`] || '',
    imageUrl: formData[`${baseFieldName}_url`] || '',
    imageDate: formData[`${baseFieldName}_date`] || '',
    imagePage: formData[`${baseFieldName}_page`] || '',
    imageIncludePage: formData[`${baseFieldName}_includePage`] !== false,
    imageLicense: formData[`${baseFieldName}_license`] || '',
    imageIncludeLicense: formData[`${baseFieldName}_includeLicense`] !== false,
    imageTableNumber: formData[`${baseFieldName}_tableNumber`] || '',
    imageIsAdapted: Boolean(formData[`${baseFieldName}_isAdapted`]),
    imageReferenceCategory: formData[`${baseFieldName}_referenceCategory`] || '',
    imageMedium: formData[`${baseFieldName}_medium`] || '',
    imageReferenceTitle: formData[`${baseFieldName}_referenceTitle`] || '',
    imageArticleTitle: formData[`${baseFieldName}_articleTitle`] || '',
    caption: formData[`${baseFieldName}_caption`] || '',
    alt: formData[`${baseFieldName}_alt`] || ''
  });
  const notePreview = buildMediaNote(previewMeta);
  const referencePreview = buildReferenceContent(previewMeta);
  // Which template the entry will use, and why, so the author can see the app's
  // guess before deciding to override it.
  const resolvedReferenceCategory = inferReferenceCategory(previewMeta);
  const resolvedReferenceOption = IMAGE_REFERENCE_CATEGORIES
    .find((option) => option.value === resolvedReferenceCategory);
  const resolvedReferenceCategoryLabel = resolvedReferenceOption ? resolvedReferenceOption.label : 'Image from a site or database';
  const resolvedReferenceCategoryHint = resolvedReferenceOption ? resolvedReferenceOption.hint : '';
  // A copyright line is not a licence under this house style: it is dropped
  // from the slide note, so the editor says so rather than letting it vanish.
  const licenseNotice = formData[`${baseFieldName}_includeLicense`] === false
    ? null
    : licenseWarning(formData[`${baseFieldName}_license`]);
  const tableLabelPreview = sourceType === 'table'
    ? formatTableLabel({ ...previewMeta, title: formData[`${baseFieldName}_title`] || '' })
    : null;
  const includeCitation = formData[`${baseFieldName}_includeCitation`] !== false;
  const showCaption = formData[`${baseFieldName}_showCaption`] !== false;
  const citationErrors = (() => {
    if (!includeCitation || sourceType === 'clipart_builtin') return {};
    const errors = {};
    if (!formData[`${baseFieldName}_title`]) errors.title = 'Title is required.';
    if (!formData[`${baseFieldName}_author`]) errors.author = 'Author or creator is required.';
    if (!formData[`${baseFieldName}_source`]) errors.source = 'Source is required.';
    // No date is a legitimate answer: APA writes it as "(n.d.)".
    return errors;
  })();

  const handleChange = (field, value) => {
    onFieldChange(`${baseFieldName}_${field}`, value);
  };

  const clearCitationJsonFeedback = () => {
    setCitationJsonError('');
    setCitationJsonStatus('');
  };

  const clearCitationFields = () => {
    clearCitationJsonFeedback();
    handleChange('title', '');
    handleChange('author', '');
    handleChange('source', '');
    handleChange('url', '');
    handleChange('date', '');
    handleChange('sourceType', 'image');
    handleChange('page', '');
    handleChange('license', '');
    handleChange('tableNumber', '');
    handleChange('isAdapted', false);
    handleChange('includeCitation', true);
  };

  const handleSaveToLibrary = () => {
    if (!onSaveCitationToLibrary) return;
    onSaveCitationToLibrary({
      sourceType,
      type: sourceType,
      title: formData[`${baseFieldName}_title`] || '',
      author: formData[`${baseFieldName}_author`] || '',
      source: formData[`${baseFieldName}_source`] || '',
      url: formData[`${baseFieldName}_url`] || '',
      date: formData[`${baseFieldName}_date`] || '',
      page: formData[`${baseFieldName}_page`] || '',
      license: formData[`${baseFieldName}_license`] || '',
      tableNumber: formData[`${baseFieldName}_tableNumber`] || '',
      isAdapted: Boolean(formData[`${baseFieldName}_isAdapted`])
    });
    setSaveFeedbackActive(true);
    setTimeout(() => setSaveFeedbackActive(false), 2000);
  };

  const handleApplySavedCitation = (citation) => {
    onApplySavedCitation?.(baseFieldName, citation);
    setCitationTab('create');
  };

  // A file may carry citations for several images. Entries fill this image and
  // the ones after it, so a gallery can be described in one upload; extras that
  // run past the last image are reported rather than silently dropped.
  const applyCitationEntries = (entries) => {
    const remainingSlots = isMultiple ? Math.max(totalImages - index, 1) : 1;
    const applied = entries.slice(0, remainingSlots);
    const fields = applied.reduce((accumulator, entry, offset) => {
      const targetField = isMultiple ? `image_${index + offset}` : 'image';
      return { ...accumulator, ...citationEntryToFormFields(entry, targetField) };
    }, {});

    if (onApplyCitationFields) {
      onApplyCitationFields(fields);
    } else {
      Object.entries(fields).forEach(([field, value]) => onFieldChange(field, value));
    }

    const ignored = entries.length - applied.length;
    const target = isMultiple
      ? `image${applied.length === 1 ? ` ${index + 1}` : `s ${index + 1}–${index + applied.length}`}`
      : 'this image';
    setCitationJsonError('');
    setCitationJsonStatus(
      `Applied ${applied.length} citation${applied.length === 1 ? '' : 's'} to ${target}.` +
      (ignored > 0 ? ` ${ignored} extra entr${ignored === 1 ? 'y was' : 'ies were'} ignored — no image left to fill.` : '')
    );
    setCitationTab('create');
  };

  const handleCitationJsonImport = (rawJson) => {
    try {
      applyCitationEntries(parseImageCitationJson(rawJson));
      setCitationJsonText('');
    } catch (error) {
      setCitationJsonStatus('');
      setCitationJsonError(error.message);
    }
  };

  const handleCitationFileChange = (event) => {
    const file = event.target.files?.[0];
    // Reset now so re-picking the same file after a fix still fires onChange.
    event.target.value = '';
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => handleCitationJsonImport(String(reader.result || ''));
    reader.onerror = () => {
      setCitationJsonStatus('');
      setCitationJsonError('That file could not be read.');
    };
    reader.readAsText(file);
  };

  const handleCitationJsonExport = () => {
    const entry = formFieldsToCitationEntry(formData, baseFieldName);
    const slug = (entry.title || fileName || 'image')
      .toString()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 40) || 'image';
    downloadJsonFile(`${slug}-citation.json`, buildImageCitationEnvelope([entry]));
    setCitationJsonError('');
    setCitationJsonStatus('Exported this image’s citation as JSON.');
  };

  const handleCitationExampleDownload = () => {
    downloadJsonFile('image-citation-example.json', buildImageCitationExample());
    setCitationJsonError('');
    setCitationJsonStatus('Example downloaded. Hand it to an AI assistant, then upload what it sends back.');
  };

  const handleCopyCitationPrompt = async () => {
    const prompt = buildImageCitationPrompt(isMultiple ? Math.max(totalImages - index, 1) : 1);
    try {
      await navigator.clipboard.writeText(prompt);
      setPromptCopied(true);
      setTimeout(() => setPromptCopied(false), 2000);
      setCitationJsonError('');
      setCitationJsonStatus('Prompt copied. Paste it into an AI chat along with your image.');
    } catch (error) {
      // Clipboard is blocked in some browsers/contexts; drop the prompt into the
      // textarea so it can still be copied by hand.
      setCitationJsonText(prompt);
      setCitationJsonStatus('Clipboard blocked — the prompt is in the box below, copy it from there.');
    }
  };

  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-gray-50/50 space-y-4">
      <div className="flex items-center justify-between gap-3">
        <h4 className="font-semibold text-gray-800 mb-2 truncate">
          {isMultiple ? `Image ${index + 1}: ${fileName}` : 'Image Details'}
        </h4>
        {onRemove && (
          <button
            type="button"
            onClick={onRemove}
            className="text-xs font-semibold text-red-600 hover:text-red-700"
          >
            Remove
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Alt Text:</label>
          <input
            type="text"
            placeholder="Descriptive alt text"
            value={formData[`${baseFieldName}_alt`] || ''}
            onChange={(e) => handleChange('alt', e.target.value)}
            className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-slate-500 focus:border-transparent"
          />
        </div>
        {!isMultiple && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Size:</label>
            <select
              value={formData.image_size || 'medium'}
              onChange={(e) => handleChange('size', e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-slate-500 focus:border-transparent"
            >
              <option value="small">Small</option>
              <option value="medium">Medium</option>
              <option value="large">Large</option>
              <option value="full">Full Width</option>
            </select>
          </div>
        )}
      </div>

      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="text-sm font-medium text-gray-700">Caption:</label>
          <label
            className="flex items-center gap-1 text-[11px] text-gray-500 cursor-pointer"
            title="Uncheck to keep the caption text but leave it off the slide — the APA note and the reference list entry are unaffected"
          >
            <input
              type="checkbox"
              checked={showCaption}
              onChange={(e) => handleChange('showCaption', e.target.checked)}
              className="h-3 w-3 rounded border-gray-300 text-slate-600 focus:ring-slate-500"
            />
            Show on slide
          </label>
        </div>
        <input
          type="text"
          placeholder="Optional caption"
          value={formData[`${baseFieldName}_caption`] || ''}
          onChange={(e) => handleChange('caption', e.target.value)}
          className={`w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-slate-500 focus:border-transparent ${showCaption ? '' : 'bg-gray-100 text-gray-500'}`}
        />
        {!showCaption && (
          <p className="text-[11px] text-gray-500 mt-1">
            Hidden on the slide. The caption is still saved, and the APA note below still shows.
          </p>
        )}
      </div>

      <div className="pt-3">
        <div className="flex items-center justify-between mb-2">
          <h5 className="text-sm font-semibold text-gray-600">APA Citation (Optional)</h5>
          <div className="flex items-center gap-3">
            {citationTab === 'create' && (
              <button
                type="button"
                onClick={handleSaveToLibrary}
                className={`text-xs font-semibold ${
                  saveFeedbackActive ? 'text-emerald-600' : 'text-slate-600 hover:text-slate-800'
                }`}
              >
                {saveFeedbackActive ? 'Saved!' : 'Save to Library'}
              </button>
            )}
            <button
              type="button"
              onClick={clearCitationFields}
              className="text-xs font-semibold text-slate-600 hover:text-slate-800"
            >
              Clear citation
            </button>
          </div>
        </div>
        {/* One switch for the whole citation, so a picture that needs none is
            one click rather than a hunt through the boxes below. Off is the
            same state the fields already understood — no note on the slide, no
            reference-list entry, and nothing required. */}
        <label
          className={`flex items-center gap-3 rounded-xl border-2 p-3 mb-3 cursor-pointer transition-colors ${
            includeCitation
              ? 'border-slate-400 bg-slate-50'
              : 'border-gray-200 bg-gray-50 hover:border-gray-300'
          }`}
        >
          <input
            type="checkbox"
            checked={includeCitation}
            onChange={(e) => handleChange('includeCitation', e.target.checked)}
            className="h-6 w-6 rounded border-gray-300 text-slate-600 focus:ring-slate-500"
          />
          <span className="min-w-0">
            <span className="block text-sm font-bold text-gray-800">
              Cite this source {includeCitation ? '' : '— off'}
            </span>
            <span className="block text-xs text-gray-600 mt-0.5">
              {includeCitation
                ? 'Shows the APA note on the slide and adds it to the deck’s reference list.'
                : 'No note on the slide, nothing in the reference list, and none of the fields below are required.'}
            </span>
          </span>
        </label>

        {includeCitation && (
        <>
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 bg-white p-2">
          <button
            type="button"
            onClick={() => {
              setCitationTab('create');
              clearCitationJsonFeedback();
            }}
            className={`px-3 py-1.5 text-xs font-semibold rounded-lg ${
              citationTab === 'create' ? 'bg-slate-700 text-white' : 'bg-slate-50 text-slate-600 border border-slate-200'
            }`}
          >
            Create / Edit
          </button>
          <button
            type="button"
            onClick={() => {
              setCitationTab('library');
              clearCitationJsonFeedback();
            }}
            className={`px-3 py-1.5 text-xs font-semibold rounded-lg ${
              citationTab === 'library' ? 'bg-slate-700 text-white' : 'bg-slate-50 text-slate-600 border border-slate-200'
            }`}
          >
            Saved Library
          </button>
          <button
            type="button"
            onClick={() => {
              setCitationTab('json');
              clearCitationJsonFeedback();
            }}
            className={`px-3 py-1.5 text-xs font-semibold rounded-lg ${
              citationTab === 'json' ? 'bg-slate-700 text-white' : 'bg-slate-50 text-slate-600 border border-slate-200'
            }`}
          >
            JSON / AI
          </button>
        </div>

        {/* Outside the tab panels: a successful import switches to Create / Edit
            so the filled fields are visible, and the confirmation has to survive
            that switch. */}
        {citationJsonError && (
          <p className="mt-3 text-xs text-red-700 bg-red-50 border border-red-200 rounded-md p-2">
            {citationJsonError}
          </p>
        )}
        {citationJsonStatus && !citationJsonError && (
          <p className="mt-3 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md p-2">
            {citationJsonStatus}
          </p>
        )}

        {citationTab === 'json' ? (
          <div className="mt-4 space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
            <div>
              <h6 className="text-xs font-bold text-slate-700">Import citation details from JSON</h6>
              <p className="text-[11px] text-slate-600 mt-1">
                Download the example file, hand it to an AI assistant with your image (or a copy of the
                page it came from), and upload the JSON it sends back. The fields below fill in for you.
                {isMultiple && ' A file with several citations fills this image and the ones after it.'}
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={handleCitationExampleDownload}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-100"
              >
                <Download size={13} />
                Download example JSON
              </button>
              <button
                type="button"
                onClick={handleCopyCitationPrompt}
                className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-lg border ${
                  promptCopied
                    ? 'text-emerald-700 bg-emerald-50 border-emerald-300'
                    : 'text-slate-700 bg-white border-slate-300 hover:bg-slate-100'
                }`}
              >
                <Copy size={13} />
                {promptCopied ? 'Prompt copied!' : 'Copy AI prompt'}
              </button>
              <button
                type="button"
                onClick={() => citationFileInputRef.current?.click()}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-white bg-slate-700 rounded-lg hover:bg-slate-800"
              >
                <Upload size={13} />
                Choose .json file
              </button>
              <button
                type="button"
                onClick={handleCitationJsonExport}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-100"
              >
                <Download size={13} />
                Export this citation
              </button>
              <input
                ref={citationFileInputRef}
                type="file"
                accept="application/json,.json"
                onChange={handleCitationFileChange}
                className="hidden"
              />
            </div>

            <div>
              <label className="block text-[11px] font-medium text-slate-600 mb-1">
                …or paste the JSON here:
              </label>
              <textarea
                value={citationJsonText}
                onChange={(e) => {
                  setCitationJsonText(e.target.value);
                  setCitationJsonError('');
                }}
                rows={6}
                placeholder={'{\n  "citations": [\n    {\n      "title": "Eric Goldberg at work on Aladdin",\n      "author": "Goldberg, E.",\n      "source": "Disney Animation Archives",\n      "url": "https://example.com/archives",\n      "date": "1992-11-25",\n      "license": "© 1992 Walt Disney Animation Studios"\n    }\n  ]\n}'}
                className="w-full p-2 border border-slate-300 rounded-md text-[11px] font-mono bg-white"
              />
              <button
                type="button"
                onClick={() => handleCitationJsonImport(citationJsonText)}
                className="mt-2 inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-white bg-slate-700 rounded-lg hover:bg-slate-800"
              >
                Apply pasted JSON
              </button>
            </div>

          </div>
        ) : citationTab === 'create' ? (
        <>
        {/* Its own switch now, not a second name for the one above: a source
            can be noted under its picture without joining the reference list at
            the back of the deck. */}
        <div className="flex items-start gap-2 mb-4">
          <input
            id={`${baseFieldName}-add-to-reference-list`}
            type="checkbox"
            checked={formData[`${baseFieldName}_addToReferenceList`] !== false}
            onChange={(e) => handleChange('addToReferenceList', e.target.checked)}
            className="h-4 w-4 mt-0.5 rounded border-gray-300 text-slate-600 focus:ring-slate-500"
          />
          <label htmlFor={`${baseFieldName}-add-to-reference-list`} className="text-xs text-gray-700">
            Add to reference list
            <span className="block text-[11px] text-gray-500 font-normal mt-0.5">
              Lists this source at the back of the deck. If no reference list exists yet, one is
              created on its own slide at the end. Turn it off to show the note under the picture
              only.
            </span>
          </label>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Source Type:</label>
            <select
              value={formData[`${baseFieldName}_sourceType`] || 'image'}
              onChange={(e) => handleChange('sourceType', e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md text-sm"
            >
              <option value="image">Image/Figure</option>
              <option value="table">Table</option>
              <option value="clipart_builtin">Clipart (built-in)</option>
              <option value="clipart_external">Clipart (external)</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Image Title:</label>
            <input
              type="text"
              value={formData[`${baseFieldName}_title`] || ''}
              onChange={(e) => handleChange('title', e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md text-sm"
            />
            {citationErrors.title && <p className="text-xs text-red-600 mt-1">{citationErrors.title}</p>}
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Author/Creator:</label>
            <input
              type="text"
              value={formData[`${baseFieldName}_author`] || ''}
              onChange={(e) => handleChange('author', e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md text-sm"
            />
            {citationErrors.author && <p className="text-xs text-red-600 mt-1">{citationErrors.author}</p>}
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Source:</label>
            <input
              type="text"
              value={formData[`${baseFieldName}_source`] || ''}
              onChange={(e) => handleChange('source', e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md text-sm"
            />
            {citationErrors.source && <p className="text-xs text-red-600 mt-1">{citationErrors.source}</p>}
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Date:</label>
            <input
              type="date"
              value={formData[`${baseFieldName}_date`] || ''}
              onChange={(e) => handleChange('date', e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md text-sm"
            />
            {!formData[`${baseFieldName}_date`] && (
              <p className="text-xs text-gray-500 mt-1">
                No date? Leave it — the note reads <strong>(n.d.)</strong>.
              </p>
            )}
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Source URL:</label>
            <div className="flex gap-2">
              <input
                type="url"
                value={formData[`${baseFieldName}_url`] || ''}
                onChange={(e) => handleChange('url', e.target.value)}
                placeholder="https://example.com/the-page-this-came-from"
                className="min-w-0 flex-1 p-2 border border-gray-300 rounded-md text-sm"
              />
              {onFetchCitationFromUrl && (
                <button
                  type="button"
                  onClick={() => onFetchCitationFromUrl(baseFieldName)}
                  disabled={isFetchingCitationUrl}
                  title="Read the title, creator and date from that page"
                  className="shrink-0 px-3 py-2 text-xs font-semibold rounded-md bg-slate-700 text-white hover:bg-slate-800 disabled:opacity-60"
                >
                  {isFetchingCitationUrl ? 'Reading…' : 'Get details'}
                </button>
              )}
            </div>
            <p className="text-[11px] text-gray-500 mt-1">
              The page this image came from. It goes into the reference list so readers can open the
              original — and <strong>Get details</strong> reads the title, creator and date off it for you.
            </p>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs font-medium text-gray-600">Page:</label>
              <label className="flex items-center gap-1 text-[11px] text-gray-500 cursor-pointer" title="Uncheck if this source has no page (e.g. a web image)">
                <input
                  type="checkbox"
                  checked={formData[`${baseFieldName}_includePage`] !== false}
                  onChange={(e) => handleChange('includePage', e.target.checked)}
                  className="h-3 w-3 rounded border-gray-300 text-slate-600 focus:ring-slate-500"
                />
                Include
              </label>
            </div>
            <input
              type="text"
              value={formData[`${baseFieldName}_page`] || ''}
              onChange={(e) => handleChange('page', e.target.value)}
              placeholder="45-47"
              disabled={formData[`${baseFieldName}_includePage`] === false}
              className={`w-full p-2 border border-gray-300 rounded-md text-sm ${formData[`${baseFieldName}_includePage`] === false ? 'bg-gray-100 text-gray-400' : ''}`}
            />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs font-medium text-gray-600">License/Copyright:</label>
              <label className="flex items-center gap-1 text-[11px] text-gray-500 cursor-pointer" title="Uncheck if this source has no license/copyright info">
                <input
                  type="checkbox"
                  checked={formData[`${baseFieldName}_includeLicense`] !== false}
                  onChange={(e) => handleChange('includeLicense', e.target.checked)}
                  className="h-3 w-3 rounded border-gray-300 text-slate-600 focus:ring-slate-500"
                />
                Include
              </label>
            </div>
            <input
              type="text"
              value={formData[`${baseFieldName}_license`] || ''}
              onChange={(e) => handleChange('license', e.target.value)}
              placeholder="CC BY-NC 2.0"
              disabled={formData[`${baseFieldName}_includeLicense`] === false}
              className={`w-full p-2 border border-gray-300 rounded-md text-sm ${formData[`${baseFieldName}_includeLicense`] === false ? 'bg-gray-100 text-gray-400' : ''}`}
              list={`${baseFieldName}-license-presets`}
            />
            <datalist id={`${baseFieldName}-license-presets`}>
              {LICENSE_PRESETS.filter((preset) => preset.value).map((preset) => (
                <option key={preset.value} value={preset.value}>{preset.label}</option>
              ))}
            </datalist>
            {licenseNotice && (
              <p className="mt-1 text-[11px] leading-snug text-amber-700">{licenseNotice}</p>
            )}
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Table # (if table):</label>
            <input
              type="text"
              value={formData[`${baseFieldName}_tableNumber`] || ''}
              onChange={(e) => handleChange('tableNumber', e.target.value)}
              placeholder="1"
              className="w-full p-2 border border-gray-300 rounded-md text-sm"
            />
          </div>
          <div className="flex items-center gap-2 mt-2">
            <input
              id={`${baseFieldName}-adapted`}
              type="checkbox"
              checked={Boolean(formData[`${baseFieldName}_isAdapted`])}
              onChange={(e) => handleChange('isAdapted', e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-slate-600 focus:ring-slate-500"
            />
            <label htmlFor={`${baseFieldName}-adapted`} className="text-xs text-gray-700">
              Mark as adapted (tables)
            </label>
          </div>
        </div>
        {/* Reference-list fields. None of these reach the slide note: the style
            keeps the medium, the site and the URL for the reference entry. */}
        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3 rounded-lg border border-slate-200 bg-white p-3">
          <div className="md:col-span-2 text-xs font-semibold text-slate-700">
            Reference list entry
            <span className="ml-1 font-normal text-slate-500">— these fields never appear in the slide note.</span>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Reference template:</label>
            <select
              value={formData[`${baseFieldName}_referenceCategory`] || ''}
              onChange={(e) => handleChange('referenceCategory', e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md text-sm"
            >
              <option value="">
                {`Choose from the fields (${resolvedReferenceCategoryLabel})`}
              </option>
              {IMAGE_REFERENCE_CATEGORIES.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
            <p className="mt-1 text-[11px] leading-snug text-slate-500">{resolvedReferenceCategoryHint}</p>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Medium / image format:</label>
            <input
              type="text"
              value={formData[`${baseFieldName}_medium`] || ''}
              onChange={(e) => handleChange('medium', e.target.value)}
              placeholder="Photograph"
              list={`${baseFieldName}-medium-options`}
              className="w-full p-2 border border-gray-300 rounded-md text-sm"
            />
            <datalist id={`${baseFieldName}-medium-options`}>
              {MEDIUM_OPTIONS.map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </datalist>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Title in sentence case:</label>
            <input
              type="text"
              value={formData[`${baseFieldName}_referenceTitle`] || ''}
              onChange={(e) => handleChange('referenceTitle', e.target.value)}
              placeholder="Picture perfect: The making of Sleeping Beauty"
              className="w-full p-2 border border-gray-300 rounded-md text-sm"
            />
            <p className="mt-1 text-[11px] leading-snug text-slate-500">
              Optional. The slide note shows the title in Title Case; the reference list uses sentence case.
            </p>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Article title (magazine):</label>
            <input
              type="text"
              value={formData[`${baseFieldName}_articleTitle`] || ''}
              onChange={(e) => handleChange('articleTitle', e.target.value)}
              placeholder="The making of a classic"
              className="w-full p-2 border border-gray-300 rounded-md text-sm"
            />
          </div>
        </div>
        <div className="mt-4 rounded-lg border border-slate-200 bg-white p-3 text-xs text-slate-600">
          <div className="font-semibold text-slate-700 mb-1">Formatted preview</div>
          {includeCitation ? (
            <>
              {tableLabelPreview && (
                <div className="mb-1">
                  <div className="font-semibold">{tableLabelPreview.tableNumberLabel}</div>
                  {tableLabelPreview.tableTitle && <div className="italic">{tableLabelPreview.tableTitle}</div>}
                </div>
              )}
              <div>
                {notePreview
                  ? <ApaSegments segments={notePreview.segments} />
                  : (sourceType === 'clipart_builtin'
                    ? 'Built-in clipart does not require a note or citation.'
                    : 'Add image details to preview the APA figure note.')}
              </div>
              <div className="mt-2 border-t border-slate-100 pt-2">
                <div className="font-semibold text-slate-700 mb-1">Reference list entry</div>
                {referencePreview
                  ? <ApaSegments segments={referencePreview.segments} />
                  : 'Add image details to preview the reference entry.'}
              </div>
            </>
          ) : (
            <div>Citation disabled for this image.</div>
          )}
        </div>
        </>
        ) : (
          <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
            {savedCitations?.length > 0 ? (
              <div className="space-y-2">
                {savedCitations.map((citation, citationIndex) => (
                  <div key={`saved-image-citation-${citationIndex}`} className="rounded border border-slate-200 bg-white p-2">
                    <div className="text-slate-700">
                      {(() => {
                        const { before, link, after, inline } = buildReferenceDisplay(citation);
                        if (!link) return before || 'Untitled citation';
                        return (
                          <>
                            {before}
                            <a
                              href={link}
                              target="_blank"
                              rel="noreferrer"
                              className="text-indigo-600 hover:text-indigo-800 hover:underline"
                            >
                              {inline ? link : ` ${link}`}
                            </a>
                            {inline ? after : ''}
                          </>
                        );
                      })()}
                    </div>
                    <button
                      type="button"
                      onClick={() => handleApplySavedCitation(citation)}
                      className="mt-2 text-xs font-semibold text-slate-600 hover:text-slate-800"
                    >
                      Apply to this image
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p>No saved citations yet.</p>
            )}
          </div>
        )}
        </>
        )}
      </div>
    </div>
  );
});

/**
 * Copy one formatted citation line to the clipboard.
 *
 * The in-text note is the thing an author hands to their assistant — "put this
 * under the figure" — so it needs to be one click away rather than something to
 * retype from a preview panel.
 */
const CopyTextButton = ({ value, label }) => {
  const [copied, setCopied] = useState(false);
  const text = String(value || '').trim();
  if (!text) return null;

  const copy = async () => {
    try {
      await navigator.clipboard?.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch (error) {
      console.warn('Clipboard write failed', error);
    }
  };

  return (
    <button
      type="button"
      onClick={copy}
      title={label}
      aria-label={label}
      className="shrink-0 inline-flex items-center gap-1 rounded border border-slate-300 bg-white px-1.5 py-0.5 text-[10px] font-semibold text-slate-600 hover:bg-slate-100"
    >
      {copied ? <CheckCircle size={11} /> : <Copy size={11} />}
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
};

/**
 * Read a picture's intrinsic pixel size. The author has to tell the assistant
 * what shape the image is before it can write a layout around it, and reading
 * it here is the difference between "about square, I think" and "1600 x 900".
 */
const readImageDimensions = (src) => new Promise((resolve) => {
  // document.createElement, not `new Image()` — this module imports lucide's
  // `Image` icon, which shadows the DOM constructor and throws "is not a
  // constructor" at runtime while type-checking and building perfectly happily.
  if (typeof document === 'undefined' || !src) { resolve({ width: 0, height: 0 }); return; }
  const probe = document.createElement('img');
  probe.onload = () => resolve({ width: probe.naturalWidth || 0, height: probe.naturalHeight || 0 });
  probe.onerror = () => resolve({ width: 0, height: 0 });
  probe.src = src;
});

/** The shared formatter, guarded — it returns "NaN undefined" for 0/undefined. */
const describeFileSize = (bytes) => {
  const size = Number(bytes);
  return Number.isFinite(size) && size > 0 ? formatFileSize(size) : '';
};

const nextImageSlotName = (images = []) => {
  const taken = new Set(images.map((image) => String(image?.name || '').toLowerCase()));
  for (let i = 1; i <= images.length + 1; i += 1) {
    if (!taken.has(`image-${i}`)) return `image-${i}`;
  }
  return `image-${images.length + 1}`;
};

const nextVideoSlotName = (videos = []) => {
  const taken = new Set(videos.map((video) => String(video?.name || '').toLowerCase()));
  for (let i = 1; i <= videos.length + 1; i += 1) {
    if (!taken.has(`video-${i}`)) return `video-${i}`;
  }
  return `video-${videos.length + 1}`;
};

/**
 * Fetch a video's poster and keep it as a data URL.
 *
 * Storing the remote thumbnail URL would work on screen and then fail where it
 * matters: the PowerPoint/PDF rasterizer paints the layout onto a canvas, and a
 * cross-origin image taints it, so `toDataURL` throws and the whole slide falls
 * back to plain text. Embedding the poster at the moment the author adds the
 * link keeps every destination self-contained. If the fetch is refused we keep
 * the remote URL — the editor and the HTML export still show it.
 */
const fetchPosterDataUrl = async (url) => {
  if (!url) return '';
  try {
    const response = await fetch(url, { mode: 'cors' });
    if (!response.ok) return '';
    const blob = await response.blob();
    if (!blob.type.startsWith('image/')) return '';
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ''));
      reader.onerror = () => resolve('');
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.warn('Video poster could not be embedded; keeping the remote URL', error);
    return '';
  }
};

/**
 * The optional video library for a Custom HTML/CSS block.
 *
 * A linked clip becomes a poster image wrapped in a link rather than an
 * embedded player: the slide-layout sanitizer forbids iframes, and a poster is
 * the only form that survives the editor, the HTML export, PowerPoint and the
 * PDF alike while staying clickable wherever links work.
 */
const CustomHtmlVideoPanel = ({ videos, onChange, onFetchInfo, fetchingIndex = -1 }) => {
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const posterInputRef = useRef(null);
  const [posterFor, setPosterFor] = useState(-1);

  const addVideo = async () => {
    const trimmed = url.trim();
    if (!trimmed) return;
    const platform = detectPlatform(trimmed);
    const videoId = platform ? extractVideoId(trimmed, platform) : null;
    if (!platform || !videoId) {
      setError('Paste a YouTube, Vimeo or Panopto link.');
      return;
    }
    setBusy(true);
    setError('');
    const remotePoster = platform === 'youtube'
      ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`
      : '';
    const poster = remotePoster ? (await fetchPosterDataUrl(remotePoster)) || remotePoster : '';
    onChange([...videos, {
      name: nextVideoSlotName(videos),
      url: trimmed,
      platform,
      videoId,
      poster,
      title: '',
    }]);
    setUrl('');
    setBusy(false);
  };

  const updateVideo = (index, patch) => {
    onChange(videos.map((video, i) => (i === index ? { ...video, ...patch } : video)));
  };

  // Replace a clip's thumbnail with an uploaded picture. The platform's own
  // thumbnail can only be embedded when the browser is allowed to read it
  // cross-origin; when it is not, the link still works everywhere but the
  // rasterized PowerPoint and PDF slides have no picture to draw. An uploaded
  // poster is a data URL, so it draws in every destination.
  const handlePosterFile = async (fileList) => {
    const file = Array.from(fileList || [])[0];
    if (!file || posterFor < 0) return;
    if (!file.type?.startsWith('image/') || file.type === 'image/svg+xml') {
      setError('Use a PNG, JPEG or WebP for the thumbnail.');
      return;
    }
    const src = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ''));
      reader.onerror = reject;
      reader.readAsDataURL(file);
    }).catch(() => '');
    if (!src) { setError('That thumbnail could not be read.'); return; }
    setError('');
    updateVideo(posterFor, { poster: src });
    setPosterFor(-1);
    if (posterInputRef.current) posterInputRef.current.value = '';
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addVideo(); } }}
          placeholder="https://www.youtube.com/watch?v=..."
          className="flex-1 min-w-[16rem] rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-fuchsia-500"
        />
        <button
          type="button"
          onClick={addVideo}
          disabled={busy}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-700 hover:bg-slate-800 disabled:opacity-60 text-white text-sm font-semibold shadow-sm"
        >
          <Video size={15} />
          {busy ? 'Reading…' : 'Add video'}
        </button>
      </div>
      <p className="text-xs text-gray-600">
        Optional. The clip becomes a clickable poster in the layout, not an embedded player —
        that is the one form that works in the editor, the HTML file, PowerPoint and the PDF.
        The assistant writes{' '}
        <code className="font-mono">&lt;a data-lp-video="…"&gt;&lt;/a&gt;</code> and the app fills it in.
      </p>
      {error && <p className="text-xs font-semibold text-red-600">{error}</p>}
      <input
        ref={posterInputRef}
        type="file"
        accept="image/png,image/jpeg,image/gif,image/webp,image/avif"
        className={HIDDEN_FILE_INPUT_CLASS}
        onChange={(e) => handlePosterFile(e.target.files)}
      />

      {videos.length > 0 && (
        <div className="space-y-2">
          {videos.map((video, index) => (
            <div key={`${video.name}-${index}`} className="flex items-start gap-3 rounded-lg border border-gray-200 bg-white p-2">
              {video.poster
                ? <img src={video.poster} alt="" className="h-14 w-24 rounded object-cover border border-gray-200 shrink-0" />
                : <span className="h-14 w-24 rounded border border-gray-200 bg-gray-100 shrink-0 flex items-center justify-center text-gray-400"><Video size={18} /></span>}
              <div className="min-w-0 flex-1 space-y-1.5">
                <div className="flex flex-wrap items-center gap-2">
                  <label className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Slot name</label>
                  <input
                    type="text"
                    value={video.name}
                    onChange={(e) => updateVideo(index, { name: e.target.value })}
                    className="w-32 rounded border border-gray-300 px-2 py-1 font-mono text-xs focus:ring-2 focus:ring-fuchsia-500"
                    spellCheck={false}
                  />
                  <span className="text-xs font-semibold text-gray-700 capitalize">{video.platform}</span>
                  {!video.poster && <span className="text-[11px] text-amber-700">no thumbnail — shows as a text link</span>}
                  {video.poster && !video.poster.startsWith('data:') && (
                    <span className="text-[11px] text-amber-700">
                      thumbnail loads from {video.platform} — PowerPoint and PDF will show a labelled
                      card instead
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => { setPosterFor(index); posterInputRef.current?.click(); }}
                    className="text-[11px] font-semibold text-fuchsia-700 underline hover:text-fuchsia-900"
                  >
                    {video.poster ? 'Replace thumbnail' : 'Upload thumbnail'}
                  </button>
                  {/* Pulls the clip's real title, channel and publication date
                      and writes them into this slide's citation list, so the
                      reference entry is the platform's own metadata rather than
                      whatever the author remembered to type. */}
                  {onFetchInfo && (
                    <button
                      type="button"
                      onClick={() => onFetchInfo(index)}
                      disabled={fetchingIndex === index}
                      className="inline-flex items-center gap-1.5 rounded border border-slate-300 bg-white px-2 py-0.5 text-[11px] font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
                    >
                      {fetchingIndex === index ? (
                        <span className="h-3 w-3 animate-spin rounded-full border-2 border-slate-400 border-t-transparent" />
                      ) : (
                        <FileText size={11} />
                      )}
                      Fetch info
                    </button>
                  )}
                </div>
                {video.author && (
                  <p className="text-[11px] text-gray-500">
                    {video.author}
                    {video.date ? ` · ${video.date}` : ''}
                    <span className="ml-1.5 font-semibold text-emerald-700">cited</span>
                  </p>
                )}
                <input
                  type="text"
                  value={video.title || ''}
                  onChange={(e) => updateVideo(index, { title: e.target.value })}
                  placeholder="Title (used as the link's label and alt text)"
                  className="w-full rounded border border-gray-300 px-2 py-1 text-xs focus:ring-2 focus:ring-fuchsia-500"
                />
                <p className="text-[11px] text-gray-500 truncate" title={video.url}>
                  <code className="font-mono">{`<a data-lp-video="${video.name}"></a>`}</code>
                </p>
              </div>
              <button
                type="button"
                onClick={() => onChange(videos.filter((_, i) => i !== index))}
                className="shrink-0 rounded p-1 text-gray-400 hover:text-red-600"
                title={`Remove ${video.name}`}
                aria-label={`Remove ${video.name}`}
              >
                <X size={16} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * The optional picture library for a Custom HTML/CSS block.
 *
 * The alternative — telling the assistant to write a base64 data URL inline —
 * produces markup megabytes long that no one can read or edit, and the picture
 * is then trapped in a string where the PowerPoint and PDF exporters cannot see
 * it. Uploading here keeps the markup short: the assistant writes
 * `<img data-lp-image="image-1">` and the app resolves it to the real picture
 * everywhere, so it exports like any other image in the deck.
 *
 * Nothing here is required. A block with no uploads behaves exactly as before.
 */
const CustomHtmlImagePanel = ({ images, onChange }) => {
  const fileInputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const readAsDataUrl = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter(Boolean);
    if (!files.length) return;
    setBusy(true);
    setError('');
    try {
      const added = [];
      let working = images;
      for (const file of files) {
        if (!file.type?.startsWith('image/')) {
          setError(`${file.name} is not an image.`);
          continue;
        }
        // SVG is blocked everywhere else in the app as a script vector; it has
        // no business being the one picture that slips through here.
        if (file.type === 'image/svg+xml') {
          setError('SVG images are not supported — use PNG, JPEG or WebP.');
          continue;
        }
        // eslint-disable-next-line no-await-in-loop
        const src = await readAsDataUrl(file);
        // eslint-disable-next-line no-await-in-loop
        const { width, height } = await readImageDimensions(src);
        const entry = {
          name: nextImageSlotName(working),
          src,
          width,
          height,
          fileName: file.name,
          fileSize: file.size,
          alt: '',
        };
        working = [...working, entry];
        added.push(entry);
      }
      if (added.length) onChange(working);
    } catch (err) {
      console.error('Custom HTML image upload failed', err);
      setError('That image could not be read.');
    } finally {
      setBusy(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const updateImage = (index, patch) => {
    onChange(images.map((image, i) => (i === index ? { ...image, ...patch } : image)));
  };

  const removeImage = (index) => {
    onChange(images.filter((_, i) => i !== index));
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={busy}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-700 hover:bg-slate-800 disabled:opacity-60 text-white text-sm font-semibold shadow-sm"
        >
          <Image size={15} />
          {busy ? 'Reading…' : 'Upload image'}
        </button>
        <p className="text-xs text-gray-600 flex-1 min-w-[16rem]">
          Optional. Upload a picture instead of asking the assistant to paste a base64 image into
          the markup. Give it the slot name and size below, and it writes{' '}
          <code className="font-mono">&lt;img data-lp-image="…"&gt;</code> — the app fills in the
          picture, and it exports to PowerPoint and PDF like any other image.
        </p>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/gif,image/webp,image/avif"
          multiple
          className={HIDDEN_FILE_INPUT_CLASS}
          onChange={(e) => handleFiles(e.target.files)}
        />
      </div>

      {error && <p className="text-xs font-semibold text-red-600">{error}</p>}

      {images.length > 0 && (
        <div className="space-y-2">
          {images.map((image, index) => (
            <div key={`${image.name}-${index}`} className="flex items-start gap-3 rounded-lg border border-gray-200 bg-white p-2">
              <img
                src={image.src}
                alt={image.alt || image.fileName || image.name}
                className="h-16 w-16 rounded object-cover border border-gray-200 bg-gray-100 shrink-0"
              />
              <div className="min-w-0 flex-1 space-y-1.5">
                <div className="flex flex-wrap items-center gap-2">
                  <label className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                    Slot name
                  </label>
                  <input
                    type="text"
                    value={image.name}
                    onChange={(e) => updateImage(index, { name: e.target.value })}
                    className="w-40 rounded border border-gray-300 px-2 py-1 font-mono text-xs focus:ring-2 focus:ring-fuchsia-500"
                    spellCheck={false}
                  />
                  <span className="text-xs font-semibold text-gray-700">
                    {image.width && image.height ? `${image.width} × ${image.height} px` : 'size unknown'}
                  </span>
                  {describeFileSize(image.fileSize) && (
                    <span className="text-xs text-gray-500">{describeFileSize(image.fileSize)}</span>
                  )}
                </div>
                <input
                  type="text"
                  value={image.alt || ''}
                  onChange={(e) => updateImage(index, { alt: e.target.value })}
                  placeholder="Alt text (describe the image)"
                  className="w-full rounded border border-gray-300 px-2 py-1 text-xs focus:ring-2 focus:ring-fuchsia-500"
                />
                <p className="text-[11px] text-gray-500 truncate" title={image.fileName}>
                  {image.fileName}
                  {' · '}
                  <code className="font-mono">{`<img data-lp-image="${image.name}">`}</code>
                  {' · '}
                  <code className="font-mono">{`var(--lp-${image.name})`}</code>
                </p>
              </div>
              <button
                type="button"
                onClick={() => removeImage(index)}
                className="shrink-0 rounded p-1 text-gray-400 hover:text-red-600"
                title={`Remove ${image.name}`}
                aria-label={`Remove ${image.name}`}
              >
                <X size={16} />
              </button>
            </div>
          ))}
          <p className="text-[11px] text-gray-600">
            These names and sizes are included in the LLM brief, so downloading it after uploading
            tells the assistant exactly what it has to work with.
          </p>
        </div>
      )}
    </div>
  );
};

// The Custom HTML/CSS block's editor holds four unrelated jobs — writing the
// layout, citing it, fetching the briefs an assistant is given, and uploading
// the media the layout links. Stacked in one scroll they buried the two code
// boxes, which are the reason the dialog is open. Each job gets a tab.
const CUSTOM_HTML_TABS = [
  { id: 'editor', label: 'HTML / CSS', icon: Edit3 },
  { id: 'citations', label: 'Citations', icon: FileText },
  { id: 'examples', label: 'Examples & brief', icon: Download },
  { id: 'media', label: 'Media', icon: Image }
];

// Replace the ContentModal component with this fixed version
const ContentModal = ({ isOpen, contentType, onClose, onSave, initialData = {}, sections = [], onSetSourceInclusion, deckName = '' }) => {
  const [formData, setFormData] = useState(initialData);
  const [isHtmlMode, setIsHtmlMode] = useState(false);
  const [imageSource, setImageSource] = useState('upload');
  const [modalHtmlMode, setModalHtmlMode] = useState(false);
  const [isLoadingVideoInfo, setIsLoadingVideoInfo] = useState(false);
  const [isLoadingEmbedInfo, setIsLoadingEmbedInfo] = useState(false);
  const [isImageDragOver, setIsImageDragOver] = useState(false);
  const [isEmbedThumbDragOver, setIsEmbedThumbDragOver] = useState(false);
  const [citationUrlFetchField, setCitationUrlFetchField] = useState(null);
  // Which pasted picture's citation tab is showing (see contentType
  // 'pasted-images'). Reset whenever the dialog reopens on a new paste.
  const [activePastedImage, setActivePastedImage] = useState(0);
  const [citationFetchIndex, setCitationFetchIndex] = useState(null);
  const [customVideoFetchIndex, setCustomVideoFetchIndex] = useState(-1);
  const [multiColumnClipboard, setMultiColumnClipboard] = useState(() => readMultiColumnCardClipboard());
  const [showMultiColumnPaste, setShowMultiColumnPaste] = useState(false);
  const [savedApaCitations, setSavedApaCitations] = useState(() => readSavedApaCitations());
  const [citationTab, setCitationTab] = useState('create');
  // The paste-in-JSON panel beside Import JSON, and what it is holding.
  const [showCitationPaste, setShowCitationPaste] = useState(false);
  const [citationPasteText, setCitationPasteText] = useState('');
  // "Find" at the top of the citation list: what was typed, and which match the
  // last press landed on, so pressing again walks to the next one.
  const [citationFindText, setCitationFindText] = useState('');
  const [citationFindAt, setCitationFindAt] = useState(-1);
  const [citationPasteError, setCitationPasteError] = useState('');
  // Which job the Custom HTML/CSS editor is showing. Only that block has tabs;
  // every other content type still renders one panel of fields.
  const [customHtmlTab, setCustomHtmlTab] = useState('editor');
  // An import that is waiting on "are you sure": { incoming, replacing, sources }.
  const [pendingImport, setPendingImport] = useState(null);
  const [isImporting, setIsImporting] = useState(false);
  // What the last import did, said next to the button that did it. Not the
  // corner toast: that is rendered only inside the multi-column card panel,
  // and a rejected file needs to stay on screen long enough to be read and
  // acted on rather than fading after three seconds.
  const [importStatus, setImportStatus] = useState(null);
  // The two code boxes, so each one's arrow can bring the dialog to the other.
  const cssSectionRef = useRef(null);
  const htmlSectionRef = useRef(null);
  const [libraryQuery, setLibraryQuery] = useState('');
  const [libraryFilter, setLibraryFilter] = useState('all');
  const [editingLibraryCitationId, setEditingLibraryCitationId] = useState(null);
  // Rescan feedback for the reference list's "Slide citations" tab. The list is
  // derived on every deck change, so a rescan can only ever confirm or report a
  // difference — never repair one.
  const [referenceRescan, setReferenceRescan] = useState(null);

  // Drag and drop state for multi-column cards
  const [draggedCard, setDraggedCard] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);
  const [multiColumnJsonInput, setMultiColumnJsonInput] = useState('');
  const [multiColumnJsonError, setMultiColumnJsonError] = useState('');
  const [copyPasteToast, setCopyPasteToast] = useState({ show: false, message: '', type: '' });
  const [cardActiveTabs, setCardActiveTabs] = useState({}); // Track active tab for each card {colIndex-cardIndex: 'basic'|'styling'|'content'}
  const [expandedSections, setExpandedSections] = useState({ blockSettings: true, columns: true }); // Track which sections are expanded
  const [citationScrollTarget, setCitationScrollTarget] = useState(null);
  const sortedSavedApaCitations = useMemo(
    () => sortCitationsByMeta(savedApaCitations || []),
    [savedApaCitations]
  );
  const filteredSavedApaCitations = useMemo(() => {
    const query = libraryQuery.trim().toLowerCase();
    return sortedSavedApaCitations.filter((citation) => {
      const type = citation.sourceType || citation.type;
      if (libraryFilter !== 'all' && type !== libraryFilter) return false;
      if (!query) return true;
      const label = formatReference(mapCitationToSourceMeta(citation)) || buildCitationAuthorLabel(citation);
      return label.toLowerCase().includes(query);
    });
  }, [libraryFilter, libraryQuery, sortedSavedApaCitations]);
  const stackedCardPalette = ['#0b57d0', '#1d6fd8', '#2f81e0', '#4b95ea', '#5aa1ef', '#6faef3'];
  const defaultBadgeConfig = { enabled: false, type: 'theme', gradientDirection: 'to right' };
  const canPasteMultiColumnCard = Boolean(multiColumnClipboard);

  // Track when modal opens to only initialize once
  const prevIsOpenRef = useRef(false);
  // Last embed link the modal saw, so a *changed* link can invalidate the
  // preview image captured for the old one. Reset on open — the modal instance
  // outlives a single editing session.
  const lastEmbedSrcRef = useRef(null);
  // Link the automatic metadata fetch has already tried, so a failed lookup is
  // not retried on every keystroke.
  const autoEmbedFetchRef = useRef(null);
  const modalBodyRef = useRef(null);

  useEffect(() => {
    const handleStorage = (event) => {
      if (event.key === MULTI_COLUMN_CARD_CLIPBOARD_KEY) {
        setMultiColumnClipboard(readMultiColumnCardClipboard());
      }
      if (event.key === APA_CITATION_LIBRARY_KEYS.key) {
        setSavedApaCitations(readSavedApaCitations());
      }
    };
    window.addEventListener('storage', handleStorage);
    const unsubscribeLibrary = subscribeToApaLibraryUpdates((citations) => {
      setSavedApaCitations(citations);
    });
    return () => {
      window.removeEventListener('storage', handleStorage);
      unsubscribeLibrary();
    };
  }, []);

  useEffect(() => {
    if (isOpen) {
      setMultiColumnClipboard(readMultiColumnCardClipboard());
      setSavedApaCitations(readSavedApaCitations());
      setCitationTab('create');
      setEditingLibraryCitationId(null);
    }
  }, [isOpen]);

  // Initialize multiColumnData structure for new multi-column-cards blocks
  const hasInitializedRef = useRef(false);
  useEffect(() => {
    if (isOpen && contentType === 'multi-column-cards') {
      // Only initialize once when modal opens, not on every formData change
      if (!hasInitializedRef.current && !formData.multiColumnData && !formData.columns) {
        setFormData(prev => {
          // Double-check before setting to avoid race conditions
          if (prev.multiColumnData || prev.columns) return prev;

          return {
            ...prev,
            multiColumnData: {
              columns: [{
                widthPercent: 100,
                cards: [{
                  title: '',
                  primaryTitle: '',
                  categoryLabel: '',
                  instructionalText: '',
                  numberedList: [],
                  numberedListStyle: 'numbered',
                  titleAlignment: 'center',
                  iconName: '',
                  content: '',
                  isChecklist: false,
                  checklistItems: [],
                  backgroundColor: 'transparent',
                  borderColor: '#94a3b8',
                  showBorder: true,
                  listStyle: 'checkbox',
                  badge: { enabled: false, type: 'theme', gradientDirection: 'to right' },
                  columnSpan: 1
                }]
              }],
              matchHeights: false,
              matchHeightColumns: [],
              blockTitleEnabled: false,
              blockTitle: ''
            }
          };
        });
        hasInitializedRef.current = true;
      }
    } else {
      // Reset when modal closes
      hasInitializedRef.current = false;
    }
  }, [isOpen, contentType]);

  // Seed default steps for a fresh project-timeline modal so the editor is
  // never empty (edited blocks arrive with timelineSteps already populated).
  useEffect(() => {
    if (isOpen && contentType === 'project-timeline') {
      setFormData(prev => {
        if (Array.isArray(prev.timelineSteps) && prev.timelineSteps.length) return prev;
        const defaults = createTimelineBlockData();
        return {
          ...prev,
          timelineTitle: typeof prev.timelineTitle === 'string' ? prev.timelineTitle : defaults.title,
          timelineSteps: defaults.steps
        };
      });
    }
  }, [isOpen, contentType]);

  useEffect(() => {
    if (isOpen && contentType === 'link') {
      setFormData(prev => {
        if (prev.linkItems?.length) return prev;
        return {
          ...prev,
          linkItems: [{
            title: '',
            url: '',
            description: '',
            category: 'website',
            platform: '',
            pageInfo: '',
            notes: '',
            tags: ''
          }]
        };
      });
    }
  }, [isOpen, contentType]);

  useEffect(() => {
    if (isOpen && contentType === 'apa-quote') {
      setFormData(prev => ({
        ...prev,
        quoteSourceType: prev.quoteSourceType === 'paraphrase' ? 'paraphrase' : 'quote'
      }));
    }
  }, [isOpen, contentType]);

  const normalizeStackedCardItems = (items) => {
    const normalized = (items || []).map((card, index) => ({
      title: card.title || '',
      content: card.content || '',
      sideText: card.sideText || '',
      color: card.color || stackedCardPalette[index % stackedCardPalette.length]
    }));
    return normalized.length > 0
      ? normalized
      : [{ title: '', content: '', sideText: '', color: stackedCardPalette[0] }];
  };

  const handleImageDrop = (e) => {
    e.preventDefault();
    setIsImageDragOver(false);
    if (e.dataTransfer?.files?.length) {
      setFormData(prev => ({ ...prev, imageFiles: e.dataTransfer.files }));
    }
  };

  const handleImageDragOver = (e) => {
    e.preventDefault();
    setIsImageDragOver(true);
  };

  // A screenshot on the clipboard arrives as an unnamed "image.png"; give it a
  // readable name so the alt text and caption defaults are not all identical.
  const nameClipboardImage = (file, index) => {
    const extension = (file.type.split('/')[1] || 'png').replace('jpeg', 'jpg');
    try {
      return new File([file], `Pasted image ${index}.${extension}`, { type: file.type });
    } catch {
      return file;   // File constructor unavailable — keep the original
    }
  };

  const handleImageDragLeave = () => {
    setIsImageDragOver(false);
  };

  // Helper functions for improved UI
  const getCardActiveTab = (colIndex, cardIndex) => {
    return cardActiveTabs[`${colIndex}-${cardIndex}`] || 'basic';
  };

  const setCardActiveTab = (colIndex, cardIndex, tab) => {
    setCardActiveTabs(prev => ({ ...prev, [`${colIndex}-${cardIndex}`]: tab }));
  };

  const toggleSection = (sectionName) => {
    setExpandedSections(prev => ({ ...prev, [sectionName]: !prev[sectionName] }));
  };

  const createEmptyLinkItem = () => ({
    title: '',
    url: '',
    description: '',
    category: 'website',
    platform: '',
    pageInfo: '',
    notes: '',
    tags: ''
  });

  const updateLinkItem = (index, updates) => {
    setFormData(prev => {
      const items = Array.isArray(prev.linkItems) ? [...prev.linkItems] : [];
      items[index] = { ...items[index], ...updates };
      return { ...prev, linkItems: items };
    });
  };

  const addLinkItem = () => {
    setFormData(prev => ({
      ...prev,
      linkItems: [...(Array.isArray(prev.linkItems) ? prev.linkItems : []), createEmptyLinkItem()]
    }));
  };

  const removeLinkItem = (index) => {
    setFormData(prev => {
      const items = Array.isArray(prev.linkItems) ? [...prev.linkItems] : [];
      items.splice(index, 1);
      return { ...prev, linkItems: items };
    });
  };

  const createEmptyCitation = (overrides = {}) => ({
    id: generateId(),
    citationId: '',
    citationMode: '',
    type: 'book',
    sourceType: 'paraphrase',
    author: '',
    authorFirstName: '',
    authorLastName: '',
    contributors: [],
    year: '',
    date: '',
    title: '',
    source: '',
    publisher: '',
    siteName: '',
    version: '',
    prompt: '',
    notes: '',
    url: '',
    doi: '',
    volume: '',
    issue: '',
    pages: '',
    page: '',
    license: '',
    isAdapted: false,
    platform: 'youtube',
    retrievalDateEnabled: false,
    retrievalDate: '',
    ...overrides
  });

  const createEmptyQuoteSource = () => ({
    author: '',
    year: '',
    page: ''
  });

  const updateCitation = (index, updates) => {
    setFormData(prev => {
      const citations = Array.isArray(prev.citations) ? [...prev.citations] : [];
      citations[index] = { ...citations[index], ...updates };
      return { ...prev, citations };
    });
  };

  const addCitation = () => {
    const overrides = contentType === 'apa-reference-list'
      ? { sourceType: 'book' }
      : { sourceType: 'paraphrase' };
    setFormData(prev => ({
      ...prev,
      citations: [...(Array.isArray(prev.citations) ? prev.citations : []), createEmptyCitation(overrides)]
    }));
  };

  // One state write for a whole imported citation, so a gallery-wide JSON
  // import does not fan out into dozens of individual field updates.
  const applyImageCitationFields = useCallback((fields) => {
    setFormData(prev => ({ ...prev, ...fields }));
  }, []);

  const applySavedCitationToImage = (baseFieldName, citation) => {
    const authorLabel = buildCitationAuthorLabel(citation);
    const sourceType = citation.sourceType || citation.type || 'image';
    const dateValue = citation.date || '';
    const titleValue = citation.title || '';
    const sourceValue = citation.source || '';
    const pageValue = citation.page || citation.pages || '';

    setFormData(prev => ({
      ...prev,
      [`${baseFieldName}_title`]: titleValue,
      [`${baseFieldName}_author`]: authorLabel,
      [`${baseFieldName}_source`]: sourceValue,
      [`${baseFieldName}_url`]: citation.url || '',
      [`${baseFieldName}_date`]: dateValue,
      [`${baseFieldName}_sourceType`]: sourceType,
      [`${baseFieldName}_page`]: pageValue,
      [`${baseFieldName}_license`]: citation.license || '',
      [`${baseFieldName}_includeCitation`]: true,
      [`${baseFieldName}_isAdapted`]: Boolean(citation.isAdapted),
      [`${baseFieldName}_referenceCategory`]: citation.referenceCategory || '',
      [`${baseFieldName}_medium`]: citation.medium || '',
      [`${baseFieldName}_referenceTitle`]: citation.referenceTitle || '',
      [`${baseFieldName}_articleTitle`]: citation.articleTitle || ''
    }));
  };

  const removeCitation = (index) => {
    setFormData(prev => {
      const citations = Array.isArray(prev.citations) ? [...prev.citations] : [];
      citations.splice(index, 1);
      return { ...prev, citations };
    });
  };

  const updateQuoteSource = (index, updates) => {
    setFormData(prev => {
      const sources = Array.isArray(prev.quoteSources)
        ? [...prev.quoteSources]
        : [createEmptyQuoteSource()];
      sources[index] = { ...sources[index], ...updates };
      return { ...prev, quoteSources: sources };
    });
  };

  const addQuoteSource = () => {
    setFormData(prev => ({
      ...prev,
      quoteSources: [...(Array.isArray(prev.quoteSources) ? prev.quoteSources : [createEmptyQuoteSource()]), createEmptyQuoteSource()]
    }));
  };

  const removeQuoteSource = (index) => {
    setFormData(prev => {
      const sources = Array.isArray(prev.quoteSources) ? [...prev.quoteSources] : [];
      sources.splice(index, 1);
      return { ...prev, quoteSources: sources.length ? sources : [createEmptyQuoteSource()] };
    });
  };

  const clearCitationFields = (index) => {
    updateCitation(index, {
      author: '',
      authorFirstName: '',
      authorLastName: '',
      year: '',
      date: '',
      title: '',
      source: '',
      url: '',
      doi: '',
      volume: '',
      issue: '',
      pages: '',
      page: '',
      license: '',
      isAdapted: false
    });
  };

  const clearSingleImage = () => {
    setFormData(prev => ({
      ...prev,
      src: '',
      image_alt: '',
      image_caption: '',
      image_showCaption: true,
      image_title: '',
      image_author: '',
      image_source: '',
      image_date: '',
      image_sourceType: 'image',
      image_page: '',
      image_license: '',
      image_tableNumber: '',
      image_isAdapted: false,
      image_includeCitation: true,
      image_referenceCategory: '',
      image_medium: '',
      image_referenceTitle: '',
      image_articleTitle: '',
      imageFiles: null
    }));
  };

  const removeGalleryImage = (removeIndex) => {
    setFormData(prev => {
      const items = Array.isArray(prev.items) ? prev.items : [];
      const keptIndices = items.map((_, index) => index).filter((index) => index !== removeIndex);
      const nextItems = keptIndices.map((index) => items[index]);
      const next = { ...prev, items: nextItems };

      Object.keys(next).forEach((key) => {
        if (/^image_\d+_/.test(key)) {
          delete next[key];
        }
      });

      keptIndices.forEach((oldIndex, newIndex) => {
        ['alt', 'caption', 'title', 'author', 'source', 'date', 'sourceType', 'page', 'license', 'tableNumber', 'isAdapted', 'includeCitation'].forEach((field) => {
          const oldKey = `image_${oldIndex}_${field}`;
          const newKey = `image_${newIndex}_${field}`;
          next[newKey] = prev[oldKey] || '';
        });
      });

      return next;
    });
  };

  const sanitizeCitationData = (citation = {}) => ({
    id: citation.id || citation.citationId || generateId(),
    citationId: citation.citationId || citation.id || '',
    citationMode: citation.citationMode || '',
    type: citation.type || citation.sourceType || 'book',
    sourceType: citation.sourceType || citation.type || 'paraphrase',
    author: citation.author || '',
    authorFirstName: citation.authorFirstName || '',
    authorLastName: citation.authorLastName || '',
    contributors: Array.isArray(citation.contributors) ? citation.contributors : [],
    year: citation.year || '',
    date: citation.date || '',
    title: citation.title || '',
    source: citation.source || '',
    publisher: citation.publisher || '',
    siteName: citation.siteName || '',
    version: citation.version || '',
    prompt: citation.prompt || '',
    notes: citation.notes || '',
    url: citation.url || '',
    doi: citation.doi || '',
    volume: citation.volume || '',
    issue: citation.issue || '',
    pages: citation.pages || '',
    page: citation.page || '',
    license: citation.license || '',
    isAdapted: Boolean(citation.isAdapted),
    platform: citation.platform || 'youtube',
    retrievalDateEnabled: Boolean(citation.retrievalDateEnabled),
    retrievalDate: citation.retrievalDate || ''
  });

  const getCitationAuthorInput = (citation = {}) => {
    const { firstName, lastName } = getCitationAuthorParts(citation);
    return {
      firstName: firstName || '',
      lastName: lastName || ''
    };
  };

  const saveCitationToLibrary = (citation, options = {}) => {
    if (!hasApaCitationContent(citation)) {
      showCopyPasteToast('Add citation details before saving.', 'error');
      return;
    }
    const normalized = sanitizeCitationData(citation);
    const key = getApaCitationKey(normalized);
    const existing = Array.isArray(savedApaCitations) ? savedApaCitations : [];
    const existingIndex = existing.findIndex((entry) => entry.citationId && entry.citationId === normalized.citationId);
    const duplicateIndex = existing.findIndex((entry, index) =>
      index !== existingIndex && getApaCitationKey(entry) === key
    );
    if (duplicateIndex !== -1) {
      showCopyPasteToast('Citation already saved.', 'error');
      return;
    }
    const updated = existingIndex >= 0
      ? existing.map((entry, index) => (index === existingIndex ? normalized : entry))
      : [...existing, normalized];
    setSavedApaCitations(updated);
    writeSavedApaCitations(updated);
    if (existingIndex >= 0 || options.isEdit) {
      setEditingLibraryCitationId(null);
    }
    showCopyPasteToast(existingIndex >= 0 || options.isEdit ? 'Citation updated in library.' : 'Saved to citation library.');
  };

  const removeSavedCitation = (citation) => {
    const keyToRemove = getApaCitationKey(citation);
    const updated = (savedApaCitations || []).filter(
      (entry) => getApaCitationKey(entry) !== keyToRemove
    );
    setSavedApaCitations(updated);
    writeSavedApaCitations(updated);
  };

  const addSavedCitationToBlock = (citation) => {
    const normalized = sanitizeCitationData(citation);
    let nextTargetIndex = 0;
    setFormData(prev => ({
      ...prev,
      citations: (() => {
        const existing = Array.isArray(prev.citations) ? prev.citations : [];
        const firstHasContent = existing.length > 0 && hasApaCitationContent(existing[0]);
        const newCitation = { ...normalized, id: generateId(), citationId: normalized.citationId || normalized.id };
        const merged = (!existing.length || !firstHasContent)
          ? [newCitation]
          : [existing[0], newCitation, ...existing.slice(1)];
        if (contentType === 'apa-reference-list') {
          nextTargetIndex = 0;
          return sortCitationsByMeta(merged);
        }
        nextTargetIndex = !existing.length || !firstHasContent ? 0 : 1;
        return merged;
      })()
    }));
    setCitationScrollTarget(nextTargetIndex);
    showCopyPasteToast('Citation added to block.');
  };

  const editSavedCitation = (citation) => {
    const normalized = sanitizeCitationData(citation);
    setFormData(prev => ({
      ...prev,
      citations: (() => {
        const existing = Array.isArray(prev.citations) ? [...prev.citations] : [];
        if (!existing.length || !hasApaCitationContent(existing[0])) {
          return [{ ...normalized, id: normalized.id || generateId() }];
        }
        existing[0] = { ...existing[0], ...normalized };
        return existing;
      })()
    }));
    setEditingLibraryCitationId(normalized.citationId || normalized.id);
    setCitationTab('create');
    showCopyPasteToast('Loaded citation for editing.');
  };

  // Export the citations currently in this block to a portable JSON file.
  // The file holds the full citation record for each entry — the app renders
  // the full reference (for the deck's reference list) and the in-text
  // citation (for the current slide) from these fields on import.
  const handleExportCitationsJson = () => {
    const source = Array.isArray(formData.citations) ? formData.citations : [];
    const citations = source
      .filter((citation) => hasApaCitationContent(citation))
      .map((citation) => {
        // Drop the volatile render id; keep a stable citationId for de-duping.
        const { id, ...rest } = sanitizeCitationData(citation);
        return rest;
      });

    if (!citations.length) {
      showCopyPasteToast('Add citation details before exporting.', 'error');
      return;
    }

    const payload = {
      type: 'boppps-apa-citation',
      version: 1,
      exportedAt: new Date().toISOString(),
      citations
    };

    const dataStr = JSON.stringify(payload, null, 2);
    const blob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    const first = citations[0] || {};
    const baseName = (first.author || first.title || 'citation')
      .toString()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 40) || 'citation';
    anchor.download = `${baseName}.citation.json`;
    anchor.click();
    URL.revokeObjectURL(url);

    showCopyPasteToast(`Exported ${citations.length} citation${citations.length === 1 ? '' : 's'} to JSON.`);
  };

  // Accepts an exported wrapper ({ citations: [...] }), a bare array of
  // citations, or a single citation object, and merges them into the block.
  const importCitationsFromData = (data) => {
    let rawList = [];
    if (Array.isArray(data)) rawList = data;
    else if (data && Array.isArray(data.citations)) rawList = data.citations;
    else if (data && typeof data === 'object') rawList = [data];

    const valid = rawList
      .filter((entry) => entry && typeof entry === 'object')
      .map((entry) => sanitizeCitationData(entry))
      .filter((entry) => hasApaCitationContent(entry));

    if (!valid.length) {
      showCopyPasteToast('No valid citations found in that file.', 'error');
      return;
    }

    setFormData(prev => {
      const existing = Array.isArray(prev.citations) ? prev.citations : [];
      const firstHasContent = existing.length > 0 && hasApaCitationContent(existing[0]);
      const prepared = valid.map((entry) => ({
        ...entry,
        id: generateId(),
        citationId: entry.citationId || generateId()
      }));
      let merged = (!existing.length || !firstHasContent)
        ? prepared
        : [existing[0], ...prepared, ...existing.slice(1)];
      if (contentType === 'apa-reference-list') {
        merged = sortCitationsByMeta(merged);
      }
      return { ...prev, citations: merged };
    });

    setCitationTab('create');
    showCopyPasteToast(`Imported ${valid.length} citation${valid.length === 1 ? '' : 's'}.`);
  };

  /**
   * The same import, from the clipboard instead of a file.
   *
   * What an assistant hands back is text in a chat window, and saving it to a
   * file first only to open a file picker for it is two steps that exist for
   * the app's convenience rather than the author's. Fenced code (```json) is
   * tolerated because that is how the text usually arrives.
   */
  const handlePasteCitationsJson = () => {
    const raw = (citationPasteText || '').trim();
    if (!raw) {
      setCitationPasteError('Paste the JSON first.');
      return;
    }
    const unfenced = raw
      .replace(/^```[a-zA-Z]*\s*/, '')
      .replace(/```\s*$/, '')
      .trim();
    let parsed;
    try {
      parsed = JSON.parse(unfenced);
    } catch (err) {
      setCitationPasteError('That is not valid JSON. Check for a missing comma or bracket, or paste the whole file.');
      return;
    }
    setCitationPasteError('');
    setCitationPasteText('');
    setShowCitationPaste(false);
    importCitationsFromData(parsed);
  };

  const handleImportCitationsJson = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'application/json,.json';
    input.onchange = (event) => {
      const file = event.target?.files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (ev) => {
        try {
          const parsed = JSON.parse(ev.target?.result || '{}');
          importCitationsFromData(parsed);
        } catch (err) {
          console.error('Citation import failed', err);
          showCopyPasteToast('Could not read that file as JSON.', 'error');
        }
      };
      reader.readAsText(file);
    };
    input.click();
  };

  // Downloads a self-documenting template so a person (or an AI assistant)
  // can see exactly what shape a citation import file must take. The `_readme`
  // block is ignored on import — only the `citations` array is read — so this
  // file is also directly importable as-is.
  const handleDownloadSampleCitationJson = () => {
    const sample = {
      type: 'boppps-apa-citation',
      version: 1,
      _readme: {
        description: 'Citation import file for the LessonPlanner One APA citation block. Import it from the citation block editor with the "Import JSON" button.',
        howToUse: 'Only the "citations" array is imported. Every entry becomes one citation: the app renders its full reference for the deck reference list and its in-text citation for the current slide. Keys beginning with "_" (like this one) are ignored on import.',
        allowedSourceTypes: [
          'paraphrase', 'quote', 'book', 'journal', 'webpage', 'video',
          'image', 'table', 'clipart_external', 'clipart_builtin', 'software', 'ai'
        ],
        fieldNotes: {
          sourceType: 'One of allowedSourceTypes. Controls which fields are used and how the reference is formatted.',
          author: 'For a personal author, prefer authorFirstName/authorLastName. For a group/organization author (e.g. a newspaper, studio, or company), also add it to "contributors" as { "name": "…", "isOrganization": true } so it is not inverted to "Lastname, F.".',
          date: 'Use ISO format YYYY-MM-DD for sources that carry a full date (webpage, video, image, table, ai). Leave empty and use "year" for year-only sources (book, journal, paraphrase, quote, software).',
          year: '4-digit year, used when there is no full date.',
          title: 'For images/figures, append the medium in brackets, e.g. "… [Photograph]". For video the app adds "[Video]" automatically.',
          source: 'Container / site / database / publisher name (e.g. "SuperStock", "YouTube", a journal name).',
          url: 'Direct link to the source. For DOIs, use the "doi" field instead.',
          volumeIssuePages: 'Journal-only fields: volume, issue, pages.',
          license: 'Optional image/figure licence or copyright note shown in the figure note.'
        }
      },
      citations: [
        {
          citationId: 'sample-image-smh-1954',
          citationMode: '',
          type: 'image',
          sourceType: 'image',
          author: 'Sydney Morning Herald',
          authorFirstName: '',
          authorLastName: '',
          contributors: [{ name: 'Sydney Morning Herald', isOrganization: true }],
          year: '',
          date: '1954-12-15',
          title: 'Raymond Leach tracing an original animation on celluloid while Eric Porter and Val Walker work in the animation studio [Photograph]',
          source: 'SuperStock',
          publisher: '',
          siteName: '',
          version: '',
          prompt: '',
          notes: '',
          url: 'https://images.example.edu/search/animation%20disk/detail/107_4950005',
          doi: '',
          volume: '',
          issue: '',
          pages: '',
          page: '',
          license: '',
          isAdapted: false,
          platform: 'youtube',
          retrievalDateEnabled: false,
          retrievalDate: ''
        },
        {
          citationId: 'sample-book',
          citationMode: '',
          type: 'book',
          sourceType: 'book',
          author: '',
          authorFirstName: 'Frank',
          authorLastName: 'Thomas',
          contributors: [],
          year: '1981',
          date: '',
          title: 'The illusion of life: Disney animation',
          source: 'Disney Editions',
          publisher: 'Disney Editions',
          siteName: '',
          version: '',
          prompt: '',
          notes: '',
          url: '',
          doi: '',
          volume: '',
          issue: '',
          pages: '',
          page: '',
          license: '',
          isAdapted: false,
          platform: 'youtube',
          retrievalDateEnabled: false,
          retrievalDate: ''
        },
        {
          citationId: 'sample-webpage',
          citationMode: '',
          type: 'webpage',
          sourceType: 'webpage',
          author: '',
          authorFirstName: '',
          authorLastName: '',
          contributors: [{ name: 'Riverbend College', isOrganization: true }],
          year: '',
          date: '2024-03-08',
          title: 'Traditional animation techniques',
          source: 'Riverbend College',
          publisher: '',
          siteName: 'Riverbend College',
          version: '',
          prompt: '',
          notes: '',
          url: 'https://www.example.edu/example-article',
          doi: '',
          volume: '',
          issue: '',
          pages: '',
          page: '',
          license: '',
          isAdapted: false,
          platform: 'youtube',
          retrievalDateEnabled: false,
          retrievalDate: ''
        }
      ]
    };

    const dataStr = JSON.stringify(sample, null, 2);
    const blob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'citation-sample.json';
    anchor.click();
    URL.revokeObjectURL(url);

    showCopyPasteToast('Downloaded sample citation JSON.');
  };

  // ----- Project timeline modal helpers -----
  const timelineStepsForDisplay = Array.isArray(formData.timelineSteps) ? formData.timelineSteps : [];

  const updateTimelineStep = (index, patch) => {
    setFormData(prev => ({
      ...prev,
      timelineSteps: (prev.timelineSteps || []).map((step, i) => (i === index ? { ...step, ...patch } : step))
    }));
  };

  const addTimelineStep = () => {
    setFormData(prev => ({
      ...prev,
      timelineSteps: [...(prev.timelineSteps || []), createTimelineStep((prev.timelineSteps || []).length)]
    }));
  };

  const removeTimelineStep = (index) => {
    setFormData(prev => {
      const steps = prev.timelineSteps || [];
      if (steps.length <= 1) return prev;
      return { ...prev, timelineSteps: steps.filter((_, i) => i !== index) };
    });
  };

  const moveTimelineStep = (index, direction) => {
    setFormData(prev => {
      const steps = [...(prev.timelineSteps || [])];
      const target = index + direction;
      if (target < 0 || target >= steps.length) return prev;
      [steps[index], steps[target]] = [steps[target], steps[index]];
      return { ...prev, timelineSteps: steps };
    });
  };

  const handleTimelineSampleJson = () => {
    downloadTimelineJson(buildTimelineSamplePayload(), 'timeline-sample.json');
    showCopyPasteToast('Downloaded sample timeline JSON.');
  };

  const handleTimelineExportJson = () => {
    const steps = timelineStepsForDisplay.map((step, index) => sanitizeTimelineStep(step, index));
    if (!steps.length) {
      showCopyPasteToast('Add timeline steps before exporting.', 'error');
      return;
    }
    const title = formData.timelineTitle || '';
    const payload = {
      type: TIMELINE_EXPORT_TYPE,
      version: 1,
      exportedAt: new Date().toISOString(),
      title,
      steps: steps.map(({ color, timeSpan, title: stepTitle, icon, text, position }) => ({
        color, timeSpan, title: stepTitle, icon, text, position
      }))
    };
    const slug = (title || 'timeline').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40) || 'timeline';
    downloadTimelineJson(payload, `${slug}.timeline.json`);
    showCopyPasteToast('Exported timeline to JSON.');
  };

  const handleTimelineImportJson = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'application/json,.json';
    input.onchange = (event) => {
      const file = event.target?.files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (ev) => {
        try {
          const data = JSON.parse(ev.target?.result || '{}');
          let rawSteps = [];
          let nextTitle = null;
          if (Array.isArray(data)) {
            rawSteps = data;
          } else if (data && typeof data === 'object') {
            if (Array.isArray(data.steps)) rawSteps = data.steps;
            if (typeof data.title === 'string') nextTitle = data.title;
          }
          const cleaned = rawSteps
            .filter((entry) => entry && typeof entry === 'object')
            .map((entry, index) => sanitizeTimelineStep(entry, index));
          if (!cleaned.length) {
            showCopyPasteToast('No timeline steps found in that file.', 'error');
            return;
          }
          setFormData(prev => ({
            ...prev,
            ...(nextTitle !== null ? { timelineTitle: nextTitle } : {}),
            timelineSteps: cleaned
          }));
          showCopyPasteToast(`Imported ${cleaned.length} timeline step${cleaned.length === 1 ? '' : 's'}.`);
        } catch (err) {
          console.error('Timeline import failed', err);
          showCopyPasteToast('Could not read that file as JSON.', 'error');
        }
      };
      reader.readAsText(file);
    };
    input.click();
  };

  const handleFieldChange = useCallback((fieldName, value) => {
    setFormData(prev => ({ ...prev, [fieldName]: value }));
  }, []);

  const clearVideoCitationFields = useCallback(() => {
    handleFieldChange('videoTitle', '');
    handleFieldChange('videoAuthor', '');
    handleFieldChange('videoSource', '');
    handleFieldChange('videoDate', '');
  }, [handleFieldChange]);

  const showCopyPasteToast = useCallback((message, type = 'success') => {
    setCopyPasteToast({ show: true, message, type });
    setTimeout(() => {
      setCopyPasteToast({ show: false, message: '', type: '' });
    }, 3000);
  }, []);

  /* ---------------- The video card's citation, as a file ------------------ */

  // A video's citation lives in four flat fields rather than in the
  // `citations` array the APA blocks use, so it needs its own three handlers.
  // The file they read and write is the same envelope those blocks use
  // (`boppps-apa-citation`), which is the point: a citation exported from a
  // video imports into a reference list, and one an assistant wrote for a
  // reference list imports here.

  /** One entry of that envelope, from the fields in this dialog. */
  const videoCitationEntry = useCallback(() => ({
    citationId: `video-${formData.id || generateId()}`,
    type: 'video',
    sourceType: 'video',
    author: (formData.videoAuthor || '').trim(),
    contributors: (formData.videoAuthor || '').trim()
      ? [{ name: (formData.videoAuthor || '').trim(), isOrganization: true }]
      : [],
    year: '',
    date: (formData.videoDate || '').trim(),
    title: (formData.videoTitle || '').trim(),
    source: (formData.videoSource || '').trim(),
    url: (formData.videoUrl || '').trim(),
    platform: formData.videoPlatform || detectPlatform(formData.videoUrl) || 'youtube'
  }), [formData]);

  /**
   * The other direction: an entry from a file onto this dialog's fields.
   *
   * A group author (a channel, a studio) can arrive as `author`, as a
   * contributor marked as an organization, or split into first/last name, so
   * all three are read. `date` is a native date input here, so a year-only
   * entry leaves it empty rather than inventing January 1st.
   */
  const applyVideoCitationEntry = useCallback((entry) => {
    const clean = (value) => (typeof value === 'string' ? value.trim() : '');
    const organization = Array.isArray(entry.contributors)
      ? entry.contributors.find((person) => person && person.isOrganization && clean(person.name))
      : null;
    const named = [clean(entry.authorLastName), clean(entry.authorFirstName)].filter(Boolean).join(', ');
    const author = clean(entry.author) || clean(organization && organization.name) || named;
    const date = clean(entry.date);
    const isFullDate = /^\d{4}-\d{2}-\d{2}$/.test(date);
    const url = clean(entry.url);
    // A link that arrives with the citation is the block's link too, so it
    // has to move the platform dropdown the same way typing it would —
    // otherwise the block saves as YouTube and refuses its own address.
    const platform = url ? detectPlatform(url) : null;

    setFormData((prev) => ({
      ...prev,
      videoTitle: clean(entry.title) || prev.videoTitle || '',
      videoAuthor: author || prev.videoAuthor || '',
      videoSource: clean(entry.source) || clean(entry.siteName) || clean(entry.publisher) || prev.videoSource || '',
      videoDate: isFullDate ? date : (prev.videoDate || ''),
      videoUrl: url || prev.videoUrl || '',
      videoPlatform: platform || prev.videoPlatform || 'youtube',
      // Filling the fields in is also the answer to "should this be cited?".
      videoIncludeCitation: true
    }));
    return { hasYearOnly: Boolean(clean(entry.year)) && !isFullDate };
  }, []);

  const handleExportVideoCitationJson = useCallback(() => {
    const entry = videoCitationEntry();
    if (!entry.title && !entry.author && !entry.source && !entry.url) {
      showCopyPasteToast('Add the video details before exporting.', 'error');
      return;
    }
    const slug = (entry.title || 'video')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 40) || 'video';
    downloadJsonFile(`${slug}.citation.json`, {
      type: 'boppps-apa-citation',
      version: 1,
      exportedAt: new Date().toISOString(),
      citations: [entry]
    });
    showCopyPasteToast('Exported this video citation as JSON.');
  }, [videoCitationEntry, showCopyPasteToast]);

  const handleDownloadVideoCitationSample = useCallback(() => {
    downloadJsonFile('video-citation-sample.json', {
      type: 'boppps-apa-citation',
      version: 1,
      _readme: {
        description: 'Citation import file for the LessonPlanner One video block. Import it with the "Import JSON" button beside the video citation fields.',
        howToUse: 'Only the "citations" array is read, and only its first entry fills a video block — a video card cites one video. Keys beginning with "_" (like this one) are ignored, so this file imports as it stands.',
        fieldNotes: {
          sourceType: 'Leave this as "video".',
          author: 'The channel, studio or person who published it. For a group author, also list it under "contributors" as { "name": "...", "isOrganization": true } so it is not inverted to "Lastname, F.".',
          date: 'Full publication date, YYYY-MM-DD. A year on its own cannot fill the date box in the editor, so type it there by hand.',
          title: 'The video own title, without a medium tag - the app adds "[Video]" to the reference itself.',
          source: 'Where it is published: YouTube, Vimeo, Disney Video, your Panopto server.',
          url: 'The page a reader would open to watch it, not the /embed/ player address.'
        }
      },
      citations: [
        {
          citationId: 'sample-video',
          type: 'video',
          sourceType: 'video',
          author: 'Disney',
          contributors: [{ name: 'Disney', isOrganization: true }],
          year: '',
          date: '2012-11-19',
          title: 'Peter Pan trailer',
          source: 'Disney Video',
          url: 'https://video.disney.com/watch/peter-pan-trailer-4bd800c3ddcbb93a6ac45c67',
          platform: 'disney'
        }
      ]
    });
    showCopyPasteToast('Downloaded the sample citation JSON.');
  }, [showCopyPasteToast]);

  const handleImportVideoCitationJson = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'application/json,.json';
    input.onchange = (event) => {
      const file = event.target && event.target.files ? event.target.files[0] : null;
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (loaded) => {
        let parsed;
        try {
          parsed = JSON.parse((loaded.target && loaded.target.result) || '{}');
        } catch (error) {
          showCopyPasteToast('Could not read that file as JSON.', 'error');
          return;
        }
        // A bare entry, a bare array, or the whole envelope — an assistant
        // hands back whichever of the three it felt like.
        const list = Array.isArray(parsed)
          ? parsed
          : (Array.isArray(parsed && parsed.citations) ? parsed.citations : [parsed]);
        const entry = list.find((candidate) => candidate && typeof candidate === 'object'
          && (candidate.title || candidate.author || candidate.url || candidate.source));
        if (!entry) {
          showCopyPasteToast('No citation details found in that file.', 'error');
          return;
        }
        const { hasYearOnly } = applyVideoCitationEntry(entry);
        const ignored = list.length - 1;
        const extra = ignored > 0
          ? ` ${ignored} further entr${ignored === 1 ? 'y was' : 'ies were'} ignored — a video card cites one video.`
          : '';
        const yearNote = hasYearOnly
          ? ' It carried a year but no full date, so the date box was left for you.'
          : '';
        showCopyPasteToast(`Imported the citation.${extra}${yearNote}`);
      };
      reader.onerror = () => showCopyPasteToast('That file could not be read.', 'error');
      reader.readAsText(file);
    };
    input.click();
  }, [applyVideoCitationEntry, showCopyPasteToast]);

  /* ---------------- Custom HTML/CSS: moving around, and loading files ------ */

  /**
   * Bring one of the dialog's two code boxes up to the top of its working area.
   *
   * `scrollIntoView` is not used, and the reason is the dialog's own furniture:
   * its header and tab strip sit above the scrolling area, so aligning a
   * heading to the top of the *viewport* tucks it underneath them. The offset
   * is measured against the scroll container instead, which is the box the code
   * actually has to appear inside.
   *
   * One function for both directions. The trip up is the same problem as the
   * trip down -- a layout of any size puts three hundred lines between the two
   * boxes, and going back to the markup to check a class name is that same
   * scroll in reverse -- so a second copy would be these twenty lines again,
   * and it is where the reduced-motion fallback would quietly go missing.
   */
  const scrollDialogToEditor = useCallback((targetRef, fieldSelector) => {
    const container = modalBodyRef.current;
    const target = targetRef?.current;
    if (!container || !target) return;
    // A little air above it, so the label reads as a heading rather than as
    // something clipped by the edge.
    const GAP = 12;
    const start = container.scrollTop;
    const top = Math.max(0, target.getBoundingClientRect().top
      - container.getBoundingClientRect().top
      + start
      - GAP);

    // Smooth where it is wanted and available, instant where it is not.
    // `behavior: 'smooth'` is not always honoured — a reduced-motion setting
    // is the reason to skip it deliberately, and a container that is not being
    // composited is one where it silently does nothing at all. A jump button
    // that sometimes does nothing is worse than one that never animates, so
    // the destination is guaranteed either way.
    const reducedMotion = typeof window !== 'undefined'
      && window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;

    if (reducedMotion || typeof container.scrollTo !== 'function') {
      container.scrollTop = top;
    } else {
      container.scrollTo({ top, behavior: 'smooth' });
      // Not "has it arrived" — a real animation is still in flight at this
      // point — but "has it moved at all". A container still exactly where it
      // started is one where the animation never ran.
      setTimeout(() => {
        if (container.scrollTop === start && start !== top) container.scrollTop = top;
      }, 250);
    }

    // Ready to work in, without a second scroll fighting the first.
    const field = container.querySelector(fieldSelector);
    if (field?.focus) field.focus({ preventScroll: true });
  }, []);

  /** The HTML box's down arrow: on to the CSS. */
  const scrollToCssEditor = useCallback(
    () => scrollDialogToEditor(cssSectionRef, '#custom-css-source'),
    [scrollDialogToEditor]
  );

  /** The CSS box's up arrow: back to the markup those rules are written for. */
  const scrollToHtmlEditor = useCallback(
    () => scrollDialogToEditor(htmlSectionRef, '#custom-html-source'),
    [scrollDialogToEditor]
  );

  /** Put a read import into the two boxes. */
  const applyImportedLayout = useCallback((incoming, sources = {}) => {
    if (incoming.html !== null && incoming.html !== undefined) handleFieldChange('customHtml', incoming.html);
    if (incoming.css !== null && incoming.css !== undefined) handleFieldChange('customCss', incoming.css);
    const landed = [
      incoming.html !== null && incoming.html !== undefined ? `HTML from ${sources.html || 'the file'}` : '',
      incoming.css !== null && incoming.css !== undefined ? `CSS from ${sources.css || 'the file'}` : ''
    ].filter(Boolean);
    setImportStatus({ tone: 'ok', message: `Imported ${landed.join(' and ')}.` });
  }, [handleFieldChange]);

  /**
   * "Import HTML/CSS": read one or two files into the editor.
   *
   * The picker takes both at once, and the extension decides which box each
   * one lands in — the pairing an author already has on disk (`slide.html` and
   * `slide.css`) goes in in one go.
   */
  const handleImportCustomHtmlFiles = useCallback(async () => {
    if (isImporting) return;
    setIsImporting(true);
    setImportStatus(null);
    try {
      const picked = await pickFile({ accept: IMPORT_ACCEPT, multiple: true });
      const files = Array.isArray(picked) ? picked : (picked ? [picked] : []);
      if (!files.length) return;

      const { html, css, sources, errors } = await readImportedFiles(files);

      if (errors.length) {
        setImportStatus({ tone: 'error', message: errors.join(' ') });
        // A batch that produced nothing usable stops here; one that produced
        // something still offers it, so a stray extra file does not throw away
        // the file the author actually wanted.
        if (html === null && css === null) return;
      }

      const incoming = { html, css };
      const replacing = importWouldReplace({
        incoming,
        current: { html: formData.customHtml, css: formData.customCss }
      });

      if (replacing.length) {
        setPendingImport({ incoming, replacing, sources });
        return;
      }
      applyImportedLayout(incoming, sources);
    } catch (error) {
      console.error('[customHtmlImport] Could not import', error);
      setImportStatus({ tone: 'error', message: 'Those files could not be read.' });
    } finally {
      setIsImporting(false);
    }
  }, [applyImportedLayout, formData.customCss, formData.customHtml, isImporting]);

  const confirmPendingImport = useCallback(() => {
    if (!pendingImport) return;
    applyImportedLayout(pendingImport.incoming, pendingImport.sources);
    setPendingImport(null);
  }, [applyImportedLayout, pendingImport]);

  const cancelPendingImport = useCallback(() => {
    setPendingImport(null);
    setImportStatus({ tone: 'ok', message: 'Import cancelled — the editor is unchanged.' });
  }, []);

  useEffect(() => {
    if (citationScrollTarget === null) return;
    const container = modalBodyRef.current;
    if (!container) return;
    const target = container.querySelector(`#citation-block-${citationScrollTarget}`);
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    setCitationScrollTarget(null);
  }, [citationScrollTarget, formData.citations]);

  const normalizeMultiColumnSpan = (spanValue, colIndex, totalColumns) => {
    const maxSpan = Math.max(1, totalColumns - colIndex);
    const normalized = Math.max(Number(spanValue) || 1, 1);
    return Math.min(normalized, maxSpan);
  };

  const normalizeMultiColumnCard = useCallback((card = {}) => ({
    title: card.title || '',
    primaryTitle: card.primaryTitle || card.title || '',
    categoryLabel: card.categoryLabel || '',
    instructionalText: card.instructionalText || '',
    numberedList: card.numberedList || [],
    numberedListStyle: card.numberedListStyle || 'numbered',
    titleAlignment: card.titleAlignment || 'center',
    iconName: card.iconName || '',
    content: card.content || '',
    isChecklist: card.isChecklist || false,
    checklistItems: card.checklistItems || [],
    backgroundColor: card.backgroundColor || 'transparent',
    borderColor: card.borderColor || '#94a3b8',
    showBorder: card.showBorder !== false,
    titleColor: card.titleColor || undefined,
    checkmarkColor: card.checkmarkColor || undefined,
    listStyle: card.listStyle || 'checkbox',
    badge: { ...defaultBadgeConfig, ...(card.badge || {}) },
    callout: {
      enabled: card.callout?.enabled || false,
      title: card.callout?.title || '',
      description: card.callout?.description || '',
      borderColor: card.callout?.borderColor || undefined,
      backgroundColor: card.callout?.backgroundColor || undefined,
      position: card.callout?.position || 'top'
    },
    imagePlaceholder: card.imagePlaceholder || false,
    image: card.image || {
      src: '',
      alt: '',
      caption: '',
      imageTitle: '',
      imageAuthor: '',
      imageSource: '',
      imageDate: ''
    },
    videoPlaceholder: card.videoPlaceholder || false,
    video: card.video || {
      src: '',
      videoUrl: '',
      videoPlatform: 'youtube',
      embedCode: '',
      aspectRatio: '16-9',
      videoTitle: '',
      videoAuthor: '',
      videoSource: '',
      videoDate: ''
    },
    placeholderHeading: card.placeholderHeading || '',
    placeholderText: card.placeholderText || '',
    columnSpan: Number(card.columnSpan) || 1,
    rowIndex: Number.isInteger(Number(card.rowIndex)) ? Number(card.rowIndex) : undefined
  }), [defaultBadgeConfig]);

  const normalizeMultiColumnColumns = useCallback((inputColumns = []) => {
    const fallbackColumns = [{ cards: [{ title: '', content: '', isChecklist: false, checklistItems: [], backgroundColor: 'transparent', borderColor: '#94a3b8', showBorder: true }] }];
    const columns = (inputColumns.length > 0 ? inputColumns : fallbackColumns).map((column = {}) => ({
      widthPercent: column.widthPercent || 100,
      cards: (column.cards || [{ title: '', content: '', isChecklist: false, checklistItems: [], backgroundColor: 'transparent', borderColor: '#94a3b8', showBorder: true }]).map(normalizeMultiColumnCard)
    }));

    const totalColumns = Math.max(columns.length, 1);
    return columns.map((column, colIndex) => {
      const maxSpan = Math.max(1, totalColumns - colIndex);
      const cards = (column.cards || []).map((card) => ({
        ...card,
        columnSpan: Math.min(Math.max(Number(card.columnSpan) || 1, 1), maxSpan)
      }));
      return { ...column, cards };
    });
  }, [normalizeMultiColumnCard]);

  const normalizeMultiColumnData = useCallback((rawData = {}) => {
    const sourceData = rawData?.multiColumnData ? rawData.multiColumnData : rawData;
    const inputColumns = sourceData?.columns || rawData?.columns || [];
    const normalizedColumns = normalizeMultiColumnColumns(inputColumns);
    const matchHeights = sourceData?.matchHeights ?? rawData?.matchHeights ?? false;
    const matchHeightColumns = sourceData?.matchHeightColumns ?? rawData?.matchHeightColumns ?? [];
    const blockTitleEnabled = sourceData?.blockTitleEnabled ?? rawData?.blockTitleEnabled ?? false;
    const blockTitle = sourceData?.blockTitle ?? rawData?.blockTitle ?? '';
    const filteredMatchHeightColumns = (matchHeightColumns || [])
      .map((colIndex) => Number(colIndex))
      .filter((colIndex) => Number.isInteger(colIndex) && colIndex >= 0 && colIndex < normalizedColumns.length);

    return {
      columns: normalizedColumns,
      matchHeights,
      matchHeightColumns: filteredMatchHeightColumns,
      blockTitleEnabled,
      blockTitle
    };
  }, [normalizeMultiColumnColumns]);

  const buildMultiColumnSaveData = (sourceData = {}) => {
    const inputColumns = sourceData.columns || [];
    const matchHeights = sourceData.matchHeights || false;
    const blockTitleEnabled = sourceData.blockTitleEnabled || false;
    const blockTitle = sourceData.blockTitle || '';
    const matchHeightColumns = (sourceData.matchHeightColumns || [])
      .map((colIndex) => Number(colIndex))
      .filter((colIndex) => Number.isInteger(colIndex) && colIndex >= 0 && colIndex < inputColumns.length);

    const columns = inputColumns
      .map((column, colIndex) => {
        const maxSpan = Math.max(1, inputColumns.length - colIndex);

        return ({
          widthPercent: column.widthPercent || 100,
          cards: (column.cards || [])
            .filter(card =>
              card.title
              || card.primaryTitle
              || card.categoryLabel
              || card.instructionalText
              || (card.numberedList && card.numberedList.some(item => item.text))
              || card.content
              || (card.isChecklist && card.checklistItems?.some(item => item.text))
              || card.imagePlaceholder
              || card.image?.src
              || card.videoPlaceholder
              || card.video?.src
            )
            .map(card => {
              const parsedSpan = Number(card.columnSpan);
              const columnSpan = Math.min(Math.max(Number.isInteger(parsedSpan) ? parsedSpan : 1, 1), maxSpan);
              const parsedRowIndex = Number(card.rowIndex);
              const rowIndex = Number.isInteger(parsedRowIndex) && parsedRowIndex > 0 ? parsedRowIndex : undefined;

              return ({
                title: card.title || card.primaryTitle || '',
                content: card.content || '',
                isChecklist: card.isChecklist || false,
                checklistItems: card.isChecklist ? (card.checklistItems || []).filter(item => item.text) : [],
                backgroundColor: card.backgroundColor || 'transparent',
                borderColor: card.borderColor || '#94a3b8',
                showBorder: card.showBorder !== false,
                titleColor: card.titleColor || undefined,
                checkmarkColor: card.checkmarkColor || undefined,
                listStyle: card.listStyle || 'checkbox',
                categoryLabel: card.categoryLabel || '',
                primaryTitle: card.primaryTitle || '',
                instructionalText: card.instructionalText || '',
                titleAlignment: card.titleAlignment || 'center',
                iconName: card.iconName || '',
                numberedListStyle: card.numberedListStyle || 'numbered',
                numberedList: (card.numberedList || []).filter(item => item.text),
                badge: {
                  enabled: card.badge?.enabled || false,
                  type: card.badge?.type || 'theme',
                  color: card.badge?.color,
                  color2: card.badge?.color2,
                  gradientDirection: card.badge?.gradientDirection || 'to right'
                },
                callout: {
                  enabled: card.callout?.enabled || false,
                  title: card.callout?.title || '',
                  description: card.callout?.description || '',
                  borderColor: card.callout?.borderColor || '#94a3b8',
                  backgroundColor: card.callout?.backgroundColor || undefined,
                  position: card.callout?.position || 'top'
                },
                imagePlaceholder: card.imagePlaceholder || false,
                image: card.image || undefined,
                videoPlaceholder: card.videoPlaceholder || false,
                video: card.video || undefined,
                placeholderHeading: card.placeholderHeading || '',
                placeholderText: card.placeholderText || '',
                columnSpan,
                rowIndex
              });
            })
        });
      })
      .filter(column => column.cards.length > 0);

    return {
      columns,
      matchHeights,
      matchHeightColumns,
      blockTitleEnabled,
      blockTitle,
      multiColumnData: {
        columns,
        matchHeights,
        matchHeightColumns,
        blockTitleEnabled,
        blockTitle
      }
    };
  };

  const parseClipboardCardText = (text) => {
    if (!text) return null;
    try {
      const parsed = JSON.parse(text);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch (error) {
      return null;
    }
  };

  const handleCopyMultiColumnCard = async (card) => {
    console.log('[Copy] Starting copy operation', card);
    if (!card) {
      showCopyPasteToast('No card to copy', 'error');
      return;
    }
    const cloned = cloneMultiColumnCardData(card);
    writeMultiColumnCardClipboard(cloned);
    setMultiColumnClipboard(cloned);
    console.log('[Copy] Card copied to clipboard:', cloned);

    const cardTitle = stripHtml(card.title || card.primaryTitle || card.content || 'Untitled card');
    const truncatedTitle = cardTitle.length > 30 ? cardTitle.substring(0, 30) + '...' : cardTitle;

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(JSON.stringify(cloned, null, 2));
      }
      showCopyPasteToast(`✓ Copied: ${truncatedTitle}`, 'success');
      console.log('[Copy] Copy operation complete');
    } catch (error) {
      console.warn('[LessonTemplate] Unable to copy multi-column card to clipboard', error);
      showCopyPasteToast(`✓ Copied: ${truncatedTitle}`, 'success');
    }
  };

  const handlePasteMultiColumnCard = async (columnsList, colIndex, cardIndex) => {
    console.log('[Paste] Starting paste operation', { colIndex, cardIndex, hasClipboard: !!multiColumnClipboard });

    let clipboard = readMultiColumnCardClipboard();
    if (!clipboard && navigator?.clipboard?.readText) {
      try {
        const clipboardText = await navigator.clipboard.readText();
        clipboard = parseClipboardCardText(clipboardText);
      } catch (error) {
        console.warn('[LessonTemplate] Unable to read system clipboard for multi-column card', error);
      }
    }
    if (!clipboard) {
      console.warn('[Paste] No clipboard data found');
      showCopyPasteToast('No card in clipboard to paste', 'error');
      return;
    }

    const pasted = cloneMultiColumnCardData(clipboard);
    console.log('[Paste] Card data to paste:', pasted);

    setFormData((prev) => {
      console.log('[Paste] Current formData:', prev);
      const sourceColumns = prev.multiColumnData?.columns || columnsList || [];
      const totalColumns = Math.max(sourceColumns.length, 1);
      const newColumns = [...sourceColumns];

      // Ensure the target column exists
      if (!newColumns[colIndex]) {
        newColumns[colIndex] = { widthPercent: 100, cards: [] };
      }

      const newCards = [...(newColumns[colIndex]?.cards || [])];
      pasted.columnSpan = normalizeMultiColumnSpan(pasted.columnSpan, colIndex, totalColumns);
      newCards.splice(cardIndex + 1, 0, pasted);
      newColumns[colIndex] = {
        widthPercent: newColumns[colIndex]?.widthPercent || 100,
        ...newColumns[colIndex],
        cards: newCards
      };

      // Ensure multiColumnData exists with all required properties
      const updatedMultiColumnData = {
        matchHeights: prev.multiColumnData?.matchHeights || false,
        matchHeightColumns: prev.multiColumnData?.matchHeightColumns || [],
        blockTitleEnabled: prev.multiColumnData?.blockTitleEnabled || false,
        blockTitle: prev.multiColumnData?.blockTitle || '',
        ...prev.multiColumnData,
        columns: newColumns
      };

      const updated = {
        ...prev,
        multiColumnData: updatedMultiColumnData
      };

      console.log('[Paste] Updated formData:', updated);
      return updated;
    });

    writeMultiColumnCardClipboard(pasted);
    setMultiColumnClipboard(pasted);

    const cardTitle = stripHtml(pasted.title || pasted.primaryTitle || pasted.content || 'Untitled card');
    const truncatedTitle = cardTitle.length > 30 ? cardTitle.substring(0, 30) + '...' : cardTitle;
    showCopyPasteToast(`✓ Pasted: ${truncatedTitle}`, 'success');
    console.log('[Paste] Paste operation complete');
  };

  const buildMultiColumnCardJson = (card) => JSON.stringify(cloneMultiColumnCardData(card), null, 2);

  const handleCopyCardJson = async (card) => {
    const cardJson = buildMultiColumnCardJson(card);
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(cardJson);
      }
    } catch (error) {
      console.warn('[LessonTemplate] Unable to copy card JSON', error);
    }
  };

  const parseMultiColumnJsonInput = (rawText) => {
    const trimmed = rawText.trim();
    if (!trimmed) {
      throw new Error('Please paste JSON to import.');
    }

    const attemptParse = (value) => {
      try {
        return { parsed: JSON.parse(value), error: null };
      } catch (error) {
        return { parsed: null, error };
      }
    };

    const baseAttempt = attemptParse(trimmed);
    if (!baseAttempt.error) {
      return baseAttempt.parsed;
    }

    const unescaped = trimmed
      .replace(/\\n/g, '\n')
      .replace(/\\r/g, '\r')
      .replace(/\\t/g, '\t')
      .replace(/\\"/g, '"');
    const unescapedAttempt = attemptParse(unescaped);
    if (!unescapedAttempt.error) {
      return unescapedAttempt.parsed;
    }

    const stringWrappedAttempt = attemptParse(trimmed.replace(/^"|"$/g, ''));
    if (!stringWrappedAttempt.error) {
      return stringWrappedAttempt.parsed;
    }

    const nestedStringAttempt = attemptParse(JSON.stringify(unescaped));
    if (!nestedStringAttempt.error && typeof nestedStringAttempt.parsed === 'string') {
      return JSON.parse(nestedStringAttempt.parsed);
    }

    throw baseAttempt.error;
  };

  const isStandaloneMultiColumnCard = (parsed) => {
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return false;
    if (parsed.columns || parsed.cards || parsed.multiColumnData?.columns) return false;
    return [
      'title',
      'primaryTitle',
      'categoryLabel',
      'instructionalText',
      'numberedList',
      'content',
      'isChecklist',
      'backgroundColor',
      'borderColor',
      'showBorder',
      'badge',
      'callout',
      'image',
      'video',
      'placeholderHeading',
      'placeholderText',
      'columnSpan'
    ].some((key) => key in parsed);
  };

  const handleMultiColumnJsonImport = () => {
    try {
      const parsed = parseMultiColumnJsonInput(multiColumnJsonInput || '');
      let inputData = parsed;

      if (Array.isArray(parsed)) {
        inputData = { columns: parsed };
      } else if (parsed?.columns) {
        inputData = parsed;
      } else if (parsed?.cards) {
        inputData = { columns: [parsed] };
      } else if (parsed?.multiColumnData?.columns) {
        inputData = parsed.multiColumnData;
      } else if (isStandaloneMultiColumnCard(parsed)) {
        inputData = { columns: [{ cards: [parsed] }] };
      } else {
        throw new Error('JSON must include a columns array, a cards array, or a single card object.');
      }

      const normalized = normalizeMultiColumnData(inputData);
      const resolvedBlockTitleEnabled = normalized.blockTitleEnabled || Boolean(normalized.blockTitle);
      const resolved = {
        ...normalized,
        blockTitleEnabled: resolvedBlockTitleEnabled
      };
      setFormData((prev) => ({
        ...prev,
        multiColumnData: resolved,
        columns: resolved.columns,
        matchHeights: resolved.matchHeights,
        matchHeightColumns: resolved.matchHeightColumns,
        blockTitleEnabled: resolved.blockTitleEnabled,
        blockTitle: resolved.blockTitle
      }));
      setMultiColumnJsonInput('');
      setMultiColumnJsonError('');
      setShowMultiColumnPaste(false);
    } catch (error) {
      setMultiColumnJsonError(error?.message || 'Invalid JSON provided.');
    }
  };

  const handleMultiColumnJsonImportAndSave = () => {
    try {
      const parsed = parseMultiColumnJsonInput(multiColumnJsonInput || '');
      let inputData = parsed;

      if (Array.isArray(parsed)) {
        inputData = { columns: parsed };
      } else if (parsed?.columns) {
        inputData = parsed;
      } else if (parsed?.cards) {
        inputData = { columns: [parsed] };
      } else if (parsed?.multiColumnData?.columns) {
        inputData = parsed.multiColumnData;
      } else if (isStandaloneMultiColumnCard(parsed)) {
        inputData = { columns: [{ cards: [parsed] }] };
      } else {
        throw new Error('JSON must include a columns array, a cards array, or a single card object.');
      }

      const normalized = normalizeMultiColumnData(inputData);
      const resolvedBlockTitleEnabled = normalized.blockTitleEnabled || Boolean(normalized.blockTitle);
      const resolved = {
        ...normalized,
        blockTitleEnabled: resolvedBlockTitleEnabled
      };
      const savePayload = buildMultiColumnSaveData(resolved);
      const targetSectionId = formData.sectionId || initialData.sectionId;

      setFormData((prev) => ({
        ...prev,
        multiColumnData: resolved,
        columns: resolved.columns,
        matchHeights: resolved.matchHeights,
        matchHeightColumns: resolved.matchHeightColumns,
        blockTitleEnabled: resolved.blockTitleEnabled,
        blockTitle: resolved.blockTitle
      }));

      onSave({
        ...formData,
        ...savePayload,
        type: 'multi-column-cards',
        sectionId: targetSectionId
      });
      setMultiColumnJsonInput('');
      setMultiColumnJsonError('');
      setShowMultiColumnPaste(false);
      onClose();
    } catch (error) {
      setMultiColumnJsonError(error?.message || 'Invalid JSON provided.');
    }
  };

  const adaptWorksheetQuestionsForEditor = useCallback((questions = []) => (
    (questions || []).map((q) => ({
      ...q,
      type: q?.type || 'short-answer',
      title: q?.title || q?.prompt || q?.question || '',
      content: q?.content || q?.instructions || '',
      points: Number.isFinite(q?.points) ? q.points : (q?.points ? Number(q.points) : 0),
      options: Array.isArray(q?.options)
        ? q.options
        : Array.isArray(q?.choices)
          ? q.choices
          : [],
      mode: q?.mode === 'multiple' || q?.allowMultiple ? 'multiple' : q?.mode || 'single',
      correctIndex: typeof q?.correctIndex === 'number' ? q.correctIndex : -1,
      correctIndices: Array.isArray(q?.correctIndices) ? q.correctIndices : [],
    }))
  ), []);

  const handleAddAccordion = () => {
    setFormData(prev => {
      const items = addAccordionItem(prev.accordionItems || []);
      return { ...prev, accordionItems: items, selectedAccordionId: items[items.length - 1].id };
    });
  };

  const handleRemoveAccordion = (id) => {
    setFormData(prev => {
      const items = removeAccordionItem(prev.accordionItems || [], id);
      const selected = prev.selectedAccordionId === id ? (items[0]?.id || null) : prev.selectedAccordionId;
      return { ...prev, accordionItems: items, selectedAccordionId: selected };
    });
  };

  const handleUpdateAccordion = (id, data) => {
    setFormData(prev => ({ ...prev, accordionItems: updateAccordionItem(prev.accordionItems || [], id, data) }));
  };

  const moveAccordion = (from, to) => {
    setFormData(prev => {
      const items = [...(prev.accordionItems || [])];
      if (from < 0 || to < 0 || from >= items.length || to >= items.length) return prev;
      const [moved] = items.splice(from, 1);
      items.splice(to, 0, moved);
      return { ...prev, accordionItems: reorderAccordionItems(prev.accordionItems || [], items) };
    });
  };
  
  useEffect(() => {
    // CRITICAL: Only initialize when modal opens, not when initialData reference changes
    // This prevents resetting formData while user is typing/pasting
    if (isOpen && !prevIsOpenRef.current) {
      console.log('[Modal] Initializing with contentType:', contentType);
      if (contentType === 'worksheet') {
        const worksheetQuestions = adaptWorksheetQuestionsForEditor(initialData.questions || initialData.worksheetQuestions || []);
        const initialWorksheetData = {
          ...initialData,
          worksheetTitle: initialData.title || '',
          worksheetDescription: initialData.description || initialData.instructions || '',
          worksheetTotalPoints: initialData.totalPoints || '',
          worksheetQuestions,
          worksheetUsePoints: initialData.usePoints !== false
        };
        setFormData(initialWorksheetData);
      } else if (contentType === 'accordion') {
        const { items } = createAccordionModal(initialData.items || initialData.accordionItems || []);
        setFormData({
          ...initialData,
          accordionItems: items,
          selectedAccordionId: items[0]?.id || null,
          accordionSettings: initialData.settings || {
            allowMultipleOpen: false,
            defaultOpenFirst: false,
            theme: 'default'
          }
        });
      } else if (contentType === 'video') {
        // Initialize video data with proper defaults
        const initialVideoData = {
          ...initialData,
          videoPlatform: initialData.videoPlatform || 'youtube',
          videoUrl: initialData.videoUrl || '',
          embedCode: initialData.embedCode || '',
          aspectRatio: initialData.aspectRatio || '16-9',
          videoTitle: initialData.videoTitle || '',
          videoAuthor: initialData.videoAuthor || '',
          videoDate: initialData.videoDate || '',
          videoSource: initialData.videoSource || ''
        };
        setFormData(initialVideoData);
      } else if (contentType === 'youtube-playlist') {
        // The card is built from one pasted link; everything else has a working
        // default so a freshly added block already renders.
        setFormData({
          ...initialData,
          playlistInput: initialData.playlistInput
            || initialData.playlistWatchUrl
            || (initialData.playlistId
              ? playlistWatchUrl({
                  playlistId: initialData.playlistId,
                  videoId: initialData.startVideoId,
                  index: initialData.startIndex
                })
              : ''),
          playlistId: initialData.playlistId || '',
          startVideoId: initialData.startVideoId || '',
          startIndex: initialData.startIndex || '',
          playlistTitle: initialData.playlistTitle || initialData.title || initialData.videoTitle || '',
          playlistChannel: initialData.playlistChannel || initialData.channel || initialData.videoAuthor || '',
          playlistDescription: initialData.playlistDescription || initialData.description || '',
          playlistItemCount: initialData.playlistItemCount || initialData.itemCount || '',
          playlistItems: normalizePlaylistItems(initialData.playlistItems || initialData.items),
          playlistLayout: initialData.playlistLayout || initialData.layout || 'split',
          playlistTheme: initialData.playlistTheme || initialData.theme || 'light',
          playlistAccentColor: initialData.playlistAccentColor || initialData.accentColor || DEFAULT_PLAYLIST_ACCENT,
          playlistShowThumbnails: (initialData.playlistShowThumbnails ?? initialData.showThumbnails) !== false,
          aspectRatio: initialData.aspectRatio || '16-9',
          videoDate: initialData.videoDate || '',
          videoSource: initialData.videoSource || 'YouTube',
          videoIncludeCitation: initialData.videoIncludeCitation !== false,
          videoAddToReferenceList: initialData.videoAddToReferenceList !== false,
          playlistBulkInput: ''
        });
      } else if (contentType === 'cards') {
        setFormData({
          ...initialData,
          cardItems: initialData.items || initialData.cardItems || []
        });
      } else if (contentType === 'stacked-cards') {
        setFormData({
          ...initialData,
          stackedCardItems: normalizeStackedCardItems(initialData.items || initialData.stackedCardItems),
          includeSideText: initialData.includeSideText !== false
        });
      } else if (contentType === 'multi-column-cards') {
        const normalized = normalizeMultiColumnData(initialData);
        setFormData({
          ...initialData,
          multiColumnData: normalized,
          blockTitleEnabled: normalized.blockTitleEnabled,
          blockTitle: normalized.blockTitle
        });
      } else if (contentType === 'html') {
        setFormData({
          ...initialData,
          codeContent: initialData.content || '',
          codeLanguage: initialData.language || 'html',
          codeMode: initialData.mode || 'displayCode',
          codeCustomCss: initialData.customCss || '',
          codeCitation: initialData.citation || '',
          codeSoftWrap: initialData.options?.softWrap ?? false,
          codeShowLineNumbers: initialData.options?.showLineNumbers ?? true,
          codeAllowScripts: initialData.options?.allowScripts ?? false,
        });
      } else if (contentType === 'custom-html') {
        setFormData({
          ...initialData,
          customHtml: initialData.html || '',
          customCss: initialData.css || '',
          customCssScope: initialData.cssScope === 'deck' ? 'deck' : 'slide',
          customImages: Array.isArray(initialData.images) ? initialData.images : [],
          customVideos: Array.isArray(initialData.videos) ? initialData.videos : [],
          citations: Array.isArray(initialData.citations) ? initialData.citations : [],
          citationOverlay: initialData.citationOverlay === true,
          citationOverlayPos: initialData.citationOverlayPos || null,
        });
      } else if (contentType === 'apa-quote') {
        setFormData({
          ...initialData,
          quoteText: initialData.quoteText || '',
          author: initialData.author || '',
          year: initialData.year || '',
          title: initialData.title || '',
          source: initialData.source || '',
          url: initialData.url || '',
          doi: initialData.doi || '',
          page: initialData.page || '',
          quoteSources: (initialData.quoteSources && initialData.quoteSources.length > 0)
            ? initialData.quoteSources
            : [{
                author: initialData.author || '',
                year: initialData.year || '',
                page: initialData.page || ''
              }]
        });
      } else if (contentType === 'apa-citation') {
        setFormData({
          ...initialData,
          citations: (initialData.citations && initialData.citations.length > 0)
            ? initialData.citations
            : [createEmptyCitation({ sourceType: 'paraphrase' })]
        });
      } else if (contentType === 'apa-reference-list') {
        setFormData({
          ...initialData,
          citations: (initialData.citations && initialData.citations.length > 0)
            ? initialData.citations
            : [createEmptyCitation({ sourceType: 'book' })]
        });
      } else {
        setFormData(initialData);
      }
      setIsHtmlMode(false);
      setModalHtmlMode(false);
      // Reopening an embedded image should land on the embed panel, not on the
      // upload panel with an apparently empty block.
      setImageSource(initialData.imageEmbedSrc ? 'embed' : 'upload');
      setActivePastedImage(0);
      // A layout is opened to be worked on, so the code is what it opens on
      // however the last session left it.
      setCustomHtmlTab('editor');
      lastEmbedSrcRef.current = null;
      autoEmbedFetchRef.current = null;
    }
    // Update ref to track modal state
    prevIsOpenRef.current = isOpen;
  }, [isOpen, initialData, contentType, adaptWorksheetQuestionsForEditor, normalizeMultiColumnData]);

  useEffect(() => {
    if (imageSource === 'upload' && formData.imageFiles) {
      const files = Array.from(formData.imageFiles);
      if (files.length > 0) {
        const newFormData = { ...formData };

        files.forEach((file, index) => {
          const baseFieldName = files.length > 1 ? `image_${index}` : 'image';
          if (!newFormData[`${baseFieldName}_alt`]) {
            newFormData[`${baseFieldName}_alt`] = file.name.replace(/\.[^/.]+$/, "");
          }
          if (!newFormData[`${baseFieldName}_caption`]) {
            newFormData[`${baseFieldName}_caption`] = '';
          }
          if (!newFormData[`${baseFieldName}_title`]) {
            newFormData[`${baseFieldName}_title`] = '';
          }
          if (!newFormData[`${baseFieldName}_author`]) {
            newFormData[`${baseFieldName}_author`] = '';
          }
          if (!newFormData[`${baseFieldName}_source`]) {
            newFormData[`${baseFieldName}_source`] = '';
          }
          if (!newFormData[`${baseFieldName}_date`]) {
            newFormData[`${baseFieldName}_date`] = '';
          }
          if (!newFormData[`${baseFieldName}_sourceType`]) {
            newFormData[`${baseFieldName}_sourceType`] = 'image';
          }
          if (!newFormData[`${baseFieldName}_page`]) {
            newFormData[`${baseFieldName}_page`] = '';
          }
          if (!newFormData[`${baseFieldName}_license`]) {
            newFormData[`${baseFieldName}_license`] = '';
          }
          if (!newFormData[`${baseFieldName}_tableNumber`]) {
            newFormData[`${baseFieldName}_tableNumber`] = '';
          }
          if (!newFormData[`${baseFieldName}_isAdapted`]) {
            newFormData[`${baseFieldName}_isAdapted`] = false;
          }
          if (!newFormData[`${baseFieldName}_includeCitation`]) {
            newFormData[`${baseFieldName}_includeCitation`] = true;
          }
        });

        setFormData(newFormData);
      }
    }
  }, [formData.imageFiles, imageSource]);

  useEffect(() => {
    if (imageSource === 'server' && formData.imageFilenames) {
      const filenames = formData.imageFilenames.split('\n').filter(Boolean);
      if (filenames.length > 0) {
        const newFormData = { ...formData };

        filenames.forEach((filename, index) => {
          const baseFieldName = filenames.length > 1 ? `image_${index}` : 'image';
          if (!newFormData[`${baseFieldName}_alt`]) {
            newFormData[`${baseFieldName}_alt`] = filename.replace(/\.[^/.]+$/, "");
          }
          if (!newFormData[`${baseFieldName}_caption`]) {
            newFormData[`${baseFieldName}_caption`] = '';
          }
          if (!newFormData[`${baseFieldName}_title`]) {
            newFormData[`${baseFieldName}_title`] = '';
          }
          if (!newFormData[`${baseFieldName}_author`]) {
            newFormData[`${baseFieldName}_author`] = '';
          }
          if (!newFormData[`${baseFieldName}_source`]) {
            newFormData[`${baseFieldName}_source`] = '';
          }
          if (!newFormData[`${baseFieldName}_date`]) {
            newFormData[`${baseFieldName}_date`] = '';
          }
          if (!newFormData[`${baseFieldName}_sourceType`]) {
            newFormData[`${baseFieldName}_sourceType`] = 'image';
          }
          if (!newFormData[`${baseFieldName}_page`]) {
            newFormData[`${baseFieldName}_page`] = '';
          }
          if (!newFormData[`${baseFieldName}_license`]) {
            newFormData[`${baseFieldName}_license`] = '';
          }
          if (!newFormData[`${baseFieldName}_tableNumber`]) {
            newFormData[`${baseFieldName}_tableNumber`] = '';
          }
          if (!newFormData[`${baseFieldName}_isAdapted`]) {
            newFormData[`${baseFieldName}_isAdapted`] = false;
          }
          if (!newFormData[`${baseFieldName}_includeCitation`]) {
            newFormData[`${baseFieldName}_includeCitation`] = true;
          }
        });

        setFormData(newFormData);
      }
    }
  }, [formData.imageFilenames, imageSource]);

  const handleFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

// Normalize incoming video URLs for consistent server requests
function normalizeVideoUrl(rawUrl, platform) {
  try {
    const u = new URL(rawUrl);
    if (platform === 'youtube') {
      const host = u.hostname.replace(/^m\./, '').replace(/^music\./, '');
      if (host.includes('youtu.be')) {
        const id = u.pathname.replace('/', '');
        return `https://www.youtube.com/watch?v=${id}`;
      }
      if (/youtube\.com$/.test(host) && u.pathname.startsWith('/shorts/')) {
        const id = u.pathname.split('/')[2] || '';
        return id ? `https://www.youtube.com/watch?v=${id}` : rawUrl;
      }
      if (/youtube\.com$/.test(host) && u.pathname.startsWith('/embed/')) {
        const id = u.pathname.split('/')[2] || '';
        return id ? `https://www.youtube.com/watch?v=${id}` : rawUrl;
      }
      if (/youtube\.com$/.test(host)) {
        const v = u.searchParams.get('v');
        if (v) return `https://www.youtube.com/watch?v=${v}`;
      }
    }
    if (platform === 'vimeo') {
      return `https://vimeo.com${u.pathname}`;
    }
    if (platform === 'disney') {
      // Strip tracking query strings; the path is the whole address. An
      // /embed/<id> link is left as it is — only Disney knows the slug that
      // belongs in the canonical /watch/ address, and the server returns it.
      return `https://video.disney.com${u.pathname}`;
    }
    return rawUrl; // Panopto: keep as-is
  } catch {
    return rawUrl;
  }
}


const fetchVideoInfo = async (url, platform) => {
  setIsLoadingVideoInfo(true);
  try {
    const normalizedUrl = normalizeVideoUrl(url, platform);
    try {
      const { isValid } = validateVideoUrl(normalizedUrl, platform);
      if (!isValid) console.warn('[VideoMeta] Local validation failed; trying server anyway.');
    } catch {}
    const res = await fetch(`/api/video-meta.php?platform=${encodeURIComponent(platform)}&url=${encodeURIComponent(normalizedUrl)}`);
    if (!res.ok) throw new Error(`Video meta request failed (${res.status})`);
    const data = await res.json();
    if (data.error) throw new Error(data.error);
    setFormData(prev => ({
      ...prev,
      videoTitle: data.title || '',
      videoAuthor: data.author || '',
      videoSource: data.platform || '',
      videoUrl: data.videoUrl || normalizedUrl,
      videoDate: data.date || '',
      videoChannelUrl: data.channelUrl || ''
    }));
    alert(`✅ ${data.platform} details fetched successfully.`);
  } catch (err) {
    console.error('Video info fetch error:', err);
    alert('⚠️ Could not fetch video details. Please enter them manually.');
  } finally {
    setIsLoadingVideoInfo(false);
  }
};

  // --- YouTube playlists ----------------------------------------------------
  // Everything the card needs is in the link the author copied out of the
  // address bar, so it is parsed on every keystroke: the list id, the video
  // they were watching, and its position in the queue.
  const parsedPlaylist = useMemo(
    () => parseYouTubePlaylist(formData.playlistInput || ''),
    [formData.playlistInput]
  );

  const playlistItemsForEditor = Array.isArray(formData.playlistItems) ? formData.playlistItems : [];

  const updatePlaylistItems = (updater) => {
    setFormData((prev) => {
      const current = Array.isArray(prev.playlistItems) ? prev.playlistItems : [];
      const next = updater(current).map((item, index) => ({ ...item, position: index + 1 }));
      return { ...prev, playlistItems: next };
    });
  };

  const handlePlaylistItemChange = (index, updates) => {
    updatePlaylistItems((items) => items.map((item, itemIndex) => {
      if (itemIndex !== index) return item;
      const merged = { ...item, ...updates };
      // A pasted link in the id field resolves to the video it points at, and
      // the poster follows the id.
      if (updates.videoId !== undefined) {
        return normalizePlaylistItem({ ...merged, url: updates.videoId, src: '' }, index);
      }
      return merged;
    }));
  };

  const addPlaylistItem = () => {
    updatePlaylistItems((items) => [...items, normalizePlaylistItem({}, items.length)]);
  };

  const removePlaylistItem = (index) => {
    updatePlaylistItems((items) => items.filter((_, itemIndex) => itemIndex !== index));
  };

  const movePlaylistItem = (index, delta) => {
    updatePlaylistItems((items) => {
      const target = index + delta;
      if (target < 0 || target >= items.length) return items;
      const next = [...items];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  // "Paste the links" path, for a playlist the API cannot reach (unlisted, or
  // no API key configured): every YouTube link in the box becomes an entry.
  const applyPlaylistBulkInput = () => {
    const ids = parseVideoIdsFromText(formData.playlistBulkInput || '');
    if (!ids.length) {
      alert('No YouTube links found in that text.');
      return;
    }
    updatePlaylistItems((items) => {
      const known = new Set(items.map((item) => item.videoId).filter(Boolean));
      const added = ids
        .filter((id) => !known.has(id))
        .map((id, index) => normalizePlaylistItem({ videoId: id }, items.length + index));
      return [...items, ...added];
    });
    setFormData((prev) => ({ ...prev, playlistBulkInput: '' }));
  };

  // Same endpoint as the video fetcher, asked for a playlist: title, channel,
  // date, how many videos it holds, and the running order with durations.
  //
  // The playlist branch is newer than the video one, so a site running an older
  // copy of /api/video-meta.php answers "Unsupported platform" — the failure is
  // reported as what it is, since the fix is a deploy rather than anything the
  // author can do in this dialog.
  const fetchPlaylistInfo = async () => {
    const parsed = parseYouTubePlaylist(formData.playlistInput || '');
    if (!parsed) {
      alert('Paste a YouTube link that contains a playlist (its address includes "list=").');
      return;
    }
    setIsLoadingVideoInfo(true);
    try {
      const lookup = playlistPageUrl(parsed.playlistId);
      const res = await fetch(`/api/video-meta.php?platform=youtube-playlist&url=${encodeURIComponent(lookup)}`);
      const body = await res.text();
      let data = null;
      try {
        data = JSON.parse(body);
      } catch {
        // A dev server answers every path with index.html, so the body is a page
        throw new Error('NO_API');
      }
      if (data.error) throw new Error(/unsupported platform/i.test(data.error) ? 'OLD_API' : data.error);
      if (!res.ok) throw new Error(`Playlist meta request failed (${res.status})`);

      const fetchedItems = normalizePlaylistItems(data.items || []);
      setFormData((prev) => ({
        ...prev,
        playlistTitle: data.title || prev.playlistTitle || '',
        playlistChannel: data.author || prev.playlistChannel || '',
        playlistDescription: prev.playlistDescription || data.description || '',
        playlistItemCount: data.itemCount || fetchedItems.length || prev.playlistItemCount || '',
        playlistItems: fetchedItems.length ? fetchedItems : prev.playlistItems,
        videoSource: prev.videoSource || 'YouTube',
        videoDate: prev.videoDate || data.date || ''
      }));
      alert(`✅ Playlist details fetched${fetchedItems.length ? ` (${fetchedItems.length} videos)` : ''}.`);
    } catch (err) {
      console.error('Playlist info fetch error:', err);
      const reason = err?.message === 'NO_API'
        ? 'The lookup runs on the site\'s own API, which does not answer while the app is running locally.'
        : err?.message === 'OLD_API'
          ? 'This site\'s copy of /api/video-meta.php does not know about playlists yet — it needs the updated file (the one that handles platform=youtube-playlist).'
          : `The lookup answered: ${err?.message || 'no details'}.`;
      alert(
        `⚠️ Could not fetch the playlist details.\n\n${reason}\n\n`
        + 'You can still build the card by hand: paste the video links into "Or paste the video links, '
        + 'one per line", then use "Fetch titles" to name them.'
      );
    } finally {
      setIsLoadingVideoInfo(false);
    }
  };

  // Names the videos already listed, one lookup each against the *video*
  // endpoint — the one every deployment already has. It is what makes the card
  // usable on a site whose API has not been updated for playlists yet.
  const fetchPlaylistItemTitles = async () => {
    const items = Array.isArray(formData.playlistItems) ? formData.playlistItems : [];
    const targets = items
      .map((item, index) => ({ item, index }))
      .filter(({ item }) => item.videoId && !String(item.title || '').trim())
      .slice(0, 50);

    if (!targets.length) {
      alert(items.length
        ? 'Every listed video already has a title.'
        : 'Add some videos first — paste their links into the box below.');
      return;
    }

    setIsLoadingVideoInfo(true);
    const found = new Map();
    let channel = '';
    try {
      // A handful at a time, so a long list does not open fifty sockets at once.
      for (let start = 0; start < targets.length; start += 5) {
        // eslint-disable-next-line no-await-in-loop
        await Promise.all(targets.slice(start, start + 5).map(async ({ item, index }) => {
          try {
            const res = await fetch(`/api/video-meta.php?platform=youtube&id=${encodeURIComponent(item.videoId)}`);
            const data = await res.json();
            if (data && data.title) {
              found.set(index, data.title);
              if (!channel && data.author) channel = data.author;
            }
          } catch {
            // One video that will not answer should not stop the rest
          }
        }));
      }
    } finally {
      setIsLoadingVideoInfo(false);
    }

    if (!found.size) {
      alert('⚠️ Could not fetch any titles. Type them in below — the card works either way.');
      return;
    }

    setFormData((prev) => ({
      ...prev,
      playlistChannel: prev.playlistChannel || channel,
      playlistItems: (prev.playlistItems || []).map((item, index) => (
        found.has(index) ? { ...item, title: found.get(index) } : item
      ))
    }));
    alert(`✅ Named ${found.size} of ${targets.length} video${targets.length === 1 ? '' : 's'}.`);
  };

  // --- Embedded image links -------------------------------------------------
  // The pasted iframe/URL is parsed on every keystroke so the panel can show a
  // live preview and the detected provider before anything is saved.
  const parsedImageEmbed = useMemo(
    () => parseImageEmbed(formData.imageEmbedInput || ''),
    [formData.imageEmbedInput]
  );
  // A link straight to a picture file (an R2 bucket, an LMS folder, any CDN)
  // renders as a scaled <img>, not a provider card — so it needs no frame size
  // and no still picture for the exports.
  const isDirectImageLink = parsedImageEmbed?.kind === 'image';

  // The two citation fields the link itself proves — where it came from and
  // where it lives — are filled the moment it parses, without waiting for the
  // metadata fetch. Existing values are never overwritten.
  useEffect(() => {
    if (imageSource !== 'embed' || !parsedImageEmbed) return;
    const defaults = embedCitationDefaults(parsedImageEmbed);
    setFormData(prev => {
      if (prev.image_source && prev.image_url) return prev;
      return {
        ...prev,
        image_source: prev.image_source || defaults.imageSource || '',
        image_url: prev.image_url || defaults.imageUrl || ''
      };
    });
  }, [imageSource, parsedImageEmbed]);

  // A new link invalidates the preview image captured for the previous one.
  // Only an actual change clears it — reopening a saved block must keep the
  // preview it already has.
  useEffect(() => {
    const embedSrc = parsedImageEmbed?.embedSrc || '';
    const previous = lastEmbedSrcRef.current;
    lastEmbedSrcRef.current = embedSrc;
    if (previous === null || previous === embedSrc) return;
    setFormData(prev => (prev.imageEmbedThumbnail ? { ...prev, imageEmbedThumbnail: '' } : prev));
  }, [parsedImageEmbed]);

  // Ask the server to resolve the link's title/author/date via the provider's
  // oEmbed endpoint (falling back to the page's Open Graph tags), and to inline
  // the provider's preview image. The endpoint is the same shape as
  // /api/video-meta.php; when it is unavailable the user fills the citation in
  // by hand and the slide exports fall back to a link.
  const requestEmbedMeta = async (lookupUrl) => {
    const res = await fetch(`/api/embed-meta.php?url=${encodeURIComponent(lookupUrl)}`);
    if (!res.ok) throw new Error(`Embed meta request failed (${res.status})`);
    const data = await res.json();
    if (data.error) throw new Error(data.error);
    return data;
  };

  // Capture the details and preview automatically as soon as a link parses, so
  // a pasted embed exports with a picture without the user having to know the
  // button exists. Only blank fields are filled, and failures stay silent —
  // the button below is the loud, deliberate retry.
  useEffect(() => {
    if (imageSource !== 'embed' || !parsedImageEmbed) return undefined;
    // A picture file publishes no oEmbed or Open Graph data, and needs no
    // still — it is one. Asking about it only spends a request to fail.
    if (parsedImageEmbed.kind === 'image') return undefined;
    if (formData.imageEmbedThumbnail) return undefined;
    const embedSrc = parsedImageEmbed.embedSrc;
    if (autoEmbedFetchRef.current === embedSrc) return undefined;

    let active = true;
    const timer = setTimeout(async () => {
      autoEmbedFetchRef.current = embedSrc;
      try {
        const data = await requestEmbedMeta(parsedImageEmbed.pageUrl || embedSrc);
        if (!active) return;
        setFormData(prev => ({
          ...prev,
          // The fetched title doubles as alt text, so exports caption the
          // picture with what it is rather than a generic "Pinterest image".
          image_alt: prev.image_alt || data.title || '',
          image_title: prev.image_title || data.title || '',
          image_author: prev.image_author || data.author || '',
          image_source: prev.image_source || data.provider || parsedImageEmbed.provider || '',
          image_url: prev.image_url || data.url || '',
          image_date: prev.image_date || data.date || '',
          imageEmbedThumbnail: prev.imageEmbedThumbnail || data.thumbnailData || ''
        }));
      } catch (error) {
        // Nothing to say here — the panel already shows "no preview yet".
        console.debug('Embed auto-fetch skipped:', error?.message);
      }
    }, 700);

    return () => { active = false; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageSource, parsedImageEmbed, formData.imageEmbedThumbnail]);

  // Fill an uploaded or pasted image's APA fields from the page it came from.
  // Same lookup the embed panel uses, pointed at whatever address the user
  // typed — so a screenshot from a museum page or a journal article can be
  // cited without retyping the details.
  const fetchCitationFromSourceUrl = async (baseFieldName) => {
    const url = (formData[`${baseFieldName}_url`] || '').trim();
    if (!url) {
      alert('Paste the address of the page this image came from first.');
      return;
    }
    setCitationUrlFetchField(baseFieldName);
    try {
      const data = await requestEmbedMeta(url);
      setFormData(prev => ({
        ...prev,
        [`${baseFieldName}_title`]: data.title || prev[`${baseFieldName}_title`] || '',
        [`${baseFieldName}_author`]: data.author || prev[`${baseFieldName}_author`] || '',
        [`${baseFieldName}_source`]: data.provider || prev[`${baseFieldName}_source`] || '',
        [`${baseFieldName}_date`]: data.date || prev[`${baseFieldName}_date`] || '',
        [`${baseFieldName}_sourceType`]: prev[`${baseFieldName}_sourceType`] || 'image',
        // A web page has no page number to cite.
        [`${baseFieldName}_includePage`]: false
      }));
      alert(`✅ Citation details fetched from ${data.provider || 'that page'}. Check them before saving.`);
    } catch (error) {
      console.error('Citation lookup failed:', error);
      alert('⚠️ Could not read citation details from that address — some sites publish none. Please fill the fields in by hand.');
    } finally {
      setCitationUrlFetchField(null);
    }
  };

  // Manual route to an export picture. A provider iframe is cross-origin, so
  // the browser will not let the app screenshot what it renders — but the user
  // can always hand us the picture directly, and this works with no server
  // endpoint and no network at all.
  const setEmbedThumbnailFromFile = async (file) => {
    if (!file) return;
    if (!/^image\//i.test(file.type)) {
      alert('⚠️ That file is not an image. Use a PNG, JPG, GIF or WebP.');
      return;
    }
    try {
      const dataUrl = await handleFileToBase64(file);
      setFormData(prev => ({ ...prev, imageEmbedThumbnail: dataUrl }));
      // Stop the automatic lookup from overwriting a picture chosen by hand.
      autoEmbedFetchRef.current = parsedImageEmbed?.embedSrc || autoEmbedFetchRef.current;
    } catch (error) {
      console.error('Export preview image failed to load:', error);
      alert('⚠️ Could not read that image file.');
    }
  };

  // Ctrl/⌘+V anywhere in the upload panel adds a copied image — screenshot an
  // image, paste it here, done. Pastes accumulate, so several become a gallery
  // the same way selecting several files does. Ignored while the caret is in a
  // field so pasting text into the citation boxes still works.
  useEffect(() => {
    if (imageSource !== 'upload') return undefined;
    // The pasted-images dialog has no upload panel to add to — its pictures are
    // already on the slide. Collecting more here would quietly fill a list
    // nothing displays and nothing saves.
    if (contentType === 'pasted-images') return undefined;
    const onPaste = (event) => {
      const target = event.target;
      if (target && typeof target.closest === 'function'
        && target.closest('input, textarea, [contenteditable="true"]')) return;
      const pasted = Array.from(event.clipboardData?.files || [])
        .filter((file) => /^image\//i.test(file.type));
      if (!pasted.length) return;
      event.preventDefault();
      setFormData(prev => {
        const existing = Array.from(prev.imageFiles || []);
        const named = pasted.map((file, offset) => nameClipboardImage(file, existing.length + offset + 1));
        return { ...prev, imageFiles: [...existing, ...named] };
      });
    };
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, [imageSource, contentType]);

  // Ctrl/⌘+V anywhere in the embed panel drops a copied image straight in —
  // ignored while the caret is in a field so pasting the embed code still works.
  useEffect(() => {
    if (imageSource !== 'embed') return undefined;
    const onPaste = (event) => {
      const target = event.target;
      if (target && typeof target.closest === 'function'
        && target.closest('input, textarea, [contenteditable="true"]')) return;
      const file = Array.from(event.clipboardData?.files || [])
        .find((entry) => /^image\//i.test(entry.type));
      if (!file) return;
      event.preventDefault();
      setEmbedThumbnailFromFile(file);
    };
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageSource, parsedImageEmbed]);

  const fetchImageEmbedInfo = async () => {
    const parsed = parsedImageEmbed;
    if (!parsed) {
      alert('Please paste an embed link or iframe code first.');
      return;
    }
    const lookupUrl = parsed.pageUrl || parsed.embedSrc;
    setIsLoadingEmbedInfo(true);
    try {
      const data = await requestEmbedMeta(lookupUrl);

      setFormData(prev => ({
        ...prev,
        image_alt: prev.image_alt || data.title || '',
        image_title: data.title || prev.image_title || '',
        image_author: data.author || prev.image_author || '',
        image_source: data.provider || parsed.provider || prev.image_source || '',
        image_url: data.url || parsed.pageUrl || parsed.embedSrc || '',
        image_date: data.date || prev.image_date || '',
        image_sourceType: prev.image_sourceType || 'image',
        image_includePage: false,
        // Inlined by the server as a data URL, so the PowerPoint/PDF exports
        // have a real picture to place without a cross-origin fetch.
        imageEmbedThumbnail: data.thumbnailData || prev.imageEmbedThumbnail || ''
      }));
      const provider = data.provider || parsed.provider;
      alert(data.thumbnailData
        ? `✅ ${provider} details fetched, and a preview image was captured for the PowerPoint/PDF exports.`
        : `✅ ${provider} details fetched. This link publishes no preview image, so PowerPoint and PDF will show a link instead of a picture.`);
    } catch (err) {
      console.error('Image embed info fetch error:', err);
      alert('⚠️ Could not fetch details for this link. The source and URL are already filled in — please add the title, creator and date manually.');
    } finally {
      setIsLoadingEmbedInfo(false);
    }
  };

  const fetchCitationVideoInfo = async (index) => {
    const citation = Array.isArray(formData.citations) ? formData.citations[index] : null;
    if (!citation?.url) {
      alert('Please enter a video URL first.');
      return;
    }

    setCitationFetchIndex(index);
    try {
      // The link the author pasted decides who is asked; the platform dropdown
      // is the fallback for a link the detector does not recognise.
      const platform = detectPlatform(citation.url) || citation.platform || 'youtube';
      const normalizedUrl = normalizeVideoUrl(citation.url, platform);
      try {
        const { isValid } = validateVideoUrl(normalizedUrl, platform);
        if (!isValid) console.warn('[CitationMeta] Local validation failed; trying server anyway.');
      } catch {}

      const res = await fetch(`/api/video-meta.php?platform=${encodeURIComponent(platform)}&url=${encodeURIComponent(normalizedUrl)}`);
      if (!res.ok) throw new Error(`Video meta request failed (${res.status})`);
      const data = await res.json();
      if (data.error) throw new Error(data.error);

      updateCitation(index, {
        title: data.title || '',
        author: data.author || '',
        authorFirstName: getCitationAuthorParts({ author: data.author || '' }).firstName || '',
        authorLastName: getCitationAuthorParts({ author: data.author || '' }).lastName || '',
        source: data.platform || 'YouTube',
        url: data.videoUrl || normalizedUrl,
        date: data.date || '',
        platform
      });
      alert(`✅ ${data.platform} details fetched successfully.`);
    } catch (err) {
      console.error('Citation video info fetch error:', err);
      alert('⚠️ Could not fetch video details. Please enter them manually.');
    } finally {
      setCitationFetchIndex(null);
    }
  };

  /**
   * Ask the server what a clip is: title, channel, publication date.
   *
   * The same endpoint the video block's own "Fetch Info" button uses. It is a
   * server call rather than a direct oEmbed request because the platforms do
   * not let the browser read those responses cross-origin.
   */
  const fetchVideoMeta = async (url, platform = 'youtube') => {
    const normalizedUrl = normalizeVideoUrl(url, platform);
    try {
      const { isValid } = validateVideoUrl(normalizedUrl, platform);
      if (!isValid) console.warn('[VideoMeta] Local validation failed; trying the server anyway.');
    } catch {}
    const res = await fetch(
      `/api/video-meta.php?platform=${encodeURIComponent(platform)}&url=${encodeURIComponent(normalizedUrl)}`
    );
    if (!res.ok) throw new Error(`Video meta request failed (${res.status})`);
    const data = await res.json();
    if (data.error) throw new Error(data.error);
    return { ...data, videoUrl: data.videoUrl || normalizedUrl };
  };

  /**
   * Fetch a linked clip's details and cite it, from the Custom HTML/CSS block.
   *
   * The layout links the clip; the reference list needs the source. Doing both
   * from one button means the author never retypes the channel name into a
   * citation row — and the entry that lands in the deck's reference list is the
   * platform's own metadata.
   *
   * A clip already cited is updated in place rather than added twice: the match
   * is on the watch URL, so pressing the button again refreshes the entry.
   */
  const fetchCustomHtmlVideoInfo = async (index) => {
    const clips = Array.isArray(formData.customVideos) ? formData.customVideos : [];
    const clip = clips[index];
    if (!clip?.url) return;

    setCustomVideoFetchIndex(index);
    try {
      const platform = clip.platform || detectPlatform(clip.url) || 'youtube';
      const data = await fetchVideoMeta(clip.url, platform);
      const author = data.author || '';
      const { firstName, lastName } = getCitationAuthorParts({ author });
      const watchUrl = data.videoUrl || clip.url;
      const fields = {
        type: 'video',
        sourceType: 'video',
        platform,
        title: data.title || '',
        author,
        authorFirstName: firstName || '',
        authorLastName: lastName || '',
        source: data.platform || 'YouTube',
        url: watchUrl,
        date: data.date || '',
      };

      setFormData((prev) => {
        const videos = (Array.isArray(prev.customVideos) ? prev.customVideos : []).map((video, i) => (
          i === index
            // The clip's own title doubles as the link label and the poster's
            // alt text, so a fetched title is worth keeping on the clip too.
            ? { ...video, title: data.title || video.title || '', author, date: data.date || '' }
            : video
        ));
        const citations = Array.isArray(prev.citations) ? [...prev.citations] : [];
        const existing = citations.findIndex((citation) => {
          const cited = String(citation?.url || '').trim();
          return cited && (cited === watchUrl || cited === clip.url);
        });
        if (existing >= 0) citations[existing] = { ...citations[existing], ...fields };
        else citations.push(createEmptyCitation(fields));
        return { ...prev, customVideos: videos, citations };
      });
      alert(`✅ ${data.platform || 'Video'} details fetched and added to this slide's citations.`);
    } catch (err) {
      console.error('Custom HTML video info fetch error:', err);
      alert('⚠️ Could not fetch the clip details. Add the citation manually below.');
    } finally {
      setCustomVideoFetchIndex(-1);
    }
  };

  const handleSubmit = async () => {
    const targetSectionId = formData.sectionId || initialData.sectionId;
    let processedData = { ...formData, sectionId: targetSectionId };

    if (['apa-citation', 'apa-reference-list'].includes(contentType) && !isCitationFormValid) {
      alert('Please complete the required citation fields before saving.');
      return;
    }

    if (contentType === 'apa-quote' && !isQuoteValid) {
      alert('Please add quote text and at least one source before saving.');
      return;
    }

    switch (contentType) {
      // Citations for pictures a canvas paste already placed. Nothing is
      // created here — this only writes the optional half back onto blocks
      // that are on the slide, keyed by their ids.
      case 'pasted-images': {
        onSave({
          ...formData,
          type: 'pasted-images',
          pastedImageUpdates: pastedImageUpdates(formData)
        });
        break;
      }

      case 'image':
      case 'gallery': {
        // Embedded links stay `type: 'image'` — they carry imageEmbedSrc
        // instead of src, so every image feature (APA fields, reference-list
        // collection, detaching the citation) keeps working unchanged.
        if (imageSource === 'embed') {
          const parsed = parseImageEmbed(formData.imageEmbedInput || '');
          if (!parsed) {
            alert('⚠️ Paste an https embed link or iframe code (for example a Pinterest pin) before saving.');
            return;
          }
          // A direct picture link scales to the slide, so it stores no frame
          // size — those numbers only describe a provider's fixed-size card.
          const isDirectImage = parsed.kind === 'image';
          const width = isDirectImage ? '' : (parseInt(formData.imageEmbedWidth, 10) || parsed.width);
          const height = isDirectImage ? '' : (parseInt(formData.imageEmbedHeight, 10) || parsed.height);
          const defaults = embedCitationDefaults(parsed);
          const blockToSave = {
            type: 'image',
            src: '',
            // The raw pasted text is modal-only state; the block keeps the
            // resolved embed src and page URL instead.
            imageEmbedInput: '',
            imageEmbedSrc: parsed.embedSrc,
            imageEmbedUrl: parsed.pageUrl || '',
            imageEmbedProvider: parsed.provider || '',
            imageEmbedWidth: width,
            imageEmbedHeight: height,
            // Still image for the PowerPoint/PDF exports, which cannot host a
            // live frame. Empty when the provider publishes no preview — and
            // empty for a picture link, where the link is already the picture:
            // keeping the base64 still captured for a previous provider embed
            // would leave the file it replaced sitting in the deck.
            imageEmbedThumbnail: isDirectImage ? '' : (formData.imageEmbedThumbnail || ''),
            alt: formData.image_alt
              || (isDirectImage ? directImageFileName(parsed.embedSrc) : '')
              || `${parsed.provider || 'Embedded'} image`,
            size: formData.image_size || 'medium',
            caption: formData.image_caption ? `<strong>Figure:</strong> ${formData.image_caption}` : '',
            imageShowCaption: formData.image_showCaption !== false,
            imageTitle: formData.image_title || '',
            imageAuthor: formData.image_author || '',
            imageSource: formData.image_source || defaults.imageSource || '',
            imageUrl: formData.image_url || defaults.imageUrl || '',
            imageDate: formData.image_date || '',
            imageSourceType: formData.image_sourceType || 'image',
            imagePage: formData.image_page || '',
            // Embeds are web sources, so there is no page number to include.
            imageIncludePage: formData.image_includePage === true,
            imageLicense: formData.image_license || '',
            imageIncludeLicense: formData.image_includeLicense !== false,
            imageTableNumber: formData.image_tableNumber || '',
            imageIsAdapted: Boolean(formData.image_isAdapted),
            imageIncludeCitation: formData.image_includeCitation !== false,
            imageAddToReferenceList: formData.image_addToReferenceList !== false,
            imageReferenceCategory: formData.image_referenceCategory || '',
            imageMedium: formData.image_medium || '',
            imageReferenceTitle: formData.image_referenceTitle || '',
            imageArticleTitle: formData.image_articleTitle || ''
          };
          onSave({ ...formData, ...blockToSave });
          break;
        }

        const files = imageSource === 'upload' ? Array.from(formData.imageFiles || []) : [];
        const filenames = imageSource === 'server' ? (formData.imageFilenames || '').split('\n').filter(name => name.trim()) : [];
        const existingItems = Array.isArray(formData.items) ? formData.items : [];
        const hasNewSources = files.length > 0 || filenames.length > 0;

        // If editing an existing gallery without new images, update metadata only
        if (!hasNewSources && contentType === 'gallery' && existingItems.length > 0) {
          const getImageFieldValue = (baseFieldName, field, fallback = '') => {
            const key = `${baseFieldName}_${field}`;
            return Object.prototype.hasOwnProperty.call(formData, key)
              ? formData[key]
              : fallback;
          };
          const items = existingItems.map((item, i) => {
            const baseFieldName = `image_${i}`;
            return {
              src: item.src,
              alt: formData[`${baseFieldName}_alt`] || item.alt || '',
              caption: formData[`${baseFieldName}_caption`] ? `<strong>Figure ${i + 1}:</strong> ${formData[`${baseFieldName}_caption`]}` : (item.caption || ''),
              imageShowCaption: getImageFieldValue(baseFieldName, 'showCaption', item.imageShowCaption ?? true) !== false,
              imageTitle: getImageFieldValue(baseFieldName, 'title', item.imageTitle || ''),
              imageAuthor: getImageFieldValue(baseFieldName, 'author', item.imageAuthor || ''),
              imageSource: getImageFieldValue(baseFieldName, 'source', item.imageSource || ''),
              imageUrl: getImageFieldValue(baseFieldName, 'url', item.imageUrl || ''),
              imageDate: getImageFieldValue(baseFieldName, 'date', item.imageDate || ''),
              imageSourceType: getImageFieldValue(baseFieldName, 'sourceType', item.imageSourceType || 'image'),
              imagePage: getImageFieldValue(baseFieldName, 'page', item.imagePage || ''),
              imageIncludePage: getImageFieldValue(baseFieldName, 'includePage', item.imageIncludePage ?? true) !== false,
              imageLicense: getImageFieldValue(baseFieldName, 'license', item.imageLicense || ''),
              imageIncludeLicense: getImageFieldValue(baseFieldName, 'includeLicense', item.imageIncludeLicense ?? true) !== false,
              imageTableNumber: getImageFieldValue(baseFieldName, 'tableNumber', item.imageTableNumber || ''),
              imageIsAdapted: Boolean(getImageFieldValue(baseFieldName, 'isAdapted', item.imageIsAdapted)),
              imageIncludeCitation: getImageFieldValue(baseFieldName, 'includeCitation', item.imageIncludeCitation ?? true),
              imageAddToReferenceList: getImageFieldValue(baseFieldName, 'addToReferenceList', item.imageAddToReferenceList ?? true) !== false,
              imageReferenceCategory: getImageFieldValue(baseFieldName, 'referenceCategory', item.imageReferenceCategory || ''),
              imageMedium: getImageFieldValue(baseFieldName, 'medium', item.imageMedium || ''),
              imageReferenceTitle: getImageFieldValue(baseFieldName, 'referenceTitle', item.imageReferenceTitle || ''),
              imageArticleTitle: getImageFieldValue(baseFieldName, 'articleTitle', item.imageArticleTitle || '')
            };
          });
          const blockToSave = { type: 'gallery', columns: formData.galleryColumns || '2', items };
          onSave({ ...formData, ...blockToSave });
          break;
        }

        const sourceArray = imageSource === 'upload' ? files : filenames;
        const isMultiple = contentType === 'gallery' || sourceArray.length > 1;

        if (sourceArray.length === 0 && !formData.isEditing) {
          alert(`⚠️ Please ${imageSource === 'upload' ? 'select at least one image file' : 'enter at least one image filename'}.`);
          return;
        }

        try {
          let blockToSave;
          if (isMultiple) {
            const items = [];
            for (let i = 0; i < sourceArray.length; i++) {
              const sourceItem = sourceArray[i];
              const baseFieldName = `image_${i}`;
              let itemSrc = imageSource === 'upload'
                ? await handleFileToBase64(sourceItem)
                : `${formData.imagePath || ''}${formData.imagePath?.endsWith('/') ? '' : '/'}${sourceItem}`;

              items.push({
                src: itemSrc,
                alt: formData[`${baseFieldName}_alt`] || (imageSource === 'upload' ? sourceItem.name : sourceItem),
                caption: formData[`${baseFieldName}_caption`] ? `<strong>Figure ${i + 1}:</strong> ${formData[`${baseFieldName}_caption`]}` : '',
              imageShowCaption: formData[`${baseFieldName}_showCaption`] !== false,
              imageTitle: formData[`${baseFieldName}_title`] || '',
              imageAuthor: formData[`${baseFieldName}_author`] || '',
              imageSource: formData[`${baseFieldName}_source`] || '',
              imageUrl: formData[`${baseFieldName}_url`] || '',
              imageDate: formData[`${baseFieldName}_date`] || '',
              imageSourceType: formData[`${baseFieldName}_sourceType`] || 'image',
              imagePage: formData[`${baseFieldName}_page`] || '',
              imageIncludePage: formData[`${baseFieldName}_includePage`] !== false,
              imageLicense: formData[`${baseFieldName}_license`] || '',
              imageIncludeLicense: formData[`${baseFieldName}_includeLicense`] !== false,
              imageTableNumber: formData[`${baseFieldName}_tableNumber`] || '',
              imageIsAdapted: Boolean(formData[`${baseFieldName}_isAdapted`]),
              imageIncludeCitation: formData[`${baseFieldName}_includeCitation`] !== false,
              imageAddToReferenceList: formData[`${baseFieldName}_addToReferenceList`] !== false,
              imageReferenceCategory: formData[`${baseFieldName}_referenceCategory`] || '',
              imageMedium: formData[`${baseFieldName}_medium`] || '',
              imageReferenceTitle: formData[`${baseFieldName}_referenceTitle`] || '',
              imageArticleTitle: formData[`${baseFieldName}_articleTitle`] || ''
            });
          }
            blockToSave = {
              type: 'gallery',
              columns: formData.galleryColumns || '2',
              items,
              imageEmbedSrc: ''
            };
          } else {
            const singleItem = sourceArray[0];
            let imageSrc = formData.src;
            if (singleItem) {
              imageSrc = imageSource === 'upload'
                ? await handleFileToBase64(singleItem)
                : `${formData.imagePath || ''}${formData.imagePath?.endsWith('/') ? '' : '/'}${singleItem}`;
            }

            const hasImage = Boolean(imageSrc);
            blockToSave = {
              type: 'image',
              src: imageSrc,
              // Switching an embedded image back to a file must clear the embed
              // fields, or the block would keep rendering the old iframe.
              imageEmbedInput: '',
              imageEmbedSrc: '',
              imageEmbedUrl: '',
              imageEmbedProvider: '',
              imageEmbedWidth: '',
              imageEmbedHeight: '',
              imageEmbedThumbnail: '',
              alt: hasImage
                ? (formData.image_alt || (singleItem ? (imageSource === 'upload' ? singleItem.name : singleItem) : 'Image'))
                : '',
              size: formData.image_size || 'medium',
              caption: hasImage && formData.image_caption ? `<strong>Figure:</strong> ${formData.image_caption}` : '',
              imageShowCaption: formData.image_showCaption !== false,
              imageTitle: hasImage ? (formData.image_title || '') : '',
              imageAuthor: hasImage ? (formData.image_author || '') : '',
              imageSource: hasImage ? (formData.image_source || '') : '',
              imageUrl: hasImage ? (formData.image_url || '') : '',
              imageDate: hasImage ? (formData.image_date || '') : '',
              imageSourceType: hasImage ? (formData.image_sourceType || 'image') : 'image',
              imagePage: hasImage ? (formData.image_page || '') : '',
              imageIncludePage: formData.image_includePage !== false,
              imageLicense: hasImage ? (formData.image_license || '') : '',
              imageIncludeLicense: formData.image_includeLicense !== false,
              imageTableNumber: hasImage ? (formData.image_tableNumber || '') : '',
              imageIsAdapted: Boolean(formData.image_isAdapted),
              imageIncludeCitation: formData.image_includeCitation !== false,
              imageAddToReferenceList: formData.image_addToReferenceList !== false,
            imageAddToReferenceList: formData.image_addToReferenceList !== false,
              imageReferenceCategory: hasImage ? (formData.image_referenceCategory || '') : '',
              imageMedium: hasImage ? (formData.image_medium || '') : '',
              imageReferenceTitle: hasImage ? (formData.image_referenceTitle || '') : '',
              imageArticleTitle: hasImage ? (formData.image_articleTitle || '') : ''
            };
          }
          onSave({ ...formData, ...blockToSave });
        } catch (error) {
          console.error('Error processing image(s):', error);
          alert(`⚠️ Error processing image file(s): ${error.message}`);
          return;
        }
        break;
      }

      case 'video':
        const platform = formData.videoPlatform || 'youtube';
        const videoId = extractVideoId(formData.videoUrl, platform);

        if ((platform !== 'embed' && !videoId)) {
          alert('Invalid video URL. Please check the link and try again.');
          return;
        }

        if (platform === 'embed') {
          processedData.src = formData.embedCode;
        } else if (videoId) {
          const embedHtml = generateVideoEmbed(platform, videoId, null, formData.aspectRatio);
          processedData.src = embedHtml.match(/src="([^"]*)"/)?.[1];
        }

        onSave({ ...processedData, type: 'video' });
        break;

      case 'youtube-playlist': {
        // The pasted link is the only required field. Whatever it carries — the
        // list, the video the author was on, its position — becomes the card's
        // starting point.
        const parsed = parseYouTubePlaylist(formData.playlistInput || formData.playlistId || '');
        const playlistId = parsed?.playlistId || formData.playlistId || '';
        if (!playlistId) {
          alert('That link has no playlist in it. Copy the address while the playlist is open — it contains "list=".');
          return;
        }

        const items = normalizePlaylistItems(formData.playlistItems)
          .filter((item) => item.videoId || item.title)
          .map((item, index) => ({ ...item, position: index + 1 }));
        const startVideoId = formData.startVideoId || parsed?.videoId || '';
        const startIndex = Number(formData.startIndex)
          || parsed?.index
          || items.find((item) => item.videoId === startVideoId)?.position
          || null;
        const title = (formData.playlistTitle || '').trim();
        const channel = (formData.playlistChannel || '').trim();

        const {
          playlistInput, playlistTitle, playlistChannel, playlistDescription,
          playlistItemCount, playlistItems, playlistLayout, playlistTheme,
          playlistAccentColor, playlistShowThumbnails, playlistBulkInput,
          ...restData
        } = processedData;

        onSave({
          ...restData,
          type: 'youtube-playlist',
          playlistId,
          startVideoId,
          startIndex,
          title,
          channel,
          description: (formData.playlistDescription || '').trim(),
          itemCount: Number(formData.playlistItemCount) || items.length,
          items,
          layout: PLAYLIST_LAYOUTS[formData.playlistLayout] ? formData.playlistLayout : 'split',
          theme: formData.playlistTheme === 'dark' ? 'dark' : 'light',
          accentColor: formData.playlistAccentColor || DEFAULT_PLAYLIST_ACCENT,
          showThumbnails: formData.playlistShowThumbnails !== false,
          aspectRatio: formData.aspectRatio || '16-9',
          // Citation fields keep the video block's names so the APA formatter,
          // the reference list and the tear-off action treat the two alike.
          videoTitle: title,
          videoAuthor: channel,
          videoSource: (formData.videoSource || 'YouTube').trim(),
          videoDate: formData.videoDate || '',
          videoUrl: playlistPageUrl(playlistId),
          videoIncludeCitation: formData.videoIncludeCitation !== false,
          videoAddToReferenceList: formData.videoAddToReferenceList !== false
        });
        break;
      }

      case 'audio': {
        if (formData.audioFile) {
          try {
            processedData.src = await handleFileToBase64(formData.audioFile);
          } catch (error) {
            console.error('Error processing audio:', error);
            alert('⚠️ Error processing audio file. Please try again.');
            return;
          }
        } else if (formData.audioSrc) {
          processedData.src = formData.audioSrc;
        }
        // Only ever *set* src, never blank it: editing a block to fix a typo in
        // its title arrives here with neither a file nor a link, and the clip
        // already on the block has to survive that.

        // The form field is audioDescription and the block field is
        // description — the same name the renderer, the HTML export and the
        // edit round-trip all read. Without this line every description typed
        // into this panel was written to a key nothing looks at, so it
        // vanished on save and came back empty on edit.
        processedData.description = formData.audioDescription || '';
        processedData.playerStyle = normalizeAudioPlayerStyle(formData.playerStyle);
        onSave({ ...processedData, type: 'audio' });
        break;
      }

      case 'accordion': {
        const items = (formData.accordionItems || [])
          .filter(item => item.title)
          .map(item => ({
            id: item.id,
            title: item.title,
            content: item.content,
            isOpen: item.isOpen || false
          }));
        const settings = formData.accordionSettings || {
          allowMultipleOpen: false,
          defaultOpenFirst: false,
          theme: 'default'
        };
        const blockToSave = saveAccordionBlock(
          { ...processedData, html: generateAccordionHTML(items) },
          items,
          settings
        );
        onSave(blockToSave);
        break;
      }

      case 'cards':
        processedData.items = (formData.cardItems || [])
          .filter(card => card.title || card.content)
          .map(card => ({
            title: card.title || '',
            content: card.content || '', // RichTextEditor provides HTML directly
            useCustomColors: card.useCustomColors || false,
            backgroundColor: card.backgroundColor || undefined,
            borderColor: card.borderColor || undefined,
            widthPercent: card.widthPercent || 100
          }));
        onSave({ ...processedData, type: 'cards' });
        break;

      case 'stacked-cards':
        processedData.items = (formData.stackedCardItems || [])
          .filter(card => card.title || card.content || card.sideText)
          .map(card => ({
            title: card.title || '',
            content: card.content || '',
            sideText: card.sideText || '',
            color: card.color || '#0b57d0'
          }));
        processedData.includeSideText = formData.includeSideText !== false;
        onSave({ ...processedData, type: 'stacked-cards', sectionId: targetSectionId });
        break;

      case 'multi-column-cards': {
        const sourceData = formData.multiColumnData || { columns: formData.columns || [] };
        const savePayload = buildMultiColumnSaveData(sourceData);
        onSave({ ...processedData, ...savePayload, type: 'multi-column-cards', sectionId: targetSectionId });
        break;
      }

      case 'link': {
        processedData.title = formData.blockTitle || '';
        processedData.description = formData.blockDescription || '';
        processedData.items = (formData.linkItems || [])
          .filter(item => item.title || item.url || item.description || item.pageInfo || item.notes)
          .map(item => ({
            title: item.title || '',
            url: item.url || '',
            description: item.description || '',
            category: item.category || 'website',
            platform: item.platform || '',
            pageInfo: item.pageInfo || '',
            notes: item.notes || '',
            tags: item.tags || ''
          }));
        if (!processedData.items.length) {
          alert('Please add at least one link before saving.');
          return;
        }
        onSave({ ...processedData, type: 'link' });
        break;
      }

      case 'worksheet': {
        const processedWorksheet = processWorksheetData(formData);
        onSave({ 
          ...formData,
          ...processedWorksheet
        });
        break;
      }

      case 'apa-quote': {
        const quoteSourceType = formData.quoteSourceType === 'paraphrase' ? 'paraphrase' : 'quote';
        const quoteSources = Array.isArray(formData.quoteSources)
          ? formData.quoteSources.filter((source) => source.author || source.year || source.page)
          : [];
        const fallbackSource = quoteSources[0] || {};
        const blockToSave = {
          type: 'apa-quote',
          quoteText: formData.quoteText || '',
          author: fallbackSource.author || formData.author || '',
          year: fallbackSource.year || formData.year || '',
          title: formData.title || '',
          source: formData.source || '',
          url: formData.url || '',
          doi: formData.doi || '',
          page: fallbackSource.page || formData.page || '',
          sourceType: quoteSourceType,
          sources: quoteSources.length ? quoteSources : undefined
        };
        onSave({ ...processedData, ...blockToSave });
        break;
      }

      case 'apa-citation': {
        const citations = (formData.citations || [])
          .map((citation) => ({
            id: citation.id || generateId(),
            citationId: citation.citationId || citation.id || '',
            citationMode: citation.citationMode || '',
            type: citation.type || citation.sourceType || 'book',
            sourceType: citation.sourceType || citation.type || 'paraphrase',
            author: citation.author || '',
            authorFirstName: citation.authorFirstName || '',
            authorLastName: citation.authorLastName || '',
            contributors: Array.isArray(citation.contributors) ? citation.contributors : [],
            year: citation.year || '',
            date: citation.date || '',
            title: citation.title || '',
            source: citation.source || '',
            publisher: citation.publisher || '',
            siteName: citation.siteName || '',
            version: citation.version || '',
            prompt: citation.prompt || '',
            notes: citation.notes || '',
            url: citation.url || '',
            doi: citation.doi || '',
            volume: citation.volume || '',
            issue: citation.issue || '',
            pages: citation.pages || '',
            page: citation.page || '',
            license: citation.license || '',
            isAdapted: Boolean(citation.isAdapted),
            platform: citation.platform || 'youtube',
            retrievalDateEnabled: Boolean(citation.retrievalDateEnabled),
            retrievalDate: citation.retrievalDate || ''
          }))
          .filter((citation) => hasApaCitationContent(citation));
        if (!citations.length) {
          alert('Please add at least one citation before saving.');
          return;
        }
        onSave({ ...processedData, type: 'apa-citation', citations });
        break;
      }

      case 'apa-reference-list': {
        const citations = (formData.citations || [])
          .map((citation) => ({
            id: citation.id || generateId(),
            citationId: citation.citationId || citation.id || '',
            citationMode: citation.citationMode || '',
            type: citation.type || citation.sourceType || 'book',
            sourceType: citation.sourceType || citation.type || 'paraphrase',
            author: citation.author || '',
            authorFirstName: citation.authorFirstName || '',
            authorLastName: citation.authorLastName || '',
            contributors: Array.isArray(citation.contributors) ? citation.contributors : [],
            year: citation.year || '',
            date: citation.date || '',
            title: citation.title || '',
            source: citation.source || '',
            publisher: citation.publisher || '',
            siteName: citation.siteName || '',
            version: citation.version || '',
            prompt: citation.prompt || '',
            notes: citation.notes || '',
            url: citation.url || '',
            doi: citation.doi || '',
            volume: citation.volume || '',
            issue: citation.issue || '',
            pages: citation.pages || '',
            page: citation.page || '',
            license: citation.license || '',
            isAdapted: Boolean(citation.isAdapted),
            platform: citation.platform || 'youtube',
            retrievalDateEnabled: Boolean(citation.retrievalDateEnabled),
            retrievalDate: citation.retrievalDate || ''
          }))
          .filter((citation) => hasApaCitationContent(citation));
        onSave({ ...processedData, type: 'apa-reference-list', citations: sortCitationsByMeta(citations) });
        break;
      }

      case 'divider':
        onSave({
          ...processedData,
          type: 'divider',
          content: {
            ...DEFAULT_DIVIDER_CONTENT,
            ...(formData.content || {}),
          },
        });
        break;

      case 'html':
        onSave({
          ...processedData,
          type: 'html',
          content: formData.codeContent || '',
          language: formData.codeLanguage || 'html',
          mode: formData.codeMode || 'displayCode',
          customCss: formData.codeCustomCss || '',
          citation: formData.codeCitation || '',
          options: {
            softWrap: Boolean(formData.codeSoftWrap),
            showLineNumbers: formData.codeShowLineNumbers !== false,
            allowScripts: Boolean(formData.codeAllowScripts),
          },
        });
        break;

      case 'custom-html': {
        const { customHtml, customCss, customCssScope, customImages, customVideos, ...restData } = processedData;
        // Uploads are stored on the block, not inlined into the markup, so the
        // HTML stays readable and the pictures stay visible to every exporter.
        const images = (Array.isArray(formData.customImages) ? formData.customImages : [])
          .filter((image) => image && typeof image.src === 'string' && image.src)
          .map((image, index) => ({
            name: String(image.name || '').trim() || `image-${index + 1}`,
            src: image.src,
            width: Number(image.width) || 0,
            height: Number(image.height) || 0,
            alt: String(image.alt || ''),
            fileName: String(image.fileName || ''),
            fileSize: Number(image.fileSize) || 0,
          }));
        // Blank citation rows are the editor's default, not the author's
        // intent — only keep the ones they actually filled in.
        const citations = (Array.isArray(formData.citations) ? formData.citations : [])
          .filter((citation) => hasApaCitationContent(citation));
        onSave({
          ...restData,
          type: 'custom-html',
          html: formData.customHtml || '',
          css: formData.customCss || '',
          cssScope: formData.customCssScope === 'deck' ? 'deck' : 'slide',
          images,
          videos: (Array.isArray(formData.customVideos) ? formData.customVideos : [])
            .filter((video) => video && String(video.url || '').trim())
            .map((video, index) => ({
              name: String(video.name || '').trim() || `video-${index + 1}`,
              url: String(video.url || ''),
              platform: String(video.platform || ''),
              videoId: String(video.videoId || ''),
              poster: String(video.poster || ''),
              title: String(video.title || ''),
            })),
          citations,
          // Only meaningful alongside a citation, and only while the block is
          // citing at all — the switch above it decides that.
          citationOverlay: formData.citationOverlay === true && formData.includeCitation !== false,
          // Where it was dragged to, if it was. Normalised on the way into the
          // stylesheet rather than here, so a deck edited by hand cannot put
          // anything but two numbers into a CSS rule.
          citationOverlayPos: normalizeCitationOverlayPos(formData.citationOverlayPos),
        });
        break;
      }

      case 'project-timeline': {
        const steps = (Array.isArray(formData.timelineSteps) ? formData.timelineSteps : [])
          .map((step, index) => sanitizeTimelineStep(step, index));
        const { timelineSteps, timelineTitle, ...restData } = processedData;
        onSave({
          ...restData,
          type: 'project-timeline',
          title: formData.timelineTitle || '',
          steps: steps.length ? steps : createTimelineBlockData().steps
        });
        break;
      }

      default:
        onSave({ ...processedData, type: contentType });
        break;
    }

    onClose();
  };

  const existingItems = contentType === 'gallery' && Array.isArray(formData.items) ? formData.items : [];
  const existingSingleImage = contentType === 'image' && formData.src
    ? [{ name: formData.image_alt || formData.alt || 'Image', src: formData.src }]
    : [];
  const files = imageSource === 'upload' ? Array.from(formData.imageFiles || []) : [];
  const filenames = imageSource === 'server' ? (formData.imageFilenames || '').split('\n').filter(Boolean) : [];
  const linkItems = Array.isArray(formData.linkItems) ? formData.linkItems : [];
  const linkItemsForDisplay = linkItems.length > 0 ? linkItems : [createEmptyLinkItem()];
  const canRemoveLinkItem = linkItems.length > 1;
  const citations = Array.isArray(formData.citations) ? formData.citations : [];
  const citationsForDisplay = citations.length > 0
    ? citations
    : [createEmptyCitation({ sourceType: contentType === 'apa-reference-list' ? 'book' : 'paraphrase' })];
  const canRemoveCitation = citations.length > 1;
  // Reviewed against the citations sitting in the modal rather than the ones
  // last saved, so the duplicate flags track what the author is doing now.
  const referenceReview = useMemo(
    () => buildReferenceReview({ sections, manualCitations: citations }),
    [sections, citations]
  );

  // A rescan message is a snapshot. Once the list actually moves — a source
  // switched off, an item removed — the old counts are wrong, so drop it rather
  // than leave it contradicting the footer.
  useEffect(() => {
    setReferenceRescan(null);
  }, [referenceReview.totalReferences]);

  const handleReferenceRescan = () => {
    const fresh = buildReferenceReview({ sections, manualCitations: citations });
    const { added, removed } = diffReferenceReview(referenceReview, fresh);
    setReferenceRescan({
      at: Date.now(),
      total: fresh.totalReferences,
      slideCount: fresh.slideCitations.length,
      added: added.length,
      removed: removed.length
    });
  };

  const handleRemoveDuplicateReferenceItems = () => {
    const drop = new Set(referenceReview.duplicateIndexes);
    if (!drop.size) return;
    setFormData(prev => ({
      ...prev,
      citations: (Array.isArray(prev.citations) ? prev.citations : []).filter((_, index) => !drop.has(index))
    }));
    setReferenceRescan(null);
  };
  const citationValidation = useMemo(() => {
    const hasAuthor = (citation) => Boolean(
      citation.author
        || citation.authorFirstName
        || citation.authorLastName
        || (Array.isArray(citation.contributors) && citation.contributors.length > 0)
    );
    // Not a requirement any more — APA has a form for a source with no date,
    // and refusing to save until one is invented is worse than printing it.
    // Kept for the note the editor shows under an empty date field.
    const hasDate = (citation) => Boolean(citation.year || citation.date);
    const hasTitle = (citation) => Boolean(citation.title);
    const hasSource = (citation) => Boolean(citation.source || citation.publisher || citation.siteName);
    const hasUrl = (citation) => Boolean(citation.url || citation.doi);

    return citationsForDisplay.map((citation) => {
      // Citations are optional on a custom layout, so an untouched row is not
      // an error — the block's own content is the point, and the reference is
      // a bonus. Only flag a row once the author has started filling it in.
      if (contentType === 'custom-html' && !hasApaCitationContent(citation)) return {};
      const type = citation.sourceType || citation.type || 'paraphrase';
      const errors = {};
      if (['book', 'journal', 'paraphrase', 'quote'].includes(type)) {
        if (!hasAuthor(citation)) errors.author = 'Author or organization is required.';
        if (!hasTitle(citation)) errors.title = 'Title is required.';
        if (['book', 'journal'].includes(type) && !hasSource(citation)) errors.source = 'Source or publisher is required.';
        if (type === 'journal') {
          if (!citation.volume) errors.volume = 'Volume is required.';
          if (!citation.pages) errors.pages = 'Pages are required.';
        }
        if (type === 'quote' && !citation.page) errors.page = 'Page number is required for quotes.';
      }
      if (type === 'software') {
        if (!hasAuthor(citation)) errors.author = 'Author or organization is required.';
        if (!hasTitle(citation)) errors.title = 'Software name is required.';
        if (!hasUrl(citation)) errors.url = 'URL or DOI is required.';
      }
      if (type === 'ai') {
        if (!citation.publisher) errors.publisher = 'Organization is required.';
        if (!citation.source) errors.source = 'Tool name is required.';
        if (!hasTitle(citation)) errors.title = 'Prompt description is required.';
      }
      if (['webpage', 'video'].includes(type)) {
        if (!hasTitle(citation)) errors.title = 'Title is required.';
        if (!hasUrl(citation)) errors.url = 'URL or DOI is required.';
      }
      if (['image', 'table', 'clipart_external'].includes(type)) {
        if (!hasTitle(citation)) errors.title = 'Title is required.';
        if (!hasSource(citation)) errors.source = 'Source is required.';
      }
      return errors;
    });
  }, [citationsForDisplay, contentType]);
  /**
   * Find a citation by name, in a list long enough that scrolling it is the
   * slow way round.
   *
   * Everything a person might recognise the entry by is searched — the author
   * however the record happens to store them, the title, where it was
   * published, the year and the link — because an author looking for "Whitaker"
   * should not have to know which of those fields the name ended up in.
   */
  const citationSearchText = useCallback((citation = {}) => [
    citation.author,
    citation.authorFirstName,
    citation.authorLastName,
    ...(Array.isArray(citation.contributors)
      ? citation.contributors.flatMap((person) => [person?.name, person?.firstName, person?.lastName])
      : []),
    citation.title,
    citation.source,
    citation.publisher,
    citation.siteName,
    citation.year,
    citation.date,
    citation.url,
    citation.doi
  ].filter(Boolean).join(' ').toLowerCase(), []);

  const citationMatches = useMemo(() => {
    const needle = citationFindText.trim().toLowerCase();
    if (!needle) return [];
    return citationsForDisplay
      .map((citation, index) => (citationSearchText(citation).includes(needle) ? index : -1))
      .filter((index) => index !== -1);
  }, [citationFindText, citationsForDisplay, citationSearchText]);

  // Where in the run of matches the current one sits, for the "2 of 5" readout.
  const citationFindPosition = citationMatches.indexOf(citationFindAt);

  const goToNextCitationMatch = () => {
    if (!citationMatches.length) return;
    const next = citationMatches[(citationFindPosition + 1) % citationMatches.length];
    setCitationFindAt(next);
    // The card is rendered already; it just may be a long way down the dialog.
    window.requestAnimationFrame(() => {
      document.getElementById(`citation-block-${next}`)
        ?.scrollIntoView({ block: 'start', behavior: 'smooth' });
    });
  };

  const isCitationFormValid = useMemo(
    () => citationValidation.every((errors) => Object.keys(errors).length === 0),
    [citationValidation]
  );
  const quoteSources = Array.isArray(formData.quoteSources) && formData.quoteSources.length > 0
    ? formData.quoteSources
    : [createEmptyQuoteSource()];
  const quoteSourceType = formData.quoteSourceType === 'paraphrase' ? 'paraphrase' : 'quote';
  const canRemoveQuoteSource = quoteSources.length > 1;
  const quotePreviewMetas = quoteSources.map((source) => ({
    sourceType: quoteSourceType,
    author: source.author || '',
    year: source.year || '',
    page: source.page || '',
    quoteText: formData.quoteText || ''
  }));
  const quotePreviewText = (() => {
    const quoteText = (formData.quoteText || '').trim();
    if (!quoteText) return '';
    const stripped = quoteText.replace(/^"|"$/g, '');
    const parenthetical = quotePreviewMetas.length > 1
      ? formatMultiInText(quotePreviewMetas)
      : formatInText(quotePreviewMetas[0] || {});
    if (!parenthetical) {
      return quoteSourceType === 'paraphrase' ? stripped : `“${stripped}”`;
    }
    return quoteSourceType === 'paraphrase'
      ? `${stripped} ${parenthetical}.`
      : `“${stripped}” ${parenthetical}.`;
  })();

  if (!isOpen) return null;
  const canRemoveExistingImages = formData.isEditing && imageSource === 'upload' && files.length === 0;
  let sourceArray = [];
  if (imageSource === 'embed') {
    // One metadata form, shown as soon as the pasted link parses.
    sourceArray = parsedImageEmbed ? [{ name: parsedImageEmbed.provider }] : [];
  } else if (imageSource === 'upload') {
    if (files.length > 0) {
      sourceArray = files;
    } else if (existingItems.length > 0) {
      sourceArray = existingItems.map((_, index) => ({ name: `Image ${index + 1}` }));
    } else if (existingSingleImage.length > 0) {
      sourceArray = existingSingleImage;
    }
  } else {
    sourceArray = filenames;
  }
  const isMultipleImages = contentType === 'gallery' || sourceArray.length > 1;
  // Pictures a canvas paste has already placed, awaiting optional citations.
  const pastedImageList = Array.isArray(formData.pastedImages) ? formData.pastedImages : [];
  const contentTypeLabel = contentType === 'pasted-images'
    ? 'Pasted Image'
    : contentType === 'apa-quote'
      ? 'APA Quote'
    : contentType === 'apa-citation'
      ? 'Citation'
      : contentType === 'apa-reference-list'
        ? 'Reference List'
        : contentType === 'html'
          ? 'Code Block'
          : contentType === 'custom-html'
            ? 'Custom HTML/CSS'
            : contentType === 'project-timeline'
              ? 'Project Timeline'
              : contentType === 'youtube-playlist'
                ? 'YouTube Playlist'
                : contentType === 'headline'
                  // The block type stays "headline" — it is persisted in every
                  // saved deck — but "Headline" reads like a newspaper. On a
                  // slide this is the title.
                  ? 'Title'
                  : contentType;
  const isApaCitationModal = ['apa-citation', 'apa-reference-list'].includes(contentType);
  const hasQuoteText = (formData.quoteText || '').trim().length > 0;
  const hasQuoteSource = quoteSources.some((source) => source.author || source.year);
  const isQuoteValid = contentType !== 'apa-quote' || (hasQuoteText && hasQuoteSource);
  const isSubmitDisabled = (isApaCitationModal && !isCitationFormValid) || !isQuoteValid;

  // What each Custom HTML/CSS tab is holding, so a tab that has something in
  // it says so rather than having to be opened to find out. A citation counts
  // only once it has been filled in — the block starts with an empty one.
  const isCustomHtml = contentType === 'custom-html';
  // Nothing to export from two empty boxes, and a button that downloads an
  // empty file is worse than one that says why it is waiting.
  const hasThisLayoutContent = isCustomHtml && hasCustomHtmlContent({
    html: formData.customHtml,
    css: formData.customCss
  });
  const customHtmlTabCounts = {
    citations: isCustomHtml
      ? (formData.citations || []).filter(hasApaCitationContent).length
      : 0,
    media: isCustomHtml
      ? (formData.customImages || []).length + (formData.customVideos || []).length
      : 0
  };

  const selectCustomHtmlTab = (tabId) => {
    setCustomHtmlTab(tabId);
    // Each tab is its own page of the dialog; landing halfway down the last
    // one reads as a broken scroll.
    if (modalBodyRef.current) modalBodyRef.current.scrollTop = 0;
  };

  const handleCustomHtmlTabKeys = (event) => {
    const step = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
    if (!step) return;
    event.preventDefault();
    const index = CUSTOM_HTML_TABS.findIndex((tab) => tab.id === customHtmlTab);
    const next = CUSTOM_HTML_TABS[(index + step + CUSTOM_HTML_TABS.length) % CUSTOM_HTML_TABS.length];
    selectCustomHtmlTab(next.id);
    const list = event.currentTarget;
    const button = list.querySelector(`[data-tab="${next.id}"]`);
    if (button) button.focus();
  };

  return (
    // data-app-modal lets global shortcuts (the Slide Designer's copy/paste)
    // stand down while this modal owns the screen.
    <div data-app-modal="content" className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-[10050] flex items-center justify-center p-4">
      {/* An import that would paint over something already written. Rendered
          above the dialog rather than inside its scrolling body, so it cannot
          be scrolled out of sight while it is the only thing to answer. */}
      {pendingImport && (
        <ConfirmReplaceDialog
          replacing={pendingImport.replacing}
          sources={pendingImport.sources}
          onConfirm={confirmPendingImport}
          onCancel={cancelPendingImport}
        />
      )}
      <div className="bg-white rounded-2xl shadow-2xl max-w-7xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Every JSON import and export in this dialog reports through this
            one toast, so it lives above the per-block branches rather than
            inside the multi-column one it was first written for. */}
        {copyPasteToast.show && (
          <div
            className={`fixed top-4 right-4 z-50 px-6 py-4 rounded-xl shadow-2xl text-white font-semibold transition-all flex items-center gap-3 ${
              copyPasteToast.type === 'success' ? 'bg-gradient-to-r from-green-500 to-green-600' : 'bg-gradient-to-r from-red-500 to-red-600'
            }`}
            style={{ animation: 'slideIn 0.3s ease-out' }}
            role="status"
          >
            <div className="flex-shrink-0">
              {copyPasteToast.type === 'success' ? (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              )}
            </div>
            <span>{copyPasteToast.message}</span>
          </div>
        )}
        <div className="flex justify-between items-center p-6 border-b-2 border-gray-200 bg-gradient-to-r from-slate-50 to-gray-100">
          <div className="flex items-center gap-3">
            <div className="bg-slate-700 text-white rounded-xl p-2.5">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                {contentType === 'multi-column-cards'
                  ? 'Multi-Column Card Block'
                  : contentType === 'pasted-images'
                    ? (pastedImageList.length > 1 ? 'Pasted Images' : 'Pasted Image')
                    : `${formData.isEditing ? 'Edit' : 'Add'} ${contentTypeLabel} Content`}
              </h2>
              <p className="text-xs text-gray-600 mt-0.5">
                {contentType === 'pasted-images'
                  ? 'Already on your slide — add a citation only if it needs one'
                  : 'Configure your content block'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded-lg p-2 transition-all"
          >
            <X size={24} />
          </button>
        </div>

        {/* Placement selector: shown when creating new block and section has existing blocks */}
        {!formData.isEditing && Array.isArray(formData.existingBlocks) && formData.existingBlocks.length > 0 && (
          <div className="px-6 py-2 bg-blue-50 border-b border-blue-100 flex items-center gap-3 text-xs">
            <span className="font-medium text-blue-800">Insert position:</span>
            <select
              value={formData.placement || 'bottom'}
              onChange={(e) => setFormData(prev => {
                const val = e.target.value;
                const update = { ...prev, placement: val };
                if (val === 'top') {
                  update.insertAtIndex = 0;
                  delete update.insertAfterBlockId;
                } else if (val === 'bottom') {
                  delete update.insertAtIndex;
                  delete update.insertAfterBlockId;
                } else if (val.startsWith('after:')) {
                  update.insertAfterBlockId = val.replace('after:', '');
                  delete update.insertAtIndex;
                }
                return update;
              })}
              className="border border-blue-200 rounded px-2 py-1 text-xs bg-white text-blue-900"
            >
              <option value="bottom">Add to bottom</option>
              <option value="top">Add to top</option>
              {formData.existingBlocks.map((b, i) => (
                <option key={b.id} value={`after:${b.id}`}>
                  Insert after: {b.label || b.type} #{i + 1}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="flex flex-col flex-grow overflow-hidden">
          {/* Outside the scrolling body on purpose: the tabs are how you move
              around this dialog, so they stay put while a panel scrolls. */}
          {isCustomHtml && (
            <div
              role="tablist"
              aria-label="Custom HTML/CSS editor sections"
              onKeyDown={handleCustomHtmlTabKeys}
              className="flex flex-wrap items-center gap-1 px-6 pt-3 border-b border-gray-200 bg-gray-50"
            >
              {CUSTOM_HTML_TABS.map((tab) => {
                const TabIcon = tab.icon;
                const isActive = customHtmlTab === tab.id;
                const count = customHtmlTabCounts[tab.id];
                return (
                  <button
                    key={tab.id}
                    type="button"
                    role="tab"
                    data-tab={tab.id}
                    aria-selected={isActive}
                    tabIndex={isActive ? 0 : -1}
                    onClick={() => selectCustomHtmlTab(tab.id)}
                    className={`inline-flex items-center gap-2 px-4 py-2.5 -mb-px text-sm font-semibold border-b-2 rounded-t-lg transition-colors ${
                      isActive
                        ? 'border-fuchsia-600 text-fuchsia-700 bg-white'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <TabIcon size={15} aria-hidden="true" />
                    {tab.label}
                    {count > 0 && (
                      <span className={`px-1.5 py-0.5 rounded-full text-[11px] font-bold ${
                        isActive ? 'bg-fuchsia-100 text-fuchsia-700' : 'bg-gray-200 text-gray-600'
                      }`}>
                        {count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
          <div className="p-6 flex-grow overflow-y-auto" ref={modalBodyRef}>
            {/* A title gets the title editor, here as well as on the slide.
                The modal used to hand it the full rich-text editor, which let
                a Title block be given paragraphs, lists and tables — content a
                title cannot hold. Editing that same block on the slide, where
                the document is a single heading, then silently dropped
                everything but the first heading. Both routes now offer the
                same thing, so what the modal lets you make is what the slide
                keeps. */}
            {contentType === 'headline' && (
              <HeadlineEditor
                content={formData.content || ''}
                onChange={(content) => setFormData({ ...formData, content })}
              />
            )}

            {(['text', 'heading', 'list', 'info-box', 'exercise-box', 'warning-box'].includes(contentType)) && (
              <RichTextEditor
                content={formData.content || ''}
                onChange={(content) => setFormData({ ...formData, content })}
                isHtmlMode={modalHtmlMode}
                onToggleHtmlMode={() => setModalHtmlMode(!modalHtmlMode)}
                isPreviewMode={false}
              />
            )}

            {contentType === 'project-timeline' && (
              <div className="space-y-5">
                <div className="flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-slate-50/60 p-3">
                  <button
                    type="button"
                    onClick={handleTimelineSampleJson}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-white text-slate-600 border border-slate-200 hover:bg-slate-100"
                    title="Download a documented sample JSON — hand it to an AI so it knows the timeline format"
                  >
                    <FileText size={12} /> Sample JSON
                  </button>
                  <button
                    type="button"
                    onClick={handleTimelineImportJson}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-white text-slate-600 border border-slate-200 hover:bg-slate-100"
                    title="Import a timeline from a JSON file"
                  >
                    <Upload size={12} /> Import JSON
                  </button>
                  <button
                    type="button"
                    onClick={handleTimelineExportJson}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-white text-slate-600 border border-slate-200 hover:bg-slate-100"
                    title="Export this timeline to a JSON file"
                  >
                    <Download size={12} /> Export JSON
                  </button>
                  <button
                    type="button"
                    onClick={addTimelineStep}
                    className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-slate-700 text-white hover:bg-slate-800"
                  >
                    <Plus size={12} /> Add step
                  </button>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Timeline title (optional)</label>
                  <input
                    type="text"
                    value={formData.timelineTitle || ''}
                    onChange={(e) => handleFieldChange('timelineTitle', e.target.value)}
                    placeholder="e.g. Product Launch Roadmap"
                    className="w-full p-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                  />
                </div>

                <div className="space-y-4">
                  {timelineStepsForDisplay.map((step, index) => (
                    <div key={step.id || `timeline-step-${index}`} className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
                      <div className="flex items-center gap-2">
                        <span
                          className="flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold text-white"
                          style={{ backgroundColor: step.color || '#2563eb' }}
                        >
                          {index + 1}
                        </span>
                        <span className="text-sm font-semibold text-slate-700">Step {index + 1}</span>
                        <div className="ml-auto flex items-center gap-1.5">
                          {index > 0 && (
                            <button type="button" onClick={() => moveTimelineStep(index, -1)} title="Move earlier" className="rounded border border-slate-200 p-1 text-slate-500 hover:bg-slate-100">
                              <ChevronUp size={13} />
                            </button>
                          )}
                          {index < timelineStepsForDisplay.length - 1 && (
                            <button type="button" onClick={() => moveTimelineStep(index, 1)} title="Move later" className="rounded border border-slate-200 p-1 text-slate-500 hover:bg-slate-100">
                              <ChevronDown size={13} />
                            </button>
                          )}
                          {timelineStepsForDisplay.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeTimelineStep(index)}
                              className="inline-flex items-center gap-1 rounded border border-rose-200 px-2 py-1 text-[11px] font-semibold text-rose-600 hover:bg-rose-50"
                            >
                              <Trash2 size={11} /> Remove
                            </button>
                          )}
                        </div>
                      </div>

                      <div className="grid gap-3 md:grid-cols-2">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Title</label>
                          <input
                            type="text"
                            value={step.title || ''}
                            onChange={(e) => updateTimelineStep(index, { title: e.target.value })}
                            placeholder="Step title"
                            className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Time span (shown in the bar)</label>
                          <input
                            type="text"
                            value={step.timeSpan || ''}
                            onChange={(e) => updateTimelineStep(index, { timeSpan: e.target.value })}
                            placeholder="July, Q1 2022, Week 1, etc."
                            className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                          />
                        </div>
                      </div>

                      <div className="grid gap-3 md:grid-cols-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Icon (emoji)</label>
                          <input
                            type="text"
                            value={step.icon || ''}
                            onChange={(e) => updateTimelineStep(index, { icon: e.target.value })}
                            placeholder="🚀"
                            maxLength={3}
                            className="w-full p-2.5 border border-gray-300 rounded-lg text-sm text-center focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Segment colour</label>
                          <input
                            type="color"
                            value={/^#([0-9a-fA-F]{6})$/.test(step.color || '') ? step.color : '#2563eb'}
                            onChange={(e) => updateTimelineStep(index, { color: e.target.value })}
                            className="h-10 w-full cursor-pointer rounded-lg border border-gray-300 bg-white p-1"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Callout position</label>
                          <select
                            value={step.position === 'above' ? 'above' : 'below'}
                            onChange={(e) => updateTimelineStep(index, { position: e.target.value })}
                            className="w-full p-2.5 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                          >
                            <option value="above">Above the bar</option>
                            <option value="below">Below the bar</option>
                          </select>
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Text</label>
                        <textarea
                          value={step.text || ''}
                          onChange={(e) => updateTimelineStep(index, { text: e.target.value })}
                          placeholder="Type text here"
                          rows={2}
                          className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {contentType === 'divider' && (
              <div className="space-y-5">
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Line style</label>
                    <select
                      value={formData.content?.style || 'solid'}
                      onChange={(e) => setFormData((prev) => ({
                        ...prev,
                        content: {
                          ...DEFAULT_DIVIDER_CONTENT,
                          ...(prev.content || {}),
                          style: e.target.value,
                        }
                      }))}
                      className="w-full p-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                    >
                      <option value="solid">Solid</option>
                      <option value="dashed">Dashed</option>
                      <option value="dotted">Dotted</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Width</label>
                    <select
                      value={formData.content?.width || 'full'}
                      onChange={(e) => setFormData((prev) => ({
                        ...prev,
                        content: {
                          ...DEFAULT_DIVIDER_CONTENT,
                          ...(prev.content || {}),
                          width: e.target.value,
                        }
                      }))}
                      className="w-full p-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                    >
                      <option value="full">Full</option>
                      <option value="medium">Medium</option>
                      <option value="short">Short</option>
                    </select>
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Thickness</label>
                    <input
                      type="range"
                      min="1"
                      max="8"
                      value={formData.content?.thickness || 2}
                      onChange={(e) => setFormData((prev) => ({
                        ...prev,
                        content: {
                          ...DEFAULT_DIVIDER_CONTENT,
                          ...(prev.content || {}),
                          thickness: Number(e.target.value),
                        }
                      }))}
                      className="w-full"
                    />
                    <p className="text-xs text-gray-500 mt-1">{formData.content?.thickness || 2}px</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Spacing</label>
                    <select
                      value={formData.content?.spacing || 'medium'}
                      onChange={(e) => setFormData((prev) => ({
                        ...prev,
                        content: {
                          ...DEFAULT_DIVIDER_CONTENT,
                          ...(prev.content || {}),
                          spacing: e.target.value,
                        }
                      }))}
                      className="w-full p-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                    >
                      <option value="small">Small</option>
                      <option value="medium">Medium</option>
                      <option value="large">Large</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Color</label>
                  <input
                    type="color"
                    value={formData.content?.color || '#cbd5e1'}
                    onChange={(e) => setFormData((prev) => ({
                      ...prev,
                      content: {
                        ...DEFAULT_DIVIDER_CONTENT,
                        ...(prev.content || {}),
                        color: e.target.value,
                      }
                    }))}
                    className="w-14 h-10 rounded border border-gray-300"
                  />
                </div>

                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-3">Preview</p>
                  <div
                    style={{
                      width: formData.content?.width === 'short' ? '33%' : formData.content?.width === 'medium' ? '66%' : '100%',
                      borderTop: `${formData.content?.thickness || 2}px ${formData.content?.style || 'solid'} ${formData.content?.color || '#cbd5e1'}`,
                      margin: '0 auto',
                    }}
                  />
                </div>
              </div>
            )}

            {contentType === 'apa-quote' && (
              <div className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Quote or paraphrase text</label>
                  <textarea
                    value={formData.quoteText || ''}
                    onChange={(e) => handleFieldChange('quoteText', e.target.value)}
                    rows={4}
                    placeholder="Paste the quote or paraphrase you want to highlight..."
                    className="w-full p-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                  />
                  {!hasQuoteText && (
                    <p className="text-xs text-red-600 mt-1">Quote or paraphrase text is required.</p>
                  )}
                </div>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={formData.quoteSourceType === 'paraphrase'}
                    onChange={(e) => handleFieldChange('quoteSourceType', e.target.checked ? 'paraphrase' : 'quote')}
                  />
                  This is a paraphrase (no quotation marks)
                </label>
                <div className="space-y-4">
                  <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4">
                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-semibold text-slate-700">Quote sources</h4>
                      <button
                        type="button"
                        onClick={addQuoteSource}
                        className="text-xs font-semibold text-slate-600 hover:text-slate-800"
                      >
                        + Add source
                      </button>
                    </div>
                    {!hasQuoteSource && (
                      <p className="mt-2 text-xs text-red-600">Add at least one source with author and year.</p>
                    )}
                    <div className="mt-4 space-y-3">
                      {quoteSources.map((source, sourceIndex) => (
                        <div key={`quote-source-${sourceIndex}`} className="rounded-lg border border-slate-200 bg-white p-4">
                          <div className="flex items-center justify-between mb-3">
                            <p className="text-xs font-semibold text-slate-600">Source {sourceIndex + 1}</p>
                            {canRemoveQuoteSource && (
                              <button
                                type="button"
                                onClick={() => removeQuoteSource(sourceIndex)}
                                className="text-xs font-semibold text-red-600 hover:text-red-700"
                              >
                                Remove
                              </button>
                            )}
                          </div>
                          <div className="grid gap-3 md:grid-cols-3">
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Author</label>
                              <input
                                type="text"
                                value={source.author || ''}
                                onChange={(e) => updateQuoteSource(sourceIndex, { author: e.target.value })}
                                placeholder="Author, A. A."
                                className="w-full p-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Year</label>
                              <input
                                type="text"
                                value={source.year || ''}
                                onChange={(e) => updateQuoteSource(sourceIndex, { year: e.target.value })}
                                placeholder="2024"
                                className="w-full p-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Page number</label>
                              <input
                                type="text"
                                value={source.page || ''}
                                onChange={(e) => updateQuoteSource(sourceIndex, { page: e.target.value })}
                                placeholder="45-47"
                                className="w-full p-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="rounded-lg border border-slate-200 bg-white p-4 text-sm text-slate-700">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Formatted preview</p>
                    <p>{quotePreviewText || 'Add quote/paraphrase text and source details to preview the APA format.'}</p>
                  </div>

                  <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
                      <input
                        type="text"
                        value={formData.title || ''}
                        onChange={(e) => handleFieldChange('title', e.target.value)}
                        placeholder="Title of the article or book"
                        className="w-full p-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Source/Publisher</label>
                      <input
                        type="text"
                        value={formData.source || ''}
                        onChange={(e) => handleFieldChange('source', e.target.value)}
                        placeholder="Journal name, publisher, etc."
                        className="w-full p-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">DOI</label>
                      <input
                        type="text"
                        value={formData.doi || ''}
                        onChange={(e) => handleFieldChange('doi', e.target.value)}
                        placeholder="10.0000/xyz123"
                        className="w-full p-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">URL</label>
                      <input
                        type="text"
                        value={formData.url || ''}
                        onChange={(e) => handleFieldChange('url', e.target.value)}
                        placeholder="https://example.com"
                        className="w-full p-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* A layout's sources are one tab of its editor; the citation and
                reference-list blocks are the whole dialog. Same fields either
                way — only the Custom HTML/CSS block can have them hidden
                behind a tab. */}
            {(['apa-citation', 'apa-reference-list'].includes(contentType)
              || (isCustomHtml && customHtmlTab === 'citations')) && (
              <div
                className="space-y-6"
                role={isCustomHtml ? 'tabpanel' : undefined}
                aria-label={isCustomHtml ? 'Citations' : undefined}
              >
                {/* A layout block's citation is optional — the block exists to
                    look a particular way, not to cite. An APA citation block
                    and a reference list are their citation, so the switch would
                    only offer to empty them. */}
                {contentType === 'custom-html' && (
                  <>
                    <label
                      className={`flex items-center gap-3 rounded-xl border-2 p-3 cursor-pointer transition-colors ${
                        formData.includeCitation !== false
                          ? 'border-slate-400 bg-slate-50'
                          : 'border-gray-200 bg-gray-50 hover:border-gray-300'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={formData.includeCitation !== false}
                        onChange={(e) => handleFieldChange('includeCitation', e.target.checked)}
                        className="h-6 w-6 rounded border-gray-300 text-slate-600 focus:ring-slate-500"
                      />
                      <span className="min-w-0">
                        <span className="block text-sm font-bold text-gray-800">
                          Cite this layout {formData.includeCitation !== false ? '' : '— off'}
                        </span>
                        <span className="block text-xs text-gray-600 mt-0.5">
                          {formData.includeCitation !== false
                            ? 'Shows the APA note with the layout and adds its sources to the deck’s reference list.'
                            : 'No note with the layout and nothing in the reference list. The sources below are kept.'}
                        </span>
                      </span>
                    </label>
                    {formData.includeCitation !== false && (
                      <div className="flex items-start gap-2">
                        <input
                          id="custom-html-add-to-reference-list"
                          type="checkbox"
                          checked={formData.addToReferenceList !== false}
                          onChange={(e) => handleFieldChange('addToReferenceList', e.target.checked)}
                          className="h-4 w-4 mt-0.5 rounded border-gray-300 text-slate-600 focus:ring-slate-500"
                        />
                        <label htmlFor="custom-html-add-to-reference-list" className="text-xs text-gray-700">
                          Add to reference list
                          <span className="block text-[11px] text-gray-500 font-normal mt-0.5">
                            Lists these sources at the back of the deck. Turn it off to show the note
                            with the layout only.
                          </span>
                        </label>
                      </div>
                    )}
                    {/* The note on the slide itself. Until now an author had to
                        copy the in-text citation into their own markup, which
                        an assistant then wrote over the next time the layout
                        was regenerated. */}
                    {formData.includeCitation !== false && (
                      <div className="flex items-start gap-2">
                        <input
                          id="custom-html-citation-overlay"
                          type="checkbox"
                          checked={formData.citationOverlay === true}
                          onChange={(e) => handleFieldChange('citationOverlay', e.target.checked)}
                          className="h-4 w-4 mt-0.5 rounded border-gray-300 text-slate-600 focus:ring-slate-500"
                        />
                        <label htmlFor="custom-html-citation-overlay" className="text-xs text-gray-700">
                          Show the note on the slide
                          <span className="block text-[11px] text-gray-500 font-normal mt-0.5">
                            Draws the in-text note over the layout — bottom right to start with, and
                            you can drag it anywhere on the slide once it is there. It shows in the
                            editor and in every export. Style it yourself with{' '}
                            <code>.lp-cite-overlay</code> in the CSS box — your rules come after
                            these.
                          </span>
                          {formData.citationOverlayPos && (
                            <span className="mt-1 flex items-center gap-2">
                              <span className="text-[11px] text-gray-500">
                                Moved to {Math.round(formData.citationOverlayPos.x)}% across,{' '}
                                {Math.round(formData.citationOverlayPos.y)}% down.
                              </span>
                              <button
                                type="button"
                                onClick={() => handleFieldChange('citationOverlayPos', null)}
                                className="rounded-md border border-gray-300 bg-white px-2 py-0.5 text-[11px] font-semibold text-gray-600 hover:bg-gray-100"
                              >
                                Put it back in the corner
                              </button>
                            </span>
                          )}
                        </label>
                      </div>
                    )}
                  </>
                )}
                <div className="flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-slate-50/60 p-3">
                  <button
                    type="button"
                    onClick={() => setCitationTab('create')}
                    className={`px-3 py-1.5 text-xs font-semibold rounded-lg ${citationTab === 'create' ? 'bg-slate-700 text-white' : 'bg-white text-slate-600 border border-slate-200'}`}
                  >
                    Create / Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => setCitationTab('library')}
                    className={`px-3 py-1.5 text-xs font-semibold rounded-lg ${citationTab === 'library' ? 'bg-slate-700 text-white' : 'bg-white text-slate-600 border border-slate-200'}`}
                  >
                    Saved Library ({sortedSavedApaCitations.length})
                  </button>
                  {contentType === 'apa-reference-list' && (
                    <button
                      type="button"
                      onClick={() => {
                        setCitationTab('slides');
                        setReferenceRescan(null);
                      }}
                      className={`px-3 py-1.5 text-xs font-semibold rounded-lg ${citationTab === 'slides' ? 'bg-slate-700 text-white' : 'bg-white text-slate-600 border border-slate-200'}`}
                    >
                      Slide citations ({referenceReview.slideCitations.length})
                    </button>
                  )}

                  <div className="ml-auto flex items-center gap-2">
                    <button
                      type="button"
                      onClick={handleDownloadSampleCitationJson}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-white text-slate-600 border border-slate-200 hover:bg-slate-100"
                      title="Download a documented sample JSON file — hand it to an AI so it knows the exact import format"
                    >
                      <FileText size={12} /> Sample JSON
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowCitationPaste((open) => !open);
                        setCitationPasteError('');
                      }}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg border ${
                        showCitationPaste
                          ? 'bg-slate-700 text-white border-slate-700'
                          : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-100'
                      }`}
                      aria-expanded={showCitationPaste}
                      title="Paste citation JSON straight from an assistant"
                    >
                      <ClipboardPaste size={12} /> Paste JSON
                    </button>
                    <button
                      type="button"
                      onClick={handleImportCitationsJson}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-white text-slate-600 border border-slate-200 hover:bg-slate-100"
                      title="Import citations from a JSON file"
                    >
                      <Upload size={12} /> Import JSON
                    </button>
                    <button
                      type="button"
                      onClick={handleExportCitationsJson}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-white text-slate-600 border border-slate-200 hover:bg-slate-100"
                      title="Export the citations in this block to a JSON file"
                    >
                      <Download size={12} /> Export JSON
                    </button>
                  </div>
                </div>

                {showCitationPaste && (
                  <div className="rounded-xl border border-slate-300 bg-white p-3 space-y-2">
                    <div className="flex items-baseline justify-between gap-3">
                      <label htmlFor="citation-paste-json" className="text-xs font-semibold text-slate-700">
                        Paste citation JSON
                      </label>
                      <span className="text-[11px] text-slate-500">
                        The shape <span className="font-semibold">Sample JSON</span> describes — one
                        citation or a whole <code>citations</code> array.
                      </span>
                    </div>
                    <textarea
                      id="citation-paste-json"
                      value={citationPasteText}
                      onChange={(e) => {
                        setCitationPasteText(e.target.value);
                        if (citationPasteError) setCitationPasteError('');
                      }}
                      onPaste={() => setCitationPasteError('')}
                      rows={7}
                      spellCheck={false}
                      placeholder={'{\n  "citations": [\n    {\n      "sourceType": "webpage",\n      "authorLastName": "Disney",\n      "date": "2019-04-02",\n      "title": "Traditional animation desk [Photograph]",\n      "source": "D23 Expo",\n      "url": "https://example.com/desk"\n    }\n  ]\n}'}
                      className="w-full rounded-lg border border-slate-300 bg-slate-50 p-3 font-mono text-xs text-slate-800 focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                    />
                    {citationPasteError && (
                      <p className="text-xs font-semibold text-red-600">{citationPasteError}</p>
                    )}
                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        onClick={handlePasteCitationsJson}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-slate-700 text-white hover:bg-slate-800"
                      >
                        <ClipboardPaste size={12} /> Fill in the fields
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setShowCitationPaste(false);
                          setCitationPasteText('');
                          setCitationPasteError('');
                        }}
                        className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-white text-slate-600 border border-slate-200 hover:bg-slate-100"
                      >
                        Cancel
                      </button>
                      <p className="text-[11px] text-slate-500 flex-1 min-w-[14rem]">
                        Each entry fills a citation below, ready to check and edit. A ```json fence
                        around it is fine — it is stripped.
                      </p>
                    </div>
                  </div>
                )}

                {citationTab === 'slides' ? (
                  <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-4">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <h4 className="text-sm font-semibold text-slate-800">Citations collected from the deck</h4>
                        <p className="text-xs text-slate-500 mt-1">
                          Every image, video, quote and citation block in the presentation, as the reference
                          list sees it. The list rebuilds itself whenever the deck changes — rescan to confirm
                          it matches what you expect.
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={handleReferenceRescan}
                        className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-slate-700 px-3 py-2 text-xs font-semibold text-white hover:bg-slate-800"
                      >
                        <RefreshCw size={13} />
                        Rescan slides
                      </button>
                    </div>

                    {referenceRescan && (
                      <p className="rounded-md border border-emerald-200 bg-emerald-50 p-2 text-xs text-emerald-800">
                        {referenceRescan.added === 0 && referenceRescan.removed === 0
                          ? `Up to date — ${referenceRescan.slideCount} citation${referenceRescan.slideCount === 1 ? '' : 's'} from the slides, ${referenceRescan.total} reference${referenceRescan.total === 1 ? '' : 's'} in the list.`
                          : `Rescanned: ${referenceRescan.added} added, ${referenceRescan.removed} no longer present. The list now shows ${referenceRescan.total} reference${referenceRescan.total === 1 ? '' : 's'}.`}
                      </p>
                    )}

                    {referenceReview.duplicateIndexes.length > 0 && (
                      <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-amber-200 bg-amber-50 p-3">
                        <p className="text-xs text-amber-900">
                          {referenceReview.duplicateIndexes.length} reference item{referenceReview.duplicateIndexes.length === 1 ? '' : 's'} you
                          typed {referenceReview.duplicateIndexes.length === 1 ? 'is' : 'are'} already collected from a slide.
                          Items you type are kept exactly as typed, so the list is printing
                          {referenceReview.duplicateIndexes.length === 1 ? ' that source' : ' those sources'} twice.
                          Removing {referenceReview.duplicateIndexes.length === 1 ? 'it' : 'them'} leaves the slide's own entry in place.
                        </p>
                        <button
                          type="button"
                          onClick={handleRemoveDuplicateReferenceItems}
                          className="shrink-0 rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-xs font-semibold text-amber-900 hover:bg-amber-100"
                        >
                          Remove duplicate item{referenceReview.duplicateIndexes.length === 1 ? '' : 's'}
                        </button>
                      </div>
                    )}

                    <div className="flex flex-wrap items-center gap-4 text-[11px] text-slate-500">
                      <span className="flex items-center gap-1.5">
                        <span className="h-3 w-3 rounded border border-slate-300 bg-white" />
                        Cited on a slide
                      </span>
                      <span className="flex items-center gap-1.5">
                        <span className="h-3 w-3 rounded border border-indigo-300 border-l-4 border-l-indigo-500 bg-indigo-50" />
                        Typed here — no slide cites it
                      </span>
                      <span className="flex items-center gap-1.5">
                        <span className="h-3 w-3 rounded border border-amber-300 border-l-4 border-l-amber-500 bg-amber-50" />
                        Typed here and cited on a slide — printed twice
                      </span>
                    </div>

                    {referenceReview.entries.length > 0 ? (
                      <div className="space-y-2">
                        {referenceReview.entries.map((entry) => (
                          <div
                            key={`${entry.kind}-${entry.key}-${entry.index ?? ''}`}
                            className={`rounded-lg border p-3 ${
                              entry.isDuplicate
                                ? 'border-amber-300 border-l-4 border-l-amber-500 bg-amber-50'
                                : entry.kind === 'manual'
                                  ? 'border-indigo-300 border-l-4 border-l-indigo-500 bg-indigo-50'
                                  : 'border-slate-200 bg-white'
                            }`}
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div className="min-w-0">
                                <div className="text-sm text-slate-700">{entry.reference}</div>
                                <div className="mt-1 flex flex-wrap gap-1.5">
                                  {entry.kind === 'manual' ? (
                                    <span className="rounded-full bg-indigo-100 px-2 py-0.5 text-[11px] text-indigo-700">
                                      Typed here{entry.isDuplicate ? ' · also on a slide' : ''}
                                    </span>
                                  ) : entry.origins.map((origin, originIndex) => (
                                    <span
                                      key={`${entry.key}-origin-${originIndex}`}
                                      className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-600"
                                    >
                                      {describeOrigin(origin)}
                                    </span>
                                  ))}
                                </div>
                              </div>
                              {entry.kind === 'manual' ? (
                                <div className="flex shrink-0 items-center gap-3">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setCitationTab('create');
                                      setCitationScrollTarget(entry.index);
                                    }}
                                    className="text-xs font-semibold text-indigo-700 hover:text-indigo-900"
                                  >
                                    Edit
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => removeCitation(entry.index)}
                                    className="text-xs font-semibold text-red-600 hover:text-red-700"
                                  >
                                    Remove
                                  </button>
                                </div>
                              ) : entry.canExclude && onSetSourceInclusion ? (
                                <button
                                  type="button"
                                  onClick={() => entry.origins
                                    .filter((origin) => origin.includeField)
                                    .forEach((origin) => onSetSourceInclusion(origin, false))}
                                  title="Clears that block's 'Add to reference list' setting. The source note under the media goes too, and you can put it back below."
                                  className="shrink-0 text-xs font-semibold text-slate-600 hover:text-slate-800"
                                >
                                  Remove
                                </button>
                              ) : (
                                <span className="shrink-0 text-[11px] text-slate-400" title="Edit or delete this block on its own slide">
                                  Edit on its slide
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-slate-500">
                        Nothing in the reference list yet. Add items on the Create / Edit tab, or cite an
                        image or video on a slide.
                      </p>
                    )}

                    {referenceReview.excluded.length > 0 && (
                      <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-2">
                        <p className="text-xs font-semibold text-slate-700">
                          In the deck but kept out of the list ({referenceReview.excluded.length})
                        </p>
                        {referenceReview.excluded.map((entry) => (
                          <div key={`excluded-${entry.key}`} className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <div className="text-sm text-slate-500">{entry.reference}</div>
                              <div className="mt-1 flex flex-wrap gap-1.5">
                                {entry.origins.map((origin, originIndex) => (
                                  <span
                                    key={`excluded-${entry.key}-${originIndex}`}
                                    className="rounded-full bg-white px-2 py-0.5 text-[11px] text-slate-500 border border-slate-200"
                                  >
                                    {describeOrigin(origin)}
                                  </span>
                                ))}
                              </div>
                            </div>
                            {onSetSourceInclusion && entry.origins.some((origin) => origin.includeField) && (
                              <button
                                type="button"
                                onClick={() => entry.origins
                                  .filter((origin) => origin.includeField)
                                  .forEach((origin) => onSetSourceInclusion(origin, true))}
                                className="shrink-0 text-xs font-semibold text-emerald-700 hover:text-emerald-800"
                              >
                                Add to list
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-200 pt-3">
                      <p className="text-xs text-slate-500">
                        The reference list prints <strong>{referenceReview.totalReferences}</strong> entr
                        {referenceReview.totalReferences === 1 ? 'y' : 'ies'}:
                        {' '}{referenceReview.slideCitations.length} collected from the slides
                        {referenceReview.manual.filter((item) => !item.isEmpty).length > 0
                          && `, ${referenceReview.manual.filter((item) => !item.isEmpty).length} typed here by hand`}.
                      </p>
                      <button
                        type="button"
                        onClick={() => {
                          addCitation();
                          setCitationTab('create');
                        }}
                        className="shrink-0 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                      >
                        + Add a reference item
                      </button>
                    </div>
                  </div>
                ) : citationTab === 'library' ? (
                  <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-4">
                    <div className="flex flex-col gap-3 md:flex-row">
                      <input
                        type="text"
                        value={libraryQuery}
                        onChange={(e) => setLibraryQuery(e.target.value)}
                        placeholder="Search saved citations..."
                        className="flex-1 rounded-lg border border-slate-200 p-2 text-sm"
                      />
                      <select
                        value={libraryFilter}
                        onChange={(e) => setLibraryFilter(e.target.value)}
                        className="rounded-lg border border-slate-200 p-2 text-sm"
                      >
                        <option value="all">All types</option>
                        <option value="book">Book</option>
                        <option value="journal">Journal</option>
                        <option value="webpage">Webpage</option>
                        <option value="video">Video</option>
                        <option value="image">Image</option>
                        <option value="software">Software</option>
                        <option value="ai">AI</option>
                      </select>
                    </div>

                    {filteredSavedApaCitations.length > 0 ? (
                      <div className="space-y-3">
                        {filteredSavedApaCitations.map((citation, index) => (
                          <div key={`saved-citation-${index}`} className="flex flex-col gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3">
                            <div className="flex gap-2 text-xs text-slate-600">
                              <span className="font-semibold text-slate-500">{index + 1}.</span>
                              <div className="flex-1">
                                {(() => {
                                  const { before, link, after, inline } = buildReferenceDisplay(citation);
                                  if (!link) return before || 'Untitled citation';
                                  return (
                                    <>
                                      {before}
                                      <a
                                        href={link}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="text-indigo-600 hover:text-indigo-800 hover:underline"
                                      >
                                        {inline ? link : ` ${link}`}
                                      </a>
                                      {inline ? after : ''}
                                    </>
                                  );
                                })()}
                              </div>
                            </div>
                            <div className="flex flex-wrap items-center gap-2">
                              <button
                                type="button"
                                onClick={() => addSavedCitationToBlock(citation)}
                                className="text-xs font-semibold text-slate-700 hover:text-slate-900"
                              >
                                Apply to block
                              </button>
                              <button
                                type="button"
                                onClick={() => editSavedCitation(citation)}
                                className="text-xs font-semibold text-slate-700 hover:text-slate-900"
                              >
                                Edit
                              </button>
                              <button
                                type="button"
                                onClick={() => removeSavedCitation(citation)}
                                className="text-xs font-semibold text-red-600 hover:text-red-700"
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-slate-500">No saved citations match your search.</p>
                    )}
                  </div>
                ) : (
                  <>
                    {contentType === 'apa-reference-list' && (
                      <div className="rounded-lg border border-slate-200 bg-white p-4 text-sm text-slate-600">
                        APA citation blocks in the presentation will automatically appear in this reference list.
                      </div>
                    )}

                    {/* Sticky, because a list long enough to need finding is
                        long enough to scroll the search box off the top. */}
                    {citationsForDisplay.length > 1 && (
                      <form
                        className="sticky top-0 z-20 -mx-6 px-6 py-2 bg-white/95 backdrop-blur border-b border-slate-200 flex flex-wrap items-center gap-2"
                        onSubmit={(e) => {
                          e.preventDefault();
                          goToNextCitationMatch();
                        }}
                      >
                        <label htmlFor="citation-find" className="shrink-0 text-xs font-semibold text-slate-600">
                          Find
                        </label>
                        <input
                          id="citation-find"
                          type="text"
                          value={citationFindText}
                          onChange={(e) => {
                            setCitationFindText(e.target.value);
                            // A new search starts from the top of the list.
                            setCitationFindAt(-1);
                          }}
                          placeholder="Author, title, publisher, year…"
                          autoComplete="off"
                          className="min-w-0 flex-1 rounded-lg border border-slate-300 bg-white py-1.5 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-slate-500"
                        />
                        <button
                          type="submit"
                          disabled={!citationMatches.length}
                          title={citationMatches.length ? 'Go to the next match' : 'Type a name to find'}
                          className={`inline-flex shrink-0 items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-semibold ${
                            citationMatches.length
                              ? 'border-slate-300 bg-white text-slate-700 hover:bg-slate-100'
                              : 'border-slate-200 bg-slate-50 text-slate-300 cursor-not-allowed'
                          }`}
                        >
                          <Search size={13} />
                          Find
                        </button>
                        <span className="shrink-0 text-xs text-slate-500 min-w-[5rem]">
                          {!citationFindText.trim()
                            ? `${citationsForDisplay.length} entries`
                            : citationMatches.length === 0
                              ? 'No match'
                              : citationFindPosition >= 0
                                ? `${citationFindPosition + 1} of ${citationMatches.length}`
                                : `${citationMatches.length} match${citationMatches.length === 1 ? '' : 'es'}`}
                        </span>
                      </form>
                    )}

                    {citationsForDisplay.map((citation, index) => {
                  const citationType = citation.sourceType || citation.type || 'paraphrase';
                  const { firstName: authorFirstName, lastName: authorLastName } = getCitationAuthorInput(citation);
                  const usesYear = ['book', 'journal', 'paraphrase', 'quote', 'software'].includes(citationType);
                  const usesDate = ['webpage', 'video', 'image', 'table', 'clipart_external', 'clipart_builtin', 'ai'].includes(citationType);
                  const showAuthorFields = !['video', 'clipart_builtin', 'ai'].includes(citationType);
                  const showPageField = !['video', 'clipart_builtin', 'software', 'ai'].includes(citationType);
                  const showLicenseField = ['image', 'table', 'clipart_external'].includes(citationType);
                  const isTableType = citationType === 'table';
                  const citationMeta = mapCitationToSourceMeta(citation);
                  const errors = citationValidation[index] || {};
                  // A figure or table cites itself as a note; everything else
                  // takes the parenthetical form.
                  const notePreview = buildMediaNote(citationMeta);
                  const inTextPreview = notePreview ? notePreview.text : formatInText(citationMeta);
                  const inTextSegments = notePreview ? notePreview.segments : [{ text: inTextPreview }];
                  const referenceContent = buildReferenceContent(citationMeta);
                  const referencePreview = referenceContent ? referenceContent.text : '';
                  const citationLicenseNotice = showLicenseField ? licenseWarning(citation.license) : null;
                  return (
                  <div
                    key={citation.id || `citation-${index}`}
                    id={`citation-block-${index}`}
                    className={`rounded-xl border p-5 bg-white shadow-sm scroll-mt-16 ${
                      citationFindAt === index
                        ? 'border-slate-500 ring-2 ring-slate-400'
                        : 'border-gray-200'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-base font-semibold text-gray-800">
                        {contentType === 'apa-reference-list' ? 'Reference item' : 'Citation'} {index + 1}
                      </h3>
                      <div className="flex items-center gap-3">
                        <button
                          type="button"
                          onClick={() => saveCitationToLibrary(citation, { isEdit: editingLibraryCitationId === citation.citationId })}
                          className="text-xs font-semibold text-slate-600 hover:text-slate-800"
                        >
                          {editingLibraryCitationId === citation.citationId ? 'Update Library' : 'Save to Library'}
                        </button>
                        <button
                          type="button"
                          onClick={() => clearCitationFields(index)}
                          className="text-xs font-semibold text-slate-600 hover:text-slate-800"
                        >
                          Clear
                        </button>
                        {canRemoveCitation && (
                          <button
                            type="button"
                            onClick={() => removeCitation(index)}
                            className="text-xs font-semibold text-red-600 hover:text-red-700"
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="grid gap-4 md:grid-cols-2">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Source type</label>
                        <select
                          value={citationType}
                          onChange={(e) => updateCitation(index, {
                            sourceType: e.target.value,
                            type: ({
                              paraphrase: 'book',
                              quote: 'book',
                              image: 'image',
                              table: 'image',
                              clipart_builtin: 'image',
                              clipart_external: 'image',
                              video: 'video',
                              webpage: 'website',
                              book: 'book',
                              journal: 'journal',
                              software: 'software',
                              ai: 'ai'
                            })[e.target.value] || 'book',
                            ...(e.target.value === 'video' && !citation.platform ? { platform: 'youtube' } : {})
                          })}
                          className="w-full p-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                        >
                          <option value="paraphrase">Paraphrase</option>
                          <option value="quote">Quote</option>
                          <option value="image">Image/Figure</option>
                          <option value="table">Table</option>
                          <option value="clipart_builtin">Clipart (built-in)</option>
                          <option value="clipart_external">Clipart (external)</option>
                          <option value="video">Video</option>
                          <option value="webpage">Webpage</option>
                          <option value="book">Book</option>
                          <option value="journal">Journal article</option>
                          <option value="software">Software/Repository</option>
                          <option value="ai">Generative AI</option>
                        </select>
                      </div>

                      {showAuthorFields && (
                        <div className="grid gap-3 md:grid-cols-2">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Author first name</label>
                            <input
                              type="text"
                              value={authorFirstName}
                              onChange={(e) => updateCitation(index, { authorFirstName: e.target.value })}
                              placeholder="First name"
                              className="w-full p-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Author last name</label>
                            <input
                              type="text"
                              value={authorLastName}
                              onChange={(e) => updateCitation(index, { authorLastName: e.target.value })}
                              placeholder="Last name"
                              className="w-full p-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                            />
                            {errors.author && (
                              <p className="text-xs text-red-600 mt-1">{errors.author}</p>
                            )}
                          </div>
                        </div>
                      )}

                      {citationType === 'video' && (
                        <div className="md:col-span-2 space-y-3">
                          <label className="block text-sm font-medium text-gray-700">Video URL</label>
                          <div className="flex flex-col gap-3 md:flex-row">
                            <input
                              type="text"
                              value={citation.url || ''}
                              onChange={(e) => {
                                const url = e.target.value;
                                const detectedPlatform = detectPlatform(url);
                                updateCitation(index, {
                                  url,
                                  ...(detectedPlatform ? { platform: detectedPlatform } : {})
                                });
                              }}
                              placeholder="https://www.youtube.com/watch?v=..."
                              className="flex-grow p-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                            />
                            {CITATION_FETCHABLE_PLATFORMS.includes(citation.platform) && (
                              <button
                                type="button"
                                onClick={() => fetchCitationVideoInfo(index)}
                                disabled={!citation.url || citationFetchIndex === index}
                                className="px-4 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-700 disabled:bg-gray-300 transition-colors font-medium flex items-center justify-center"
                              >
                                {citationFetchIndex === index ? (
                                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                ) : (
                                  'Fetch Info'
                                )}
                              </button>
                            )}
                          </div>
                        </div>
                      )}

                      {citationType === 'video' && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Platform</label>
                          <select
                            value={citation.platform || 'youtube'}
                            onChange={(e) => updateCitation(index, { platform: e.target.value })}
                            className="w-full p-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                          >
                            <option value="youtube">YouTube</option>
                            <option value="vimeo">Vimeo</option>
                            <option value="panopto">Panopto</option>
                            <option value="disney">Disney Video</option>
                            <option value="other">Other</option>
                          </select>
                        </div>
                      )}

                      {citationType === 'video' && (
                        <div className="grid gap-3 md:grid-cols-2">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Creator first name</label>
                            <input
                              type="text"
                              value={authorFirstName}
                              onChange={(e) => updateCitation(index, { authorFirstName: e.target.value })}
                              placeholder="First name"
                              className="w-full p-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Creator last name</label>
                            <input
                              type="text"
                              value={authorLastName}
                              onChange={(e) => updateCitation(index, { authorLastName: e.target.value })}
                              placeholder="Last name"
                              className="w-full p-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                            />
                          </div>
                        </div>
                      )}

                      {usesYear && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Year</label>
                          <input
                            type="text"
                            value={citation.year || ''}
                            onChange={(e) => updateCitation(index, { year: e.target.value })}
                            placeholder="2024"
                            className="w-full p-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                          />
                          {!(citation.year || '').trim() && !(citation.date || '').trim() && (
                            <p className="text-xs text-slate-500 mt-1">
                              Leave empty if there is no date — it will be cited as <strong>(n.d.)</strong>.
                            </p>
                          )}
                        </div>
                      )}

                      {usesDate && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Publication date</label>
                          <input
                            type="date"
                            value={citation.date || ''}
                            onChange={(e) => updateCitation(index, { date: e.target.value })}
                            className="w-full p-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                          />
                          {!(citation.date || '').trim() && !(citation.year || '').trim() && (
                            <p className="text-xs text-slate-500 mt-1">
                              Leave empty if there is no date — it will be cited as <strong>(n.d.)</strong>.
                            </p>
                          )}
                        </div>
                      )}

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {citationType === 'journal'
                            ? 'Article title'
                            : citationType === 'video'
                              ? 'Video title'
                            : citationType === 'webpage'
                              ? 'Webpage title'
                            : citationType === 'image'
                                ? 'Image title'
                              : citationType === 'table'
                                ? 'Table title'
                              : citationType === 'clipart_external'
                                ? 'Clipart title'
                              : citationType === 'software'
                                ? 'Software name'
                              : citationType === 'ai'
                                ? 'Prompt description'
                              : citationType === 'book'
                                ? 'Book title'
                                : 'Title'}
                        </label>
                        <input
                          type="text"
                          value={citation.title || ''}
                          onChange={(e) => updateCitation(index, { title: e.target.value })}
                          placeholder="Title"
                          className="w-full p-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                        />
                        {errors.title && (
                          <p className="text-xs text-red-600 mt-1">{errors.title}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {citationType === 'journal'
                            ? 'Journal title'
                            : citationType === 'webpage'
                              ? 'Website name'
                            : citationType === 'video'
                              ? 'Platform name'
                            : citationType === 'image'
                                ? 'Image source'
                              : citationType === 'table'
                                ? 'Table source'
                              : citationType === 'clipart_external'
                                ? 'Clipart source'
                              : citationType === 'software'
                                ? 'Publisher/Host'
                              : citationType === 'ai'
                                ? 'Tool name'
                              : citationType === 'book'
                                ? 'Publisher'
                                : 'Source'}
                        </label>
                        <input
                          type="text"
                          value={citation.source || ''}
                          onChange={(e) => updateCitation(index, { source: e.target.value })}
                          placeholder="Source or publisher"
                          className="w-full p-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                        />
                        {errors.source && (
                          <p className="text-xs text-red-600 mt-1">{errors.source}</p>
                        )}
                      </div>

                      {citationType === 'software' && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Version</label>
                          <input
                            type="text"
                            value={citation.version || ''}
                            onChange={(e) => updateCitation(index, { version: e.target.value })}
                            placeholder="v2.1.0"
                            className="w-full p-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                          />
                        </div>
                      )}

                      {citationType === 'ai' && (
                        <>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Organization</label>
                            <input
                              type="text"
                              value={citation.publisher || ''}
                              onChange={(e) => updateCitation(index, { publisher: e.target.value })}
                              placeholder="OpenAI"
                              className="w-full p-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                            />
                            {errors.publisher && (
                              <p className="text-xs text-red-600 mt-1">{errors.publisher}</p>
                            )}
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Model/version</label>
                            <input
                              type="text"
                              value={citation.version || ''}
                              onChange={(e) => updateCitation(index, { version: e.target.value })}
                              placeholder="GPT-4"
                              className="w-full p-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                            />
                          </div>
                        </>
                      )}

                      {citationType === 'journal' && (
                        <>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Volume</label>
                            <input
                              type="text"
                              value={citation.volume || ''}
                              onChange={(e) => updateCitation(index, { volume: e.target.value })}
                              placeholder="12"
                              className="w-full p-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                            />
                            {errors.volume && (
                              <p className="text-xs text-red-600 mt-1">{errors.volume}</p>
                            )}
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Issue</label>
                            <input
                              type="text"
                              value={citation.issue || ''}
                              onChange={(e) => updateCitation(index, { issue: e.target.value })}
                              placeholder="3"
                              className="w-full p-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                            />
                          </div>
                          <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-2">Pages</label>
                            <input
                              type="text"
                              value={citation.pages || ''}
                              onChange={(e) => updateCitation(index, { pages: e.target.value })}
                              placeholder="45-67"
                              className="w-full p-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                            />
                            {errors.pages && (
                              <p className="text-xs text-red-600 mt-1">{errors.pages}</p>
                            )}
                          </div>
                        </>
                      )}

                      {(citationType === 'book' || citationType === 'paraphrase' || citationType === 'quote') && showPageField && (
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            {citationType === 'quote' || citationType === 'paraphrase'
                              ? 'Page number (required)'
                              : 'Page number (optional)'}
                          </label>
                          <input
                            type="text"
                            value={citation.page || ''}
                            onChange={(e) => updateCitation(index, { page: e.target.value })}
                            placeholder="45-47"
                            className="w-full p-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                          />
                          {errors.page && (
                            <p className="text-xs text-red-600 mt-1">{errors.page}</p>
                          )}
                        </div>
                      )}

                      {(citationType === 'journal' || citationType === 'book') && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">DOI (optional)</label>
                          <input
                            type="text"
                            value={citation.doi || ''}
                            onChange={(e) => updateCitation(index, { doi: e.target.value })}
                            placeholder="10.0000/xyz123"
                            className="w-full p-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                          />
                        </div>
                      )}

                      <div className={citationType === 'journal' ? 'md:col-span-2' : ''}>
                        <label className="block text-sm font-medium text-gray-700 mb-2">URL (optional)</label>
                        <input
                          type="text"
                          value={citation.url || ''}
                          onChange={(e) => updateCitation(index, { url: e.target.value })}
                          placeholder="https://example.com"
                          className="w-full p-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                        />
                        {errors.url && (
                          <p className="text-xs text-red-600 mt-1">{errors.url}</p>
                        )}
                      </div>

                      {showLicenseField && (
                        <div className="md:col-span-2 grid gap-3 md:grid-cols-2">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">License/Copyright</label>
                            <input
                              type="text"
                              value={citation.license || ''}
                              onChange={(e) => updateCitation(index, { license: e.target.value })}
                              placeholder="CC BY-NC 2.0"
                              list={`citation-license-presets-${citation.id || index}`}
                              className="w-full p-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                            />
                            <datalist id={`citation-license-presets-${citation.id || index}`}>
                              {LICENSE_PRESETS.filter((preset) => preset.value).map((preset) => (
                                <option key={preset.value} value={preset.value}>{preset.label}</option>
                              ))}
                            </datalist>
                            {citationLicenseNotice && (
                              <p className="mt-1 text-xs leading-snug text-amber-700">{citationLicenseNotice}</p>
                            )}
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Reference template</label>
                            <select
                              value={citation.referenceCategory || ''}
                              onChange={(e) => updateCitation(index, { referenceCategory: e.target.value })}
                              className="w-full p-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                            >
                              <option value="">Choose from the fields</option>
                              {IMAGE_REFERENCE_CATEGORIES.map((option) => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Medium / image format</label>
                            <input
                              type="text"
                              value={citation.medium || ''}
                              onChange={(e) => updateCitation(index, { medium: e.target.value })}
                              placeholder="Photograph"
                              list={`citation-medium-options-${citation.id || index}`}
                              className="w-full p-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                            />
                            <datalist id={`citation-medium-options-${citation.id || index}`}>
                              {MEDIUM_OPTIONS.map((option) => (
                                <option key={option} value={option}>{option}</option>
                              ))}
                            </datalist>
                            <p className="mt-1 text-xs text-slate-500">Reference list only — never shown in the slide note.</p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Title in sentence case</label>
                            <input
                              type="text"
                              value={citation.referenceTitle || ''}
                              onChange={(e) => updateCitation(index, { referenceTitle: e.target.value })}
                              placeholder="Picture perfect: The making of Sleeping Beauty"
                              className="w-full p-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                            />
                            <p className="mt-1 text-xs text-slate-500">Optional. The slide note keeps the Title Case title.</p>
                          </div>
                          {isTableType && (
                            <div className="flex items-center gap-2 mt-6">
                              <input
                                id={`adapted-${citation.id || index}`}
                                type="checkbox"
                                checked={Boolean(citation.isAdapted)}
                                onChange={(e) => updateCitation(index, { isAdapted: e.target.checked })}
                                className="h-4 w-4 rounded border-gray-300 text-slate-600 focus:ring-slate-500"
                              />
                              <label htmlFor={`adapted-${citation.id || index}`} className="text-sm text-gray-700">
                                Mark as adapted
                              </label>
                            </div>
                          )}
                        </div>
                      )}

                      <div className="md:col-span-2 rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
                        <div className="font-semibold text-slate-700">Formatted preview</div>
                      <div className="mt-1 flex items-start gap-2">
                        <span><span className="font-semibold">In-text:</span>{' '}
                        {inTextPreview
                          ? <ApaSegments segments={inTextSegments} />
                          : 'Add source details to preview the in-text citation.'}</span>
                        <CopyTextButton value={inTextPreview} label="Copy in-text note" />
                      </div>
                      <div className="mt-1 flex items-start gap-2">
                        <span><span className="font-semibold">Narrative:</span>{' '}
                        {formatInTextNarrative(citationMeta) || 'Add source details to preview the narrative citation.'}</span>
                        <CopyTextButton value={formatInTextNarrative(citationMeta)} label="Copy narrative citation" />
                      </div>
                        <div className="mt-1 flex items-start gap-2">
                          <span><span className="font-semibold">Reference:</span>{' '}
                          {referenceContent
                            ? <ApaSegments segments={referenceContent.segments} />
                            : 'Add source details to preview the reference entry.'}</span>
                          <CopyTextButton value={referencePreview} label="Copy reference entry" />
                        </div>
                        {contentType === 'custom-html' && (
                          <p className="mt-2 text-[11px] text-slate-500">
                            Paste the in-text note into your prompt and ask the assistant to place it in
                            the layout. The full reference is added to the deck's reference list on its
                            own — you do not need to write it into the slide.
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )})}

                    <button
                      type="button"
                      onClick={addCitation}
                      className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-slate-700 border border-slate-200 rounded-lg hover:bg-slate-50"
                    >
                      + Add citation
                    </button>
                  </>
                )}
              </div>
            )}
            
            {contentType === 'worksheet' && (
              <WorksheetBuilder
                formData={formData}
                setFormData={setFormData}
              />
            )}

            {contentType === 'html' && (
              <div className="space-y-5">
                {/* Code content */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Code Content</label>
                  <textarea
                    value={formData.codeContent || ''}
                    onChange={(e) => handleFieldChange('codeContent', e.target.value)}
                    rows={14}
                    placeholder="Enter your HTML, CSS, JavaScript, or other code here..."
                    className="w-full p-3 border border-gray-300 rounded-lg font-mono text-sm focus:ring-2 focus:ring-slate-500 focus:border-transparent bg-gray-50"
                    style={{ tabSize: 2 }}
                    spellCheck={false}
                  />
                </div>

                {/* Language + Mode row */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Language</label>
                    <select
                      value={formData.codeLanguage || 'html'}
                      onChange={(e) => handleFieldChange('codeLanguage', e.target.value)}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                    >
                      <option value="html">HTML</option>
                      <option value="css">CSS</option>
                      <option value="javascript">JavaScript</option>
                      <option value="json">JSON</option>
                      <option value="text">Plain Text</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Display Mode</label>
                    <select
                      value={formData.codeMode || 'displayCode'}
                      onChange={(e) => handleFieldChange('codeMode', e.target.value)}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                    >
                      <option value="displayCode">Show as Code</option>
                      <option value="renderRich">Render HTML Output</option>
                    </select>
                  </div>
                </div>

                {/* Options */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Options</label>
                  <div className="flex flex-wrap gap-4">
                    <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.codeShowLineNumbers !== false}
                        onChange={(e) => handleFieldChange('codeShowLineNumbers', e.target.checked)}
                        className="rounded border-gray-300"
                      />
                      Line Numbers
                    </label>
                    <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={Boolean(formData.codeSoftWrap)}
                        onChange={(e) => handleFieldChange('codeSoftWrap', e.target.checked)}
                        className="rounded border-gray-300"
                      />
                      Soft Wrap
                    </label>
                    <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={Boolean(formData.codeAllowScripts)}
                        onChange={(e) => handleFieldChange('codeAllowScripts', e.target.checked)}
                        className="rounded border-gray-300"
                      />
                      Allow Scripts
                    </label>
                  </div>
                </div>

                {/* Custom CSS */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Custom CSS <span className="text-gray-400 font-normal">(optional)</span></label>
                  <textarea
                    value={formData.codeCustomCss || ''}
                    onChange={(e) => handleFieldChange('codeCustomCss', e.target.value)}
                    rows={3}
                    placeholder="Add custom CSS styles for the rendered output..."
                    className="w-full p-3 border border-gray-300 rounded-lg font-mono text-sm focus:ring-2 focus:ring-slate-500 focus:border-transparent bg-gray-50"
                    spellCheck={false}
                  />
                </div>

                {/* Citation */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Citation / Source <span className="text-gray-400 font-normal">(optional)</span></label>
                  <input
                    type="text"
                    value={formData.codeCitation || ''}
                    onChange={(e) => handleFieldChange('codeCitation', e.target.value)}
                    placeholder="e.g. MDN Web Docs, W3Schools, etc."
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                  />
                </div>
              </div>
            )}

            {isCustomHtml && customHtmlTab === 'editor' && (
              <div className="space-y-5" role="tabpanel" aria-label="HTML / CSS">
                <div className="rounded-lg border border-fuchsia-200 bg-fuchsia-50 px-4 py-3 text-sm text-fuchsia-900">
                  Write the markup and styles for this slide's body. Scripts, iframes and form
                  controls are removed — this is layout, not an embedded app. Use the{' '}
                  <span className="font-semibold">Code</span> block instead if you want code shown
                  as code.
                </div>

                {/* The way in and the way out, together at the top. A layout
                    is written elsewhere — in an editor, or by an assistant —
                    and the round trip is one gesture in each direction, so the
                    two buttons belong beside each other rather than at
                    opposite ends of a long scroll. */}
                <div className="flex flex-wrap items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                  <button
                    type="button"
                    onClick={handleImportCustomHtmlFiles}
                    disabled={isImporting}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white hover:bg-gray-100 text-fuchsia-700 border border-fuchsia-300 text-sm font-semibold shadow-sm disabled:opacity-60"
                  >
                    <Upload size={15} />
                    {isImporting ? 'Importing…' : 'Import HTML/CSS'}
                  </button>
                  <button
                    type="button"
                    onClick={() => downloadCustomHtmlLayout({
                      html: formData.customHtml || '',
                      css: formData.customCss || '',
                      blockId: formData.id,
                      slideName: formData.blockTitle || '',
                      deckName,
                      deckScoped: formData.customCssScope === 'deck',
                      images: formData.customImages || [],
                      videos: formData.customVideos || []
                    })}
                    disabled={!hasThisLayoutContent}
                    title={hasThisLayoutContent
                      ? 'One HTML file holding both boxes — the CSS in a <style> block, the markup below it'
                      : 'Write or paste a layout below and this will hand it back as a file'}
                    className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold shadow-sm ${
                      hasThisLayoutContent
                        ? 'bg-white hover:bg-gray-100 text-fuchsia-700 border border-fuchsia-300'
                        : 'bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed'
                    }`}
                  >
                    <Download size={15} />
                    Export HTML + CSS
                  </button>
                  <p className="text-xs text-gray-600 flex-1 min-w-[16rem]">
                    <span className="font-semibold">In:</span> load <code>.html</code>,{' '}
                    <code>.htm</code> or <code>.css</code> files straight into the boxes below —
                    pick both at once and each lands where its extension says. Scripts, iframes and
                    form controls are stripped when the block renders, the same as anything typed
                    here.{' '}
                    <span className="font-semibold">Out:</span> one HTML file holding both boxes,
                    the CSS in a <code>&lt;style&gt;</code> block and the markup below it. It opens
                    in a browser at slide proportions, pastes into an assistant in one go, and comes
                    back into both boxes exactly as it left.
                  </p>
                  {importStatus && (
                    <p
                      className={`w-full text-xs font-semibold ${importStatus.tone === 'error' ? 'text-red-600' : 'text-emerald-700'}`}
                      role={importStatus.tone === 'error' ? 'alert' : 'status'}
                    >
                      {importStatus.message}
                    </p>
                  )}
                </div>

                <div ref={htmlSectionRef}>
                  <CodeEditorField
                    id="custom-html-source"
                    label="HTML"
                    language="html"
                    value={formData.customHtml || ''}
                    onChange={(next) => handleFieldChange('customHtml', next)}
                    placeholder={'<div class="my-layout">\n  <h2>Slide title</h2>\n  <p>Body copy…</p>\n</div>'}
                    height={340}
                    // The CSS box sits below three hundred-odd lines of markup.
                    // This is the way down to it without scrolling through them.
                    jumpAction={{
                      label: 'Jump to CSS editor',
                      title: 'Jump to the CSS editor below',
                      onActivate: scrollToCssEditor,
                      direction: 'down'
                    }}
                    hint={(
                      <>
                        A <code>&lt;style&gt;</code> tag pasted in here is folded into the CSS below
                        rather than dropped.
                      </>
                    )}
                  />
                </div>

                <div ref={cssSectionRef}>
                  <CodeEditorField
                    id="custom-css-source"
                    label="CSS"
                    language="css"
                    value={formData.customCss || ''}
                    onChange={(next) => handleFieldChange('customCss', next)}
                    placeholder={'.my-layout {\n  display: grid;\n  gap: 1rem;\n}'}
                    height={260}
                    // And the way back. A rule is written against a class name
                    // that lives up in the markup, so the trip is made in both
                    // directions — and until now the return leg had no button.
                    jumpAction={{
                      label: 'Jump to HTML editor',
                      title: 'Jump back to the HTML editor above',
                      onActivate: scrollToHtmlEditor,
                      direction: 'up'
                    }}
                  />
                </div>

                <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                  <label className="flex items-start gap-3 text-sm text-gray-800 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.customCssScope === 'deck'}
                      onChange={(e) => handleFieldChange('customCssScope', e.target.checked ? 'deck' : 'slide')}
                      className="mt-0.5 rounded border-gray-300"
                    />
                    <span>
                      <span className="font-semibold">Apply this CSS to the entire slide deck</span>
                      <span className="block text-xs text-gray-600 mt-0.5">
                        {formData.customCssScope === 'deck'
                          ? 'These rules are left global and reach every slide in the deck.'
                          : 'Leave unchecked and every selector is prefixed with this block, so the rules stay on this slide.'}
                      </span>
                    </span>
                  </label>
                </div>

              </div>
            )}

            {isCustomHtml && customHtmlTab === 'examples' && (
              <div className="space-y-5" role="tabpanel" aria-label="Examples and brief">
                {/* Hand the rules to an LLM instead of memorising them: the
                    brief carries the tag allowlist, the CSS scoping, the 16:9
                    geometry and the background rules, plus a skeleton of each
                    field, all generated from the constants the block actually
                    enforces. */}
                <div className="flex flex-wrap items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                  <button
                    type="button"
                    onClick={() => downloadCustomHtmlBrief({
                      blockId: formData.id,
                      deckScoped: formData.customCssScope === 'deck',
                      images: formData.customImages || [],
                      videos: formData.customVideos || [],
                      citations: formData.citations || [],
                    })}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-fuchsia-600 hover:bg-fuchsia-700 text-white text-sm font-semibold shadow-sm"
                  >
                    <Download size={15} />
                    Download LLM brief
                  </button>
                  <p className="text-xs text-gray-600 flex-1 min-w-[16rem]">
                    A structure-only spec — 16:9 fit, layout limits, background-image rules, what
                    survives sanitizing — plus a skeleton of each field. Give it to an AI assistant
                    with your slide content and it returns HTML and CSS that paste straight into
                    the two boxes on the <span className="font-semibold">HTML / CSS</span> tab.
                  </p>
                </div>

                {/* The brief states the rules; this shows two layouts that keep
                    them. Worth having as its own file: an assistant given a
                    working example matches it far more closely than one given
                    a spec, and the two examples cover the parts authors get
                    wrong most — sizing a linked picture, and writing a link
                    that still works once the slide is a picture. */}
                <div className="flex flex-wrap items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                  <button
                    type="button"
                    onClick={() => downloadCustomHtmlExamples({
                      images: formData.customImages || [],
                      videos: formData.customVideos || [],
                      citations: formData.citations || [],
                    })}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white hover:bg-gray-100 text-fuchsia-700 border border-fuchsia-300 text-sm font-semibold shadow-sm"
                  >
                    <Download size={15} />
                    Download example layouts
                  </button>
                  <p className="text-xs text-gray-600 flex-1 min-w-[16rem]">
                    Two complete slides that already fit the 16:9 content region: one placing an
                    uploaded picture beside its text, one showing how to write buttons and links
                    that stay clickable in the PowerPoint, PDF and HTML exports. Send it with the
                    brief and ask for a layout in the same shape.
                  </p>
                </div>

                {/* Both downloads are generated from what the block holds right
                    now, so this is worth saying where the buttons are rather
                    than in the panel the uploads live in. */}
                <p className="text-xs text-gray-500">
                  Both files describe this block as it stands — the pictures and clips on the{' '}
                  <span className="font-semibold">Media</span> tab and the sources on{' '}
                  <span className="font-semibold">Citations</span> are written into them. Add those
                  first and the assistant gets their real names and sizes.
                </p>
              </div>
            )}

            {isCustomHtml && customHtmlTab === 'media' && (
              <div className="space-y-5" role="tabpanel" aria-label="Media">
                <CustomHtmlImagePanel
                  images={formData.customImages || []}
                  onChange={(images) => handleFieldChange('customImages', images)}
                />

                <CustomHtmlVideoPanel
                  videos={formData.customVideos || []}
                  onChange={(videos) => handleFieldChange('customVideos', videos)}
                  onFetchInfo={fetchCustomHtmlVideoInfo}
                  fetchingIndex={customVideoFetchIndex}
                />
              </div>
            )}

            {contentType === 'video' && (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Video Platform</label>
                  <select
                    value={formData.videoPlatform || 'youtube'}
                    onChange={(e) => handleFieldChange('videoPlatform', e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                  >
                    <option value="youtube">YouTube</option>
                    <option value="vimeo">Vimeo</option>
                    <option value="panopto">Panopto</option>
                    <option value="disney">Disney Video</option>
                    <option value="embed">Custom Embed Code</option>
                  </select>
                </div>

                {formData.videoPlatform === 'embed' ? (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Embed Code</label>
                    <textarea
                      value={formData.embedCode || ''}
                      onChange={(e) => handleFieldChange('embedCode', e.target.value)}
                      rows={5}
                      placeholder='Paste your full embed code here (e.g., <iframe... ></iframe>)'
                      className="w-full p-3 border border-gray-300 rounded-lg font-mono text-sm focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                    />
                  </div>
                ) : (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Video URL</label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={formData.videoUrl || ''}
                        onChange={(e) => {
                          const url = e.target.value;
                          handleFieldChange('videoUrl', url);
                          
                          // Auto-detect platform when URL changes
                          const detectedPlatform = detectPlatform(url);
                          if (detectedPlatform) {
                            handleFieldChange('videoPlatform', detectedPlatform);
                          }
                        }}
                        placeholder="https://www.youtube.com/watch?v=..."
                        className="flex-grow p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                      />
                      <button
                        onClick={() => {
                          const platform = formData.videoPlatform || detectPlatform(formData.videoUrl) || 'youtube';
                          fetchVideoInfo(formData.videoUrl, platform);
                        }}
                        disabled={isLoadingVideoInfo || !formData.videoUrl}
                        className="px-4 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-700 disabled:bg-gray-300 transition-colors font-medium flex items-center justify-center"
                      >
                        {isLoadingVideoInfo ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div> : "Fetch Info"}
                      </button>
                    </div>
                  </div>
                )}

                {formData.videoPlatform !== 'embed' && (
                  <div className="border border-gray-200 rounded-lg p-4 bg-gray-50 space-y-4">
                    <div className="flex items-center justify-between">
                      <h4 className="font-semibold text-gray-800">APA Citation Details (Optional)</h4>
                      <button
                        type="button"
                        onClick={clearVideoCitationFields}
                        className="text-xs font-semibold text-slate-600 hover:text-slate-800"
                      >
                        Clear citation
                      </button>
                    </div>
                    {/* The same three buttons the APA citation and reference
                        list blocks carry, over the same file format: a citation
                        written for one of those imports here, and one written
                        here imports there. */}
                    <div className="flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-white p-2">
                      <button
                        type="button"
                        onClick={handleDownloadVideoCitationSample}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-white text-slate-600 border border-slate-200 hover:bg-slate-100"
                        title="Download a documented sample JSON file — hand it to an AI so it knows the exact import format"
                      >
                        <FileText size={12} /> Sample JSON
                      </button>
                      <button
                        type="button"
                        onClick={handleImportVideoCitationJson}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-white text-slate-600 border border-slate-200 hover:bg-slate-100"
                        title="Fill these fields from a citation JSON file"
                      >
                        <Upload size={12} /> Import JSON
                      </button>
                      <button
                        type="button"
                        onClick={handleExportVideoCitationJson}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-white text-slate-600 border border-slate-200 hover:bg-slate-100"
                        title="Export this video's citation to a JSON file"
                      >
                        <Download size={12} /> Export JSON
                      </button>
                    </div>
                    {/* The whole citation in one switch, matching the picture
                        card's. Off means no note under the video and nothing in
                        the reference list. */}
                    <label
                      className={`flex items-center gap-3 rounded-xl border-2 p-3 cursor-pointer transition-colors ${
                        formData.videoIncludeCitation !== false
                          ? 'border-slate-400 bg-slate-50'
                          : 'border-gray-200 bg-gray-50 hover:border-gray-300'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={formData.videoIncludeCitation !== false}
                        onChange={(e) => handleFieldChange('videoIncludeCitation', e.target.checked)}
                        className="h-6 w-6 rounded border-gray-300 text-slate-600 focus:ring-slate-500"
                      />
                      <span className="min-w-0">
                        <span className="block text-sm font-bold text-gray-800">
                          Cite this video {formData.videoIncludeCitation !== false ? '' : '— off'}
                        </span>
                        <span className="block text-xs text-gray-600 mt-0.5">
                          {formData.videoIncludeCitation !== false
                            ? 'Shows the source note under the video and adds it to the deck’s reference list. Turned off automatically when you tear the citation off into its own movable note on a slide.'
                            : 'No note under the video and nothing in the reference list. The fields below are kept, so turning this back on restores them.'}
                        </span>
                      </span>
                    </label>
                    <div className="flex items-start gap-2">
                      <input
                        id="video-add-to-reference-list"
                        type="checkbox"
                        checked={formData.videoAddToReferenceList !== false}
                        onChange={(e) => handleFieldChange('videoAddToReferenceList', e.target.checked)}
                        className="h-4 w-4 mt-0.5 rounded border-gray-300 text-slate-600 focus:ring-slate-500"
                      />
                      <label htmlFor="video-add-to-reference-list" className="text-xs text-gray-700">
                        Add to reference list
                        <span className="block text-[11px] text-gray-500 font-normal mt-0.5">
                          Lists this video at the back of the deck. Turn it off to show the note under it only.
                        </span>
                      </label>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Video Title</label>
                        <input type="text" value={formData.videoTitle || ''} onChange={(e) => handleFieldChange('videoTitle', e.target.value)} className="w-full p-2 border border-gray-300 rounded-md" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Author/Channel</label>
                        <input type="text" value={formData.videoAuthor || ''} onChange={(e) => handleFieldChange('videoAuthor', e.target.value)} className="w-full p-2 border border-gray-300 rounded-md" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Source (Platform Name)</label>
                        <input type="text" value={formData.videoSource || ''} onChange={(e) => handleFieldChange('videoSource', e.target.value)} className="w-full p-2 border border-gray-300 rounded-md" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Publication Date</label>
                        <input type="date" value={formData.videoDate || ''} onChange={(e) => handleFieldChange('videoDate', e.target.value)} className="w-full p-2 border border-gray-300 rounded-md" />
                      </div>
                    </div>
                    <div className="rounded-lg border border-slate-200 bg-white p-3 text-xs text-slate-600">
                      <div className="font-semibold text-slate-700 mb-1">Formatted preview</div>
                      <div>
                        <span className="font-semibold">In-text:</span>{' '}
                        {(() => {
                          const videoMeta = buildVideoSourceMeta(formData);
                          const videoCitation = formatInText(videoMeta);
                          const videoUrl = (videoMeta.url || '').trim();
                          if (!videoCitation && !videoUrl) {
                            return 'Add video details to preview the in-text citation.';
                          }
                          return (
                            <>
                              {videoCitation}
                              {videoUrl && (
                                <a
                                  href={videoUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="text-indigo-600 hover:text-indigo-800 hover:underline"
                                >
                                  {videoUrl}
                                </a>
                              )}
                            </>
                          );
                        })()}
                      </div>
                      <div className="mt-1">
                        <span className="font-semibold">Narrative:</span>{' '}
                        {formatInTextNarrative(buildVideoSourceMeta(formData)) || 'Add video details to preview the narrative citation.'}
                      </div>
                      <div className="mt-1">
                        <span className="font-semibold">Reference:</span>{' '}
                        {formatReference(buildVideoSourceMeta(formData)) || 'Add video details to preview the reference entry.'}
                      </div>
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Aspect Ratio</label>
                  <select
                    value={formData.aspectRatio || '16-9'}
                    onChange={(e) => handleFieldChange('aspectRatio', e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                  >
                    <option value="16-9">16:9 (Widescreen)</option>
                    <option value="4-3">4:3 (Standard)</option>
                    <option value="1-1">1:1 (Square)</option>
                    <option value="21-9">21:9 (Cinematic)</option>
                  </select>
                </div>
              </div>
            )}

            {contentType === 'youtube-playlist' && (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Playlist link</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={formData.playlistInput || ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        const parsed = parseYouTubePlaylist(value);
                        setFormData((prev) => {
                          const existing = Array.isArray(prev.playlistItems) ? prev.playlistItems : [];
                          // The link already names one video, so the card starts
                          // with a running order of one rather than an empty rail.
                          // Fetch playlist or the paste box fills in the rest.
                          const seeded = parsed?.videoId && existing.length === 0
                            ? [normalizePlaylistItem({ videoId: parsed.videoId }, 0)]
                            : existing;
                          return {
                            ...prev,
                            playlistInput: value,
                            playlistId: parsed?.playlistId || '',
                            playlistItems: seeded,
                            // The link says where the author was in the playlist;
                            // that becomes where the card opens.
                            startVideoId: parsed?.videoId || prev.startVideoId || '',
                            startIndex: parsed?.index || prev.startIndex || ''
                          };
                        });
                      }}
                      placeholder="https://www.youtube.com/watch?v=…&list=PL…&index=3"
                      className="flex-grow p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                    />
                    <button
                      type="button"
                      onClick={fetchPlaylistInfo}
                      disabled={isLoadingVideoInfo || !parsedPlaylist}
                      className="px-4 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-700 disabled:bg-gray-300 transition-colors font-medium flex items-center justify-center whitespace-nowrap"
                    >
                      {isLoadingVideoInfo
                        ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        : 'Fetch playlist'}
                    </button>
                  </div>
                  <p className="mt-2 text-xs text-gray-500">
                    Copy the address while the playlist is open — anything containing <code>list=</code> works:
                    a watch link, a share link, or the playlist page itself.
                  </p>
                  {formData.playlistInput && !parsedPlaylist && (
                    <p className="mt-2 text-xs font-medium text-red-600">
                      No playlist in that link. A single video belongs in the Video block.
                    </p>
                  )}
                  {parsedPlaylist && (
                    <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-800">
                      <div><span className="font-semibold">Playlist:</span> {parsedPlaylist.playlistId}</div>
                      {parsedPlaylist.videoId && (
                        <div>
                          <span className="font-semibold">Opens on:</span> {parsedPlaylist.videoId}
                          {parsedPlaylist.index ? ` (video ${parsedPlaylist.index})` : ''}
                        </div>
                      )}
                      <div className="mt-1 break-all">
                        <span className="font-semibold">Share link:</span> {playlistPageUrl(parsedPlaylist.playlistId)}
                      </div>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Playlist title</label>
                    <input
                      type="text"
                      value={formData.playlistTitle || ''}
                      onChange={(e) => handleFieldChange('playlistTitle', e.target.value)}
                      placeholder="Frank Thomas & Ollie Johnston Pencil Tests"
                      className="w-full p-2 border border-gray-300 rounded-md"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Channel / curator</label>
                    <input
                      type="text"
                      value={formData.playlistChannel || ''}
                      onChange={(e) => handleFieldChange('playlistChannel', e.target.value)}
                      placeholder="Brady Greensides"
                      className="w-full p-2 border border-gray-300 rounded-md"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Why this playlist (optional)</label>
                  <textarea
                    value={formData.playlistDescription || ''}
                    onChange={(e) => handleFieldChange('playlistDescription', e.target.value)}
                    rows={2}
                    placeholder="What students should watch for as they work through it."
                    className="w-full p-2 border border-gray-300 rounded-md"
                  />
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Layout</label>
                    <select
                      value={formData.playlistLayout || 'split'}
                      onChange={(e) => handleFieldChange('playlistLayout', e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded-md"
                    >
                      {Object.entries(PLAYLIST_LAYOUTS).map(([value, label]) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Card theme</label>
                    <select
                      value={formData.playlistTheme || 'light'}
                      onChange={(e) => handleFieldChange('playlistTheme', e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded-md"
                    >
                      <option value="light">Light</option>
                      <option value="dark">Dark</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Accent</label>
                    <input
                      type="color"
                      value={formData.playlistAccentColor || DEFAULT_PLAYLIST_ACCENT}
                      onChange={(e) => handleFieldChange('playlistAccentColor', e.target.value)}
                      className="w-full h-[38px] p-1 border border-gray-300 rounded-md bg-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Aspect ratio</label>
                    <select
                      value={formData.aspectRatio || '16-9'}
                      onChange={(e) => handleFieldChange('aspectRatio', e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded-md"
                    >
                      <option value="16-9">16:9 (Widescreen)</option>
                      <option value="4-3">4:3 (Standard)</option>
                      <option value="1-1">1:1 (Square)</option>
                      <option value="21-9">21:9 (Cinematic)</option>
                    </select>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-6">
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={formData.playlistShowThumbnails !== false}
                      onChange={(e) => handleFieldChange('playlistShowThumbnails', e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300 text-slate-600 focus:ring-slate-500"
                    />
                    Show thumbnails in the list
                  </label>
                  <div className="flex items-center gap-2">
                    <label className="text-sm text-gray-700">Videos in the full playlist</label>
                    <input
                      type="number"
                      min="0"
                      value={formData.playlistItemCount || ''}
                      onChange={(e) => handleFieldChange('playlistItemCount', e.target.value)}
                      placeholder="14"
                      className="w-20 p-2 border border-gray-300 rounded-md"
                    />
                  </div>
                </div>

                {/* The running order. Fetching fills it in; the paste box and the
                    rows below cover playlists the API cannot reach. */}
                <div className="border border-gray-200 rounded-lg p-4 bg-gray-50 space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold text-gray-800">
                      Videos in this card {playlistItemsForEditor.length ? `(${playlistItemsForEditor.length})` : ''}
                    </h4>
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={fetchPlaylistItemTitles}
                        disabled={isLoadingVideoInfo || playlistItemsForEditor.length === 0}
                        className="text-xs font-semibold text-slate-600 hover:text-slate-800 disabled:text-gray-300"
                        title="Look each listed video up and fill in its title"
                      >
                        Fetch titles
                      </button>
                      <button
                        type="button"
                        onClick={addPlaylistItem}
                        className="text-xs font-semibold text-slate-600 hover:text-slate-800"
                      >
                        + Add video
                      </button>
                    </div>
                  </div>

                  {playlistItemsForEditor.length === 0 && (
                    <p className="text-xs text-gray-500">
                      The card shows no running order until there are videos here — the player still
                      carries the whole playlist. Use <span className="font-semibold">Fetch playlist</span> above
                      to pull the whole list in at once, or paste the video links below and press{' '}
                      <span className="font-semibold">Fetch titles</span> to name them.
                    </p>
                  )}

                  <div className="space-y-2">
                    {playlistItemsForEditor.map((item, index) => {
                      const isStart = item.videoId && item.videoId === formData.startVideoId;
                      return (
                        <div key={item.id || index} className="rounded-lg border border-gray-200 bg-white p-3">
                          <div className="flex items-center gap-2">
                            <span className="w-5 text-center text-xs font-semibold text-gray-500">{index + 1}</span>
                            {item.videoId && (
                              <img
                                src={item.src || videoThumbnailUrl(item.videoId)}
                                alt=""
                                className="h-8 w-14 rounded object-cover bg-slate-900"
                              />
                            )}
                            <input
                              type="text"
                              value={item.title || ''}
                              onChange={(e) => handlePlaylistItemChange(index, { title: e.target.value })}
                              placeholder="Video title"
                              className="flex-1 min-w-0 p-2 border border-gray-300 rounded-md text-sm"
                            />
                            <input
                              type="text"
                              value={item.duration || ''}
                              onChange={(e) => handlePlaylistItemChange(index, { duration: formatDuration(e.target.value) })}
                              placeholder="0:14"
                              className="w-20 p-2 border border-gray-300 rounded-md text-sm"
                            />
                            <div className="flex items-center gap-1">
                              <button type="button" onClick={() => movePlaylistItem(index, -1)} disabled={index === 0}
                                className="px-1.5 text-gray-500 hover:text-gray-800 disabled:text-gray-300" title="Move up">↑</button>
                              <button type="button" onClick={() => movePlaylistItem(index, 1)} disabled={index === playlistItemsForEditor.length - 1}
                                className="px-1.5 text-gray-500 hover:text-gray-800 disabled:text-gray-300" title="Move down">↓</button>
                              <button type="button" onClick={() => removePlaylistItem(index)}
                                className="px-1.5 text-red-600 hover:text-red-700" title="Remove">×</button>
                            </div>
                          </div>
                          <div className="mt-2 flex flex-wrap items-center gap-2">
                            <input
                              type="text"
                              value={item.videoId || ''}
                              onChange={(e) => handlePlaylistItemChange(index, { videoId: e.target.value })}
                              placeholder="Video ID or link"
                              className="flex-1 min-w-[12rem] p-2 border border-gray-300 rounded-md text-xs font-mono"
                            />
                            <label className="flex items-center gap-1.5 text-xs text-gray-600">
                              <input
                                type="radio"
                                name="playlist-start-video"
                                checked={Boolean(isStart)}
                                disabled={!item.videoId}
                                onChange={() => setFormData((prev) => ({
                                  ...prev,
                                  startVideoId: item.videoId,
                                  startIndex: index + 1
                                }))}
                                className="h-3.5 w-3.5 text-slate-600 focus:ring-slate-500"
                              />
                              Card opens here
                            </label>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className="rounded-lg border border-dashed border-gray-300 bg-white p-3">
                    <label className="block text-xs font-semibold text-gray-700 mb-1">
                      Or paste the video links, one per line
                    </label>
                    <textarea
                      value={formData.playlistBulkInput || ''}
                      onChange={(e) => handleFieldChange('playlistBulkInput', e.target.value)}
                      rows={3}
                      placeholder={'https://www.youtube.com/watch?v=…\nhttps://youtu.be/…'}
                      className="w-full p-2 border border-gray-300 rounded-md text-xs font-mono"
                    />
                    <button
                      type="button"
                      onClick={applyPlaylistBulkInput}
                      disabled={!formData.playlistBulkInput}
                      className="mt-2 px-3 py-1.5 text-xs font-semibold rounded-md bg-slate-600 text-white hover:bg-slate-700 disabled:bg-gray-300"
                    >
                      Add these videos
                    </button>
                  </div>
                </div>

                <div className="border border-gray-200 rounded-lg p-4 bg-gray-50 space-y-4">
                  <h4 className="font-semibold text-gray-800">APA Citation Details (Optional)</h4>
                  {/* Same single switch as the video and picture cards. */}
                  <label
                    className={`flex items-center gap-3 rounded-xl border-2 p-3 cursor-pointer transition-colors ${
                      formData.videoIncludeCitation !== false
                        ? 'border-slate-400 bg-slate-50'
                        : 'border-gray-200 bg-gray-50 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={formData.videoIncludeCitation !== false}
                      onChange={(e) => handleFieldChange('videoIncludeCitation', e.target.checked)}
                      className="h-6 w-6 rounded border-gray-300 text-slate-600 focus:ring-slate-500"
                    />
                    <span className="min-w-0">
                      <span className="block text-sm font-bold text-gray-800">
                        Cite this playlist {formData.videoIncludeCitation !== false ? '' : '— off'}
                      </span>
                      <span className="block text-xs text-gray-600 mt-0.5">
                        {formData.videoIncludeCitation !== false
                          ? 'Shows the source note under the card and adds the playlist to the deck’s reference list, exactly as a video block does.'
                          : 'No note under the card and nothing in the reference list. The fields below are kept.'}
                      </span>
                    </span>
                  </label>
                  <div className="flex items-start gap-2">
                    <input
                      id="playlist-add-to-reference-list"
                      type="checkbox"
                      checked={formData.videoAddToReferenceList !== false}
                      onChange={(e) => handleFieldChange('videoAddToReferenceList', e.target.checked)}
                      className="h-4 w-4 mt-0.5 rounded border-gray-300 text-slate-600 focus:ring-slate-500"
                    />
                    <label htmlFor="playlist-add-to-reference-list" className="text-xs text-gray-700">
                      Add to reference list
                      <span className="block text-[11px] text-gray-500 font-normal mt-0.5">
                        Lists this playlist at the back of the deck. Turn it off to show the note under it only.
                      </span>
                    </label>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Source (platform name)</label>
                      <input
                        type="text"
                        value={formData.videoSource || ''}
                        onChange={(e) => handleFieldChange('videoSource', e.target.value)}
                        placeholder="YouTube"
                        className="w-full p-2 border border-gray-300 rounded-md"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Publication date</label>
                      <input
                        type="date"
                        value={formData.videoDate || ''}
                        onChange={(e) => handleFieldChange('videoDate', e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded-md"
                      />
                    </div>
                  </div>
                  <div className="rounded-lg border border-slate-200 bg-white p-3 text-xs text-slate-600">
                    <div className="font-semibold text-slate-700 mb-1">Formatted preview</div>
                    <div>
                      <span className="font-semibold">In-text:</span>{' '}
                      {formatInText(buildVideoSourceMeta({
                        videoAuthor: formData.playlistChannel,
                        videoTitle: formData.playlistTitle,
                        videoSource: formData.videoSource,
                        videoDate: formData.videoDate,
                        videoUrl: parsedPlaylist ? playlistPageUrl(parsedPlaylist.playlistId) : ''
                      })) || 'Add the channel and date to preview the in-text citation.'}
                    </div>
                    <div className="mt-1">
                      <span className="font-semibold">Reference:</span>{' '}
                      {formatReference(buildVideoSourceMeta({
                        videoAuthor: formData.playlistChannel,
                        videoTitle: formData.playlistTitle,
                        videoSource: formData.videoSource,
                        videoDate: formData.videoDate,
                        videoUrl: parsedPlaylist ? playlistPageUrl(parsedPlaylist.playlistId) : ''
                      })) || 'Add the playlist details to preview the reference entry.'}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {contentType === 'link' && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Block Title</label>
                    <input
                      type="text"
                      value={formData.blockTitle || ''}
                      onChange={(e) => handleFieldChange('blockTitle', e.target.value)}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                      placeholder="Resources, Tools, References..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Block Description</label>
                    <input
                      type="text"
                      value={formData.blockDescription || ''}
                      onChange={(e) => handleFieldChange('blockDescription', e.target.value)}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                      placeholder="Optional context for this collection"
                    />
                  </div>
                </div>

                <div className="space-y-4">
                  {linkItemsForDisplay.map((item, index) => (
                    <div key={`link-item-${index}`} className="border border-gray-200 rounded-xl p-4 bg-gray-50 space-y-4">
                      <div className="flex items-center justify-between">
                        <h4 className="text-sm font-semibold text-gray-800">Link {index + 1}</h4>
                        <button
                          type="button"
                          onClick={() => removeLinkItem(index)}
                          disabled={!canRemoveLinkItem}
                          className={`text-xs ${canRemoveLinkItem ? 'text-red-600 hover:text-red-700' : 'text-gray-300 cursor-not-allowed'}`}
                        >
                          Remove
                        </button>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-semibold text-gray-600 mb-1">Title</label>
                          <input
                            type="text"
                            value={item.title || ''}
                            onChange={(e) => updateLinkItem(index, { title: e.target.value })}
                            className="w-full p-2 border border-gray-300 rounded-md"
                            placeholder="Name of the resource"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-600 mb-1">URL</label>
                          <input
                            type="text"
                            value={item.url || ''}
                            onChange={(e) => updateLinkItem(index, { url: e.target.value })}
                            className="w-full p-2 border border-gray-300 rounded-md"
                            placeholder="https://example.com"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Description</label>
                        <textarea
                          value={item.description || ''}
                          onChange={(e) => updateLinkItem(index, { description: e.target.value })}
                          rows={2}
                          className="w-full p-2 border border-gray-300 rounded-md"
                          placeholder="Why is this link useful?"
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-semibold text-gray-600 mb-1">Category</label>
                          <select
                            value={item.category || 'website'}
                            onChange={(e) => updateLinkItem(index, { category: e.target.value })}
                            className="w-full p-2 border border-gray-300 rounded-md"
                          >
                            <option value="website">Website</option>
                            <option value="software">Software</option>
                            <option value="youtube">YouTube</option>
                            <option value="documentation">Documentation</option>
                            <option value="article">Article</option>
                            <option value="other">Other</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-600 mb-1">Software / Platform</label>
                          <input
                            type="text"
                            value={item.platform || ''}
                            onChange={(e) => updateLinkItem(index, { platform: e.target.value })}
                            className="w-full p-2 border border-gray-300 rounded-md"
                            placeholder="Figma, Miro, YouTube..."
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-semibold text-gray-600 mb-1">Page Info</label>
                          <input
                            type="text"
                            value={item.pageInfo || ''}
                            onChange={(e) => updateLinkItem(index, { pageInfo: e.target.value })}
                            className="w-full p-2 border border-gray-300 rounded-md"
                            placeholder="Chapter, module, playlist..."
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-600 mb-1">Tags</label>
                          <input
                            type="text"
                            value={item.tags || ''}
                            onChange={(e) => updateLinkItem(index, { tags: e.target.value })}
                            className="w-full p-2 border border-gray-300 rounded-md"
                            placeholder="design, tutorial, API"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Notes</label>
                        <textarea
                          value={item.notes || ''}
                          onChange={(e) => updateLinkItem(index, { notes: e.target.value })}
                          rows={2}
                          className="w-full p-2 border border-gray-300 rounded-md"
                          placeholder="Anything else learners should know"
                        />
                      </div>
                    </div>
                  ))}

                  <button
                    type="button"
                    onClick={addLinkItem}
                    className="w-full border border-dashed border-slate-300 rounded-lg py-2 text-sm font-semibold text-slate-600 hover:border-slate-400 hover:text-slate-700"
                  >
                    + Add another link
                  </button>
                </div>
              </div>
            )}

            {(contentType === 'image' || contentType === 'gallery') && (
              <div className="space-y-4">
                <fieldset className="space-y-2">
                  <div className="flex items-center gap-2">
                    <label className="block text-sm font-semibold text-gray-800">Image Source</label>
                    <span
                      className="text-xs text-gray-500"
                      title="Choose how you want to point to your images. Uploads embed files; server paths keep files hosted elsewhere."
                    >
                      What should I pick?
                    </span>
                  </div>
                  <p className="text-xs text-gray-600">Select the option that matches where your images live.</p>
                  <div className={`grid grid-cols-1 gap-3 ${contentType === 'image' ? 'md:grid-cols-3' : 'md:grid-cols-2'}`}>
                    <label
                      className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        imageSource === 'upload'
                          ? 'border-slate-500 bg-slate-50 shadow-sm'
                          : 'border-gray-200 hover:border-slate-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name="imageSource"
                        value="upload"
                        checked={imageSource === 'upload'}
                        onChange={(e) => setImageSource(e.target.value)}
                        className="mt-1"
                      />
                      <div>
                        <div className="text-sm font-semibold text-gray-800">Upload file(s)</div>
                        <p className="text-xs text-gray-600">Drag-and-drop or browse for images on your device.</p>
                      </div>
                    </label>

                    <label
                      className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        imageSource === 'server'
                          ? 'border-slate-500 bg-slate-50 shadow-sm'
                          : 'border-gray-200 hover:border-slate-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name="imageSource"
                        value="server"
                        checked={imageSource === 'server'}
                        onChange={(e) => setImageSource(e.target.value)}
                        className="mt-1"
                      />
                      <div>
                        <div className="text-sm font-semibold text-gray-800">Use server path</div>
                        <p className="text-xs text-gray-600">
                          Reference images that already live on a server or LMS folder. This works with the new path-based HTML export.
                        </p>
                      </div>
                    </label>

                    {contentType === 'image' && (
                      <label
                        className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                          imageSource === 'embed'
                            ? 'border-slate-500 bg-slate-50 shadow-sm'
                            : 'border-gray-200 hover:border-slate-300'
                        }`}
                      >
                        <input
                          type="radio"
                          name="imageSource"
                          value="embed"
                          checked={imageSource === 'embed'}
                          onChange={(e) => setImageSource(e.target.value)}
                          className="mt-1"
                        />
                        <div>
                          <div className="text-sm font-semibold text-gray-800">Embed a link</div>
                          <p className="text-xs text-gray-600">
                            Paste a link to a picture file (Cloudflare R2, an LMS folder, any CDN), or a
                            Pinterest, Flickr, Instagram, Imgur or GIPHY link — or its iframe code — to embed
                            the image from its source.
                          </p>
                        </div>
                      </label>
                    )}
                  </div>
                </fieldset>

                {imageSource === 'embed' ? (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-1">
                        Embed link or iframe code <span className="text-red-500">*</span>
                      </label>
                      <p className="text-xs text-gray-500 mb-2">
                        Paste a link straight to the picture file (e.g. <code>https://…/photo.png</code>), the
                        page URL (e.g. <code>https://www.pinterest.com/pin/123…/</code>), or the full
                        <code> &lt;iframe&gt;</code> snippet the site gives you. The image stays hosted where it
                        is, so the link — not a copy of the file — is what gets saved.
                      </p>
                      <textarea
                        value={formData.imageEmbedInput || ''}
                        onChange={(e) => setFormData({ ...formData, imageEmbedInput: e.target.value })}
                        rows={3}
                        placeholder={'<iframe src="https://assets.pinterest.com/ext/embed.html?id=455919162258173338" height="359" width="345" frameborder="0" scrolling="no"></iframe>'}
                        className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-transparent font-mono text-xs transition-colors ${
                          (formData.imageEmbedInput || '').trim() && !parsedImageEmbed
                            ? 'border-red-300 bg-red-50'
                            : 'border-gray-300'
                        }`}
                      />
                      {(formData.imageEmbedInput || '').trim() && !parsedImageEmbed && (
                        <p className="text-red-500 text-sm mt-1">
                          ⚠️ That doesn't look like an https link or iframe code. Copy the embed code from the source site and paste it again.
                        </p>
                      )}
                      {parsedImageEmbed && (
                        <p className="text-green-600 text-sm mt-1">
                          {isDirectImageLink
                            ? `✅ Picture link detected (${parsedImageEmbed.provider}) — it will scale to fit the slide. Use Size below to choose how big.`
                            : `✅ ${parsedImageEmbed.provider} embed detected`}
                        </p>
                      )}
                    </div>

                    {parsedImageEmbed && !isDirectImageLink && (
                      <>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Frame width (px):</label>
                            <input
                              type="number"
                              min="80"
                              value={formData.imageEmbedWidth ?? parsedImageEmbed.width}
                              onChange={(e) => setFormData({ ...formData, imageEmbedWidth: e.target.value })}
                              className="w-full p-2 border border-gray-300 rounded-md text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Frame height (px):</label>
                            <input
                              type="number"
                              min="80"
                              value={formData.imageEmbedHeight ?? parsedImageEmbed.height}
                              onChange={(e) => setFormData({ ...formData, imageEmbedHeight: e.target.value })}
                              className="w-full p-2 border border-gray-300 rounded-md text-sm"
                            />
                          </div>
                          <div className="flex items-end">
                            <button
                              type="button"
                              onClick={fetchImageEmbedInfo}
                              disabled={isLoadingEmbedInfo}
                              className="w-full px-3 py-2 text-sm font-semibold rounded-lg bg-slate-700 text-white hover:bg-slate-800 disabled:opacity-60"
                            >
                              {isLoadingEmbedInfo ? 'Fetching…' : 'Fetch details & preview'}
                            </button>
                          </div>
                        </div>
                        <p className="text-xs text-gray-500">
                          Fetching asks the source site for the title, creator and date. Not every site publishes
                          them — whatever comes back is editable below, and anything missing can be typed in.
                        </p>

                        {/* PowerPoint and PDF cannot host a live frame, and the
                            browser will not let the app screenshot a
                            cross-origin one, so the exports need a still
                            picture: fetched from the source site, or supplied
                            here by hand. */}
                        <div className={`rounded-lg border p-3 ${
                          formData.imageEmbedThumbnail
                            ? 'border-emerald-200 bg-emerald-50'
                            : 'border-amber-200 bg-amber-50'
                        }`}>
                          <div className={`text-xs font-semibold mb-1 ${
                            formData.imageEmbedThumbnail ? 'text-emerald-800' : 'text-amber-800'
                          }`}>
                            {formData.imageEmbedThumbnail
                              ? '✅ Export preview image ready'
                              : 'ℹ️ No export preview image yet'}
                          </div>

                          {formData.imageEmbedThumbnail ? (
                            <div className="flex items-center gap-3">
                              <img
                                src={formData.imageEmbedThumbnail}
                                alt="Export preview"
                                className="h-20 w-auto max-w-[160px] rounded-md border border-emerald-200 bg-white object-contain"
                              />
                              <div className="min-w-0 flex-1 text-xs text-emerald-800">
                                <p>PowerPoint and PDF will show this picture with the caption, the APA note and a link to the source.</p>
                                <div className="mt-2 flex flex-wrap gap-3">
                                  <label className="cursor-pointer font-semibold text-slate-700 hover:text-slate-900">
                                    Replace…
                                    <input
                                      type="file"
                                      accept="image/*"
                                      className="hidden"
                                      onChange={(e) => { setEmbedThumbnailFromFile(e.target.files?.[0]); e.target.value = ''; }}
                                    />
                                  </label>
                                  <button
                                    type="button"
                                    onClick={() => setFormData({ ...formData, imageEmbedThumbnail: '' })}
                                    className="font-semibold text-red-600 hover:text-red-700"
                                  >
                                    Remove
                                  </button>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <>
                              <p className="text-xs text-amber-800 mb-2">
                                Without one, PowerPoint and PDF show the link on its own. Press
                                {' '}<strong>Fetch details &amp; preview</strong> to pull it from the source site, or add a
                                picture yourself — take a screenshot of the embed, then drop it here, browse for it,
                                or just paste it with Ctrl/⌘+V.
                              </p>
                              <div
                                onDragOver={(e) => { e.preventDefault(); setIsEmbedThumbDragOver(true); }}
                                onDragLeave={() => setIsEmbedThumbDragOver(false)}
                                onDrop={(e) => {
                                  e.preventDefault();
                                  setIsEmbedThumbDragOver(false);
                                  setEmbedThumbnailFromFile(e.dataTransfer?.files?.[0]);
                                }}
                                onClick={() => document.getElementById('embed-thumb-input')?.click()}
                                className={`cursor-pointer rounded-lg border-2 border-dashed px-3 py-4 text-center text-xs transition-colors ${
                                  isEmbedThumbDragOver
                                    ? 'border-amber-500 bg-amber-100 text-amber-900'
                                    : 'border-amber-300 text-amber-700 hover:border-amber-400 hover:bg-amber-100/60'
                                }`}
                              >
                                <input
                                  id="embed-thumb-input"
                                  type="file"
                                  accept="image/*"
                                  className="hidden"
                                  onChange={(e) => { setEmbedThumbnailFromFile(e.target.files?.[0]); e.target.value = ''; }}
                                />
                                Drop an image, click to browse, or paste with Ctrl/⌘+V
                              </div>
                            </>
                          )}
                        </div>
                      </>
                    )}

                    {parsedImageEmbed && (
                      <div>
                        <div className="text-xs font-semibold text-gray-600 mb-1">Preview</div>
                        {isDirectImageLink ? (
                          <img
                            src={parsedImageEmbed.embedSrc}
                            alt="Linked picture preview"
                            className="mx-auto max-h-64 w-auto max-w-full rounded-xl border border-gray-200 bg-gray-50 object-contain"
                          />
                        ) : (
                          <div
                            className="relative mx-auto overflow-hidden rounded-xl border border-gray-200 bg-gray-50"
                            style={{
                              width: `min(100%, ${parseInt(formData.imageEmbedWidth, 10) || parsedImageEmbed.width}px)`,
                              aspectRatio: embedFrameStyle({
                                imageEmbedWidth: formData.imageEmbedWidth ?? parsedImageEmbed.width,
                                imageEmbedHeight: formData.imageEmbedHeight ?? parsedImageEmbed.height
                              }).aspectRatio
                            }}
                          >
                            <iframe
                              src={parsedImageEmbed.embedSrc}
                              title="Embedded image preview"
                              scrolling="no"
                              className="absolute inset-0 w-full h-full border-0"
                              referrerPolicy="strict-origin-when-cross-origin"
                            />
                          </div>
                        )}
                        {isDirectImageLink && (
                          <p className="text-xs text-gray-500 mt-2 text-center">
                            The picture stays where it is hosted — the slide, the HTML export and the
                            PowerPoint/PDF exports all load it from this link.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                ) : imageSource === 'upload' ? (
                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-1">
                      Upload image(s)
                    </label>
                    <p className="text-xs text-gray-500 mb-2">
                      Drop several images at once to auto-create a gallery, or copy an image anywhere on your
                      computer and press Ctrl/⌘+V. Smaller files export faster.
                    </p>
                    <div
                      onDragOver={handleImageDragOver}
                      onDragLeave={handleImageDragLeave}
                      onDrop={handleImageDrop}
                      onClick={() => document.getElementById('image-upload-input')?.click()}
                      className={`w-full rounded-xl border-2 border-dashed text-center cursor-pointer transition-colors ${
                        isImageDragOver
                          ? 'border-slate-500 bg-slate-50'
                          : 'border-gray-300 hover:border-slate-400 hover:bg-gray-50'
                      }`}
                    >
                      <input
                        id="image-upload-input"
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={(e) => setFormData({ ...formData, imageFiles: e.target.files })}
                        className="hidden"
                      />
                      <div className="flex flex-col items-center gap-2 py-7 px-4">
                        <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center">
                          <Upload className="w-5 h-5 text-slate-500" />
                        </div>
                        <p className="text-sm text-gray-700 font-medium">
                          Drag &amp; drop images, <span className="text-slate-700 underline">browse files</span>, or paste with Ctrl/⌘+V
                        </p>
                        <p className="text-xs text-gray-400">PNG, JPG, GIF or SVG</p>
                      </div>
                    </div>
                    {formData.imageFiles && formData.imageFiles.length > 0 ? (
                      <div className="mt-3">
                        <div className="flex items-center gap-1.5 text-sm text-green-700 font-medium mb-2">
                          <CheckCircle className="w-4 h-4" />
                          {formData.imageFiles.length} file{formData.imageFiles.length > 1 ? 's' : ''} selected
                          {formData.imageFiles.length > 1 && <span className="text-gray-500 font-normal">· will create a gallery</span>}
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {Array.from(formData.imageFiles).map((file, index) => (
                            <span
                              key={`${file.name}-${index}`}
                              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gray-100 text-xs text-gray-700 max-w-[220px]"
                              title={file.name}
                            >
                              <Image className="w-3 h-3 text-gray-400 flex-shrink-0" />
                              <span className="truncate">{file.name}</span>
                            </span>
                          ))}
                        </div>
                      </div>
                    ) : (
                      !formData.isEditing && (
                        <p className="text-amber-600 text-xs mt-2 flex items-center gap-1">
                          <AlertTriangle className="w-3.5 h-3.5" /> Add at least one image to save this block.
                        </p>
                      )
                    )}
                    {canRemoveExistingImages && contentType === 'image' && formData.src && (
                      <button
                        type="button"
                        onClick={clearSingleImage}
                        className="mt-3 text-xs font-semibold text-red-600 hover:text-red-700"
                      >
                        Remove current image
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Server path (without filename)
                        <span className="ml-2 text-xs text-gray-500" title="This base path is reused whenever you export using image paths.">learn more</span>
                      </label>
                      <input
                        type="text"
                        value={formData.imagePath || ''}
                        onChange={(e) => setFormData({ ...formData, imagePath: e.target.value })}
                        placeholder="e.g., /content/enforced/123456-Course/images/"
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                      />
                      <p className="text-xs text-gray-600 mt-1">Use an absolute or relative folder path that stays the same for every image in this block.</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Image filename(s) <span className="text-red-500">*</span>
                        <span className="text-gray-500 font-normal">(one per line for multiple)</span>
                      </label>
                      <textarea
                        value={formData.imageFilenames || ''}
                        onChange={(e) => setFormData({ ...formData, imageFilenames: e.target.value })}
                        placeholder={`diagram.jpg\nchart.png\nphoto.gif`}
                        rows={4}
                        className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-transparent font-mono text-sm transition-colors ${!formData.imageFilenames || !formData.imageFilenames.trim()
                          ? 'border-red-300 bg-red-50'
                          : 'border-gray-300'
                          }`}
                      />
                      <p className="text-xs text-gray-600 mt-1">Filenames pair with the server path above and export using that path instead of embedded images.</p>
                      {(!formData.imageFilenames || !formData.imageFilenames.trim()) && !formData.isEditing && (
                        <p className="text-red-500 text-sm mt-1">
                          ⚠️ Please enter at least one image filename
                        </p>
                      )}
                      {formData.imageFilenames && formData.imageFilenames.trim() && (
                        <p className="text-green-600 text-sm mt-1">
                          ✅ {formData.imageFilenames.split('\n').filter(Boolean).length} filename(s) entered
                          {formData.imageFilenames.split('\n').filter(Boolean).length > 1 && ' (will create gallery)'}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {isMultipleImages && (
                  <div className="my-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Gallery Columns:</label>
                    <select
                      value={formData.galleryColumns || '2'}
                      onChange={(e) => setFormData({ ...formData, galleryColumns: e.target.value })}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                    >
                      <option value="2">2 Columns</option>
                      <option value="3">3 Columns</option>
                      <option value="4">4 Columns</option>
                    </select>
                  </div>
                )}

                <div className="space-y-4 mt-4">
                  {sourceArray.map((sourceItem, index) => (
                    <ImageMetadataForm
                      key={`${sourceItem.name || sourceItem}-${index}`}
                      index={index}
                      fileName={sourceItem.name || sourceItem}
                      isMultiple={isMultipleImages}
                      formData={formData}
                      onFieldChange={handleFieldChange}
                      onRemove={
                        canRemoveExistingImages
                          ? (contentType === 'gallery'
                            ? () => removeGalleryImage(index)
                            : contentType === 'image'
                              ? clearSingleImage
                              : undefined)
                          : undefined
                      }
                      savedCitations={sortedSavedApaCitations}
                      onApplySavedCitation={applySavedCitationToImage}
                      onSaveCitationToLibrary={saveCitationToLibrary}
                      onFetchCitationFromUrl={fetchCitationFromSourceUrl}
                      isFetchingCitationUrl={
                        citationUrlFetchField === (isMultipleImages ? `image_${index}` : 'image')
                      }
                      totalImages={sourceArray.length}
                      onApplyCitationFields={applyImageCitationFields}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Citations for pictures pasted straight onto a canvas slide.
                They are already on the slide, so this dialog only fills in the
                optional half — one tab per picture, and closing it changes
                nothing. */}
            {contentType === 'pasted-images' && (
              <div className="space-y-4">
                <div className="rounded-lg bg-blue-50 border border-blue-100 p-3 text-sm text-blue-900">
                  {pastedImageList.length === 1
                    ? 'The picture is on your slide. Add a citation if it needs one — or just close this.'
                    : `${pastedImageList.length} pictures are on your slide. Add citations to any that need one — or just close this.`}
                </div>

                {pastedImageList.length > 1 && (
                  <div className="flex flex-wrap gap-2" role="tablist" aria-label="Pasted images">
                    {pastedImageList.map((image, index) => (
                      <button
                        key={image.id}
                        type="button"
                        role="tab"
                        aria-selected={index === activePastedImage}
                        onClick={() => setActivePastedImage(index)}
                        className={`flex items-center gap-2 rounded-lg border px-2 py-1.5 text-xs font-medium transition-colors ${
                          index === activePastedImage
                            ? 'border-slate-700 bg-slate-700 text-white'
                            : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        <img
                          src={image.src}
                          alt=""
                          className="h-7 w-7 rounded object-cover bg-white"
                        />
                        Image {index + 1}
                      </button>
                    ))}
                  </div>
                )}

                {pastedImageList.map((image, index) => (
                  index === activePastedImage ? (
                    <div key={image.id} className="space-y-3">
                      <img
                        src={image.src}
                        alt={image.name}
                        className="max-h-40 rounded-lg border border-gray-200 bg-gray-50 object-contain"
                      />
                      <ImageMetadataForm
                        index={index}
                        fileName={image.name}
                        // Always the indexed field names, so one picture and
                        // five behave identically and the save path has a
                        // single shape to read.
                        isMultiple
                        formData={formData}
                        onFieldChange={handleFieldChange}
                        savedCitations={sortedSavedApaCitations}
                        onApplySavedCitation={applySavedCitationToImage}
                        onSaveCitationToLibrary={saveCitationToLibrary}
                        onFetchCitationFromUrl={fetchCitationFromSourceUrl}
                        isFetchingCitationUrl={citationUrlFetchField === `image_${index}`}
                        totalImages={pastedImageList.length}
                        onApplyCitationFields={applyImageCitationFields}
                      />
                    </div>
                  ) : null
                ))}
              </div>
            )}

            {contentType === 'audio' && (
              <div className="space-y-4">
                <div className="space-y-3 rounded-lg border border-gray-200 p-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Audio file:</label>
                    <input
                      type="file"
                      accept="audio/*"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        // An upload and a link are two answers to one question,
                        // so choosing either clears the other rather than
                        // leaving the save path to guess which one was meant.
                        setFormData((prev) => ({
                          ...prev,
                          audioFile: file || null,
                          audioSrc: file ? '' : prev.audioSrc,
                          audioLinkName: file ? '' : prev.audioLinkName,
                          audioHasUploadedFile: file ? false : prev.audioHasUploadedFile
                        }));
                      }}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      An uploaded clip is stored inside the deck, which grows the file and every export of it. A link does not.
                    </p>
                  </div>

                  <AudioLinkInput
                    onUseLink={(url, result) => setFormData((prev) => ({
                      ...prev,
                      audioSrc: url,
                      audioFile: null,
                      audioHasUploadedFile: false,
                      audioLinkName: result?.name || '',
                      // The clip's own file name is a better title than an
                      // empty box; anything already typed is left alone.
                      audioTitle: prev.audioTitle || result?.name || ''
                    }))}
                  />

                  {(formData.audioSrc || formData.audioFile || formData.audioHasUploadedFile) && (
                    <p className="break-all text-xs font-semibold text-emerald-700" role="status">
                      {formData.audioFile
                        ? `Using the uploaded file: ${formData.audioFile.name}`
                        : formData.audioSrc
                          ? `Using the linked clip: ${formData.audioSrc}`
                          : 'Using the clip already uploaded into this block. Pick a file or paste a link to replace it.'}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Player:</label>
                  <div className="grid gap-2 sm:grid-cols-2">
                    {AUDIO_PLAYER_CHOICES.map((choice) => {
                      const selected = normalizeAudioPlayerStyle(formData.playerStyle) === choice.value;
                      return (
                        <button
                          key={choice.value}
                          type="button"
                          onClick={() => handleFieldChange('playerStyle', choice.value)}
                          aria-pressed={selected}
                          className={`rounded-lg border p-3 text-left transition ${selected
                            ? 'border-slate-600 bg-slate-50 ring-2 ring-slate-500'
                            : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'}`}
                        >
                          <span className="block text-sm font-semibold text-gray-800">{choice.label}</span>
                          <span className="mt-0.5 block text-xs text-gray-600">{choice.description}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description:</label>
                  <textarea
                    value={formData.audioDescription || ''}
                    onChange={(e) => setFormData({ ...formData, audioDescription: e.target.value })}
                    rows={3}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Audio Title:</label>
                    <input
                      type="text"
                      value={formData.audioTitle || ''}
                      onChange={(e) => setFormData({ ...formData, audioTitle: e.target.value })}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Creator:</label>
                    <input
                      type="text"
                      value={formData.audioCreator || ''}
                      onChange={(e) => setFormData({ ...formData, audioCreator: e.target.value })}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Source:</label>
                    <input
                      type="text"
                      value={formData.audioSourceInfo || ''}
                      onChange={(e) => setFormData({ ...formData, audioSourceInfo: e.target.value })}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Date:</label>
                    <input
                      type="date"
                      value={formData.audioDateInfo || ''}
                      onChange={(e) => setFormData({ ...formData, audioDateInfo: e.target.value })}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                    />
                  </div>
            </div>
          </div>
        )}

        {contentType === 'accordion' && (
          <div className="space-y-4">
            <div className="flex gap-4">
              <div className="w-1/3">
                <button
                  type="button"
                  onClick={handleAddAccordion}
                  className="w-full mb-2 flex items-center justify-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-lg bg-gray-50 hover:bg-gray-100"
                >
                  <Plus size={16} /> Add Item
                </button>
                <ul className="space-y-1">
                  {(formData.accordionItems || []).map((item, index) => (
                    <li
                      key={item.id}
                      className={`flex items-center justify-between p-2 border rounded cursor-pointer ${formData.selectedAccordionId === item.id ? 'bg-gray-200' : 'bg-white'}`}
                      onClick={() => setFormData({ ...formData, selectedAccordionId: item.id })}
                    >
                      <span className="truncate">{item.title || `Item ${index + 1}`}</span>
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); moveAccordion(index, index - 1); }}
                          disabled={index === 0}
                          className="text-gray-500 disabled:opacity-25"
                        >
                          <ChevronUp size={14} />
                        </button>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); moveAccordion(index, index + 1); }}
                          disabled={index === (formData.accordionItems || []).length - 1}
                          className="text-gray-500 disabled:opacity-25"
                        >
                          <ChevronDown size={14} />
                        </button>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); handleRemoveAccordion(item.id); }}
                          className="text-red-500"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="w-2/3 space-y-3">
                {(() => {
                  const current = (formData.accordionItems || []).find(it => it.id === formData.selectedAccordionId);
                  if (!current) return <p className="text-sm text-gray-500">Select an item to edit</p>;
                  return (
                    <>
                      <input
                        type="text"
                        maxLength={100}
                        value={current.title}
                        onChange={(e) => handleUpdateAccordion(current.id, { title: e.target.value })}
                        className="w-full p-3 border border-gray-300 rounded focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                        placeholder="Item title"
                      />
                      <RichTextEditor
                        content={current.content}
                        onChange={(content) => handleUpdateAccordion(current.id, { content })}
                        isHtmlMode={false}
                        onToggleHtmlMode={() => {}}
                        isPreviewMode={false}
                      />
                    </>
                  );
                })()}
            </div>
          </div>
          <div className="flex gap-4 pt-4">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={formData.accordionSettings?.allowMultipleOpen || false}
                onChange={(e) =>
                  setFormData(prev => ({
                    ...prev,
                    accordionSettings: {
                      ...prev.accordionSettings,
                      allowMultipleOpen: e.target.checked
                    }
                  }))
                }
              />
              Allow multiple open
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={formData.accordionSettings?.defaultOpenFirst || false}
                onChange={(e) =>
                  setFormData(prev => ({
                    ...prev,
                    accordionSettings: {
                      ...prev.accordionSettings,
                      defaultOpenFirst: e.target.checked
                    }
                  }))
                }
              />
              Open first item
            </label>
          </div>
        </div>
      )}

      {contentType === 'cards' && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Layout:</label>
                    <select
                      value={formData.cardLayout || '2x1'}
                      onChange={(e) => setFormData({ ...formData, cardLayout: e.target.value })}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                    >
                      <option value="2x1">2 Columns</option>
                      <option value="3x1">3 Columns</option>
                      <option value="1x3">Single Column</option>
                      <option value="2x2">2x2 Grid</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Style:</label>
                    <select
                      value={formData.cardStyle || 'info'}
                      onChange={(e) => setFormData({ ...formData, cardStyle: e.target.value })}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                    >
                      <option value="info">Info (Blue)</option>
                      <option value="exercise">Exercise (Green)</option>
                      <option value="warning">Warning (Yellow)</option>
                      <option value="success">Success (Green)</option>
                    </select>
                  </div>
                </div>

                <div>
                  <div className="flex justify-between items-center mb-4">
                    <label className="block text-sm font-medium text-gray-700">Cards (Max 4):</label>
                    <span className="text-sm text-gray-500">
                      {(formData.cardItems || []).length}/4 cards
                    </span>
                  </div>

                  <div className="space-y-3">
                    {(() => {
                      const cards = formData.cardItems || [{ title: '', content: '' }];
                      return cards.map((card, index) => (
                        <div key={index} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                          <div className="flex justify-between items-center mb-3">
                            <h4 className="font-medium text-gray-700">Card {index + 1}</h4>
                            {cards.length > 1 && (
                              <button
                                type="button"
                                onClick={() => {
                                  const newItems = [...cards];
                                  newItems.splice(index, 1);
                                  setFormData({ ...formData, cardItems: newItems });
                                }}
                                className="px-3 py-1 bg-red-500 text-white text-sm rounded hover:bg-red-600 transition-colors"
                              >
                                Remove
                              </button>
                            )}
                          </div>

                          <div className="space-y-3">
                            <div className="space-y-3">
                              <div className="flex items-center gap-2 mb-2">
                                <input
                                  type="checkbox"
                                  id={`use-custom-colors-${index}`}
                                  checked={card?.useCustomColors || false}
                                  onChange={(e) => {
                                    const newItems = [...cards];
                                    newItems[index] = {
                                      ...newItems[index],
                                      useCustomColors: e.target.checked,
                                      backgroundColor: e.target.checked ? (newItems[index].backgroundColor || '#f8fafc') : undefined,
                                      borderColor: e.target.checked ? (newItems[index].borderColor || '#94a3b8') : undefined
                                    };
                                    setFormData({ ...formData, cardItems: newItems });
                                  }}
                                  className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                                />
                                <label htmlFor={`use-custom-colors-${index}`} className="text-sm font-medium text-gray-700">
                                  Use Custom Colors
                                </label>
                              </div>

                              {card?.useCustomColors && (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                  <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">Background Color</label>
                                    <input
                                      type="color"
                                      value={card?.backgroundColor || '#f8fafc'}
                                      onChange={(e) => {
                                        const newItems = [...cards];
                                        newItems[index] = { ...newItems[index], backgroundColor: e.target.value };
                                        setFormData({ ...formData, cardItems: newItems });
                                      }}
                                      className="h-10 w-full border border-gray-300 rounded"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">Border Color</label>
                                    <input
                                      type="color"
                                      value={card?.borderColor || '#94a3b8'}
                                      onChange={(e) => {
                                        const newItems = [...cards];
                                        newItems[index] = { ...newItems[index], borderColor: e.target.value };
                                        setFormData({ ...formData, cardItems: newItems });
                                      }}
                                      className="h-10 w-full border border-gray-300 rounded"
                                    />
                                  </div>
                                </div>
                              )}

                              <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
                                <div className="flex items-center justify-between mb-2">
                                  <label className="text-xs font-medium text-gray-700">Card Width</label>
                                  <span className="text-xs font-semibold text-purple-600">
                                    {card?.widthPercent || 100}%
                                  </span>
                                </div>
                                <input
                                  type="range"
                                  min="20"
                                  max="300"
                                  step="5"
                                  value={card?.widthPercent || 100}
                                  onChange={(e) => {
                                    const newItems = [...cards];
                                    newItems[index] = { ...newItems[index], widthPercent: parseInt(e.target.value) };
                                    setFormData({ ...formData, cardItems: newItems });
                                  }}
                                  className="w-full h-2 bg-purple-200 rounded-lg appearance-none cursor-pointer accent-purple-600"
                                />
                                <div className="flex justify-between text-xs text-gray-500 mt-1">
                                  <span>20% (Smallest)</span>
                                  <span>100% (Default)</span>
                                  <span>300% (Largest)</span>
                                </div>
                              </div>
                            </div>

                            <input
                              type="text"
                              placeholder="Card Title"
                              value={card?.title || ''}
                              onChange={(e) => {
                                const newItems = [...cards];
                                newItems[index] = { ...newItems[index], title: e.target.value };
                                setFormData({ ...formData, cardItems: newItems });
                              }}
                              className="w-full p-3 border border-gray-300 rounded focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                            />

                            <div className="border border-gray-200 rounded-lg overflow-hidden">
                              <div className="bg-gray-50 px-3 py-2 border-b border-gray-200">
                                <span className="text-sm font-medium text-gray-700">Card Content</span>
                              </div>
                              <RichTextEditor
                                content={card?.content || ''}
                                onChange={(content) => {
                                  const newItems = [...cards];
                                  newItems[index] = { ...newItems[index], content: content };
                                  setFormData({ ...formData, cardItems: newItems });
                                }}
                                isHtmlMode={false}
                                onToggleHtmlMode={() => { }}
                                isPreviewMode={false}
                              />
                            </div>
                          </div>
                        </div>
                      ));
                    })()}

                    {(formData.cardItems || []).length < 4 && (
                      <button
                        type="button"
                        onClick={() => {
                          const currentItems = formData.cardItems || [];
                          const newItems = [...currentItems, { title: '', content: '' }];
                          setFormData({ ...formData, cardItems: newItems });
                        }}
                        className="w-full p-4 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-gray-400 hover:text-gray-700 hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
                      >
                        <Plus size={16} />
                        Add Card ({(formData.cardItems || []).length + 1}/4)
                      </button>
                    )}

                    {(formData.cardItems || []).length >= 4 && (
                      <div className="text-center p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <p className="text-sm text-blue-700">
                          ✅ Maximum of 4 cards reached
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <h4 className="font-medium text-blue-800 mb-2">💡 Tips for Cards:</h4>
                  <ul className="text-sm text-blue-700 space-y-1">
                    <li>• Use the rich text editor for formatting (bold, italic, lists, etc.)</li>
                    <li>• Add links, headings, and styled text</li>
                    <li>• Customize background and border colors for each card</li>
                    <li>• Adjust card width (small/medium/large) for variable layouts</li>
                    <li>• Keep titles short and descriptive</li>
                    <li>• Cards work great for key points, steps, or highlights</li>
                  </ul>
                </div>
              </div>
            )}

          {contentType === 'stacked-cards' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <label className="block text-sm font-medium text-gray-700">Stacked Cards:</label>
                <span className="text-sm text-gray-500">
                  {(formData.stackedCardItems || []).length} cards
                </span>
              </div>

              <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <input
                  type="checkbox"
                  id="includeSideText"
                  checked={formData.includeSideText !== false}
                  onChange={(e) => {
                    setFormData({ ...formData, includeSideText: e.target.checked });
                  }}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer"
                />
                <label htmlFor="includeSideText" className="text-sm font-medium text-gray-700 cursor-pointer">
                  Include Side Text / Time for cards
                </label>
              </div>

              <div className="space-y-3">
                {(formData.stackedCardItems || normalizeStackedCardItems([])).map((card, index, list) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                    <div className="flex justify-between items-center mb-3">
                      <h4 className="font-medium text-gray-700">Card {index + 1}</h4>
                      {list.length > 1 && (
                        <button
                          type="button"
                          onClick={() => {
                            const newItems = [...list];
                            newItems.splice(index, 1);
                            setFormData({ ...formData, stackedCardItems: newItems });
                          }}
                          className="px-3 py-1 bg-red-500 text-white text-sm rounded hover:bg-red-600 transition-colors"
                        >
                          Remove
                        </button>
                      )}
                    </div>

                    <div className={`grid grid-cols-1 ${formData.includeSideText !== false ? 'md:grid-cols-2' : ''} gap-3`}>
                      {formData.includeSideText !== false && (
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Side Text / Time</label>
                          <input
                            type="text"
                            placeholder="e.g., 30 min"
                            value={card.sideText || ''}
                            onChange={(e) => {
                              const newItems = [...list];
                              newItems[index] = { ...newItems[index], sideText: e.target.value };
                              setFormData({ ...formData, stackedCardItems: newItems });
                            }}
                            className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                          />
                        </div>
                      )}
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Card Color</label>
                        <input
                          type="color"
                          value={card.color || stackedCardPalette[index % stackedCardPalette.length]}
                          onChange={(e) => {
                            const newItems = [...list];
                            newItems[index] = { ...newItems[index], color: e.target.value };
                            setFormData({ ...formData, stackedCardItems: newItems });
                          }}
                          className="h-10 w-full border border-gray-300 rounded"
                        />
                      </div>
                    </div>

                    <div className="space-y-3 mt-3">
                      <input
                        type="text"
                        placeholder="Card Title"
                        value={card?.title || ''}
                        onChange={(e) => {
                          const newItems = [...list];
                          newItems[index] = { ...newItems[index], title: e.target.value };
                          setFormData({ ...formData, stackedCardItems: newItems });
                        }}
                        className="w-full p-3 border border-gray-300 rounded focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                      />

                      <div className="border border-gray-200 rounded-lg overflow-hidden">
                        <div className="bg-gray-50 px-3 py-2 border-b border-gray-200">
                          <span className="text-sm font-medium text-gray-700">Card Content</span>
                        </div>
                        <RichTextEditor
                          content={card?.content || ''}
                          onChange={(content) => {
                            const newItems = [...list];
                            newItems[index] = { ...newItems[index], content };
                            setFormData({ ...formData, stackedCardItems: newItems });
                          }}
                          isHtmlMode={false}
                          onToggleHtmlMode={() => {}}
                          isPreviewMode={false}
                        />
                      </div>
                    </div>
                  </div>
                ))}

                <button
                  type="button"
                  onClick={() => {
                    const currentItems = formData.stackedCardItems || [];
                    const nextIndex = currentItems.length;
                    const newItems = [
                      ...currentItems,
                      {
                        title: '',
                        content: '',
                        sideText: '',
                        color: stackedCardPalette[nextIndex % stackedCardPalette.length]
                      }
                    ];
                    setFormData({ ...formData, stackedCardItems: newItems });
                  }}
                  className="w-full p-4 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-gray-400 hover:text-gray-700 hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
                >
                  <Plus size={16} />
                  Add Stacked Card
                </button>
              </div>

              <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                <h4 className="font-medium text-orange-800 mb-2">💡 Tips for Stacked Cards:</h4>
                <ul className="text-sm text-orange-700 space-y-1">
                  <li>• Use the side text for time, step numbers, or quick labels</li>
                  <li>• Pick a unique color per card to match your timeline</li>
                  <li>• Rich text formatting is available in the card body</li>
                </ul>
              </div>
            </div>
          )}

          {contentType === 'multi-column-cards' && (
            <div className="space-y-5">
              {/* Clipboard Indicator - Sticky at top */}
              {multiColumnClipboard && (
                <div className="sticky top-0 z-10 p-4 bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-400 rounded-xl shadow-lg flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 mt-0.5 bg-green-500 rounded-full p-1.5">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="text-sm font-bold text-green-900">Card Ready to Paste</h4>
                      <p className="text-sm text-green-800 mt-1 font-medium">
                        {(() => {
                          const title = multiColumnClipboard.title || multiColumnClipboard.primaryTitle || multiColumnClipboard.content || 'Untitled card';
                          const stripped = stripHtml(title);
                          return stripped.length > 60 ? stripped.substring(0, 60) + '...' : stripped;
                        })()}
                      </p>
                      <p className="text-xs text-green-700 mt-2 flex items-center gap-1">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Use "Paste Card Below" button on any card
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setMultiColumnClipboard(null);
                      writeMultiColumnCardClipboard(null);
                      showCopyPasteToast('Clipboard cleared', 'success');
                    }}
                    className="flex-shrink-0 px-4 py-2 bg-white text-green-700 hover:text-green-900 border-2 border-green-400 hover:border-green-500 rounded-lg text-sm font-semibold transition-all hover:shadow-md"
                    title="Clear clipboard"
                  >
                    Clear
                  </button>
                </div>
              )}

              {/* Block Settings - Collapsible */}
              <div className="bg-white border-2 border-slate-200 rounded-xl shadow-sm overflow-hidden">
                <button
                  type="button"
                  onClick={() => toggleSection('blockSettings')}
                  className="w-full px-5 py-4 flex items-center justify-between bg-gradient-to-r from-slate-50 to-gray-50 hover:from-slate-100 hover:to-gray-100 transition-all"
                >
                  <div className="flex items-center gap-3">
                    <div className="bg-slate-600 text-white rounded-lg p-2">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                    <div className="text-left">
                      <h3 className="text-lg font-bold text-gray-900">Block Settings</h3>
                      <p className="text-xs text-gray-600">Title, height matching, and import options</p>
                    </div>
                  </div>
                  <svg
                    className={`w-6 h-6 text-gray-600 transition-transform duration-200 ${expandedSections.blockSettings ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {expandedSections.blockSettings && (
                  <div className="p-5 space-y-4 border-t-2 border-slate-100">
                    {/* Block Title */}
                    <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg space-y-3">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <h4 className="text-sm font-bold text-blue-900">Block Title</h4>
                          <p className="text-xs text-blue-700 mt-1">Add an optional heading above your card columns</p>
                        </div>
                        <label className="inline-flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            className="h-5 w-5 text-blue-600 rounded border-gray-300 focus:ring-2 focus:ring-blue-500 cursor-pointer"
                            checked={formData.multiColumnData?.blockTitleEnabled || false}
                            onChange={(e) => {
                              setFormData({
                                ...formData,
                                multiColumnData: {
                                  ...formData.multiColumnData,
                                  blockTitleEnabled: e.target.checked
                                }
                              });
                            }}
                          />
                          <span className="text-sm font-semibold text-blue-900">Show Title</span>
                        </label>
                      </div>
                      <input
                        type="text"
                        value={formData.multiColumnData?.blockTitle || ''}
                        onChange={(e) => setFormData({
                          ...formData,
                          multiColumnData: {
                            ...formData.multiColumnData,
                            blockTitle: e.target.value
                          }
                        })}
                        placeholder="e.g., Let's start with a quick check-in"
                        className="w-full rounded-lg border-2 border-blue-200 px-4 py-2.5 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-200 disabled:bg-blue-100 disabled:text-gray-500"
                        disabled={!formData.multiColumnData?.blockTitleEnabled}
                      />
                    </div>

                    {/* JSON Import */}
                    <div className="border-2 border-dashed border-purple-300 rounded-lg bg-gradient-to-r from-purple-50 to-pink-50 overflow-hidden">
                      <div className="flex items-center justify-between px-4 py-3 bg-white/50">
                        <div className="flex items-center gap-2">
                          <div className="bg-purple-500 rounded-lg p-1.5">
                            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                            </svg>
                          </div>
                          <div>
                            <h4 className="text-sm font-bold text-purple-900">Import from JSON</h4>
                            <p className="text-xs text-purple-700">Load columns and cards from a JSON snippet</p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setShowMultiColumnPaste((prev) => !prev);
                            setMultiColumnJsonError('');
                          }}
                          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-bold text-purple-700 bg-white border-2 border-purple-300 rounded-lg hover:bg-purple-100 transition-all shadow-sm hover:shadow"
                        >
                          {showMultiColumnPaste ? (
                            <>
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                              Close
                            </>
                          ) : (
                            <>
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                              Open
                            </>
                          )}
                        </button>
                      </div>

                      {showMultiColumnPaste && (
                        <div className="px-4 pb-4 space-y-3 border-t border-purple-200 pt-4">
                          <textarea
                            className="w-full h-40 p-3 border-2 border-purple-200 rounded-lg text-xs font-mono bg-white shadow-inner focus:border-purple-400 focus:ring-2 focus:ring-purple-200"
                            placeholder='{"blockTitle":"Quick check-in","blockTitleEnabled":true,"autoConfigureEnabled":true,"matchHeights":false,"columns":[{"widthPercent":100,"cards":[{"title":"Card 1","categoryLabel":"Warmup","instructionalText":"Prompt","content":"<p>Details</p>","columnSpan":1}]}]}'
                            value={multiColumnJsonInput}
                            onChange={(e) => setMultiColumnJsonInput(e.target.value)}
                          />
                          {multiColumnJsonError && (
                            <div className="p-3 bg-red-50 border-2 border-red-300 rounded-lg flex items-start gap-2">
                              <svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              <p className="text-sm text-red-800 font-medium">{multiColumnJsonError}</p>
                            </div>
                          )}
                          <div className="flex flex-wrap items-center gap-2">
                            <button
                              type="button"
                              onClick={handleMultiColumnJsonImportAndSave}
                              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-bold text-white bg-gradient-to-r from-purple-600 to-pink-600 rounded-lg hover:from-purple-700 hover:to-pink-700 shadow-md hover:shadow-lg transition-all"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                              </svg>
                              Import + Save Block
                            </button>
                            <a
                              href={`data:application/json,${encodeURIComponent(JSON.stringify({
                                blockTitle: 'Quick check-in',
                                blockTitleEnabled: true,
                                autoConfigureEnabled: true,
                                matchHeights: false,
                                columns: [
                                  {
                                    widthPercent: 40,
                                    cards: [
                                      {
                                        title: 'Connect the dots',
                                        categoryLabel: 'Warmup',
                                        instructionalText: 'Pair up and map prior knowledge.',
                                        content: '<p>List two ideas you already know.</p>',
                                        numberedListStyle: 'numbered',
                                        numberedList: [
                                          { text: 'Brainstorm key terms.' },
                                          { text: 'Share with a partner.' }
                                        ],
                                        columnSpan: 1,
                                        badge: { enabled: true, type: 'gradient', color: '#7c3aed', color2: '#ec4899' }
                                      },
                                      {
                                        title: 'Check-in',
                                        content: '<p>Use the checklist to self-assess.</p>',
                                        isChecklist: true,
                                        checklistItems: [
                                          { text: 'I can define the concept.', checked: false },
                                          { text: 'I can share an example.', checked: false }
                                        ],
                                        listStyle: 'checkbox',
                                        columnSpan: 1
                                      }
                                    ]
                                  },
                                  {
                                    widthPercent: 60,
                                    cards: [
                                      {
                                        title: 'Instructional focus',
                                        content: '<p>Review the steps and annotate the model.</p>',
                                        callout: {
                                          enabled: true,
                                          title: 'Tip',
                                          description: 'Highlight connections between ideas.',
                                          position: 'top'
                                        },
                                        columnSpan: 1
                                      }
                                    ]
                                  }
                                ]
                              }, null, 2))}`}
                              download="multi-column-card-example.json"
                              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-bold text-purple-700 bg-white border-2 border-purple-300 rounded-lg hover:bg-purple-50 transition-all"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                              </svg>
                              Download Example
                            </a>
                            <button
                              type="button"
                              onClick={() => {
                                setShowMultiColumnPaste(false);
                                setMultiColumnJsonInput('');
                                setMultiColumnJsonError('');
                              }}
                              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-bold text-gray-700 bg-white border-2 border-gray-300 rounded-lg hover:bg-gray-50 transition-all"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Height Matching */}
                    <div className="p-4 bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 rounded-lg space-y-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="text-sm font-bold text-amber-900">Card Height Matching</h4>
                          <p className="text-xs text-amber-700 mt-1">Make cards in the same row equal height</p>
                        </div>
                        <label className="inline-flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            className="h-5 w-5 text-amber-600 rounded border-gray-300 focus:ring-2 focus:ring-amber-500 cursor-pointer"
                            checked={formData.multiColumnData?.matchHeights || false}
                            onChange={(e) => {
                              setFormData({
                                ...formData,
                                multiColumnData: {
                                  ...formData.multiColumnData,
                                  matchHeights: e.target.checked
                                }
                              });
                            }}
                          />
                          <span className="text-sm font-semibold text-amber-900">Enable</span>
                        </label>
                      </div>

                      {formData.multiColumnData?.matchHeights && (
                        <div className="p-3 bg-white/80 border border-amber-200 rounded-lg">
                          <p className="text-sm font-semibold text-amber-900 mb-2">Select Columns to Equalize</p>
                          <p className="text-xs text-amber-700 mb-3">
                            Leave all unchecked to equalize every column in each row.
                          </p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                            {(formData.multiColumnData?.columns || []).map((_, colIndex) => {
                              const columnNumber = colIndex + 1;
                              const columnLetter = String.fromCharCode(65 + colIndex);
                              const selectedColumns = (formData.multiColumnData?.matchHeightColumns || []).map((value) => Number(value));
                              const isChecked = selectedColumns.includes(colIndex);
                                return (
                                  <label
                                    key={`match-height-${colIndex}`}
                                  className="flex items-center gap-2 rounded-lg border-2 border-amber-200 px-3 py-2 text-sm text-amber-900 hover:border-amber-400 bg-white cursor-pointer transition-all"
                                >
                                  <input
                                    type="checkbox"
                                    className="h-4 w-4 text-amber-600 rounded border-gray-300 focus:ring-amber-500 cursor-pointer"
                                    checked={isChecked}
                                    onChange={(e) => {
                                      const normalizedExisting = (formData.multiColumnData?.matchHeightColumns || [])
                                        .map((value) => Number(value))
                                        .filter((value) => !Number.isNaN(value));
                                      const updatedColumns = new Set(normalizedExisting);
                                      if (e.target.checked) {
                                        updatedColumns.add(colIndex);
                                      } else {
                                        updatedColumns.delete(colIndex);
                                      }

                                      setFormData({
                                        ...formData,
                                        multiColumnData: {
                                          ...formData.multiColumnData,
                                          matchHeightColumns: Array.from(updatedColumns)
                                        }
                                      });
                                    }}
                                  />
                                  <span className="font-bold">Column {columnNumber} ({columnLetter})</span>
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Columns Section */}
              <div className="bg-white border-2 border-indigo-200 rounded-xl shadow-sm overflow-hidden">
                <div className="px-5 py-4 bg-gradient-to-r from-indigo-50 to-blue-50 border-b-2 border-indigo-100 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="bg-indigo-600 text-white rounded-lg p-2">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">Columns & Cards</h3>
                      <p className="text-xs text-gray-600">{(formData.multiColumnData?.columns || []).length} column(s) configured</p>
                    </div>
                  </div>
                </div>

                <div className="p-5 space-y-5">
                  {(() => {
                    // Drag and drop handlers for multi-column cards
                    return ((formData.multiColumnData?.columns || [{ cards: [{ title: '', content: '', isChecklist: false, checklistItems: [], backgroundColor: 'transparent', borderColor: '#94a3b8', showBorder: true, listStyle: 'checkbox', badge: { enabled: false, type: 'theme', gradientDirection: 'to right' } }] }])).map((column, colIndex, columnsList) => {
                      // Drag and drop handlers
                      const handleDragStart = (e, colIdx, cardIdx) => {
                        setDraggedCard({ colIndex: colIdx, cardIndex: cardIdx });
                        e.dataTransfer.effectAllowed = 'move';
                        e.dataTransfer.setData('text/html', e.currentTarget);
                        e.currentTarget.style.opacity = '0.4';
                      };

                      const handleDragEnd = (e) => {
                        e.currentTarget.style.opacity = '1';
                        setDraggedCard(null);
                        setDropTarget(null);
                      };

                      const handleDragOver = (e) => {
                        e.preventDefault();
                        e.dataTransfer.dropEffect = 'move';
                      };

                      const handleDrop = (e, targetColIdx, targetCardIdx, handleMoveCardFn) => {
                        e.preventDefault();
                        e.stopPropagation();

                        if (!draggedCard) return;

                        if (draggedCard.colIndex === targetColIdx && draggedCard.cardIndex === targetCardIdx) {
                          return;
                        }

                        handleMoveCardFn(draggedCard.colIndex, draggedCard.cardIndex, targetColIdx, targetCardIdx);
                        setDraggedCard(null);
                        setDropTarget(null);
                      };

                      const handleDropZoneDragOver = (e, targetColIdx, targetCardIdx) => {
                        e.preventDefault();
                        e.dataTransfer.dropEffect = 'move';
                        setDropTarget({ colIndex: targetColIdx, cardIndex: targetCardIdx });
                      };

                      const handleDropZoneDragLeave = () => {
                        setDropTarget(null);
                      };

                      return (
                    <div key={colIndex} className="border-2 border-indigo-300 rounded-xl overflow-hidden shadow-md bg-gradient-to-br from-indigo-50 to-blue-50">
                      {/* Column Header */}
                      <div className="px-5 py-4 bg-gradient-to-r from-indigo-100 to-blue-100 border-b-2 border-indigo-200">
                        <div className="flex justify-between items-center">
                          <div className="flex items-center gap-3">
                            <div className="bg-indigo-600 text-white rounded-lg p-2 font-bold text-sm">
                              {String.fromCharCode(65 + colIndex)}
                            </div>
                            <div>
                              <h4 className="text-lg font-bold text-indigo-900">Column {colIndex + 1}</h4>
                              <p className="text-xs text-indigo-700">{column.cards?.length || 0} card(s)</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {columnsList.length > 1 && (
                              <button
                                type="button"
                                onClick={() => {
                                  const newColumns = [...columnsList];
                                  newColumns.splice(colIndex, 1);
                                  const filteredMatches = (formData.multiColumnData?.matchHeightColumns || [])
                                    .map((idx) => Number(idx))
                                    .filter((idx) => !Number.isNaN(idx))
                                    .map((idx) => (idx > colIndex ? idx - 1 : idx))
                                    .filter((idx) => idx !== colIndex && idx < newColumns.length);
                                  const clampedColumns = newColumns.map((column, idx, arr) => {
                                    const maxSpan = Math.max(1, arr.length - idx);
                                    const cards = (column.cards || []).map((card) => {
                                      const parsedSpan = Number(card.columnSpan);
                                      const columnSpan = Math.min(Math.max(Number.isInteger(parsedSpan) ? parsedSpan : 1, 1), maxSpan);
                                      return { ...card, columnSpan };
                                    });
                                    return { ...column, cards };
                                  });
                                  setFormData({
                                    ...formData,
                                    multiColumnData: { ...formData.multiColumnData, columns: clampedColumns, matchHeightColumns: filteredMatches }
                                  });
                                }}
                                className="inline-flex items-center gap-1 px-3 py-2 bg-red-500 text-white text-sm font-semibold rounded-lg hover:bg-red-600 transition-all shadow hover:shadow-md"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                                Delete Column
                              </button>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Column Width Control */}
                      <div className="p-4 bg-white border-l-4 border-indigo-400 mx-4 my-3 rounded-r-lg shadow-sm">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                            </svg>
                            <label className="text-sm font-bold text-gray-800">Column Width</label>
                          </div>
                          <span className="px-3 py-1 text-base font-bold text-white bg-indigo-600 rounded-lg">
                            {column?.widthPercent || 100}%
                          </span>
                        </div>
                        <input
                          type="range"
                          min="20"
                          max="300"
                          step="10"
                          value={column?.widthPercent || 100}
                          onChange={(e) => {
                            const newColumns = [...columnsList];
                            newColumns[colIndex] = { ...newColumns[colIndex], widthPercent: parseInt(e.target.value) };
                            setFormData({
                              ...formData,
                              multiColumnData: { ...formData.multiColumnData, columns: newColumns }
                            });
                          }}
                          className="w-full h-3 bg-gradient-to-r from-indigo-200 via-indigo-300 to-indigo-200 rounded-lg appearance-none cursor-pointer accent-indigo-600"
                          style={{
                            background: `linear-gradient(to right, #c7d2fe 0%, #818cf8 ${((column?.widthPercent || 100) - 20) / 280 * 100}%, #c7d2fe ${((column?.widthPercent || 100) - 20) / 280 * 100}%, #c7d2fe 100%)`
                          }}
                        />
                        <div className="flex justify-between text-xs font-medium text-gray-600 mt-2">
                          <span>20%</span>
                          <span className="text-indigo-700">100% (Default)</span>
                          <span>300%</span>
                        </div>
                      </div>

                      {/* Cards within this column */}
                      <div className="px-4 pb-4 space-y-4">
                        <div className="flex items-center justify-between">
                          <h5 className="text-sm font-bold text-indigo-900 flex items-center gap-2">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                            </svg>
                            Cards in This Column
                          </h5>
                          <span className="px-2 py-1 bg-indigo-100 text-indigo-800 text-xs font-bold rounded-full">
                            {column.cards?.length || 0}
                          </span>
                        </div>

                        {(column.cards || []).map((card, cardIndex) => {
                        const listStyle = card.listStyle || 'checkbox';
                        const badgeConfig = { enabled: false, type: 'theme', gradientDirection: 'to right', ...(card.badge || {}) };
                        const numberedListStyle = (card?.numberedListStyle || 'numbered').toLowerCase();
                        const useCheckmarks = numberedListStyle.startsWith('check');
                        const checkmarkColor = card?.checkmarkColor || '#1f2937';

                        const handleMoveCard = (fromColIndex, fromCardIndex, targetColIndex, targetCardIndex) => {
                          const preparedColumns = (formData.multiColumnData?.columns || []).map((col) => ({
                            ...col,
                            cards: [...(col.cards || [])]
                          }));

                          if (
                            !preparedColumns[fromColIndex]?.cards?.length ||
                            !preparedColumns[targetColIndex]
                          ) {
                            return;
                          }

                          const [cardToMove] = preparedColumns[fromColIndex].cards.splice(fromCardIndex, 1);
                          const safeTargetIndex = Math.min(
                            Math.max(targetCardIndex, 0),
                            preparedColumns[targetColIndex].cards.length
                          );

                          preparedColumns[targetColIndex].cards.splice(safeTargetIndex, 0, cardToMove);

                          setFormData({
                            ...formData,
                            multiColumnData: { ...formData.multiColumnData, columns: preparedColumns }
                          });
                        };

                          return (
                          <div key={cardIndex}>
                            {/* Drop Zone Above Card */}
                            <div
                              onDragOver={(e) => handleDropZoneDragOver(e, colIndex, cardIndex)}
                              onDragLeave={handleDropZoneDragLeave}
                              onDrop={(e) => handleDrop(e, colIndex, cardIndex, handleMoveCard)}
                              className={`h-2 transition-all ${
                                dropTarget?.colIndex === colIndex && dropTarget?.cardIndex === cardIndex
                                  ? 'h-12 bg-indigo-200 border-2 border-dashed border-indigo-500 rounded-lg mb-2'
                                  : 'h-0'
                              }`}
                            />

                            <div
                              className={`border-2 border-gray-300 rounded-xl overflow-hidden bg-white shadow-md transition-all ${
                                draggedCard?.colIndex === colIndex && draggedCard?.cardIndex === cardIndex
                                  ? 'ring-4 ring-indigo-300'
                                  : ''
                              }`}
                            >
                            {/* Card Header */}
                            <div className="px-4 py-3 bg-gradient-to-r from-gray-50 to-slate-50 border-b-2 border-gray-200">
                              <div className="flex flex-col gap-3">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-3">
                                    {/* Drag Handle */}
                                    <div
                                      className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-indigo-600 transition-colors"
                                      title="Drag to reorder"
                                      draggable="true"
                                      onDragStart={(e) => handleDragStart(e, colIndex, cardIndex)}
                                      onDragEnd={handleDragEnd}
                                      onDragOver={handleDragOver}
                                    >
                                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                                      </svg>
                                    </div>
                                    <span className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-gradient-to-br from-indigo-500 to-blue-600 text-white font-bold text-sm shadow">
                                      {cardIndex + 1}
                                    </span>
                                    <div>
                                      <h5 className="font-bold text-gray-900">Card {cardIndex + 1}</h5>
                                      <p className="text-xs text-gray-600">
                                        {card.isChecklist ? 'Checklist Card' : 'Content Card'}
                                      </p>
                                    </div>
                                  </div>
                                  {column.cards.length > 1 && (
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const newColumns = [...columnsList];
                                        const newCards = [...column.cards];
                                        newCards.splice(cardIndex, 1);
                                        newColumns[colIndex] = { ...newColumns[colIndex], cards: newCards };
                                        setFormData({
                                          ...formData,
                                          multiColumnData: { ...formData.multiColumnData, columns: newColumns }
                                        });
                                      }}
                                      className="inline-flex items-center gap-1 px-3 py-2 bg-red-500 text-white text-sm font-semibold rounded-lg hover:bg-red-600 transition-all shadow hover:shadow-md"
                                    >
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                      </svg>
                                      Delete
                                    </button>
                                  )}
                                </div>

                                {/* Action Buttons */}
                                <div className="flex flex-wrap gap-2">
                                  {/* Copy/Paste */}
                                  <div className="flex gap-2">
                                    <button
                                      type="button"
                                      onClick={() => handleCopyMultiColumnCard(card)}
                                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg border-2 border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100 transition-all"
                                      title="Copy card"
                                    >
                                      <Copy size={14} /> Copy
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => handlePasteMultiColumnCard(columnsList, colIndex, cardIndex)}
                                      disabled={!canPasteMultiColumnCard}
                                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg border-2 transition-all ${
                                        canPasteMultiColumnCard
                                          ? 'border-green-300 bg-green-50 text-green-700 hover:bg-green-100'
                                          : 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed'
                                      }`}
                                      title="Paste card below"
                                    >
                                      <ClipboardPaste size={14} /> Paste Below
                                    </button>
                                  </div>

                                  {/* Move Buttons */}
                                  {(column.cards.length > 1 || columnsList.length > 1) && (
                                    <div className="flex gap-2">
                                      <button
                                        type="button"
                                        onClick={() => handleMoveCard(colIndex, cardIndex, colIndex, cardIndex - 1)}
                                        disabled={cardIndex === 0}
                                        className={`inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-lg border-2 transition-all ${
                                          cardIndex === 0
                                            ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
                                            : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
                                        }`}
                                      >
                                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                        </svg>
                                        Up
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => handleMoveCard(colIndex, cardIndex, colIndex, cardIndex + 1)}
                                        disabled={cardIndex === column.cards.length - 1}
                                        className={`inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-lg border-2 transition-all ${
                                          cardIndex === column.cards.length - 1
                                            ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
                                            : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
                                        }`}
                                      >
                                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                        </svg>
                                        Down
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => handleMoveCard(colIndex, cardIndex, colIndex - 1, (columnsList[colIndex - 1]?.cards || []).length)}
                                        disabled={colIndex === 0}
                                        className={`inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-lg border-2 transition-all ${
                                          colIndex === 0
                                            ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
                                            : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
                                        }`}
                                      >
                                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                        </svg>
                                        ← Col
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => handleMoveCard(colIndex, cardIndex, colIndex + 1, (columnsList[colIndex + 1]?.cards || []).length)}
                                        disabled={colIndex === columnsList.length - 1}
                                        className={`inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-lg border-2 transition-all ${
                                          colIndex === columnsList.length - 1
                                            ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
                                            : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
                                        }`}
                                      >
                                        Col →
                                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                        </svg>
                                      </button>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* Collapsible Card Controls */}
                            <details className="border-t-2 border-gray-200" open>
                              <summary className="px-4 py-3 bg-gradient-to-r from-slate-50 to-gray-50 cursor-pointer hover:from-slate-100 hover:to-gray-100 transition-colors flex items-center justify-between group">
                                <span className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                                  <svg className="w-4 h-4 text-indigo-600 transition-transform group-open:rotate-90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                  </svg>
                                  Card Settings & Content
                                </span>
                                <span className="text-xs text-gray-500">Click to expand/collapse</span>
                              </summary>

                              {/* Card Content */}
                              <div className="p-4 space-y-4">

                          <details className="mt-3 rounded-lg border border-slate-200 bg-slate-50/70 px-3 py-2">
                            <summary className="cursor-pointer text-xs font-semibold text-slate-700">
                              View card JSON
                            </summary>
                            <div className="mt-2 space-y-2">
                              <textarea
                                readOnly
                                className="w-full rounded-md border border-slate-200 bg-white px-2 py-1.5 text-xs font-mono text-slate-700"
                                rows={10}
                                value={buildMultiColumnCardJson(card)}
                              />
                              <div className="flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() => handleCopyCardJson(card)}
                                  className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-100"
                                >
                                  <Copy size={12} /> Copy JSON
                                </button>
                                <span className="text-xs text-slate-500">
                                  Edit the JSON in an external editor if you want to reuse it.
                                </span>
                              </div>
                            </div>
                          </details>

                          <div className="grid lg:grid-cols-3 gap-3">
                            <div className="space-y-3 p-3 bg-gray-50 border border-gray-200 rounded">
                              <div className="flex items-center justify-between">
                                <h4 className="text-sm font-semibold text-gray-800">Card Type & Border</h4>
                              </div>
                              <label className="flex items-center gap-2">
                                <input
                                  type="checkbox"
                                  checked={card?.isChecklist || false}
                                  onChange={(e) => {
                                    const newColumns = [...columnsList];
                                    const newCards = [...column.cards];
                                    const defaultBadgeConfig = { enabled: false, type: 'theme', gradientDirection: 'to right' };
                                    newCards[cardIndex] = {
                                      ...newCards[cardIndex],
                                      isChecklist: e.target.checked,
                                      checklistItems: e.target.checked ? (card.checklistItems || [{ text: '', description: '', checked: false }]) : [],
                                      listStyle: card.listStyle || 'checkbox',
                                      badge: { ...defaultBadgeConfig, ...(card.badge || {}) },
                                      // Preserve all existing properties including callout, titleColor, checkmarkColor
                                      callout: card.callout || { enabled: false, title: '', description: '', borderColor: undefined, backgroundColor: undefined, position: 'top' },
                                      titleColor: card.titleColor || undefined,
                                      checkmarkColor: card.checkmarkColor || undefined
                                    };
                                    newColumns[colIndex] = { ...newColumns[colIndex], cards: newCards };
                                    setFormData({
                                      ...formData,
                                      multiColumnData: { ...formData.multiColumnData, columns: newColumns }
                                    });
                                  }}
                                  className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                                />
                                <span className="text-sm font-medium text-gray-700">
                                  Checklist Card (e.g., Core Milestones)
                                </span>
                              </label>

                              <div className="flex items-center gap-2">
                                <input
                                  type="checkbox"
                                  id={`show-border-${colIndex}-${cardIndex}`}
                                  checked={card?.showBorder !== false}
                                  onChange={(e) => {
                                    const newColumns = [...columnsList];
                                    const newCards = [...column.cards];
                                    newCards[cardIndex] = { ...newCards[cardIndex], showBorder: e.target.checked };
                                    newColumns[colIndex] = { ...newColumns[colIndex], cards: newCards };
                                    setFormData({
                                      ...formData,
                                      multiColumnData: { ...formData.multiColumnData, columns: newColumns }
                                    });
                                  }}
                                  className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                                />
                                <label htmlFor={`show-border-${colIndex}-${cardIndex}`} className="text-sm font-medium text-gray-700">
                                  Show Border
                                </label>
                              </div>
                            </div>

                            <div className="lg:col-span-2 space-y-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                              <div className="flex items-center justify-between">
                                <h4 className="text-sm font-semibold text-blue-900">Card Colors</h4>
                                <span className="text-xs text-blue-800">Background, border, and text</span>
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">Card Background</label>
                                  <div className="flex gap-2 items-center">
                                    <input
                                      type="color"
                                      value={card?.backgroundColor === 'transparent' ? '#ffffff' : (card?.backgroundColor || '#ffffff')}
                                      onChange={(e) => {
                                        const newColumns = [...columnsList];
                                        const newCards = [...column.cards];
                                        newCards[cardIndex] = { ...newCards[cardIndex], backgroundColor: e.target.value };
                                        newColumns[colIndex] = { ...newColumns[colIndex], cards: newCards };
                                        setFormData({
                                          ...formData,
                                          multiColumnData: { ...formData.multiColumnData, columns: newColumns }
                                        });
                                      }}
                                      className="h-10 w-full border-2 border-gray-400 rounded"
                                    />
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const newColumns = [...columnsList];
                                        const newCards = [...column.cards];
                                        newCards[cardIndex] = { ...newCards[cardIndex], backgroundColor: 'transparent' };
                                        newColumns[colIndex] = { ...newColumns[colIndex], cards: newCards };
                                        setFormData({
                                          ...formData,
                                          multiColumnData: { ...formData.multiColumnData, columns: newColumns }
                                        });
                                      }}
                                      className="px-2 py-1 text-xs bg-blue-200 hover:bg-blue-300 rounded whitespace-nowrap"
                                    >
                                      Clear
                                    </button>
                                  </div>
                                </div>

                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">Card Border</label>
                                  <input
                                    type="color"
                                    value={card?.borderColor || '#94a3b8'}
                                    onChange={(e) => {
                                      const newColumns = [...columnsList];
                                      const newCards = [...column.cards];
                                      newCards[cardIndex] = { ...newCards[cardIndex], borderColor: e.target.value };
                                      newColumns[colIndex] = { ...newColumns[colIndex], cards: newCards };
                                      setFormData({
                                        ...formData,
                                        multiColumnData: { ...formData.multiColumnData, columns: newColumns }
                                      });
                                    }}
                                    className="h-10 w-full border-2 border-gray-400 rounded"
                                  />
                                </div>

                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">Title Text Color</label>
                                  <div className="flex gap-2 items-center">
                                    <input
                                      type="color"
                                      value={card?.titleColor || '#1f2937'}
                                      onChange={(e) => {
                                        const newColumns = [...columnsList];
                                        const newCards = [...column.cards];
                                        newCards[cardIndex] = { ...newCards[cardIndex], titleColor: e.target.value };
                                        newColumns[colIndex] = { ...newColumns[colIndex], cards: newCards };
                                        setFormData({
                                          ...formData,
                                          multiColumnData: { ...formData.multiColumnData, columns: newColumns }
                                        });
                                      }}
                                      className="h-10 w-full border-2 border-gray-400 rounded"
                                    />
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const newColumns = [...columnsList];
                                        const newCards = [...column.cards];
                                        newCards[cardIndex] = { ...newCards[cardIndex], titleColor: undefined };
                                        newColumns[colIndex] = { ...newColumns[colIndex], cards: newCards };
                                        setFormData({
                                          ...formData,
                                          multiColumnData: { ...formData.multiColumnData, columns: newColumns }
                                        });
                                      }}
                                      className="px-2 py-1 text-xs bg-blue-200 hover:bg-blue-300 rounded whitespace-nowrap"
                                    >
                                      Auto
                                    </button>
                                  </div>
                                </div>

                                {card?.isChecklist && (
                                  <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Checkmark Color (✓)</label>
                                    <div className="flex gap-2 items-center">
                                      <input
                                        type="color"
                                        value={card?.checkmarkColor || '#1f2937'}
                                        onChange={(e) => {
                                          const newColumns = [...columnsList];
                                          const newCards = [...column.cards];
                                          newCards[cardIndex] = { ...newCards[cardIndex], checkmarkColor: e.target.value };
                                          newColumns[colIndex] = { ...newColumns[colIndex], cards: newCards };
                                          setFormData({
                                            ...formData,
                                            multiColumnData: { ...formData.multiColumnData, columns: newColumns }
                                          });
                                        }}
                                        className="h-10 w-full border-2 border-gray-400 rounded"
                                      />
                                      <button
                                        type="button"
                                        onClick={() => {
                                          const newColumns = [...columnsList];
                                          const newCards = [...column.cards];
                                          newCards[cardIndex] = { ...newCards[cardIndex], checkmarkColor: undefined };
                                          newColumns[colIndex] = { ...newColumns[colIndex], cards: newCards };
                                          setFormData({
                                            ...formData,
                                            multiColumnData: { ...formData.multiColumnData, columns: newColumns }
                                          });
                                        }}
                                        className="px-2 py-1 text-xs bg-blue-200 hover:bg-blue-300 rounded whitespace-nowrap"
                                      >
                                        Auto
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>

                          <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg space-y-3">
                            <div className="flex items-center justify-between">
                              <h4 className="text-sm font-semibold text-gray-800">Card Header & Layout</h4>
                              <span className="text-xs text-gray-600">Titles, labels, alignment, and icons</span>
                            </div>
                            <div className="grid md:grid-cols-2 gap-3">
                              <div className="space-y-3">
                                <div>
                                  <label className="block text-xs font-medium text-gray-600 mb-1">Category Label (Top Center)</label>
                                  <input
                                    type="text"
                                    placeholder="e.g., Asynchronous Review"
                                    value={card?.categoryLabel || ''}
                                    onChange={(e) => {
                                      const newColumns = [...columnsList];
                                      const newCards = [...column.cards];
                                      newCards[cardIndex] = { ...newCards[cardIndex], categoryLabel: e.target.value };
                                      newColumns[colIndex] = { ...newColumns[colIndex], cards: newCards };
                                      setFormData({
                                        ...formData,
                                        multiColumnData: { ...formData.multiColumnData, columns: newColumns }
                                      });
                                    }}
                                    className="w-full p-3 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-600 mb-1">Primary Title</label>
                                  <input
                                    type="text"
                                    placeholder="e.g., SyncSketch + Schedule Links"
                                    value={card?.primaryTitle || card?.title || ''}
                                    onChange={(e) => {
                                      const newColumns = [...columnsList];
                                      const newCards = [...column.cards];
                                      newCards[cardIndex] = {
                                        ...newCards[cardIndex],
                                        primaryTitle: e.target.value,
                                        title: e.target.value || card?.title || ''
                                      };
                                      newColumns[colIndex] = { ...newColumns[colIndex], cards: newCards };
                                      setFormData({
                                        ...formData,
                                        multiColumnData: { ...formData.multiColumnData, columns: newColumns }
                                      });
                                    }}
                                    className="w-full p-3 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-600 mb-1">Instructional Text</label>
                                  <input
                                    type="text"
                                    placeholder="e.g., Week 1 has two parts"
                                    value={card?.instructionalText || ''}
                                    onChange={(e) => {
                                      const newColumns = [...columnsList];
                                      const newCards = [...column.cards];
                                      newCards[cardIndex] = { ...newCards[cardIndex], instructionalText: e.target.value };
                                      newColumns[colIndex] = { ...newColumns[colIndex], cards: newCards };
                                      setFormData({
                                        ...formData,
                                        multiColumnData: { ...formData.multiColumnData, columns: newColumns }
                                      });
                                    }}
                                    className="w-full p-3 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                  />
                                </div>
                              </div>

                              <div className="space-y-3">
                                <div>
                                  <label className="block text-xs font-medium text-gray-600 mb-1">Title Alignment</label>
                                  <select
                                    value={card?.titleAlignment || 'center'}
                                    onChange={(e) => {
                                      const newColumns = [...columnsList];
                                      const newCards = [...column.cards];
                                      newCards[cardIndex] = { ...newCards[cardIndex], titleAlignment: e.target.value };
                                      newColumns[colIndex] = { ...newColumns[colIndex], cards: newCards };
                                      setFormData({
                                        ...formData,
                                        multiColumnData: { ...formData.multiColumnData, columns: newColumns }
                                      });
                                    }}
                                    className="w-full p-3 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                  >
                                    <option value="left">Left</option>
                                    <option value="center">Center</option>
                                    <option value="right">Right</option>
                                    <option value="justify">Justify</option>
                                  </select>
                                </div>

                                <div>
                                  <label className="block text-xs font-medium text-gray-600 mb-1">Optional Icon</label>
                                  <select
                                    value={card?.iconName || ''}
                                    onChange={(e) => {
                                      const newColumns = [...columnsList];
                                      const newCards = [...column.cards];
                                      newCards[cardIndex] = { ...newCards[cardIndex], iconName: e.target.value };
                                      newColumns[colIndex] = { ...newColumns[colIndex], cards: newCards };
                                      setFormData({
                                        ...formData,
                                        multiColumnData: { ...formData.multiColumnData, columns: newColumns }
                                      });
                                    }}
                                    className="w-full p-3 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                  >
                                    <option value="">None</option>
                                    <option value="link">Link</option>
                                    <option value="external-link">External Link</option>
                                    <option value="info">Info</option>
                                    <option value="star">Star</option>
                                    <option value="check">Check</option>
                                    <option value="bookmark">Bookmark</option>
                                    <option value="file">File</option>
                                    <option value="folder">Folder</option>
                                    <option value="calendar">Calendar</option>
                                    <option value="pencil">Pencil</option>
                                    <option value="brush">Brush</option>
                                    <option value="palette">Palette</option>
                                  </select>
                                </div>

                                <div>
                                  <label className="block text-xs font-medium text-gray-600 mb-1">Card Row</label>
                                  <input
                                    type="number"
                                    min="1"
                                    value={card?.rowIndex ?? cardIndex + 1}
                                    onChange={(e) => {
                                      const newColumns = [...columnsList];
                                      const newCards = [...column.cards];
                                      const parsedRow = parseInt(e.target.value, 10);
                                      const rowIndex = Number.isInteger(parsedRow) && parsedRow > 0 ? parsedRow : undefined;
                                      newCards[cardIndex] = { ...newCards[cardIndex], rowIndex };
                                      newColumns[colIndex] = { ...newColumns[colIndex], cards: newCards };
                                      setFormData({
                                        ...formData,
                                        multiColumnData: { ...formData.multiColumnData, columns: newColumns }
                                      });
                                    }}
                                    className="w-full p-3 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                  />
                                  <p className="text-xs text-gray-500 mt-1">
                                    Use row numbers to align cards across columns.
                                  </p>
                                </div>

                                <div>
                                  <label className="block text-xs font-medium text-gray-600 mb-1">Card Column</label>
                                  <select
                                    value={colIndex}
                                    onChange={(e) => {
                                      const targetColIndex = parseInt(e.target.value, 10);
                                      if (!Number.isInteger(targetColIndex) || targetColIndex === colIndex) return;
                                      const newColumns = [...columnsList];
                                      const sourceCards = [...column.cards];
                                      const [movedCard] = sourceCards.splice(cardIndex, 1);
                                      if (!movedCard) return;
                                      const targetCards = [...(newColumns[targetColIndex]?.cards || [])];
                                      const normalizedSpan = normalizeMultiColumnSpan(movedCard.columnSpan, targetColIndex, newColumns.length);
                                      targetCards.push({ ...movedCard, columnSpan: normalizedSpan });
                                      newColumns[colIndex] = { ...newColumns[colIndex], cards: sourceCards };
                                      newColumns[targetColIndex] = { ...newColumns[targetColIndex], cards: targetCards };
                                      setFormData({
                                        ...formData,
                                        multiColumnData: { ...formData.multiColumnData, columns: newColumns }
                                      });
                                    }}
                                    className="w-full p-3 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                  >
                                    {columnsList.map((_, optionIndex) => {
                                      const columnLetter = String.fromCharCode(65 + optionIndex);
                                      return (
                                        <option key={`column-option-${colIndex}-${cardIndex}-${optionIndex}`} value={optionIndex}>
                                          Column {columnLetter}
                                        </option>
                                      );
                                    })}
                                  </select>
                                  <p className="text-xs text-gray-500 mt-1">
                                    Move this card to a different column.
                                  </p>
                                </div>

                                <div>
                                  <label className="block text-xs font-medium text-gray-600 mb-1">Column Span</label>
                                  <select
                                    value={card?.columnSpan || 1}
                                    onChange={(e) => {
                                      const maxSpan = Math.max(1, (columnsList || []).length - colIndex);
                                      const parsedSpan = parseInt(e.target.value, 10);
                                      const columnSpan = Math.min(Math.max(Number.isInteger(parsedSpan) ? parsedSpan : 1, 1), maxSpan);
                                      const newColumns = [...columnsList];
                                      const newCards = [...column.cards];
                                      newCards[cardIndex] = { ...newCards[cardIndex], columnSpan };
                                      newColumns[colIndex] = { ...newColumns[colIndex], cards: newCards };
                                      setFormData({
                                        ...formData,
                                        multiColumnData: { ...formData.multiColumnData, columns: newColumns }
                                      });
                                    }}
                                    className="w-full p-3 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                  >
                                    {Array.from({ length: Math.max(1, (columnsList || []).length - colIndex) }).map((_, spanIndex) => {
                                      const optionValue = spanIndex + 1;
                                      return (
                                        <option key={`span-${colIndex}-${cardIndex}-${optionValue}`} value={optionValue}>
                                          Span {optionValue} column{optionValue > 1 ? 's' : ''}
                                        </option>
                                      );
                                    })}
                                  </select>
                                  <p className="text-xs text-gray-500 mt-1">
                                    Extends this card into adjacent columns starting here.
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="mb-4">
                            <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
                              <label className="block text-sm font-medium text-gray-700">Numbered List (optional)</label>
                              <div className="flex items-center gap-2">
                                <label className="text-xs font-medium text-gray-600">Step Style</label>
                                <select
                                  value={card?.numberedListStyle || 'numbered'}
                                  onChange={(e) => {
                                    const newColumns = [...columnsList];
                                    const newCards = [...column.cards];
                                    newCards[cardIndex] = { ...newCards[cardIndex], numberedListStyle: e.target.value };
                                    newColumns[colIndex] = { ...newColumns[colIndex], cards: newCards };
                                    setFormData({
                                      ...formData,
                                      multiColumnData: { ...formData.multiColumnData, columns: newColumns }
                                    });
                                  }}
                                  className="px-2 py-1 border border-gray-300 rounded text-xs"
                                >
                                  <option value="numbered">Numbered</option>
                                  <option value="checkmarks">Checkmarks</option>
                                </select>
                              </div>
                              <button
                                type="button"
                                onClick={() => {
                                  const newColumns = [...columnsList];
                                  const newCards = [...column.cards];
                                  const updatedList = [...(card.numberedList || []), { text: '' }];
                                  newCards[cardIndex] = { ...newCards[cardIndex], numberedList: updatedList };
                                  newColumns[colIndex] = { ...newColumns[colIndex], cards: newCards };
                                  setFormData({
                                    ...formData,
                                    multiColumnData: { ...formData.multiColumnData, columns: newColumns }
                                  });
                                }}
                                className="px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                              >
                                + Add Step
                              </button>
                            </div>
                            <div className="space-y-2">
                              {(card.numberedList || []).map((item, itemIndex) => (
                                <div key={itemIndex} className="flex items-start gap-2">
                                  {useCheckmarks ? (
                                    <span
                                      className="w-6 h-6 flex items-center justify-center rounded-full border-2 text-xs font-semibold"
                                      style={{ borderColor: checkmarkColor, color: checkmarkColor }}
                                      aria-hidden="true"
                                    >
                                      ✓
                                    </span>
                                  ) : (
                                    <span className="w-6 h-6 flex items-center justify-center bg-blue-600 text-white text-xs font-semibold rounded">
                                      {itemIndex + 1}
                                    </span>
                                  )}
                                  <input
                                    type="text"
                                    value={item.text || ''}
                                    onChange={(e) => {
                                      const newColumns = [...columnsList];
                                      const newCards = [...column.cards];
                                      const updatedList = [...(card.numberedList || [])];
                                      updatedList[itemIndex] = { ...updatedList[itemIndex], text: e.target.value };
                                      newCards[cardIndex] = { ...newCards[cardIndex], numberedList: updatedList };
                                      newColumns[colIndex] = { ...newColumns[colIndex], cards: newCards };
                                      setFormData({
                                        ...formData,
                                        multiColumnData: { ...formData.multiColumnData, columns: newColumns }
                                      });
                                    }}
                                    placeholder={`Step ${itemIndex + 1}`}
                                    className="flex-1 p-2 border border-gray-300 rounded"
                                  />
                                  {(card.numberedList || []).length > 1 && (
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const newColumns = [...columnsList];
                                        const newCards = [...column.cards];
                                        const updatedList = [...(card.numberedList || [])];
                                        updatedList.splice(itemIndex, 1);
                                        newCards[cardIndex] = { ...newCards[cardIndex], numberedList: updatedList };
                                        newColumns[colIndex] = { ...newColumns[colIndex], cards: newCards };
                                        setFormData({
                                          ...formData,
                                          multiColumnData: { ...formData.multiColumnData, columns: newColumns }
                                        });
                                      }}
                                      className="px-2 py-1 text-xs bg-red-500 text-white rounded"
                                    >
                                      Remove
                                    </button>
                                  )}
                                </div>
                              ))}
                              {(card.numberedList || []).length === 0 && (
                                <p className="text-xs text-gray-500">Use this to add numbered steps without enabling checklist mode.</p>
                              )}
                            </div>
                          </div>

                          {/* Dotted Callout Box */}
                          <div className="mb-3 p-3 bg-amber-50 border-2 border-amber-200 rounded-lg">
                            <div className="flex items-center gap-2 mb-2">
                              <input
                                type="checkbox"
                                id={`callout-enabled-${colIndex}-${cardIndex}`}
                                checked={card?.callout?.enabled || false}
                                onChange={(e) => {
                                  const newColumns = [...columnsList];
                                  const newCards = [...column.cards];
                                  newCards[cardIndex] = {
                                    ...newCards[cardIndex],
                                    callout: {
                                      enabled: e.target.checked,
                                      title: card?.callout?.title || '',
                                      description: card?.callout?.description || '',
                                      borderColor: card?.callout?.borderColor || undefined,
                                      backgroundColor: card?.callout?.backgroundColor || undefined
                                    }
                                  };
                                  newColumns[colIndex] = { ...newColumns[colIndex], cards: newCards };
                                  setFormData({
                                    ...formData,
                                    multiColumnData: { ...formData.multiColumnData, columns: newColumns }
                                  });
                                }}
                                className="h-4 w-4 text-amber-600 rounded border-gray-300 focus:ring-amber-500"
                              />
                              <label htmlFor={`callout-enabled-${colIndex}-${cardIndex}`} className="text-sm font-semibold text-amber-900">
                                Enable Dotted Callout Box
                              </label>
                            </div>
                            {card?.callout?.enabled && (
                              <div className="space-y-2 mt-3">
                                <input
                                  type="text"
                                  placeholder="Callout Title (e.g., 'SyncSketch Step')"
                                  value={card?.callout?.title || ''}
                                  onChange={(e) => {
                                    const newColumns = [...columnsList];
                                    const newCards = [...column.cards];
                                    newCards[cardIndex] = {
                                      ...newCards[cardIndex],
                                      callout: { ...card.callout, title: e.target.value }
                                    };
                                    newColumns[colIndex] = { ...newColumns[colIndex], cards: newCards };
                                    setFormData({
                                      ...formData,
                                      multiColumnData: { ...formData.multiColumnData, columns: newColumns }
                                    });
                                  }}
                                  className="w-full p-2 border border-amber-300 rounded text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                                />
                                <input
                                  type="text"
                                  placeholder="Callout Description (e.g., 'Generate one shareable review link')"
                                  value={card?.callout?.description || ''}
                                  onChange={(e) => {
                                    const newColumns = [...columnsList];
                                    const newCards = [...column.cards];
                                    newCards[cardIndex] = {
                                      ...newCards[cardIndex],
                                      callout: { ...card.callout, description: e.target.value }
                                    };
                                    newColumns[colIndex] = { ...newColumns[colIndex], cards: newCards };
                                    setFormData({
                                      ...formData,
                                      multiColumnData: { ...formData.multiColumnData, columns: newColumns }
                                    });
                                  }}
                                  className="w-full p-2 border border-amber-300 rounded text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                                />
                                <div>
                                  <label className="block text-xs font-medium text-amber-800 mb-1">Position in Card</label>
                                  <select
                                    value={card?.callout?.position || 'top'}
                                    onChange={(e) => {
                                      const newColumns = [...columnsList];
                                      const newCards = [...column.cards];
                                      newCards[cardIndex] = {
                                        ...newCards[cardIndex],
                                        callout: { ...card.callout, position: e.target.value }
                                      };
                                      newColumns[colIndex] = { ...newColumns[colIndex], cards: newCards };
                                      setFormData({
                                        ...formData,
                                        multiColumnData: { ...formData.multiColumnData, columns: newColumns }
                                      });
                                    }}
                                    className="w-full p-2 border border-amber-300 rounded text-sm focus:ring-2 focus:ring-amber-500"
                                  >
                                    <option value="top">Top of Card (after title)</option>
                                    <option value="bottom">Bottom of Card (after content)</option>
                                  </select>
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                  <div>
                                    <label className="block text-xs font-medium text-amber-800 mb-1">Border Color</label>
                                    <div className="flex gap-1 items-center">
                                      <input
                                        type="color"
                                        value={card?.callout?.borderColor || '#94a3b8'}
                                        onChange={(e) => {
                                          const newColumns = [...columnsList];
                                          const newCards = [...column.cards];
                                          newCards[cardIndex] = {
                                            ...newCards[cardIndex],
                                            callout: { ...card.callout, borderColor: e.target.value }
                                          };
                                          newColumns[colIndex] = { ...newColumns[colIndex], cards: newCards };
                                          setFormData({
                                            ...formData,
                                            multiColumnData: { ...formData.multiColumnData, columns: newColumns }
                                          });
                                        }}
                                        className="h-8 w-full border border-amber-300 rounded"
                                      />
                                      <button
                                        type="button"
                                        onClick={() => {
                                          const newColumns = [...columnsList];
                                          const newCards = [...column.cards];
                                          newCards[cardIndex] = {
                                            ...newCards[cardIndex],
                                            callout: { ...card.callout, borderColor: undefined }
                                          };
                                          newColumns[colIndex] = { ...newColumns[colIndex], cards: newCards };
                                          setFormData({
                                            ...formData,
                                            multiColumnData: { ...formData.multiColumnData, columns: newColumns }
                                          });
                                        }}
                                        className="px-2 py-1 text-xs bg-amber-200 hover:bg-amber-300 rounded whitespace-nowrap"
                                      >
                                        Auto
                                      </button>
                                    </div>
                                  </div>
                                  <div>
                                    <label className="block text-xs font-medium text-amber-800 mb-1">Background Color</label>
                                    <div className="flex gap-1 items-center">
                                      <input
                                        type="color"
                                        value={card?.callout?.backgroundColor || '#f8fafc'}
                                        onChange={(e) => {
                                          const newColumns = [...columnsList];
                                          const newCards = [...column.cards];
                                          newCards[cardIndex] = {
                                            ...newCards[cardIndex],
                                            callout: { ...card.callout, backgroundColor: e.target.value }
                                          };
                                          newColumns[colIndex] = { ...newColumns[colIndex], cards: newCards };
                                          setFormData({
                                            ...formData,
                                            multiColumnData: { ...formData.multiColumnData, columns: newColumns }
                                          });
                                        }}
                                        className="h-8 w-full border border-amber-300 rounded"
                                      />
                                      <button
                                        type="button"
                                        onClick={() => {
                                          const newColumns = [...columnsList];
                                          const newCards = [...column.cards];
                                          newCards[cardIndex] = {
                                            ...newCards[cardIndex],
                                            callout: { ...card.callout, backgroundColor: undefined }
                                          };
                                          newColumns[colIndex] = { ...newColumns[colIndex], cards: newCards };
                                          setFormData({
                                            ...formData,
                                            multiColumnData: { ...formData.multiColumnData, columns: newColumns }
                                          });
                                        }}
                                        className="px-2 py-1 text-xs bg-amber-200 hover:bg-amber-300 rounded whitespace-nowrap"
                                      >
                                        Auto
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Checklist Items or Content */}
                          {card?.isChecklist ? (
                            <div className="space-y-2">
                              <label className="block text-sm font-medium text-gray-700 mb-2">Checklist Items:</label>
                              <div className="grid md:grid-cols-2 gap-3 bg-gray-50 p-3 rounded border border-gray-200">
                                <div>
                                  <label className="block text-xs font-medium text-gray-600 mb-1">List Style</label>
                                  <select
                                    value={listStyle}
                                    onChange={(e) => {
                                      const newColumns = [...columnsList];
                                      const newCards = [...column.cards];
                                      newCards[cardIndex] = { ...newCards[cardIndex], listStyle: e.target.value };
                                      newColumns[colIndex] = { ...newColumns[colIndex], cards: newCards };
                                      setFormData({
                                        ...formData,
                                        multiColumnData: { ...formData.multiColumnData, columns: newColumns }
                                      });
                                    }}
                                    className="w-full p-2 border border-gray-300 rounded text-sm"
                                  >
                                    <option value="checkbox">Checkbox (current)</option>
                                    <option value="numbered">Numbered (square badges)</option>
                                  </select>
                                </div>

                                {listStyle === 'numbered' && (
                                  <div className="space-y-2">
                                    <div className="flex items-center gap-2">
                                      <input
                                        type="checkbox"
                                        checked={badgeConfig.enabled || false}
                                        onChange={(e) => {
                                          const newColumns = [...columnsList];
                                          const newCards = [...column.cards];
                                          newCards[cardIndex] = {
                                            ...newCards[cardIndex],
                                            badge: { ...badgeConfig, enabled: e.target.checked }
                                          };
                                          newColumns[colIndex] = { ...newColumns[colIndex], cards: newCards };
                                          setFormData({
                                            ...formData,
                                            multiColumnData: { ...formData.multiColumnData, columns: newColumns }
                                          });
                                        }}
                                        className="h-4 w-4 text-blue-600 rounded border-gray-300"
                                      />
                                      <span className="text-sm font-medium text-gray-700">Enable Badge Color</span>
                                    </div>

                                    <div>
                                      <label className="block text-xs font-medium text-gray-600 mb-1">Badge Color Type</label>
                                      <select
                                        value={badgeConfig.type || 'theme'}
                                        disabled={!badgeConfig.enabled}
                                        onChange={(e) => {
                                          const newColumns = [...columnsList];
                                          const newCards = [...column.cards];
                                          newCards[cardIndex] = {
                                            ...newCards[cardIndex],
                                            badge: { ...badgeConfig, type: e.target.value }
                                          };
                                          newColumns[colIndex] = { ...newColumns[colIndex], cards: newCards };
                                          setFormData({
                                            ...formData,
                                            multiColumnData: { ...formData.multiColumnData, columns: newColumns }
                                          });
                                        }}
                                        className="w-full p-2 border border-gray-300 rounded text-sm"
                                      >
                                        <option value="solid">Solid</option>
                                        <option value="gradient">Gradient</option>
                                        <option value="theme">Theme Accent</option>
                                      </select>
                                    </div>

                                    {badgeConfig.enabled && badgeConfig.type === 'solid' && (
                                      <div>
                                        <label className="block text-xs font-medium text-gray-600 mb-1">Badge Color</label>
                                        <input
                                          type="color"
                                          value={badgeConfig.color || '#2563eb'}
                                          onChange={(e) => {
                                            const newColumns = [...columnsList];
                                            const newCards = [...column.cards];
                                            newCards[cardIndex] = {
                                              ...newCards[cardIndex],
                                              badge: { ...badgeConfig, color: e.target.value }
                                            };
                                            newColumns[colIndex] = { ...newColumns[colIndex], cards: newCards };
                                            setFormData({
                                              ...formData,
                                              multiColumnData: { ...formData.multiColumnData, columns: newColumns }
                                            });
                                          }}
                                          className="w-full h-10 p-1 border border-gray-300 rounded"
                                        />
                                      </div>
                                    )}

                                    {badgeConfig.enabled && badgeConfig.type === 'gradient' && (
                                      <div className="grid grid-cols-2 gap-2">
                                        <div>
                                          <label className="block text-xs font-medium text-gray-600 mb-1">Start Color</label>
                                          <input
                                            type="color"
                                            value={badgeConfig.color || '#2563eb'}
                                            onChange={(e) => {
                                              const newColumns = [...columnsList];
                                              const newCards = [...column.cards];
                                              newCards[cardIndex] = {
                                                ...newCards[cardIndex],
                                                badge: { ...badgeConfig, color: e.target.value }
                                              };
                                              newColumns[colIndex] = { ...newColumns[colIndex], cards: newCards };
                                              setFormData({
                                                ...formData,
                                                multiColumnData: { ...formData.multiColumnData, columns: newColumns }
                                              });
                                            }}
                                            className="w-full h-10 p-1 border border-gray-300 rounded"
                                          />
                                        </div>
                                        <div>
                                          <label className="block text-xs font-medium text-gray-600 mb-1">End Color</label>
                                          <input
                                            type="color"
                                            value={badgeConfig.color2 || badgeConfig.color || '#3b82f6'}
                                            onChange={(e) => {
                                              const newColumns = [...columnsList];
                                              const newCards = [...column.cards];
                                              newCards[cardIndex] = {
                                                ...newCards[cardIndex],
                                                badge: { ...badgeConfig, color2: e.target.value }
                                              };
                                              newColumns[colIndex] = { ...newColumns[colIndex], cards: newCards };
                                              setFormData({
                                                ...formData,
                                                multiColumnData: { ...formData.multiColumnData, columns: newColumns }
                                              });
                                            }}
                                            className="w-full h-10 p-1 border border-gray-300 rounded"
                                          />
                                        </div>
                                        <div className="col-span-2">
                                          <label className="block text-xs font-medium text-gray-600 mb-1">Gradient Direction</label>
                                          <select
                                            value={badgeConfig.gradientDirection || 'to right'}
                                            onChange={(e) => {
                                              const newColumns = [...columnsList];
                                              const newCards = [...column.cards];
                                              newCards[cardIndex] = {
                                                ...newCards[cardIndex],
                                                badge: { ...badgeConfig, gradientDirection: e.target.value }
                                              };
                                              newColumns[colIndex] = { ...newColumns[colIndex], cards: newCards };
                                              setFormData({
                                                ...formData,
                                                multiColumnData: { ...formData.multiColumnData, columns: newColumns }
                                              });
                                            }}
                                            className="w-full p-2 border border-gray-300 rounded text-sm"
                                          >
                                            <option value="to right">Left to Right</option>
                                            <option value="to bottom">Top to Bottom</option>
                                            <option value="to bottom right">Diagonal</option>
                                          </select>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                              {(card.checklistItems || []).map((item, itemIndex) => (
                                <div key={itemIndex} className="flex gap-2 items-start p-2 bg-gray-50 rounded">
                                  <input
                                    type="checkbox"
                                    checked={item.checked || false}
                                    onChange={(e) => {
                                      const newColumns = [...columnsList];
                                      const newCards = [...column.cards];
                                      const newItems = [...card.checklistItems];
                                      newItems[itemIndex] = { ...newItems[itemIndex], checked: e.target.checked };
                                      newCards[cardIndex] = { ...newCards[cardIndex], checklistItems: newItems };
                                      newColumns[colIndex] = { ...newColumns[colIndex], cards: newCards };
                                      setFormData({
                                        ...formData,
                                        multiColumnData: { ...formData.multiColumnData, columns: newColumns }
                                      });
                                    }}
                                    className="mt-1 h-4 w-4 text-blue-600 rounded border-gray-300"
                                  />
                                  <div className="flex-1 space-y-1">
                                    <input
                                      type="text"
                                      placeholder="Item text (e.g., 'Layout locked')"
                                      value={item.text || ''}
                                      onChange={(e) => {
                                        const newColumns = [...columnsList];
                                        const newCards = [...column.cards];
                                        const newItems = [...card.checklistItems];
                                        newItems[itemIndex] = { ...newItems[itemIndex], text: e.target.value };
                                        newCards[cardIndex] = { ...newCards[cardIndex], checklistItems: newItems };
                                        newColumns[colIndex] = { ...newColumns[colIndex], cards: newCards };
                                        setFormData({
                                          ...formData,
                                          multiColumnData: { ...formData.multiColumnData, columns: newColumns }
                                        });
                                      }}
                                      className="w-full p-2 border border-gray-300 rounded text-sm"
                                    />
                                    <input
                                      type="text"
                                      placeholder="Description (e.g., 'cameras + staging approved')"
                                      value={item.description || ''}
                                      onChange={(e) => {
                                        const newColumns = [...columnsList];
                                        const newCards = [...column.cards];
                                        const newItems = [...card.checklistItems];
                                        newItems[itemIndex] = { ...newItems[itemIndex], description: e.target.value };
                                        newCards[cardIndex] = { ...newCards[cardIndex], checklistItems: newItems };
                                        newColumns[colIndex] = { ...newColumns[colIndex], cards: newCards };
                                        setFormData({
                                          ...formData,
                                          multiColumnData: { ...formData.multiColumnData, columns: newColumns }
                                        });
                                      }}
                                      className="w-full p-2 border border-gray-300 rounded text-sm"
                                    />
                                  </div>
                                  {card.checklistItems.length > 1 && (
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const newColumns = [...columnsList];
                                        const newCards = [...column.cards];
                                        const newItems = [...card.checklistItems];
                                        newItems.splice(itemIndex, 1);
                                        newCards[cardIndex] = { ...newCards[cardIndex], checklistItems: newItems };
                                        newColumns[colIndex] = { ...newColumns[colIndex], cards: newCards };
                                        setFormData({
                                          ...formData,
                                          multiColumnData: { ...formData.multiColumnData, columns: newColumns }
                                        });
                                      }}
                                      className="px-2 py-1 bg-red-500 text-white text-xs rounded hover:bg-red-600"
                                    >
                                      ×
                                    </button>
                                  )}
                                </div>
                              ))}
                              <button
                                type="button"
                                onClick={() => {
                                  const newColumns = [...columnsList];
                                  const newCards = [...column.cards];
                                  const newItems = [...(card.checklistItems || []), { text: '', description: '', checked: false }];
                                  newCards[cardIndex] = { ...newCards[cardIndex], checklistItems: newItems };
                                  newColumns[colIndex] = { ...newColumns[colIndex], cards: newCards };
                                  setFormData({
                                    ...formData,
                                    multiColumnData: { ...formData.multiColumnData, columns: newColumns }
                                  });
                                }}
                                className="w-full p-2 border-2 border-dashed border-gray-300 rounded text-sm text-gray-600 hover:border-gray-400 hover:text-gray-700 hover:bg-gray-50 transition-colors"
                              >
                                + Add Checklist Item
                              </button>
                            </div>
                          ) : (
                            <div className="border border-gray-200 rounded-lg overflow-hidden">
                              <div className="bg-gray-50 px-3 py-2 border-b border-gray-200">
                                <span className="text-sm font-medium text-gray-700">Card Content</span>
                              </div>
                              <RichTextEditor
                                content={card?.content || ''}
                                onChange={(content) => {
                                  const newColumns = [...columnsList];
                                  const newCards = [...column.cards];
                                  newCards[cardIndex] = { ...newCards[cardIndex], content };
                                  newColumns[colIndex] = { ...newColumns[colIndex], cards: newCards };
                                  setFormData({
                                    ...formData,
                                    multiColumnData: { ...formData.multiColumnData, columns: newColumns }
                                  });
                                }}
                                isHtmlMode={false}
                                onToggleHtmlMode={() => {}}
                                isPreviewMode={false}
                              />
                            </div>
                          )}
                        </div>
                      </details>
                      </div>

                            {/* Drop Zone Below Card (for last card or when dragging) */}
                            {cardIndex === column.cards.length - 1 && (
                              <div
                                onDragOver={(e) => handleDropZoneDragOver(e, colIndex, cardIndex + 1)}
                                onDragLeave={handleDropZoneDragLeave}
                                onDrop={(e) => handleDrop(e, colIndex, cardIndex + 1, handleMoveCard)}
                                className={`h-2 transition-all mt-2 ${
                                  dropTarget?.colIndex === colIndex && dropTarget?.cardIndex === cardIndex + 1
                                    ? 'h-12 bg-indigo-200 border-2 border-dashed border-indigo-500 rounded-lg'
                                    : 'h-0'
                                }`}
                              />
                            )}
                          </div>
                          );
                      })}

                      {/* Add Card button */}
                      <button
                        type="button"
                        onClick={() => {
                          const newColumns = [...columnsList];
                          const newCards = [...(column.cards || []), {
                            title: '',
                            primaryTitle: '',
                            categoryLabel: '',
                            instructionalText: '',
                            numberedList: [],
                            numberedListStyle: 'numbered',
                            titleAlignment: 'center',
                            iconName: '',
                            content: '',
                            isChecklist: false,
                            checklistItems: [],
                            backgroundColor: 'transparent',
                            borderColor: '#94a3b8',
                            showBorder: true,
                            titleColor: undefined,
                            checkmarkColor: undefined,
                            listStyle: 'checkbox',
                            badge: { enabled: false, type: 'theme', gradientDirection: 'to right' },
                            callout: { enabled: false, title: '', description: '', borderColor: undefined, backgroundColor: undefined, position: 'top' },
                            columnSpan: 1
                          }];
                          newColumns[colIndex] = { ...newColumns[colIndex], cards: newCards };
                          setFormData({
                            ...formData,
                            multiColumnData: { ...formData.multiColumnData, columns: newColumns }
                          });
                        }}
                        className="w-full p-4 border-2 border-dashed border-indigo-300 rounded-xl bg-white text-indigo-700 hover:border-indigo-500 hover:bg-indigo-50 hover:text-indigo-900 transition-all flex items-center justify-center gap-2 font-semibold shadow-sm hover:shadow"
                      >
                        <Plus size={18} strokeWidth={3} />
                        Add Card to Column {colIndex + 1}
                      </button>
                    </div>
                    </div>
                  );
                    });
                  })()}

                  {/* Add Column button */}
                  <button
                  type="button"
                  onClick={() => {
                    const currentColumns = formData.multiColumnData?.columns || [];
                    const newColumns = [
                      ...currentColumns,
                      {
                        widthPercent: 100,
                        cards: [{
                          title: '',
                          primaryTitle: '',
                          categoryLabel: '',
                          instructionalText: '',
                          numberedList: [],
                          numberedListStyle: 'numbered',
                          titleAlignment: 'center',
                          iconName: '',
                          content: '',
                          isChecklist: false,
                          checklistItems: [],
                          backgroundColor: 'transparent',
                          borderColor: '#94a3b8',
                          showBorder: true,
                          titleColor: undefined,
                          checkmarkColor: undefined,
                          listStyle: 'checkbox',
                          badge: { enabled: false, type: 'theme', gradientDirection: 'to right' },
                          callout: { enabled: false, title: '', description: '', borderColor: undefined, backgroundColor: undefined, position: 'top' },
                          columnSpan: 1
                        }]
                      }
                    ];
                    setFormData({
                      ...formData,
                      multiColumnData: { ...formData.multiColumnData, columns: newColumns }
                    });
                  }}
                  className="w-full p-5 border-3 border-dashed border-indigo-400 rounded-xl bg-gradient-to-r from-indigo-50 to-blue-50 text-indigo-700 hover:border-indigo-600 hover:from-indigo-100 hover:to-blue-100 hover:text-indigo-900 transition-all flex items-center justify-center gap-3 font-bold text-lg shadow-md hover:shadow-lg"
                >
                  <div className="bg-indigo-600 text-white rounded-lg p-2">
                    <Plus size={20} strokeWidth={3} />
                  </div>
                  Add New Column
                </button>
                </div>
              </div>

              {/* Tips Section */}
              <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border-2 border-emerald-300 rounded-xl p-5 shadow-sm">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 bg-emerald-500 text-white rounded-lg p-2">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h4 className="font-bold text-emerald-900 mb-3 text-lg">💡 Pro Tips for Multi-Column Cards</h4>
                    <ul className="text-sm text-emerald-800 space-y-2">
                      <li className="flex items-start gap-2">
                        <span className="text-emerald-600 font-bold flex-shrink-0">✓</span>
                        <span><strong>Flexible Layouts:</strong> Create dynamic multi-column designs</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-emerald-600 font-bold flex-shrink-0">✓</span>
                        <span><strong>Checklist Cards:</strong> Perfect for milestones, tasks, or requirements</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-emerald-600 font-bold flex-shrink-0">✓</span>
                        <span><strong>Content Cards:</strong> Great for explanatory content or notes</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-emerald-600 font-bold flex-shrink-0">✓</span>
                        <span><strong>Custom Styling:</strong> Customize colors and borders for each card</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-emerald-600 font-bold flex-shrink-0">✓</span>
                        <span><strong>Column Widths:</strong> Adjust widths to create asymmetric layouts</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-emerald-600 font-bold flex-shrink-0">✓</span>
                        <span><strong>Copy & Paste:</strong> Reuse cards across different blocks</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          </div>

          <div className="p-6 border-t-2 border-gray-200 bg-gradient-to-r from-gray-50 to-slate-50 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center gap-2 px-6 py-3 text-gray-700 border-2 border-gray-300 rounded-xl hover:bg-white hover:border-gray-400 transition-all font-semibold shadow-sm hover:shadow"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isSubmitDisabled}
              className={`inline-flex items-center gap-2 px-6 py-3 rounded-xl transition-all font-semibold shadow-md hover:shadow-lg ${
                isSubmitDisabled
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-gradient-to-r from-slate-700 to-slate-800 text-white hover:from-slate-800 hover:to-slate-900'
              }`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Save Block
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const ZOOM_LEVELS = [0.75, 0.9, 1, 1.1, 1.25, 1.5];

/**
 * The outline drawer's slide thumbnail, chosen once per page load.
 *
 * Read at module scope rather than per render because the preference only
 * changes on reload anyway (see utils/thumbnailMode.js), and because swapping
 * thumbnail implementations mid-session would remount every row in the drawer.
 */
const DrawerSlideThumbnail = isLiveThumbnailMode() ? SlideLiveThumbnail : SlideThumbnailPreview;

/**
 * How far the outline has to be scrolled before its "Top" button appears.
 * About a screenful of section rows: far enough that the way back is no
 * longer obvious, and not so eager that the button flickers in on a nudge.
 */
const DRAWER_TOP_JUMP_THRESHOLD = 240;

// Main Application Component
/**
 * Validate -> migrate -> repair -> validate.
 *
 * At module scope because BOTH ways into the app need it. The in-app Open menu
 * always ran it; the landing screen's "Load Presentation" did not — it handed
 * the parsed file straight to normalizeLectureJson, so a deck opened that way
 * skipped every repair. That is how a custom HTML/CSS block flattened by an
 * older save stayed flattened on the one path most people use to reopen work.
 */
const runDeckPipelineFor = (data, contextLabel) => {
  const initialValidation = validateDeck(data);
  let working = data;
  let migrations = [];
  if (!working?.version || working.version !== '4.0.0') {
    const migration = migrateDeckTo400(working);
    if (!migration.ok) {
      return { ok: false, errors: migration.errors, repairs: [], migrations: [] };
    }
    working = migration.deck;
    migrations = migration.migrations;
  }

  const { deck: repaired, repairs } = repairDeck(working);
  const finalValidation = validateDeck(repaired);

  if (!finalValidation.ok) {
    console.error(`[${contextLabel}] Deck validation failed`, finalValidation.errors);
    return { ok: false, errors: finalValidation.errors, repairs, migrations };
  }

  if (!initialValidation.ok) {
    console.warn(`[${contextLabel}] Deck repaired`, { repairs, migrations });
  }

  return { ok: true, deck: repaired, repairs, migrations };
};

/**
 * Put a deck through the pipeline before it becomes state, falling back to the
 * raw file if it cannot pass. A deck that fails validation is still better
 * shown than swallowed — normalizeLectureJson downstream does what it can.
 */
const repairDeckForLoad = (data, contextLabel) => {
  if (!data || typeof data !== 'object') return data;
  const looksLikeDeck = data.format === 'boppps_lesson' || data.schema_type === 'full_deck' || data.version;
  if (!looksLikeDeck) return data;
  const result = runDeckPipelineFor(data, contextLabel);
  if (!result.ok) {
    console.warn(`[${contextLabel}] Deck could not be repaired; loading as-is`, result.errors);
    return data;
  }
  return result.deck;
};

const LectureTemplateSystem = ({ initialData, onLoadPresentation, showSettingsPage, activeAppTab, lightMode = false, onToggleLightMode }) => {
  const [isEditMode, setIsEditMode] = useState(false);
  // Start collapsed so the slide gets the full width on first load. The edge
  // tab on the right stays visible and labelled, so the panel is still
  // discoverable — see ToolbarDrawer's collapsed affordance.
  const [isToolbarDrawerOpen, setIsToolbarDrawerOpen] = useState(false);
  const [showInteractiveTools, setShowInteractiveTools] = useState(false);
  const [isHotkeysOpen, setIsHotkeysOpen] = useState(false);

  /**
   * The deck handed in by the landing screen, turned into something the editor
   * can hold — or, if that is impossible, the reasons why.
   *
   * The failure branch used to return null and log to the console, which meant
   * the editor mounted on its default empty deck: the file looked like it had
   * opened and lost everything. The reasons are kept here and raised as the
   * failure panel by the effect below.
   */
  const initialLoad = useMemo(() => {
    if (!initialData) return { deck: null, failure: null };
    // Repaired first — this is the landing screen's "Load Presentation" path.
    const result = normalizeLectureJson(repairDeckForLoad(initialData, 'initial-load'));
    if (!result.success || !result.normalized) {
      console.error('[LectureTemplateSystem] Failed to normalize initial data', result.issues);
      return { deck: null, failure: result.issues || [] };
    }
    if (result.issues.length) {
      console.warn('[LectureTemplateSystem] Normalized initial data with issues:', result.issues);
    }
    return { deck: result.normalized, failure: null };
  }, [initialData]);

  const normalizedInitialData = initialLoad.deck;

  const [week, setWeek] = useState(() => safeString(normalizedInitialData?.week, '1'));
  const [date, setDate] = useState(() => normalizedInitialData?.date || new Date().toISOString().split('T')[0]);
  const [isControlPanelOpen, setIsControlPanelOpen] = useState(false);
  const [saveIndicator, setSaveIndicator] = useState({ show: false, message: '', type: '' });
  // A long export needs to say it is still going. The save indicator hides
  // itself after three seconds, which is fine for "Saved" and useless for a
  // job that runs for a minute, so exports report through this instead.
  const [exportProgress, setExportProgress] = useState(null);
  const [modeToast, setModeToast] = useState({ visible: false, message: '', isEdit: false });
  const modeToastTimerRef = useRef(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [modalContentType, setModalContentType] = useState('');
  const [modalInitialData, setModalInitialData] = useState({});
  const [htmlModes, setHtmlModes] = useState({});
  const [modalHtmlMode, setModalHtmlMode] = useState(false);
  const [showAutoSaveRecovery, setShowAutoSaveRecovery] = useState(false);
  const [autoSaveData, setAutoSaveData] = useState(null);
  const [defaultSection, setDefaultSection] = useState('bridge-in');
  const [activeSectionId, setActiveSectionId] = useState('');
  const [openSectionIds, setOpenSectionIds] = useState(['bridge-in']); // Can hold multiple IDs
  const [showToolbar, setShowToolbar] = useState(true);
  const [isFullScreenPreview, setIsFullScreenPreview] = useState(false);
  // Full screen can drop every overlay control — slide counter, mode banner,
  // zoom stepper, exit button and both drawer arrows — leaving nothing but the
  // slide. Collapse with the chevron or H, restore the same way.
  const [isFullScreenChromeHidden, setIsFullScreenChromeHidden] = useState(false);
  // Present mode is full screen with the editor's affordances stood down: no
  // edit chrome, browser fullscreen requested, and Space/PageDown advancing the
  // deck so a USB slide clicker (which sends exactly those keys) just works.
  const [isPresenting, setIsPresenting] = useState(false);
  const presentReturnStateRef = useRef({ editMode: false });
  // Scale that brings the whole slide inside the viewport while presenting.
  const presentStageRef = useRef(null);
  const [presentFitScale, setPresentFitScale] = useState(1);
  const [bodyLayout, setBodyLayout] = useState(() => normalizedInitialData?.bodyLayout || 'slides');
  const [bodyBackground, setBodyBackground] = useState(() => normalizedInitialData?.bodyBackground || 'white');
  const [slideBackgrounds, setSlideBackgrounds] = useState(() => normalizedInitialData?.slideBackgrounds || {});
  const [titleCardBackground, setTitleCardBackground] = useState(() => normalizedInitialData?.titleCardBackground || null);

  // Sync settings between the settings instance and the slides instance.
  // MainApp renders two separate LessonTemplate instances; custom events bridge them.
  const syncToSlides = useCallback((key, value) => {
    if (showSettingsPage) {
      window.dispatchEvent(new CustomEvent('boppps-settings-sync', { detail: { key, value } }));
    }
  }, [showSettingsPage]);

  /**
   * What the settings page is allowed to change on the real deck.
   *
   * This list was six keys long, and everything the Configure tab offered
   * outside it was editing a copy nobody could see: typing an instructor name,
   * a course title, a deck name or a title card there changed that instance's
   * own state and stopped. The deck kept its old values, and so did every
   * export — which is why exports go and ask the slides instance for the live
   * payload (see exportPayloadSync) rather than using the page's own copy.
   *
   * So the list now covers everything the page can edit. A control that cannot
   * reach the deck is not a setting, it is a decoration, and the honest fix
   * for a settings page full of them is to connect them rather than to remove
   * them.
   *
   * Still one-way, settings -> slides, which is the limitation this mechanism
   * has always had: the slides instance does not report back, so the Configure
   * tab can show values that are behind if the same thing was edited there.
   * Narrowing that gap means one deck state instead of two, which is a change
   * to how MainApp mounts the app rather than to this bridge.
   */
  useEffect(() => {
    if (showSettingsPage) return; // Only the slides instance listens
    const handleSync = (e) => {
      const { key, value } = e.detail;
      const setters = {
        bodyBackground: setBodyBackground,
        slideBackgrounds: setSlideBackgrounds,
        titleCardBackground: setTitleCardBackground,
        slideHeaderMode: setSlideHeaderMode,
        slideHeaderColor: setSlideHeaderColor,
        slideGlobalCss: setSlideGlobalCss,
        // Session details, printed on the title cards and the exports.
        headerData: setHeaderData,
        week: setWeek,
        date: setDate,
        // The deck's own name, and the cards at either end of it.
        slideDeckName: setSlideDeckName,
        titleCardSettings: setTitleCardSettings,
        endTitleCardSettings: setEndTitleCardSettings,
        // Per-slide metadata.
        slideNames: setSlideNames,
        slideSubtitles: setSlideSubtitles,
        slideDurations: setSlideDurations,
        slideVisibility: setSlideVisibility,
        // The furniture along the bottom of a slide, and the design switches.
        slideNumbersEnabled: setSlideNumbersEnabled,
        deckNameBadgeEnabled: setDeckNameBadgeEnabled,
        deckNameOnContentSlides: setDeckNameOnContentSlides,
        slideDesignMode: setSlideDesignMode,
        showAspectGuides: setShowAspectGuides,
      };
      setters[key]?.(value);
    };
    window.addEventListener('boppps-settings-sync', handleSync);
    return () => window.removeEventListener('boppps-settings-sync', handleSync);
  }, [showSettingsPage]);

  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [isNavDrawerOpen, setIsNavDrawerOpen] = useState(true);
  const [sectionDividerSettings, setSectionDividerSettings] = useState(null);
  const [isCanvasMaximized, setIsCanvasMaximized] = useState(false);
  const [isAppHeaderCollapsed, setIsAppHeaderCollapsed] = useState(false);
  const preMaximizeRef = useRef({ toolbar: true, nav: true });
  const [slideZoom, setSlideZoom] = useState(1);
  // Extra zoom applied on top of the full-screen fit-to-viewport base (1 = fit)
  const [fullScreenZoom, setFullScreenZoom] = useState(1);
  // Sideways offset of the slide on the full-screen stage, in screen pixels
  // (0 = centred). The left/right arrows walk it; it clears whenever the slide
  // or the zoom changes, so no slide ever opens already shoved off-centre.
  const [fullScreenPanX, setFullScreenPanX] = useState(0);
  // Drawer open-state captured on entering full screen, restored on exit
  const preFullScreenDrawersRef = useRef({ toolbar: true, nav: true });
  const [drawerExpandedSections, setDrawerExpandedSections] = useState(['bridge-in']);
  const [drawerCollapsedSections, setDrawerCollapsedSections] = useState([]);
  const [drawerFilterText, setDrawerFilterText] = useState('');
  const [drawerIssuesOnly, setDrawerIssuesOnly] = useState(false);
  // What has been typed into the outline's "Go to" box. Kept as the raw string
  // rather than a number so a half-typed value is still what the box shows.
  const [drawerSlideJump, setDrawerSlideJump] = useState('');
  // Whether the outline has been scrolled far enough down for the "Top"
  // button to be worth showing. See handleDrawerScroll.
  const [showDrawerTopJump, setShowDrawerTopJump] = useState(false);
  const [drawerSlideMenu, setDrawerSlideMenu] = useState(null);
  const [drawerSlideMenuPosition, setDrawerSlideMenuPosition] = useState({ top: 0, left: 0, visibility: 'hidden' });
  const [slideClipboard, setSlideClipboard] = useState(null);
  const [formatPainter, setFormatPainter] = useState(null);
  const [showNewSlideMenu, setShowNewSlideMenu] = useState(false);
  const newSlideMenuRef = useRef(null);
  const newSlideMenuButtonRef = useRef(null);
  const drawerSlideMenuRef = useRef(null);
  const drawerSlideMenuButtonRef = useRef(null);
  const navDrawerRef = useRef(null);
  // Set when Delete removes a slide, so the outline can take focus back off the
  // row that just stopped existing. See handleSlideRowKeyDown.
  const focusSlideRowAfterDeleteRef = useRef(false);
  const focusSlideRowTimerRef = useRef(null);
  // The outline can be far longer than the panel, so the slide you are on is
  // scrolled back into view whenever it changes.
  const activeSlideItemRef = useRef(null);
  const navDrawerTriggerRef = useRef(null);
  const wasNavDrawerOpenRef = useRef(false);

  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showHtmlExportMenu, setShowHtmlExportMenu] = useState(false);
  const [showSettingsMenu, setShowSettingsMenu] = useState(false);
  const [showSaveMenu, setShowSaveMenu] = useState(false);
  const [showOpenMenu, setShowOpenMenu] = useState(false);
  const [showTemplateManager, setShowTemplateManager] = useState(false);
  const [showAdvancedExports, setShowAdvancedExports] = useState(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem('showAdvancedExports') === 'true';
  });
  const exportMenuRef = useRef(null);
  const exportMenuButtonRef = useRef(null);
  const htmlExportMenuRef = useRef(null);
  const htmlExportMenuButtonRef = useRef(null);
  const settingsMenuRef = useRef(null);
  const settingsMenuButtonRef = useRef(null);
  const saveMenuRef = useRef(null);
  const saveMenuButtonRef = useRef(null);
  const openMenuRef = useRef(null);
  const openMenuButtonRef = useRef(null);
  const toolbarButtonClass = 'min-w-[128px] h-10 inline-flex items-center justify-center gap-2 px-2.5 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-blue-50 rounded-lg transition-colors border border-blue-100 bg-white';
  const toolbarHighlightButtonClass = `${toolbarButtonClass} bg-blue-50 text-blue-800 border-blue-200 hover:bg-blue-100 hover:border-blue-300 hover:text-blue-900`;
  const primaryActionButtonClass = 'inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-semibold text-white rounded-lg shadow-sm h-11 min-w-[210px]';
  const [slideHeaderMode, setSlideHeaderMode] = useState('single');
  const [slideHeaderColor, setSlideHeaderColor] = useState('#545f7d');
  const [slideHeaderColors, setSlideHeaderColors] = useState({});
  const [slideGlobalCss, setSlideGlobalCss] = useState('');
  const [titleCardSettings, setTitleCardSettings] = useState(DEFAULT_TITLE_CARD_SETTINGS);
  const [endTitleCardSettings, setEndTitleCardSettings] = useState(DEFAULT_END_TITLE_CARD_SETTINGS);
  const [slideVisibility, setSlideVisibility] = useState({});
  const [slideNames, setSlideNames] = useState({}); // Store custom names for subsection slides
  // A slide's own header subtitle. Like slideNames, it is keyed by slideId, so
  // editing the header of one slide leaves the rest of the section alone; the
  // section's student-friendly line is only the fallback.
  const [slideSubtitles, setSlideSubtitles] = useState({});
  const [slideDurations, setSlideDurations] = useState({});
  /**
   * Slides the author has said are meant to be blank, keyed by slide id.
   *
   * A slide with no blocks is normally flagged as missing content, which is
   * what the drawer's issue filter is for. A section emptied by a move is the
   * one case where that is wrong: the blank is a deliberate answer to the
   * prompt, not a slide someone forgot. Persisted with the deck so the answer
   * survives a save and reload — an acknowledgement that reappeared as a
   * warning next session would be worse than never asking.
   */
  const [acknowledgedBlankSlides, setAcknowledgedBlankSlides] = useState({});
  // { sectionId, sectionTitle, blankSlideId } while the prompt is up.
  const [emptiedSectionPrompt, setEmptiedSectionPrompt] = useState(null);
  // The slide "New section after this slide..." was asked for, or null while the
  // dialog is closed. Both entry points set this and nothing else, so there is
  // one path into the operation. See openCreateSectionModal.
  const [createSectionRequest, setCreateSectionRequest] = useState(null);
  const [slideDeckName, setSlideDeckName] = useState('Slide Deck');
  const [exportFileName, setExportFileName] = useState('');
  const [htmlExportTitle, setHtmlExportTitle] = useState('');
  const [exportVersion, setExportVersion] = useState(1);
  const [exportVersionInput, setExportVersionInput] = useState('');
  const [prefilledVersionInput, setPrefilledVersionInput] = useState(false);
  const exportVersionRef = useRef(1);
  /**
   * Does `exportVersionRef` describe a version that has actually been written?
   *
   * The ref itself cannot say: it holds 1 for a brand-new deck as well as for a
   * deck saved once, and those need opposite treatment. A deck that has never
   * been saved has its first Save create v1 and gets no overwrite warning; a
   * deck sitting at v1 has Save rewrite v1 and does. Set when a deck is opened
   * at a known version, and after any save that lands.
   */
  const deckHasSavedVersionRef = useRef(false);
  /** The pending "Save is about to replace a file" question, or null. */
  const [overwritePrompt, setOverwritePrompt] = useState(null);
  const stableLessonMetaRef = useRef({
    id: generateId(),
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  });

  const [showLogoSettings, setShowLogoSettings] = useState(false);
  const [showTitleCardModal, setShowTitleCardModal] = useState(false);
  const [titleCardModalTarget, setTitleCardModalTarget] = useState('start');
  // Draggable position of the Customize Title Card panel. null = default spot
  // (docked to the right). Dragging its header sets an explicit {x, y}.
  const [titleModalPos, setTitleModalPos] = useState(null);
  const titleModalDragRef = useRef(null);

  const [jsonPasteModal, setJsonPasteModal] = useState({ open: false, blockId: null, text: '' });
  const [showJsonInput, setShowJsonInput] = useState(false);
  // Why a paste was refused, shown in the modal rather than in the toolbar
  // indicator — the box the text is in is where the reason belongs.
  const [pasteJsonError, setPasteJsonError] = useState('');
  // Why a *file* would not open. The toolbar indicator clears itself after
  // three seconds, which is no use when the answer is "your file is truncated
  // at line 812" — so a failed open raises a panel that stays put, with the
  // deck you already had still open behind it.
  const [deckLoadError, setDeckLoadError] = useState(null);

  // A deck that arrived from the landing screen and could not be turned into
  // editor state. Without this the editor simply comes up empty, which reads as
  // "the app ate my lesson" rather than "that file is damaged".
  useEffect(() => {
    if (!initialLoad.failure) return;
    setDeckLoadError(describeLoadError(
      incompleteDeckError(initialLoad.failure, {
        message: 'The deck could not be turned into a lesson'
      }),
      { source: 'file', startedBlank: true }
    ));
  }, [initialLoad]);

  // Where Save writes: the file it last wrote, and the folder "Save +" puts the
  // next version in. Restored from IndexedDB, so reopening the app still knows
  // where this deck lives.
  const saveLocation = useSaveLocation();
  // A save that failed, together with the bytes it was trying to write, so
  // whichever way out the user picks still writes the deck they pressed Save
  // on rather than a rebuilt one.
  const [saveProblem, setSaveProblem] = useState(null);
  const [saveProblemBusy, setSaveProblemBusy] = useState(false);
  // The last file this deck was written to. `lastFileSave` stays put — it is
  // what the toolbar reports, so "did that save?" can be answered at any
  // moment rather than only in the seconds after pressing the button.
  // `saveConfirmation` is the same news as a banner, and goes away by itself.
  const [lastFileSave, setLastFileSave] = useState(null);
  const [saveConfirmation, setSaveConfirmation] = useState(null);
  // True once the deck has been edited since that save, so the toolbar can
  // stop claiming the file on disk matches what is on screen.
  const [editedSinceSave, setEditedSinceSave] = useState(false);
  // Stable on purpose: the banner's dismissal timer is keyed on this, and a
  // fresh arrow every render would restart the countdown on every render —
  // in a busy editor the banner would then never leave on its own.
  const handleDismissSaveConfirmation = useCallback(() => setSaveConfirmation(null), []);
  const [jsonInput, setJsonInput] = useState('');
  const [showSelectionModal, setShowSelectionModal] = useState(false);
  const [selectionMode, setSelectionMode] = useState('export');
  const [selectionType, setSelectionType] = useState('blocks');
  const [selectedBlockIds, setSelectedBlockIds] = useState({});
  const [selectedSlideIds, setSelectedSlideIds] = useState({});
  const [collapsedExportSections, setCollapsedExportSections] = useState({});
  const [selectionImportData, setSelectionImportData] = useState(null);
  const [selectionTargetSection, setSelectionTargetSection] = useState('');
  const [selectionPlacementMode, setSelectionPlacementMode] = useState('append');
  const [selectionPlacementSlideId, setSelectionPlacementSlideId] = useState('');
  const [blockTargetSlideId, setBlockTargetSlideId] = useState('');
  const [importSelectionSlideIds, setImportSelectionSlideIds] = useState({});
  const [recentlyImportedSlideIds, setRecentlyImportedSlideIds] = useState([]);
  const selectionFileInputRef = useRef(null);
  const [draggingSlide, setDraggingSlide] = useState(null);
  const [slideDropTarget, setSlideDropTarget] = useState(null);
  const [isSectionReorderOpen, setIsSectionReorderOpen] = useState(false);
  // Which section's slides the reorder dialog is listing, if any.
  const [slideReorderSectionId, setSlideReorderSectionId] = useState(null);
  // Slides picked out in the outline, by id, so several can be dragged at
  // once. Ids rather than positions: a move renumbers positions, and this has
  // to survive the move it is being used for.
  const [pickedSlideIds, setPickedSlideIds] = useState([]);
  // Where a Shift range measures from — the last slide clicked on its own.
  const pickAnchorRef = useRef(null);
  const [draggingSection, setDraggingSection] = useState(null);
  const [sectionDropTarget, setSectionDropTarget] = useState(null);
  const [newSectionPlacement, setNewSectionPlacement] = useState('before:resources');
  const [newSectionName, setNewSectionName] = useState('');

  const isDraggingInDrawer = Boolean(draggingSlide || draggingSection);

  /**
   * Set by a move to say "leave the outline's scroll alone this time".
   *
   * A drop ends the drag, and ending the drag used to hand the scroll straight
   * back to the effect below, which pulls the *selected* slide into view. The
   * selected slide is almost never the one just dragged, so dropping a slide
   * two thirds of the way down a deck threw the list back to wherever the
   * current slide happened to be — reported as the drawer jumping to the top,
   * which is exactly what it does when slide 1 is the one you are on.
   *
   * The list is already showing the place the slide landed, because that is
   * where the pointer was. So the fix is to do nothing rather than to scroll
   * somewhere better.
   */
  const holdDrawerScrollRef = useRef(false);

  // Keep the current slide visible in the outline. `block: 'nearest'` leaves
  // the list alone when the slide is already on screen, so navigating between
  // neighbouring slides doesn't make the panel jump.
  //
  // Declared here rather than up with the other drawer state because it has to
  // see the drag flags below it.
  useEffect(() => {
    if (!isNavDrawerOpen) return;
    if (holdDrawerScrollRef.current) {
      holdDrawerScrollRef.current = false;
      return;
    }
    // Never while a drag is in flight. Dropping a slide into a collapsed
    // section opens it, and that is a real expansion change — but scrolling
    // the *selected* slide back into view mid-drag moves the list out from
    // under the pointer and fights the drag's own auto-scroll. The drag owns
    // the scroll position until it ends.
    if (isDraggingInDrawer) return;
    const node = activeSlideItemRef.current;
    if (!node) return;
    const id = window.requestAnimationFrame(() => {
      node.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    });
    return () => window.cancelAnimationFrame(id);
  }, [isNavDrawerOpen, currentSlideIndex, drawerExpandedSections, drawerCollapsedSections, isDraggingInDrawer]);

  /**
   * Open or close a section in the outline.
   *
   * withSectionMembership hands back the same array when nothing changes, which
   * is load-bearing rather than tidy: ensureSectionOpenForDrop calls this on
   * every dragover, and a fresh array each time restarted the current-slide
   * scroll below and made the outline lurch under the drag. See that module.
   */
  const setDrawerSectionExpansion = useCallback((sectionId, expand) => {
    setDrawerCollapsedSections((prev) => withSectionMembership(prev, sectionId, !expand));
    setDrawerExpandedSections((prev) => withSectionMembership(prev, sectionId, expand));
  }, []);

  const ensureSectionOpenForDrop = useCallback((sectionId) => {
    if (!sectionId) return;
    setDrawerSectionExpansion(sectionId, true);
  }, [setDrawerSectionExpansion]);

  const handleZoomIn = () => {
    setSlideZoom(prev => {
      const nextLevel = ZOOM_LEVELS.find(z => z > prev + 0.001);
      return nextLevel || prev;
    });
  };
  const handleZoomOut = () => {
    setSlideZoom(prev => {
      const nextLevel = [...ZOOM_LEVELS].reverse().find(z => z < prev - 0.001);
      return nextLevel || prev;
    });
  };
  const handleZoomReset = () => setSlideZoom(1);

  // A transform paints at the scaled size but still occupies its unscaled
  // layout box, so the zoom viewport has to be told how tall the content really
  // is or the bottom of a zoomed-in deck would be cut off. offsetHeight is the
  // untransformed height, which is exactly what we want to multiply.
  const slideZoomStageRef = useRef(null);
  const slideZoomViewportRef = useRef(null);
  const [slideZoomNaturalHeight, setSlideZoomNaturalHeight] = useState(0);

  /**
   * Scale the slide so the whole of it is on screen at once.
   *
   * Compares the space actually available — the viewport between the two
   * drawers, and whatever is left below the toolbar — against the slide's
   * unscaled size, then takes whichever axis runs out first. offsetWidth /
   * offsetHeight are untransformed, so this is stable to call repeatedly
   * rather than compounding on the zoom already applied.
   */
  const handleZoomFit = useCallback(() => {
    const stage = slideZoomStageRef.current;
    const viewport = slideZoomViewportRef.current;
    if (!stage || !viewport) return;

    const naturalWidth = stage.offsetWidth;
    const naturalHeight = stage.offsetHeight;
    if (!naturalWidth || !naturalHeight) return;

    const availableWidth = viewport.clientWidth;
    // A little breathing room at the bottom so the slide does not sit flush
    // against the edge of the window.
    const availableHeight = window.innerHeight - viewport.getBoundingClientRect().top - 24;
    if (availableWidth <= 0 || availableHeight <= 0) return;

    const fit = Math.min(availableWidth / naturalWidth, availableHeight / naturalHeight);
    const clamped = Math.min(Math.max(fit, 0.25), 3);
    // Floor rather than round to the nearest percent: rounding up overshoots
    // the space we just measured, which is how "fit" ends up a few pixels too
    // tall to actually fit.
    setSlideZoom(Math.floor(clamped * 100) / 100);
  }, []);

  useEffect(() => {
    const stage = slideZoomStageRef.current;
    if (!stage) return undefined;

    const measure = () => setSlideZoomNaturalHeight(stage.offsetHeight);
    measure();

    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(stage);
    return () => observer.disconnect();
    // Deliberately empty: the observer already reports every content or drawer
    // driven size change, so there is nothing to re-subscribe to. Listing
    // `sections` here would also reference it ~300 lines before it is declared,
    // which is a temporal-dead-zone crash rather than a lint nit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleNavDrawer = (event) => {
    if (event?.currentTarget) {
      navDrawerTriggerRef.current = event.currentTarget;
    }
    setIsNavDrawerOpen((prev) => !prev);
  };

  const openSectionDividerSettings = (sectionItem, sectionSlide) => {
    setSectionDividerSettings({
      sectionId: sectionItem.id,
      sectionLabel: sectionItem.label,
      slideLabel: sectionSlide?.label || sectionItem.label
    });
  };

  const closeSectionDividerSettings = () => setSectionDividerSettings(null);

  // Collapse both the top toolbar and the side outline drawer in one action
  // to maximize the editing canvas; restore both to their prior state on exit.
  const toggleCanvasMaximized = () => {
    setIsCanvasMaximized((prev) => {
      const next = !prev;
      if (next) {
        preMaximizeRef.current = { toolbar: showToolbar, nav: isNavDrawerOpen };
        setShowToolbar(false);
        setIsNavDrawerOpen(false);
      } else {
        setShowToolbar(preMaximizeRef.current.toolbar);
        setIsNavDrawerOpen(preMaximizeRef.current.nav);
      }
      return next;
    });
  };

  // Heights for sticky navigation elements
  const toolbarRef = useRef(null);
  const [tabNavHeight, setTabNavHeight] = useState(0);
  const collapsedToolbarRef = useRef(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    localStorage.setItem('showAdvancedExports', String(showAdvancedExports));
  }, [showAdvancedExports]);

  const [showAspectGuides, setShowAspectGuides] = useState(() => {
    if (normalizedInitialData?.showAspectGuides) return true;
    if (typeof window === 'undefined') return false;
    return localStorage.getItem('showAspectGuides') === 'true';
  });

  // When false, the slide-deck name badge only appears on title cards instead
  // of the bottom of every content slide. Defaults to on (badge everywhere).
  const [deckNameOnContentSlides, setDeckNameOnContentSlides] = useState(() => {
    if (typeof normalizedInitialData?.deckNameOnContentSlides === 'boolean') {
      return normalizedInitialData.deckNameOnContentSlides;
    }
    if (typeof window === 'undefined') return true;
    return localStorage.getItem('deckNameOnContentSlides') !== 'false';
  });

  // The badge itself. Off means the deck's name appears on no slide at all —
  // a title card that already says what the deck is does not need it repeated
  // along the bottom of every slide.
  const [deckNameBadgeEnabled, setDeckNameBadgeEnabled] = useState(() => {
    if (typeof normalizedInitialData?.deckNameBadgeEnabled === 'boolean') {
      return normalizedInitialData.deckNameBadgeEnabled;
    }
    if (typeof window === 'undefined') return true;
    return localStorage.getItem('deckNameBadgeEnabled') !== 'false';
  });

  // Slide numbers, bottom-right of every slide. On unless the author turns
  // them off: a numbered deck is what a room full of students expects when
  // they ask you to go back to slide 14. A deck that travels with the setting
  // wins over the local preference, same as the deck-name badge.
  const [slideNumbersEnabled, setSlideNumbersEnabled] = useState(() => {
    if (typeof normalizedInitialData?.slideNumbersEnabled === 'boolean') {
      return normalizedInitialData.slideNumbersEnabled;
    }
    if (typeof window === 'undefined') return true;
    return localStorage.getItem('slideNumbersEnabled') !== 'false';
  });

  const [slideDesignMode, setSlideDesignMode] = useState(() =>
    normalizedInitialData?.slideDesignMode === 'canvas' ? 'canvas' : 'flow'
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;
    localStorage.setItem('showAspectGuides', String(showAspectGuides));
  }, [showAspectGuides]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    localStorage.setItem('deckNameOnContentSlides', String(deckNameOnContentSlides));
  }, [deckNameOnContentSlides]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    localStorage.setItem('deckNameBadgeEnabled', String(deckNameBadgeEnabled));
  }, [deckNameBadgeEnabled]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    localStorage.setItem('slideNumbersEnabled', String(slideNumbersEnabled));
  }, [slideNumbersEnabled]);

  useEffect(() => {
    const updateHeights = () => {
      if (isFullScreenPreview) {
        setTabNavHeight(0);
        return;
      }

      const tabNav = document.getElementById('tab-navigation');
      const measuredNavHeight = tabNav?.getBoundingClientRect?.().height || 0;
      setTabNavHeight(Math.round(measuredNavHeight));
    };

    updateHeights();
    window.addEventListener('resize', updateHeights);
    return () => window.removeEventListener('resize', updateHeights);
  }, [showJsonInput, isFullScreenPreview, showToolbar, activeAppTab]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!showExportMenu && !showHtmlExportMenu && !showSettingsMenu && !showSaveMenu && !showOpenMenu && !showNewSlideMenu) return;

      const isOutsideJsonButton = exportMenuButtonRef.current && !exportMenuButtonRef.current.contains(event.target);
      const isOutsideJsonMenu = exportMenuRef.current && !exportMenuRef.current.contains(event.target);
      const isOutsideHtmlButton = htmlExportMenuButtonRef.current && !htmlExportMenuButtonRef.current.contains(event.target);
      const isOutsideHtmlMenu = htmlExportMenuRef.current && !htmlExportMenuRef.current.contains(event.target);
      const isOutsideSettingsButton = settingsMenuButtonRef.current && !settingsMenuButtonRef.current.contains(event.target);
      const isOutsideSettingsMenu = settingsMenuRef.current && !settingsMenuRef.current.contains(event.target);
      const isOutsideSaveButton = saveMenuButtonRef.current && !saveMenuButtonRef.current.contains(event.target);
      const isOutsideSaveMenu = saveMenuRef.current && !saveMenuRef.current.contains(event.target);

      const isOutsideOpenButton = openMenuButtonRef.current && !openMenuButtonRef.current.contains(event.target);
      const isOutsideOpenMenu = openMenuRef.current && !openMenuRef.current.contains(event.target);
      const isOutsideNewSlide = newSlideMenuRef.current && !newSlideMenuRef.current.contains(event.target)
        && newSlideMenuButtonRef.current && !newSlideMenuButtonRef.current.contains(event.target);

      if (showSaveMenu && isOutsideSaveButton && isOutsideSaveMenu) {
        setShowSaveMenu(false);
      }

      if (showOpenMenu && isOutsideOpenButton && isOutsideOpenMenu) {
        setShowOpenMenu(false);
      }
      if (showNewSlideMenu && isOutsideNewSlide) setShowNewSlideMenu(false);

      if (showExportMenu && isOutsideJsonButton && isOutsideJsonMenu) {
        setShowExportMenu(false);
      }

      if (showHtmlExportMenu && isOutsideHtmlButton && isOutsideHtmlMenu) {
        setShowHtmlExportMenu(false);
      }

      if (showSettingsMenu && isOutsideSettingsButton && isOutsideSettingsMenu) {
        setShowSettingsMenu(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        if (showExportMenu) setShowExportMenu(false);
        if (showHtmlExportMenu) setShowHtmlExportMenu(false);
        if (showSettingsMenu) setShowSettingsMenu(false);
        if (showSaveMenu) setShowSaveMenu(false);
        if (showOpenMenu) setShowOpenMenu(false);
        if (showNewSlideMenu) setShowNewSlideMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [showExportMenu, showHtmlExportMenu, showSettingsMenu, showSaveMenu, showOpenMenu, showNewSlideMenu]);

  // Keyboard shortcuts for editor mode switching
  useEffect(() => {
    const handleModeKeyboardShortcuts = (event) => {
      // Ignore if user is typing in an input, textarea, or contenteditable element
      const target = event.target;
      const tagName = target?.tagName?.toLowerCase();
      const isEditable = target?.isContentEditable || ['input', 'textarea', 'select'].includes(tagName);

      if (isEditable) return;

      // Present mode runs its own key map (Space/PageDown/arrows drive the
      // deck); the editor shortcuts would fight it for the arrow keys.
      if (isPresenting) return;

      // "?" before the modifier guard below: on most layouts it *is* Shift+/,
      // so checking it after that guard would mean it never fires.
      if (event.key === '?' && !event.ctrlKey && !event.metaKey && !event.altKey) {
        event.preventDefault();
        setIsHotkeysOpen((prev) => !prev);
        return;
      }

      // Ignore if any modifier keys are pressed
      if (event.ctrlKey || event.metaKey || event.altKey || event.shiftKey) return;

      const key = event.key.toLowerCase();

      if (key === 'e') {
        // Toggle between Edit and Preview Mode (also works in full screen)
        event.preventDefault();
        handleToggleEditMode();
      } else if (key === 'p') {
        // Switch to Preview Mode
        if (isEditMode) {
          event.preventDefault();
          handleToggleEditMode();
        }
      } else if (key === 'f') {
        // Toggle Full Screen Preview
        event.preventDefault();
        if (isFullScreenPreview) {
          handleExitFullScreenPreview();
        } else {
          handleEnterFullScreenPreview();
        }
      } else if (key === '[') {
        // Toggle the left sections/outline drawer
        event.preventDefault();
        setIsNavDrawerOpen((prev) => !prev);
      } else if (key === ']') {
        // Toggle the right insert/tools drawer
        event.preventDefault();
        setIsToolbarDrawerOpen((prev) => !prev);
      } else if (isFullScreenPreview && !isEditMode && key === 'h') {
        // Collapse the full-screen controls out of the way, and bring them
        // back. This used to be Up/Down, but those now drive the deck the way
        // they do in PowerPoint, so hiding the chrome has its own key. Left to
        // the editor in edit mode, where H still belongs to the content.
        event.preventDefault();
        setIsFullScreenChromeHidden((prev) => !prev);
      } else if (key === 'escape' && isFullScreenPreview) {
        // Escape leaves full screen
        event.preventDefault();
        handleExitFullScreenPreview();
      }
    };

    document.addEventListener('keydown', handleModeKeyboardShortcuts);
    return () => document.removeEventListener('keydown', handleModeKeyboardShortcuts);
  }, [isEditMode, isFullScreenPreview, isPresenting]);

  // NEW: Use the logo context
  const { logo, logoName, logoSize, getLogoHtml, hasLogo, saveLogo, removeLogo } = useLogo();

  const createDefaultHeaderData = () => ({
    courseTopic: 'BOPPPS Lesson Template',
    instructorName: 'Instructor: Your Name',
    instructorEmail: 'email@institution.edu',
    footerCourseInfo: 'Course Title - Course Code',
    footerInstitution: 'Institution Name',
    footerCopyright: '© 2025 All Rights Reserved',
    showHeaderBar: true
  });

  const createDefaultSections = () => {
    // Use the comprehensive template as the default
    const template = createSlideTemplate();
    return template.sections;
  };

  // Header data
  const [headerData, setHeaderData] = useState(() => {
    const defaults = createDefaultHeaderData();
    if (!normalizedInitialData) return defaults;
    return {
      ...defaults,
      courseTopic: safeString(normalizedInitialData.courseTopic, defaults.courseTopic),
      instructorName: safeString(normalizedInitialData.instructorName, defaults.instructorName),
      instructorEmail: safeString(normalizedInitialData.instructorEmail, defaults.instructorEmail),
      footerCourseInfo: safeString(normalizedInitialData.footerCourseInfo, defaults.footerCourseInfo),
      footerInstitution: safeString(normalizedInitialData.footerInstitution, defaults.footerInstitution),
      footerCopyright: safeString(normalizedInitialData.footerCopyright, defaults.footerCopyright),
      showHeaderBar: normalizedInitialData.showHeaderBar !== false
    };
  });
  const showHeaderBar = headerData.showHeaderBar !== false;
  const isHeaderExpanded = showHeaderBar && !isAppHeaderCollapsed;

  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [tempTitle, setTempTitle] = useState('');
  const [isEditingDate, setIsEditingDate] = useState(false);

  const getCourseTopicForWeek = useCallback(() => {
    const fallback = `Course Topic - Week ${week}`;
    const baseTopic = safeString(headerData?.courseTopic, fallback);
    const topic = baseTopic.trim() ? baseTopic : fallback;
    return safeString(topic, fallback).replace(/Week \d+/, `Week ${week}`);
  }, [headerData.courseTopic, week]);

  const handleTitleSave = () => {
    const newTitle = tempTitle.trim();
    if (newTitle) {
      handleHeaderDataChange('courseTopic', newTitle);
    }
    setIsEditingTitle(false);
  };

  const handleTitleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleTitleSave();
    } else if (e.key === 'Escape') {
      setTempTitle(getCourseTopicForWeek());
      setIsEditingTitle(false);
    }
  };

  const smoothScrollTo = (elementY, duration = 1000) => {
    const startingY = window.pageYOffset;
    const diff = elementY - startingY;
    let start;

    const step = (timestamp) => {
      if (!start) start = timestamp;
      const time = timestamp - start;
      const percent = Math.min(time / duration, 1);

      // easeInOutCubic timing function for a soft start and end
      const easing = percent < 0.5 ? 4 * percent * percent * percent : 1 - Math.pow(-2 * percent + 2, 3) / 2;

      window.scrollTo(0, startingY + diff * easing);

      if (time < duration) {
        window.requestAnimationFrame(step);
      }
    }
    window.requestAnimationFrame(step);
  }

  // Initial sections data - UPDATED to handle initialData prop
  const [sections, setSections] = useState(() => {
    if (normalizedInitialData && normalizedInitialData.sections) {
      return normalizedInitialData.sections;
    }
    return createDefaultSections();
  });

  // Keep defaultSection in sync: if the current value doesn't exist in sections, reset to first
  useEffect(() => {
    if (sections.length === 0) return;
    if (!sections.some(s => s.id === defaultSection)) {
      setDefaultSection(sections[0].id);
    }
  }, [sections, defaultSection]);

  // Citations collected from the deck's own blocks (images, videos, quotes…).
  const deckApaCitations = useMemo(
    () => dedupeSourcesByReference(collectSourceMetasFromSections(sections)),
    [sections]
  );
  // The complete reference list: the deck's citations plus the ones typed
  // directly into the reference blocks. Every reference page paginates from
  // this one list, so the pages line up no matter which block renders them.
  const autoApaCitations = useMemo(
    () => collectReferenceListMetas(sections, deckApaCitations),
    [sections, deckApaCitations]
  );
  useEffect(() => {
    if (!autoApaCitations.length) return;
    if (hasReferenceListBlock(sections)) return;
    setSections((prevSections) => ensureReferenceListBlockExists(prevSections));
  }, [autoApaCitations, sections]);

  // Grow (or shrink) the run of reference slides so the whole list fits. Each
  // reference-list block renders one page, so a deck with more references than
  // one slide holds gets continuation slides added automatically.
  useEffect(() => {
    setSections((prevSections) => syncReferenceListPages(prevSections, autoApaCitations));
  }, [autoApaCitations, sections]);
  const undoStackRef = useRef([]);
  const redoStackRef = useRef([]);
  const lastSnapshotRef = useRef({ snapshot: null, serialized: '' });
  const pendingSnapshotRef = useRef(null);
  const undoTimeoutRef = useRef(null);
  // Timestamp until which state changes are treated as part of an in-flight
  // undo/redo rather than new user edits (see the snapshot effect below).
  const isUndoRedoRef = useRef(0);
  const HISTORY_SETTLE_MS = 600;
  const UNDO_HISTORY_LIMIT = 50;
  const UNDO_DEBOUNCE_MS = 400;

  const cloneSnapshot = useCallback((snapshot) => {
    if (typeof structuredClone === 'function') {
      return structuredClone(snapshot);
    }
    return JSON.parse(JSON.stringify(snapshot));
  }, []);

  const buildUndoSnapshot = useCallback(() => ({
    sections,
    headerData,
    titleCardSettings,
    endTitleCardSettings,
    slideVisibility,
    acknowledgedBlankSlides,
    slideNames,
    slideSubtitles,
    slideDurations
  }), [sections, headerData, titleCardSettings, endTitleCardSettings, slideVisibility, acknowledgedBlankSlides, slideNames, slideSubtitles, slideDurations]);

  const commitPendingSnapshot = useCallback(() => {
    if (undoTimeoutRef.current) {
      clearTimeout(undoTimeoutRef.current);
      undoTimeoutRef.current = null;
    }
    if (!pendingSnapshotRef.current) return;
    undoStackRef.current.push(cloneSnapshot(pendingSnapshotRef.current));
    if (undoStackRef.current.length > UNDO_HISTORY_LIMIT) {
      undoStackRef.current.shift();
    }
    redoStackRef.current = [];
    pendingSnapshotRef.current = null;
  }, [cloneSnapshot]);

  useEffect(() => {
    const snapshot = buildUndoSnapshot();
    const serialized = JSON.stringify(snapshot);

    // An undo or redo does not just change state once: effects that derive
    // state from it run straight afterwards (restoring a quote block brings
    // the reference-list block back with it). Clearing the flag on the first
    // run let those follow-ups be recorded as fresh user edits, which wiped
    // the redo stack the moment anything was undone. Suppress for a short
    // settling window instead, so the whole cascade counts as the one step.
    if (Date.now() < isUndoRedoRef.current) {
      lastSnapshotRef.current = { snapshot: cloneSnapshot(snapshot), serialized };
      return;
    }

    if (!lastSnapshotRef.current.snapshot) {
      lastSnapshotRef.current = { snapshot: cloneSnapshot(snapshot), serialized };
      return;
    }

    if (lastSnapshotRef.current.serialized === serialized) {
      return;
    }

    // Keep the state from *before* the first change of a burst. Overwriting it
    // on every change meant a burst recorded its own last intermediate state,
    // so undo landed after the action instead of before it — popping a quote
    // out of a text block adds an apa-quote block and then, milliseconds later,
    // the reference-list effect adds a block of its own, and Ctrl+Z appeared to
    // do nothing at all. Typing coalesces the same way: one step, not one
    // per keystroke.
    if (!pendingSnapshotRef.current) {
      pendingSnapshotRef.current = lastSnapshotRef.current.snapshot;
    }
    lastSnapshotRef.current = { snapshot: cloneSnapshot(snapshot), serialized };

    if (undoTimeoutRef.current) {
      clearTimeout(undoTimeoutRef.current);
    }

    undoTimeoutRef.current = setTimeout(() => {
      commitPendingSnapshot();
    }, UNDO_DEBOUNCE_MS);
  }, [buildUndoSnapshot, cloneSnapshot, commitPendingSnapshot]);

  useEffect(() => () => {
    if (undoTimeoutRef.current) {
      clearTimeout(undoTimeoutRef.current);
    }
  }, []);

  const handleUndo = useCallback(() => {
    commitPendingSnapshot();
    if (!undoStackRef.current.length) {
      showSaveIndicator('⚠️ Nothing to undo');
      return;
    }

    const currentSnapshot = cloneSnapshot(buildUndoSnapshot());
    const previousSnapshot = undoStackRef.current.pop();
    redoStackRef.current.push(currentSnapshot);

    applySnapshotRef.current(previousSnapshot);

    showSaveIndicator('↩️ Undo');
    // showSaveIndicator is declared further down the component, so it stays out
    // of the dependency list — the body only reaches it once this actually runs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildUndoSnapshot, cloneSnapshot, commitPendingSnapshot]);

  // Redo was half-built: undo pushed onto the redo stack, but nothing ever
  // popped it and no shortcut reached it.
  // Undo is declared above this point, so it reaches the shared restore
  // through a ref rather than forcing a reorder of the whole block.
  const applySnapshotRef = useRef(null);
  const applySnapshot = useCallback((snapshot) => {
    isUndoRedoRef.current = Date.now() + HISTORY_SETTLE_MS;
    setSections(snapshot.sections);
    setHeaderData(snapshot.headerData);
    setTitleCardSettings(snapshot.titleCardSettings);
    setEndTitleCardSettings(snapshot.endTitleCardSettings);
    setSlideVisibility(snapshot.slideVisibility);
    setAcknowledgedBlankSlides(snapshot.acknowledgedBlankSlides || {});
    setSlideNames(snapshot.slideNames);
    setSlideSubtitles(snapshot.slideSubtitles || {});
    setSlideDurations(snapshot.slideDurations || {});
  }, []);
  applySnapshotRef.current = applySnapshot;

  const handleRedo = useCallback(() => {
    // A pending edit means the user has moved on; redoing past it would drop
    // that edit, so commit it and let the redo stack clear as it normally does.
    commitPendingSnapshot();
    if (!redoStackRef.current.length) {
      showSaveIndicator('⚠️ Nothing to redo');
      return;
    }
    const nextSnapshot = redoStackRef.current.pop();
    undoStackRef.current.push(cloneSnapshot(buildUndoSnapshot()));
    applySnapshot(nextSnapshot);
    showSaveIndicator('↪️ Redo');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applySnapshot, buildUndoSnapshot, cloneSnapshot, commitPendingSnapshot]);

  useEffect(() => {
    // Two LessonTemplate instances are mounted at once (the Slides tab and the
    // hidden Settings tab), so without this both would answer the shortcut and
    // every undo would run twice, against two separate histories.
    if (showSettingsPage) return undefined;

    const handleUndoShortcut = (event) => {
      if (!(event.ctrlKey || event.metaKey)) return;
      const key = event.key?.toLowerCase();
      // ⌘/Ctrl+Z undoes; ⌘/Ctrl+Shift+Z and Ctrl+Y redo.
      const isUndo = key === 'z' && !event.shiftKey;
      const isRedo = (key === 'z' && event.shiftKey) || key === 'y';
      if (!isUndo && !isRedo) return;

      const target = event.target;
      const tagName = target?.tagName?.toLowerCase();
      const isEditable = target?.isContentEditable || ['input', 'textarea', 'select'].includes(tagName);
      // Inside an editor the editor's own history wins, so leave it alone.
      if (isEditable) return;
      // A modal owns the screen while it is open; undoing the deck behind it
      // changes something the user cannot even see.
      if (document.querySelector('[data-app-modal]')) return;

      event.preventDefault();
      if (isRedo) handleRedo();
      else handleUndo();
    };

    window.addEventListener('keydown', handleUndoShortcut);
    return () => window.removeEventListener('keydown', handleUndoShortcut);
  }, [handleUndo, handleRedo, showSettingsPage]);


  const sectionNumberMap = useMemo(() => buildSectionNumberMap(sections), [sections]);

  useEffect(() => {
    const allowedPlacements = new Set(['start', 'end', ...sections.map((section) => `before:${section.id}`)]);
    if (!allowedPlacements.has(newSectionPlacement)) {
      const fallbackPlacement = sections.some((section) => section.id === 'resources')
        ? 'before:resources'
        : 'end';
      setNewSectionPlacement(fallbackPlacement);
    }
  }, [sections, newSectionPlacement]);

  useEffect(() => {
    if (!selectionTargetSection && sections.length > 0) {
      setSelectionTargetSection(sections[0].id);
    }
  }, [sections, selectionTargetSection]);

  const rawSlides = useMemo(
    () => {
      console.log('[slides useMemo] Rebuilding slides from sections');
      console.log('[slides useMemo] initialData?.slides:', initialData?.slides);
      console.log('[slides useMemo] sections:', sections.map(s => ({ id: s.id, blockCount: s.blocks?.length })));
      // Always rebuild from sections to ensure subsection blocks are current
      // Don't use initialData?.slides as it may be stale
      return buildSlidesFromJson({
        slides: undefined,
        sections,
        titleCard: titleCardSettings,
        endTitleCard: endTitleCardSettings,
        includeEmptySections: true,
        sectionNumberMap
      });
    },
    [sections, titleCardSettings, endTitleCardSettings, sectionNumberMap]
  );

  const slides = useMemo(
    () => renumberSlides(rawSlides, sectionNumberMap),
    [rawSlides, sectionNumberMap]
  );

  const slideById = useMemo(() => {
    const map = {};
    slides.forEach((slide) => {
      if (slide?.slideId) {
        map[slide.slideId] = slide;
      }
    });
    return map;
  }, [slides]);

  useEffect(() => {
    console.log('[sections updated] New sections state:', sections.map(s => ({
      id: s.id,
      blocksCount: s.blocks?.length || 0,
      blockIds: s.blocks?.map(b => b.id) || []
    })));
  }, [sections]);

  /**
   * The slide id each section's first slide had, as of the last render.
   *
   * Held so an emptied first slide can be restored under its own id rather
   * than a fresh one, which is what keeps its name, background and duration
   * attached to it. See `hasLostFirstSlide` for why the first slide is the
   * only one that needs this.
   */
  const firstSlideIdsRef = useRef({});

  /**
   * Keep a slide that has had its last block deleted.
   *
   * Deleting the last block on slide 4 leaves the slide there and empty,
   * because the slide-break divider in front of it still says it exists.
   * Deleting the last block on slide **1** used to remove the slide itself —
   * it has no divider of its own, so its blocks were the only record of it,
   * and the deck lost a slide when the author meant to clear one.
   *
   * A section whose block list now *starts* with a slide-break divider is the
   * signature of exactly that: the blocks in front of it are gone. Giving the
   * first slide its own divider puts it back, blank, in its own place, and
   * from then on it behaves like every other slide.
   *
   * This runs where the slide-id repair already runs, so every route that can
   * empty a slide — the block menu, the Slide Designer, a cut, a card delete —
   * is covered by one rule rather than by a guard at each of them.
   */
  useEffect(() => {
    setSections((prevSections) => {
      let didUpdate = false;
      const nextSections = prevSections.map((section) => {
        const remembered = firstSlideIdsRef.current[section.id];
        if (!hasLostFirstSlide(section.blocks, remembered)) return section;
        const usedIds = collectSlideIds(section.blocks);
        // The id it had, unless something else has since taken it.
        const slideId = remembered && !usedIds.has(remembered)
          ? remembered
          : resolveNextAvailableSlideId(section.id, usedIds, 0);
        didUpdate = true;
        return { ...section, blocks: [createSlideDivider(slideId), ...section.blocks] };
      });
      return didUpdate ? nextSections : prevSections;
    });

    // Recorded after the repair, so the value read above is still the one from
    // before whatever change brought us here.
    const ids = {};
    sections.forEach((section) => {
      const id = firstSlideIdOf(section.blocks);
      if (id) ids[section.id] = id;
    });
    firstSlideIdsRef.current = { ...firstSlideIdsRef.current, ...ids };
  }, [sections]);

  useEffect(() => {
    setSections((prevSections) => {
      let didUpdate = false;
      const nextSections = prevSections.map((section) => {
        const { updated, blocks } = ensureSectionSlideIds(section);
        if (!updated) return section;
        didUpdate = true;
        return { ...section, blocks };
      });
      return didUpdate ? nextSections : prevSections;
    });
  }, [sections]);

  // Custom HTML/CSS blocks whose author ticked "apply to the entire slide deck"
  // are hoisted out of the slide they live on. The deck viewer mounts one slide
  // at a time, so rules left with the block would blink out the moment you
  // navigated away from it.
  const deckScopedCustomCss = useMemo(() => {
    const parts = [];
    sections.forEach((section) => {
      (section.blocks || []).forEach((block) => {
        if (block?.type !== 'custom-html' || !isDeckScopedCustomHtml(block)) return;
        const { css } = prepareCustomHtmlBlock(block);
        if (css) parts.push(css);
      });
    });
    return parts.join('\n\n');
  }, [sections]);

  const effectiveSlideCss = useMemo(
    () => [slideGlobalCss, deckScopedCustomCss].filter((part) => (part || '').trim()).join('\n\n'),
    [slideGlobalCss, deckScopedCustomCss]
  );

  const sectionSlidesMap = useMemo(() => {
    const map = {};

    slides.forEach((slide, index) => {
      if ((slide.type !== 'section' && slide.type !== 'section-title-card') || !slide.section?.id) return;
      const sectionId = slide.section.id;
      if (!map[sectionId]) {
        map[sectionId] = [];
      }

      if (slide.type === 'section-title-card') {
        map[sectionId].push({
          label: slide.title || `${slide.section?.title || 'Section'} title slide`,
          slideIndex: -1,
          globalIndex: Number.isFinite(slide.slideOrderIndex) ? slide.slideOrderIndex : index,
          slideId: slide.slideId,
          isSectionTitleCard: true
        });
        return;
      }

      const customName = slideNames[slide.slideId];
      const slidePositionLabel = `Slide ${(slide.slideIndex ?? 0) + 1} of ${slide.slidesInSection || 1}`;
      const slideNumberLabel = slide.section?.sectionNumber
        ? `${slide.section.sectionNumber}.${(slide.slideIndex ?? 0) + 1}`
        : slidePositionLabel;
      // Same rule as the header bar: a name generated from the slide's id is
      // not a name. (This used to catch only the raw id and the "id …" prefix,
      // which let the title-cased form — "Section 1 Slide 7" — straight
      // through.)
      const effectiveName = resolveSlideName(customName, slide.slideId);
      // The header bar no longer numbers anything, so the thumbnail carries the
      // number — paired with the slide's name, or the section's title while it
      // is still unnamed. "Slide 2 of 7" only appears when there is no title at
      // all to fall back on.
      const thumbnailName = effectiveName
        || (slide.section?.title || '').trim()
        || slidePositionLabel;

      map[sectionId].push({
        label: `${slideNumberLabel} • ${thumbnailName}`,
        slideIndex: Number.isFinite(slide.slideIndex) ? slide.slideIndex : 0,
        globalIndex: Number.isFinite(slide.slideOrderIndex) ? slide.slideOrderIndex : index,
        slideId: slide.slideId,
        isSectionTitleCard: false
      });
    });

    Object.values(map).forEach((sectionSlides) => {
      sectionSlides.sort((a, b) => a.slideIndex - b.slideIndex);
    });

    return map;
  }, [slides, slideNames]);

  /* ---- picking out more than one slide in the outline --------------------
     Every content slide in the deck, in order, as the list a Shift range is
     measured across. Section title cards are absent: they belong to their
     section's settings rather than to its running order, and nothing can drag
     one. */
  const pickableSlides = useMemo(() => slides
    .map((slide, index) => ({
      slideId: slide.slideId,
      sectionId: slide.section?.id,
      slideIndex: Number.isFinite(slide.slideIndex) ? slide.slideIndex : 0,
      type: slide.type,
      order: Number.isFinite(slide.slideOrderIndex) ? slide.slideOrderIndex : index
    }))
    .filter((entry) => entry.type === 'section' && entry.slideId && entry.sectionId),
  [slides]);

  const pickedSlideSet = useMemo(() => new Set(pickedSlideIds), [pickedSlideIds]);

  // The selection as something a move can take, in deck order.
  const pickedSlideRefs = useMemo(() => pickableSlides
    .filter((entry) => pickedSlideSet.has(entry.slideId))
    .map(({ sectionId, slideId, slideIndex }) => ({ sectionId, slideId, slideIndex })),
  [pickableSlides, pickedSlideSet]);

  const clearSlidePicks = useCallback(() => {
    pickAnchorRef.current = null;
    setPickedSlideIds((prev) => (prev.length ? [] : prev));
  }, []);

  const toggleSlidePick = useCallback((slideId) => {
    if (!slideId) return;
    pickAnchorRef.current = slideId;
    setPickedSlideIds((prev) => (
      prev.includes(slideId)
        ? prev.filter((id) => id !== slideId)
        : [...prev, slideId]
    ));
  }, []);

  const extendSlidePickTo = useCallback((slideId) => {
    if (!slideId) return;
    const anchor = pickAnchorRef.current;
    const toIndex = pickableSlides.findIndex((entry) => entry.slideId === slideId);
    if (toIndex < 0) return;
    const fromIndex = anchor
      ? pickableSlides.findIndex((entry) => entry.slideId === anchor)
      : -1;
    // No anchor yet — a Shift click with nothing to measure from picks one
    // slide and becomes the anchor itself.
    if (fromIndex < 0) {
      pickAnchorRef.current = slideId;
      setPickedSlideIds([slideId]);
      return;
    }
    const start = Math.min(fromIndex, toIndex);
    const end = Math.max(fromIndex, toIndex);
    setPickedSlideIds(pickableSlides.slice(start, end + 1).map((entry) => entry.slideId));
  }, [pickableSlides]);

  // A slide that has been deleted, or moved out from under its id, must not
  // stay in the selection — a stale id would silently join the next drag.
  useEffect(() => {
    setPickedSlideIds((prev) => {
      if (!prev.length) return prev;
      const live = new Set(pickableSlides.map((entry) => entry.slideId));
      const next = prev.filter((id) => live.has(id));
      return next.length === prev.length ? prev : next;
    });
  }, [pickableSlides]);

  // Escape drops the selection, wherever the focus happens to be.
  useEffect(() => {
    if (!pickedSlideIds.length) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') clearSlidePicks();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [pickedSlideIds.length, clearSlidePicks]);

  const blockTargetOptions = useMemo(() => {
    return slides
      .filter((slide) => slide.type === 'section')
      .map((slide) => ({
        id: slide.slideId,
        label: `${slide.section?.title || 'Untitled section'} — ${slideNames[slide.slideId] || `Slide ${slide.slideIndex + 1}`}`,
        sectionId: slide.section?.id || ''
      }));
  }, [slides, slideNames]);

  const sectionSlideBlockMap = useMemo(() => {
    const map = {};
    slides.forEach((slide) => {
      if (slide.type !== 'section') return;
      const sectionId = slide.section?.id || 'ungrouped';
      if (!map[sectionId]) {
        map[sectionId] = [];
      }
      const slidePositionLabel = `Slide ${(slide.slideIndex ?? 0) + 1} of ${slide.slidesInSection || 1}`;
      const slideNumberLabel = slide.section?.sectionNumber
        ? `${slide.section.sectionNumber}.${(slide.slideIndex ?? 0) + 1}`
        : `Slide ${(slide.slideIndex ?? 0) + 1}`;
      const defaultLabel = `${slideNumberLabel} • ${slidePositionLabel}`;
      map[sectionId].push({
        slideId: slide.slideId,
        label: slideNames[slide.slideId] || defaultLabel,
        slideNumberLabel,
        slideIndex: Number.isFinite(slide.slideIndex) ? slide.slideIndex : 0,
        blocks: slide.blocks || []
      });
    });
    Object.values(map).forEach((slidesList) => slidesList.sort((a, b) => a.slideIndex - b.slideIndex));
    return map;
  }, [slides, slideNames]);

  const blockContextMap = useMemo(() => {
    const map = {};
    slides.forEach((slide) => {
      if (slide.type !== 'section') return;
      const slideTitle = slideNames[slide.slideId] || `Slide ${(slide.slideIndex ?? 0) + 1}`;
      const sectionTitle = slide.section?.title || '';
      (slide.blocks || []).forEach((block, blockIndex) => {
        const labelParts = [block?.type, block?.title || block?.heading || block?.content?.title].filter(Boolean);
        map[block.id] = {
          slideId: slide.slideId,
          slideTitle,
          sectionId: slide.section?.id,
          sectionTitle,
          blockIndex: blockIndex + 1,
          blockLabel: labelParts.join(' • ')
        };
      });
    });
    return map;
  }, [slides, slideNames]);

  const navigationItems = useMemo(() => {
    const baseItems = bodyLayout === 'slides'
      ? buildNavItemsFromSlides(slides, sectionNumberMap)
      : buildNavItemsFromSections(sections, titleCardSettings, endTitleCardSettings, sectionNumberMap);

    const titleSlideIndex = slides.findIndex((slide) => slide.type === 'title-card');
    const titleLabel = titleCardSettings?.title?.trim() || 'Title';
    const endSlideIndex = slides.findIndex((slide) => slide.type === 'end-title-card');
    const endTitleLabel = endTitleCardSettings?.title?.trim() || 'End Title';

    let normalizedItems = baseItems.map((item) => {
      if (item.id === 'title-card') {
        return {
          ...item,
          label: titleLabel,
          enabled: titleCardSettings?.enabled,
          hasSlide: titleSlideIndex !== -1
        };
      }
      if (item.id === 'end-title-card') {
        return {
          ...item,
          label: endTitleLabel,
          enabled: endTitleCardSettings?.enabled,
          hasSlide: endSlideIndex !== -1
        };
      }
      return item;
    });

    const hasTitleNavItem = normalizedItems.some((item) => item.id === 'title-card');
    if (!hasTitleNavItem) {
      normalizedItems = [
        {
          type: 'title-card',
          id: 'title-card',
          label: titleLabel,
          slideIndex: titleSlideIndex !== -1 ? titleSlideIndex : 0,
          enabled: titleCardSettings?.enabled,
          hasSlide: titleSlideIndex !== -1
        },
        ...normalizedItems
      ];
    }

    const hasEndNavItem = normalizedItems.some((item) => item.id === 'end-title-card');
    if (!hasEndNavItem) {
      normalizedItems = [
        ...normalizedItems,
        {
          type: 'end-title-card',
          id: 'end-title-card',
          label: endTitleLabel,
          slideIndex: endSlideIndex !== -1 ? endSlideIndex : slides.length,
          enabled: endTitleCardSettings?.enabled,
          hasSlide: endSlideIndex !== -1
        }
      ];
    }

    return normalizedItems;
  }, [bodyLayout, slides, sections, titleCardSettings, endTitleCardSettings, sectionNumberMap]);

  const drawerIssueMap = useMemo(() => {
    const map = {};
    slides.forEach((slide) => {
      if (!slide?.slideId) return;
      // A blank the author explicitly chose to keep is not an issue.
      map[slide.slideId] = isEmptySlide(slide) && !acknowledgedBlankSlides[slide.slideId];
    });
    return map;
  }, [slides, acknowledgedBlankSlides]);

  const drawerIssueCount = useMemo(
    () => Object.values(drawerIssueMap).filter(Boolean).length,
    [drawerIssueMap]
  );

  const handleDrawerExpandAll = useCallback(() => {
    const expandIds = navigationItems
      .filter((item) => item.type === 'section' && (sectionSlidesMap[item.id] || []).length > 0)
      .map((item) => item.id);
    setDrawerExpandedSections(expandIds);
    setDrawerCollapsedSections([]);
  }, [navigationItems, sectionSlidesMap]);

  const handleDrawerCollapseAll = useCallback(() => {
    const collapseIds = navigationItems
      .filter((item) => item.type === 'section' && (sectionSlidesMap[item.id] || []).length > 0)
      .map((item) => item.id);
    setDrawerCollapsedSections(collapseIds);
    setDrawerExpandedSections([]);
  }, [navigationItems, sectionSlidesMap]);

  const drawerSubtitle = useMemo(() => {
    const courseTopic = safeString(headerData?.courseTopic).trim();
    if (courseTopic) return courseTopic;
    if (slideDeckName) return slideDeckName;
    return 'BOPPPS lesson sequence';
  }, [headerData?.courseTopic, slideDeckName]);

  const normalizedDrawerFilter = drawerFilterText.trim().toLowerCase();

  /* ---- the slide reorder dialog ------------------------------------------
     One section's running order, described the way the drawer describes it so
     the two lists cannot disagree about a slide's name. The section's title
     card is left out: it is a setting of the section, not a step in its
     order, and nothing else lets it be moved either. */
  const slideReorderSection = useMemo(
    () => (slideReorderSectionId
      ? sections.find((section) => section.id === slideReorderSectionId) || null
      : null),
    [sections, slideReorderSectionId]
  );

  const slideReorderSlides = useMemo(() => {
    if (!slideReorderSectionId) return [];
    return (sectionSlidesMap[slideReorderSectionId] || [])
      .filter((entry) => !entry.isSectionTitleCard)
      .map((entry) => {
        const primaryBlock = (slideById[entry.slideId]?.blocks || [])
          .find((block) => block?.type && block.type !== 'divider' && !isEmptyBlock(block));
        return {
          slideId: entry.slideId,
          label: entry.label,
          typeLabel: formatBlockTypeLabel(primaryBlock?.type),
          hasIssue: Boolean(drawerIssueMap[entry.slideId]),
          isCurrent: currentSlideIndex === entry.globalIndex
        };
      });
  }, [slideReorderSectionId, sectionSlidesMap, slideById, drawerIssueMap, currentSlideIndex]);

  /**
   * One nudge of the reorder dialog's arrows, for one slide or for the set it
   * has picked out.
   *
   * Rewritten as a whole order rather than as a sequence of single moves: with
   * several slides in hand the intermediate states are not orders anyone asked
   * for, and applying them one at a time would make each one a step of its own
   * to undo. Nothing leaves the section and no id changes, so this is only the
   * blocks being written back in a new order.
   */
  const handleSlideReorderMove = useCallback((slideIds, offset) => {
    const ids = (Array.isArray(slideIds) ? slideIds : [slideIds]).filter(Boolean);
    if (!slideReorderSectionId || !ids.length || !offset) return;

    setSections((prevSections) => {
      const section = prevSections.find((entry) => entry.id === slideReorderSectionId);
      if (!section) return prevSections;
      const sectionSlides = splitSectionSlides(section, { includeEmpty: true, includeMeta: true });
      const order = sectionSlides.map((slide) => slide.slideId);
      const next = shiftSelection(order, ids, offset);
      if (next.every((slideId, index) => slideId === order[index])) return prevSections;

      const byId = new Map(sectionSlides.map((slide) => [slide.slideId, slide]));
      const reordered = next.map((slideId) => byId.get(slideId)).filter(Boolean);
      if (reordered.length !== sectionSlides.length) return prevSections;

      return prevSections.map((entry) => (
        entry.id === slideReorderSectionId
          ? { ...entry, blocks: flattenSlidesToBlocks(reordered, entry.id) }
          : entry
      ));
    });

    // The outline behind the dialog is showing these slides; it should not
    // chase the current one because they moved.
    holdDrawerScrollRef.current = true;
    showSaveIndicator(ids.length > 1
      ? `↕️ ${ids.length} slides moved`
      : (offset < 0 ? '⬆️ Slide moved up' : '⬇️ Slide moved down'));
    // showSaveIndicator is deliberately absent: it is declared further down the
    // component, so naming it here would read it during render, before it
    // exists. Calling it is fine — that happens long after.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slideReorderSectionId]);

  useEffect(() => {
    setSlideVisibility((prev) => {
      const next = { ...prev };
      slides.forEach((slide) => {
        if (!slide.slideId) return;
        if (next[slide.slideId] === undefined) {
          next[slide.slideId] = true;
        }
      });
      return next;
    });
  }, [slides]);

  const handleToggleSlideVisibility = useCallback((slideId, enabled) => {
    if (!slideId) return;
    setSlideVisibility((prev) => ({ ...prev, [slideId]: enabled }));
  }, []);

  /**
   * Turn slides on or off in one go — one, a picked set, or a whole section.
   *
   * "Off" is the flag the slide header's own Include switch has always set:
   * the slide stays in the deck and stays editable, and the exported files
   * leave it out. Nothing here changes what that means; it only makes it
   * reachable for more than one slide at a time.
   */
  const setSlidesActive = useCallback((slideIds, active) => {
    const ids = (Array.isArray(slideIds) ? slideIds : [slideIds]).filter(Boolean);
    if (!ids.length) return;
    setSlideVisibility((prev) => {
      const next = { ...prev };
      let changed = false;
      ids.forEach((id) => {
        if ((prev[id] !== false) === active) return;
        next[id] = active;
        changed = true;
      });
      return changed ? next : prev;
    });
    // The outline is showing the slides being switched; it should not chase
    // the current one because of it.
    holdDrawerScrollRef.current = true;
    const what = ids.length === 1 ? 'Slide' : `${ids.length} slides`;
    showSaveIndicator(active ? `👁️ ${what} back in the export` : `🚫 ${what} left out of the export`);
    // showSaveIndicator is deliberately absent: it is declared further down the
    // component, so naming it here would read it during render, before it
    // exists. Calling it is fine — that happens long after.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Every slide in a section, on or off together. Off unless they are all
   * already off, so a section with one slide left on is switched off rather
   * than being made inconsistent in the other direction.
   */
  const toggleSectionSlidesActive = useCallback((sectionId) => {
    const section = sections.find((entry) => entry.id === sectionId);
    if (!section) return;
    const ids = splitSectionSlides(section, { includeEmpty: true, includeMeta: true })
      .map((slide) => slide.slideId)
      .filter(Boolean);
    if (!ids.length) return;
    const anyActive = ids.some((id) => slideVisibility?.[id] !== false);
    setSlidesActive(ids, !anyActive);
  }, [sections, slideVisibility, setSlidesActive]);

  const handleUpdateSlideName = useCallback((slideId, name) => {
    if (!slideId) return;
    setSlideNames((prev) => ({ ...prev, [slideId]: name }));
  }, []);

  const handleUpdateSlideSubtitle = useCallback((slideId, subtitle) => {
    if (!slideId) return;
    setSlideSubtitles((prev) => ({ ...prev, [slideId]: subtitle }));
  }, []);

  const resolveExportTitle = useCallback(() => {
    const trimmed = htmlExportTitle.trim();
    if (trimmed) return trimmed;
    return getCourseTopicForWeek();
  }, [getCourseTopicForWeek, htmlExportTitle]);

  const resolveExportFileName = useCallback(() => {
    const defaultName = `Week${week}_Lecture_${safeString(date).replace(/[^a-zA-Z0-9]/g, '_')}`;
    const raw = safeString(exportFileName).trim() || defaultName;
    const sanitized = raw.replace(/[\\/?:%*|"<>]/g, '_').trim();
    return sanitized || defaultName;
  }, [exportFileName, week, date]);

  const resolveCurrentExportVersionLabel = useCallback(() => {
    const manualVersion = Number.parseInt(exportVersionInput.trim(), 10);
    if (Number.isFinite(manualVersion) && manualVersion > 0) {
      return manualVersion;
    }
    return exportVersionRef.current;
  }, [exportVersionInput]);

  /**
   * The version state, as one value, so a save that does not happen can put it
   * back exactly as it was.
   */
  const captureVersionState = useCallback(() => ({
    version: exportVersionRef.current,
    input: exportVersionInput,
    prefilled: prefilledVersionInput,
    saved: deckHasSavedVersionRef.current
  }), [exportVersionInput, prefilledVersionInput]);

  const restoreVersionState = useCallback((state) => {
    if (!state) return;
    exportVersionRef.current = state.version;
    deckHasSavedVersionRef.current = state.saved;
    setExportVersion(state.version);
    setExportVersionInput(state.input);
    setPrefilledVersionInput(state.prefilled);
  }, []);

  /**
   * Work out the version number the next export should carry.
   *
   * `bump: false` is "Save": rewrite the version this deck is already at, so
   * the file keeps its name. `bump: true` is "Save +": start the next one.
   *
   * The one case both share is a deck that has never been saved. There is no
   * version to rewrite and none to advance from, so either button produces
   * **version 1**. Save used to skip that reasoning entirely and simply read
   * the ref's initial value, which happened to be 1 — right by accident, and
   * only until anything else touched the ref.
   */
  const resolveExportVersionLabel = useCallback((options = {}) => {
    const bump = options.bump !== false;
    const manualVersion = Number.parseInt(exportVersionInput.trim(), 10);
    const hasManualVersion = Number.isFinite(manualVersion) && manualVersion > 0;
    // A number typed into the version box is a deliberate statement about
    // where this deck sits, so it counts as a known current version even for a
    // deck the app has not saved yet.
    const currentVersion = hasManualVersion
      ? manualVersion
      : (deckHasSavedVersionRef.current ? exportVersionRef.current : null);

    const version = versionForSave({ currentVersion, versionUp: bump });

    // A plain Save of a deck already at this version changes nothing — and
    // must not, or the number in the box would creep upwards on every save.
    if (version === currentVersion) return version;

    exportVersionRef.current = version;
    setExportVersion(version);
    setPrefilledVersionInput(!hasManualVersion);
    setExportVersionInput(String(version));

    return version;
  }, [exportVersionInput]);
  const inlineTitleCardImage = useCallback(async (settings) => {
    // A hosted title-card picture stays hosted; only one with nowhere to point
    // needs its bytes carried into the file. The corner logo travels the same
    // way — an export that dropped it would be missing the school's crest.
    const needsImage = shouldInlineForExport(settings?.image);
    const needsCornerLogo = shouldInlineForExport(settings?.cornerLogo);
    if (!needsImage && !needsCornerLogo) return settings;
    try {
      const { fetchImageAsDataUrl } = await loadHtmlExporter();
      const next = { ...settings };
      if (needsImage) next.image = await fetchImageAsDataUrl(settings.image);
      if (needsCornerLogo) next.cornerLogo = await fetchImageAsDataUrl(settings.cornerLogo);
      return next;
    } catch (error) {
      console.error('Failed to inline title card image', error);
      return settings;
    }
  }, []);

  const headerColorMap = useMemo(() => {
    if (slideHeaderMode === 'single') {
      return sections.reduce((acc, section) => {
        acc[section.id] = slideHeaderColor;
        return acc;
      }, {});
    }
    return sections.reduce((acc, section) => {
      acc[section.id] = slideHeaderColors[section.id] || slideHeaderColor;
      return acc;
    }, {});
  }, [sections, slideHeaderMode, slideHeaderColor, slideHeaderColors]);

  // The deck as the transfer window needs it: every section with its slides,
  // and every slide carrying the number the deck itself shows. That number is
  // the slide's place in the running deck — the old window numbered slides by
  // their index inside their section, so "Slide 3" appeared once per section
  // and told the author nothing about where it actually was.
  const deckSlideNumbers = useMemo(() => {
    const numbers = new Map();
    slides.forEach((slide, index) => {
      if (slide.slideId) numbers.set(slide.slideId, index + 1);
    });
    return numbers;
  }, [slides]);

  const transferDeckSections = useMemo(() => {
    const deckNumbers = deckSlideNumbers;

    // Title cards hold a deck position like any other slide, so leaving them
    // out of the list left gaps in the numbering with nothing to explain them:
    // a section that ended at 20 was followed by one starting at 22. They are
    // listed, numbered and named — and not selectable, because a title card's
    // content lives in its own settings rather than in blocks, so there is
    // nothing for a block-shaped export to carry.
    const titleCardEntry = (slide, kind, fallbackTitle) => ({
      slideId: slide.slideId,
      deckNumber: deckNumbers.get(slide.slideId) || 0,
      kind: 'titleCard',
      titleCardKind: kind,
      title: slide.title?.trim() || fallbackTitle,
      sectionRef: '',
      blocks: [],
      previewStyle: getTitleCardPreviewStyles(slide, titleCardBackground)
    });

    const slidesBySection = {};
    slides.forEach((slide) => {
      const sectionId = slide.section?.id || 'ungrouped';
      if (slide.type === 'section-title-card') {
        if (!slidesBySection[sectionId]) slidesBySection[sectionId] = [];
        slidesBySection[sectionId].push({
          ...titleCardEntry(slide, 'section', slide.section?.title || 'Section title'),
          // Ahead of the section's first content slide, which is where it sits
          // in the deck.
          slideIndex: -1
        });
        return;
      }
      if (slide.type !== 'section') return;
      if (!slidesBySection[sectionId]) slidesBySection[sectionId] = [];
      const position = (slide.slideIndex ?? 0) + 1;
      slidesBySection[sectionId].push({
        slideId: slide.slideId,
        deckNumber: deckNumbers.get(slide.slideId) || position,
        kind: 'slide',
        // The section-relative reference stays as the quiet second line, for
        // authors who think in "4.2" rather than in deck position.
        sectionRef: slide.section?.sectionNumber ? `${slide.section.sectionNumber}.${position}` : '',
        title: resolveSlideName(slideNames[slide.slideId], slide.slideId) || `Slide ${position}`,
        slideIndex: Number.isFinite(slide.slideIndex) ? slide.slideIndex : 0,
        blocks: slide.blocks || [],
        headerColor: headerColorMap?.[slide.section?.id] || slideHeaderColor || '#545f7d',
        bodyStyle: getBackgroundStyles(slideBackgrounds?.[slide.slideId] || bodyBackground)
      });
    });
    Object.values(slidesBySection).forEach((list) => list.sort((a, b) => a.slideIndex - b.slideIndex));

    return sections.map((section) => ({
      id: section.id,
      title: section.title || 'Untitled section',
      slides: slidesBySection[section.id] || []
    }));
  }, [
    slides, sections, slideNames, deckSlideNumbers, headerColorMap, slideHeaderColor,
    slideBackgrounds, bodyBackground, titleCardBackground
  ]);

  // The deck's own opening and closing cards, which belong to no section.
  const transferDeckEdges = useMemo(() => {
    const deckNumbers = deckSlideNumbers;
    const card = (type, fallbackTitle) => {
      const slide = slides.find((entry) => entry.type === type);
      if (!slide) return null;
      return {
        slideId: slide.slideId,
        deckNumber: deckNumbers.get(slide.slideId) || 0,
        kind: 'titleCard',
        titleCardKind: type === 'end-title-card' ? 'end' : 'start',
        title: slide.title?.trim() || fallbackTitle,
        sectionRef: '',
        blocks: [],
        previewStyle: getTitleCardPreviewStyles(slide, titleCardBackground)
      };
    };
    return {
      start: card('title-card', 'Title card'),
      end: card('end-title-card', 'End title card')
    };
  }, [slides, deckSlideNumbers, titleCardBackground]);

  const renderTransferThumbnail = useCallback((slide) => {
    if (slide.kind === 'titleCard') {
      return (
        <span
          className="drawer-slide-thumb drawer-slide-thumb--section-title h-full w-full"
          style={slide.previewStyle}
          aria-hidden="true"
        >
          <span className="drawer-title-card-thumb__content">
            <span className="drawer-title-card-thumb__title">{slide.title}</span>
          </span>
        </span>
      );
    }
    return (
      <DrawerSlideThumbnail
        blocks={slide.blocks}
        headerColor={slide.headerColor}
        bodyStyle={slide.bodyStyle}
        designMode={slideDesignMode}
      />
    );
  }, [slideDesignMode]);

  // The incoming file's slides, in the order they will arrive.
  const transferImportSlides = useMemo(() => {
    if (!selectionImportData || selectionImportData.type !== 'slides') return [];
    return (selectionImportData.slides || []).map((slide) => ({
      slideId: slide.slideId,
      title: slide.title || slide.sectionTitle || 'Slide',
      sectionTitle: slide.sectionTitle || ''
    }));
  }, [selectionImportData]);

  const sectionsForPersistence = useMemo(
    () => normalizeSectionsDividerContent(sections),
    [sections]
  );

  // Auto-save functionality
  const autoSaveKey = `lecture-template-autosave-${week}-${date}`;
  const { lastSaved, hasUnsavedChanges, loadAutoSavedData, clearAutoSavedData } = useAutoSave({
    headerData,
    schoolLogo: logo || null,
    schoolLogoName: logoName || '',
    schoolLogoSize: logoSize || 0,
    week,
    date,
    sections: sectionsForPersistence,
    bodyLayout,
    bodyBackground,
    slideBackgrounds,
    titleCardBackground,
    slideHeaderMode,
    slideHeaderColor,
    slideHeaderColors,
    slideGlobalCss,
    titleCardSettings,
    endTitleCardSettings,
    slideVisibility,
    acknowledgedBlankSlides,
    slideDurations,
    slideDeckName,
    exportFileName,
    htmlExportTitle,
    exportVersion
  }, autoSaveKey);

  // ADDED: Load initial data if provided
  useEffect(() => {
    if (!normalizedInitialData) return;

    const data = normalizedInitialData;
    if (data.headerData) {
      const defaults = createDefaultHeaderData();
      setHeaderData({
        ...defaults,
        ...data.headerData,
        showHeaderBar: data.headerData.showHeaderBar !== false
      });
    } else {
      setHeaderData((prev) => ({
        ...prev,
        courseTopic: safeString(data.courseTopic, prev.courseTopic),
        instructorName: safeString(data.instructorName, prev.instructorName),
        instructorEmail: safeString(data.instructorEmail, prev.instructorEmail),
        footerCourseInfo: safeString(data.footerCourseInfo, prev.footerCourseInfo),
        footerInstitution: safeString(data.footerInstitution, prev.footerInstitution),
        footerCopyright: safeString(data.footerCopyright, prev.footerCopyright),
        showHeaderBar: data.showHeaderBar !== false
      }));
    }
    if (data.slideDesignMode) {
      setSlideDesignMode(data.slideDesignMode === 'canvas' ? 'canvas' : 'flow');
    }
    if (data.bodyLayout) {
      setBodyLayout(data.bodyLayout);
    }
    if (data.bodyBackground) {
      setBodyBackground(data.bodyBackground);
    }
    setSlideBackgrounds(normalizeBackgroundMapForPersistence(data.slideBackgrounds));
    setTitleCardBackground(normalizeBackgroundPersistenceValue(data.titleCardBackground, { allowEmpty: false }));
    if (data.slideHeaderMode) {
      setSlideHeaderMode(data.slideHeaderMode);
    }
    if (data.slideHeaderColor) {
      setSlideHeaderColor(data.slideHeaderColor);
    }
    if (data.slideHeaderColors) {
      setSlideHeaderColors(data.slideHeaderColors);
    }
    if (data.slideGlobalCss) {
      setSlideGlobalCss(data.slideGlobalCss);
    }
    if (data.titleCardSettings) {
      setTitleCardSettings(normalizeTitleCardSettings(data.titleCardSettings));
    }
    if (data.endTitleCardSettings) {
      setEndTitleCardSettings(normalizeTitleCardSettings(data.endTitleCardSettings, DEFAULT_END_TITLE_CARD_SETTINGS));
    }
    if (data.acknowledgedBlankSlides && typeof data.acknowledgedBlankSlides === 'object') {
      setAcknowledgedBlankSlides(data.acknowledgedBlankSlides);
    }
    if (data.slideVisibility) {
      setSlideVisibility(data.slideVisibility);
    }
    if (data.slideNames) {
      setSlideNames(data.slideNames);
    }
    if (data.slideSubtitles) {
      setSlideSubtitles(data.slideSubtitles);
    }
    if (data.slideDurations) {
      setSlideDurations(data.slideDurations);
    }
    if (data.slideDeckName) {
      setSlideDeckName(data.slideDeckName);
    }
    if (data.exportFileName) {
      setExportFileName(data.exportFileName);
    }
    if (data.htmlExportTitle) {
      setHtmlExportTitle(data.htmlExportTitle);
    }
    // Where this deck left off, from its own record or — for a deck whose JSON
    // has lost `exportVersion` — from the name of the file it was opened from.
    // Without the second source `wee_v12.json` reopened at v1, and the next
    // "Save +" wrote a v2 over a file that had existed since v2.
    // Read from the RAW deck, not the normalized one: `repairDeckForLoad` and
    // `normalizeLectureJson` both fill a missing `exportVersion` in with 1, so
    // by this point every deck claims to know its version and a deck that
    // never carried one would silently open at v1 with its file name — the
    // only real record left — never consulted.
    const openedVersion = resolveOpenedDeckVersion({
      exportVersion: initialData?.exportVersion,
      fileName: openedFileNameFor(initialData)
    });
    if (openedVersion) {
      exportVersionRef.current = openedVersion;
      deckHasSavedVersionRef.current = true;
      setExportVersion(openedVersion);
      setExportVersionInput(String(openedVersion));
      setPrefilledVersionInput(true);
    }
    if ('schoolLogo' in data) {
      if (data.schoolLogo) {
        saveLogo(data.schoolLogo, data.schoolLogoName || 'Saved Logo', data.schoolLogoSize || 0);
      } else {
        removeLogo();
      }
    }
    if (data.week) {
      setWeek(safeString(data.week, '1'));
    }
    if (data.date) {
      setDate(data.date);
    }
    if (data.sections) {
      setSections(data.sections);

      // Expand existing sections on initial load so the user sees their content.
      const initialSectionIds = data.sections
        .filter((section) => section && section.id)
        .map((section) => section.id);
      setOpenSectionIds(initialSectionIds);
      setDrawerExpandedSections(initialSectionIds);
      setDrawerCollapsedSections([]);
    }
  }, [normalizedInitialData]);

  // Check for auto-saved data on mount - UPDATED to not run if initialData is present
  useEffect(() => {
    const savedData = loadAutoSavedData();
    if (savedData && savedData.timestamp && !initialData) {
      setAutoSaveData(savedData);
      setShowAutoSaveRecovery(true);
    }
  }, [loadAutoSavedData, initialData]);

  useEffect(() => {
    setSections((prevSections) => {
      const filtered = prevSections.filter((section) => !isPlaceholderNewSection(section));
      return filtered.length ? filtered : prevSections;
    });
  }, []);

  // Update tempTitle when editing starts
  useEffect(() => {
    if (isEditingTitle) {
      setTempTitle(getCourseTopicForWeek());
    }
  }, [getCourseTopicForWeek, isEditingTitle]);

  useEffect(() => {
    if (bodyLayout === 'slides') return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSectionId(entry.target.id);
          }
        });
      },
      { rootMargin: '-50% 0px -50% 0px' }
    );

    sections.forEach((section) => {
      const el = document.getElementById(section.id);
      if (el) observer.observe(el);
    });

    return () => {
      sections.forEach((section) => {
        const el = document.getElementById(section.id);
        if (el) observer.unobserve(el);
      });
    };
  }, [sections, bodyLayout]);

  useEffect(() => {
    if (bodyLayout !== 'slides') return;
    const activeSlide = slides[currentSlideIndex];
    if (activeSlide?.type === 'title-card') {
      setActiveSectionId('title-card');
      return;
    }
    if (activeSlide?.type === 'end-title-card') {
      setActiveSectionId('end-title-card');
      return;
    }
    if (activeSlide?.section) {
      setActiveSectionId(activeSlide.section.id);
    } else {
      setActiveSectionId('');
    }
  }, [bodyLayout, currentSlideIndex, slides]);

  useEffect(() => {
    setCurrentSlideIndex((prev) => Math.min(prev, Math.max(slides.length - 1, 0)));
  }, [slides.length]);

  useEffect(() => {
    if (selectionPlacementSlideId) return;
    const firstSlide = slides.find((slide) => slide.type === 'section');
    if (firstSlide?.slideId) {
      setSelectionPlacementSlideId(firstSlide.slideId);
    }
  }, [slides, selectionPlacementSlideId]);

  useEffect(() => {
    if (!selectionTargetSection) return;
    const currentPlacement = slides.find((slide) => slide.slideId === selectionPlacementSlideId);
    const firstInTarget = slides.find((slide) => slide.section?.id === selectionTargetSection);
    if (!currentPlacement || currentPlacement.section?.id !== selectionTargetSection) {
      if (firstInTarget?.slideId) {
        setSelectionPlacementSlideId(firstInTarget.slideId);
      }
    }
  }, [selectionTargetSection, slides, selectionPlacementSlideId]);

  useEffect(() => {
    if (!selectionTargetSection) return;
    if (blockTargetSlideId === '__new__') return;
    const currentTarget = blockTargetOptions.find((option) => option.id === blockTargetSlideId);
    if (currentTarget?.sectionId === selectionTargetSection) return;
    const firstInSection = blockTargetOptions.find((option) => option.sectionId === selectionTargetSection);
    if (firstInSection) {
      setBlockTargetSlideId(firstInSection.id);
    } else {
      setBlockTargetSlideId('');
    }
  }, [blockTargetOptions, blockTargetSlideId, selectionTargetSection]);

  useEffect(() => {
    if (!recentlyImportedSlideIds.length) return undefined;
    const timer = setTimeout(() => setRecentlyImportedSlideIds([]), 2200);
    return () => clearTimeout(timer);
  }, [recentlyImportedSlideIds]);

  const showSaveIndicator = useCallback((message, type = '') => {
    setSaveIndicator({ show: true, message, type });
    setTimeout(() => setSaveIndicator({ show: false, message: '', type: '' }), 3000);
  }, []);

  // Switching a source in or out of the reference list from the reference
  // block's review panel. The panel has no store of its own to edit — an entry
  // is in the list because a block put it there — so this reaches back to that
  // block and sets the same flag its own "Add to reference list" checkbox does.
  const handleSetSourceInclusion = useCallback((origin, included) => {
    setSections((prevSections) => setSourceInclusion(prevSections, origin, included));
    showSaveIndicator(included ? '💾 Source added back to the reference list' : '💾 Source removed from the reference list');
  }, [showSaveIndicator]);

  const toggleHtmlMode = useCallback((blockId) => {
    setHtmlModes(prev => ({
      ...prev,
      [blockId]: !prev[blockId]
    }));
  }, []);

  const handleRecoverAutoSave = () => {
    if (autoSaveData) {
      if (autoSaveData.headerData) setHeaderData(autoSaveData.headerData);
      if (autoSaveData.bodyLayout) setBodyLayout(autoSaveData.bodyLayout);
      if (autoSaveData.slideDesignMode) setSlideDesignMode(autoSaveData.slideDesignMode === 'canvas' ? 'canvas' : 'flow');
      if (autoSaveData.bodyBackground) setBodyBackground(autoSaveData.bodyBackground);
      setSlideBackgrounds(normalizeBackgroundMapForPersistence(autoSaveData.slideBackgrounds));
      setTitleCardBackground(normalizeBackgroundPersistenceValue(autoSaveData.titleCardBackground, { allowEmpty: false }));
      if (autoSaveData.slideHeaderMode) setSlideHeaderMode(autoSaveData.slideHeaderMode);
      if (autoSaveData.slideHeaderColor) setSlideHeaderColor(autoSaveData.slideHeaderColor);
      if (autoSaveData.slideHeaderColors) setSlideHeaderColors(autoSaveData.slideHeaderColors);
      if (autoSaveData.slideGlobalCss) setSlideGlobalCss(autoSaveData.slideGlobalCss);
      if (autoSaveData.titleCardSettings) {
        setTitleCardSettings(normalizeTitleCardSettings(autoSaveData.titleCardSettings));
      }
      if (autoSaveData.endTitleCardSettings) {
        setEndTitleCardSettings(normalizeTitleCardSettings(autoSaveData.endTitleCardSettings, DEFAULT_END_TITLE_CARD_SETTINGS));
      }
      if (autoSaveData.acknowledgedBlankSlides && typeof autoSaveData.acknowledgedBlankSlides === 'object') {
        setAcknowledgedBlankSlides(autoSaveData.acknowledgedBlankSlides);
      }
      if (autoSaveData.slideVisibility) {
        setSlideVisibility(autoSaveData.slideVisibility);
      }
      if (autoSaveData.slideDurations) {
        setSlideDurations(autoSaveData.slideDurations);
      }
      if (autoSaveData.slideDeckName !== undefined) {
        setSlideDeckName(autoSaveData.slideDeckName);
      }
      if (autoSaveData.exportFileName) {
        setExportFileName(autoSaveData.exportFileName);
      }
      if (autoSaveData.htmlExportTitle) {
        setHtmlExportTitle(autoSaveData.htmlExportTitle);
      }
      if (autoSaveData.exportVersion) {
        exportVersionRef.current = autoSaveData.exportVersion;
        deckHasSavedVersionRef.current = true;
        setExportVersion(autoSaveData.exportVersion);
        setExportVersionInput(String(autoSaveData.exportVersion));
        setPrefilledVersionInput(true);
      }
      if (autoSaveData.week) setWeek(autoSaveData.week);
      if (autoSaveData.date) setDate(autoSaveData.date);
      if (autoSaveData.sections) {
        // Through the same repair a file gets: an autosave written before slide
        // breaks were derived from slideId holds a deck with every block piled
        // onto one slide, and restoring it as-is brings the pile back.
        setSections(ensureSlideBreaksInSections(autoSaveData.sections, { generateIdFn: generateId }));

        // Expand recovered sections so the user sees their restored content.
        const recoveredSectionIds = autoSaveData.sections
          .filter((section) => section && section.id)
          .map((section) => section.id);
        setOpenSectionIds(recoveredSectionIds);
        setDrawerExpandedSections(recoveredSectionIds);
        setDrawerCollapsedSections([]);
      }
      showSaveIndicator('✅ Auto-saved content recovered');
    }
    setShowAutoSaveRecovery(false);
  };

  const handleDiscardAutoSave = () => {
    clearAutoSavedData();
    setShowAutoSaveRecovery(false);
    showSaveIndicator('🗑️ Auto-saved content discarded');
  };

  const showModeToast = (message, isEdit) => {
    if (modeToastTimerRef.current) clearTimeout(modeToastTimerRef.current);
    setModeToast({ visible: true, message, isEdit });
    modeToastTimerRef.current = setTimeout(() => {
      setModeToast(prev => ({ ...prev, visible: false }));
    }, 2000);
  };

  const handleToggleEditMode = () => {
    // If switching from edit mode to preview mode, force save any pending content
    if (isEditMode) {
      // Give any pending editor updates a moment to complete
      setTimeout(() => {
        setIsEditMode(false);
        showModeToast('Switched to Preview Mode', false);
      }, 100);
    } else {
      setIsEditMode(true);
      showModeToast('Entered Edit Mode', true);
    }
  };

  const handleEnterFullScreenPreview = () => {
    // Keep the current mode: entering full screen from Edit gives a chrome-free
    // editing canvas, from Preview a chrome-free final preview.
    if (bodyLayout !== 'slides') {
      setBodyLayout('slides');
    }
    // Remember drawer state so we can restore it on exit; start collapsed so
    // full screen is a clean canvas with just the edge arrows to reopen them.
    preFullScreenDrawersRef.current = { toolbar: isToolbarDrawerOpen, nav: isNavDrawerOpen };
    setIsToolbarDrawerOpen(false);
    setIsNavDrawerOpen(false);
    setFullScreenZoom(1);
    setFullScreenPanX(0);
    setIsFullScreenChromeHidden(false);
    setIsFullScreenPreview(true);
    showSaveIndicator(isEditMode ? '🖥️ Full screen edit' : '🖥️ Full screen preview');
  };

  const handleFullScreenZoomIn = () => {
    setFullScreenPanX(0);
    setFullScreenZoom((z) => Math.min(3, Math.round((z + 0.1) * 100) / 100));
  };
  const handleFullScreenZoomOut = () => {
    setFullScreenPanX(0);
    setFullScreenZoom((z) => Math.max(0.25, Math.round((z - 0.1) * 100) / 100));
  };
  const handleFullScreenZoomFit = () => {
    setFullScreenPanX(0);
    setFullScreenZoom(1);
  };

  // One press of Left or Right slides the slide a step across the stage. The
  // travel is capped at half a screen either way: far enough to bring the far
  // edge of a zoomed slide into view, near enough that the slide can never be
  // walked off the screen and lost.
  const handleFullScreenNudge = useCallback((direction) => {
    const step = 40;
    const limit = typeof window !== 'undefined' ? window.innerWidth / 2 : 400;
    setFullScreenPanX((x) => Math.min(Math.max(x + direction * step, -limit), limit));
  }, []);

  const handleExitFullScreenPreview = () => {
    // Present mode is full screen plus browser fullscreen and a stashed edit
    // mode, so leaving it has more to undo than leaving a plain preview.
    if (isPresenting) {
      handleExitPresentMode();
      return;
    }
    setIsFullScreenPreview(false);
    setIsFullScreenChromeHidden(false);
    setFullScreenPanX(0);
    // Restore the drawers to whatever they were before entering full screen
    setIsToolbarDrawerOpen(preFullScreenDrawersRef.current.toolbar);
    setIsNavDrawerOpen(preFullScreenDrawersRef.current.nav);
  };

  const handleEnterPresentMode = () => {
    if (bodyLayout !== 'slides') {
      setBodyLayout('slides');
    }
    presentReturnStateRef.current = { editMode: isEditMode };
    preFullScreenDrawersRef.current = { toolbar: isToolbarDrawerOpen, nav: isNavDrawerOpen };
    setIsToolbarDrawerOpen(false);
    setIsNavDrawerOpen(false);
    setFullScreenZoom(1);
    setFullScreenPanX(0);
    // Presenting always starts from the first slide of the deck with every
    // editing affordance out of the way.
    setIsEditMode(false);
    setIsFullScreenChromeHidden(true);
    setIsFullScreenPreview(true);
    setIsPresenting(true);
    // Browser fullscreen is a nice-to-have: it needs a user gesture and can be
    // blocked outright, so a rejection must not take the overlay down with it.
    const root = document.documentElement;
    const request = root?.requestFullscreen || root?.webkitRequestFullscreen;
    if (request && !document.fullscreenElement) {
      Promise.resolve(request.call(root)).catch(() => {});
    }
    showSaveIndicator('▶️ Presenting — Space advances, Esc exits');
  };

  const handleExitPresentMode = useCallback(() => {
    setIsPresenting(false);
    setIsFullScreenPreview(false);
    setIsFullScreenChromeHidden(false);
    setFullScreenPanX(0);
    setIsEditMode(presentReturnStateRef.current.editMode);
    setIsToolbarDrawerOpen(preFullScreenDrawersRef.current.toolbar);
    setIsNavDrawerOpen(preFullScreenDrawersRef.current.nav);
    const exit = document.exitFullscreen || document.webkitExitFullscreen;
    if (exit && document.fullscreenElement) {
      Promise.resolve(exit.call(document)).catch(() => {});
    }
  }, []);

  useEffect(() => {
    setFullScreenPanX(0);
  }, [currentSlideIndex]);

  // A slide's height is driven by its content, not pinned to 16:9, so a
  // bullet-heavy slide runs past the bottom of the screen and the overlay grows
  // a scrollbar. Presenting has no room for that: measure the slide's natural
  // box and scale it down until the whole thing fits.
  //
  // The measurement reads offsetWidth/offsetHeight, which ignore our own
  // transform, so scaling cannot feed back into the number it was derived from.
  useEffect(() => {
    if (!isPresenting) {
      setPresentFitScale(1);
      return undefined;
    }

    const measure = () => {
      const el = presentStageRef.current;
      if (!el) return;
      const naturalWidth = el.offsetWidth;
      const naturalHeight = el.offsetHeight;
      if (!naturalWidth || !naturalHeight) return;
      // Never scale *up*: a short slide stays at its natural size rather than
      // being blown up to fill the height.
      const next = Math.min(1, window.innerWidth / naturalWidth, window.innerHeight / naturalHeight);
      setPresentFitScale(Number.isFinite(next) && next > 0 ? next : 1);
    };

    measure();

    const el = presentStageRef.current;
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (observer && el) observer.observe(el);
    window.addEventListener('resize', measure);

    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [isPresenting, currentSlideIndex, slides]);

  // Leaving browser fullscreen by any other route (Esc handled natively, the
  // window chrome, the F11 key) has to take present mode down with it, or the
  // overlay is left covering a page with no visible way out.
  useEffect(() => {
    if (!isPresenting) return undefined;
    const handleFullscreenChange = () => {
      if (!document.fullscreenElement) handleExitPresentMode();
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, [isPresenting, handleExitPresentMode]);

  // Slide navigation itself belongs to SlideDeck, which already owns Space,
  // the arrows and the clicker's PageUp/PageDown whenever it is full screen and
  // out of edit mode. Present mode only has to add its own way out, since the
  // editor's shortcut map stands down while presenting.
  useEffect(() => {
    if (!isPresenting) return undefined;

    const handlePresentKeys = (event) => {
      const target = event.target;
      const tagName = target?.tagName?.toLowerCase();
      if (target?.isContentEditable || ['input', 'textarea', 'select'].includes(tagName)) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        handleExitPresentMode();
      }
    };

    document.addEventListener('keydown', handlePresentKeys);
    return () => document.removeEventListener('keydown', handlePresentKeys);
  }, [isPresenting, handleExitPresentMode]);

  const handleSectionUpdate = (updatedSection) => {
    setSections(prevSections =>
      prevSections.map(section =>
        section.id === updatedSection.id ? updatedSection : section
      )
    );
  };

  // Apply a column layout to the current section's blocks
  // layoutType: '1-col' | '2-col' | '3-col' | '2-1-split'

  const buildExistingBlockPlacementData = useCallback((sectionId) => {
    const targetSection = sections.find((section) => section.id === sectionId);
    const existingBlocks = (targetSection?.blocks || []).map((block) => ({
      id: block.id,
      type: block.type,
      label: block.type === 'headline' ? 'Title' : block.type === 'text' ? 'Text' : block.type,
    }));

    return {
      existingBlocks,
      placement: existingBlocks.length > 0 ? 'bottom' : undefined,
    };
  }, [sections]);

  const openAddContentModal = useCallback((sectionId, contentType, extraData = {}) => {
    const placementData = buildExistingBlockPlacementData(sectionId);
    const currentSlide = slides[currentSlideIndex];
    const targetSlideId = currentSlide?.section?.id === sectionId
      ? currentSlide.slideId || null
      : null;

    setModalContentType(contentType);
    setModalInitialData({
      sectionId,
      targetSlideId,
      ...placementData,
      ...extraData,
    });
    setIsModalOpen(true);
  }, [buildExistingBlockPlacementData, currentSlideIndex, slides]);

  const buildInitialMultiColumnData = useCallback((columnCount = 1) => {
    const safeCount = Math.max(1, Number(columnCount) || 1);
    const widthPercent = Number((100 / safeCount).toFixed(2));

    return {
      columns: Array.from({ length: safeCount }, () => ({
        widthPercent,
        cards: [{
          title: '',
          primaryTitle: '',
          categoryLabel: '',
          instructionalText: '',
          numberedList: [],
          numberedListStyle: 'numbered',
          titleAlignment: 'center',
          iconName: '',
          content: '',
          isChecklist: false,
          checklistItems: [],
          backgroundColor: 'transparent',
          borderColor: '#94a3b8',
          showBorder: true,
          listStyle: 'checkbox',
          badge: { enabled: false, type: 'theme', gradientDirection: 'to right' },
          columnSpan: 1,
        }],
      })),
      matchHeights: false,
      matchHeightColumns: [],
      blockTitleEnabled: false,
      blockTitle: '',
    };
  }, []);

  const handleOpenPresetColumnsModal = useCallback((sectionId, presetKey) => {
    const presetColumnsMap = {
      'single-column': 1,
      'two-columns': 2,
      'three-columns': 3,
    };
    const columnCount = presetColumnsMap[presetKey];
    if (!columnCount) return;

    const resolvedSectionId = sectionId || defaultSection || sections[0]?.id || 'bridge-in';
    openAddContentModal(resolvedSectionId, 'multi-column-cards', {
      multiColumnData: buildInitialMultiColumnData(columnCount),
    });
  }, [buildInitialMultiColumnData, defaultSection, openAddContentModal, sections]);

  const handleAddContent = (sectionId, contentType = 'text', options = {}) => {
    const normalizedContentType = safeString(contentType, 'text');
    const { forceModal = false, source = '' } = options || {};

    // Resolve target section: prefer explicit sectionId, then defaultSection, then first available
    let targetSectionId = sectionId || defaultSection || sections[0]?.id || 'bridge-in';

    // If the resolved ID doesn't match any section, fall back to the first available section
    if (!sections.some(s => s.id === targetSectionId)) {
      if (sections.length === 0) {
        console.warn('[handleAddContent] No sections exist');
        showSaveIndicator('⚠️ No sections available — create a section first');
        return;
      }
      targetSectionId = sections[0].id;
    }

    // Handle preset layouts
    if (normalizedContentType.startsWith('preset-')) {
      const presetKey = normalizedContentType.replace('preset-', '');
      const preset = PRESET_LAYOUTS[presetKey];

      if (preset && preset.blocks) {
        const newBlocks = preset.blocks.map(blockTemplate => ({
          ...blockTemplate,
          id: generateId()
        }));

        setSections(prevSections =>
          prevSections.map(section => {
            if (section.id !== targetSectionId) return section;

            if (source !== 'toolbar') {
              return { ...section, blocks: [...section.blocks, ...newBlocks] };
            }

            const activeSlide = slides[currentSlideIndex];
            const activeSlideId = activeSlide?.section?.id === targetSectionId ? activeSlide.slideId : null;
            if (!activeSlideId) {
              return { ...section, blocks: [...section.blocks, ...newBlocks] };
            }

            const blocks = [...section.blocks];
            const sectionSlides = splitSectionSlides(section, { includeEmpty: true, includeMeta: true });
            const matchedSlide = sectionSlides.find((slide) => slide.slideId === activeSlideId);

            if (matchedSlide?.blocks?.length) {
              const lastBlockId = matchedSlide.blocks[matchedSlide.blocks.length - 1]?.id;
              const index = blocks.findIndex((block) => block.id === lastBlockId);
              if (index !== -1) {
                blocks.splice(index + 1, 0, ...newBlocks);
              } else {
                blocks.push(...newBlocks);
              }
            } else {
              const dividerIndex = blocks.findIndex((block) => block?.type === 'divider'
                && block?.content?.isSlideBreak
                && (block?.content?.slideId === activeSlideId || block?.slideId === activeSlideId));
              if (dividerIndex !== -1) {
                blocks.splice(dividerIndex + 1, 0, ...newBlocks);
              } else {
                blocks.push(...newBlocks);
              }
            }

            return { ...section, blocks };
          })
        );
        showSaveIndicator(`✨ ${preset.name} preset added`);
        return;
      }
    }

    if (normalizedContentType === 'text' || normalizedContentType === 'headline' || normalizedContentType === 'divider') {
      if (forceModal) {
        const defaultContent = {
          text: 'Click to edit this text content.',
          headline: '<h1>Headline</h1>',
          divider: DEFAULT_DIVIDER_CONTENT,
        };
        openAddContentModal(targetSectionId, normalizedContentType, {
          content: defaultContent[normalizedContentType] ?? '',
        });
        return;
      }

      const defaultContent = {
        text: 'Click to edit this text content.',
        headline: '<h1>Headline</h1>',
        divider: DEFAULT_DIVIDER_CONTENT,
      };

      const newBlock = {
        id: generateId(),
        type: normalizedContentType,
        content: defaultContent[normalizedContentType] ?? '',
      };

      setSections(prevSections =>
        prevSections.map(section =>
          section.id === targetSectionId
            ? { ...section, blocks: [...section.blocks, newBlock] }
            : section
        )
      );
      const feedbackMap = { text: '➕ Text block added', headline: '➕ Headline added', divider: '➕ Divider added' };
      showSaveIndicator(feedbackMap[normalizedContentType] || '➕ Block added');
    } else {
      openAddContentModal(targetSectionId, normalizedContentType);
    }
  };

  const handleAddBlockAtTop = (sectionId, contentType = 'text') => {
    const normalizedContentType = safeString(contentType, 'text');
    // Handle preset layouts
    if (normalizedContentType.startsWith('preset-')) {
      const presetKey = normalizedContentType.replace('preset-', '');
      const preset = PRESET_LAYOUTS[presetKey];

      if (preset && preset.blocks) {
        const newBlocks = preset.blocks.map(blockTemplate => ({
          ...blockTemplate,
          id: generateId()
        }));

        setSections(prevSections =>
          prevSections.map(section =>
            section.id === sectionId
              ? { ...section, blocks: [...newBlocks, ...section.blocks] }
              : section
          )
        );
        showSaveIndicator(`✨ ${preset.name} preset added`);
        return;
      }
    }

    if (normalizedContentType === 'text' || normalizedContentType === 'headline' || normalizedContentType === 'html' || normalizedContentType === 'divider') {
      const newBlock = {
        id: generateId(),
        type: normalizedContentType,
        content:
          normalizedContentType === 'headline'
            ? '<h1>Headline</h1>'
            : normalizedContentType === 'html'
            ? ''
            : normalizedContentType === 'text'
            ? 'Click to edit this text content.'
            : DEFAULT_DIVIDER_CONTENT,
        ...(normalizedContentType === 'html'
          ? {
              language: 'html',
              mode: 'displayCode',
              options: { softWrap: false, showLineNumbers: true, allowScripts: false }
            }
          : {}),
      };

      setSections(prevSections =>
        prevSections.map(section =>
          section.id === sectionId
            ? { ...section, blocks: [newBlock, ...section.blocks] }
            : section
        )
      );
      showSaveIndicator(normalizedContentType === 'html' ? '➕ Code block added' : '➕ Text content added');
    } else {
      setModalContentType(normalizedContentType);
      setModalInitialData({ sectionId, insertAtIndex: 0 });
      setIsModalOpen(true);
    }
  };

  // Add a new block directly below the specified block within a section
  const handleAddBlockBelow = (blockId, contentType = 'text', sectionId) => {
    const normalizedContentType = safeString(contentType, 'text');
    console.log('[handleAddBlockBelow] Called with:', { blockId, contentType, sectionId });

    // Handle preset layouts
    if (normalizedContentType.startsWith('preset-')) {
      const presetKey = normalizedContentType.replace('preset-', '');
      const preset = PRESET_LAYOUTS[presetKey];

      if (preset && preset.blocks) {
        const newBlocks = preset.blocks.map(blockTemplate => ({
          ...blockTemplate,
          id: generateId()
        }));

        setSections(prevSections =>
          prevSections.map(section => {
            if (section.id !== sectionId) return section;
            const blocks = [...section.blocks];
            const index = blocks.findIndex(b => b.id === blockId);
            if (index === -1) {
              blocks.push(...newBlocks);
            } else {
              blocks.splice(index + 1, 0, ...newBlocks);
            }
            return { ...section, blocks };
          })
        );
        showSaveIndicator(`✨ ${preset.name} preset added`);
        return;
      }
    }

    if (normalizedContentType === 'text' || normalizedContentType === 'headline' || normalizedContentType === 'html' || normalizedContentType === 'divider') {
      const newBlock = {
        id: generateId(),
        type: normalizedContentType,
        content:
          normalizedContentType === 'headline'
            ? '<h1>Headline</h1>'
            : normalizedContentType === 'html'
            ? ''
            : normalizedContentType === 'text'
            ? 'Click to edit this text content.'
            : DEFAULT_DIVIDER_CONTENT,
        ...(normalizedContentType === 'html'
          ? {
              language: 'html',
              mode: 'displayCode',
              options: { softWrap: false, showLineNumbers: true, allowScripts: false }
            }
          : {})
        };

      setSections(prevSections =>
        prevSections.map(section => {
          if (section.id !== sectionId) return section;
          const blocks = [...section.blocks];
          const index = blocks.findIndex(b => b.id === blockId);
          if (index === -1) {
            blocks.push(newBlock);
          } else {
            blocks.splice(index + 1, 0, newBlock);
          }
          return { ...section, blocks };
        })
      );
      showSaveIndicator(normalizedContentType === 'html' ? '➕ Code block added' : '➕ Text content added');
    } else {
      console.log('[handleAddBlockBelow] Opening modal with:', { sectionId, insertAfterBlockId: blockId, contentType: normalizedContentType });
      setModalContentType(normalizedContentType);
      setModalInitialData({ sectionId, insertAfterBlockId: blockId });
      setIsModalOpen(true);
    }
  };

  /**
   * Add a slide after a given block.
   *
   * `variant` decides what lands on it — see slideStarters.js. It defaults to
   * blank, so any caller that does not ask for content does not get any.
   */
  const handleAddSlideAfter = (sectionId, afterBlockId, variant = SLIDE_VARIANTS.BLANK) => {
    if (!sectionId) return;

    setSections(prevSections => {
      const updatedSections = prevSections.map(section => {
        if (section.id !== sectionId) return section;
        const blocks = [...section.blocks];
        const usedSlideIds = collectSlideIds(blocks);
        const sectionSlides = splitSectionSlides(section, { includeEmpty: true, includeMeta: true });
        const newSlideId = resolveNextAvailableSlideId(section.id, usedSlideIds, sectionSlides.length);
        const dividerBlock = createSlideDivider(newSlideId);
        const starterBlocks = createSlideStarterBlocks({ slideId: newSlideId, variant, generateId });
        const insertIndex = afterBlockId
          ? blocks.findIndex(block => block.id === afterBlockId)
          : blocks.length - 1;
        if (insertIndex === -1) {
          blocks.push(dividerBlock, ...starterBlocks);
        } else {
          blocks.splice(insertIndex + 1, 0, dividerBlock, ...starterBlocks);
        }
        return { ...section, blocks };
      });

      // Calculate the new slide index and advance to it
      setTimeout(() => {
        const newSlides = buildSlidesFromJson({
          sections: updatedSections,
          titleCard: titleCardSettings,
          endTitleCard: endTitleCardSettings,
          includeEmptySections: true
        });
        // Find the section and count slides before the new one
        let targetSlideIndex = 0;
        for (const slide of newSlides) {
          if (slide.section?.id === sectionId) {
            // Find the last slide in this section (the newly added one)
            const sectionSlides = newSlides.filter(s => s.section?.id === sectionId);
            const lastSlideInSection = sectionSlides[sectionSlides.length - 1];
            targetSlideIndex = newSlides.indexOf(lastSlideInSection);
            break;
          }
        }
        setCurrentSlideIndex(targetSlideIndex);
      }, 0);

      return updatedSections;
    });
    showSaveIndicator('➕ Slide added');
  };

  const handleAddSectionTitleSlide = (sectionId) => {
    if (!sectionId) return;
    setSections((prevSections) => {
      const updatedSections = prevSections.map((section) => {
        if (section.id !== sectionId) return section;
        const nextSettings = section.sectionTitleCard
          ? {
              ...section.sectionTitleCard,
              enabled: true,
              slideId: section.sectionTitleCard.slideId || buildSectionTitleCardId(section.id)
            }
          : buildSectionTitleCardSettings(section);
        return { ...section, sectionTitleCard: nextSettings };
      });

      setTimeout(() => {
        const newSlides = buildSlidesFromJson({
          sections: updatedSections,
          titleCard: titleCardSettings,
          endTitleCard: endTitleCardSettings,
          includeEmptySections: true,
          sectionNumberMap
        });
        const targetSlide = newSlides.find((slide) => slide.type === 'section-title-card' && slide.section?.id === sectionId);
        if (targetSlide) {
          setCurrentSlideIndex(newSlides.indexOf(targetSlide));
        }
      }, 0);

      return updatedSections;
    });
    showSaveIndicator('➕ Section title slide added');
  };


  /**
   * Add a slide after the one identified by `slideIndex` / `slideId`.
   *
   * The shared body of every "new slide" command: the Slide Tools button, the
   * New Slide menu, the Ctrl+Shift+N shortcut and the drawer's "Add slide
   * below" all arrive here. `variant` is the only thing that differs between
   * Blank Slide and Text Slide — see slideStarters.js.
   */
  const handleAddSlideFromDrawer = (sectionId, slideIndex, slideId, globalIndex, variant = SLIDE_VARIANTS.BLANK) => {
    if (!sectionId) return;

    let createdSlideId = null;

    setSections((prevSections) => {
      const updatedSections = prevSections.map((section) => {
        if (section.id !== sectionId) return section;

        const blocks = [...section.blocks];
        const sectionSlides = splitSectionSlides(section, { includeEmpty: true, includeMeta: true });

        let resolvedSlideIndex = -1;
        if (slideId) {
          resolvedSlideIndex = sectionSlides.findIndex((slide) => slide.slideId === slideId);
        }

        if (resolvedSlideIndex < 0 && Number.isFinite(globalIndex)) {
          const slideFromDeck = slides[globalIndex];
          if (slideFromDeck?.section?.id === sectionId) {
            if (slideFromDeck.slideId) {
              resolvedSlideIndex = sectionSlides.findIndex((slide) => slide.slideId === slideFromDeck.slideId);
            }
            if (resolvedSlideIndex < 0 && Number.isFinite(slideFromDeck.slideIndex)) {
              resolvedSlideIndex = slideFromDeck.slideIndex;
            }
          }
        }

        if (resolvedSlideIndex < 0 && Number.isFinite(slideIndex)) {
          resolvedSlideIndex = slideIndex;
        }

        if (resolvedSlideIndex < 0 || resolvedSlideIndex >= sectionSlides.length) {
          return section;
        }

        const targetSlide = sectionSlides[resolvedSlideIndex];
        const usedSlideIds = collectSlideIds(blocks);
        createdSlideId = resolveNextAvailableSlideId(section.id, usedSlideIds, sectionSlides.length);

        const dividerBlock = createSlideDivider(createdSlideId);
        const starterBlocks = createSlideStarterBlocks({ slideId: createdSlideId, variant, generateId });

        const lastSlideBlockId = targetSlide?.blocks?.length
          ? targetSlide.blocks[targetSlide.blocks.length - 1]?.id
          : null;

        let insertIndex = lastSlideBlockId
          ? blocks.findIndex((block) => block.id === lastSlideBlockId)
          : -1;

        if (insertIndex === -1) {
          insertIndex = blocks.findIndex((block) => block?.type === 'divider'
            && block?.content?.isSlideBreak
            && (block?.content?.slideId === targetSlide.slideId || block?.slideId === targetSlide.slideId));
        }

        if (insertIndex === -1) {
          insertIndex = blocks.length - 1;
        }

        blocks.splice(insertIndex + 1, 0, dividerBlock, ...starterBlocks);
        return { ...section, blocks };
      });

      if (createdSlideId) {
        setTimeout(() => {
          const rebuiltSlides = buildSlidesFromJson({
            sections: updatedSections,
            titleCard: titleCardSettings,
            endTitleCard: endTitleCardSettings,
            includeEmptySections: true,
            sectionNumberMap,
          });
          const insertedIndex = rebuiltSlides.findIndex((slide) => slide.slideId === createdSlideId);
          if (insertedIndex >= 0) {
            setCurrentSlideIndex(insertedIndex);
          }
        }, 0);
      }

      return updatedSections;
    });

    if (createdSlideId) {
      showSaveIndicator('➕ Slide added');
    }
  };

  const handleRemoveSlide = (sectionId, slideBlocks = [], slideId = null) => {
    if (!sectionId) {
      showSaveIndicator('⚠️ Error: Section ID not provided');
      return;
    }

    const resolvedSlideId = slideId || resolveSlideIdFromBlocks(slideBlocks, null);

    if ((!slideBlocks || slideBlocks.length === 0) && !resolvedSlideId) {
      showSaveIndicator('⚠️ Error: Slide ID not provided');
      return;
    }

    const removalStatus = {
      removed: false,
      blocked: false,
      missingDivider: false
    };

    // Close whatever edit is still open in the history before taking the slide
    // out, so the removal starts a step of its own.
    //
    // Snapshots are debounced, and a burst coalesces into one entry — right for
    // typing, wrong for this. Deleting a slide within the debounce window of
    // any other edit made Ctrl+Z undo both, so the slide came back and the edit
    // before it silently went. A destructive action has to be reversible on its
    // own, which is the whole point of the shortcut on the outline row.
    commitPendingSnapshot();

    setSections(prevSections => {
      const updatedSections = prevSections.map(section => {
        if (section.id !== sectionId) return section;

        const blocks = [...section.blocks];
        // Count all slides including empty ones to determine if deletion is allowed
        const sectionSlides = splitSectionSlides(section, { includeEmpty: true });

        // Don't allow removing the last slide in a section
        if (sectionSlides.length <= 1) {
          removalStatus.blocked = true;
          return section;
        }

        // Find the blocks to remove
        const blockIdsToRemove = new Set(slideBlocks.map(b => b.id));

        // Find the divider before this slide (if any)
        let dividerIndex = -1;
        if (blockIdsToRemove.size > 0) {
          for (let i = 0; i < blocks.length; i++) {
            if (blockIdsToRemove.has(blocks[i].id)) {
              // Look backwards for a divider
              for (let j = i - 1; j >= 0; j--) {
                if (blocks[j].type === 'divider' && blocks[j].content?.isSlideBreak) {
                  dividerIndex = j;
                  break;
                }
              }
              break;
            }
          }
        } else if (resolvedSlideId) {
          dividerIndex = blocks.findIndex((block) =>
            block?.type === 'divider' &&
            block?.content?.isSlideBreak &&
            (block?.content?.slideId === resolvedSlideId || block?.slideId === resolvedSlideId)
          );
        }

        if (blockIdsToRemove.size === 0 && dividerIndex === -1) {
          removalStatus.missingDivider = true;
          return section;
        }

        // Remove the slide blocks and the divider before it
        const newBlocks = blocks.filter((block, index) => {
          return !blockIdsToRemove.has(block.id) && index !== dividerIndex;
        });

        // Deleting the *first* slide has no divider in front of it to take
        // with it, so the next slide's divider is left leading — a shape a
        // section never otherwise has, and the one that says "this section's
        // first slide was emptied out of existence". Dropping it makes the
        // slide that moved up into first place look like any other first
        // slide, and keeps a deliberate deletion distinct from an emptied
        // slide, which is restored rather than removed.
        while (newBlocks.length
          && newBlocks[0]?.type === 'divider'
          && newBlocks[0]?.content?.isSlideBreak) {
          // Only when it introduces a slide that still has blocks; a leading
          // divider in front of another one is a blank first slide, which is
          // a slide in its own right and must stay.
          const next = newBlocks[1];
          if (!next || (next.type === 'divider' && next.content?.isSlideBreak)) break;
          newBlocks.shift();
        }

        removalStatus.removed = true;
        return { ...section, blocks: newBlocks };
      });

      return updatedSections;
    });

    if (removalStatus.removed) {
      // A deleted slide's background goes with it. Left behind, an uploaded one
      // stayed in the file as a megabyte of base64 for a slide that no longer
      // exists — and was written out again by every later save. The cleanup at
      // save time would catch it, but the right moment to forget it is now.
      if (resolvedSlideId) {
        setSlideBackgrounds((prev) => {
          if (!prev || !(resolvedSlideId in prev)) return prev;
          const { [resolvedSlideId]: removed, ...rest } = prev;
          syncToSlides('slideBackgrounds', rest);
          return rest;
        });
      }
      // Navigate to the previous slide
      setTimeout(() => {
        setCurrentSlideIndex(prev => Math.max(0, prev - 1));
      }, 0);
      // Named, because the alternative to knowing about Ctrl+Z here is
      // rebuilding the slide by hand.
      showSaveIndicator('🗑️ Slide removed - Ctrl+Z to undo');
    } else if (removalStatus.blocked) {
      showSaveIndicator('⚠️ Cannot remove the only slide in a section');
    } else if (removalStatus.missingDivider) {
      showSaveIndicator('⚠️ Error: Slide break not found');
    }
  };

  const handleClearSlideBlocks = (sectionId, slideBlocks) => {
    if (!sectionId || !slideBlocks || slideBlocks.length === 0) return;

    // Get the slideId from the blocks being cleared
    const slideId = resolveSlideIdFromBlocks(slideBlocks, generateId());

    setSections(prevSections => {
      const updatedSections = prevSections.map(section => {
        if (section.id !== sectionId) return section;

        const blocks = [...section.blocks];
        const blockIdsToRemove = new Set(slideBlocks.map(b => b.id));

        // Find the index where the first block is being removed
        const firstBlockIndex = blocks.findIndex(block => blockIdsToRemove.has(block.id));

        // Remove only the slide content blocks
        const newBlocks = blocks.filter(block => !blockIdsToRemove.has(block.id));

        // If we removed all content from a slide, insert an empty slide divider to preserve the slide
        if (firstBlockIndex >= 0) {
          // Check if there's already a divider with this slideId
          const hasDividerForSlide = newBlocks.some(
            block => block.type === 'divider' &&
            block.content?.isSlideBreak &&
            block.content?.slideId === slideId
          );

          // If no divider exists for this slide, add one at the original position
          if (!hasDividerForSlide) {
            const slideDivider = createSlideDivider(slideId);
            newBlocks.splice(firstBlockIndex, 0, slideDivider);
          }
        }

        return { ...section, blocks: newBlocks };
      });

      return updatedSections;
    });
    showSaveIndicator('🧹 Slide blocks cleared');
  };

  /**
   * Move one slide, or several, to a place in the deck.
   *
   * `refs` are the slides to move — `{ sectionId, slideId, slideIndex }`, in
   * any order — and they may come from more than one section. They arrive in
   * the destination in deck order, together, as a block.
   *
   * The list surgery is in utils/slideMoves.js, under test. What is left here
   * is what only this component can do: splitting sections into slides and
   * flattening them back, minting ids in the deck's namespace, and the two
   * things a move has to tell the rest of the app about — a slide whose id
   * changed, and a section left with nothing in it.
   */
  const moveSlides = ({ refs, toSectionId, toIndex = 0, position = 'before' }) => {
    const requested = (Array.isArray(refs) ? refs : []).filter((ref) => ref && ref.sectionId);
    if (!requested.length) return;

    // The outline is already looking at the place these are going.
    holdDrawerScrollRef.current = true;

    setSections((prevSections) => {
      const working = [...prevSections];
      if (!working.some((section) => section.id === toSectionId)) return prevSections;

      const slidesBySection = {};
      working.forEach((section) => {
        slidesBySection[section.id] = splitSectionSlides(section, { includeEmpty: true, includeMeta: true });
      });

      // Every id in use before the move, so a slide arriving in a new section
      // can be given one that collides with nothing — including the blank a
      // section it just emptied is about to regenerate.
      const usedSlideIds = new Set();
      Object.values(slidesBySection).forEach((sectionSlides) => {
        sectionSlides.forEach((slide) => { if (slide?.slideId) usedSlideIds.add(slide.slideId); });
      });

      /**
       * A slide id encodes which section it belongs to (`<sectionId>-slide-N`),
       * and an emptied section regenerates `<sectionId>-slide-0` for the blank
       * slide it is left showing. So a slide that moved to another section and
       * kept its old id collided with that regenerated one, whenever the slide
       * dragged out was its section's first — which, for the last slide left in
       * a section, it always is.
       *
       * The drawer keys both slide names and its "missing content" flags by id,
       * so the two slides then shared a name and one inherited the other's
       * emptiness: a phantom copy of the moved slide left behind in the old
       * section, and the real one flagged empty in its new one.
       *
       * Moving between sections therefore mints an id in the destination's
       * namespace. Within one section the id is left alone — nothing is
       * regenerated there, and keeping it means undo and autosave still line up.
       */
      const mintSlideId = (sectionId) => {
        let index = 0;
        let candidate = buildSectionSlideId(sectionId, index);
        while (usedSlideIds.has(candidate)) {
          index += 1;
          candidate = buildSectionSlideId(sectionId, index);
        }
        usedSlideIds.add(candidate);
        return candidate;
      };

      const plan = planSlideMove({
        slidesBySection,
        sectionOrder: working.map((section) => section.id),
        refs: requested,
        toSectionId,
        toIndex,
        position,
        mintSlideId
      });
      if (!plan.moved) return prevSections;

      const { renamedIds, emptiedSectionIds: emptiedSourceIds } = plan;
      const touchedSections = new Set(plan.touchedSectionIds);
      const finalizedSections = working.map((section) => (
        touchedSections.has(section.id)
          ? {
            ...section,
            blocks: flattenSlidesToBlocks(plan.slidesBySection[section.id] || [], section.id)
          }
          : section
      ));

      /**
       * Carry each slide's own settings across to its new id, and drop the old
       * entries. Leaving them behind is what let the blank slide of an emptied
       * section show the moved slide's name: the section regenerates the id the
       * moved slide used to have, and it would find the old name still sitting
       * under it.
       */
      const rekey = (setter, source) => {
        const pairs = renamedIds.filter(([from]) => source[from] !== undefined);
        if (!pairs.length) return;
        setter((prev) => {
          const next = { ...prev };
          pairs.forEach(([from, to]) => {
            next[to] = prev[from];
            delete next[from];
          });
          return next;
        });
      };
      rekey(setSlideNames, slideNames);
      rekey(setSlideSubtitles, slideSubtitles);
      rekey(setSlideDurations, slideDurations);
      rekey(setSlideVisibility, slideVisibility);

      // The move emptied a section the slides came from. Ask what should
      // happen to it rather than silently leaving a blank flagged as missing
      // content. Raised after the move, never in front of it: the move is what
      // was asked for and is not in question. One prompt at a time, so a move
      // that drains two sections asks about the first and leaves the second to
      // the drawer's own "needs attention" flag.
      //
      // An empty section stores no blocks, so the blank it renders is the
      // section's own first slide id — the same one splitSectionSlides derives
      // for it, which is what the drawer will key its flag by.
      if (emptiedSourceIds.length) {
        const emptiedId = emptiedSourceIds[0];
        const emptied = working.find((section) => section.id === emptiedId);
        setEmptiedSectionPrompt({
          sectionId: emptiedId,
          sectionTitle: emptied?.title || '',
          blankSlideId: buildSectionSlideId(emptiedId, 0)
        });
      }

      return finalizedSections;
    });
    setSlideDropTarget(null);
    setDraggingSlide(null);

    setDrawerSectionExpansion(toSectionId, true);
  };

  // The single-slide move every menu, arrow and drop already speaks.
  const moveSlide = ({ fromSectionId, fromIndex, toSectionId, toIndex, position = 'before', slideId }) => {
    moveSlides({
      refs: [{ sectionId: fromSectionId, slideId, slideIndex: fromIndex }],
      toSectionId,
      toIndex,
      position
    });
  };


  /* ---------------- "New section after this slide…" ----------------------
     One command, two menus, one operation.

     The structural half is `planSectionSplit` (utils/sectionSplit.js), which
     is pure and delegates the slide transfer to `planSlideMove` — so a slide
     that changes section here is re-minted and re-tagged by exactly the code
     that does it for "Move to section…", rather than by a second opinion.

     What is left here is what only this component can do: splitting sections
     into slides and flattening them back, minting ids in the deck's namespace,
     creating the section record, and carrying each moved slide's own settings
     across to its new id. */

  /**
   * The new section's blocks, in a shape the deck can actually read back.
   *
   * A section's first slide has no slide-break divider of its own —
   * `flattenSlidesToBlocks` writes one only for `index > 0`, so the first
   * slide's existence is implied purely by its blocks (see
   * slideSegmentation.js). That is fine everywhere it already happens, and
   * exactly wrong here: a split whose first transferred slide is blank would
   * flatten to markup that has no record of it at all, and the slide would
   * vanish along with its name, background and duration.
   *
   * Giving that one slide a leading divider is what the deck already does to
   * repair a first slide emptied by hand, so the shape is one the rest of the
   * app reads correctly: `segmentBlocksIntoSlides` sees a divider introducing
   * an empty run and yields the blank slide under its own id, and
   * `hasLostFirstSlide` does not mistake it for damage, because the block
   * after it is another divider rather than a slide's content.
   *
   * Scoped to the section being created rather than pushed into
   * `flattenSlidesToBlocks`: every other caller flattens a list that came
   * straight back from `splitSectionSlides`, where the situation cannot arise,
   * and changing the shared serializer would rewrite the stored markup of
   * every deck for a case only this operation can produce.
   */
  const withRepresentableFirstSlide = (sectionSlides, sectionId) => {
    const blocks = flattenSlidesToBlocks(sectionSlides, sectionId);
    const [first] = sectionSlides;
    const firstIsBlank = sectionSlides.length > 1 && !(first?.blocks || []).length;
    if (!firstIsBlank || !first?.slideId) return blocks;
    return [createSlideDivider(first.slideId), ...blocks];
  };

  /** Open the dialog for a slide. Both entry points call this and nothing else. */
  const openCreateSectionModal = useCallback((ref) => {
    if (!ref?.sectionId) return;
    setCreateSectionRequest({
      sectionId: ref.sectionId,
      slideId: ref.slideId || '',
      slideIndex: Number.isFinite(ref.slideIndex) ? ref.slideIndex : null
    });
  }, []);

  const closeCreateSectionModal = useCallback(() => setCreateSectionRequest(null), []);

  /** The Slide Tools entry: the slide currently on screen. */
  const handleCreateSectionFromToolbar = useCallback(() => {
    const active = slides[currentSlideIndex];
    setShowNewSlideMenu(false);
    if (!active?.section?.id || active.type !== 'section') {
      // Title cards belong to the deck rather than to a section's running
      // order, so there is no "after this slide" for one.
      showSaveIndicator('Choose a content slide to start a new section after.');
      return;
    }
    openCreateSectionModal({
      sectionId: active.section.id,
      slideId: active.slideId,
      slideIndex: active.slideIndex
    });
    // showSaveIndicator is declared further down the component.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSlideIndex, openCreateSectionModal, slides]);

  /**
   * What the dialog shows: the slide it starts after, and the slides that will
   * move. Read from live state through the same helper the operation uses, so
   * the preview cannot describe a split different from the one that happens.
   */
  const createSectionPreview = useMemo(() => {
    if (!createSectionRequest) return null;
    const section = sections.find((entry) => entry.id === createSectionRequest.sectionId);
    if (!section) return null;

    const sectionSlides = splitSectionSlides(section, { includeEmpty: true, includeMeta: true });
    const { index, trailing } = trailingSlidesAfter(sectionSlides, {
      slideId: createSectionRequest.slideId,
      slideIndex: createSectionRequest.slideIndex
    });
    if (index < 0) return null;

    // The name the outline and the header bar already show for a slide: its own
    // title, then its section's, then its position. Never the slide id, which
    // is bookkeeping.
    const describe = (slideId, positionInSection) => ({
      slideId,
      deckNumber: deckSlideNumbers.get(slideId) || null,
      name: resolveSlideName(slideNames?.[slideId], slideId)
        || (section.title || '').trim()
        || `Slide ${positionInSection + 1}`
    });

    return {
      fromSectionTitle: section.title || '',
      afterSlide: describe(sectionSlides[index]?.slideId, index),
      trailingSlides: trailing.map((slide, offset) => describe(slide?.slideId, index + 1 + offset))
    };
  }, [createSectionRequest, deckSlideNumbers, sections, slideNames]);

  /**
   * Create the section. The selected slide stays put; everything after it in
   * the same section comes along, and a selected slide that was already last
   * gets a blank slide instead.
   */
  const handleCreateSectionAfterSlide = (rawTitle) => {
    const request = createSectionRequest;
    const title = String(rawTitle ?? '').trim();
    if (!request?.sectionId || !title) return;

    // A structural change is its own history step. Snapshots are debounced and
    // a burst coalesces into one entry, which would fold this together with
    // whatever edit came just before it — and undoing a section split has to
    // put the deck back exactly as it was, not further.
    commitPendingSnapshot();

    let outcome = null;

    setSections((prevSections) => {
      const working = [...prevSections];
      if (!working.some((section) => section.id === request.sectionId)) return prevSections;

      const slidesBySection = {};
      working.forEach((section) => {
        slidesBySection[section.id] = splitSectionSlides(section, { includeEmpty: true, includeMeta: true });
      });

      // Same id namespace as every other section: `generateId` with a
      // collision loop, which is what handleAddSection uses. Never a slug and
      // never a reserved BOPPPS id, both because a section id is the prefix of
      // every slide id inside it and because two sections may share a title.
      const usedSectionIds = new Set(working.map((section) => section.id));
      let newSectionId = generateId();
      while (usedSectionIds.has(newSectionId)) newSectionId = generateId();

      const usedSlideIds = new Set();
      Object.values(slidesBySection).forEach((sectionSlides) => {
        sectionSlides.forEach((slide) => { if (slide?.slideId) usedSlideIds.add(slide.slideId); });
      });
      const mintSlideId = (sectionId) => {
        let index = 0;
        let candidate = buildSectionSlideId(sectionId, index);
        while (usedSlideIds.has(candidate)) {
          index += 1;
          candidate = buildSectionSlideId(sectionId, index);
        }
        usedSlideIds.add(candidate);
        return candidate;
      };

      const plan = planSectionSplit({
        slidesBySection,
        sectionOrder: working.map((section) => section.id),
        fromSectionId: request.sectionId,
        afterSlideId: request.slideId,
        afterSlideIndex: request.slideIndex,
        newSectionId,
        mintSlideId,
        // The blocks a new blank slide starts with — the same call "New Blank
        // Slide" makes, which deliberately returns none.
        createBlankSlideBlocks: (slideId) => createSlideStarterBlocks({
          slideId,
          variant: SLIDE_VARIANTS.BLANK,
          generateId
        })
      });
      if (!plan.split) return prevSections;

      const touched = new Set(plan.touchedSectionIds);
      const rebuilt = working.map((section) => (
        touched.has(section.id)
          ? {
            ...section,
            blocks: flattenSlidesToBlocks(plan.slidesBySection[section.id] || [], section.id)
          }
          : section
      ));

      const newSection = {
        id: newSectionId,
        title,
        // The app's ordinary content section. Reserved BOPPPS ids are fixed
        // strings and cannot be produced by generateId, so a user-created
        // section is never mistaken for one.
        type: 'content',
        blocks: withRepresentableFirstSlide(plan.slidesBySection[newSectionId] || [], newSectionId)
      };
      rebuilt.splice(plan.insertSectionAtIndex, 0, newSection);

      /**
       * Carry each moved slide's own settings to the id it now has. A slide
       * changing section is re-minted (see slideMoves.js), and settings left
       * under the old key would be read by whatever slide next takes that id.
       *
       * Backgrounds are in this list even though the drag-and-drop move does
       * not rekey them — a section split that dropped every moved slide's
       * background would be losing exactly the thing the author set by hand.
       */
      const rekey = (setter, source) => {
        const pairs = plan.renamedIds.filter(([from]) => source?.[from] !== undefined);
        if (!pairs.length) return;
        setter((prev) => {
          const next = { ...prev };
          pairs.forEach(([from, to]) => {
            next[to] = prev[from];
            delete next[from];
          });
          return next;
        });
      };
      rekey(setSlideNames, slideNames);
      rekey(setSlideSubtitles, slideSubtitles);
      rekey(setSlideDurations, slideDurations);
      rekey(setSlideVisibility, slideVisibility);
      rekey(setSlideBackgrounds, slideBackgrounds);
      rekey(setAcknowledgedBlankSlides, acknowledgedBlankSlides);

      outcome = {
        newSectionId,
        title,
        transferredCount: plan.transferredCount,
        firstSlideId: plan.firstSlideId,
        sections: rebuilt
      };
      return rebuilt;
    });

    if (!outcome) {
      showSaveIndicator('Could not create the section.');
      return;
    }

    closeCreateSectionModal();
    setOpenSectionIds((prev) => Array.from(new Set([...prev, outcome.newSectionId])));
    setDrawerSectionExpansion(outcome.newSectionId, true);
    setActiveSectionId(outcome.newSectionId);

    // Go to the new section's first slide. The deck's slide list is derived
    // from `sections`, so the index only exists once that state has landed —
    // the same rebuild-and-find that adding a slide already does.
    setTimeout(() => {
      const rebuiltSlides = buildSlidesFromJson({
        sections: outcome.sections,
        titleCard: titleCardSettings,
        endTitleCard: endTitleCardSettings,
        includeEmptySections: true,
        sectionNumberMap: buildSectionNumberMap(outcome.sections)
      });
      const index = rebuiltSlides.findIndex((slide) => slide.slideId === outcome.firstSlideId);
      if (index >= 0) setCurrentSlideIndex(index);
    }, 0);

    showSaveIndicator(outcome.transferredCount > 0
      ? `${outcome.title} created with ${outcome.transferredCount} slide${outcome.transferredCount === 1 ? '' : 's'}.`
      : `${outcome.title} created with a blank slide.`);
  };

  const keepEmptiedSection = useCallback(() => {
    setEmptiedSectionPrompt((prompt) => {
      if (prompt?.blankSlideId) {
        setAcknowledgedBlankSlides((prev) => ({ ...prev, [prompt.blankSlideId]: true }));
      }
      return null;
    });
  }, []);

  const openDrawerSlideMenu = useCallback((event, slideInfo) => {
    event.preventDefault();
    event.stopPropagation();
    if (!slideInfo?.menuId) return;
    drawerSlideMenuButtonRef.current = event.currentTarget;
    setDrawerSlideMenu((prev) => {
      if (prev?.menuId === slideInfo.menuId) {
        return null;
      }
      return { ...slideInfo, mode: 'root' };
    });
  }, []);

  const handleDuplicateSlide = useCallback((sectionId, slideIndex, slideId, globalIndex) => {
    if (!sectionId) return;
    const sourceSection = sections.find((section) => section.id === sectionId);
    if (!sourceSection) return;

    const sectionSlides = splitSectionSlides(sourceSection, { includeEmpty: true, includeMeta: true });
    let resolvedIndex = -1;
    if (slideId) {
      resolvedIndex = sectionSlides.findIndex((slide) => slide.slideId === slideId);
    }
    if (resolvedIndex < 0 && Number.isFinite(globalIndex)) {
      const slideFromDeck = slides[globalIndex];
      if (slideFromDeck?.section?.id === sectionId) {
        if (Number.isFinite(slideFromDeck.slideIndex)) {
          resolvedIndex = slideFromDeck.slideIndex;
        } else if (slideFromDeck.slideId) {
          resolvedIndex = sectionSlides.findIndex((slide) => slide.slideId === slideFromDeck.slideId);
        }
      }
    }
    if (resolvedIndex < 0 && Number.isFinite(slideIndex)) {
      resolvedIndex = slideIndex;
    }
    if (resolvedIndex < 0 || resolvedIndex >= sectionSlides.length) return;

    const targetSlide = sectionSlides[resolvedIndex];
    const newSlideId = generateId();
    const duplicatedBlocks = (targetSlide.blocks || []).map((block) => {
      const clone = JSON.parse(JSON.stringify(block));
      clone.id = generateId();
      if (clone.slideId) clone.slideId = newSlideId;
      if (clone.content?.slideId) clone.content.slideId = newSlideId;
      return clone;
    });

    const nextSlides = [...sectionSlides];
    nextSlides.splice(resolvedIndex + 1, 0, { slideId: newSlideId, blocks: duplicatedBlocks });

    setSections((prevSections) => prevSections.map((section) => (
      section.id === sectionId
        ? { ...section, blocks: flattenSlidesToBlocks(nextSlides, section.id) }
        : section
    )));

    if (slideId && slideNames[slideId]) {
      setSlideNames((prev) => ({ ...prev, [newSlideId]: `${prev[slideId]} (Copy)` }));
    }
    if (slideId && slideSubtitles[slideId]) {
      setSlideSubtitles((prev) => ({ ...prev, [newSlideId]: prev[slideId] }));
    }
    if (slideId && slideDurations[slideId]) {
      setSlideDurations((prev) => ({ ...prev, [newSlideId]: prev[slideId] }));
    }

    showSaveIndicator('📄 Slide duplicated');
  }, [sections, slideDurations, slideNames, slideSubtitles, slides, showSaveIndicator]);

  const getCurrentClipboardSlide = useCallback(() => {
    const active = slides[currentSlideIndex];
    if (!active?.section?.id || !active.slideId) return null;
    return {
      slide: deepClone({ slideId: active.slideId, blocks: active.blocks || [] }),
      sectionId: active.section.id,
      metadata: {
        name: slideNames[active.slideId],
        subtitle: slideSubtitles[active.slideId],
        duration: slideDurations[active.slideId],
        background: slideBackgrounds[active.slideId],
        visible: slideVisibility[active.slideId]
      }
    };
  }, [currentSlideIndex, slideBackgrounds, slideDurations, slideNames, slideSubtitles, slideVisibility, slides]);

  const handleCopySlide = useCallback(() => {
    const payload = getCurrentClipboardSlide();
    if (!payload) return;
    setSlideClipboard({ ...payload, operation: 'copy' });
    showSaveIndicator('Slide copied.');
  }, [getCurrentClipboardSlide, showSaveIndicator]);

  const handleCutSlide = useCallback(() => {
    const payload = getCurrentClipboardSlide();
    if (!payload) return;
    const sourceSection = sections.find((section) => section.id === payload.sectionId);
    if (!sourceSection) return;
    const sectionSlides = splitSectionSlides(sourceSection, { includeEmpty: true, includeMeta: true });
    const sourceIndex = sectionSlides.findIndex((entry) => entry.slideId === payload.slide.slideId);
    if (sourceIndex < 0) return;

    const totalContentSlides = slides.filter((entry) => entry?.section?.id).length;
    let selectedId = sectionSlides[sourceIndex + 1]?.slideId || sectionSlides[sourceIndex - 1]?.slideId || payload.slide.slideId;
    setSections((prev) => prev.map((section) => {
      if (section.id !== payload.sectionId) return section;
      const nextSlides = splitSectionSlides(section, { includeEmpty: true, includeMeta: true });
      if (totalContentSlides <= 1 || nextSlides.length <= 1) {
        const blank = {
          slideId: payload.slide.slideId,
          blocks: [{ id: generateId(), type: 'text', content: '', slideId: payload.slide.slideId }]
        };
        nextSlides.splice(sourceIndex, 1, blank);
      } else {
        nextSlides.splice(sourceIndex, 1);
      }
      return { ...section, blocks: flattenSlidesToBlocks(nextSlides, section.id) };
    }));
    setSlideClipboard({ ...payload, operation: 'cut' });
    setSlideNames((prev) => { const next = { ...prev }; delete next[payload.slide.slideId]; return next; });
    setSlideSubtitles((prev) => { const next = { ...prev }; delete next[payload.slide.slideId]; return next; });
    setSlideDurations((prev) => { const next = { ...prev }; delete next[payload.slide.slideId]; return next; });
    setSlideBackgrounds((prev) => { const next = { ...prev }; delete next[payload.slide.slideId]; return next; });
    setTimeout(() => {
      const index = slides.findIndex((entry) => entry.slideId === selectedId);
      setCurrentSlideIndex(index >= 0 ? Math.min(index, Math.max(slides.length - 2, 0)) : Math.max(currentSlideIndex - 1, 0));
    }, 0);
    showSaveIndicator('Slide cut.');
  }, [currentSlideIndex, getCurrentClipboardSlide, sections, showSaveIndicator, slides]);

  const insertSlideAfterCurrent = useCallback((sourcePayload) => {
    const active = slides[currentSlideIndex];
    const targetSectionId = active?.section?.id || sections[0]?.id;
    if (!targetSectionId || !sourcePayload?.slide) return null;
    const inserted = cloneSlideWithFreshIds(sourcePayload.slide);
    setSections((prev) => prev.map((section) => {
      if (section.id !== targetSectionId) return section;
      const sectionSlides = splitSectionSlides(section, { includeEmpty: true, includeMeta: true });
      const activeIndex = sectionSlides.findIndex((entry) => entry.slideId === active?.slideId);
      sectionSlides.splice(activeIndex >= 0 ? activeIndex + 1 : sectionSlides.length, 0, inserted);
      return { ...section, blocks: flattenSlidesToBlocks(sectionSlides, section.id) };
    }));
    const meta = sourcePayload.metadata || {};
    if (meta.name) setSlideNames((prev) => ({ ...prev, [inserted.slideId]: meta.name }));
    if (meta.subtitle) setSlideSubtitles((prev) => ({ ...prev, [inserted.slideId]: meta.subtitle }));
    if (meta.duration) setSlideDurations((prev) => ({ ...prev, [inserted.slideId]: meta.duration }));
    if (meta.background != null) setSlideBackgrounds((prev) => ({ ...prev, [inserted.slideId]: deepClone(meta.background) }));
    if (meta.visible != null) setSlideVisibility((prev) => ({ ...prev, [inserted.slideId]: meta.visible }));
    setTimeout(() => {
      const rebuilt = buildSlidesFromJson({ sections: sections.map((section) => {
        if (section.id !== targetSectionId) return section;
        const list = splitSectionSlides(section, { includeEmpty: true, includeMeta: true });
        const activeIndex = list.findIndex((entry) => entry.slideId === active?.slideId);
        list.splice(activeIndex >= 0 ? activeIndex + 1 : list.length, 0, inserted);
        return { ...section, blocks: flattenSlidesToBlocks(list, section.id) };
      }), titleCard: titleCardSettings, endTitleCard: endTitleCardSettings, includeEmptySections: true, sectionNumberMap });
      const index = rebuilt.findIndex((entry) => entry.slideId === inserted.slideId);
      if (index >= 0) setCurrentSlideIndex(index);
    }, 0);
    return inserted;
  }, [currentSlideIndex, endTitleCardSettings, sectionNumberMap, sections, slides, titleCardSettings]);

  const handlePasteSlide = useCallback(() => {
    if (!slideClipboard) return;
    const inserted = insertSlideAfterCurrent(slideClipboard);
    if (!inserted) return;
    if (slideClipboard.operation === 'cut') setSlideClipboard(null);
    showSaveIndicator('Slide pasted.');
  }, [insertSlideAfterCurrent, showSaveIndicator, slideClipboard]);

  /**
   * Insert a slide after the current one.
   *
   * The single body behind every new-slide entry point — the Slide Tools
   * button, both New Slide menu items and the keyboard shortcut — so the three
   * cannot drift apart. Only `variant` differs between them.
   */
  const createSlideAfterCurrent = useCallback((variant) => {
    const active = slides[currentSlideIndex];
    const sectionId = active?.section?.id || sections[0]?.id;
    if (!sectionId) return;
    const sourceSlide = active?.section?.id === sectionId ? active : slides.find((slide) => slide.section?.id === sectionId);
    handleAddSlideFromDrawer(
      sectionId,
      sourceSlide?.slideIndex ?? 0,
      sourceSlide?.slideId,
      sourceSlide ? slides.indexOf(sourceSlide) : undefined,
      variant
    );
    setShowNewSlideMenu(false);
    // handleAddSlideFromDrawer is rebuilt every render and is not a useCallback,
    // so naming it here would defeat the memo without making anything safer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSlideIndex, sections, slides]);

  /** "Blank Slide": the slide, and nothing on it. */
  const handleNewBlankSlide = useCallback(
    () => createSlideAfterCurrent(SLIDE_VARIANTS.BLANK),
    [createSlideAfterCurrent]
  );

  /** "Text Slide": what "Blank Slide" used to make — a slide with a text box. */
  const handleNewTextSlide = useCallback(
    () => createSlideAfterCurrent(SLIDE_VARIANTS.TEXT),
    [createSlideAfterCurrent]
  );

  const handleDuplicateCurrentSlide = useCallback(() => {
    const payload = getCurrentClipboardSlide();
    if (!payload) return;
    const inserted = insertSlideAfterCurrent(payload);
    if (inserted) showSaveIndicator('Slide duplicated.');
    setShowNewSlideMenu(false);
  }, [getCurrentClipboardSlide, insertSlideAfterCurrent, showSaveIndicator]);

  const handleFormatPainter = useCallback(() => {
    if (formatPainter) {
      setFormatPainter(null);
      showSaveIndicator('Format Painter cancelled.');
      return;
    }
    const payload = getCurrentClipboardSlide();
    if (!payload) return;
    setFormatPainter({
      sourceSlideId: payload.slide.slideId,
      captured: captureSlideFormat({ slide: payload.slide, background: payload.metadata.background })
    });
    showSaveIndicator('Select a target slide to apply formatting.');
  }, [formatPainter, getCurrentClipboardSlide, showSaveIndicator]);

  useEffect(() => {
    if (!formatPainter) return;
    const target = slides[currentSlideIndex];
    if (!target?.section?.id || !target.slideId || target.slideId === formatPainter.sourceSlideId) return;
    setSections((prev) => prev.map((section) => {
      if (section.id !== target.section.id) return section;
      const list = splitSectionSlides(section, { includeEmpty: true, includeMeta: true });
      const index = list.findIndex((entry) => entry.slideId === target.slideId);
      if (index < 0) return section;
      list[index] = applySlideFormat(list[index], formatPainter.captured);
      return { ...section, blocks: flattenSlidesToBlocks(list, section.id) };
    }));
    if (formatPainter.captured.background != null) {
      setSlideBackgrounds((prev) => ({ ...prev, [target.slideId]: deepClone(formatPainter.captured.background) }));
    }
    setFormatPainter(null);
    showSaveIndicator('Formatting applied.');
  }, [currentSlideIndex, formatPainter, showSaveIndicator, slides]);

  useEffect(() => {
    const onSlideShortcut = (event) => {
      // Typing, presenting, or a dialog on screen — the last of these used to
      // ride along inside the typing guard, which asked it through focus and
      // so missed a dialog that had not claimed any.
      if (isTypingTarget(event.target) || isPresenting || isDialogOpen()) return;

      if (event.key === 'Escape') {
        if (formatPainter) {
          event.preventDefault();
          setFormatPainter(null);
          showSaveIndicator('Format Painter cancelled.');
        }
        if (showNewSlideMenu) {
          event.preventDefault();
          setShowNewSlideMenu(false);
          newSlideMenuButtonRef.current?.focus();
        }
        return;
      }
      if (!(event.ctrlKey || event.metaKey) || event.altKey) return;
      const key = event.key.toLowerCase();
      if (key === 'c' && !event.shiftKey) {
        event.preventDefault();
        handleCopySlide();
      } else if (key === 'x' && !event.shiftKey) {
        event.preventDefault();
        handleCutSlide();
      } else if (key === 'v' && !event.shiftKey && slideClipboard) {
        event.preventDefault();
        handlePasteSlide();
      } else if (key === 'n' && event.shiftKey) {
        event.preventDefault();
        handleNewBlankSlide();
      }
    };
    document.addEventListener('keydown', onSlideShortcut);
    return () => document.removeEventListener('keydown', onSlideShortcut);
  }, [formatPainter, handleCopySlide, handleCutSlide, handleNewBlankSlide, handlePasteSlide, isPresenting, showNewSlideMenu, showSaveIndicator, slideClipboard]);

  const resolveDrawerSlideRef = useCallback((sectionId, slideIndex, slideId, globalIndex) => {
    if (!sectionId) return { resolvedIndex: -1, resolvedSlideId: null };
    const sourceSection = sections.find((section) => section.id === sectionId);
    if (!sourceSection) return { resolvedIndex: -1, resolvedSlideId: null };
    const sectionSlides = splitSectionSlides(sourceSection, { includeEmpty: true, includeMeta: true });
    let resolvedIndex = -1;
    let resolvedSlideId = null;

    if (slideId) {
      resolvedIndex = sectionSlides.findIndex((slide) => slide.slideId === slideId);
      if (resolvedIndex >= 0) {
        resolvedSlideId = slideId;
      }
    }

    if (resolvedIndex < 0 && Number.isFinite(globalIndex)) {
      const slideFromDeck = slides[globalIndex];
      if (slideFromDeck?.section?.id === sectionId) {
        if (slideFromDeck.slideId) {
          resolvedIndex = sectionSlides.findIndex((slide) => slide.slideId === slideFromDeck.slideId);
          if (resolvedIndex >= 0) {
            resolvedSlideId = slideFromDeck.slideId;
          }
        }
        if (resolvedIndex < 0 && Number.isFinite(slideFromDeck.slideIndex)) {
          resolvedIndex = slideFromDeck.slideIndex;
        }
      }
    }

    if (resolvedIndex < 0 && Number.isFinite(slideIndex)) {
      resolvedIndex = slideIndex;
    }

    if (resolvedIndex < 0 || resolvedIndex >= sectionSlides.length) {
      return { resolvedIndex: -1, resolvedSlideId: null };
    }

    if (!resolvedSlideId) {
      resolvedSlideId = sectionSlides[resolvedIndex]?.slideId || slideId || null;
    }

    return { resolvedIndex, resolvedSlideId };
  }, [sections, slides]);

  const handleMoveSlideUp = (sectionId, slideIndex, slideId, globalIndex) => {
    if (!sectionId) return;
    const { resolvedIndex, resolvedSlideId } = resolveDrawerSlideRef(sectionId, slideIndex, slideId, globalIndex);
    if (resolvedIndex <= 0) return;
    moveSlide({
      fromSectionId: sectionId,
      fromIndex: resolvedIndex,
      toSectionId: sectionId,
      toIndex: resolvedIndex - 1,
      position: 'before',
      slideId: resolvedSlideId
    });
    showSaveIndicator('⬆️ Slide moved up');
  };

  const handleMoveSlideDown = (sectionId, slideIndex, slideId, globalIndex) => {
    if (!sectionId) return;
    const { resolvedIndex, resolvedSlideId } = resolveDrawerSlideRef(sectionId, slideIndex, slideId, globalIndex);
    if (resolvedIndex < 0) return;
    const sourceSection = sections.find((section) => section.id === sectionId);
    if (!sourceSection) return;
    const sectionSlides = splitSectionSlides(sourceSection, { includeEmpty: true, includeMeta: true });
    if (resolvedIndex >= sectionSlides.length - 1) return;
    moveSlide({
      fromSectionId: sectionId,
      fromIndex: resolvedIndex,
      toSectionId: sectionId,
      toIndex: resolvedIndex + 1,
      position: 'after',
      slideId: resolvedSlideId
    });
    showSaveIndicator('⬇️ Slide moved down');
  };

  /**
   * "Move to section…" from a slide's own menu.
   *
   * Same rule as dragging: if the slide the menu was opened on is one of the
   * picked ones, the whole set goes; if it is not, the menu is about that
   * slide and the picks are dropped rather than quietly coming along. They
   * arrive at the end of the target section, in deck order.
   */
  const handleMoveSlideToSection = (fromSectionId, slideIndex, slideId, targetSectionId) => {
    if (!targetSectionId) return;
    const inSelection = Boolean(slideId) && pickedSlideSet.has(slideId);
    const refs = inSelection && pickedSlideRefs.length > 1
      ? pickedSlideRefs
      : [{ sectionId: fromSectionId, slideId, slideIndex }];

    // Only a single slide already sitting in the target has nothing to do. A
    // set spread across sections still has to gather there.
    if (refs.length === 1 && fromSectionId === targetSectionId) return;

    const targetSection = sections.find((section) => section.id === targetSectionId);
    const targetSlides = targetSection
      ? splitSectionSlides(targetSection, { includeEmpty: true, includeMeta: true })
      : [];
    const targetIndex = Math.max(targetSlides.length - 1, 0);

    moveSlides({
      refs,
      toSectionId: targetSectionId,
      toIndex: targetIndex,
      position: 'after'
    });

    if (refs.length > 1) {
      showSaveIndicator(`↪️ ${refs.length} slides moved`);
      clearSlidePicks();
    } else {
      if (!inSelection) clearSlidePicks();
      showSaveIndicator('↪️ Slide moved');
    }
  };


  const handleSlideDragStart = (sectionId, slideIndex, slideId) => (e) => {
    setDraggingSection(null);
    setSectionDropTarget(null);
    // Dragging a slide that is part of the selection drags the whole
    // selection; dragging one that is not is how you say you meant only that
    // one, so the selection is dropped rather than quietly carried along.
    const inSelection = Boolean(slideId) && pickedSlideSet.has(slideId);
    const refs = inSelection && pickedSlideRefs.length
      ? pickedSlideRefs
      : [{ sectionId, slideId, slideIndex }];
    if (!inSelection) clearSlidePicks();
    setDraggingSlide({ sectionId, slideIndex, slideId, refs });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', `${sectionId}-${slideIndex}`);
    // Slides scroll the drawer while dragging for the same reason sections do;
    // they just do not collapse anything, since a slide's drop targets are the
    // rows themselves.
    startDrawerAutoScroll();
  };

  const handleSlideDragOver = (sectionId, slideIndex, position = 'before') => (e) => {
    if (!draggingSlide || draggingSection) return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    ensureSectionOpenForDrop(sectionId);
    // dragover repeats for as long as the pointer is over the same target, so
    // re-set this only when the target has actually moved. A fresh object each
    // time re-rendered the whole outline several times a second for no visible
    // change, which is the other half of what made dragging feel like a fight.
    setSlideDropTarget((prev) => (
      prev
        && prev.sectionId === sectionId
        && prev.slideIndex === slideIndex
        && prev.position === position
        ? prev
        : { sectionId, slideIndex, position }
    ));
  };

  const handleSlideDrop = (sectionId, slideIndex, position = 'before') => (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!draggingSlide || draggingSection) return;
    ensureSectionOpenForDrop(sectionId);

    const refs = draggingSlide.refs?.length
      ? draggingSlide.refs
      : [{
        sectionId: draggingSlide.sectionId,
        slideId: draggingSlide.slideId,
        slideIndex: draggingSlide.slideIndex
      }];

    // Dropping a single slide back where it started is a no-op. With several
    // in hand it is not: the rest of the selection still has to gather around
    // the one that was dropped.
    const isSameSpot = refs.length === 1
      && draggingSlide.sectionId === sectionId
      && draggingSlide.slideIndex === slideIndex;
    if (isSameSpot) {
      setSlideDropTarget(null);
      setDraggingSlide(null);
      stopDrawerAutoScroll();
      return;
    }

    moveSlides({
      refs,
      toSectionId: sectionId,
      toIndex: slideIndex,
      position
    });
    if (refs.length > 1) {
      showSaveIndicator(`↪️ ${refs.length} slides moved`);
      clearSlidePicks();
    }
    stopDrawerAutoScroll();
  };

  const handleSlideDragEnd = () => {
    setSlideDropTarget(null);
    setDraggingSlide(null);
    stopDrawerAutoScroll();
  };

  const resolveSectionInsertIndex = (list, placement) => {
    if (!Array.isArray(list)) return 0;
    if (!placement || placement === 'end') return list.length;
    if (placement === 'start') return 0;
    if (placement.startsWith('before:')) {
      const targetId = placement.replace('before:', '');
      const targetIndex = list.findIndex((section) => section.id === targetId);
      return targetIndex >= 0 ? targetIndex : list.length;
    }
    if (placement.startsWith('after:')) {
      const targetId = placement.replace('after:', '');
      const targetIndex = list.findIndex((section) => section.id === targetId);
      return targetIndex >= 0 ? targetIndex + 1 : list.length;
    }
    return list.length;
  };

  const moveSection = ({ fromId, toId, position = 'before' }) => {
    setSections((prevSections) => {
      const working = [...prevSections];
      const fromIndex = working.findIndex((section) => section.id === fromId);
      const toIndex = working.findIndex((section) => section.id === toId);
      if (fromIndex === -1 || toIndex === -1) return prevSections;

      const [movedSection] = working.splice(fromIndex, 1);
      if (!movedSection) return prevSections;
      const adjustedToIndex = working.findIndex((section) => section.id === toId);
      const insertionIndex = position === 'after' ? adjustedToIndex + 1 : adjustedToIndex;
      working.splice(insertionIndex, 0, movedSection);
      return working;
    });
  };

  /**
   * Auto-scroll the drawer while something is being dragged over it.
   *
   * HTML5 drag-and-drop does not scroll a scroll container on its own, so
   * moving a section past one that is off screen meant dragging to the edge,
   * watching nothing happen, dropping, scrolling, and starting again. It is
   * worse the taller the rows are, which is exactly when reordering matters.
   *
   * Driven by requestAnimationFrame off the last known pointer position rather
   * than by the dragover event itself: dragover stops firing when the pointer
   * stops moving, so an event-driven version would scroll only while the
   * pointer jiggled and stall the moment someone held still at the edge —
   * which is the natural thing to do when you are waiting for a scroll.
   */
  const drawerScrollRef = useRef(null);
  const dragAutoScrollRef = useRef({
    pointerY: null,
    frame: null,
    // Whether each edge is allowed to pull yet. See the arming rule in the
    // loop below.
    armedTop: false,
    armedBottom: false
  });

  const stopDrawerAutoScroll = useCallback(() => {
    const state = dragAutoScrollRef.current;
    if (state.frame !== null) cancelAnimationFrame(state.frame);
    state.frame = null;
    state.pointerY = null;
    state.armedTop = false;
    state.armedBottom = false;
  }, []);

  const startDrawerAutoScroll = useCallback(() => {
    const state = dragAutoScrollRef.current;
    if (state.frame !== null) return;
    // Both edges start disarmed. An edge arms the first time the pointer is
    // seen outside it, so a drag that *begins* inside a band does not scroll
    // until the author has actually asked for it.
    state.armedTop = false;
    state.armedBottom = false;

    // The band at each end that pulls, and how fast it pulls at the very edge.
    //
    // Measured in pixels per SECOND, not per frame. Per-frame movement is a
    // speed that depends on the monitor: the same constant scrolls twice as
    // fast on a 120Hz display as on a 60Hz one, which is most of why this felt
    // uncontrollable. Multiplying by real elapsed time makes it the same speed
    // everywhere.
    //
    // 340px/s is a bit under half a drawer-height per second — fast enough to
    // cross a long deck without waiting, slow enough to stop on the row you
    // wanted. Speed ramps with depth into the band, so resting just inside the
    // edge gives a slow creep and pushing right to it gives the full rate.
    const EDGE = 64;
    const MAX_SPEED_PER_SEC = 340;
    let previous = null;

    const step = (now) => {
      const pane = drawerScrollRef.current;
      const y = state.pointerY;
      // Clamped: a backgrounded tab or a long frame would otherwise return with
      // one enormous delta and jump the list.
      const elapsed = previous === null ? 16 : Math.min(now - previous, 50);
      previous = now;

      if (pane && typeof y === 'number') {
        const { top, bottom } = pane.getBoundingClientRect();
        const inTop = y < top + EDGE;
        const inBottom = y > bottom - EDGE;

        /**
         * An edge arms the first time the pointer is outside it, and only an
         * armed edge pulls.
         *
         * Picking up a slide near the foot of the drawer put the pointer inside
         * the bottom band before the drag had gone anywhere, so the very first
         * dragover started scrolling down — the list jumped away underneath a
         * drag that was heading up. Arming fixes that without a timer: grabbing
         * low leaves the bottom disarmed, while the top is already armed
         * (the pointer is plainly outside it), so dragging up works at once and
         * the bottom starts working as soon as the pointer has left it.
         */
        if (!inTop) state.armedTop = true;
        if (!inBottom) state.armedBottom = true;

        let rate = 0;
        if (inTop && state.armedTop) {
          rate = -MAX_SPEED_PER_SEC * Math.min(1, (top + EDGE - y) / EDGE);
        } else if (inBottom && state.armedBottom) {
          rate = MAX_SPEED_PER_SEC * Math.min(1, (y - (bottom - EDGE)) / EDGE);
        }
        if (rate !== 0) pane.scrollTop += (rate * elapsed) / 1000;
      }
      state.frame = requestAnimationFrame(step);
    };
    state.frame = requestAnimationFrame(step);
  }, []);

  /**
   * Records where the pointer is for the loop above. Deliberately does not call
   * preventDefault: this is a passive observer on the scroll pane, and calling
   * it here would mark the whole drawer as a drop target and let a section be
   * dropped into dead space between rows.
   */
  const handleDrawerDragOver = useCallback((e) => {
    dragAutoScrollRef.current.pointerY = e.clientY;
  }, []);

  /**
   * The way back to the top of a long outline.
   *
   * A deck of sixty slides is several screens of drawer, and the section you
   * started from is a long flick away once you are down among the last ones.
   * The button appears only after the list has actually been scrolled — a
   * control that says "top" while you are already at the top is clutter — and
   * sits over the foot of the pane, where the thumb already is.
   */
  const syncDrawerTopJump = useCallback(() => {
    const pane = drawerScrollRef.current;
    setShowDrawerTopJump(!!pane && pane.scrollTop > DRAWER_TOP_JUMP_THRESHOLD);
  }, []);

  const handleDrawerScroll = useCallback(() => {
    syncDrawerTopJump();
  }, [syncDrawerTopJump]);

  const handleDrawerScrollToTop = useCallback(() => {
    const pane = drawerScrollRef.current;
    if (!pane) return;
    const start = pane.scrollTop;

    // Focus moves off the button before it goes away: the button unmounts the
    // moment the pane reaches the top, and an unmounted focused element drops
    // focus to the body. The pane takes it instead, so arrow keys carry on
    // scrolling the list the author was just reading.
    pane.focus?.({ preventScroll: true });

    // Smooth where it is wanted and available, instant where it is not — the
    // same reasoning as the worksheet jump: `behavior: 'smooth'` is skipped
    // deliberately under reduced motion, and can silently do nothing at all,
    // so the destination is guaranteed either way.
    const reducedMotion = typeof window !== 'undefined'
      && window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;

    if (reducedMotion || typeof pane.scrollTo !== 'function') {
      pane.scrollTop = 0;
    } else {
      pane.scrollTo({ top: 0, behavior: 'smooth' });
      setTimeout(() => {
        if (pane.scrollTop === start && start !== 0) pane.scrollTop = 0;
      }, 250);
    }

    // Hidden straight away rather than waiting for the scroll to report back:
    // a programmatic scroll's own events are not a reliable trigger, and the
    // destination is known — the button that says "top" should not still be
    // sitting there once the list is at the top. Any later scroll re-decides.
    setShowDrawerTopJump(false);
  }, []);

  // Filtering, collapsing or reopening the drawer can shorten the list under a
  // scroll position that no longer exists, and a browser that clamps scrollTop
  // without firing a scroll event would leave the button stranded.
  useEffect(() => {
    syncDrawerTopJump();
  }, [
    syncDrawerTopJump,
    isNavDrawerOpen,
    drawerFilterText,
    drawerIssuesOnly,
    drawerExpandedSections,
    drawerCollapsedSections
  ]);

  /**
   * What the drawer looked like before a section drag collapsed it.
   *
   * Dragging a section that is expanded means dragging a row hundreds of pixels
   * tall past other rows just as tall, so the drop target you want is usually
   * off screen — the drag becomes a drag-plus-scroll even with auto-scroll
   * helping. Collapsing every section for the duration turns the list into one
   * short row per section, where every target is visible at once and the whole
   * move is a short drag. The previous state goes back afterwards, so this
   * never costs anyone the sections they had open.
   */
  const expansionBeforeSectionDragRef = useRef(null);

  const collapseSectionsForDrag = useCallback(() => {
    if (expansionBeforeSectionDragRef.current) return;
    expansionBeforeSectionDragRef.current = {
      expanded: drawerExpandedSections,
      collapsed: drawerCollapsedSections
    };
    setDrawerExpandedSections([]);
    setDrawerCollapsedSections(sections.map((section) => section.id));
  }, [drawerExpandedSections, drawerCollapsedSections, sections]);

  const restoreSectionsAfterDrag = useCallback(() => {
    const previous = expansionBeforeSectionDragRef.current;
    if (!previous) return;
    expansionBeforeSectionDragRef.current = null;
    setDrawerExpandedSections(previous.expanded);
    setDrawerCollapsedSections(previous.collapsed);
  }, []);

  /**
   * Move a section one place up or down the deck.
   *
   * Dragging already reorders sections, but it is the only way to do it, it is
   * only offered in edit mode, and a drag is an awkward gesture for "swap these
   * two" in a narrow scrolling drawer — the more so on a long deck, where the
   * section you want to pass may not be on screen at the same time. This is the
   * same operation without the gesture.
   *
   * Offsets are resolved inside the setState callback so two quick clicks
   * cannot both compute against the same stale array and lose one of the moves.
   */
  const moveSectionByOffset = (sectionId, offset) => {
    setSections((prevSections) => {
      const index = prevSections.findIndex((section) => section.id === sectionId);
      if (index === -1) return prevSections;
      const target = index + offset;
      // Already at the end it would move toward — the buttons are disabled
      // there, but a keyboard repeat can still arrive after the last move.
      if (target < 0 || target >= prevSections.length) return prevSections;
      const working = [...prevSections];
      const [moved] = working.splice(index, 1);
      working.splice(target, 0, moved);
      return working;
    });
  };

  const handleSectionDragStart = (sectionId) => (e) => {
    if (!sectionId) return;
    setDraggingSlide(null);
    setSlideDropTarget(null);
    setDraggingSection({ sectionId });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', sectionId);
    collapseSectionsForDrag();
    startDrawerAutoScroll();
  };

  const handleSectionDragOver = (sectionId, position = 'before') => (e) => {
    if (!draggingSection) return;
    if (!sectionId || draggingSection.sectionId === sectionId) return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    // Same repeat as the slide case above: only re-set on a real change.
    setSectionDropTarget((prev) => (
      prev && prev.sectionId === sectionId && prev.position === position
        ? prev
        : { sectionId, position }
    ));
  };

  const handleSectionDrop = (sectionId, position = 'before') => (e) => {
    if (!draggingSection) return;
    e.preventDefault();
    e.stopPropagation();
    if (!sectionId || draggingSection.sectionId === sectionId) {
      setDraggingSection(null);
      setSectionDropTarget(null);
      stopDrawerAutoScroll();
      restoreSectionsAfterDrag();
      return;
    }
    moveSection({ fromId: draggingSection.sectionId, toId: sectionId, position });
    setDraggingSection(null);
    setSectionDropTarget(null);
    stopDrawerAutoScroll();
    restoreSectionsAfterDrag();
  };

  // Fires on a cancelled drag (Escape, or a drop outside a target) as well as a
  // completed one, so it is the backstop that guarantees the drawer is put back
  // the way it was found.
  const handleSectionDragEnd = () => {
    setDraggingSection(null);
    setSectionDropTarget(null);
    stopDrawerAutoScroll();
    restoreSectionsAfterDrag();
  };

  const handlePresetImageRequest = useCallback(({ block, sectionId, columnIndex, cardIndex, imageData = {} }) => {
    if (!block?.id || !sectionId) return;

    const cleanCaption = imageData.caption?.replace(/^<strong>Figure:?<\/strong>\s*/i, '') || '';

    setModalContentType('image');
    setModalInitialData({
      sectionId,
      targetBlockId: block.id,
      targetCardPath: { columnIndex, cardIndex },
      imagePlaceholder: true,
      image_alt: imageData.alt || '',
      image_caption: cleanCaption,
      image_showCaption: imageData.imageShowCaption !== false,
      image_title: imageData.imageTitle || '',
      image_author: imageData.imageAuthor || '',
      image_source: imageData.imageSource || '',
      image_date: imageData.imageDate || '',
      image_size: imageData.size || 'large',
      src: imageData.src || '',
      isEditing: Boolean(imageData.src)
    });
    setIsModalOpen(true);
  }, []);

  const handlePresetVideoRequest = useCallback(({ block, sectionId, columnIndex, cardIndex, videoData = {} }) => {
    if (!block?.id || !sectionId) return;

    setModalContentType('video');
    setModalInitialData({
      sectionId,
      targetBlockId: block.id,
      targetCardPath: { columnIndex, cardIndex },
      videoPlaceholder: true,
      videoPlatform: videoData.videoPlatform || 'youtube',
      videoUrl: videoData.videoUrl || '',
      embedCode: videoData.embedCode || '',
      aspectRatio: videoData.aspectRatio || '16-9',
      videoTitle: videoData.videoTitle || '',
      videoAuthor: videoData.videoAuthor || '',
      videoSource: videoData.videoSource || '',
      videoDate: videoData.videoDate || '',
      src: videoData.src || '',
      isEditing: Boolean(videoData.src)
    });
    setIsModalOpen(true);
  }, []);

  // An image file dropped directly onto a canvas slide opens the image importer
  // modal pre-loaded with that file, ready for the user to add alt text and a
  // citation before saving it as a new image block on that slide.
  const handleCanvasImageDrop = useCallback(({ files, sectionId, slideId } = {}) => {
    if (!files || !files.length) return;
    setModalContentType('image');
    setModalInitialData({
      sectionId,
      targetSlideId: slideId,
      imageFiles: files,
      image_size: 'large',
      isEditing: false
    });
    setIsModalOpen(true);
  }, []);

  /**
   * The second half of pasting pictures onto a canvas slide.
   *
   * By the time this runs the pictures are already on the slide — SlideDesigner
   * placed them the moment they were pasted. All that is left is to offer the
   * citation form, one dialog covering however many were pasted.
   *
   * Nothing here is required. Closing the dialog without touching it leaves the
   * pictures exactly as they landed, because plenty of images on a lesson slide
   * are the author's own and have nothing to cite.
   */
  const handleCanvasImagePaste = useCallback(({ blocks, sectionId, slideId } = {}) => {
    const images = (blocks || []).filter((block) => block?.type === 'image' && block.id);
    if (!images.length) return;

    // Seed the per-image fields the citation form reads, so it opens showing
    // the picture's own alt text rather than empty boxes.
    const seededFields = {};
    images.forEach((image, index) => {
      seededFields[`image_${index}_alt`] = image.alt || '';
      seededFields[`image_${index}_showCaption`] = true;
      seededFields[`image_${index}_includeCitation`] = true;
      seededFields[`image_${index}_sourceType`] = 'image';
    });

    setModalContentType('pasted-images');
    setModalInitialData({
      sectionId,
      targetSlideId: slideId,
      // The blocks exist already; this dialog edits them rather than adding.
      isEditing: true,
      pastedImages: images.map((image) => ({
        id: image.id,
        src: image.src,
        name: image.alt || 'Pasted image'
      })),
      ...seededFields
    });
    setIsModalOpen(true);
  }, []);

  const handleDeleteSection = (sectionId) => {
    setSections(prevSections => prevSections.filter(section => section.id !== sectionId));
    setOpenSectionIds(prev => prev.filter(id => id !== sectionId));
    setActiveSectionId(prev => (prev === sectionId ? '' : prev));
    showSaveIndicator('🗑️ Section removed');
  };

  const isPlaceholderNewSection = (section) => {
    if (section.title !== 'New Section' || section.type !== 'content') return false;
    if (!Array.isArray(section.blocks) || section.blocks.length !== 1) return false;
    const [firstBlock] = section.blocks;
    return firstBlock?.type === 'text' && firstBlock.content === 'This is a new section. Click to edit this content.';
  };

  const handleAddSection = () => {
    const existingIds = new Set(sections.map((section) => section.id));
    let newSectionId = generateId();
    while (existingIds.has(newSectionId)) {
      newSectionId = generateId();
    }
    const trimmedName = newSectionName.trim();
    const newSection = {
      id: newSectionId,
      title: trimmedName || 'New Section',
      type: 'content',
      blocks: [
        {
          id: generateId(),
          type: 'text',
          content: 'This is a new section. Click to edit this content.'
        }
      ]
    };

    setSections(prevSections => {
      const newSections = [...prevSections];
      const insertIndex = resolveSectionInsertIndex(newSections, newSectionPlacement || 'before:resources');
      newSections.splice(insertIndex, 0, newSection);
      return newSections;
    });
    setOpenSectionIds(prev => Array.from(new Set([...prev, newSectionId])));
    setActiveSectionId(newSectionId);
    setNewSectionName('');
    showSaveIndicator('➕ New section added');
  };

  const buildLogoHtml = async ({ className = 'logo', style = 'max-height: 80px; margin-bottom: 10px;' } = {}) => {
    if (!logo) return '';
    const { fetchImageAsDataUrl } = await loadHtmlExporter();
    let src = logo;
    // A logo served from a durable address is referenced, not copied.
    if (shouldInlineForExport(src)) {
      src = await fetchImageAsDataUrl(src);
    }
    return `<img src="${src}" alt="School Logo" class="${className}" style="${style}" />`;
  };

  const buildTitleCardMetaHtml = (textColor, logoMarkup = '', settings = titleCardSettings) => {
    const allowCourseInfo = settings?.showCourseInfo !== false;
    const allowLogo = settings?.showLogoOnTitle !== false;
    const courseInfo = headerData.courseTopic?.trim();
    const instructorName = headerData.instructorName?.trim();
    const instructorEmail = headerData.instructorEmail?.trim();
    const hasCourseInfo = Boolean(courseInfo);
    const hasInstructorInfo = Boolean(instructorName || instructorEmail);
    const hasLogoMarkup = allowLogo && Boolean(logoMarkup);
    const shouldRenderRight = allowCourseInfo && (hasCourseInfo || hasInstructorInfo);
    const shouldRenderMeta = hasLogoMarkup || shouldRenderRight;

    if (!shouldRenderMeta) return '';

    // The lower-right course/instructor block can carry its own color, font
    // size and one-row/stacked layout.
    const metaColor = settings?.metaTextColor || textColor;
    const metaFontSize = Number(settings?.metaFontSize) || 16;
    const instructorRow = settings?.instructorLayout === 'row';
    const rightStyle = `${metaColor ? `color: ${metaColor}; ` : ''}font-size: ${metaFontSize}px;`;
    const rightBlock = shouldRenderRight
      ? `<div class="title-card-meta-right${instructorRow ? ' title-card-meta-right--row' : ''}" style="${rightStyle}">
          ${hasCourseInfo ? `<p class="title-card-meta-course">${courseInfo}</p>` : ''}
          ${hasInstructorInfo ? `
            ${instructorName ? `<p class="title-card-meta-instructor">${instructorName}</p>` : ''}
            ${instructorEmail ? `<p class="title-card-meta-email">${instructorEmail}</p>` : ''}` : ''}
        </div>`
      : '';

    const leftBlock = hasLogoMarkup ? `<div class="title-card-meta-left">${logoMarkup}</div>` : '';

    return `
          <div class="title-card-meta">
            ${leftBlock}
            ${rightBlock}
          </div>`;
  };

  const isDataUrl = (value) => typeof value === 'string' && value.trim().toLowerCase().startsWith('data:image');

  const buildPathFromParts = (basePath = '', fileName = '') => {
    if (!basePath || !fileName) return '';
    return `${basePath}${basePath.endsWith('/') ? '' : '/'}${fileName}`;
  };

  const prepareSectionsForPathExport = (sectionsToProcess = []) => {
    return (sectionsToProcess || []).map((section) => ({
      ...section,
      blocks: (section.blocks || []).map((block) => {
        if (block?.type === 'image') {
          const filenames = (block.imageFilenames || '').split('\n').map((name) => name.trim()).filter(Boolean);
          const nextSrc = (!block.src || !isDataUrl(block.src))
            ? block.src
            : buildPathFromParts(block.imagePath, filenames[0]);

          return { ...block, src: nextSrc || block.src };
        }

        if (block?.type === 'gallery') {
          const filenames = (block.imageFilenames || '').split('\n').map((name) => name.trim()).filter(Boolean);
          const items = (block.items || []).map((item, index) => {
            const fileName = filenames[index] || filenames[0] || '';
            const nextSrc = (!item.src || !isDataUrl(item.src))
              ? item.src
              : buildPathFromParts(block.imagePath, fileName);

            return { ...item, src: nextSrc || item.src };
          });

          return { ...block, items };
        }

        return block;
      })
    }));
  };

  const stripEmbeddedImages = (value) => {
    if (Array.isArray(value)) {
      return value.map((item) => stripEmbeddedImages(item));
    }

    if (value && typeof value === 'object') {
      return Object.entries(value).reduce((acc, [key, val]) => {
        acc[key] = stripEmbeddedImages(val);
        return acc;
      }, {});
    }

    if (typeof value === 'string') {
      if (isDataUrl(value)) return '';
      return value
        .replace(/src="data:image[^"']*"/gi, 'src="" data-removed-image="true"')
        .replace(/src='data:image[^"']*'/gi, "src='' data-removed-image='true'");
    }

    return value;
  };

  const attachAutoCitationsToBlock = useCallback(
    (block) => (block?.type === 'apa-reference-list'
      ? { ...block, autoCitations: autoApaCitations }
      : block),
    [autoApaCitations]
  );

  const handleExportPDF = async () => {
    showSaveIndicator('📄 Preparing PDF...', 'saving');
    try {
    const { blockToHtml, embedImagesInSections, pairCssBlocksToHtmlBlocks } = await loadHtmlExporter();

    const logoHtml = await buildLogoHtml();

    // Create clean header for PDF
    const headerHtml = showHeaderBar ? `
      <header class="header-section">
        <div class="header-content">
          <div class="header-top">
            <div class="date-section">
              <p class="date-text">${displayDate}</p>
              ${logoHtml ? `<div class="logo">${logoHtml}</div>` : ''}
            </div>
            <div class="instructor-info">
              <div class="instructor-grid">
                <span class="instructor-label">Instructor:</span>
                <span class="instructor-value">${headerData.instructorName}</span>
                <span class="instructor-label">Email:</span>
                <span class="instructor-value">${headerData.instructorEmail}</span>
              </div>
            </div>
          </div>

          <div class="title-section">
            <h1 class="main-title">${safeString(headerData.courseTopic, getCourseTopicForWeek()).replace(/Week \d+/, `Week ${week}`)}</h1>
          </div>
        </div>
      </header>
    ` : '';

      // Generate clean sections HTML with minimal spacing
      const processedSections = await embedImagesInSections(sections);
    const { cssByHtmlId } = pairCssBlocksToHtmlBlocks(processedSections);
    const sectionsHtml = (await Promise.all(processedSections.map(async (section, index) => {
      const label = getSectionExportLabel(section);
      const blocksHtml = (await Promise.all((section.blocks || []).map(block => {
        const blockWithAutoCitations = attachAutoCitationsToBlock(block);
        if (block.type === 'html' && block.mode === 'renderRich') {
          const cssParts = [];
          if (block.customCss?.trim()) cssParts.push(block.customCss);
          cssParts.push(...(cssByHtmlId.get(block.id) || []));
          const extra = cssParts.join('\n');
          return blockToHtml(blockWithAutoCitations, extra);
        }
        return blockToHtml(blockWithAutoCitations);
      }))).join('');

      const sectionColors = {
        'bridge-in': { bg: '#ef4444', light: '#f87171' },
        'outcomes': { bg: '#10b981', light: '#34d399' },
        'pre-assessment': { bg: '#f59e0b', light: '#fbbf24' },
        'participatory-learning': { bg: '#3b82f6', light: '#60a5fa' },
        'post-assessment': { bg: '#8b5cf6', light: '#a78bfa' },
        'summary': { bg: '#6366f1', light: '#818cf8' },
        'resources': { bg: '#374151', light: '#6b7280' }
      };

      const colors = sectionColors[section.id] || { bg: '#475569', light: '#64748b' };

      return `
        <section class="section-container">
          <div class="section-header" style="background: linear-gradient(135deg, ${colors.bg} 0%, ${colors.light} 100%);">
            <h2 class="section-title">${label}</h2>
          </div>
          <div class="section-content">
            ${blocksHtml}
          </div>
        </section>
      `;
    }))).join('');

    // Footer HTML
    const footerHtml = `
      <footer class="footer-section">
        <div class="footer-content">
          <div class="footer-main">${headerData.footerCourseInfo}</div>
          <div class="footer-sub">${headerData.footerInstitution}</div>
          <div class="footer-copyright">${headerData.footerCopyright}</div>
        </div>
      </footer>
    `;

    // Compact PDF-specific styles
    const pdfStyles = `
      <style>
        @page {
          size: A4;
          margin: 0.4in;
        }
        
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        
        body {
          font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif;
          line-height: 1.4;
          color: #1f2937;
          background: white;
          font-size: 10pt;
        }
        
        /* Header Styles - Compact */
        .header-section {
          background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
          border-bottom: 3px solid #3b82f6;
          margin-bottom: 0.5rem;
        }
        
        .header-content {
          padding: 0.75rem;
        }
        
        .header-top {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 0.5rem;
        }
        
        .date-section {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
        }
        
        .date-text {
          font-size: 12pt;
          color: #4b5563;
          font-weight: 500;
          margin-bottom: 0.25rem;
        }
        
        .logo {
          max-height: 40px;
          opacity: 0.9;
        }
        
        .instructor-info {
          text-align: right;
        }
        
        .instructor-grid {
          display: grid;
          grid-template-columns: auto 1fr;
          gap: 0.15rem 0.5rem;
          align-items: center;
          font-size: 9pt;
        }
        
        .instructor-label {
          font-weight: 600;
          color: #374151;
        }
        
        .instructor-value {
          color: #6b7280;
        }
        
        .title-section {
          text-align: center;
          padding: 0.5rem 0;
          background: white;
          border-radius: 6px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        }
        
        .main-title {
          font-size: 22pt;
          font-weight: 700;
          color: #1e293b;
          margin: 0;
          line-height: 1.1;
        }
        
        /* Section Styles - Compact */
        .section-container {
          margin-bottom: 0.5rem;
          break-inside: avoid;
          background: white;
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 1px 4px rgba(0,0,0,0.1);
        }
        
        .section-header {
          padding: 0.5rem 1rem;
          color: white;
        }
        
        .section-title {
          font-size: 14pt;
          font-weight: 600;
          margin: 0;
          text-shadow: 0 1px 2px rgba(0,0,0,0.2);
        }
        
        .section-content {
          padding: 0.75rem;
          background: white;
        }
        
        /* Typography - Compact */
        h1 { 
          font-size: 18pt;
          font-weight: 700;
          color: #1e293b;
          margin: 0.25rem 0 0.5rem 0;
          break-after: avoid;
          line-height: 1.2;
        }
        
        h2 { 
          font-size: 14pt;
          font-weight: 600;
          color: #1e293b;
          margin: 0.5rem 0 0.25rem 0;
          break-after: avoid;
          line-height: 1.2;
        }
        
        h3 { 
          font-size: 12pt;
          font-weight: 600;
          color: #374151;
          margin: 0.4rem 0 0.2rem 0;
          break-after: avoid;
          line-height: 1.2;
        }
        
        h4 { 
          font-size: 11pt;
          font-weight: 600;
          color: #4b5563;
          margin: 0.3rem 0 0.15rem 0;
          break-after: avoid;
          line-height: 1.2;
        }
        
        p {
          margin-bottom: 0.4rem;
          line-height: 1.4;
        }
        
        ul, ol {
          margin: 0.3rem 0 0.4rem 1.2rem;
          line-height: 1.4;
        }
        
        li {
          margin-bottom: 0.15rem;
        }
        
        strong {
          font-weight: 600;
          color: #1e293b;
        }
        
        em {
          font-style: italic;
          color: #4b5563;
        }
        
        /* Card Layouts - Compact */
        .grid {
          display: grid;
          gap: 0.5rem;
          margin: 0.5rem 0;
        }
        
        .grid-cols-1 { grid-template-columns: 1fr; }
        .grid-cols-2 { grid-template-columns: repeat(2, 1fr); }
        .grid-cols-3 { grid-template-columns: repeat(3, 1fr); }
        .grid-cols-4 { grid-template-columns: repeat(4, 1fr); }
        
        /* Info Boxes - Compact */
        .p-4.rounded-lg {
          padding: 0.5rem;
          border-radius: 6px;
          margin: 0.5rem 0;
          border-left: 3px solid;
          background: #f8fafc;
        }
        
        .bg-blue-50 { 
          background: #eff6ff !important; 
          border-left-color: #3b82f6;
        }
        
        .bg-emerald-50 { 
          background: #ecfdf5 !important; 
          border-left-color: #10b981;
        }
        
        .bg-amber-50 { 
          background: #fffbeb !important; 
          border-left-color: #f59e0b;
        }
        
        .bg-slate-50 { 
          background: #f8fafc !important; 
          border-left-color: #64748b;
        }
        
        /* Images - Compact */
        img {
          max-width: 100%;
          height: auto;
          break-inside: avoid;
          margin: 0.25rem 0;
          border-radius: 4px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        }
        
        /* Video Placeholder - Compact */
        iframe {
          border: 2px solid #e5e7eb;
          background: #f3f4f6;
          min-height: 120px;
          border-radius: 6px;
          margin: 0.5rem 0;
        }

        /* Worksheet Styles for Print */
        .worksheet-container {
          break-inside: avoid;
          margin: 1rem 0;
        }
        
        .worksheet-header {
          break-inside: avoid;
          margin-bottom: 1rem;
        }
        
        .worksheet-question {
          break-inside: avoid;
          margin-bottom: 1rem;
        }
        
        .worksheet-answer-input {
          border-bottom: 2px solid #374151 !important;
          border-top: none !important;
          border-left: none !important;
          border-right: none !important;
          background: transparent !important;
          padding-bottom: 4px !important;
          margin-bottom: 4px !important;
        }
        
        .print-checkbox {
          -webkit-appearance: none !important;
          appearance: none !important;
          width: 16px !important;
          height: 16px !important;
          border: 2px solid #374151 !important;
          border-radius: 3px !important;
          background: white !important;
          margin-right: 8px !important;
        }
        
        /* Footer - Compact */
        .footer-section {
          background: linear-gradient(135deg, #1e293b 0%, #334155 100%);
          color: white;
          margin-top: 1rem;
          break-before: auto;
        }
        
        .footer-content {
          text-align: center;
          padding: 0.75rem;
        }
        
        .footer-main {
          font-size: 11pt;
          font-weight: 600;
          margin-bottom: 0.25rem;
        }
        
        .footer-sub {
          font-size: 10pt;
          color: #cbd5e1;
          margin-bottom: 0.25rem;
        }
        
        .footer-copyright {
          font-size: 8pt;
          color: #94a3b8;
        }
        
        /* Remove excessive spacing */
        .my-6 { 
          margin: 0.5rem 0; 
        }
        
        .mb-6 { 
          margin-bottom: 0.5rem; 
        }
        
        .mt-8 { 
          margin-top: 0.5rem; 
        }
        
        .py-8 { 
          padding: 0.5rem 0; 
        }
        
        /* Hide interactive elements */
        .no-print, 
        button, 
        .cursor-pointer,
        [contenteditable] {
          display: none !important;
        }
        
        /* Utility classes */
        .break-inside-avoid { break-inside: avoid; }
        .break-before-page { break-before: page; }
        .text-center { text-align: center; }
        .font-semibold { font-weight: 600; }
        .font-bold { font-weight: 700; }
      </style>
    `;

    // Complete HTML document
    const fullHtml = `
      <!DOCTYPE html>
      <html lang="en">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Week ${week} - ${headerData.courseTopic} - PDF Export</title>
        ${pdfStyles}
      </head>
      <body>
        ${headerHtml}
        <main>
          ${sectionsHtml}
        </main>
        ${footerHtml}
        
        <script>
          // Auto-print when page loads
          window.onload = function() {
            setTimeout(function() {
              window.print();
            }, 1000);
          };
        </script>
      </body>
      </html>
    `;

    // A new window is asked for, but not relied on.
    //
    // Everything above this line is awaited — the exporter module, the logo,
    // the image inlining, every section and every block — and a browser only
    // treats window.open as user-initiated for a moment after the click. By
    // the time a deck of any size gets here that moment is long gone, so the
    // popup is blocked and window.open returns null. This used to be written
    // straight to `.document`, which threw "Cannot read properties of null"
    // and left the indicator stuck on "Preparing PDF..." with nothing said.
    //
    // The fallback is the one the worksheet printer and the HTML export
    // already use: an off-screen iframe printed in place, which needs no
    // popup permission at all.
    const printWindow = window.open('', '_blank');
    if (printWindow && !printWindow.closed) {
      printWindow.document.open();
      printWindow.document.write(fullHtml);
      printWindow.document.close();
    } else {
      const frame = document.createElement('iframe');
      frame.style.position = 'fixed';
      frame.style.right = '-9999px';
      frame.style.bottom = '-9999px';
      document.body.appendChild(frame);
      const frameDoc = frame.contentDocument;
      if (!frameDoc) {
        frame.remove();
        throw new Error('This browser would not open a window or a frame to print from.');
      }
      frameDoc.open();
      frameDoc.write(fullHtml);
      frameDoc.close();
      // The document prints itself on load; the frame is kept long enough for
      // that to happen and for the print dialog to take its snapshot.
      setTimeout(() => frame.remove(), 60000);
    }

    showSaveIndicator('📄 PDF export ready');
    } catch (error) {
      // Matching the PowerPoint export: the console gets the stack, the person
      // gets a sentence, and the indicator never sits on "Preparing" forever.
      console.error('PDF export failed:', error);
      showSaveIndicator('❌ PDF export failed', 'error');
    }
  };


  const exportHtmlVariant = async ({
    embedImages = true,
    useImagePaths = false,
    fileSuffix = '_Locked',
    savingMessage = '🔒 Preparing locked HTML...',
    successMessage = '🔒 Locked HTML exported'
  } = {}) => {
    const htmlBar = { title: 'Exporting HTML', icon: '🌐' };
    const htmlProgress = (label, done = 0, total = 0) => setExportProgress({ ...htmlBar, label, done, total });
    htmlProgress('Preparing the deck…');
    const {
      blockToHtml,
      buildExportFrameSrc,
      createBackgroundClassRegistry,
      embedImagesInSections,
      fetchImageAsDataUrl,
      hasVisibleExportContent,
      pairCssBlocksToHtmlBlocks,
      CANVAS_FIT_SCRIPT,
      TIMELINE_FIT_SCRIPT
    } = await loadHtmlExporter();
    const buildLogoMarkup = async ({ className = 'logo', style = 'max-height: 80px; margin-bottom: 10px;' } = {}) => {
      if (embedImages) return buildLogoHtml({ className, style });
      if (!logo) return '';
      const src = stripEmbeddedImages(logo);
      if (!src) return '';
      return `<img src="${src}" alt="School Logo" class="${className}" style="${style}" />`;
    };

    const logoHtml = await buildLogoMarkup();
    const titleCardLogoHtml = await buildLogoMarkup({ className: 'title-card-meta-logo', style: 'max-height: 80px;' });
    // Bottom-left slide logo for canvas (PowerPoint) slides, mirroring the app
    // and the pptx export.
    const slideCornerLogoHtml = await buildLogoMarkup({
      className: 'slide-corner-logo-img',
      style: `max-height: ${Math.max(28, Math.min(96, Math.round((logoSize || 120) * 0.45)))}px;`
    });
    const isSlideLayout = bodyLayout === 'slides';
    const isCanvasLayout = slideDesignMode === 'canvas';

    // Convert a background choice (preset key or object) into an inline CSS
    // string so exported slides carry the same background the app shows.
    const styleObjectToCss = (styleObj = {}) =>
      Object.entries(styleObj)
        .map(([key, value]) => `${key.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`)}: ${value}`)
        .join('; ');
    // Resolve a background choice to an inline CSS string. For embedded
    // exports, remote preset images are fetched and inlined as data URLs so
    // the file shows the background even when opened offline.
    const backgroundCssFor = async (bg) => {
      if (!bg) return '';
      const styleObj = { ...getBackgroundStyles(bg) };
      if (embedImages && typeof styleObj.backgroundImage === 'string') {
        const match = /url\((['"]?)(.*?)\1\)/.exec(styleObj.backgroundImage);
        const url = match && match[2];
        // A background that already has a durable address keeps it. Copying
        // those bytes into the file is what turned a deck of hosted pictures
        // into a 56 MB export.
        if (shouldInlineForExport(url)) {
          try {
            const dataUrl = await fetchImageAsDataUrl(url);
            // Only inline a genuine image; if the fetch fell back to an error
            // page (non-image), keep the remote URL so a reachable browser
            // still shows the background.
            if (typeof dataUrl === 'string' && dataUrl.startsWith('data:image')) {
              styleObj.backgroundImage = `url(${dataUrl})`;
            }
          } catch { /* keep the remote url as a fallback */ }
        }
      }
      return styleObjectToCss(styleObj);
    };
    // Backgrounds are written as classes, not as an inline style on each slide.
    // Thirty-one slides sharing one uploaded photo used to inline the same data
    // URL thirty-one times — about 50 MB of a 56 MB file. Identical CSS now
    // collapses to one rule that every slide using it points at.
    const backgroundClasses = createBackgroundClassRegistry();
    const internBackgroundCss = (css) => backgroundClasses.classFor(css);
    const backgroundClassFor = async (bg) => internBackgroundCss(await backgroundCssFor(bg));
    const backgroundClassRules = () => backgroundClasses.rules();
    const resolveSlideBackgroundClass = (slideId) => {
      const bg = (slideId && slideBackgrounds?.[slideId]) ? slideBackgrounds[slideId] : bodyBackground;
      return backgroundClassFor(bg);
    };
    const exportTitle = resolveExportTitle();
    const exportBaseName = resolveExportFileName();
    const exportVersionLabel = resolveCurrentExportVersionLabel();
    const isSlideExportEnabled = (slideId, slideType) => {
      if (slideType === 'end-title-card' && endTitleCardSettings.enabled) return true;
      return slideVisibility?.[slideId] !== false;
    };
    const deckNameLabel = sanitizeHtmlString(slideDeckName);
    // Off means the deck's name appears on no slide in the file either.
    const deckNameHtml = (deckNameBadgeEnabled && deckNameLabel) ? `<div class="slide-deck-name">${deckNameLabel}</div>` : '';
    // When the deck-name badge is limited to title cards, content slides omit it.
    const contentDeckNameHtml = deckNameOnContentSlides ? deckNameHtml : '';
    // Slide numbers count the slides that actually reach the file, so the run
    // is unbroken even when the author has switched slides out of the deck.
    // The count is the length of what has been pushed so far, plus this one.
    const slideNumberHtml = (position) => (
      slideNumbersEnabled ? `<div class="slide-number">${position + 1}</div>` : ''
    );

    const titleCardForExport = embedImages
      ? await inlineTitleCardImage(titleCardSettings)
      : stripEmbeddedImages(titleCardSettings);
    const hasTitleContent = Boolean(
      titleCardForExport?.title?.trim()
      || titleCardForExport?.subtitle?.trim()
      || titleCardForExport?.image
    );

    const endTitleCardForExport = embedImages
      ? await inlineTitleCardImage(endTitleCardSettings)
      : stripEmbeddedImages(endTitleCardSettings);
    const hasEndTitleContent = Boolean(
      endTitleCardForExport?.title?.trim()
      || endTitleCardForExport?.subtitle?.trim()
      || endTitleCardForExport?.image
    );

    const buildSectionsAndSlides = async () => {
      htmlProgress('Collecting the pictures…');
      const processedSections = useImagePaths
        ? stripEmbeddedImages(prepareSectionsForPathExport(sections))
        : embedImages
          ? await embedImagesInSections(sections)
          : stripEmbeddedImages(sections);

      const exportSlides = isSlideLayout
        ? buildSlidesFromJson({
          sections: processedSections,
          titleCard: { ...(titleCardForExport || {}), enabled: hasTitleContent },
          endTitleCard: { ...(endTitleCardForExport || {}), enabled: hasEndTitleContent && endTitleCardSettings.enabled },
          includeEmptySections: true
        })
        : [];

      return { processedSections, exportSlides };
    };

    const { processedSections, exportSlides } = await buildSectionsAndSlides();

    const headerHtml = isSlideLayout || !showHeaderBar
      ? ''
      : `
  <header class="bg-white border-b border-gray-200">
    <div class="max-w-7xl mx-auto px-6 py-12">
      <div class="flex items-start justify-between space-x-6">

        <div class="flex flex-col items-center md:items-start space-y-3">
          <p class="text-lg text-gray-600">${displayDate}</p>
          ${logoHtml ? `<div class="logo">${logoHtml}</div>` : ''}
        </div>

        <div class="flex-1 text-center md:text-left">
          <h1 class="text-4xl font-bold text-gray-900">
            ${exportTitle}
          </h1>
        </div>

        <div class="grid grid-cols-[auto,1fr] gap-x-4 gap-y-1 items-center flex-shrink-0">
          <span class="font-medium text-gray-800 text-right">Instructor:</span>
          <span class="text-gray-600">${headerData.instructorName}</span>
          <span class="font-medium text-gray-800 text-right">Email:</span>
          <span class="text-gray-600">${headerData.instructorEmail}</span>
        </div>

      </div>
    </div>
</header>
`;

    const navHtml = isSlideLayout
      ? ''
      : `
<nav class="bg-white border-b border-gray-200 sticky top-0 z-40">
  <div class="max-w-8xl mx-auto px-6">
    <ul class="flex justify-center gap-1 py-2 flex-wrap">
      ${sections.map(section => {
      const label = getSectionExportLabel(section);
      return `
        <li>
          <a
            href="#${section.id}"
            class="px-4 py-2 rounded-t-lg transition-all font-medium text-sm text-gray-700 hover:text-gray-900 hover:bg-gray-100"
          >
            ${label}
          </a>
        </li>`;
    }).join('')}
    </ul>
  </div>
</nav>
`;

    const sectionColors = {
      'bridge-in': { bg: 'bg-red-500' },
      'outcomes': { bg: 'bg-emerald-500' }, 'pre-assessment': { bg: 'bg-amber-500' },
      'participatory-learning': { bg: 'bg-blue-500' }, 'post-assessment': { bg: 'bg-purple-500' },
      'summary': { bg: 'bg-indigo-500' }, 'resources': { bg: 'bg-gray-600' }
    };

    const { cssByHtmlId } = pairCssBlocksToHtmlBlocks(processedSections);

    const renderBlocks = async (blocks) => (await Promise.all(blocks.map(block => {
      const blockWithAutoCitations = attachAutoCitationsToBlock(block);
      if (block.type === 'html' && block.mode === 'renderRich') {
        const cssParts = [];
        if (block.customCss?.trim()) cssParts.push(block.customCss);
        cssParts.push(...(cssByHtmlId.get(block.id) || []));
        const extra = cssParts.join('\n');
        return blockToHtml(blockWithAutoCitations, extra);
      }
      return blockToHtml(blockWithAutoCitations);
    }))).join('');

    const slideNavItems = [];
    const slideSections = [];
    const buildSlideHtml = async () => {
      for (let i = 0; i < exportSlides.length; i++) {
        htmlProgress(`Building slide ${i + 1} of ${exportSlides.length}`, i + 1, exportSlides.length);
        // Same reason as the PDF writer: without a yield the browser never
        // repaints, so the count would jump from nothing to done.
        if (i % 4 === 0) await new Promise((resolve) => { setTimeout(resolve, 0); });
        const slide = exportSlides[i];
        const slideId = slide.slideId || buildSectionSlideId(slide.section?.id || 'slide', slide.slideIndex || i);
        if (!isSlideExportEnabled(slideId, slide.type)) continue;

        if (slide.type === 'title-card' || slide.type === 'end-title-card' || slide.type === 'section-title-card') {
          const resolveTitleCardData = async () => {
            if (slide.titleCard) return slide.titleCard;
            if (slide.type === 'end-title-card') return endTitleCardForExport;
            if (slide.type === 'title-card') return titleCardForExport;
            if (slide.type === 'section-title-card') {
              return embedImages ? await inlineTitleCardImage(slide) : stripEmbeddedImages(slide);
            }
            return {};
          };
          const titleCardData = (await resolveTitleCardData()) || {};
          const hasTitleContent = Boolean(
            titleCardData.title?.trim()
            || titleCardData.subtitle?.trim()
            || titleCardData.image
          );
          if (!hasTitleContent) continue;
          const titleCardTextColor = titleCardData.textColor || '#ffffff';
          const titleCardMetaHtml = buildTitleCardMetaHtml(titleCardTextColor, titleCardLogoHtml, titleCardData);
          const titlePlacement = titleCardData.textPlacement || 'below';
          // Background precedence mirrors the app's TitleCardSlide:
          //   1) the card's own backgroundImage
          //   2) the deck-wide titleCardBackground preset
          //   3) the card's backgroundColor
          const resolvedCardImage = resolveTitleCardImageSource(titleCardData.backgroundImage);
          const resolvedDeckImage = resolveTitleCardImageSource(titleCardBackground);
          let titleCardBgStyle = '';
          if (resolvedCardImage?.value) {
            titleCardBgStyle = `background-image: url(${resolvedCardImage.value}); background-size: cover; background-position: center; background-repeat: no-repeat; background-color: ${resolvedCardImage.fallbackColor || titleCardData.backgroundColor || '#545f7d'};`;
          } else if (resolvedDeckImage?.value) {
            titleCardBgStyle = `background-image: url(${resolvedDeckImage.value}); background-size: cover; background-position: center; background-repeat: no-repeat; background-color: ${resolvedDeckImage.fallbackColor || titleCardData.backgroundColor || '#545f7d'};`;
          } else if (titleCardBackground) {
            titleCardBgStyle = await backgroundCssFor(titleCardBackground);
          }
          if (!titleCardBgStyle) {
            titleCardBgStyle = `background-color: ${titleCardData.backgroundColor || '#545f7d'}`;
          }
          // Section title cards commonly share one picture; interning the rule
          // keeps it in the file once however many cards use it.
          const titleCardBgClass = internBackgroundCss(titleCardBgStyle);
          const titleCardTitleSize = Number(titleCardData.titleFontSize) || 48;
          const titleCardSubtitleSize = Number(titleCardData.subtitleFontSize) || 20;
          const titleCardTextBlock = `
        <h2 class="title-card-title" style="color: ${titleCardTextColor} !important; font-size: ${titleCardTitleSize}px;">${titleCardData.title || ''}</h2>
        ${titleCardData.subtitle ? `<p class="title-card-subtitle" style="color: ${titleCardTextColor} !important; font-size: ${titleCardSubtitleSize}px;">${titleCardData.subtitle}</p>` : ''}`;
          const imageCitation = sanitizeHtmlString(titleCardData.imageCitation || '');
          const titleCardImageBlock = titleCardData.image
            ? `<div class="title-card-image-group">
                <img src="${titleCardData.image}" alt="" class="title-card-image" style="max-width: ${titleCardData.imageSize || 120}px;" />
                ${imageCitation ? `<p class="title-card-image-citation">${imageCitation}</p>` : ''}
              </div>`
            : '';
          const titleCardContent = titlePlacement === 'above'
            ? `${titleCardTextBlock}${titleCardImageBlock}`
            : `${titleCardImageBlock}${titleCardTextBlock}`;
          // The corner mark. Its authored width is measured against the 1280px
          // slide, and the exported card is a fluid 16:9 box, so it travels as
          // a share of the card rather than as pixels that would only be right
          // at one window size.
          const cornerLogoBlock = titleCardData.cornerLogo
            ? `<img src="${escapeHtmlText(titleCardData.cornerLogo)}" alt="" class="title-card-corner-logo title-card-corner-logo--${
              titleCardData.cornerLogoPosition === 'top-left' ? 'top-left' : 'top-right'
            }" style="width: ${titleCardCornerLogoWidthPercent(titleCardData.cornerLogoSize)}%; max-height: ${
              titleCardCornerLogoHeightPercent(titleCardData.cornerLogoSize)
            }%;" />`
            : '';
          const label = titleCardData.title || (slide.type === 'end-title-card'
            ? 'End Title Card'
            : slide.type === 'title-card'
              ? 'Title Card'
              : getSectionExportLabel(slide.section || {}));
          const isActive = slideSections.length === 0;
          const sectionIdForCard = slide.type === 'end-title-card'
            ? 'end-title-card'
            : slide.type === 'title-card'
              ? 'title-card'
              : (slide.slideId || buildSectionTitleCardId(slide.section?.id || 'section'));
          slideNavItems.push({ index: slideSections.length, label });
          slideSections.push(`
  <section class="slide ${isActive ? 'is-active' : ''}" data-slide-index="${slideSections.length}" id="${sectionIdForCard}">
    <div class="slide-card">
      <div class="title-card title-card--${titleCardData.titlePosition || 'center'} ${titleCardBgClass}" style="--title-card-text-color: ${titleCardTextColor};">
        ${cornerLogoBlock}
        ${titleCardContent}
        ${titleCardMetaHtml}
        ${deckNameHtml}
        ${slideNumberHtml(slideSections.length)}
      </div>
    </div>
  </section>`);
          continue;
        }

        const section = slide.section;
        if (!section) continue;
        const label = getSectionExportLabel(section);
        const headerColor = headerColorMap[section.id] || slideHeaderColor;
        const slideBlocks = slide.blocks || [];

        // resolveSlideName, not the raw map: older decks store a name generated
        // from the slide's id ("Section 1 Slide 7"), and the export was still
        // printing it as the slide's title after the app had stopped.
        const customSlideName = resolveSlideName(slideNames[slideId], slideId);
        // Same rule as the app's header bar: the title once, the
        // student-friendly line as a subtitle, and no slide numbering — the
        // nav list and the drawer thumbnails both number the slides already.
        const headerParts = getSlideHeaderParts(section, customSlideName, slideSubtitles[slideId]);
        const slideLabel = headerParts.title || label;
        const slideSubtitle = headerParts.subtitle;
        const slideSubtitleHtml = slideSubtitle
          ? `<span class="slide-title__subtitle">${escapeHtmlText(slideSubtitle)}</span>`
          : '';
        const slideBgClass = await resolveSlideBackgroundClass(slideId);

        // Render the slide's blocks once; reused by both layout modes so a
        // slide with content is never dropped just because of the mode.
        const contentBlocks = slideBlocks.filter(
          (block) => !(block.type === 'divider' && block.content?.isSlideBreak)
        );
        const blocksHtml = await renderBlocks(contentBlocks);
        if (!hasVisibleExportContent(blocksHtml)) continue;

        if (isCanvasLayout) {
          // PowerPoint (canvas) slides: keep a fixed 16:9 card with the header
          // bar and background, and position each block by its frame — the same
          // layout rules the pptx export follows.
          const framedItems = [];
          for (let bi = 0; bi < contentBlocks.length; bi++) {
            const block = contentBlocks[bi];
            let html = await renderBlocks([block]);
            if (!hasVisibleExportContent(html)) continue;
            // Only a picture block: a custom layout or a gallery does its own
            // sizing, and pinning its images would fight it.
            if (block.type === 'image') html = await pinCanvasImageWidths(html);
            const frame = frameForBlock(block, bi);
            // Stacking order mirrors the designer: later blocks (and any block
            // brought forward) sit on top. An explicit block.zIndex wins.
            const z = Number.isFinite(Number(block.zIndex)) ? Number(block.zIndex) : 10 + bi;
            // The frame's text scale — what dragging a corner handle sets —
            // travels with the item, exactly as the designer and the pptx
            // export use it. Without it the export drew every block's text at
            // full size, so a block the author had scaled down to fit spilled
            // out of its frame and was cut off by the stage's overflow.
            const textScale = Number(frame.s) > 0 ? Number(frame.s) : 1;
            // Media blocks are the ones the designer scales as a single object
            // (SCALED_MEDIA_TYPES in SlideDesigner): the picture and its caption
            // and citation shrink together to fit the frame. Flagging them here
            // lets the export's fit script treat them the same way, instead of
            // shrinking the picture and leaving the citation at full size — which
            // is how a two-line APA note ended up wider than its frame with its
            // second line cut off.
            const fitMode = EXPORT_SCALED_MEDIA_TYPES.has(block.type) ? 'media' : 'text';
            framedItems.push(
              `<div class="slide-canvas-item" data-fit="${fitMode}" style="left:${frame.x}%; top:${frame.y}%; width:${frame.w}%; height:${frame.h}%; z-index:${z}; --text-scale:${textScale};">${html}</div>`
            );
          }
          // Fallback: if nothing produced a positioned item (unexpected frame
          // data), still render the blocks flow-style so the slide is kept.
          const stageInner = framedItems.length
            ? framedItems.join('\n        ')
            : `<div class="slide-canvas-flow">${blocksHtml}</div>`;

          const isActive = slideSections.length === 0;
          const navIndex = slideSections.length;
          slideNavItems.push({ index: navIndex, label: slideLabel });
          slideSections.push(`
  <section class="slide ${isActive ? 'is-active' : ''}" data-slide-index="${navIndex}" id="${section.id}-${slide.slideIndex}">
    <div class="slide-card slide-card--canvas${slideBgClass ? ` ${slideBgClass}` : ''}">
      <div class="slide-header slide-header--canvas" style="background: ${headerColor};">
        <h2 class="slide-title slide-title--canvas"><span class="slide-title__main">${escapeHtmlText(slideLabel)}</span>${slideSubtitleHtml}</h2>
      </div>
      <div class="slide-canvas-stage">
        ${stageInner}
        ${slideCornerLogoHtml ? `<div class="slide-corner-logo">${slideCornerLogoHtml}</div>` : ''}
        ${slideNumberHtml(navIndex)}
      </div>
    </div>
  </section>`);
          continue;
        }

        const isActive = slideSections.length === 0;
        const navIndex = slideSections.length;
        slideNavItems.push({ index: navIndex, label: slideLabel });
        slideSections.push(`
  <section class="slide ${isActive ? 'is-active' : ''}" data-slide-index="${navIndex}" id="${section.id}-${slide.slideIndex}">
    <div class="slide-card">
      <div class="slide-header" style="background: ${headerColor};">
        <div class="slide-header-row">
          <div>
            <h2 class="slide-title"><span class="slide-title__main">${escapeHtmlText(slideLabel)}</span>${slideSubtitleHtml}</h2>
          </div>
          ${logoHtml ? `<div class="slide-logo">${logoHtml}</div>` : ''}
        </div>
      </div>
      <div class="slide-body${slideBgClass ? ` ${slideBgClass}` : ''}">
        ${blocksHtml}
        ${contentDeckNameHtml}
        ${slideNumberHtml(navIndex)}
      </div>
    </div>
  </section>`);
      }
    };
    if (isSlideLayout) {
      await buildSlideHtml();
    }
    const sectionsHtml = (await Promise.all(processedSections.map(async (section) => {
      const label = getSectionExportLabel(section);
      if (isSlideLayout) {
        return '';
      }

      const contentBlocks = (section.blocks || []).filter(hasBlockContent);
      if (!contentBlocks.length) return '';
      const blocksHtml = await renderBlocks(contentBlocks);
      const colorConfig = sectionColors[section.id] || { bg: 'bg-slate-600' };
      return `
  <div id="${section.id}" class="bg-white rounded-2xl shadow-sm border border-gray-200 mb-8 overflow-hidden">
    <div class="${colorConfig.bg} text-white px-8 py-6 cursor-pointer flex justify-between items-center section-header">
      <h2 class="text-xl font-semibold">${label}</h2>
      <div class="toggle-icon">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none"
             viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
          <polyline points="9 18 15 12 9 6" />
        </svg>
      </div>
    </div>
    <div class="content-container closed">
      <div>
        <div class="p-8">
          ${blocksHtml}
        </div>
      </div>
    </div>
  </div>`;
    }))).join('');
    const sanitizeExternalMediaForLocalExport = (html) => {
      if (!html || typeof DOMParser === 'undefined') return html;
      const parser = new DOMParser();
      const doc = parser.parseFromString(`<div>${html}</div>`, 'text/html');
      const frames = Array.from(doc.querySelectorAll('iframe[src]'));

      frames.forEach((frame) => {
        const originalSrc = frame.getAttribute('src') || '';
        let sanitizedSrc = originalSrc.startsWith('//') ? `https:${originalSrc}` : originalSrc;

        if (/youtube(?:-nocookie)?\.com/i.test(sanitizedSrc)) {
          sanitizedSrc = sanitizedSrc.replace(/https?:\/\/(www\.)?youtube\.com/i, 'https://www.youtube-nocookie.com');
          try {
            const url = new URL(sanitizedSrc);
            url.searchParams.delete('origin');
            url.searchParams.delete('enablejsapi');
            sanitizedSrc = url.toString();
          } catch {
            sanitizedSrc = sanitizedSrc
              .replace(/([?&])origin=[^&]*(&|$)/gi, '$1')
              .replace(/([?&])enablejsapi=[^&]*(&|$)/gi, '$1')
              .replace(/[?&]+$/, '')
              .replace('?&', '?');
          }
        }

        frame.setAttribute('src', sanitizedSrc);
        if (!frame.hasAttribute('allow')) {
          frame.setAttribute('allow', 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture');
        }
        frame.setAttribute('allowfullscreen', frame.getAttribute('allowfullscreen') || '');

        const isExternal = /^(https?:)?\/\//i.test(originalSrc) || /^(https?:)?\/\//i.test(sanitizedSrc);
        if (!isExternal) return;

        const existingFallback = frame.parentElement?.querySelector('.export-media-fallback');
        if (existingFallback) return;

        let fallbackHref = sanitizedSrc;
        const youtubeMatch = sanitizedSrc.match(/(?:youtube(?:-nocookie)?\.com\/embed\/)([A-Za-z0-9_-]{11})/);
        if (youtubeMatch) {
          fallbackHref = `https://www.youtube.com/watch?v=${youtubeMatch[1]}`;
        }

        const wrapper = frame.parentElement;
        if (wrapper) {
          const wrapperStyle = wrapper.getAttribute('style') || '';
          if (!/position\s*:/i.test(wrapperStyle)) {
            wrapper.setAttribute('style', `${wrapperStyle}; position: relative;`.trim());
          }
          const link = doc.createElement('a');
          link.className = 'export-media-fallback';
          link.href = fallbackHref;
          link.target = '_blank';
          link.rel = 'noreferrer';
          link.textContent = youtubeMatch ? 'Open on YouTube' : 'Open media';
          wrapper.appendChild(link);
        }
      });

      return doc.body.firstElementChild?.innerHTML || html;
    };

    const combinedSlidesHtml = isSlideLayout ? slideSections.join('') : sectionsHtml;
    let renderedSlidesHtml = combinedSlidesHtml || sectionsHtml;
    renderedSlidesHtml = sanitizeExternalMediaForLocalExport(renderedSlidesHtml);
    const slideCount = slideSections.length;

    if (isSlideLayout) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(`<div>${combinedSlidesHtml}</div>`, 'text/html');
      const exportedSlides = Array.from(doc.querySelectorAll('section.slide'));

      exportedSlides.forEach((slideEl) => {
        const nestedSlide = slideEl.querySelector('section.slide');
        if (nestedSlide) {
          console.warn('[Export Debug] Nested slide section detected inside a slide', {
            parentId: slideEl.id || '(no id)',
            nestedId: nestedSlide.id || '(no id)'
          });
        }

        const nestedMarkers = slideEl.querySelectorAll('section.slide[data-slide-index]');
        if (nestedMarkers.length > 1) {
          console.warn('[Export Debug] Multiple slide markers detected inside a single slide', {
            parentId: slideEl.id || '(no id)',
            markers: nestedMarkers.length
          });
        }
      });

      const expectedSlides = exportSlides.filter((slide, idx) => {
        const effectiveId = slide.slideId || buildSectionSlideId(slide.section?.id || 'slide', slide.slideIndex ?? idx);
        return (slide.type === 'title-card' || slide.type === 'end-title-card' || slide.type === 'section-title-card')
          || isSlideExportEnabled(effectiveId);
      });

      if (exportedSlides.length !== expectedSlides.length) {
        console.warn('[Export Debug] Slide count mismatch in exported HTML', {
          expected: expectedSlides.length,
          found: exportedSlides.length
        });
      }

      expectedSlides.forEach((slide, idx) => {
        if (slide.type !== 'section') return;
        const targetId = `${slide.section?.id || 'slide'}-${slide.slideIndex ?? idx}`;
        if (!doc.getElementById(targetId)) {
          console.warn('[Export Debug] Missing slide id in exported HTML', {
            expectedId: targetId,
            sectionId: slide.section?.id || '(unknown)',
            slideIndex: slide.slideIndex ?? idx
          });
        }
      });
    }

    const slideNavHtml = isSlideLayout
      ? `
      <div class="slide-side-nav" data-slide-nav>
        <div class="slide-side-nav-header">
          <h3>Slides</h3>
          <button type="button" class="slide-side-close" data-slide-nav-close>&times;</button>
        </div>
        <nav>
          ${slideNavItems
            .map(
              (item) =>
                `<button type="button" class="slide-side-link" data-slide-nav-link data-slide-index="${item.index}"><span class="slide-side-thumb" data-slide-thumb aria-hidden="true"></span><span class="slide-side-label"><span class="slide-side-num">${item.index + 1}</span>${item.label}</span></button>`
            )
            .join('')}
        </nav>
      </div>
      <button class="slide-side-toggle" type="button" data-slide-nav-toggle aria-label="Open slide menu">
        ☰ Slides
      </button>
      <div class="slide-nav" aria-label="Slide navigation">
        <button type="button" data-slide-prev aria-label="Previous slide">&larr;</button>
        <div class="slide-counter" data-slide-counter>1 / ${Math.max(slideCount, 1)}</div>
        <button type="button" data-slide-next aria-label="Next slide">&rarr;</button>
      </div>
      `
      : '';

    const footerHtml = isSlideLayout
      ? ''
      : `
      <footer class="bg-gray-900 text-white py-8 mt-16">
        <div class="max-w-7xl mx-auto px-6 text-center">
          <p class="mb-2 font-medium">${headerData.footerCourseInfo}</p>
          <p class="mb-2 text-gray-300">${headerData.footerInstitution}</p>
          <p class="text-gray-400 text-sm">${headerData.footerCopyright}</p>
        </div>
      </footer>
    `;

    const accordionJs = `
      <script>
        document.addEventListener('DOMContentLoaded', function() {
            const smoothScrollTo = (elementY, duration = 1000) => {
                const startingY = window.pageYOffset;
                const diff = elementY - startingY;
                let start;

                const step = (timestamp) => {
                    if (!start) start = timestamp;
                    const time = timestamp - start;
                    const percent = Math.min(time / duration, 1);
                    const easing = percent < 0.5 ? 4 * percent * percent * percent : 1 - Math.pow(-2 * percent + 2, 3) / 2;
                    window.scrollTo(0, startingY + diff * easing);
                    if (time < duration) {
                        window.requestAnimationFrame(step);
                    }
                }
                window.requestAnimationFrame(step);
            }

            document.querySelectorAll('.section-header').forEach(header => {
                header.addEventListener('click', function() {
                    const content = this.nextElementSibling;
                    const icon = this.querySelector('.toggle-icon');
                    
                    if (content && icon) {
                        const isClosed = content.classList.contains('closed');
                        
                        if (isClosed) {
                            content.classList.remove('closed');
                            icon.classList.add('rotated');
                        } else {
                            content.classList.add('closed');
                            icon.classList.remove('rotated');
                        }
                    }
                });
            });
            
            document.querySelectorAll('nav a').forEach(link => {
                link.addEventListener('click', function(e) {
                    e.preventDefault();
                    const targetId = this.getAttribute('href').substring(1);
                    const targetSection = document.getElementById(targetId);
                    
                    if (targetSection) {
                        const content = targetSection.querySelector('.content-container');
                        const icon = targetSection.querySelector('.toggle-icon');
                        
                        if (content && content.classList.contains('closed')) {
                            content.classList.remove('closed');
                            if (icon) icon.classList.add('rotated');
                        }
                        
                        const elementPosition = targetSection.getBoundingClientRect().top + window.pageYOffset;
                        const offsetPosition = elementPosition - 60; 
                        smoothScrollTo(offsetPosition, 1000);
                    }
                });
            });
        });
      </script>`;

    const slideJs = `
      <script>
        (function() {
          var slides = Array.from(document.querySelectorAll('section.slide[data-slide-index]'));
          function isSlideEmpty(el) {
            if (!el) return true;
            // Check only the slide body content, not the header. Canvas
            // (PowerPoint) slides hold their content in .slide-canvas-stage.
            var slideBody = el.querySelector('.slide-body, .slide-canvas-stage, .title-card');
            if (!slideBody) return true;
            var text = (slideBody.textContent || '').trim();
            var hasMedia = slideBody.querySelector('img, video, canvas, svg');
            var richNodes = slideBody.querySelectorAll('.info-box, .exercise-box, .warning-box, ul, ol, li, table');
            var hasRichText = Array.from(richNodes).some(function(node) {
              return (node.textContent || '').trim().length > 0;
            });
            return !text && !hasMedia && !hasRichText;
          }
          function cleanupSlides() {
            slides.forEach(function(slide) {
              if (isSlideEmpty(slide)) {
                slide.remove();
              }
            });
            slides = Array.from(document.querySelectorAll('section.slide[data-slide-index]'));
            slides.forEach(function(slide, idx) {
              slide.setAttribute('data-slide-index', idx);
            });
          }
          cleanupSlides();
          if (!slides.length) return;
          var current = 0;
          var counter = document.querySelector('[data-slide-counter]');
          var prevBtn = document.querySelector('[data-slide-prev]');
          var nextBtn = document.querySelector('[data-slide-next]');
          var nav = document.querySelector('[data-slide-nav]');
          var navToggle = document.querySelector('[data-slide-nav-toggle]');
          var navClose = document.querySelector('[data-slide-nav-close]');
          var navLinks = Array.from(document.querySelectorAll('[data-slide-nav-link]'));

          function buildNavLinksFromSlides() {
            if (!nav) return;
            var navContainer = nav.querySelector('nav');
            if (!navContainer) return;
            var existingLinks = navContainer.querySelectorAll('[data-slide-nav-link]');
            if (existingLinks.length) return;
            navContainer.innerHTML = slides.map(function(slide, idx) {
              var labelEl = slide.querySelector('.slide-title, .title-card-title');
              var label = labelEl ? labelEl.textContent.trim() : '';
              if (!label) {
                label = 'Slide ' + (idx + 1);
              }
              return '<button type="button" class="slide-side-link" data-slide-nav-link data-slide-index="' + idx + '">'
                + '<span class="slide-side-thumb" data-slide-thumb aria-hidden="true"></span>'
                + '<span class="slide-side-label"><span class="slide-side-num">' + (idx + 1) + '</span>' + label + '</span>'
                + '</button>';
            }).join('');
          }

          function rebuildNavLinks() {
            navLinks = Array.from(document.querySelectorAll('[data-slide-nav-link]'));
            var validLinks = [];
            navLinks.forEach(function(link, linkIndex) {
              var idx = parseInt(link.getAttribute('data-slide-index'), 10);
              if (isNaN(idx) || idx >= slides.length) {
                link.remove();
                return;
              }
              // Update the link's index to match the cleaned slide order
              link.setAttribute('data-slide-index', validLinks.length);
              link.addEventListener('click', function() {
                var targetIdx = parseInt(link.getAttribute('data-slide-index'), 10);
                if (!isNaN(targetIdx)) setSlide(targetIdx);
              });
              validLinks.push(link);
            });
            navLinks = validLinks;
          }

          function update() {
            slides.forEach(function(slide, idx) {
              slide.classList.toggle('is-active', idx === current);
            });
            if (counter) counter.textContent = (current + 1) + ' / ' + slides.length;
            if (prevBtn) prevBtn.disabled = current === 0;
            if (nextBtn) nextBtn.disabled = current === slides.length - 1;
            navLinks.forEach(function(link, idx) {
              link.classList.toggle('is-active', idx === current);
            });
          }

          function setSlide(index) {
            current = Math.max(0, Math.min(index, slides.length - 1));
            panX = 0;
            applyPan();
            update();
            window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
          }

          // Left/Right walk the slide sideways rather than changing it, the
          // way they do in the editor's full screen. The offset lives on the
          // card, so it travels with whatever slide is showing and clears the
          // moment another one comes up.
          var panX = 0;
          function applyPan() {
            slides.forEach(function(slide) {
              var card = slide.querySelector('.slide-card');
              if (card) card.style.transform = panX ? 'translateX(' + panX + 'px)' : '';
            });
          }

          function nudge(direction) {
            var limit = window.innerWidth / 2;
            panX = Math.min(Math.max(panX + direction * 40, -limit), limit);
            applyPan();
          }

          function handleKeyDown(event) {
            var target = event.target;
            var tag = target && target.tagName ? target.tagName.toLowerCase() : '';
            var isEditable = target && (target.isContentEditable || ['input', 'textarea', 'select', 'button'].includes(tag));
            if (isEditable) return;
            if (event.key === 'ArrowDown' || event.key === 'PageDown' || event.key === ' ') {
              event.preventDefault();
              setSlide(current + 1);
            }
            if (event.key === 'ArrowUp' || event.key === 'PageUp') {
              event.preventDefault();
              setSlide(current - 1);
            }
            if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
              event.preventDefault();
              nudge(event.key === 'ArrowLeft' ? -1 : 1);
            }
          }

          // --- slide thumbnails ------------------------------------------
          //
          // Each drawer entry shows a miniature of its slide. The miniature is
          // a clone of the real slide element scaled down with a CSS transform,
          // so it always matches what the slide actually looks like and costs
          // the exported file no extra bytes — no rasterised images are stored.
          //
          // Clones are hygiene-sensitive: ids would be duplicated, scripts
          // re-run, players reconnect, and controls stay tabbable. Each is
          // stripped before the clone goes into the document.
          function slideRefWidth() {
            var activeCard = document.querySelector('section.slide.is-active .slide-card');
            var width = activeCard ? activeCard.getBoundingClientRect().width : 0;
            if (!width) {
              var deck = document.querySelector('.slide-deck');
              width = deck ? deck.getBoundingClientRect().width : 0;
            }
            return width || 1200;
          }

          function buildThumb(link) {
            if (!link) return;
            var host = link.querySelector('[data-slide-thumb]');
            if (!host || host.getAttribute('data-thumb-ready')) return;
            host.setAttribute('data-thumb-ready', '1');
            var idx = parseInt(link.getAttribute('data-slide-index'), 10);
            var slide = slides[idx];
            if (!slide) return;
            try {
              var refWidth = slideRefWidth();
              var clone = slide.cloneNode(true);
              clone.classList.remove('is-active');
              clone.removeAttribute('id');
              clone.removeAttribute('data-slide-index');
              // A slide nudged sideways must not hand that offset to its
              // thumbnail — the miniature is always shown centred.
              var cloneCard = clone.querySelector('.slide-card');
              if (cloneCard) cloneCard.style.transform = '';
              Array.prototype.forEach.call(clone.querySelectorAll('[id]'), function(node) {
                node.removeAttribute('id');
              });
              Array.prototype.forEach.call(clone.querySelectorAll('script'), function(node) {
                node.parentNode.removeChild(node);
              });
              Array.prototype.forEach.call(clone.querySelectorAll('iframe, video, audio'), function(node) {
                var placeholder = document.createElement('div');
                placeholder.className = 'slide-side-thumb-media';
                node.parentNode.replaceChild(placeholder, node);
              });
              Array.prototype.forEach.call(
                clone.querySelectorAll('a, button, input, select, textarea, [tabindex]'),
                function(node) { node.setAttribute('tabindex', '-1'); }
              );

              var stage = document.createElement('span');
              stage.className = 'slide-side-thumb-stage';
              stage.setAttribute('inert', '');
              stage.style.width = refWidth + 'px';
              stage.appendChild(clone);
              host.appendChild(stage);

              // Measured after insertion: a flow slide is as tall as its
              // content, so the scale that fits it is only knowable once the
              // clone has laid out.
              var naturalHeight = stage.offsetHeight || (refWidth * 9 / 16);
              var boxWidth = host.clientWidth || 240;
              var boxHeight = host.clientHeight || (boxWidth * 9 / 16);
              var scale = Math.min(boxWidth / refWidth, boxHeight / naturalHeight);
              stage.style.transform = 'scale(' + scale + ')';
              // Centre whatever is left over, so a tall slide that scales to
              // fit the height is not pinned to the left edge.
              stage.style.left = Math.max(0, (boxWidth - refWidth * scale) / 2) + 'px';
              stage.style.top = Math.max(0, (boxHeight - naturalHeight * scale) / 2) + 'px';
            } catch (err) {
              // A thumbnail is a nicety; the drawer still navigates without it.
              host.setAttribute('data-thumb-ready', 'error');
            }
          }

          function setupThumbs() {
            if (!navLinks.length) return;
            if (typeof IntersectionObserver === 'undefined') {
              navLinks.forEach(buildThumb);
              return;
            }
            // Only the entries actually scrolled into the drawer get cloned, so
            // a thirty-slide deck does not clone thirty slides up front.
            var observer = new IntersectionObserver(function(entries) {
              entries.forEach(function(entry) {
                if (!entry.isIntersecting) return;
                buildThumb(entry.target);
                observer.unobserve(entry.target);
              });
            }, { root: nav ? nav.querySelector('nav') : null, rootMargin: '300px 0px' });
            navLinks.forEach(function(link) { observer.observe(link); });
          }

          if (prevBtn) prevBtn.addEventListener('click', function() { setSlide(current - 1); });
          if (nextBtn) nextBtn.addEventListener('click', function() { setSlide(current + 1); });
          buildNavLinksFromSlides();
          rebuildNavLinks();
          setupThumbs();
          if (navToggle && nav) {
            navToggle.addEventListener('click', function() {
              nav.classList.toggle('is-open');
            });
          }
          if (navClose && nav) {
            navClose.addEventListener('click', function() {
              nav.classList.remove('is-open');
            });
          }

          window.addEventListener('keydown', handleKeyDown);
          window.setSlide = setSlide;
          update();
        })();
      </script>`;

    const imageModalHtml = `
      <div class="export-image-modal hidden" id="export-image-modal" aria-hidden="true">
        <div class="export-image-modal-dialog" role="dialog" aria-modal="true">
          <button type="button" class="export-image-modal-close" aria-label="Close image" data-export-image-close>&times;</button>
          <img src="" alt="" class="export-image-modal-img" data-export-image-full />
          <div class="export-image-modal-caption" data-export-image-caption></div>
        </div>
      </div>`;

    const imageModalJs = `
      <script>
        (function() {
          function initExportImageModal() {
            var modal = document.getElementById('export-image-modal');
            if (!modal) return;
            var modalImage = modal.querySelector('[data-export-image-full]');
            var modalCaption = modal.querySelector('[data-export-image-caption]');
            var closeButtons = Array.from(modal.querySelectorAll('[data-export-image-close]'));
            // The trigger wraps the picture it opens, so the picture's own src
            // is the one to enlarge. Copying it into an attribute as well wrote
            // every base64 upload into the file twice.
            var triggers = Array.from(document.querySelectorAll('.export-image-trigger'));

            function openModal(src, alt, caption) {
              if (!modalImage) return;
              modalImage.src = src || '';
              modalImage.alt = alt || '';
              if (modalCaption) {
                modalCaption.textContent = caption || '';
              }
              modal.classList.remove('hidden');
              modal.classList.add('is-open');
              modal.setAttribute('aria-hidden', 'false');
              document.body.style.overflow = 'hidden';
            }

            function closeModal() {
              modal.classList.remove('is-open');
              modal.classList.add('hidden');
              modal.setAttribute('aria-hidden', 'true');
              if (modalImage) {
                modalImage.src = '';
                modalImage.alt = '';
              }
              document.body.style.overflow = '';
            }

            triggers.forEach(function(trigger) {
              trigger.addEventListener('click', function(event) {
                event.preventDefault();
                var picture = trigger.querySelector('img');
                var src = picture ? (picture.getAttribute('src') || picture.src || '') : '';
                if (!src) return;
                var alt = picture ? (picture.getAttribute('alt') || '') : '';
                var caption = trigger.getAttribute('data-export-image-caption') || '';
                openModal(src, alt, caption);
              });
            });

            closeButtons.forEach(function(btn) {
              btn.addEventListener('click', closeModal);
            });

            modal.addEventListener('click', function(event) {
              if (event.target === modal) {
                closeModal();
              }
            });

            document.addEventListener('keydown', function(event) {
              if (event.key === 'Escape' && modal.classList.contains('is-open')) {
                closeModal();
              }
            });
          }

          document.addEventListener('DOMContentLoaded', initExportImageModal);
          setTimeout(initExportImageModal, 100);
        })();
      </script>`;

    const fixedStyles = `
      <style>
        /* Exported HTML must be self-contained; keep a small Tailwind-compatible utility subset instead of loading the development CDN. */
        *, ::before, ::after { box-sizing: border-box; border-width: 0; border-style: solid; border-color: #e5e7eb; }
        /* The same stack the app renders with (index.css body). It used to
           declare a different one, and a different font means different text
           metrics: lines wrapped where they do not wrap on screen, which is how
           a block that fits its frame in the app arrived overflowing in the
           file. */
        html { line-height: 1.5; -webkit-text-size-adjust: 100%; tab-size: 4; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif; }
        body { margin: 0; }
        h1, h2, h3, h4, h5, h6, p { margin: 0; }
        img, svg, canvas, iframe { display: block; vertical-align: middle; }
        img { max-width: 100%; height: auto; }
        video, iframe {
            width: 100%;
            height: 100%;
            max-width: 100%;
            max-height: 100%;
        }
        .export-media-fallback {
            position: absolute;
            right: 0.75rem;
            bottom: 0.75rem;
            z-index: 2;
            color: #fff;
            background: rgba(15, 23, 42, 0.88);
            border-radius: 999px;
            padding: 0.4rem 0.65rem;
            font-size: 0.8rem;
            font-weight: 600;
            text-decoration: none;
            box-shadow: 0 10px 24px rgba(0,0,0,0.25);
        }
        .relative { position: relative; } .absolute { position: absolute; } .fixed { position: fixed; } .inset-0 { inset: 0; }
        .mx-auto { margin-left: auto; margin-right: auto; } .my-8 { margin-top: 2rem; margin-bottom: 2rem; } .mb-1 { margin-bottom: 0.25rem; } .mb-2 { margin-bottom: 0.5rem; } .mb-3 { margin-bottom: 0.75rem; } .mb-4 { margin-bottom: 1rem; } .mt-2 { margin-top: 0.5rem; } .mt-4 { margin-top: 1rem; }
        .block { display: block; } .inline-block { display: inline-block; } .flex { display: flex; } .inline-flex { display: inline-flex; } .grid { display: grid; } .hidden { display: none; }
        .h-full { height: 100%; } .w-full { width: 100%; } .max-w-6xl { max-width: 72rem; } .max-w-7xl { max-width: 80rem; }
        .grid-cols-1 { grid-template-columns: repeat(1, minmax(0, 1fr)); } .gap-2 { gap: 0.5rem; } .gap-3 { gap: 0.75rem; } .gap-4 { gap: 1rem; } .gap-6 { gap: 1.5rem; }
        .items-center { align-items: center; } .items-start { align-items: flex-start; } .justify-center { justify-content: center; } .justify-between { justify-content: space-between; } .overflow-hidden { overflow: hidden; }
        .rounded { border-radius: 0.25rem; } .rounded-lg { border-radius: 0.5rem; } .rounded-xl { border-radius: 0.75rem; } .rounded-2xl { border-radius: 1rem; } .border-0 { border-width: 0; } .border { border-width: 1px; } .border-l-4 { border-left-width: 4px; }
        .bg-gray-50 { background-color: #f9fafb; } .bg-gray-100 { background-color: #f3f4f6; } .bg-gray-900 { background-color: #111827; } .bg-white { background-color: #fff; }
        .p-2 { padding: 0.5rem; } .p-3 { padding: 0.75rem; } .p-4 { padding: 1rem; } .p-6 { padding: 1.5rem; } .px-4 { padding-left: 1rem; padding-right: 1rem; } .px-6 { padding-left: 1.5rem; padding-right: 1.5rem; } .px-16 { padding-left: 4rem; padding-right: 4rem; } .py-2 { padding-top: 0.5rem; padding-bottom: 0.5rem; } .py-3 { padding-top: 0.75rem; padding-bottom: 0.75rem; } .py-12 { padding-top: 3rem; padding-bottom: 3rem; }
        .text-xs { font-size: 0.75rem; line-height: 1rem; } .text-sm { font-size: 0.875rem; line-height: 1.25rem; } .text-base { font-size: 1rem; line-height: 1.5rem; } .text-lg { font-size: 1.125rem; line-height: 1.75rem; } .text-xl { font-size: 1.25rem; line-height: 1.75rem; } .text-2xl { font-size: 1.5rem; line-height: 2rem; } .text-3xl { font-size: 1.875rem; line-height: 2.25rem; }
        .font-medium { font-weight: 500; } .font-semibold { font-weight: 600; } .font-bold { font-weight: 700; } .text-center { text-align: center; } .text-gray-500 { color: #6b7280; } .text-gray-600 { color: #4b5563; } .text-gray-700 { color: #374151; } .text-gray-800 { color: #1f2937; } .text-gray-900 { color: #111827; } .text-white { color: #fff; }
        .shadow { box-shadow: 0 1px 3px rgba(0,0,0,.1), 0 1px 2px rgba(0,0,0,.06); } .shadow-lg { box-shadow: 0 10px 15px -3px rgba(0,0,0,.1), 0 4px 6px -4px rgba(0,0,0,.1); }
        .whitespace-pre-wrap { white-space: pre-wrap; }
        @media (min-width: 768px) { .md\\:grid-cols-2 { grid-template-columns: repeat(2, minmax(0, 1fr)); } .md\\:grid-cols-3 { grid-template-columns: repeat(3, minmax(0, 1fr)); } }
        .logo {
            max-height: 80px;
            margin-bottom: 15px;
            display: block;
            margin-left: auto;
            margin-right: auto;
        }
        .toggle-icon { 
            transition: transform 0.3s ease-in-out; 
        }
        .toggle-icon.rotated { 
            transform: rotate(90deg); 
        }
        body {
            background-color: #f9fafb;
        }

        .block {
            margin: 1rem 0;
        }
        .export-image-trigger {
            background: transparent;
            border: none;
            padding: 0;
            cursor: zoom-in;
            display: inline-flex;
        }
        .export-image-trigger:focus-visible {
            outline: 3px solid rgba(59, 130, 246, 0.6);
            outline-offset: 4px;
            border-radius: 12px;
        }
        .export-image-modal {
            position: fixed;
            inset: 0;
            display: none;
            align-items: center;
            justify-content: center;
            background: rgba(15, 23, 42, 0.72);
            z-index: 80;
            padding: 2rem;
        }
        .export-image-modal.is-open {
            display: flex;
        }
        .export-image-modal-dialog {
            background: #ffffff;
            border-radius: 20px;
            padding: 2.5rem 2rem 2rem;
            max-width: min(90vw, 960px);
            max-height: 90vh;
            width: 100%;
            box-shadow: 0 30px 80px rgba(15, 23, 42, 0.3);
            position: relative;
            display: flex;
            flex-direction: column;
            gap: 1rem;
        }
        .export-image-modal-img {
            width: 100%;
            max-height: 70vh;
            object-fit: contain;
            border-radius: 12px;
            background: #f8fafc;
        }
        .export-image-modal-caption {
            font-size: 0.95rem;
            color: #4b5563;
            text-align: center;
        }
        .export-image-modal-caption:empty {
            display: none;
        }
        .export-image-modal-close {
            position: absolute;
            top: 0.75rem;
            right: 0.75rem;
            width: 40px;
            height: 40px;
            border-radius: 50%;
            border: none;
            background: #f1f5f9;
            color: #0f172a;
            font-size: 1.5rem;
            cursor: pointer;
            display: inline-flex;
            align-items: center;
            justify-content: center;
        }
        .export-image-modal-close:hover {
            background: #e2e8f0;
        }
        .headline-preview h1,
        .headline-preview h2,
        .headline-preview h3 {
            font-weight: 700;
            line-height: 1.2;
            margin: 0 0 0.5em 0;
        }
        .headline-preview h1 { font-size: 2em; }
        .headline-preview h2 { font-size: 1.5em; }
        .headline-preview h3 { font-size: 1.25em; }
        .rich-editor-content h1,
        .rich-editor-content h2,
        .rich-editor-content h3,
        .rich-editor-content h4,
        .rich-editor-content h5,
        .rich-editor-content h6 {
            font-weight: 600;
            margin-top: 1.5em;
            margin-bottom: 0.5em;
            line-height: 1.2;
        }
        .rich-editor-content h1 { font-size: 2em; }
        .rich-editor-content h2 { font-size: 1.5em; }
        .rich-editor-content h3 { font-size: 1.25em; }
        .rich-editor-content h4 { font-size: 1.1em; }
        .rich-editor-content p {
            margin-bottom: 1em;
            line-height: 1.6;
        }
        .rich-editor-content ul,
        .rich-editor-content ol {
            margin-bottom: 1em;
            padding-left: 1.5em;
        }
        .rich-editor-content li {
            /* Matches the app, whose later rule narrows this from 0.5em. The
               export's copy of the stylesheet had kept the earlier value, so
               every list in an exported slide ran ~4px per item taller than the
               same list on screen and the last items fell off the frame. */
            margin-bottom: 0.25em;
        }
        .rich-editor-content a {
            color: #3b82f6;
            text-decoration: underline;
        }
        .rich-editor-content a:hover {
            color: #1d4ed8;
        }
        .rich-editor-content strong { font-weight: 600; }
        .rich-editor-content em { font-style: italic; }
        .box {
            margin: 1.5rem 0;
            padding: 1rem;
            border-left: 4px solid;
            border-radius: 0.5rem;
        }
        .info-box { background: #eff6ff; border-color: #60a5fa; }
        .exercise-box { background: #ecfdf5; border-color: #34d399; }
        .warning-box { background: #fffbeb; border-color: #fbbf24; }

        /* EXPORT FIX: ensure accordion titles are white with no underline */
        .section-header h2,
        .section-header h2 a {
            color: #fff;
            text-decoration: none;
        }

        .content-container {
            display: grid;
            grid-template-rows: 1fr;
            transition: grid-template-rows 0.7s cubic-bezier(0.83, 0, 0.17, 1), opacity 0.5s ease-out;
            opacity: 1;
            overflow: hidden;
        }
        .content-container.closed {
            grid-template-rows: 0fr;
            opacity: 0;
        }
        .content-container > div {
            min-height: 0;
            overflow: hidden;
        }
        
        .content-container * {
            will-change: auto;
        }

        .slide-deck {
            position: relative;
        }
        .slide {
            display: none;
        }
        .slide.is-active {
            display: block;
            animation: slide-fade-in 220ms ease-in-out;
        }
        .slide-card {
            background: #ffffff;
            border-radius: 24px;
            border: 1px solid #e5e7eb;
            box-shadow: 0 10px 30px rgba(15, 23, 42, 0.08);
            overflow: hidden;
            position: relative;
        }
        /* Author-written slide layouts. Deliberately unstyled apart from being
           a container in its own right, which is what makes the cqw units in
           the block's brief mean the same thing here as in the editor. */
        .slide-custom-html {
            width: 100%;
            min-width: 0;
            container-type: inline-size;
        }
        .slide-card-title {
            font-size: 1.75rem;
            font-weight: 700;
            color: #0f172a;
            margin-bottom: 0.5rem;
        }
        .slide-card-subtitle {
            font-size: 1rem;
            color: #475569;
            margin-bottom: 1.5rem;
        }
        .slide-card-grid {
            display: grid;
            gap: 1.25rem;
            grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
        }
        .slide-card-item {
            background: #ffffff;
            border: 1px solid #e2e8f0;
            border-radius: 16px;
            padding: 1rem 1.25rem;
            box-shadow: 0 8px 20px rgba(15, 23, 42, 0.08);
        }
        .slide-card-item-title {
            font-size: 1.1rem;
            font-weight: 700;
            color: #0f172a;
            margin-bottom: 0.5rem;
        }
        .slide-card-item-body {
            color: #475569;
            line-height: 1.6;
        }
        .title-card {
            aspect-ratio: 16 / 9;
            width: 100%;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            text-align: center;
            padding: 4rem 2rem;
            padding-bottom: 5rem;
            color: var(--title-card-text-color, #ffffff);
            position: relative;
        }
        .title-card.title-card--top {
            justify-content: flex-start;
        }
        .title-card.title-card--center {
            justify-content: center;
        }
        .title-card.title-card--bottom {
            justify-content: flex-end;
        }
        .title-card-image {
            max-width: 120px;
            margin-bottom: 1.5rem;
        }
        .title-card-image-group {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 0.35rem;
            margin-bottom: 1.5rem;
        }
        .title-card-image-group .title-card-image {
            margin-bottom: 0;
        }
        .title-card-image-citation {
            font-size: 0.75rem;
            opacity: 0.85;
            margin: 0;
            color: var(--title-card-text-color, #ffffff);
            text-align: center;
        }
        .title-card-title {
            font-size: 3rem;
            font-weight: 700;
            margin-bottom: 1rem;
            color: var(--title-card-text-color, #ffffff);
        }
        .title-card-subtitle {
            font-size: 1.25rem;
            opacity: 0.9;
            color: var(--title-card-text-color, #ffffff);
        }
        .title-card-meta {
            margin-top: auto;
            width: 100%;
            display: flex;
            justify-content: space-between;
            align-items: flex-end;
            gap: 1.5rem;
        }
        .title-card-meta-left,
        .title-card-meta-right {
            display: flex;
            flex-direction: column;
            gap: 0.35rem;
        }
        .title-card-meta-right {
            text-align: right;
            color: var(--title-card-text-color, #ffffff);
        }
        .title-card-meta-right--row {
            flex-direction: row;
            flex-wrap: wrap;
            align-items: baseline;
            justify-content: flex-end;
            gap: 0.35rem 0.75rem;
        }
        .title-card-meta-right--row > p:not(:last-child)::after {
            content: '·';
            margin-left: 0.75rem;
            opacity: 0.6;
        }
        .title-card-meta-logo {
            max-height: 80px;
            object-fit: contain;
        }
        .title-card-meta-course,
        .title-card-meta-instructor,
        .title-card-meta-email {
            margin: 0;
            font-weight: 600;
        }
        .title-card-meta-email {
            font-weight: 500;
            opacity: 0.9;
        }
        .slide-card-title {
            font-size: 1.75rem;
            font-weight: 700;
            color: #0f172a;
            margin-bottom: 0.5rem;
        }
        .slide-card-subtitle {
            font-size: 1rem;
            color: #475569;
            margin-bottom: 1.5rem;
        }
        .slide-card-grid {
            display: grid;
            gap: 1.25rem;
            grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
        }
        .slide-card-item {
            background: #ffffff;
            border: 1px solid #e2e8f0;
            border-radius: 16px;
            padding: 1rem 1.25rem;
            box-shadow: 0 8px 20px rgba(15, 23, 42, 0.08);
        }
        .slide-card-item-title {
            font-size: 1.1rem;
            font-weight: 700;
            color: #0f172a;
            margin-bottom: 0.5rem;
        }
        .slide-card-item-body {
            color: #475569;
            line-height: 1.6;
        }
        .title-card {
            aspect-ratio: 16 / 9;
            width: 100%;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            text-align: center;
            padding: 4rem 2rem;
            color: var(--title-card-text-color, #ffffff);
        }
        .title-card-image {
            max-width: 120px;
            margin-bottom: 1.5rem;
        }
        .title-card-image-group {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 0.35rem;
            margin-bottom: 1.5rem;
        }
        .title-card-image-group .title-card-image {
            margin-bottom: 0;
        }
        .title-card-image-citation {
            font-size: 0.75rem;
            opacity: 0.85;
            margin: 0;
            color: var(--title-card-text-color, #ffffff);
            text-align: center;
        }
        .title-card-title {
            font-size: 3rem;
            font-weight: 700;
            margin-bottom: 1rem;
            color: var(--title-card-text-color, #ffffff);
        }
        .title-card-subtitle {
            font-size: 1.25rem;
            opacity: 0.9;
            color: var(--title-card-text-color, #ffffff);
        }
        .title-card {
            aspect-ratio: 16 / 9;
            width: 100%;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            text-align: center;
            padding: 4rem 2rem;
            color: var(--title-card-text-color, #ffffff);
        }
        .title-card-image {
            max-width: 120px;
            margin-bottom: 1.5rem;
        }
        .title-card-title {
            font-size: 3rem;
            font-weight: 700;
            margin-bottom: 1rem;
            color: var(--title-card-text-color, #ffffff);
        }
        .title-card-subtitle {
            font-size: 1.25rem;
            opacity: 0.9;
            color: var(--title-card-text-color, #ffffff);
        }
        .slide-card-title {
            font-size: 1.75rem;
            font-weight: 700;
            color: #0f172a;
            margin-bottom: 0.5rem;
        }
        .slide-card-subtitle {
            font-size: 1rem;
            color: #475569;
            margin-bottom: 1.5rem;
        }
        .slide-header {
            color: #ffffff;
            padding: 2rem 2rem 1.5rem;
        }
        .slide-header-row {
            display: flex;
            align-items: flex-start;
            justify-content: space-between;
            gap: 1rem;
        }
        .slide-logo img {
            max-height: 48px;
        }
        .slide-header-row {
            display: flex;
            align-items: flex-start;
            justify-content: space-between;
            gap: 1rem;
        }
        .slide-logo img {
            max-height: 48px;
        }
        .slide-label {
            text-transform: uppercase;
            letter-spacing: 0.2em;
            font-size: 0.7rem;
            opacity: 0.8;
        }
        .slide-title {
            font-size: 2rem;
            font-weight: 700;
            margin-top: 0.5rem;
            display: flex;
            flex-direction: column;
            gap: 0.15rem;
        }
        /* The student-friendly line, in a deliberately quieter voice so the
           header reads as one title with a subtitle, not two titles. */
        .slide-title__subtitle {
            font-size: 0.8125rem;
            font-weight: 400;
            letter-spacing: 0.06em;
            opacity: 0.8;
        }
        .slide-body {
            position: relative;
            padding: 2rem;
            padding-bottom: 120px;
        }
        /* PowerPoint (canvas) slides: fixed 16:9 card, header bar, and a stage
           where blocks are positioned by their frames — matching the app
           designer and the pptx export. */
        .slide-card--canvas {
            aspect-ratio: 16 / 9;
            display: flex;
            flex-direction: column;
            overflow: hidden;
            background-size: cover;
            background-position: center;
            background-repeat: no-repeat;
            container-type: inline-size;
        }
        .slide-header--canvas {
            height: 11.33%;
            padding: 0 2.5%;
            display: flex;
            align-items: center;
        }
        .slide-title--canvas {
            margin: 0;
            font-weight: 700;
            font-size: 2.3cqw;
            line-height: 1.1;
            flex-direction: row;
            align-items: baseline;
            gap: 0.6em;
            min-width: 0;
        }
        .slide-title--canvas .slide-title__main {
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }
        .slide-title--canvas .slide-title__subtitle {
            font-size: 1.5cqw;
            letter-spacing: 0.04em;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            flex-shrink: 0;
            max-width: 40%;
        }
        .slide-canvas-stage {
            position: relative;
            flex: 1 1 auto;
            overflow: hidden;
        }
        .slide-canvas-item {
            position: absolute;
            box-sizing: border-box;
            overflow: hidden;
            --text-scale: 1;
        }
        /* Canvas text is sized as a share of the slide's width, mirroring
           .slide-designer__item in the app (and the point sizes the pptx export
           uses: body 14pt on a 13.33in slide ≈ 1.46% of its width). --text-scale
           is the block's own multiplier, set from its frame above.

           Without these two things the export drew canvas text at browser
           default sizes regardless of the slide's size or the author's scaling,
           so blocks overflowed their frames and the stage clipped them —
           lists lost their last items and notes were cut mid-line. */
        /* A Custom HTML/CSS block is exempt. It is not a text block the slide
           gets to size — it is a whole layout with its own type scale, and the
           app never applies this rule to it, because the wrapper it renders
           carries no "block" class. Letting it through here meant every em
           inside a hand-written layout was multiplied by the slide's text
           scale — 3.2x on a scaled-up frame — so a 0.5em corner radius came
           out pill-shaped in the export while looking right in the editor.
           Card padding, gaps, border widths and badge sizes drifted with it. */
        .slide-canvas-item .rich-editor-content,
        .slide-canvas-item .block:not(.slide-custom-html) {
            --base-font: 1.46cqw;
            font-size: calc(var(--base-font) * var(--text-scale));
            line-height: 1.4;
        }
        /* Pinned rather than inherited, so an em inside a hand-written layout
           means the same thing here as it does in the editor and in the
           rasterized PowerPoint/PDF slide. */
        .slide-custom-html {
            font-size: 16px;
            line-height: 1.5;
        }
        .slide-canvas-item h1 { --base-font: 2.7cqw;  font-size: calc(var(--base-font) * var(--text-scale)); }
        .slide-canvas-item h2 { --base-font: 2.3cqw;  font-size: calc(var(--base-font) * var(--text-scale)); }
        .slide-canvas-item h3 { --base-font: 1.98cqw; font-size: calc(var(--base-font) * var(--text-scale)); }
        .slide-canvas-item h4 { --base-font: 1.77cqw; font-size: calc(var(--base-font) * var(--text-scale)); }
        .slide-canvas-item .apa-figure-note {
            --base-font: 1.3cqw;
            font-size: calc(var(--base-font) * var(--text-scale));
            line-height: 1.4;
        }
        .slide-canvas-item .apa-figure-note a {
            overflow-wrap: anywhere;
            word-break: break-word;
        }
        /* A citation detached from its image renders as a bare figure note, the
           way ApaCitationRenderer does. It carries Tailwind utilities for its
           colour and leading (text-slate-600, leading-snug, text-slate-400)
           which the export's stylesheet does not define, so the note's own rule
           carries them instead. Its size comes from text-sm above — the same
           rule that wins in the app. */
        .apa-figure-note {
            color: #475569;
            line-height: 1.375;
            text-align: left;
        }
        .apa-figure-note .text-slate-400 {
            color: #94a3b8;
        }
        .apa-figure-note a {
            color: #4f46e5;
            margin-left: 0.5rem;
        }
        /* Blocks whose renderers size text with utility classes rather than the
           canvas cqw system still answer to the block's scale, as in the app. */
        .slide-canvas-item .text-xs   { font-size: calc(0.75rem  * var(--text-scale)); }
        .slide-canvas-item .text-sm   { font-size: calc(0.875rem * var(--text-scale)); }
        .slide-canvas-item .text-base { font-size: calc(1rem     * var(--text-scale)); }
        .slide-canvas-item .text-lg   { font-size: calc(1.125rem * var(--text-scale)); }
        .slide-canvas-item .text-xl   { font-size: calc(1.25rem  * var(--text-scale)); }
        .slide-canvas-item .text-2xl  { font-size: calc(1.5rem   * var(--text-scale)); }
        /* A citation URL has no spaces to break at, so in a frame that clips its
           overflow it was sliced off mid-address. Wrap it instead, as the app
           does. break-word rather than anywhere: it only breaks the words that
           would otherwise overflow, and leaves intrinsic widths alone. */
        .slide-canvas-item {
            overflow-wrap: break-word;
        }
        .slide-canvas-flow {
            position: absolute;
            inset: 0;
            padding: 3% 4%;
            overflow: auto;
        }
        .slide-canvas-item > * {
            margin-top: 0;
        }
        /* Media blocks in canvas slides are laid out at the frame's width and
           then scaled as one object by the fit script, the way the designer's
           ScaleToFit does — so a picture and its citation shrink together.

           This used to be done with flexbox instead: the picture was allowed to
           shrink while the caption and citation kept their natural size. A
           two-line APA note then took more room than the frame had, so it spilled
           past the frame's edge and lost its second line. The rules below only
           bound the picture by the frame's width; its height, and the block's,
           follow from that. */
        .slide-canvas-item[data-fit="media"] > * {
            margin-top: 0 !important;
            margin-bottom: 0 !important;
        }
        .slide-canvas-item .export-image-trigger {
            display: block;
            width: 100%;
        }
        /* On a canvas slide the frame is the picture's size — the author drags
           it, and the block's size preset gives way to it. This is the app's
           own behaviour, not an export decision: LessonTemplate.css carries the
           identical rule on .slide-designer__fit-inner img, where !important
           beats the max-w-* class ImageRenderer puts on the same element. The
           two have to agree, so this rule mirrors that one exactly.

           !important is load-bearing here and must stay. Without it the inline
           preset blockToHtml writes for the normal (non-canvas) flow leaks into
           canvas slides and becomes a second, tighter cap, so any picture in a
           frame wider than its preset exports smaller than it looks on the
           slide. Measured in an 800px frame: the app draws 800px, a preset-
           capped export drew 672px. The same goes for folding the frame into
           the inline style as min(100%, 42rem) — that is this bug written one
           level down, where this rule can no longer correct it. */
        .slide-canvas-item img,
        .slide-canvas-item iframe,
        .slide-canvas-item video {
            max-width: 100% !important;
        }
        .slide-canvas-item .export-image-trigger img,
        .slide-canvas-item img {
            height: auto;
            margin-left: auto;
            margin-right: auto;
            object-fit: contain;
        }
        /* Video blocks in canvas slides: fit the player (and its caption)
           inside the frame instead of overflowing/clipping. */
        .slide-canvas-item:has(.export-video-frame) {
            display: flex;
            flex-direction: column;
        }
        .slide-canvas-item:has(.export-video-frame) > * {
            height: 100%;
            min-height: 0;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: flex-start;
            margin-top: 0 !important;
            margin-bottom: 0 !important;
        }
        .slide-canvas-item .export-video-frame {
            flex: 0 1 auto;
            min-height: 0;
            width: auto !important;
            height: 100%;
            max-width: 100%;
            max-height: 100%;
            margin: 0 auto !important;
        }
        .slide-corner-logo {
            position: absolute;
            left: 2.2%;
            bottom: 3.5%;
            z-index: 5;
        }
        .slide-corner-logo-img {
            width: auto;
            display: block;
        }
        .slide-deck-name {
            position: absolute;
            left: 1.5rem;
            bottom: 1.25rem;
            font-weight: 600;
            color: #6b7280;
        }
        /* Slide number, bottom-right. Matches the app's badge: black on the
           white content slides, and the card's own text colour on a title
           card, which can be any colour the author chose. */
        .slide-number {
            position: absolute;
            right: 1.5rem;
            bottom: 1.25rem;
            font-size: 0.85rem;
            font-weight: 600;
            line-height: 1;
            font-variant-numeric: tabular-nums;
            color: #111827;
        }
        .title-card .slide-number {
            color: var(--title-card-text-color, #111827);
        }
        /* The corner mark. Sized and inset as a share of the card, not in
           pixels: the exported card is a fluid 16:9 box, so a pixel width
           would only be right at one window size. The percentages are the
           authored values against the 1280x720 slide the app measures with. */
        .title-card {
            position: relative;
        }
        .title-card-corner-logo {
            position: absolute;
            top: 3.9%;
            height: auto;
            max-width: 45%;
            object-fit: contain;
        }
        .title-card-corner-logo--top-left {
            left: 2.2%;
        }
        .title-card-corner-logo--top-right {
            right: 2.2%;
        }
        .slide-nav {
            position: fixed;
            bottom: 1.75rem;
            left: 50%;
            transform: translateX(-50%);
            display: flex;
            align-items: center;
            gap: 0.75rem;
            background: transparent;
            padding: 0.5rem 0.75rem;
            z-index: 50;
        }
        .slide-side-toggle {
            position: fixed;
            top: 1.5rem;
            left: 1.5rem;
            z-index: 50;
            background: rgba(255, 255, 255, 0.95);
            backdrop-filter: blur(10px);
            border: 1px solid rgba(0, 0, 0, 0.08);
            border-radius: 8px;
            padding: 0.4rem 0.8rem;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
            font-weight: 500;
            font-size: 0.875rem;
            color: #1f2937;
            cursor: pointer;
            transition: all 0.2s ease;
        }
        .slide-side-toggle:hover {
            background: #ffffff;
            box-shadow: 0 6px 16px rgba(0, 0, 0, 0.15);
        }
        .slide-side-nav {
            position: fixed;
            top: 0;
            left: 0;
            height: 100%;
            width: 320px;
            background: #ffffff;
            border-right: 1px solid #e5e7eb;
            box-shadow: 0 20px 40px rgba(15, 23, 42, 0.15);
            transform: translateX(-100%);
            transition: transform 0.2s ease-in-out;
            z-index: 60;
            padding: 1.5rem;
            display: flex;
            flex-direction: column;
            gap: 1rem;
        }
        .slide-side-nav.is-open {
            transform: translateX(0);
        }
        .slide-side-nav-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
        }
        .slide-side-nav-header h3 {
            font-size: 1.1rem;
            font-weight: 700;
            color: #111827;
        }
        .slide-side-close {
            background: transparent;
            border: none;
            font-size: 1.5rem;
            line-height: 1;
            color: #6b7280;
        }
        .slide-side-nav nav {
            display: flex;
            flex-direction: column;
            gap: 0.5rem;
            flex: 1;
            min-height: 0;
            overflow-y: auto;
            overflow-x: auto;
            padding-right: 0.5rem;
        }
        .slide-side-link {
            display: flex;
            flex-direction: column;
            gap: 0.4rem;
            width: 100%;
            text-align: left;
            padding: 0.5rem;
            border-radius: 0.75rem;
            border: 1px solid transparent;
            color: #374151;
            background: #f9fafb;
            font-size: 0.9rem;
            cursor: pointer;
        }
        .slide-side-link.is-active {
            border-color: #3b82f6;
            background: #eff6ff;
            color: #1d4ed8;
        }
        /* Slide thumbnails. The box is a fixed 16:9 window; the script drops a
           scaled-down clone of the real slide inside it, so the picture is the
           slide itself rather than a separate rendering that could drift from
           it. Empty until the script fills it, which is why it carries its own
           placeholder background. */
        .slide-side-thumb {
            position: relative;
            display: block;
            width: 100%;
            aspect-ratio: 16 / 9;
            overflow: hidden;
            border-radius: 0.5rem;
            border: 1px solid #e5e7eb;
            background: #f3f4f6;
        }
        .slide-side-link.is-active .slide-side-thumb {
            border-color: #93c5fd;
        }
        .slide-side-thumb-stage {
            position: absolute;
            top: 0;
            left: 0;
            transform-origin: top left;
            pointer-events: none;
        }
        /* The live deck hides every slide but the active one; a clone standing
           in for a thumbnail has to show regardless. */
        .slide-side-thumb-stage .slide {
            display: block;
        }
        /* Players are not cloned — thirty live iframes would each start their
           own network connection. They leave a neutral block behind so the
           slide's composition still reads. */
        .slide-side-thumb-media {
            width: 100%;
            aspect-ratio: 16 / 9;
            border-radius: 0.5rem;
            background: #1f2937;
        }
        .slide-side-label {
            display: flex;
            align-items: baseline;
            gap: 0.4rem;
            font-size: 0.8rem;
            line-height: 1.3;
        }
        .slide-side-num {
            flex: none;
            min-width: 1.25rem;
            font-variant-numeric: tabular-nums;
            font-weight: 600;
            color: #9ca3af;
        }
        .slide-side-link.is-active .slide-side-num {
            color: #3b82f6;
        }
        .slide-nav button {
            width: 44px;
            height: 44px;
            border-radius: 50%;
            border: 1px solid #dbeafe;
            background: #ffffff;
            color: #1d4ed8;
            font-weight: 700;
            font-size: 1rem;
            cursor: pointer;
            transition: all 0.2s ease;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            box-shadow: 0 6px 20px rgba(37, 99, 235, 0.18);
        }
        .slide-nav button[data-slide-prev] {
            background: #2b6fd7;
            color: #ffffff;
            border-color: #2b6fd7;
            box-shadow: 0 8px 24px rgba(37, 99, 235, 0.35);
        }
        .slide-nav button:hover:not(:disabled) {
            transform: translateY(-1px);
            box-shadow: 0 10px 28px rgba(37, 99, 235, 0.22);
        }
        .slide-nav button:disabled {
            opacity: 0.4;
            cursor: not-allowed;
            box-shadow: none;
        }
        .slide-counter {
            background: #ffffff;
            color: #1d4ed8;
            font-weight: 700;
            border-radius: 999px;
            padding: 0.35rem 0.9rem;
            border: 1px solid #dbeafe;
            box-shadow: 0 6px 20px rgba(37, 99, 235, 0.14);
            min-width: 78px;
            text-align: center;
        }
        ${slideGlobalCss || ''}
        @keyframes slide-fade-in {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
        }
        /* Card Content Styles */
        .card-content {
            line-height: 1.6;
        }
        .card-content h1,
        .card-content h2,
        .card-content h3,
        .card-content h4,
        .card-content h5,
        .card-content h6 {
            font-weight: 600;
            margin-top: 1em;
            margin-bottom: 0.5em;
            line-height: 1.2;
        }
        .card-content h1 { font-size: 1.5em; }
        .card-content h2 { font-size: 1.25em; }
        .card-content h3 { font-size: 1.1em; }
        .card-content h4 { font-size: 1em; }
        .card-content p {
            margin-bottom: 0.75em;
            line-height: 1.6;
        }
        .card-content p:first-child {
            margin-top: 0;
        }
        .card-content p:last-child {
            margin-bottom: 0;
        }
        .card-content ul,
        .card-content ol {
            margin: 0.75em 0;
            padding-left: 1.5em;
        }
        .card-content ul {
            list-style-type: disc;
        }
        .card-content ol {
            list-style-type: decimal;
        }
        .card-content ol[type="a"],
        .rich-editor-content ol[type="a"],
        .html-block-preview ol[type="a"] {
            list-style-type: lower-alpha;
        }
        .card-content ol[type="A"],
        .rich-editor-content ol[type="A"],
        .html-block-preview ol[type="A"] {
            list-style-type: upper-alpha;
        }
        .card-content ol[type="i"],
        .rich-editor-content ol[type="i"],
        .html-block-preview ol[type="i"] {
            list-style-type: lower-roman;
        }
        .card-content ol[type="I"],
        .rich-editor-content ol[type="I"],
        .html-block-preview ol[type="I"] {
            list-style-type: upper-roman;
        }
        .card-content ol[type="1"],
        .rich-editor-content ol[type="1"],
        .html-block-preview ol[type="1"] {
            list-style-type: decimal;
        }
        .card-content li {
            margin-bottom: 0.25em;
            line-height: 1.5;
        }
        .card-content a {
            color: inherit;
            text-decoration: underline;
            opacity: 0.9;
        }
        .card-content a:hover {
            opacity: 1;
        }
        .card-content strong {
            font-weight: 600;
        }
        .card-content em {
            font-style: italic;
        }
        .card-content u {
            text-decoration: underline;
        }
        .card-content s {
            text-decoration: line-through;
        }
        .card-content mark {
            background-color: rgba(255, 255, 0, 0.3);
            padding: 0.125em 0.25em;
            border-radius: 0.125em;
        }
        /* Responsive Grid Classes for Cards */
        .card-grid-2col {
            display: grid;
            grid-template-columns: 1fr;
            gap: 16px;
        }
        .card-grid-3col {
            display: grid;
            grid-template-columns: 1fr;
            gap: 16px;
        }
        .card-grid-1col {
            display: grid;
            grid-template-columns: 1fr;
            gap: 16px;
        }
        @media (min-width: 768px) {
            .card-grid-2col {
                grid-template-columns: repeat(2, 1fr);
            }
            .card-grid-3col {
                grid-template-columns: repeat(3, 1fr);
            }
        }
${backgroundClassRules()}
      </style>`;

    const containerClasses = isSlideLayout ? 'slide-deck max-w-6xl mx-auto px-6 py-12' : 'max-w-7xl mx-auto px-16 py-12';
    // The players this file contains, so its own policy does not refuse them.
    const frameSrc = buildExportFrameSrc(renderedSlidesHtml);
    let fullHtml = `
      <!DOCTYPE html>
      <html lang="en">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>${exportTitle}</title>
        <meta http-equiv="Content-Security-Policy" content="default-src 'self' data: blob:; script-src 'self' 'unsafe-inline' https://cdnjs.cloudflare.com; style-src 'self' 'unsafe-inline' https://cdnjs.cloudflare.com; img-src 'self' data: blob: https:; media-src 'self' data: blob: https:; frame-src ${frameSrc}; connect-src 'self' data: blob: https:;">
        ${fixedStyles}
      </head>
      <body class="bg-gray-50">
        ${headerHtml}
        ${navHtml}
        <div class="${containerClasses}">
            ${renderedSlidesHtml}
        </div>
        ${footerHtml}
        ${imageModalHtml}
        ${slideNavHtml}
        ${isSlideLayout ? slideJs : accordionJs}
        ${imageModalJs}
        <script>${TIMELINE_FIT_SCRIPT}</script>
        <script>${CANVAS_FIT_SCRIPT}</script>
      </body>
      </html>
    `;

    const __PRINT_WORKSHEET_SCRIPT__ = `<script>
function printWorksheet(id){
  try{
    var source=document.getElementById(id);
    if(!source){alert('Worksheet not found for printing.');return;}
    var formData={};
    var srcEls=Array.from(source.querySelectorAll('input, textarea, select'));
    srcEls.forEach(function(el,i){
      var key='el_'+i;
      if(el.type==='checkbox'||el.type==='radio'){formData[key]=el.checked;formData[key+'_value']=el.value;}else{formData[key]=el.value;}
      formData[key+'_type']=el.type;
    });
    var clone=source.cloneNode(true);
    var cloneEls=Array.from(clone.querySelectorAll('input, textarea, select'));
    cloneEls.forEach(function(el,i){
      var key='el_'+i;
      var val=formData[key];
      var type=formData[key+'_type'];
      if(type==='checkbox'||type==='radio'){
        var savedVal=formData[key+'_value'];
        var target=el;
        if(type==='radio'){
          var cb=document.createElement('input');
          cb.type='checkbox';
          cb.name=el.name;
          cb.className=el.className;
          cb.id=el.id;
          cb.style.cssText=el.style.cssText;
          el.parentNode.replaceChild(cb,el);
          target=cb;
        }
        if(val){target.setAttribute('checked','checked');target.checked=true;}else{target.removeAttribute('checked');target.checked=false;}
        if(savedVal!==undefined){target.setAttribute('value',savedVal);target.value=savedVal;}
      }else{
        var txt=document.createElement('div');
        txt.className='print-response';
        txt.textContent=val||'';
        el.replaceWith(txt);
      }
    });
    Array.from(clone.querySelectorAll('.no-print, .worksheet-print-button, button')).forEach(function(el){el.remove();});
    var titleEl=clone.querySelector('.worksheet-title,h1,h2,h3');
    var title=titleEl?titleEl.textContent.trim():'Worksheet';
    if(titleEl){titleEl.remove();}
    var headerHtml='<div class="worksheet-header"><h2>'+title+'</h2></div>';
    var styles='@page{size:8.5in 11in;margin:0.75in;}'+
      'body{font-family:"Times New Roman",serif;font-size:12pt;line-height:1.6;color:#000;background:white;}'+
      '.worksheet-header h2{text-align:center;font-size:16pt;font-weight:bold;margin-bottom:1em;border-bottom:2px solid #000;padding-bottom:0.5em;}'+
      '.student-info{margin:1em 0;padding:0.5em;border:1px solid #000;background:#f9f9f9;}'+
      '.student-info label{font-weight:bold;font-size:10pt;}'+
      'input[type="text"],input[type="date"],textarea{border:1px solid #000 !important;padding:0.25em !important;font-family:"Times New Roman",serif !important;font-size:11pt !important;background:white !important;color:#000 !important;}'+
      '.print-response{display:block;margin:0.25em 0;white-space:pre-wrap;}'+
      'input[type="checkbox"]{position:relative;width:14pt !important;height:14pt !important;border:2px solid #000 !important;background:white !important;-webkit-appearance:none !important;appearance:none !important;margin-right:0.5em !important;}'+
      'input[type="checkbox"]:checked::after{content:"\u2713" !important;position:absolute !important;font-size:12pt !important;font-weight:bold !important;color:#000 !important;left:1pt !important;top:-1pt !important;}'+
      '.worksheet-question{margin-bottom:1.5em;page-break-inside:avoid;border-left:2px solid #ddd;padding-left:1em;}'+
      '.worksheet-question p{font-weight:bold;margin-bottom:0.5em;}'+
      '.worksheet-question label{display:block;margin-bottom:0.4em;font-size:11pt;}'+
      '.no-print,button,.worksheet-print-button{display:none !important;}';
    var markup='<!DOCTYPE html><html><head><meta charset="UTF-8"><title>'+title+' - Print</title><style>'+styles+'</style></head><body>'+headerHtml+clone.outerHTML+'</body></html>';
    var w=window.open('', '_blank');
    if(w&&!(w.closed)){w.document.open();w.document.write(markup);w.document.close();w.onload=function(){w.focus();setTimeout(function(){w.print();},300);};return;}
    var iframe=document.createElement('iframe');
    iframe.style.position='fixed';iframe.style.right='-9999px';iframe.style.bottom='-9999px';
    document.body.appendChild(iframe);
    iframe.contentDocument.open();
    iframe.contentDocument.write(markup);
    iframe.contentDocument.close();
    iframe.onload=function(){iframe.contentWindow&&iframe.contentWindow.focus();iframe.contentWindow&&iframe.contentWindow.print();setTimeout(function(){iframe.remove();},500);};
  }catch(err){
    console.error('printWorksheet failed',err);
    alert('Unable to open print view. Please try again.');
  }
}
window.printWorksheet=printWorksheet;
</script>`;

    const __WORKSHEET_MCQ_SCRIPT__ = `<script>
function initWorksheetInteractions(){
  document.querySelectorAll('.mcq-multiple').forEach(function(group){
    var max=parseInt(group.dataset.maxSelect,10)||Infinity;
    var boxes=Array.from(group.querySelectorAll('input[type="checkbox"]'));
    function sync(){
      var checked=boxes.filter(function(b){return b.checked;}).length;
      boxes.forEach(function(b){if(!b.checked)b.disabled=checked>=max;});
    }
    boxes.forEach(function(b){b.addEventListener('change',sync);});
    sync();
  });
}
window.initWorksheetInteractions=initWorksheetInteractions;
document.addEventListener('DOMContentLoaded',initWorksheetInteractions);
setTimeout(initWorksheetInteractions,100);
</script>`;

    const __WORKSHEET_SAVE_LOAD_SCRIPT__ = `<script>
function saveWorksheet(id){
  try{
    var source=document.getElementById(id);
    if(!source)return;
    var elements=Array.from(source.querySelectorAll('input, textarea, select'));
    var responses=elements.map(function(el){
      if(el.type==='checkbox'||el.type==='radio'){
        return{type:el.type,name:el.name,value:el.value,checked:el.checked};
      }
      return{type:el.type,name:el.name,value:el.value};
    });
    var blob=new Blob([JSON.stringify({worksheetId:id,responses:responses})],{type:'application/json'});
    var url=URL.createObjectURL(blob);
    var a=document.createElement('a');
    a.href=url;
    a.download=id+'.json';
    a.click();
    URL.revokeObjectURL(url);
  }catch(err){console.error('saveWorksheet failed',err);alert('Unable to save worksheet.');}
}
function loadWorksheet(id){
  var input=document.createElement('input');
  input.type='file';
  input.accept='application/json';
  input.onchange=function(e){
    var file=e.target.files&&e.target.files[0];
    if(!file)return;
    var reader=new FileReader();
    reader.onload=function(ev){
      try{
        var data=JSON.parse(ev.target.result||'{}');
        var source=document.getElementById(id);
        if(!source)return;
        var elements=Array.from(source.querySelectorAll('input, textarea, select'));
        (data.responses||[]).forEach(function(saved,i){
          var el=elements[i];
          if(!el)return;
          if(saved.type==='checkbox'||saved.type==='radio'){
            el.checked=!!saved.checked;
            if(saved.value!==undefined)el.value=saved.value;
          }else{
            el.value=saved.value||'';
          }
        });
        if(typeof initWorksheetInteractions==='function'){initWorksheetInteractions();}
      }catch(err){console.error('loadWorksheet failed',err);alert('Unable to load worksheet data.');}
    };
    reader.readAsText(file);
  };
  input.click();
}
window.saveWorksheet=saveWorksheet;
window.loadWorksheet=loadWorksheet;
</script>`;

    let exportedHtml = fullHtml.replace(/<\/body>/i, __PRINT_WORKSHEET_SCRIPT__ + __WORKSHEET_MCQ_SCRIPT__ + __WORKSHEET_SAVE_LOAD_SCRIPT__ + '</body>');
    exportedHtml = exportedHtml.replace(/^\s*\.\s*$/gm, '');

    // What the file actually ended up containing. Every embedded picture is
    // named with the markup around it, so a surprise is visible here rather
    // than as an unexplained megabyte after the download.
    const assetAudit = auditExportedHtml(exportedHtml);
    console.info(formatAssetAudit(assetAudit));
    if (assetAudit.base64Images) {
      console.info(
        `[export] ${assetAudit.base64Images} picture(s) are embedded because they have no durable https address. `
        + 'Link them, or upload them to R2, and they will be referenced instead.'
      );
    }

    htmlProgress('Writing the file…');
    const blob = new Blob([exportedHtml], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${exportBaseName}_v${exportVersionLabel}${fileSuffix}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    setPrefilledVersionInput(true);
    setExportVersionInput(String(exportVersionLabel));

    setExportProgress(null);
    showSaveIndicator(successMessage);
  };


  const handleExportHTML = () => exportHtmlVariant();

  const handleExportOptimizedHTML = () => exportHtmlVariant({
    fileSuffix: '_Optimized',
    savingMessage: '📱 Preparing optimized HTML...',
    successMessage: '📱 Optimized HTML exported'
  });

  const handleExportSlimHTML = () => exportHtmlVariant({
    embedImages: false,
    fileSuffix: '_Slim',
    savingMessage: '🪶 Preparing slim HTML...',
    successMessage: '🪶 Slim HTML exported'
  });

  const handleExportPathHTML = () => exportHtmlVariant({
    embedImages: false,
    useImagePaths: true,
    fileSuffix: '_Path',
    savingMessage: '🖼️ Preparing path-based HTML...',
    successMessage: '🖼️ Path-based HTML exported'
  });

  const resolveExportBackground = async (presetOrObj, { cacheBust = false } = {}) => {
    const bg = resolveBackgroundPreset(presetOrObj);
    if (!bg) return null;
    if (bg.type === 'color') return { color: bg.value };
    const { fetchImageAsDataUrl } = await loadHtmlExporter();
    if (bg.type === 'gradient' || bg.type === 'pattern') {
      // PowerPoint slide backgrounds are flat; approximate with the base color
      const firstColor = bg.backgroundColor
        || (typeof bg.value === 'string' && (bg.value.match(/#[0-9a-fA-F]{3,8}/) || [])[0])
        || '#ffffff';
      return { color: firstColor };
    }
    if (bg.type === 'image' && bg.value) {
      try {
        return { image: cacheBust ? await inlineExportImage(bg.value) : await fetchImageAsDataUrl(bg.value), color: bg.backgroundColor || '#ffffff' };
      } catch (error) {
        console.warn('Could not inline background image for PPTX export', error);
        return { color: bg.backgroundColor || '#ffffff' };
      }
    }
    return null;
  };

  const cacheBustExportImageUrl = (src) => {
    if (typeof src !== 'string' || !/^https?:\/\//i.test(src)) return src;
    try {
      const url = new URL(src);
      url.searchParams.set('lpoExportCacheBust', String(Date.now()));
      return url.toString();
    } catch {
      return src;
    }
  };

  const inlineExportImage = async (src) => {
    if (!src || typeof src !== 'string' || src.startsWith('data:')) return src || '';
    const { fetchImageAsDataUrl } = await loadHtmlExporter();
    return fetchImageAsDataUrl(cacheBustExportImageUrl(src));
  };

  const resolveAndInlineTitleCardBackgroundImage = async (backgroundImage) => {
    const resolved = resolveTitleCardImageSource(backgroundImage);
    const imageSrc = resolved?.value || backgroundImage || '';
    if (!imageSrc || imageSrc.startsWith('data:')) return imageSrc;
    const inlined = await inlineExportImage(imageSrc);
    return inlined && inlined.startsWith('data:') ? inlined : imageSrc;
  };

  const inlineCardImagesForExport = async (settings) => {
    let result = await inlineTitleCardImage(settings);
    if (result?.backgroundImage) {
      try {
        result = {
          ...result,
          backgroundImage: await resolveAndInlineTitleCardBackgroundImage(result.backgroundImage)
        };
      } catch (error) {
        console.warn('Could not inline title card background image', error);
      }
    }
    return result;
  };

  // Build the slides + meta + style payload shared by the slide-deck exports
  // (PowerPoint and slide-per-page PDF). Both consume the same renderer-agnostic
  // deck model, so they must resolve slides, backgrounds, colors and the logo
  // identically — keeping this in one place stops the two exports from drifting.
  const buildSlideDeckExportPayload = async () => {
    const { embedImagesInSections } = await loadHtmlExporter();
    const embeddedSections = await embedImagesInSections(sections);
    // Reference-list blocks store no citations directly — they collect the
    // deck's citations at render time. Attach them so the reference slide
    // exports with content instead of being dropped as empty.
    const processedSections = embeddedSections.map((section) => ({
      ...section,
      blocks: (section.blocks || []).map((block) =>
        block?.type === 'apa-reference-list'
          ? { ...block, autoCitations: autoApaCitations }
          : block
      )
    }));
    const titleCardForExport = await inlineCardImagesForExport(titleCardSettings);
    const endTitleCardForExport = await inlineCardImagesForExport(endTitleCardSettings);
    const hasTitleContent = Boolean(
      titleCardForExport?.title?.trim()
      || titleCardForExport?.subtitle?.trim()
      || titleCardForExport?.image
    );
    const hasEndTitleContent = Boolean(
      endTitleCardForExport?.title?.trim()
      || endTitleCardForExport?.subtitle?.trim()
      || endTitleCardForExport?.image
    );

    const exportSlides = buildSlidesFromJson({
      sections: processedSections,
      titleCard: { ...(titleCardForExport || {}), enabled: hasTitleContent },
      endTitleCard: { ...(endTitleCardForExport || {}), enabled: hasEndTitleContent && endTitleCardSettings.enabled }
    }).filter((slide) => {
      if (slide.type === 'end-title-card') return endTitleCardSettings.enabled;
      return slideVisibility?.[slide.slideId] !== false;
    });

    // Title and section-title cards store selected backgrounds as preset ids
    // (for example, title-card-001) or URLs. Resolve those ids to the current
    // image URL and fetch with a cache-busting query before building the shared
    // PDF/PPTX model so exports do not reuse stale title-card artwork.
    for (const slide of exportSlides) {
      if (slide.type !== 'title-card' && slide.type !== 'section-title-card' && slide.type !== 'end-title-card') {
        continue;
      }
      if (slide.backgroundImage) {
        try {
          slide.backgroundImage = await resolveAndInlineTitleCardBackgroundImage(slide.backgroundImage);
        } catch (error) {
          console.warn('Could not inline exported title-card slide background image', error);
        }
      }
    }

    const defaultBackground = await resolveExportBackground(bodyBackground);
    const titleCardBackgroundResolved = await resolveExportBackground(titleCardBackground, { cacheBust: true });
    const backgrounds = {};
    for (const slide of exportSlides) {
      const perSlideBg = slide.slideId ? slideBackgrounds?.[slide.slideId] : null;
      if (perSlideBg) {
        backgrounds[slide.slideId] = await resolveExportBackground(perSlideBg);
      }
    }

    return {
      slides: exportSlides,
      meta: {
        courseTopic: safeString(headerData.courseTopic, ''),
        courseName: safeString(headerData.footerCourseInfo, ''),
        instructorName: safeString(headerData.instructorName, ''),
        instructorEmail: safeString(headerData.instructorEmail, ''),
        displayDate
      },
      style: {
        headerColors: headerColorMap,
        defaultHeaderColor: slideHeaderColor,
        backgrounds,
        defaultBackground,
        titleCardBackground: titleCardBackgroundResolved,
        designMode: slideDesignMode,
        logo: logo || null,
        logoSize: logoSize || 120
      }
    };
  };

  const buildSlideDeckExportPayloadRef = useRef(buildSlideDeckExportPayload);

  useEffect(() => {
    buildSlideDeckExportPayloadRef.current = buildSlideDeckExportPayload;
  });

  useEffect(() => {
    if (showSettingsPage) return undefined;
    return registerLiveSlideDeckExportPayloadResponder(() => {
      const builder = buildSlideDeckExportPayloadRef.current;
      return builder ? builder() : null;
    });
  }, [showSettingsPage]);

  const handleExportPDFSlides = async () => {
    const pdfBar = { title: 'Exporting PDF', icon: '📄' };
    setExportProgress({ ...pdfBar, label: 'Preparing the deck…', done: 0, total: 0 });
    try {
      const { exportLessonToPdf } = await loadPdfExporter();
      const { slides, meta, style } = await resolveExportPayloadAtClick({
        showSettingsPage,
        buildLocalPayload: buildSlideDeckExportPayload
      });
      const pageCount = await exportLessonToPdf({
        slides,
        meta,
        style,
        fileName: `${resolveExportFileName()}_Slides`,
        onProgress: ({ phase, done, total }) => {
          const label = {
            layouts: 'Rendering the hand-written layouts…',
            images: 'Collecting the pictures…',
            pages: total ? `Drawing page ${done} of ${total}` : 'Drawing the pages…',
            writing: 'Writing the file…'
          }[phase] || 'Working…';
          setExportProgress({ ...pdfBar, label, done: phase === 'pages' ? done : 0, total: phase === 'pages' ? total : 0 });
        }
      });
      setExportProgress(null);
      showSaveIndicator(`📄 PDF downloaded (${pageCount} slides)`);
    } catch (error) {
      console.error('PDF slide export failed:', error);
      setExportProgress(null);
      showSaveIndicator('❌ PDF export failed', 'error');
    }
  };

  const handleExportPPTX = async () => {
    const pptxBar = { title: 'Exporting PowerPoint', icon: '📊' };
    setExportProgress({ ...pptxBar, label: 'Preparing the deck…', done: 0, total: 0 });
    try {
      const { exportLessonToPptx } = await loadPptxExporter();
      const { slides, meta, style } = await resolveExportPayloadAtClick({
        showSettingsPage,
        buildLocalPayload: buildSlideDeckExportPayload
      });
      // What could not be carried across, so the toast can say so instead of
      // the deck quietly arriving with a picture missing.
      let report = null;
      const exportedCount = await exportLessonToPptx({
        slides,
        meta,
        style,
        fileName: `${resolveExportFileName()}_Slides`,
        onReport: (result) => { report = result; },
        onProgress: ({ phase, total }) => {
          const label = {
            layouts: 'Rendering the hand-written layouts…',
            images: 'Collecting the pictures…',
            vectors: 'Converting vector images…',
            building: total ? `Building ${total} slides…` : 'Building the slides…',
            writing: 'Writing the file…'
          }[phase] || 'Working…';
          setExportProgress({ ...pptxBar, label, done: 0, total: 0 });
        }
      });
      setExportProgress(null);
      const missing = (report?.droppedSvgs?.length || 0) + (report?.slideErrors?.length || 0);
      showSaveIndicator(
        missing
          ? `📊 PowerPoint exported (${exportedCount} slides, ${missing} item${missing === 1 ? '' : 's'} left out — see the browser console)`
          : `📊 PowerPoint exported (${exportedCount} slides)`
      );
    } catch (error) {
      // The exporter has already printed the slide/asset detail. The toast
      // gets the one sentence it wrote for a person, never the stack.
      console.error('PowerPoint export failed:', error);
      setExportProgress(null);
      const message = error?.name === 'PptxExportError'
        ? error.userMessage
        : 'PowerPoint export failed';
      showSaveIndicator(`❌ ${message}`, 'error');
    }
  };

  // The deck on screen decides what a file may contain. Everything below builds
  // the payload; `prepareDeckForSave` then drops what nothing on screen still
  // points at and writes each repeated picture once.
  const buildExportDataPayload = useCallback((overrides = {}) => {
    const sourceSections = overrides.sections || sectionsForPersistence;
    const sectionsObject = sourceSections.reduce((obj, section) => {
      obj[section.id] = {
        ...section,
        ...(section.sectionTitleCard ? { sectionTitleCard: normalizeTitleCardSettings(section.sectionTitleCard, DEFAULT_SECTION_TITLE_CARD_SETTINGS) } : {}),
        blocks: section.blocks.map(({ id, customCss, ...rest }) => ({
          ...rest,
          ...(customCss !== undefined ? { customCss } : {}),
        }))
      };
      return obj;
    }, {});

    return prepareDeckForSave({
      version: '4.0.0',
      timestamp: overrides.timestamp || new Date().toISOString(),
      ...(overrides.headerData || headerData),
      schoolLogo: overrides.schoolLogo ?? (logo || null),
      schoolLogoName: overrides.schoolLogoName ?? (logoName || ''),
      schoolLogoSize: overrides.schoolLogoSize ?? (logoSize || 0),
      week: overrides.week ?? week,
      date: overrides.date ?? date,
      sections: sectionsObject,
      bodyLayout: overrides.bodyLayout ?? bodyLayout,
      slideDesignMode: overrides.slideDesignMode ?? slideDesignMode,
      deckNameOnContentSlides: overrides.deckNameOnContentSlides ?? deckNameOnContentSlides,
      deckNameBadgeEnabled: overrides.deckNameBadgeEnabled ?? deckNameBadgeEnabled,
      slideNumbersEnabled: overrides.slideNumbersEnabled ?? slideNumbersEnabled,
      bodyBackground: normalizeBackgroundPersistenceValue(overrides.bodyBackground ?? bodyBackground) || 'white',
      slideBackgrounds: normalizeBackgroundMapForPersistence(overrides.slideBackgrounds ?? slideBackgrounds),
      titleCardBackground: normalizeBackgroundPersistenceValue(overrides.titleCardBackground ?? titleCardBackground, { allowEmpty: false }),
      slideHeaderMode: overrides.slideHeaderMode ?? slideHeaderMode,
      slideHeaderColor: overrides.slideHeaderColor ?? slideHeaderColor,
      slideHeaderColors: overrides.slideHeaderColors ?? slideHeaderColors,
      slideGlobalCss: overrides.slideGlobalCss ?? slideGlobalCss,
      titleCardSettings: normalizeTitleCardSettings(overrides.titleCardSettings ?? titleCardSettings),
      endTitleCardSettings: normalizeTitleCardSettings(overrides.endTitleCardSettings ?? endTitleCardSettings, DEFAULT_END_TITLE_CARD_SETTINGS),
      slideVisibility: overrides.slideVisibility ?? slideVisibility,
      acknowledgedBlankSlides: overrides.acknowledgedBlankSlides ?? acknowledgedBlankSlides,
      slideNames: overrides.slideNames ?? slideNames,
      slideSubtitles: overrides.slideSubtitles ?? slideSubtitles,
      slideDurations: overrides.slideDurations ?? slideDurations,
      slideDeckName: overrides.slideDeckName ?? slideDeckName,
      exportFileName: overrides.exportFileName ?? exportFileName,
      htmlExportTitle: overrides.htmlExportTitle ?? htmlExportTitle,
      exportVersion: overrides.exportVersion ?? exportVersionRef.current
    }).deck;
  }, [bodyLayout, slideDesignMode, deckNameOnContentSlides, slideNumbersEnabled, bodyBackground, slideBackgrounds, titleCardBackground, date, exportFileName, headerData, htmlExportTitle, logo, logoName, logoSize, sectionsForPersistence, slideDeckName, slideDurations, slideHeaderColor, slideHeaderColors, slideHeaderMode, slideGlobalCss, slideNames, slideSubtitles, slideVisibility, acknowledgedBlankSlides, titleCardSettings, endTitleCardSettings, week]);

  const buildExportData = useCallback(() => buildExportDataPayload(), [buildExportDataPayload]);

  // Anything that would change the saved file changes this callback's
  // identity — its dependency list is exactly the deck's persisted state — so
  // it doubles as the "edited since the last save" signal without serializing
  // a megabyte of deck on every keystroke to find out.
  useEffect(() => {
    setEditedSinceSave(true);
  }, [buildExportDataPayload]);

  const buildDefaultExportData = useCallback(() => {
    const defaultDate = new Date().toISOString().split('T')[0];
    return buildExportDataPayload({
      headerData: createDefaultHeaderData(),
      sections: normalizeSectionsDividerContent(createDefaultSections()),
      week: '1',
      date: defaultDate,
      bodyLayout: 'slides',
      bodyBackground: 'white',
      slideBackgrounds: {},
      titleCardBackground: null,
      slideHeaderMode: 'single',
      slideHeaderColor: '#7c3aed',
      slideHeaderColors: {},
      slideGlobalCss: '',
      titleCardSettings: normalizeTitleCardSettings(DEFAULT_TITLE_CARD_SETTINGS),
      endTitleCardSettings: normalizeTitleCardSettings(DEFAULT_END_TITLE_CARD_SETTINGS, DEFAULT_END_TITLE_CARD_SETTINGS),
      slideVisibility: {},
      slideNames: {},
      slideSubtitles: {},
      slideDeckName: 'Slide Deck',
      exportFileName: '',
      htmlExportTitle: '',
      exportVersion: 1,
      schoolLogo: null,
      schoolLogoName: '',
      schoolLogoSize: 0
    });
  }, [buildExportDataPayload, createDefaultHeaderData, createDefaultSections]);

  const getCurrentTemplateData = useCallback(
    () => prepareJsonForDownload(buildExportData()),
    [buildExportData]
  );

  const getDefaultTemplateData = useCallback(
    () => prepareJsonForDownload(buildDefaultExportData()),
    [buildDefaultExportData]
  );

  /**
   * Where the file actually went, in words.
   *
   * Built entirely from the outcome rather than from `saveLocation`'s label:
   * the label is React state and still holds the previous location while this
   * runs, which is exactly one version out of date.
   */
  const describeSaveDestination = (outcome) => {
    const folder = outcome.directoryHandle?.name;
    switch (outcome.method) {
      case SaveMethod.OVERWRITE:
        return 'Replaced the file this deck was saved to last time.';
      case SaveMethod.DIRECTORY:
        return folder
          ? `Written into your “${folder}” folder.`
          : 'Written into the folder you set.';
      case SaveMethod.PICKER:
        return 'Written to the place you just chose. Save will go straight there from now on.';
      default:
        return 'Downloaded to wherever your browser puts downloads.';
    }
  };

  /**
   * Write the deck out as full JSON.
   *
   * `versionUp` is the difference between the two save buttons:
   *   false — "Save": reuse the current version, so the file keeps its name
   *           and overwrites the file it was last saved to.
   *   true  — "Save +": bump the version first, producing a new numbered file
   *           alongside the old one, in the same folder.
   *
   * Where it lands is `useSaveLocation`'s business: the remembered file, the
   * folder set with "Set Dir", or — the first time, and on browsers without
   * the File System Access API — the save dialog or the Downloads folder.
   *
   * A plain Save that is about to replace an existing numbered version asks
   * first (see `handleSave`); the first save of a deck creates a file and does
   * not, because there is nothing to replace.
   */
  const runFullJsonSave = async (versionUp, { fileName: fileNameOverride = '' } = {}) => {
    const priorVersionState = captureVersionState();
    const exportBaseName = resolveExportFileName();
    const exportVersionLabel = resolveExportVersionLabel({ bump: versionUp });
    const prepared = prepareJsonForDownload(buildExportData());
    const pipelineResult = runDeckPipeline(prepared, 'export');
    if (!pipelineResult.ok) {
      const message = pipelineResult.errors?.[0]?.message || 'Export blocked: validation errors found.';
      showSaveIndicator(`⚠️ ${message}`, 'error');
      return null;
    }
    // Last-mile guard: re-sanitize immediately before serialization so divider
    // content cannot be coerced to strings in the final blob.
    const downloadReady = sanitizeForExport(pipelineResult.deck);

    const dataStr = JSON.stringify(downloadReady, null, 2);
    const fileName = fileNameOverride || `${exportBaseName}_v${exportVersionLabel}.json`;

    let outcome;
    try {
      outcome = await saveLocation.saveJson({ text: dataStr, fileName, newFile: versionUp });
    } catch (error) {
      // Never a toast on its own. A failed save is the one message that must
      // not scroll away after three seconds, because the file the user thinks
      // they have does not exist.
      console.error('[runFullJsonSave] Save failed', error);
      const problem = describeSaveProblem(error, {
        folderName: saveLocation.folderName,
        fileName
      });
      // The version must not move over a save that did not happen, or the next
      // Save would rewrite a file that was never written.
      restoreVersionState(priorVersionState);
      setSaveProblem({ info: problem, pending: { text: dataStr, fileName, newFile: versionUp } });
      showSaveIndicator(`⚠️ ${problem.title}`, 'error');
      return null;
    }

    // A dismissed save dialog leaves everything as it was — including the
    // version number, which must not creep up over a save that never happened.
    if (outcome.cancelled) {
      restoreVersionState(priorVersionState);
      showSaveIndicator('Save cancelled', '');
      return null;
    }

    // The file exists now, so the version in the box describes something real.
    // That is what lets the next Save know to warn before replacing it, and
    // what stops a second Save on a new deck from being treated as another
    // first save.
    deckHasSavedVersionRef.current = true;
    let savedVersion = exportVersionLabel;
    setPrefilledVersionInput(true);
    setExportVersionInput(String(exportVersionLabel));

    // A save dialog lets the user type any name they like. Adopt it, so the
    // deck's name and version match the file that now exists — otherwise the
    // next Save would compute the old name, find no match, and quietly write a
    // second file beside the one they just named.
    if (outcome.method === SaveMethod.PICKER && outcome.fileName && outcome.fileName !== fileName) {
      const chosen = parseVersionedFileName(outcome.fileName);
      if (chosen.baseName) setExportFileName(chosen.baseName);
      const chosenVersion = chosen.version || 1;
      savedVersion = chosenVersion;
      exportVersionRef.current = chosenVersion;
      setExportVersion(chosenVersion);
      setExportVersionInput(String(chosenVersion));
    }

    clearAutoSavedData();

    // The banner replaces the corner toast for this one action: it says the
    // same thing in a place the eye is already pointed, and it names the file
    // rather than asking anyone to take "Saved" on trust.
    const confirmation = {
      key: Date.now(),
      fileName: outcome.fileName,
      where: describeSaveDestination(outcome),
      version: savedVersion,
      at: new Date()
    };
    setLastFileSave(confirmation);
    setSaveConfirmation(confirmation);
    setEditedSinceSave(false);

    return savedVersion;
  };

  /**
   * The file a plain Save would write, without moving the version.
   *
   * Worked out the same way `runFullJsonSave` does, so the name in the
   * confirmation dialog is the name that will actually be replaced.
   */
  const resolvePlainSaveTarget = () => {
    const manualVersion = Number.parseInt(exportVersionInput.trim(), 10);
    const hasManualVersion = Number.isFinite(manualVersion) && manualVersion > 0;
    const currentVersion = hasManualVersion
      ? manualVersion
      : (deckHasSavedVersionRef.current ? exportVersionRef.current : null);
    const version = versionForSave({ currentVersion, versionUp: false });
    return { version, fileName: `${resolveExportFileName()}_v${version}.json` };
  };

  // Parameterless on purpose: this is passed straight to onClick/onSave props
  // in several places, and a click event arriving as an options argument would
  // be read as a version-up request.
  const handleSave = async () => {
    const target = resolvePlainSaveTarget();
    // Only a save that replaces something asks. A first save creates the file,
    // and a browser without the File System Access API gets its own "a file
    // with that name exists" dialog from the save picker or the download.
    if (await saveLocation.willOverwrite(target.fileName)) {
      setOverwritePrompt({ ...target, where: saveLocation.locationLabel });
      return;
    }
    await runFullJsonSave(false);
  };

  const handleConfirmOverwrite = async () => {
    setOverwritePrompt(null);
    await runFullJsonSave(false);
  };

  const handleCancelOverwrite = () => {
    setOverwritePrompt(null);
    showSaveIndicator('Save cancelled — nothing was written', '');
  };

  const handleVersionUpFullJson = () => { runFullJsonSave(true); };

  /**
   * Ctrl+S / Ctrl+Shift+S to save, Ctrl+Alt+<letter> to insert a block.
   *
   * Kept apart from the mode-switching shortcut effect above for two reasons.
   * Ctrl+S has to fire *while* the author is typing — that is the whole point
   * of it, and the other effect quite rightly refuses to act on a keystroke
   * aimed at an editor. And these handlers are rebuilt on nearly every render,
   * so they are read through a ref: re-binding a document listener that often
   * is wasteful, and swapping a listener between keydown and keyup is a good
   * way to drop a keystroke.
   */
  const shortcutActionsRef = useRef(null);
  // No dependency array: the point is to hold whatever this render produced.
  useEffect(() => {
    shortcutActionsRef.current = {
      isEditMode,
      isPresenting,
      save: handleSave,
      saveNewVersion: handleVersionUpFullJson,
      insertBlock: (action) => {
        const sectionId = slides[currentSlideIndex]?.section?.id || defaultSection || sections[0]?.id;
        if (!sectionId) return;
        // Same call the Insert drawer's buttons make, so a shortcut and a click
        // land the author in exactly the same place.
        handleAddContent(sectionId, action, { forceModal: true, source: 'toolbar' });
      },
      notify: showSaveIndicator
    };
  });

  useEffect(() => {
    // Two LessonTemplate instances are mounted at once — the Slides editor and
    // the Template Settings page, the latter kept in a `display: none` wrapper
    // — and both would otherwise answer this. That is not harmless: their edit
    // modes are independent, so the hidden one can insert a block, or open a
    // block dialog, on a copy of the deck nobody is looking at, while calling
    // preventDefault on the way past. From the editor it looks exactly like a
    // shortcut that does nothing.
    //
    // The deck editor owns these keys, the same way it already owns undo (see
    // the handler above, which stands down on the same condition and for the
    // same reason).
    if (showSettingsPage) return undefined;

    const onKeyDown = (event) => {
      const actions = shortcutActionsRef.current;
      if (!actions || actions.isPresenting) return;

      // Shift must be *up*. Ctrl+Shift+S is a popular extension shortcut —
      // Raindrop's "save bookmark" among them — and an extension's command is
      // registered with the browser, so it fires whatever a page does. Claiming
      // it here would not win the key, it would only add a second thing that
      // happens. Save + lives on Ctrl+Alt+S instead, which also puts it in the
      // same Ctrl+Alt row as the insert shortcuts.
      const isSaveKey = (event.ctrlKey || event.metaKey) && !event.shiftKey
        && (event.code === 'KeyS' || event.key?.toLowerCase?.() === 's');

      if (isSaveKey) {
        // Before any "are we typing?" guard, and always preventDefault: the
        // browser's own Ctrl+S offers to write the page to disk, which is never
        // what someone editing a deck meant by it.
        event.preventDefault();
        if (event.altKey) actions.saveNewVersion();
        else actions.save();
        return;
      }

      const shortcut = findBlockShortcut(event);
      if (!shortcut) return;

      // Inserting a block *is* refused mid-typing, unlike saving: Ctrl+Alt is
      // AltGr on many layouts, so this row is how accented characters are typed.
      // The same guard the slide shortcuts use, which also covers the code
      // editors.
      if (isTypingTarget(event.target)) return;
      // And refused behind an open dialog, which is a question about the
      // screen rather than about focus: the block editor does not claim focus
      // when it opens, so a dialog can be filling the screen while focus sits
      // on whatever was clicked before it. Same check the Slide Designer's
      // clipboard shortcuts and the deck's undo already make.
      if (isDialogOpen()) return;
      // Nothing to insert into outside edit mode — but say so rather than
      // doing nothing. A shortcut that silently declines is indistinguishable
      // from a shortcut the browser ate before the page saw it, and that
      // ambiguity is most of why this one has been reported broken more than
      // once. preventDefault first: the keystroke was meant for the deck
      // either way.
      if (!actions.isEditMode) {
        event.preventDefault();
        actions.notify?.(`Switch to Edit mode (E) to add a ${shortcut.label} block.`);
        return;
      }

      event.preventDefault();
      actions.insertBlock(shortcut.action);
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
    // The handler itself reads everything through the ref, so it never needs
    // rebuilding; only the question of *whether this instance listens at all*
    // depends on a prop.
  }, [showSettingsPage]);

  /**
   * "Set Dir": name the folder this deck belongs in before saving anything, so
   * the first Save does not have to ask.
   *
   * The folder is written to and read back before it is accepted. A folder
   * that fails that test is refused here, with the reason — the alternative is
   * accepting it and losing the first save into it.
   */
  const handleSetSaveDirectory = async () => {
    try {
      const result = await saveLocation.setDirectory();
      if (result.cancelled) return;
      if (!result.ok) {
        setSaveProblem({ info: result.failure, pending: null });
        showSaveIndicator(`⚠️ ${result.failure.title}`, 'error');
        return;
      }
      showSaveIndicator(`📁 Saving to ${result.handle.name}`);
    } catch (error) {
      console.error('[handleSetSaveDirectory] Could not set the save folder', error);
      showSaveIndicator('⚠️ Could not set the save folder', 'error');
    }
  };

  /**
   * Finish a save that failed, once the user has said where it should go
   * instead. The bytes are the ones the failed save built, so the version
   * number and the contents are the ones they pressed Save on.
   */
  const retryPendingSave = async (pending) => {
    if (!pending) {
      setSaveProblem(null);
      return;
    }
    try {
      const outcome = await saveLocation.saveJson({
        text: pending.text,
        fileName: pending.fileName,
        newFile: pending.newFile
      });
      if (outcome.cancelled) return;
      setSaveProblem(null);
      clearAutoSavedData();
      showSaveIndicator(`💾 Saved → ${outcome.fileName}`);
    } catch (error) {
      console.error('[retryPendingSave] Save failed again', error);
      const problem = describeSaveProblem(error, {
        folderName: saveLocation.folderName,
        fileName: pending.fileName
      });
      setSaveProblem({ info: problem, pending });
    }
  };

  /** "Choose another folder" on the failure dialog. */
  const handleSaveProblemChooseFolder = async () => {
    setSaveProblemBusy(true);
    try {
      const result = await saveLocation.setDirectory();
      if (result.cancelled) return;
      if (!result.ok) {
        setSaveProblem((current) => ({ info: result.failure, pending: current?.pending || null }));
        return;
      }
      // The remembered file still points into the folder that just refused the
      // write, and a plain Save would go straight back to it.
      await saveLocation.forgetFile();
      await retryPendingSave(saveProblem?.pending);
    } finally {
      setSaveProblemBusy(false);
    }
  };

  /** "Save as…" on the failure dialog. */
  const handleSaveProblemSaveAs = async () => {
    setSaveProblemBusy(true);
    try {
      const handle = await saveLocation.setFile(saveProblem?.pending?.fileName);
      if (!handle) return;
      await retryPendingSave(saveProblem?.pending);
    } catch (error) {
      console.error('[handleSaveProblemSaveAs] Could not choose a file', error);
    } finally {
      setSaveProblemBusy(false);
    }
  };

  /**
   * "Download instead" on the failure dialog — the escape hatch that always
   * works, because it is the browser's own download and touches no handle.
   */
  const handleSaveProblemDownload = () => {
    const pending = saveProblem?.pending;
    if (!pending) {
      setSaveProblem(null);
      return;
    }
    // The file-first argument order is this module's own downloadTextFile,
    // which is the one that attaches the anchor to the document — Firefox
    // ignores a click on a detached one.
    downloadTextFile(pending.fileName, pending.text, 'application/json');
    setSaveProblem(null);
    clearAutoSavedData();
    showSaveIndicator(`⬇️ Downloaded ${pending.fileName}`);
  };

  /** "Save as": pick the exact file Save overwrites from here on. */
  const handleSaveAs = async () => {
    const fileName = `${resolveExportFileName()}_v${resolveCurrentExportVersionLabel()}.json`;
    try {
      const handle = await saveLocation.setFile(fileName);
      if (!handle) return;
      // The picker may have been given a different name. Adopt it before
      // saving, so the write lands in the file the user actually chose rather
      // than beside it under the name this deck happened to compute.
      if (handle.name && handle.name !== fileName) {
        const chosen = parseVersionedFileName(handle.name);
        if (chosen.baseName) setExportFileName(chosen.baseName);
        const chosenVersion = chosen.version || 1;
        exportVersionRef.current = chosenVersion;
        deckHasSavedVersionRef.current = true;
        setExportVersion(chosenVersion);
        setExportVersionInput(String(chosenVersion));
        setPrefilledVersionInput(true);
      }
      await runFullJsonSave(false, { fileName: handle.name || fileName });
    } catch (error) {
      console.error('[handleSaveAs] Could not choose a save location', error);
      showSaveIndicator('⚠️ Could not choose a save location', 'error');
    }
  };

  /** Forget the remembered file and folder — the next Save asks again. */
  const handleClearSaveLocation = async () => {
    await saveLocation.forget();
    showSaveIndicator('📍 Save location cleared');
  };

  const handleNewSlideDeck = () => {
    const confirmed = window.confirm('Make sure your current work is saved or exported before resetting to the default slide deck. Continue?');
    if (!confirmed) return;

    const freshHeaderData = createDefaultHeaderData();
    const freshSections = createDefaultSections();

    stableLessonMetaRef.current = {
      id: generateId(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    setHeaderData(freshHeaderData);
    setWeek('1');
    setDate(new Date().toISOString().split('T')[0]);
    setBodyLayout('slides');
    setBodyBackground('white');
    setSlideHeaderMode('single');
    setSlideHeaderColor('#7c3aed');
    setSlideHeaderColors({});
    setSlideGlobalCss('');
    setTitleCardSettings(normalizeTitleCardSettings(DEFAULT_TITLE_CARD_SETTINGS));
    setEndTitleCardSettings(normalizeTitleCardSettings(DEFAULT_END_TITLE_CARD_SETTINGS, DEFAULT_END_TITLE_CARD_SETTINGS));
    setSlideVisibility({});
    setSlideNames({});
    setSlideDurations({});
    setSlideDeckName('Slide Deck');
    setExportFileName('');
    setHtmlExportTitle('');
    exportVersionRef.current = 1;
    deckHasSavedVersionRef.current = false;
    setExportVersion(1);
    setExportVersionInput('');
    setPrefilledVersionInput(false);
    // The remembered file belongs to the deck that was just replaced. Left in
    // place, the first Save of this one would write over it — a new deck's
    // v1 would never appear, and the previous deck would be gone.
    saveLocation.forgetFile();
    setSections(freshSections);
    setDefaultSection('bridge-in');
    setActiveSectionId('');
    setOpenSectionIds(['bridge-in']);
    setDrawerExpandedSections(['bridge-in']);
    setDrawerCollapsedSections([]);
    setCurrentSlideIndex(0);
    setSelectionMode('export');
    setSelectionType('blocks');
    setSelectedBlockIds({});
    setSelectedSlideIds({});
    setCollapsedExportSections({});
    setSelectionImportData(null);
    setSelectionTargetSection(freshSections[0]?.id || '');
    setSelectionPlacementMode('append');
    setSelectionPlacementSlideId('');
    setBlockTargetSlideId('');
    setImportSelectionSlideIds({});
    setRecentlyImportedSlideIds([]);
    setDraggingSlide(null);
    setSlideDropTarget(null);
    setHtmlModes({});
    setShowExportMenu(false);
    setShowHtmlExportMenu(false);
    setShowSelectionModal(false);
    setJsonInput('');
    setShowJsonInput(false);
    setIsNavDrawerOpen(false);
    setIsFullScreenPreview(false);
    removeLogo();

    clearAutoSavedData();
    showSaveIndicator('🆕 Default slide deck loaded');
  };

  const handleSaveSlimJson = () => {
    const exportBaseName = resolveExportFileName();
    const exportVersionLabel = resolveExportVersionLabel();
    const slimData = prepareJsonForDownload(buildExportData(), { stripFn: stripEmbeddedImages });
    const pipelineResult = runDeckPipeline(slimData, 'export-slim');
    if (!pipelineResult.ok) {
      const message = pipelineResult.errors?.[0]?.message || 'Export blocked: validation errors found.';
      showSaveIndicator(`⚠️ ${message}`, 'error');
      return;
    }
    // Last-mile guard for slim exports as well.
    const downloadReady = sanitizeForExport(pipelineResult.deck);

    const dataStr = JSON.stringify(downloadReady, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${exportBaseName}_v${exportVersionLabel}_Slim.json`;
    a.click();
    URL.revokeObjectURL(url);

    showSaveIndicator('🪶 Slim JSON exported');
  };

  const handleExportStableJson = () => {
    const now = new Date().toISOString();
    const exportTitle = resolveExportFileName() || headerData.courseTopic || slideDeckName || 'lesson';
    const { stableLesson, filenameSlug } = buildStableLessonJson({
      lessonId: stableLessonMetaRef.current.id,
      createdAt: stableLessonMetaRef.current.createdAt,
      updatedAt: now,
      title: headerData.courseTopic || slideDeckName || 'Lesson',
      description: htmlExportTitle || '',
      tags: [],
      context: {
        course: headerData.courseTopic || '',
        unit: week ? `Week ${week}` : '',
        grade_level: '',
        duration_minutes: null,
        delivery_mode: '',
        standards: [],
        materials: []
      },
      sections: sectionsForPersistence,
      rawSections: sectionsForPersistence,
      slideBackgrounds,
      titleCardBackground,
      slideVisibility,
      slideNames,
      slideSubtitles,
      slideDeckName,
      bodyLayout,
      titleCardSettings,
      endTitleCardSettings,
      slideHeaderMode,
      slideHeaderColor,
      slideHeaderColors,
      slideGlobalCss,
      exportVersion: exportVersionRef.current,
      extensions: {
        boppps_planner: {
          metadata: { stable_schema: STABLE_SCHEMA_VERSION }
        }
      }
    });

    stableLessonMetaRef.current.updatedAt = now;

    const sanitized = sanitizeForExport(stableLesson);
    const dataStr = JSON.stringify(sanitized, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const filename = stableLessonFilename(exportTitle || filenameSlug);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);

    showSaveIndicator('🧠 Stable JSON (LLM) exported');
  };

  const handleDownloadLlmTemplate = async () => {
    const { buildLlmTemplateExport } = await loadLlmTemplate();
    const data = buildLlmTemplateExport({
      exportVersion: exportVersionRef.current || exportVersion,
      sourceAppVersion: '4.0.3',
      presentationContext: {
        courseTopic: headerData.courseTopic,
        week,
        topic: headerData.courseTopic,
        instructorName: headerData.instructorName,
        institution: headerData.footerInstitution
      },
      blockLibrary: CONTENT_BLOCK_GUIDE,
      slimSchemaVersion: '4.0.0'
    });
    const dataStr = JSON.stringify(data, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'LLM_Generation_Template.json';
    a.click();
    URL.revokeObjectURL(url);

    showSaveIndicator('🧭 LLM generation template exported');
  };

  const runDeckPipeline = useCallback(runDeckPipelineFor, []);

  /**
   * Raise the failure panel for a deck that would not open.
   *
   * A cancelled picker is not a failure and is swallowed. Everything else gets
   * both the transient indicator (for anyone watching the toolbar) and the
   * panel (for anyone who needs to know why).
   */
  const showLoadFailure = useCallback((error, context = {}) => {
    if (error?.type === OpenPresentationErrorType.CANCELLED) {
      showSaveIndicator('', '');
      return;
    }
    const described = describeLoadError(error, context);
    setDeckLoadError(described);
    showSaveIndicator(`⚠️ ${described.title}`, 'error');
  }, [showSaveIndicator]);

  /**
   * Turn a loaded deck into editor state.
   *
   * Every refusal in here ends at `showLoadFailure` rather than at a toast,
   * because the alternative — returning quietly — leaves the previous deck on
   * screen with no explanation, or on first load leaves an empty one.
   *
   * @param {object} data - The parsed deck
   * @param {{ fileName?: string, source?: string }} [context] - Where it came from
   */
  const applyLoadedData = (data, context = {}) => {
    // Captured before anything touches the deck: the repair and normalization
    // passes below both fill a missing `exportVersion` in with 1, which would
    // make every deck look as though it knew its own version and hide the fact
    // that the file name is the only record of it.
    const declaredExportVersion = data && typeof data === 'object' ? data.exportVersion : undefined;
    let workingData = data;
    const looksStable = data && typeof data === 'object' && (data.format === STABLE_FORMAT || data.schema_version === STABLE_SCHEMA_VERSION);
    if (looksStable) {
      const { migrated, issues: migrationIssues } = migrateStableLessonJson(data);
      const isStable = migrated.format === STABLE_FORMAT;
      if (isStable || data.schema_version === STABLE_SCHEMA_VERSION) {
        if (migrationIssues.length) {
          console.warn('[applyLoadedData] Stable JSON migration issues', migrationIssues);
        }
        const validation = validateStableLessonJson(migrated);
        if (!validation.valid) {
          console.error('[applyLoadedData] Stable JSON validation failed', validation.errors);
          showLoadFailure(
            incompleteDeckError(validation.errors, {
              fileName: context.fileName,
              message: 'Stable lesson JSON failed validation'
            }),
            context
          );
          return;
        }
        stableLessonMetaRef.current = {
          id: safeString(migrated.id, stableLessonMetaRef.current.id),
          createdAt: safeString(migrated.created_at, stableLessonMetaRef.current.createdAt),
          updatedAt: safeString(migrated.updated_at, new Date().toISOString())
        };
        if (migrated.schema_version && migrated.schema_version !== STABLE_SCHEMA_VERSION) {
          console.warn('[applyLoadedData] Stable JSON schema is newer than supported:', migrated.schema_version);
        }
        workingData = stableLessonToInternal(migrated, { generateIdFn: generateId });
      }
    }

    if (workingData && typeof workingData === 'object' && (workingData.format === 'boppps_lesson' || workingData.schema_type === 'full_deck' || workingData.version)) {
      const pipelineResult = runDeckPipeline(workingData, 'import');
      if (!pipelineResult.ok) {
        const messages = (pipelineResult.errors || []).map((issue) => issue?.message || String(issue));
        showLoadFailure(
          incompleteDeckError(messages, {
            fileName: context.fileName,
            message: messages[0] || 'Import failed: validation errors found.'
          }),
          context
        );
        return;
      }
      workingData = pipelineResult.deck;
    }

    // Shared pictures come back out of the file's asset table, and whatever the
    // file accumulated before this existed — a deleted slide's background, a
    // gallery's leftovers — is dropped here rather than being carried into the
    // session and written out again on every later save. Both file shapes pass
    // through this point, and neither the migration nor the pipeline above
    // reads picture bytes.
    workingData = prepareLoadedDeck(workingData);

    const { success, normalized, issues } = normalizeLectureJson(workingData, { generateIdFn: generateId });
    if (!success || !normalized) {
      console.error('[applyLoadedData] Failed to normalize import', issues);
      showLoadFailure(
        incompleteDeckError(issues || [], {
          fileName: context.fileName,
          message: 'The deck could not be turned into a lesson'
        }),
        context
      );
      return;
    }

    if (issues.length) {
      console.warn('[applyLoadedData] Normalized lecture JSON with issues:', issues);
    }

    // The pipeline above validates structure, not markup. A deck is a file
    // people email each other, so clean its HTML fields here as well —
    // render-time sanitization remains the boundary that counts, this just
    // stops a hostile deck from sitting in state and autosave in the first
    // place.
    const safeNormalized = sanitizeDeckHtml(normalized);

    const {
      courseTopic, instructorName, instructorEmail,
      footerCourseInfo, footerInstitution, footerCopyright,
      showHeaderBar: loadedShowHeaderBar,
      week: loadedWeek, date: loadedDate,
      sections: loadedSections,
      bodyLayout: loadedBodyLayout,
      bodyBackground: loadedBodyBackground,
      slideBackgrounds: loadedSlideBackgrounds,
      titleCardBackground: loadedTitleCardBackground,
      slideHeaderMode: loadedSlideHeaderMode,
      slideHeaderColor: loadedSlideHeaderColor,
      slideHeaderColors: loadedSlideHeaderColors,
      slideGlobalCss: loadedSlideGlobalCss,
      titleCardSettings: loadedTitleCardSettings,
      endTitleCardSettings: loadedEndTitleCardSettings,
      slideVisibility: loadedSlideVisibility,
      slideNames: loadedSlideNames,
      slideSubtitles: loadedSlideSubtitles,
      slideDurations: loadedSlideDurations,
      slideDeckName: loadedSlideDeckName,
      exportFileName: loadedExportFileName,
      htmlExportTitle: loadedHtmlExportTitle,
      // `exportVersion` is deliberately not read from here — see
      // `declaredExportVersion` above, which is taken before normalization
      // invents one.
      schoolLogo,
      schoolLogoName,
      schoolLogoSize
    } = safeNormalized;

    setHeaderData({
      ...createDefaultHeaderData(),
      courseTopic: safeString(courseTopic),
      instructorName: safeString(instructorName),
      instructorEmail: safeString(instructorEmail),
      footerCourseInfo: safeString(footerCourseInfo),
      footerInstitution: safeString(footerInstitution),
      footerCopyright: safeString(footerCopyright),
      showHeaderBar: loadedShowHeaderBar !== false
    });
    if (loadedWeek) setWeek(safeString(loadedWeek, '1'));
    if (loadedDate) setDate(loadedDate);
    if (loadedBodyLayout) setBodyLayout(loadedBodyLayout);
    if (loadedBodyBackground) setBodyBackground(loadedBodyBackground);
    setSlideBackgrounds(normalizeBackgroundMapForPersistence(loadedSlideBackgrounds));
    setTitleCardBackground(normalizeBackgroundPersistenceValue(loadedTitleCardBackground, { allowEmpty: false }));
    if (loadedSlideHeaderMode) setSlideHeaderMode(loadedSlideHeaderMode);
    if (loadedSlideHeaderColor) setSlideHeaderColor(loadedSlideHeaderColor);
    if (loadedSlideHeaderColors) setSlideHeaderColors(loadedSlideHeaderColors);
    if (loadedSlideGlobalCss) setSlideGlobalCss(loadedSlideGlobalCss);
    if (loadedTitleCardSettings) {
      setTitleCardSettings(normalizeTitleCardSettings(loadedTitleCardSettings));
    }
    if (loadedEndTitleCardSettings) {
      setEndTitleCardSettings(normalizeTitleCardSettings(loadedEndTitleCardSettings, DEFAULT_END_TITLE_CARD_SETTINGS));
    }
    if (loadedSlideVisibility) {
      setSlideVisibility(loadedSlideVisibility);
    }
    if (loadedSlideNames) {
      setSlideNames(loadedSlideNames);
    }
    if (loadedSlideSubtitles) {
      setSlideSubtitles(loadedSlideSubtitles);
    }
    if (loadedSlideDurations) {
      setSlideDurations(loadedSlideDurations);
    }
    if (loadedSlideDeckName !== undefined) {
      setSlideDeckName(loadedSlideDeckName);
    }
    if (loadedExportFileName) {
      setExportFileName(loadedExportFileName);
    }
    if (loadedHtmlExportTitle) {
      setHtmlExportTitle(loadedHtmlExportTitle);
    }
    // Where this deck left off. The file's own `exportVersion` is preferred;
    // the file NAME is the fallback, so a deck whose JSON has lost the field —
    // hand-edited, produced by an assistant, migrated from an older build —
    // opens at v12 rather than silently resetting to v1 and having the next
    // "Save +" write a v2 over a file that already exists.
    const openedVersion = resolveOpenedDeckVersion({
      exportVersion: declaredExportVersion,
      fileName: context.fileName
    });
    if (openedVersion) {
      exportVersionRef.current = openedVersion;
      deckHasSavedVersionRef.current = true;
      setExportVersion(openedVersion);
      setExportVersionInput(String(openedVersion));
      setPrefilledVersionInput(true);
    } else {
      // A deck with no version anywhere has never been saved as far as this
      // app is concerned, so its first Save creates v1.
      exportVersionRef.current = 1;
      deckHasSavedVersionRef.current = false;
      setExportVersion(1);
      setExportVersionInput('');
      setPrefilledVersionInput(false);
    }
    // The remembered file is the *previous* deck's. Saving this one into it
    // would destroy that deck and never produce this one's numbered file.
    saveLocation.forgetFile();
    if ('schoolLogo' in safeNormalized) {
      if (schoolLogo) {
        saveLogo(schoolLogo, schoolLogoName || 'Saved Logo', schoolLogoSize || 0);
      } else {
        removeLogo();
      }
    }

    if (loadedSections) {
      const sectionsToApply = loadedSections.length ? loadedSections : createDefaultSections();
      setSections(sectionsToApply);

      // When opening a file that already has sections, expand them all so the
      // user can immediately see their existing content instead of a collapsed
      // outline. Applies to both the body accordion and the navigator drawer.
      const loadedSectionIds = sectionsToApply
        .filter((section) => section && section.id)
        .map((section) => section.id);
      setOpenSectionIds(loadedSectionIds);
      setDrawerExpandedSections(loadedSectionIds);
      setDrawerCollapsedSections([]);
    }

    clearAutoSavedData();
    showSaveIndicator('📂 Content loaded successfully');
  };

  const sanitizeJsonString = (input) => {
    const withoutEscapes = input
      // Remove markdown-style escapes that precede JSON syntax characters
      .replace(/\\([\[\]{}()_])/g, '$1')
      // Remove trailing commas before closing brackets/braces
      .replace(/,\s*(?=[}\]])/g, '');

    let result = '';
    let inString = false;

    for (let i = 0; i < withoutEscapes.length; i++) {
      const char = withoutEscapes[i];

      if (char === '"' && withoutEscapes[i - 1] !== '\\') {
        if (inString) {
          let j = i + 1;
          while (j < withoutEscapes.length && /\s/.test(withoutEscapes[j])) j++;
          if (j < withoutEscapes.length && /[,:}\]]/.test(withoutEscapes[j])) {
            inString = false;
            result += '"';
          } else {
            result += '\\"';
          }
        } else {
          inString = true;
          result += '"';
        }
      } else if (inString && char === '\n') {
        result += '\\n';
      } else {
        result += char;
      }
    }
    return result;
  };

  const handleLoad = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    showSaveIndicator('📂 Loading content...', 'saving');

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        // Parsed as-is: sanitizeJsonString corrupts valid JSON from exported
        // presentations, so it is not used here. HTML safety is handled where
        // it belongs — applyLoadedData cleans the deck's HTML fields, and every
        // render site sanitizes again on the way to the DOM.
        //
        // The shape check is shared with Open and with the paste box, so the
        // same file is accepted or refused for the same reason whichever route
        // it arrives by — and a file that cannot become a deck is refused here
        // instead of emptying the editor.
        const { data } = parsePresentationText(e.target.result);
        applyLoadedData(data, { fileName: file.name });
      } catch (error) {
        console.error('Error loading content:', error);
        showLoadFailure(error, { fileName: file.name, source: 'file' });
      }
    };
    reader.onerror = () => {
      console.error('Error reading file:', reader.error);
      showLoadFailure(
        Object.assign(new Error('Failed to read the selected file'), {
          type: OpenPresentationErrorType.FILE_READ_ERROR
        }),
        { fileName: file.name, source: 'file' }
      );
    };
    reader.readAsText(file);
    event.target.value = '';
  };

  /** What to put in front of someone whose paste did not load. */
  const pasteJsonMessage = (error) => {
    if (error.type === OpenPresentationErrorType.CANCELLED) {
      return 'Paste the deck JSON into the box first.';
    }
    if (error.type === OpenPresentationErrorType.INVALID_JSON) {
      return 'That is not valid JSON. Check that the whole reply was copied, including the closing brace.';
    }
    return error.message || 'That is not a deck this app can open.';
  };

  /**
   * Load a deck from pasted JSON — an assistant's reply, straight out of the
   * chat window, without saving it to a file first.
   *
   * It goes through `parsePresentationText`, the same parse and validation the
   * file route uses, so a deck that opens as a file opens as a paste and the
   * reasons a bad one is refused are the same either way.
   */
  const handleJsonPasteLoad = () => {
    setPasteJsonError('');
    let parsed;
    try {
      parsed = parsePresentationText(jsonInput);
    } catch (error) {
      // A second attempt through the repair heuristic, which unescapes
      // markdown and drops trailing commas. It is not used first because it
      // corrupts valid JSON from an exported file.
      if (error.type === OpenPresentationErrorType.INVALID_JSON) {
        try {
          parsed = parsePresentationText(sanitizeJsonString(jsonInput));
        } catch (retryError) {
          setPasteJsonError(pasteJsonMessage(retryError));
          return;
        }
      } else {
        setPasteJsonError(pasteJsonMessage(error));
        return;
      }
    }

    setShowJsonInput(false);
    setJsonInput('');
    setPasteJsonError('');

    // Same landing as Open: the callback remounts with the new deck, and
    // applyLoadedData is the fallback when this is rendered without one.
    if (onLoadPresentation) {
      onLoadPresentation(parsed.data);
    } else {
      applyLoadedData(parsed.data);
      showSaveIndicator('✓ Deck loaded from pasted JSON');
    }
  };

  /**
   * Handle opening a presentation JSON file via file picker.
   * Uses the onLoadPresentation callback to trigger a proper component remount
   * with the new data, matching the behavior of the launch page's Load button.
   */
  const handleOpenPresentation = async () => {
    let openedFileName = '';
    try {
      setDeckLoadError(null);
      showSaveIndicator('📂 Opening file...', 'saving');
      const { data, fileName } = await openPresentationFile();
      openedFileName = fileName;

      // Use the callback to trigger proper reload via MainApp
      // This ensures the component remounts with new initialData,
      // matching how the launch page's "Load Presentation" button works
      if (onLoadPresentation) {
        // The editor remounts with the deck object alone, so the file's name —
        // and the version in it, for a deck whose JSON has none — is left
        // where the remount can pick it up.
        rememberOpenedFileName(fileName, data);
        onLoadPresentation(data);
        // Note: showSaveIndicator won't work after this since component remounts
        // The user will see the presentation loaded as visual feedback
      } else {
        // Fallback to applyLoadedData if callback not available
        applyLoadedData(data, { fileName, source: 'file' });
        showSaveIndicator(`✓ Loaded: ${fileName}`);
      }
    } catch (error) {
      // Cancellation is handled inside showLoadFailure; everything else raises
      // the failure panel, which explains the problem instead of flashing a
      // three-second toast about it.
      console.error('[handleOpenPresentation] Error:', error);
      showLoadFailure(error, { fileName: openedFileName, source: 'file' });
    }
  };

  const handleDownloadTemplate = () => {
    try {
      downloadTemplate();
      showSaveIndicator('📥 Template downloaded successfully');
      setShowSettingsMenu(false);
    } catch (error) {
      console.error('Error downloading template:', error);
      showSaveIndicator('⚠️ Failed to download template', 'error');
    }
  };

  const handleApplyTemplate = () => {
    const confirmed = window.confirm(
      'Are you sure you want to apply the template? This will replace all current content with the template content. This action cannot be undone.'
    );

    if (confirmed) {
      try {
        showSaveIndicator('📄 Applying template...', 'saving');
        const templateData = createSlideTemplate();
        applyLoadedData(templateData);
        setShowSettingsMenu(false);
      } catch (error) {
        console.error('Error applying template:', error);
        showSaveIndicator('⚠️ Failed to apply template', 'error');
      }
    }
  };

  const resetSelectionState = useCallback(() => {
    setSelectionMode('export');
    setSelectionType('blocks');
    setSelectedBlockIds({});
    setSelectedSlideIds({});
    setSelectionImportData(null);
    setSelectionTargetSection((current) => current || sections[0]?.id || '');
    setSelectionPlacementMode('append');
    setSelectionPlacementSlideId('');
    setBlockTargetSlideId('');
    setImportSelectionSlideIds({});
  }, [sections]);

  const toggleBlockSelection = (blockId) => {
    setSelectedBlockIds((prev) => ({
      ...prev,
      [blockId]: !prev[blockId]
    }));
  };

  const toggleSlideSelection = (slideId) => {
    setSelectedSlideIds((prev) => ({
      ...prev,
      [slideId]: !prev[slideId]
    }));
  };

  const toggleExportSectionCollapse = useCallback((sectionId) => {
    if (!sectionId) return;
    setCollapsedExportSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  }, []);

  const toggleImportSlideSelection = (slideId) => {
    setImportSelectionSlideIds((prev) => ({
      ...prev,
      [slideId]: !prev[slideId]
    }));
  };

  // "Select all" in the transfer window works on whole groups, so each of the
  // three selections takes a list and one answer rather than a click apiece.
  const setBlockSelection = useCallback((ids, value) => {
    setSelectedBlockIds((prev) => {
      const next = { ...prev };
      ids.forEach((id) => { next[id] = value; });
      return next;
    });
  }, []);

  const setSlideSelection = useCallback((ids, value) => {
    setSelectedSlideIds((prev) => {
      const next = { ...prev };
      ids.forEach((id) => { next[id] = value; });
      return next;
    });
  }, []);

  const setImportSlideSelection = useCallback((ids, value) => {
    setImportSelectionSlideIds((prev) => {
      const next = { ...prev };
      ids.forEach((id) => { next[id] = value; });
      return next;
    });
  }, []);

  const resolveBlockExportBaseName = useCallback((blocks = []) => (
    buildBlockSelectionName((blocks || []).map((entry = {}) => ({
      deckNumber: deckSlideNumbers.get(entry.slideId),
      slideTitle: entry.slideTitle || (entry.slideId ? slideNames[entry.slideId] : '') || '',
      sectionTitle: entry.sectionTitle || '',
      blockIndex: entry.blockIndex,
      // The block's type is what tells one card from another in a file
      // listing; its preview text is a sentence and makes a poor filename.
      blockType: entry.block?.type,
      blockLabel: entry.blockLabel
    })))
  ), [deckSlideNumbers, slideNames]);

  const resolveSlideExportBaseName = useCallback((slidesToExport = []) => (
    buildSlideSelectionName((slidesToExport || []).map((slide) => ({
      deckNumber: deckSlideNumbers.get(slide.slideId),
      title: slide.title || slideNames[slide.slideId] || '',
      sectionTitle: slide.sectionTitle || slide.section?.title || ''
    })))
  ), [deckSlideNumbers, slideNames]);

  const downloadSelectionPayload = (payload, suffix, customBaseName = '') => {
    const exportBaseName = customBaseName || resolveExportFileName();
    const exportVersionLabel = resolveExportVersionLabel();
    const dataStr = JSON.stringify(sanitizeForExport(payload), null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const anchor = document.createElement('a');
    anchor.href = url;
    // The "Slides"/"Blocks" suffix dates from when every selection was named
    // after the deck and nothing else told two files apart. A name built from
    // the selection itself already opens with Slide or Slides, so it is not
    // said twice; a file falling back to the deck name still needs it.
    anchor.download = customBaseName
      ? `${customBaseName}_v${exportVersionLabel}.json`
      : `${exportBaseName}_v${exportVersionLabel}_${suffix}.json`;

    // Ensure the virtual link is interactable across browsers before clicking.
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  };

  const handleExportSelection = () => {
    const commonMeta = {
      selectionType,
      exportedAt: new Date().toISOString(),
      format: STABLE_FORMAT,
      schema_version: STABLE_SCHEMA_VERSION,
      producer: {
        app_name: 'LessonPlanner One',
        app_version: APP_VERSION,
        exported_at: new Date().toISOString()
      },
      source: {
        courseTopic: headerData.courseTopic,
        week,
        date
      }
    };

    if (selectionType === 'blocks') {
      const selectionSections = sections
        .map((section) => ({
          ...section,
          blocks: (section.blocks || []).filter((block) => selectedBlockIds[block.id])
        }))
        .filter((section) => (section.blocks || []).length > 0);

      const blocks = selectionSections.flatMap((section) =>
        (section.blocks || []).map((block) => {
          const matchingSlide = (sectionSlideBlockMap[section.id] || []).find((slide) =>
            (slide.blocks || []).some((slideBlock) => slideBlock?.id === block.id)
          );
          const context = blockContextMap[block.id] || {};
          const slideLabel = context.slideTitle
            || matchingSlide?.label
            || (context.slideId ? slideNames[context.slideId] : null)
            || (matchingSlide?.slideId ? slideNames[matchingSlide.slideId] : '')
            || '';
          const resolvedSlideId = context.slideId || matchingSlide?.slideId || block?.slideId || block?.content?.slideId || null;
          const resolvedSectionTitle = context.sectionTitle || section.title;
          const matchedIndex = (matchingSlide?.blocks || []).findIndex((candidate) => candidate?.id === block.id);
          const resolvedBlockIndex = context.blockIndex || (matchedIndex >= 0 ? matchedIndex + 1 : undefined);

          return {
            sectionId: section.id,
            sectionTitle: resolvedSectionTitle,
            slideId: resolvedSlideId,
            slideTitle: slideLabel,
            blockIndex: resolvedBlockIndex,
            blockLabel: context.blockLabel || getBlockPreview(block),
            block
          };
        })
      );

      if (blocks.length === 0) {
        showSaveIndicator('⚠️ Select at least one block to export', 'error');
        return;
      }

        const payload = prepareJsonForDownload({
          ...commonMeta,
          type: 'blocks',
          sections: selectionSections,
          blocks
        });

        const customBaseName = resolveBlockExportBaseName(payload.blocks);

        downloadSelectionPayload(payload, 'Blocks', customBaseName);
        showSaveIndicator('📤 Selected blocks exported');
        setShowSelectionModal(false);
        resetSelectionState();
        return;
      }

    const selectedSlidesList = slides.filter((slide) => selectedSlideIds[slide.slideId]);
    if (selectedSlidesList.length === 0) {
      showSaveIndicator('⚠️ Select at least one slide to export', 'error');
      return;
    }

    const slidePayload = selectedSlidesList.map((slide) => ({
      slideId: slide.slideId,
      title: slide.title || slideNames[slide.slideId] || slide.section?.title || '',
      sectionId: slide.section?.id || null,
      sectionTitle: slide.section?.title || '',
      blocks: slide.blocks || []
    }));

    const slidesBySection = selectedSlidesList.reduce((acc, slide) => {
      const sectionId = slide.section?.id || 'imported';
      if (!acc[sectionId]) {
        acc[sectionId] = {
          ...slide.section,
          id: sectionId,
          title: slide.section?.title || 'Imported Content',
          type: slide.section?.type || 'content',
          blocks: []
        };
      }
      acc[sectionId].blocks = [...acc[sectionId].blocks, ...(slide.blocks || [])];
      return acc;
    }, {});

    const payload = prepareJsonForDownload({
      ...commonMeta,
      type: 'slides',
      slides: slidePayload,
      sections: Object.values(slidesBySection)
    });

    const customBaseName = resolveSlideExportBaseName(slidePayload);

    downloadSelectionPayload(payload, 'Slides', customBaseName);
    showSaveIndicator('📤 Selected slides exported');
    setShowSelectionModal(false);
    resetSelectionState();
  };

  const coerceSelectionImportData = (data) => {
    if (!data) return null;
    if (data.type === 'blocks' || data.type === 'slides') return data;

    const selectionSections = data?.selection?.sections || data?.content?.sections || data?.sections;
    if (Array.isArray(selectionSections)) {
      const { normalized } = normalizeLectureJson({ ...data, sections: selectionSections });
      const normalizedSections = normalized?.sections || [];
      const reconstructedSlides = buildSlidesFromJson({
        sections: normalizedSections,
        titleCard: normalized?.titleCardSettings,
        endTitleCard: normalized?.endTitleCardSettings,
        includeEmptySections: true
      });

      const slidePayload = reconstructedSlides
        .filter((slide) => slide.type === 'section')
        .map((slide) => ({
          slideId: slide.slideId,
          title: slide.title || slideNames[slide.slideId] || slide.section?.title || '',
          sectionId: slide.section?.id || null,
          sectionTitle: slide.section?.title || '',
          blocks: slide.blocks || []
        }));

      return {
        type: 'slides',
        slides: slidePayload,
        sections: normalizedSections
      };
    }

    return null;
  };

  const handleSelectionFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const text = await file.text();
      const data = JSON.parse(text);
      const selectionData = coerceSelectionImportData(data);
      if (!selectionData) {
        throw new Error('Invalid selection file.');
      }
      setSelectionImportData(selectionData);
      if (selectionData.type === 'slides') {
        const preset = {};
        (selectionData.slides || []).forEach((slide) => { if (slide.slideId) preset[slide.slideId] = true; });
        setImportSelectionSlideIds(preset);
      }
      setSelectionMode('import');
      setShowSelectionModal(true);
      showSaveIndicator('📥 Selection ready to import');
    } catch (error) {
      console.error('Selection import failed', error);
      showSaveIndicator(`⚠️ ${error.message}`, 'error');
    } finally {
      event.target.value = '';
    }
  };

  const cloneBlockForImport = (block = {}) => {
    const { id: _oldId, ...rest } = block;
    const cloned = JSON.parse(JSON.stringify(rest || {}));
    delete cloned.slideId;
    if (cloned.content && typeof cloned.content === 'object') {
      delete cloned.content.slideId;
    }
    return { id: generateId(), ...cloned };
  };

  const applyImportedSlideId = (block = {}, slideId) => {
    const updated = { ...block, slideId };
    if (updated.content && typeof updated.content === 'object') {
      updated.content = { ...updated.content, slideId };
    }
    return updated;
  };

  const buildImportedBlocks = () => {
    if (!selectionImportData) return [];

    if (selectionImportData.type === 'blocks') {
      return (selectionImportData.blocks || [])
        .map((entry) => {
          const raw = entry?.block || entry;
          if (!raw || typeof raw !== 'object') return null;
          return {
            sectionId: entry?.sectionId || raw.sectionId || raw.section_id || null,
            slideId: entry?.slideId || raw.slideId || raw.content?.slideId || null,
            block: cloneBlockForImport(raw)
          };
        })
        .filter(Boolean);
    }

    return (selectionImportData.slides || []).map((slide) => {
      const contentBlocks = (slide.blocks || []).map((block) => cloneBlockForImport(block)).filter(Boolean);
      const newSlideId = generateId();
      return contentBlocks.map((block) => applyImportedSlideId(block, newSlideId));
    });
  };

  const applySelectionImport = () => {
    if (!selectionImportData) {
      showSaveIndicator('⚠️ Load a selection file first', 'error');
      return;
    }

    let slidesToInsert = [];
    if (selectionImportData.type === 'blocks') {
      const blocksToInsert = buildImportedBlocks();
      if (blocksToInsert.length === 0) {
        showSaveIndicator('⚠️ No blocks found to import', 'error');
        return;
      }
      slidesToInsert = blocksToInsert;
    } else {
      slidesToInsert = buildImportedBlocks().filter((_, index) => {
        const slideMetaId = selectionImportData.slides?.[index]?.slideId;
        return importSelectionSlideIds[slideMetaId];
      });
    }

    if (!slidesToInsert.length) {
      const emptyMessage = selectionImportData.type === 'blocks'
        ? '⚠️ Select at least one block to import'
        : '⚠️ Select at least one slide to import';
      showSaveIndicator(emptyMessage, 'error');
      return;
    }

    let nextHighlightIds = [];
    setSections((prevSections) => {
      const importedBlockSectionId = selectionImportData.type === 'blocks'
        ? slidesToInsert.find((entry) => entry.sectionId)?.sectionId
        : null;
      let targetId = selectionTargetSection || importedBlockSectionId || defaultSection || prevSections[0]?.id || generateId();
      let workingSections = [...prevSections];

      if (!workingSections.some((section) => section.id === targetId)) {
        workingSections = [
          ...workingSections,
          { id: targetId, title: 'Imported Content', type: 'content', blocks: [] }
        ];
      }

      return workingSections.map((section) => {
        if (section.id !== targetId) return section;
        const sectionSlides = splitSectionSlides(section, { includeEmpty: true, includeMeta: true });

        if (selectionImportData.type === 'blocks') {
          const workingSlides = sectionSlides.length ? [...sectionSlides] : [{ slideId: buildSectionSlideId(section.id, 0), blocks: [] }];

          const ensureSlideIndex = (desiredId) => {
            if (!desiredId) return -1;
            const explicitIndex = workingSlides.findIndex((slide) => slide.slideId === desiredId);
            if (explicitIndex !== -1) return explicitIndex;
            return workingSlides.findIndex((slide, idx) => buildSectionSlideId(section.id, idx) === desiredId);
          };

          const groupedBySlide = (() => {
            if (blockTargetSlideId && blockTargetSlideId !== '__new__') {
              return [{ slideId: blockTargetSlideId, blocks: slidesToInsert.map((entry) => entry.block) }];
            }

            const map = {};
            slidesToInsert.forEach((entry) => {
              const targetSlideId = blockTargetSlideId === '__new__' ? null : entry.slideId;
              const key = targetSlideId || '__new__';
              if (!map[key]) {
                map[key] = { slideId: targetSlideId, blocks: [] };
              }
              map[key].blocks.push(entry.block);
            });
            return Object.values(map);
          })();

          groupedBySlide.forEach(({ slideId, blocks }) => {
            const targetIndex = (() => {
              if (blockTargetSlideId === '__new__') return workingSlides.length;
              const matchedIndex = ensureSlideIndex(slideId);
              if (matchedIndex !== -1) return matchedIndex;
              return workingSlides.length;
            })();

            const resolvedSlideId = slideId || buildSectionSlideId(section.id, targetIndex);
            if (!workingSlides[targetIndex]) {
              workingSlides[targetIndex] = { slideId: resolvedSlideId, blocks: [] };
            }
            const existingBlocks = workingSlides[targetIndex].blocks || [];
            workingSlides[targetIndex] = {
              ...workingSlides[targetIndex],
              slideId: resolvedSlideId,
              blocks: [...existingBlocks, ...blocks]
            };
            nextHighlightIds.push(resolvedSlideId);
          });

          return { ...section, blocks: flattenSlidesToBlocks(workingSlides, section.id) };
        }

        const placementAnchorIndex = (() => {
          if (selectionPlacementMode === 'append' || !selectionPlacementSlideId) {
            return sectionSlides.length;
          }
          const matchIndex = sectionSlides.findIndex((_, idx) =>
            buildSectionSlideId(section.id, idx) === selectionPlacementSlideId
          );
          if (matchIndex === -1) return sectionSlides.length;
          return selectionPlacementMode === 'before' ? matchIndex : matchIndex + 1;
        })();

        const rebuiltSlides = sectionSlides.length ? [...sectionSlides] : [[]];
        rebuiltSlides.splice(placementAnchorIndex, 0, ...slidesToInsert);
        nextHighlightIds = slidesToInsert.map((_, idx) =>
          buildSectionSlideId(section.id, placementAnchorIndex + idx)
        );
        return { ...section, blocks: flattenSlidesToBlocks(rebuiltSlides, section.id) };
      });
    });

    if (nextHighlightIds.length) {
      setRecentlyImportedSlideIds(nextHighlightIds);
    }

    showSaveIndicator('✅ Selection imported');
    setShowSelectionModal(false);
    resetSelectionState();
  };

  // WORKSHEET JSON IMPORT
  const handleWorksheetJsonImport = (blockId) => async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const data = JSON.parse(text);

      if (!Array.isArray(data.questions)) {
        alert('Invalid JSON: "questions" must be an array');
        return;
      }

      const mapped = data.questions.map((q, idx) => {
        const isNewFormat =
          q &&
          typeof q === 'object' &&
          (q.title !== undefined ||
            ['mcq', 'true-false', 'short-answer', 'long-answer', 'fill-blank', 'instructions'].includes(q.type));
        if (isNewFormat) {
          return { id: q.id || `q_${Date.now()}_${idx}`, ...normalizeQuestion(q) };
        }
        const pts = Number.isFinite(q.points) ? q.points : 0;
        switch (q.type) {
          case 'longAnswer':
            return { id: q.id || `q_${Date.now()}_${idx}`, type: 'long-answer', title: q.prompt || '', points: pts };
          case 'multipleChoice':
            return {
              id: q.id || `q_${Date.now()}_${idx}`,
              type: 'mcq',
              title: q.prompt || '',
              options: Array.isArray(q.options) ? q.options : [],
              mode: 'single',
              allowSelectAll: true,
              correctIndex: typeof q.correctIndex === 'number' ? q.correctIndex : -1,
              correctIndices: [],
              points: pts,
            };
          case 'checkboxes':
            return {
              id: q.id || `q_${Date.now()}_${idx}`,
              type: 'mcq',
              title: q.prompt || '',
              options: Array.isArray(q.options) ? q.options : [],
              mode: 'multiple',
              allowSelectAll: true,
              correctIndex: -1,
              correctIndices: Array.isArray(q.correctIndices) ? q.correctIndices : [],
              points: pts,
            };
          case 'shortAnswer':
          default:
            return { id: q.id || `q_${Date.now()}_${idx}`, type: 'short-answer', title: q.prompt || '', points: pts };
        }
      });
      const usePoints = data.usePoints === false ? false : mapped.some(q => Number(q.points) > 0);

      const update = {
        ...(data.title ? { title: data.title } : {}),
        ...(data.instructions ? { description: data.instructions } : {}),
        ...(data.footerNote ? { footerNote: data.footerNote } : {}),
        questions: mapped,
        usePoints,
        totalPoints: usePoints ? mapped.reduce((sum, q) => sum + (q.points || 0), 0) : undefined,
      };

      setSections(prev => prev.map(section => ({
        ...section,
        blocks: section.blocks.map(b => {
          if (b.id !== blockId) return b;
          return { ...b, ...update };
        })
      })));

      alert('Worksheet imported successfully.');
    } catch (err) {
      console.error(err);
      alert('Failed to parse JSON. Please check the schema.');
    } finally {
      e.target.value = '';
    }
  };
  const handleWorksheetJsonPaste = (blockId) => () => {
    setJsonPasteModal({ open: true, blockId, text: '' });
  };

  const applyWorksheetJsonPaste = () => {
    const { text, blockId } = jsonPasteModal;
    if (!text) return;
    try {
      const data = JSON.parse(text);
      if (!Array.isArray(data.questions)) {
        alert('Invalid JSON: "questions" must be an array');
        return;
      }
      const mapped = data.questions.map((q, idx) => {
        const isNewFormat =
          q &&
          typeof q === 'object' &&
          (q.title !== undefined ||
            ['mcq', 'true-false', 'short-answer', 'long-answer', 'fill-blank', 'instructions'].includes(q.type));
        if (isNewFormat) {
          return { id: q.id || `q_${Date.now()}_${idx}`, ...normalizeQuestion(q) };
        }
        const pts = Number.isFinite(q.points) ? q.points : 0;
        switch (q.type) {
          case 'longAnswer':
            return { id: q.id || `q_${Date.now()}_${idx}`, type: 'long-answer', title: q.prompt || '', points: pts };
          case 'multipleChoice':
            return {
              id: q.id || `q_${Date.now()}_${idx}`,
              type: 'mcq',
              title: q.prompt || '',
              options: Array.isArray(q.options) ? q.options : [],
              mode: 'single',
              allowSelectAll: true,
              correctIndex: typeof q.correctIndex === 'number' ? q.correctIndex : -1,
              correctIndices: [],
              points: pts,
            };
          case 'checkboxes':
            return {
              id: q.id || `q_${Date.now()}_${idx}`,
              type: 'mcq',
              title: q.prompt || '',
              options: Array.isArray(q.options) ? q.options : [],
              mode: 'multiple',
              allowSelectAll: true,
              correctIndex: -1,
              correctIndices: Array.isArray(q.correctIndices) ? q.correctIndices : [],
              points: pts,
            };
          case 'shortAnswer':
          default:
            return { id: q.id || `q_${Date.now()}_${idx}`, type: 'short-answer', title: q.prompt || '', points: pts };
        }
      });
      const usePoints = data.usePoints === false ? false : mapped.some(q => Number(q.points) > 0);
      const update = {
        ...(data.title ? { title: data.title } : {}),
        ...(data.instructions ? { description: data.instructions } : {}),
        ...(data.footerNote ? { footerNote: data.footerNote } : {}),
        questions: mapped,
        usePoints,
        totalPoints: usePoints ? mapped.reduce((sum, q) => sum + (q.points || 0), 0) : undefined,
      };
      setSections(prev => prev.map(section => ({
        ...section,
        blocks: section.blocks.map(b => {
          if (b.id !== blockId) return b;
          return { ...b, ...update };
        })
      })));
      alert('Worksheet imported successfully.');
      setJsonPasteModal({ open: false, blockId: null, text: '' });
    } catch (err) {
      console.error(err);
      alert('Failed to parse JSON. Please check the schema.');
    }
  };

  const closeWorksheetJsonPaste = () => setJsonPasteModal({ open: false, blockId: null, text: '' });

  const handleModalSave = (blockData) => {
    const { sectionId, isEditing, insertAfterBlockId, insertAtIndex, targetSlideId, targetBlockId, targetCardPath, ...content } = blockData;


    // Citations for pictures a canvas paste already placed. The blocks exist,
    // so this patches them in place by id rather than inserting anything —
    // and every field it writes is optional, so saving with the form untouched
    // is a no-op the author can safely do.
    if (content.type === 'pasted-images') {
      const updates = content.pastedImageUpdates || [];
      if (!updates.length) return;

      setSections(prevSections => prevSections.map(section => (
        section.id === sectionId
          ? { ...section, blocks: applyPastedImageUpdates(section.blocks, updates) }
          : section
      )));
      showSaveIndicator(updates.length === 1 ? '💾 Image details saved' : `💾 ${updates.length} image details saved`);
      return;
    }

    if (content.type === 'image' && targetBlockId && targetCardPath) {
      setSections(prevSections => prevSections.map(section => {
        if (section.id !== sectionId) return section;

        const updatedBlocks = (section.blocks || []).map(block => {
          if (block.id !== targetBlockId || block.type !== 'multi-column-cards') return block;

          const updatedColumns = (block.columns || []).map((column, colIndex) => {
            if (colIndex !== targetCardPath.columnIndex) return column;
            const updatedCards = (column.cards || []).map((card, cardIndex) => {
              if (cardIndex !== targetCardPath.cardIndex) return card;

              const previousImage = card.image || {};
              const nextImage = {
                src: content.src || previousImage.src || '',
                alt: content.alt || previousImage.alt || '',
                caption: content.caption || previousImage.caption || '',
                imageShowCaption: content.imageShowCaption !== false,
                imageTitle: content.imageTitle || previousImage.imageTitle || '',
                imageAuthor: content.imageAuthor || previousImage.imageAuthor || '',
                imageSource: content.imageSource || previousImage.imageSource || '',
                imageDate: content.imageDate || previousImage.imageDate || ''
              };

              return { ...card, imagePlaceholder: true, image: nextImage };
            });

            return { ...column, cards: updatedCards };
          });

          return { ...block, columns: updatedColumns };
        });

        return { ...section, blocks: updatedBlocks };
      }));
      showSaveIndicator('💾 Image updated in preset slot');
      return;
    }

    if (content.type === 'video' && targetBlockId && targetCardPath) {
      setSections(prevSections => prevSections.map(section => {
        if (section.id !== sectionId) return section;

        const updatedBlocks = (section.blocks || []).map(block => {
          if (block.id !== targetBlockId || block.type !== 'multi-column-cards') return block;

          const updatedColumns = (block.columns || []).map((column, colIndex) => {
            if (colIndex !== targetCardPath.columnIndex) return column;

            const updatedCards = (column.cards || []).map((card, cardIndex) => {
              if (cardIndex !== targetCardPath.cardIndex) return card;

              const previousVideo = card.video || {};
              const nextVideo = {
                src: content.src || previousVideo.src || '',
                videoUrl: content.videoUrl || previousVideo.videoUrl || '',
                videoPlatform: content.videoPlatform || previousVideo.videoPlatform || 'youtube',
                embedCode: content.embedCode || previousVideo.embedCode || '',
                aspectRatio: content.aspectRatio || previousVideo.aspectRatio || '16-9',
                videoTitle: content.videoTitle || previousVideo.videoTitle || '',
                videoAuthor: content.videoAuthor || previousVideo.videoAuthor || '',
                videoSource: content.videoSource || previousVideo.videoSource || '',
                videoDate: content.videoDate || previousVideo.videoDate || ''
              };

              return { ...card, videoPlaceholder: true, video: nextVideo };
            });

            return { ...column, cards: updatedCards };
          });

          return { ...block, columns: updatedColumns };
        });

        return { ...section, blocks: updatedBlocks };
      }));
      showSaveIndicator('💾 Video updated in preset slot');
      return;
    }

    if (isEditing) {
      setSections(prevSections =>
        prevSections.map(section => {
          if (section.id === sectionId) {
            return {
              ...section,
              blocks: section.blocks.map(block => {
                if (block.id === content.id) {
                  let updatedBlock = { ...block, ...content };

                  if (block.type === 'cards') {
                    updatedBlock.items = content.cardItems || content.items || [];
                    updatedBlock.layout = content.cardLayout || content.layout || '2x1';
                    updatedBlock.style = content.cardStyle || content.style || 'info';
                  }

                  if (block.type === 'stacked-cards') {
                    updatedBlock.items = content.stackedCardItems || content.items || [];
                    updatedBlock.includeSideText = content.includeSideText !== false;
                  }

                  if (block.type === 'gallery') {
                    updatedBlock.items = content.items || [];
                    updatedBlock.columns = content.columns || '2';
                  }

                  if (block.type === 'accordion') {
                    updatedBlock.items = content.items || content.accordionItems || [];
                    updatedBlock.settings = content.settings || content.accordionSettings || {};
                  }

                  if (block.type === 'worksheet') {
                    updatedBlock.title = content.title || block.title;
                    updatedBlock.description = content.description || block.description;
                    updatedBlock.layout = content.layout || block.layout;
                    updatedBlock.questions = content.questions || block.questions || [];
                    updatedBlock.usePoints = content.usePoints;
                    updatedBlock.totalPoints = content.usePoints ? content.totalPoints : undefined;
                  }

                  return updatedBlock;
                }
                return block;
              })
            };
          }
          return section;
        })
      );
      showSaveIndicator(`💾 ${content.type} content updated`);
    } else {
      const newBlock = {
        id: generateId(),
        ...content,
        type: content.type || modalContentType,
      };

      const resolvedSectionId = sectionId || defaultSection || 'bridge-in';

      setSections(prevSections => {
        // Resolve target: prefer explicit section, fall back to first available
        let targetSectionId = resolvedSectionId;
        if (!prevSections.some(s => s.id === targetSectionId) && prevSections.length > 0) {
          targetSectionId = prevSections[0].id;
        }

        const updatedSections = prevSections.map(section => {
          if (section.id !== targetSectionId) return section;
          const blocks = [...section.blocks];

          if (typeof insertAtIndex === 'number') {
            blocks.splice(insertAtIndex, 0, newBlock);
          } else if (insertAfterBlockId) {
            const index = blocks.findIndex(b => b.id === insertAfterBlockId);
            if (index !== -1) {
              blocks.splice(index + 1, 0, newBlock);
            } else {
              blocks.push(newBlock);
            }
          } else {
            const fallbackSlideId = (() => {
              if (targetSlideId) return targetSlideId;
              const activeSlide = slides[currentSlideIndex];
              return activeSlide?.section?.id === targetSectionId ? activeSlide.slideId : null;
            })();

            if (fallbackSlideId) {
              const sectionSlides = splitSectionSlides(section, { includeEmpty: true, includeMeta: true });
              const matchedSlide = sectionSlides.find((slide) => slide.slideId === fallbackSlideId);

              if (matchedSlide?.blocks?.length) {
                const lastBlockId = matchedSlide.blocks[matchedSlide.blocks.length - 1]?.id;
                const index = blocks.findIndex((block) => block.id === lastBlockId);
                if (index !== -1) {
                  blocks.splice(index + 1, 0, newBlock);
                } else {
                  blocks.push(newBlock);
                }
              } else {
                const dividerIndex = blocks.findIndex((block) => block?.type === 'divider'
                  && block?.content?.isSlideBreak
                  && (block?.content?.slideId === fallbackSlideId || block?.slideId === fallbackSlideId));
                if (dividerIndex !== -1) {
                  blocks.splice(dividerIndex + 1, 0, newBlock);
                } else {
                  blocks.push(newBlock);
                }
              }
            } else {
              blocks.push(newBlock);
            }
          }

          return { ...section, blocks };
        });
        return updatedSections;
      });
      showSaveIndicator(`💾 ${content.type || modalContentType} content added`);
    }
  };

  const handleBlockEdit = (blockToEdit, sectionId) => {
    setModalContentType(blockToEdit.type);

    let initialDataForModal = { ...blockToEdit, sectionId, isEditing: true };

    const htmlToText = (html) => {
      if (!html) return '';
      const temp = document.createElement('div');
      temp.innerHTML = html;
      const listItems = temp.querySelectorAll('li');
      listItems.forEach(li => {
        li.innerHTML = '• ' + li.innerHTML;
      });
      return temp.textContent || temp.innerText || '';
    };

    if (blockToEdit.type === 'cards') {
      const cleanedItems = (blockToEdit.items || []).map(item => ({
        title: item.title || '',
        content: item.content || ''
      }));
      initialDataForModal.cardItems = cleanedItems;
      initialDataForModal.cardLayout = blockToEdit.layout || '2x1';
      initialDataForModal.cardStyle = blockToEdit.style || 'info';
    } else if (blockToEdit.type === 'stacked-cards') {
      const cleanedItems = (blockToEdit.items || []).map(item => ({
        title: item.title || '',
        content: item.content || '',
        sideText: item.sideText || '',
        color: item.color || ''
      }));
      initialDataForModal.stackedCardItems = cleanedItems;
      initialDataForModal.includeSideText = blockToEdit.includeSideText !== false;
    } else if (blockToEdit.type === 'accordion') {
      initialDataForModal.accordionItems = blockToEdit.items || [];
      initialDataForModal.accordionSettings = blockToEdit.settings || {
        allowMultipleOpen: false,
        defaultOpenFirst: false,
        theme: 'default'
      };
    } else if (blockToEdit.type === 'multi-column-cards') {
      // Load existing multi-column-cards data for editing
      const columnCount = (blockToEdit.columns || []).length;
      const existingMatchColumns = blockToEdit.matchHeightColumns || blockToEdit.multiColumnData?.matchHeightColumns || [];
      const filteredMatchColumns = (existingMatchColumns || [])
        .map((colIndex) => Number(colIndex))
        .filter((colIndex) => Number.isInteger(colIndex) && colIndex >= 0 && colIndex < columnCount);
      const blockTitleEnabled = blockToEdit.blockTitleEnabled ?? blockToEdit.multiColumnData?.blockTitleEnabled ?? false;
      const blockTitle = blockToEdit.blockTitle ?? blockToEdit.multiColumnData?.blockTitle ?? '';
      initialDataForModal.multiColumnData = {
          matchHeights: blockToEdit.matchHeights || blockToEdit.multiColumnData?.matchHeights || false,
          matchHeightColumns: filteredMatchColumns,
          blockTitleEnabled,
          blockTitle,
          columns: (blockToEdit.columns || []).map(column => ({
            widthPercent: column.widthPercent || 100,
            cards: (column.cards || []).map(card => ({
              title: card.title || '',
              primaryTitle: card.primaryTitle || '',
              categoryLabel: card.categoryLabel || '',
              instructionalText: card.instructionalText || '',
              numberedList: card.numberedList || [],
              numberedListStyle: card.numberedListStyle || 'numbered',
              titleAlignment: card.titleAlignment || 'center',
              iconName: card.iconName || '',
              content: card.content || '',
              isChecklist: card.isChecklist || false,
              checklistItems: card.checklistItems || [],
            backgroundColor: card.backgroundColor || 'transparent',
            borderColor: card.borderColor || '#94a3b8',
            showBorder: card.showBorder !== false,
            titleColor: card.titleColor || undefined,
            checkmarkColor: card.checkmarkColor || undefined,
            listStyle: card.listStyle || 'checkbox',
            badge: card.badge || { enabled: false, type: 'theme', gradientDirection: 'to right' },
            callout: {
              enabled: card.callout?.enabled || false,
              title: card.callout?.title || '',
              description: card.callout?.description || '',
              borderColor: card.callout?.borderColor || undefined,
              backgroundColor: card.callout?.backgroundColor || undefined,
              position: card.callout?.position || 'top'
            },
            image: card.image || undefined,
            video: card.video || undefined,
            imagePlaceholder: card.imagePlaceholder || false,
            videoPlaceholder: card.videoPlaceholder || false,
            placeholderHeading: card.placeholderHeading || '',
            placeholderText: card.placeholderText || ''
          }))
        }))
      };
      initialDataForModal.blockTitleEnabled = blockTitleEnabled;
      initialDataForModal.blockTitle = blockTitle;
    } else if (blockToEdit.type === 'image') {
      initialDataForModal.image_alt = blockToEdit.alt;
      initialDataForModal.image_size = blockToEdit.size;
      initialDataForModal.image_caption = blockToEdit.caption?.replace('<strong>Figure:</strong> ', '') || '';
      initialDataForModal.image_showCaption = blockToEdit.imageShowCaption !== false;
      initialDataForModal.image_title = blockToEdit.imageTitle;
      initialDataForModal.image_author = blockToEdit.imageAuthor;
      initialDataForModal.image_source = blockToEdit.imageSource;
      initialDataForModal.image_url = blockToEdit.imageUrl || '';
      initialDataForModal.image_date = blockToEdit.imageDate;
      // Embedded links reopen in the embed panel with the saved iframe src as
      // the editable input.
      if (blockToEdit.imageEmbedSrc) {
        initialDataForModal.imageEmbedSrc = blockToEdit.imageEmbedSrc;
        initialDataForModal.imageEmbedInput = blockToEdit.imageEmbedUrl || blockToEdit.imageEmbedSrc;
        initialDataForModal.imageEmbedUrl = blockToEdit.imageEmbedUrl || '';
        initialDataForModal.imageEmbedProvider = blockToEdit.imageEmbedProvider || '';
        initialDataForModal.imageEmbedWidth = blockToEdit.imageEmbedWidth || '';
        initialDataForModal.imageEmbedHeight = blockToEdit.imageEmbedHeight || '';
        initialDataForModal.imageEmbedThumbnail = blockToEdit.imageEmbedThumbnail || '';
      }
      initialDataForModal.image_sourceType = blockToEdit.imageSourceType || 'image';
      initialDataForModal.image_page = blockToEdit.imagePage || '';
      initialDataForModal.image_includePage = blockToEdit.imageIncludePage !== false;
      initialDataForModal.image_license = blockToEdit.imageLicense || '';
      initialDataForModal.image_includeLicense = blockToEdit.imageIncludeLicense !== false;
      initialDataForModal.image_tableNumber = blockToEdit.imageTableNumber || '';
      initialDataForModal.image_referenceCategory = blockToEdit.imageReferenceCategory || '';
      initialDataForModal.image_medium = blockToEdit.imageMedium || '';
      initialDataForModal.image_referenceTitle = blockToEdit.imageReferenceTitle || '';
      initialDataForModal.image_articleTitle = blockToEdit.imageArticleTitle || '';
      initialDataForModal.image_isAdapted = Boolean(blockToEdit.imageIsAdapted);
      initialDataForModal.image_includeCitation = blockToEdit.imageIncludeCitation !== false;
      initialDataForModal.image_addToReferenceList = blockToEdit.imageAddToReferenceList !== false;
    } else if (blockToEdit.type === 'gallery') {
      initialDataForModal.galleryColumns = blockToEdit.columns;
      initialDataForModal.imageFiles = null;
      initialDataForModal.items = blockToEdit.items || [];
      if (blockToEdit.items) {
        blockToEdit.items.forEach((item, index) => {
          const baseFieldName = `image_${index}`;
          initialDataForModal[`${baseFieldName}_alt`] = item.alt;
          initialDataForModal[`${baseFieldName}_caption`] = item.caption?.replace(/^<strong>Figure \d+:<\/strong> /, '') || '';
          initialDataForModal[`${baseFieldName}_showCaption`] = item.imageShowCaption !== false;
          initialDataForModal[`${baseFieldName}_title`] = item.imageTitle;
          initialDataForModal[`${baseFieldName}_author`] = item.imageAuthor;
          initialDataForModal[`${baseFieldName}_source`] = item.imageSource;
          initialDataForModal[`${baseFieldName}_url`] = item.imageUrl || '';
          initialDataForModal[`${baseFieldName}_date`] = item.imageDate;
          initialDataForModal[`${baseFieldName}_sourceType`] = item.imageSourceType || 'image';
          initialDataForModal[`${baseFieldName}_page`] = item.imagePage || '';
          initialDataForModal[`${baseFieldName}_includePage`] = item.imageIncludePage !== false;
          initialDataForModal[`${baseFieldName}_license`] = item.imageLicense || '';
          initialDataForModal[`${baseFieldName}_includeLicense`] = item.imageIncludeLicense !== false;
          initialDataForModal[`${baseFieldName}_tableNumber`] = item.imageTableNumber || '';
          initialDataForModal[`${baseFieldName}_referenceCategory`] = item.imageReferenceCategory || '';
          initialDataForModal[`${baseFieldName}_medium`] = item.imageMedium || '';
          initialDataForModal[`${baseFieldName}_referenceTitle`] = item.imageReferenceTitle || '';
          initialDataForModal[`${baseFieldName}_articleTitle`] = item.imageArticleTitle || '';
          initialDataForModal[`${baseFieldName}_isAdapted`] = Boolean(item.imageIsAdapted);
          initialDataForModal[`${baseFieldName}_includeCitation`] = item.imageIncludeCitation !== false;
          initialDataForModal[`${baseFieldName}_addToReferenceList`] = item.imageAddToReferenceList !== false;
        });
      }
    } else if (blockToEdit.type === 'html') {
      initialDataForModal.content = blockToEdit.content || '';
      initialDataForModal.language = blockToEdit.language || 'html';
      initialDataForModal.mode = blockToEdit.mode || 'displayCode';
      initialDataForModal.customCss = blockToEdit.customCss || '';
      initialDataForModal.citation = blockToEdit.citation || '';
      initialDataForModal.options = blockToEdit.options || {
        softWrap: false,
        showLineNumbers: true,
        allowScripts: false,
      };
    } else if (blockToEdit.type === 'custom-html') {
      initialDataForModal.html = blockToEdit.html || '';
      initialDataForModal.css = blockToEdit.css || '';
      initialDataForModal.cssScope = blockToEdit.cssScope === 'deck' ? 'deck' : 'slide';
      initialDataForModal.images = Array.isArray(blockToEdit.images) ? blockToEdit.images : [];
      initialDataForModal.videos = Array.isArray(blockToEdit.videos) ? blockToEdit.videos : [];
      initialDataForModal.citations = Array.isArray(blockToEdit.citations) ? blockToEdit.citations : [];
    } else if (blockToEdit.type === 'link') {
      initialDataForModal.blockTitle = blockToEdit.title || '';
      initialDataForModal.blockDescription = blockToEdit.description || '';
      initialDataForModal.linkItems = blockToEdit.items || [];
    } else if (blockToEdit.type === 'video') {
      initialDataForModal.videoPlatform = blockToEdit.videoPlatform || 'youtube';
      initialDataForModal.videoUrl = blockToEdit.videoUrl;
      initialDataForModal.embedCode = blockToEdit.embedCode;
      initialDataForModal.aspectRatio = blockToEdit.aspectRatio || '16-9';
      initialDataForModal.videoTitle = blockToEdit.videoTitle;
      initialDataForModal.videoAuthor = blockToEdit.videoAuthor;
      initialDataForModal.videoDate = blockToEdit.videoDate;
      initialDataForModal.videoSource = blockToEdit.videoSource;
      initialDataForModal.videoIncludeCitation = blockToEdit.videoIncludeCitation !== false;
    } else if (blockToEdit.type === 'youtube-playlist') {
      initialDataForModal.playlistInput = playlistWatchUrl({
        playlistId: blockToEdit.playlistId,
        videoId: blockToEdit.startVideoId,
        index: blockToEdit.startIndex
      });
      initialDataForModal.playlistId = blockToEdit.playlistId || '';
      initialDataForModal.startVideoId = blockToEdit.startVideoId || '';
      initialDataForModal.startIndex = blockToEdit.startIndex || '';
      initialDataForModal.playlistTitle = blockToEdit.title || blockToEdit.videoTitle || '';
      initialDataForModal.playlistChannel = blockToEdit.channel || blockToEdit.videoAuthor || '';
      initialDataForModal.playlistDescription = blockToEdit.description || '';
      initialDataForModal.playlistItemCount = blockToEdit.itemCount || '';
      initialDataForModal.playlistItems = blockToEdit.items || [];
      initialDataForModal.playlistLayout = blockToEdit.layout || 'split';
      initialDataForModal.playlistTheme = blockToEdit.theme || 'light';
      initialDataForModal.playlistAccentColor = blockToEdit.accentColor || DEFAULT_PLAYLIST_ACCENT;
      initialDataForModal.playlistShowThumbnails = blockToEdit.showThumbnails !== false;
      initialDataForModal.aspectRatio = blockToEdit.aspectRatio || '16-9';
      initialDataForModal.videoSource = blockToEdit.videoSource || 'YouTube';
      initialDataForModal.videoDate = blockToEdit.videoDate || '';
      initialDataForModal.videoIncludeCitation = blockToEdit.videoIncludeCitation !== false;
    } else if (blockToEdit.type === 'audio') {
      initialDataForModal.audioDescription = blockToEdit.description;
      // A linked clip goes back into the link field so it can be seen and
      // replaced; an uploaded one is a data URL nobody wants printed, and is
      // reported by the panel as "the uploaded file" instead.
      initialDataForModal.audioSrc = isAudioDataUrl(blockToEdit.src) ? '' : (blockToEdit.src || '');
      initialDataForModal.audioHasUploadedFile = isAudioDataUrl(blockToEdit.src);
      initialDataForModal.playerStyle = normalizeAudioPlayerStyle(blockToEdit.playerStyle);
      initialDataForModal.audioTitle = blockToEdit.audioTitle;
      initialDataForModal.audioCreator = blockToEdit.audioCreator;
      initialDataForModal.audioSourceInfo = blockToEdit.audioSourceInfo;
      initialDataForModal.audioDateInfo = blockToEdit.audioDateInfo;
    } else if (blockToEdit.type === 'worksheet') {
      initialDataForModal.worksheetTitle = blockToEdit.title;
      initialDataForModal.worksheetDescription = blockToEdit.description;
      initialDataForModal.worksheetLayout = blockToEdit.layout;
      initialDataForModal.questions = blockToEdit.questions || [];
      initialDataForModal.worksheetTotalPoints = blockToEdit.totalPoints;
      initialDataForModal.worksheetUsePoints = blockToEdit.usePoints !== false;
    } else if (blockToEdit.type === 'apa-quote') {
      initialDataForModal.quoteText = blockToEdit.quoteText || '';
      initialDataForModal.author = blockToEdit.author || '';
      initialDataForModal.year = blockToEdit.year || '';
      initialDataForModal.title = blockToEdit.title || '';
      initialDataForModal.source = blockToEdit.source || '';
      initialDataForModal.url = blockToEdit.url || '';
      initialDataForModal.doi = blockToEdit.doi || '';
      initialDataForModal.page = blockToEdit.page || '';
      initialDataForModal.quoteSourceType = blockToEdit.sourceType === 'paraphrase' ? 'paraphrase' : 'quote';
      initialDataForModal.quoteSources = (blockToEdit.sources && blockToEdit.sources.length > 0)
        ? blockToEdit.sources
        : [{
            author: blockToEdit.author || '',
            year: blockToEdit.year || '',
            page: blockToEdit.page || ''
          }];
    } else if (blockToEdit.type === 'apa-citation') {
      initialDataForModal.citations = blockToEdit.citations || [];
    } else if (blockToEdit.type === 'apa-reference-list') {
      initialDataForModal.citations = blockToEdit.citations || [];
    } else if (blockToEdit.type === 'project-timeline') {
      initialDataForModal.timelineTitle = blockToEdit.title || '';
      initialDataForModal.timelineSteps = Array.isArray(blockToEdit.steps) ? blockToEdit.steps : [];
    }

    setModalInitialData(initialDataForModal);
    setIsModalOpen(true);
  };

  const handleHeaderDataChange = (field, value) => {
    setHeaderData(prev => ({ ...prev, [field]: value }));
  };

  const handleToggleSection = (sectionId) => {
    setOpenSectionIds(prevOpenIds => {
      if (prevOpenIds.includes(sectionId)) {
        return prevOpenIds.filter(id => id !== sectionId);
      } else {
        return [...prevOpenIds, sectionId];
      }
    });
  };

  const handleToggleAllSections = () => {
    if (openSectionIds.length > 0) {
      setOpenSectionIds([]);
    } else {
      const allSectionIds = sections.map(section => section.id);
      setOpenSectionIds(allSectionIds);
    }
  };

  const handleNavClick = (e, sectionId, slideIndex, globalSlideIndex) => {
    if (e) {
      e.preventDefault();
    }
    if (bodyLayout === 'slides') {
      const targetIndex = Number.isFinite(globalSlideIndex)
        ? globalSlideIndex
        : Number.isFinite(slideIndex)
          ? slideIndex
          : slides.findIndex((slide) => slide.section?.id === sectionId);
      if (targetIndex !== -1) {
        setCurrentSlideIndex(targetIndex);
      }
      return;
    }
    if (sectionId === 'title-card') {
      setBodyLayout('slides');
      setCurrentSlideIndex(0);
      return;
    }
    if (sectionId === 'end-title-card') {
      const endSlideIndex = slides.findIndex((slide) => slide.type === 'end-title-card');
      if (endSlideIndex !== -1) {
        setBodyLayout('slides');
        setCurrentSlideIndex(endSlideIndex);
      }
      return;
    }
    if (!openSectionIds.includes(sectionId)) {
      setOpenSectionIds(prev => [...prev, sectionId]);
    }
    setTimeout(() => {
      const element = document.getElementById(sectionId);
      if (element) {
        const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
        const offsetPosition = elementPosition - 60;
        smoothScrollTo(offsetPosition, 1000);
      }
    }, 100);
  };

  /**
   * Go to a slide by the number the deck itself shows.
   *
   * That number is the slide's place in the running deck — the badge on its
   * thumbnail and the left half of the toolbar's "64 / 66" — so the box takes
   * the number already on screen rather than a position within a section.
   * Scrolling an outline of sixty-odd slides to reach a number you can already
   * name is the long way round.
   *
   * The target's section is opened even if it was collapsed by hand: asking for
   * slide 33 is asking to see slide 33, and the effect that reveals the current
   * slide deliberately leaves a hand-collapsed section shut.
   */
  const slideJump = resolveSlideJump(drawerSlideJump, slides.length);

  const handleSlideJump = () => {
    if (!slideJump.isValid) return;
    const sectionId = slides[slideJump.index]?.section?.id;
    if (sectionId) setDrawerSectionExpansion(sectionId, true);
    if (bodyLayout !== 'slides') setBodyLayout('slides');
    setCurrentSlideIndex(slideJump.index);
  };

  /**
   * A click on a slide in the outline.
   *
   * Plain click goes to the slide, as it always has, and drops any selection —
   * the same rule as a file list, where clicking one thing means you are done
   * with the last set. Ctrl (or Cmd) adds and removes one at a time; Shift
   * takes everything between the anchor and here.
   */
  const handleSlideRowClick = (event, sectionId, sectionSlide) => {
    const selectable = isEditMode
      && !sectionSlide.isSectionTitleCard
      && Boolean(sectionSlide.slideId);

    if (selectable && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
      event.stopPropagation();
      toggleSlidePick(sectionSlide.slideId);
      return;
    }
    if (selectable && event.shiftKey) {
      event.preventDefault();
      event.stopPropagation();
      extendSlidePickTo(sectionSlide.slideId);
      return;
    }

    clearSlidePicks();
    if (selectable) pickAnchorRef.current = sectionSlide.slideId;
    handleNavClick(event, sectionId, sectionSlide.slideIndex, sectionSlide.globalIndex);
  };

  /**
   * Delete on a slide row in the outline.
   *
   * Bound to the row rather than to the document, and that is the whole design:
   * "the selected slide" is an ambiguous phrase in this app — the outline has a
   * current slide (blue ring), a set of picked slides (violet), and the canvas
   * underneath has its own selected objects, which already answer Delete with
   * their own handler. Asking the question through focus removes the ambiguity
   * entirely: the row you are on is the slide that goes, and a Delete aimed at
   * a canvas object never reaches this at all.
   *
   * One slide, not the whole pick, matching the row menu's own Delete item —
   * which likewise acts on its row while leaving a multi-slide pick alone.
   *
   * No confirmation. The menu item asks first because a mis-click there is
   * silent and unrecoverable-looking; this is a deliberate keystroke on a
   * focused row, and the undo history already covers it — `sections` is
   * snapshotted, so Ctrl+Z brings the slide back with its blocks. The toast
   * says so rather than assuming anyone knows.
   */
  const handleSlideRowKeyDown = (sectionId, sectionSlide, slideBlocks) => (event) => {
    // Backspace as well: it is the Delete key on a Mac keyboard.
    if (event.key !== 'Delete' && event.key !== 'Backspace') return;
    // Both are ordinary editing keys wherever text is being entered, and the
    // outline holds fields of its own.
    if (isTypingTarget(event.target)) return;
    if (isPresenting || isDialogOpen()) return;
    // A section title card is switched off, not deleted — there is no such
    // slide to remove.
    if (sectionSlide.isSectionTitleCard || !sectionSlide.slideId) return;

    event.preventDefault();
    event.stopPropagation();

    if (!isEditMode) {
      // Same reasoning as the block shortcuts: a key that silently declines is
      // indistinguishable from one that is broken.
      showSaveIndicator('Switch to Edit mode (E) to delete slides.');
      return;
    }

    focusSlideRowAfterDeleteRef.current = true;
    handleRemoveSlide(sectionId, slideBlocks, sectionSlide.slideId);
  };

  useEffect(() => {
    if (!focusSlideRowAfterDeleteRef.current) return;
    focusSlideRowAfterDeleteRef.current = false;
    // The row that had focus has just stopped existing, and focus falls to
    // <body> with it — so the outline stops answering the keyboard halfway
    // through a clear-out, and a screen reader loses its place. Hand it to the
    // row that took its place, or to the drawer itself when the deleted slide
    // was the last one that could take it.
    //
    // On a macrotask, not here: removing a slide schedules its own move to the
    // neighbouring one (see handleRemoveSlide), so this commit is not the last
    // word. Focusing now would hand the keyboard to a row the navigation is
    // about to replace, and focus would land back on <body> when it goes.
    // Going last means `activeSlideItemRef` points at the row that actually
    // took the deleted one's place.
    //
    // The timer is held on a ref rather than cancelled by this effect's own
    // cleanup, and that is not a detail: the navigation re-runs the effect, so
    // a cleanup here would cancel the very focus it had just scheduled, every
    // time, and focus would never be restored at all.
    clearTimeout(focusSlideRowTimerRef.current);
    focusSlideRowTimerRef.current = setTimeout(() => {
      const row = activeSlideItemRef.current?.querySelector('[data-slide-row-target]');
      (row || navDrawerRef.current)?.focus?.();
    }, 0);
  }, [slides, currentSlideIndex]);

  useEffect(() => () => clearTimeout(focusSlideRowTimerRef.current), []);

  const handleDrawerSectionToggle = (sectionId, shouldExpand) => {
    const isCollapsed = drawerCollapsedSections.includes(sectionId);
    const expand = typeof shouldExpand === 'boolean'
      ? shouldExpand
      : isCollapsed;
    setDrawerSectionExpansion(sectionId, expand);
  };

  const handleNavHeaderClick = (e, { id, slideIndex, label }, sectionSlides, targetSlideIndex, isExpanded) => {
    if (id === 'title-card' && !titleCardSettings?.enabled) {
      handleTitleCardUpdate({ enabled: true });
      return;
    }
    if (id === 'end-title-card' && !endTitleCardSettings?.enabled) {
      handleEndTitleCardUpdate({ enabled: true });
      return;
    }
    handleNavClick(e, id, slideIndex, targetSlideIndex);
    if (!sectionSlides.length) return;

    const nextExpanded = typeof isExpanded === 'boolean' ? !isExpanded : true;
    setDrawerSectionExpansion(id, nextExpanded);
  };

  const closeNavDrawer = useCallback(() => {
    setIsNavDrawerOpen(false);
  }, []);

  useEffect(() => {
    if (!activeSectionId) return;
    if (drawerCollapsedSections.includes(activeSectionId)) return;

    setDrawerExpandedSections((prev) => (prev.includes(activeSectionId) ? prev : [...prev, activeSectionId]));
  }, [activeSectionId, drawerCollapsedSections]);

  useEffect(() => {
    if (isNavDrawerOpen && navDrawerRef.current) {
      navDrawerRef.current.focus();
    }

    if (!isNavDrawerOpen && wasNavDrawerOpenRef.current && navDrawerTriggerRef.current) {
      navDrawerTriggerRef.current.focus();
    }

    wasNavDrawerOpenRef.current = isNavDrawerOpen;
  }, [isNavDrawerOpen]);

  const closeDrawerSlideMenu = useCallback(() => {
    setDrawerSlideMenu(null);
    setDrawerSlideMenuPosition({ top: 0, left: 0, visibility: 'hidden' });
  }, []);

  const positionDrawerSlideMenu = useCallback(() => {
    const trigger = drawerSlideMenuButtonRef.current;
    if (!trigger?.isConnected) {
      closeDrawerSlideMenu();
      return;
    }
    const triggerRect = trigger.getBoundingClientRect();
    const menuRect = drawerSlideMenuRef.current?.getBoundingClientRect();
    const menuWidth = menuRect?.width || 224;
    const menuHeight = menuRect?.height || 320;
    const margin = 8;
    const left = Math.max(margin, Math.min(triggerRect.right - menuWidth, window.innerWidth - menuWidth - margin));
    const below = triggerRect.bottom + margin;
    const top = below + menuHeight <= window.innerHeight - margin
      ? below
      : Math.max(margin, triggerRect.top - menuHeight - margin);
    setDrawerSlideMenuPosition({ top, left, visibility: 'visible' });
  }, [closeDrawerSlideMenu]);

  useEffect(() => {
    if (!drawerSlideMenu) return undefined;

    const handleOutside = (event) => {
      const outsideMenu = drawerSlideMenuRef.current && !drawerSlideMenuRef.current.contains(event.target);
      const outsideTrigger = drawerSlideMenuButtonRef.current && !drawerSlideMenuButtonRef.current.contains(event.target);
      if (outsideMenu && outsideTrigger) {
        closeDrawerSlideMenu();
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        closeDrawerSlideMenu();
      }
    };

    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('keydown', handleEscape);
    window.addEventListener('resize', positionDrawerSlideMenu);
    document.addEventListener('scroll', positionDrawerSlideMenu, true);
    const frame = window.requestAnimationFrame(positionDrawerSlideMenu);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('keydown', handleEscape);
      window.removeEventListener('resize', positionDrawerSlideMenu);
      document.removeEventListener('scroll', positionDrawerSlideMenu, true);
      window.cancelAnimationFrame(frame);
    };
  }, [drawerSlideMenu, closeDrawerSlideMenu, positionDrawerSlideMenu]);

  useEffect(() => {
    if (!isNavDrawerOpen && drawerSlideMenu) closeDrawerSlideMenu();
  }, [closeDrawerSlideMenu, drawerSlideMenu, isNavDrawerOpen]);

  useEffect(() => {
    if (!drawerSlideMenu?.slideId) return;
    if (!slides.some((slide) => slide.slideId === drawerSlideMenu.slideId)) closeDrawerSlideMenu();
  }, [closeDrawerSlideMenu, drawerSlideMenu, slides]);

  const handleSlideHeaderColorChange = (sectionId, color) => {
    setSlideHeaderColors((prev) => ({ ...prev, [sectionId]: color }));
  };

  // Set a single slide's header color from the per-slide settings modal. When
  // the deck is in "single" header mode, switch to per-section so the override
  // actually shows (other sections keep falling back to the default color).
  const handleSlideHeaderColorForSection = (sectionId, color) => {
    setSlideHeaderColors((prev) => ({ ...prev, [sectionId]: color }));
    setSlideHeaderMode((prev) => (prev === 'single' ? 'per-section' : prev));
  };

  const handleApplySlideBackgroundScope = useCallback(({ slideId, background, scope = 'current' }) => {
    const regularSlideIds = slides
      .filter((slide) => slide.type === 'section' && slide.slideId)
      .map((slide) => slide.slideId);

    setSlideBackgrounds((prev) => {
      const next = applyScopedSlideBackground({
        existingBackgrounds: prev,
        regularSlideIds,
        scope,
        targetSlideId: slideId,
        background
      });
      syncToSlides('slideBackgrounds', next);
      return next;
    });
  }, [slides, syncToSlides]);

  const buildSectionTitleCardSettings = useCallback((section, overrides = {}) => {
    if (!section) return normalizeTitleCardSettings(overrides, DEFAULT_SECTION_TITLE_CARD_SETTINGS);
    // A title card has its own title and subtitle fields, so seed them
    // separately. getSectionHeaderLabel would hand back "1. Bridge-In (Getting
    // Started)" — the section number and the student-friendly line crushed into
    // one string, which is neither the title nor the subtitle the card asks for.
    const { title: defaultTitle, subtitle: defaultSubtitle } = getSlideHeaderParts(section);
    const defaultSlideId = buildSectionTitleCardId(section.id);
    return normalizeTitleCardSettings(
      {
        ...DEFAULT_SECTION_TITLE_CARD_SETTINGS,
        title: defaultTitle,
        subtitle: defaultSubtitle,
        slideId: defaultSlideId,
        ...overrides,
        enabled: true
      },
      DEFAULT_SECTION_TITLE_CARD_SETTINGS
    );
  }, []);

  const buildTitleCardUpdater = (setter, defaults) => (updates) => {
    setter((prev) => {
      const next = { ...prev, ...updates };
      if (updates.imageSize !== undefined) {
        const size = Number(updates.imageSize);
        next.imageSize = Number.isFinite(size) ? size : prev.imageSize;
      }
      if (updates.enabled && !next.title) {
        const courseTitle = safeString(headerData.courseTopic, defaults.title);
        const normalizedTitle = courseTitle
          ? courseTitle.replace(/Week \d+/, `Week ${week}`)
          : defaults.title;
        next.title = normalizedTitle || defaults.title;
      }
      return next;
    });
  };

  const handleTitleCardUpdate = buildTitleCardUpdater(setTitleCardSettings, DEFAULT_TITLE_CARD_SETTINGS);
  const handleEndTitleCardUpdate = buildTitleCardUpdater(setEndTitleCardSettings, DEFAULT_END_TITLE_CARD_SETTINGS);

  const handleSectionTitleCardUpdate = useCallback((sectionId, updates) => {
    if (!sectionId) return;
    setSections((prevSections) => prevSections.map((section) => {
      if (section.id !== sectionId) return section;
      const baseSettings = section.sectionTitleCard
        ? normalizeTitleCardSettings(section.sectionTitleCard, DEFAULT_SECTION_TITLE_CARD_SETTINGS)
        : buildSectionTitleCardSettings(section);
      const baseSlideId = baseSettings.slideId || buildSectionTitleCardId(section.id);
      const nextSettings = normalizeTitleCardSettings(
        { ...baseSettings, ...updates, slideId: baseSlideId, enabled: true },
        DEFAULT_SECTION_TITLE_CARD_SETTINGS
      );
      return { ...section, sectionTitleCard: nextSettings };
    }));
  }, [buildSectionTitleCardSettings]);

  // Turning a section title card off keeps its customization so switching it
  // back on restores the author's title, colours and background.
  const handleToggleSectionTitleCard = useCallback((sectionId, enabled) => {
    if (!sectionId) return;
    setSections((prevSections) => prevSections.map((section) => {
      if (section.id !== sectionId) return section;
      const baseSettings = section.sectionTitleCard
        ? normalizeTitleCardSettings(section.sectionTitleCard, DEFAULT_SECTION_TITLE_CARD_SETTINGS)
        : buildSectionTitleCardSettings(section);
      const nextSettings = {
        ...baseSettings,
        slideId: baseSettings.slideId || buildSectionTitleCardId(section.id),
        enabled
      };
      // A card with no title, subtitle or image is dropped when the deck is
      // rebuilt, so re-seed the section name to keep the toggle meaningful.
      const hasContent = Boolean(
        safeString(baseSettings.title, '').trim()
        || safeString(baseSettings.subtitle, '').trim()
        || baseSettings.image
      );
      if (enabled && !hasContent) {
        const { title, subtitle } = getSlideHeaderParts(section);
        nextSettings.title = title;
        nextSettings.subtitle = subtitle;
      }
      return { ...section, sectionTitleCard: nextSettings };
    }));
    showSaveIndicator(enabled ? '➕ Section title card added' : '➖ Section title card removed');
  }, [buildSectionTitleCardSettings, showSaveIndicator]);

  const buildTitleCardImageHandler = (setter) => (file, openCustomize = false, target = 'start') => {
    if (!file) {
      setter((prev) => ({ ...prev, image: '', imageCitation: '' }));
      return;
    }
    const reader = new FileReader();
    reader.onload = (event) => {
      setter((prev) => ({ ...prev, image: event.target.result || '' }));
      if (openCustomize) {
        setTitleCardModalTarget(target);
        setShowTitleCardModal(true);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleTitleCardImageChange = buildTitleCardImageHandler(setTitleCardSettings);
  const handleEndTitleCardImageChange = buildTitleCardImageHandler(setEndTitleCardSettings);

  const handleSectionTitleCardImageChange = useCallback((sectionId, file, openCustomize = false) => {
    if (!sectionId) return;
    if (!file) {
      handleSectionTitleCardUpdate(sectionId, { image: '', imageCitation: '' });
      return;
    }
    const reader = new FileReader();
    reader.onload = (event) => {
      handleSectionTitleCardUpdate(sectionId, { image: event.target.result || '' });
      if (openCustomize) {
        setTitleCardModalTarget(`${SECTION_TITLE_CARD_PREFIX}${sectionId}`);
        setShowTitleCardModal(true);
      }
    };
    reader.readAsDataURL(file);
  }, [handleSectionTitleCardUpdate]);

  const handleTitleCardBackgroundImageChange = useCallback((backgroundImage, target = 'start') => {
    const nextBackgroundImage = normalizeBackgroundPersistenceValue(backgroundImage || '');
    if (target === 'end') {
      handleEndTitleCardUpdate({ backgroundImage: nextBackgroundImage });
      return;
    }
    if (target === 'section') return;
    handleTitleCardUpdate({ backgroundImage: nextBackgroundImage });
  }, [handleEndTitleCardUpdate, handleTitleCardUpdate]);

  const handleTitleCardBackgroundUpload = useCallback((file, target = 'start') => {
    if (!file) {
      handleTitleCardBackgroundImageChange('', target);
      return;
    }
    const reader = new FileReader();
    reader.onload = (event) => {
      handleTitleCardBackgroundImageChange(event.target.result || '', target);
    };
    reader.readAsDataURL(file);
  }, [handleTitleCardBackgroundImageChange]);

  const handleSectionTitleCardBackgroundChange = useCallback((sectionId, backgroundImage) => {
    if (!sectionId) return;
    handleSectionTitleCardUpdate(sectionId, { backgroundImage: normalizeBackgroundPersistenceValue(backgroundImage || '') });
  }, [handleSectionTitleCardUpdate]);

  const handleSectionTitleCardBackgroundUpload = useCallback((sectionId, file) => {
    if (!sectionId) return;
    if (!file) {
      handleSectionTitleCardBackgroundChange(sectionId, '');
      return;
    }
    const reader = new FileReader();
    reader.onload = (event) => {
      handleSectionTitleCardBackgroundChange(sectionId, event.target.result || '');
    };
    reader.readAsDataURL(file);
  }, [handleSectionTitleCardBackgroundChange]);

  const openTitleCardModal = (target = 'start') => {
    setTitleCardModalTarget(target);
    setTitleModalPos(null);
    setShowTitleCardModal(true);
  };

  const openSectionTitleCardModal = (sectionId) => {
    if (!sectionId) return;
    setTitleCardModalTarget(`${SECTION_TITLE_CARD_PREFIX}${sectionId}`);
    setTitleModalPos(null);
    setShowTitleCardModal(true);
  };

  // Drag the Customize Title Card panel around by its header so it can be moved
  // clear of the title card being edited.
  const startTitleModalDrag = (event) => {
    if (event.button !== undefined && event.button !== 0) return;
    const panel = event.currentTarget.closest('[data-title-modal-panel]');
    if (!panel) return;
    const rect = panel.getBoundingClientRect();
    titleModalDragRef.current = {
      offsetX: event.clientX - rect.left,
      offsetY: event.clientY - rect.top,
      width: rect.width
    };
    const onMove = (moveEvent) => {
      const drag = titleModalDragRef.current;
      if (!drag) return;
      const maxX = window.innerWidth - drag.width - 8;
      const x = Math.max(8, Math.min(maxX, moveEvent.clientX - drag.offsetX));
      const y = Math.max(8, Math.min(window.innerHeight - 44, moveEvent.clientY - drag.offsetY));
      setTitleModalPos({ x, y });
    };
    const onUp = () => {
      titleModalDragRef.current = null;
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  const sectionTitleCardTargetId = titleCardModalTarget.startsWith(SECTION_TITLE_CARD_PREFIX)
    ? titleCardModalTarget.slice(SECTION_TITLE_CARD_PREFIX.length)
    : null;
  const activeSectionForTitleCard = sectionTitleCardTargetId
    ? sections.find((section) => section.id === sectionTitleCardTargetId)
    : null;
  const activeSectionTitleCardSettings = sectionTitleCardTargetId
    ? normalizeTitleCardSettings(
      activeSectionForTitleCard?.sectionTitleCard || buildSectionTitleCardSettings(activeSectionForTitleCard),
      DEFAULT_SECTION_TITLE_CARD_SETTINGS
    )
    : null;

  const activeTitleCardSettings = sectionTitleCardTargetId
    ? activeSectionTitleCardSettings
    : titleCardModalTarget === 'end'
      ? endTitleCardSettings
      : titleCardSettings;
  const activeTitleCardUpdater = sectionTitleCardTargetId
    ? (updates) => handleSectionTitleCardUpdate(sectionTitleCardTargetId, updates)
    : titleCardModalTarget === 'end'
      ? handleEndTitleCardUpdate
      : handleTitleCardUpdate;
  const activeTitleCardImageHandler = sectionTitleCardTargetId
    ? (file) => handleSectionTitleCardImageChange(sectionTitleCardTargetId, file, false)
    : titleCardModalTarget === 'end'
      ? (file) => handleEndTitleCardImageChange(file, false, 'end')
      : (file) => handleTitleCardImageChange(file, false, 'start');
  /**
   * Read an uploaded corner logo into whichever card the panel is pointing at.
   *
   * One handler for all three card kinds rather than three: unlike the card's
   * picture, nothing here depends on which card it is — the panel's updater
   * already knows, and the file becomes a data URL the same way every time.
   */
  const handleTitleCardCornerLogoFile = (file) => {
    if (!file) {
      activeTitleCardUpdater({ cornerLogo: '' });
      return;
    }
    const reader = new FileReader();
    reader.onload = (event) => {
      activeTitleCardUpdater({ cornerLogo: event.target.result || '' });
    };
    reader.readAsDataURL(file);
  };

  const activeTitleCardBackgroundImage = activeTitleCardSettings.backgroundImage || '';
  const activeTitleCardBackgroundSelection = getBackgroundSelectionState(activeTitleCardBackgroundImage);
  const activeTitleCardBackgroundSelect = sectionTitleCardTargetId
    ? (preset) => handleSectionTitleCardBackgroundChange(sectionTitleCardTargetId, preset?.key || preset?.value || '')
    : titleCardModalTarget === 'end'
      ? (preset) => handleTitleCardBackgroundImageChange(preset?.key || preset?.value || '', 'end')
      : (preset) => handleTitleCardBackgroundImageChange(preset?.key || preset?.value || '', 'start');
  const activeTitleCardBackgroundUpload = sectionTitleCardTargetId
    ? (file) => handleSectionTitleCardBackgroundUpload(sectionTitleCardTargetId, file)
    : titleCardModalTarget === 'end'
      ? (file) => handleTitleCardBackgroundUpload(file, 'end')
      : (file) => handleTitleCardBackgroundUpload(file, 'start');
  // A linked background lands in the same field as an uploaded one — the URL
  // takes the place of the data URL and everything downstream is unchanged.
  const activeTitleCardBackgroundLink = sectionTitleCardTargetId
    ? (url) => handleSectionTitleCardBackgroundChange(sectionTitleCardTargetId, url)
    : titleCardModalTarget === 'end'
      ? (url) => handleTitleCardBackgroundImageChange(url, 'end')
      : (url) => handleTitleCardBackgroundImageChange(url, 'start');
  const activeTitleCardBackgroundClear = sectionTitleCardTargetId
    ? () => handleSectionTitleCardBackgroundChange(sectionTitleCardTargetId, '')
    : titleCardModalTarget === 'end'
      ? () => handleTitleCardBackgroundImageChange('', 'end')
      : () => handleTitleCardBackgroundImageChange('', 'start');
  const activeTitleCardHeading = sectionTitleCardTargetId
    ? `Customize ${activeSectionForTitleCard?.title || 'Section'} Title Slide`
    : titleCardModalTarget === 'end'
      ? 'Customize End Title Card'
      : 'Customize Title Card';

  const handleDownloadScopedCssExample = () => {
    const example = `/* Scope styles to the slide deck container */\n` +
      `.slide-deck .slide-card {\n  border-radius: 32px;\n}\n\n` +
      `.slide-deck .slide-title {\n  letter-spacing: 0.05em;\n}\n\n` +
      `.slide-deck .slide-body ul {\n  margin-left: 1.5rem;\n}\n`;
    const blob = new Blob([example], { type: 'text/css' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'slide-scoped-example.css';
    link.click();
    URL.revokeObjectURL(url);
  };

  const displayDate = new Date(date + 'T00:00:00').toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  const shortDisplayDate = new Date(date + 'T00:00:00').toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });


  const slideAreaLightMode = lightMode || (typeof window !== 'undefined' && window.localStorage?.getItem('boppps-light-mode') === 'true');

  /**
   * The deck's content slides, named the way the outline names them.
   *
   * The settings page had been building its own "Apply to" list straight off
   * `slides`, reading a `label` those objects do not carry — so every option
   * fell through to "Slide 1", "Slide 2", and picking the right one meant
   * counting. These are the same three facts the outline row shows: the
   * deck-wide number, the slide's own name, and the section it sits in.
   *
   * Title cards are left out on purpose. They belong to the deck rather than
   * to a section's running order, they hold no per-slide background, and their
   * text lives in their own settings — so there is nothing on this page that
   * could act on one.
   */
  const configSlideOptions = useMemo(() => (
    (slides || [])
      .filter((slide) => slide?.slideId && slide.type === 'section')
      .map((slide) => {
        const deckNumber = deckSlideNumbers.get(slide.slideId) || null;
        const name = resolveSlideName(slideNames?.[slide.slideId], slide.slideId);
        const sectionTitle = (slide.section?.title || '').trim();
        return {
          slideId: slide.slideId,
          deckNumber,
          sectionId: slide.section?.id || '',
          sectionTitle,
          name,
          label: `${deckNumber ? `${deckNumber}. ` : ''}${name || sectionTitle || 'Untitled slide'}`
        };
      })
  ), [slides, slideNames, deckSlideNumbers]);

  const handleApplyBackgroundSelection = useCallback(({ target, background }) => {
    const normalizedBackground = normalizeBackgroundPersistenceValue(background);
    if (!normalizedBackground) return;

    if (target === 'all') {
      setBodyBackground(normalizedBackground);
      setSlideBackgrounds({});
      syncToSlides('bodyBackground', normalizedBackground);
      syncToSlides('slideBackgrounds', {});
      return;
    }

    setSlideBackgrounds((prev) => {
      const next = { ...prev, [target]: normalizedBackground };
      syncToSlides('slideBackgrounds', next);
      return next;
    });
  }, [syncToSlides]);

  /**
   * The settings page's handlers, each one also telling the deck.
   *
   * The page runs in its own LessonTemplate instance, so a plain `setState`
   * here changes a copy of the deck that nothing renders and no export reads.
   * These wrappers set the local state *and* put the new value on the bridge,
   * which is the difference between a control that works and one that only
   * looks like it does.
   *
   * The maps are rebuilt rather than updated functionally, because the value
   * has to be handed over as well as stored — a functional update knows the
   * next state only inside React.
   */
  const settingsSync = (setter, key) => (value) => {
    setter(value);
    syncToSlides(key, value);
  };

  const settingsHeaderDataChange = (field, value) => {
    const next = { ...headerData, [field]: value };
    setHeaderData(next);
    syncToSlides('headerData', next);
  };

  const settingsSlideMapChange = (setter, key, source) => (slideId, value) => {
    if (!slideId) return;
    const next = { ...source, [slideId]: value };
    setter(next);
    syncToSlides(key, next);
  };

  const settingsSlideDurationChange = (slideId, minutes) => {
    if (!slideId) return;
    const amount = Number(minutes);
    const next = { ...slideDurations };
    if (!Number.isFinite(amount) || amount <= 0) delete next[slideId];
    else next[slideId] = Math.round(amount);
    setSlideDurations(next);
    syncToSlides('slideDurations', next);
  };

  const settingsTitleCardUpdate = (updates) => {
    const next = { ...titleCardSettings, ...updates };
    setTitleCardSettings(next);
    syncToSlides('titleCardSettings', next);
  };

  const settingsEndTitleCardUpdate = (updates) => {
    const next = { ...endTitleCardSettings, ...updates };
    setEndTitleCardSettings(next);
    syncToSlides('endTitleCardSettings', next);
  };

  /** An uploaded card picture, read here so the bridge can carry the result. */
  const settingsTitleCardImage = (update) => (file) => {
    if (!file) {
      update({ image: '', imageCitation: '' });
      return;
    }
    const reader = new FileReader();
    reader.onload = (event) => update({ image: event.target?.result || '' });
    reader.readAsDataURL(file);
  };

  // If this is the settings-page instance, always render TemplateControlsPage.
  // Without this, the settings instance falls through to the full lesson UI
  // when activeAppTab !== 'template-settings', causing duplicate fixed toolbars.
  if (showSettingsPage) {
    return (
      <div
        className={`lesson-template-container min-h-screen ${slideAreaLightMode ? 'light-mode' : 'bg-gradient-to-br from-gray-900 to-gray-950'}`}
        style={slideAreaLightMode ? { backgroundColor: '#ffffff' } : undefined}
      >
        <TemplateControlsPage
          week={week}
          onWeekChange={settingsSync(setWeek, 'week')}
          date={date}
          onDateChange={settingsSync(setDate, 'date')}
          instructorName={headerData.instructorName}
          instructorEmail={headerData.instructorEmail}
          onHeaderDataChange={settingsHeaderDataChange}
          headerData={headerData}
          bodyLayout={bodyLayout}
          onBodyLayoutChange={setBodyLayout}
          bodyBackground={bodyBackground}
          slideBackgrounds={slideBackgrounds}
          onBodyBackgroundChange={(bg) => {
            setBodyBackground(bg);
            syncToSlides('bodyBackground', bg);
          }}
          onApplyBackgroundSelection={handleApplyBackgroundSelection}
          slideHeaderMode={slideHeaderMode}
          slideHeaderColor={slideHeaderColor}
          slideHeaderColors={slideHeaderColors}
          onSlideHeaderModeChange={(v) => { setSlideHeaderMode(v); syncToSlides('slideHeaderMode', v); }}
          onSlideHeaderColorChange={(v) => { setSlideHeaderColor(v); syncToSlides('slideHeaderColor', v); }}
          onSlideHeaderSectionColorChange={handleSlideHeaderColorChange}
          slideGlobalCss={slideGlobalCss}
          onSlideGlobalCssChange={(v) => { setSlideGlobalCss(v); syncToSlides('slideGlobalCss', v); }}
          titleCardSettings={titleCardSettings}
          onTitleCardUpdate={settingsTitleCardUpdate}
          onTitleCardImageChange={settingsTitleCardImage(settingsTitleCardUpdate)}
          endTitleCardSettings={endTitleCardSettings}
          onEndTitleCardUpdate={settingsEndTitleCardUpdate}
          onEndTitleCardImageChange={settingsTitleCardImage(settingsEndTitleCardUpdate)}
          slideDeckName={slideDeckName}
          onSlideDeckNameChange={settingsSync(setSlideDeckName, 'slideDeckName')}
          exportFileName={exportFileName}
          onExportFileNameChange={setExportFileName}
          htmlExportTitle={htmlExportTitle}
          onHtmlExportTitleChange={setHtmlExportTitle}
          exportVersion={exportVersion}
          isEditMode={isEditMode}
          onToggleEditMode={handleToggleEditMode}
          onOpenLogoSettings={() => setShowLogoSettings(true)}
          onSave={handleSave}
          onLoad={handleLoad}
          onExportPDF={handleExportPDF}
          onExportPPTX={handleExportPPTX}
          onExportHTML={handleExportHTML}
          onExportOptimizedHTML={handleExportOptimizedHTML}
          onExportSlimHTML={handleExportSlimHTML}
          onExportPathHTML={handleExportPathHTML}
          onExportSlimJson={handleSaveSlimJson}
          onExportStableJson={handleExportStableJson}
          onExportLlmTemplate={handleDownloadLlmTemplate}
          onDownloadScopedCssExample={handleDownloadScopedCssExample}
          showAdvancedExports={showAdvancedExports}
          setShowAdvancedExports={setShowAdvancedExports}
          sections={sections}
          hasUnsavedChanges={hasUnsavedChanges}
          lastSaved={lastSaved}
          slides={slides}
          slideOptions={configSlideOptions}
          /* Per-slide metadata. The outline shows all of this and the exporters
             carry it; until now the only way to set a name or subtitle was to
             click the slide's own header, and a duration could not be typed at
             all. */
          slideNames={slideNames}
          slideSubtitles={slideSubtitles}
          slideDurations={slideDurations}
          slideVisibility={slideVisibility}
          onUpdateSlideName={settingsSlideMapChange(setSlideNames, 'slideNames', slideNames)}
          onUpdateSlideSubtitle={settingsSlideMapChange(setSlideSubtitles, 'slideSubtitles', slideSubtitles)}
          onUpdateSlideDuration={settingsSlideDurationChange}
          onSetSlideActive={settingsSlideMapChange(setSlideVisibility, 'slideVisibility', slideVisibility)}
          resolveSlideName={resolveSlideName}
          /* The furniture along the bottom of every slide, and the two design
             switches. All of it lived only in the Tools dropdown, which is not
             where anyone looks for a deck-wide setting. Same state, so the two
             places cannot disagree. */
          slideNumbersEnabled={slideNumbersEnabled}
          onSlideNumbersEnabledChange={settingsSync(setSlideNumbersEnabled, 'slideNumbersEnabled')}
          deckNameBadgeEnabled={deckNameBadgeEnabled}
          onDeckNameBadgeEnabledChange={settingsSync(setDeckNameBadgeEnabled, 'deckNameBadgeEnabled')}
          deckNameOnContentSlides={deckNameOnContentSlides}
          onDeckNameOnContentSlidesChange={settingsSync(setDeckNameOnContentSlides, 'deckNameOnContentSlides')}
          slideDesignMode={slideDesignMode}
          onSlideDesignModeChange={settingsSync(setSlideDesignMode, 'slideDesignMode')}
          showAspectGuides={showAspectGuides}
          onShowAspectGuidesChange={settingsSync(setShowAspectGuides, 'showAspectGuides')}
          /* Background images can be linked rather than embedded, which is how
             a deck stays small. The picker is the shared one, so the settings
             page offers exactly what the slide panel does. */
          imageBackgroundPresets={IMAGE_BACKGROUND_PRESET_OPTIONS}
          getBackgroundSelectionState={getBackgroundSelectionState}
          titleCardBackground={titleCardBackground}
          BACKGROUND_PRESETS={BACKGROUND_PRESETS}
          getBackgroundStyles={getBackgroundStyles}
          lightMode={slideAreaLightMode}
        />
        {showLogoSettings && (
          <SchoolLogoSettings onClose={() => setShowLogoSettings(false)} />
        )}
      </div>
    );
  }

  return (
    <div
      className={`lesson-template-container min-h-screen ${slideAreaLightMode ? 'light-mode' : 'bg-gradient-to-br from-gray-900 to-gray-950'}${isToolbarDrawerOpen ? ' toolbar-open' : ''}${isNavDrawerOpen ? ' sections-open' : ''}`}
      style={slideAreaLightMode ? { background: 'linear-gradient(to bottom right, #f3f4f6, #e5e7eb)' } : undefined}
    >
      <SaveIndicator {...saveIndicator} />

      {/* Shown for as long as an export runs, rather than for three seconds.
          The bar fills once the page count is known; before that it is a
          moving stripe, because a job whose length is not yet known should not
          pretend to be at 0%. */}
      {exportProgress && (
        <div
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[10001] w-[min(28rem,90vw)] rounded-xl border border-slate-700 bg-slate-900/95 px-4 py-3 shadow-2xl no-print"
          role="status"
          aria-live="polite"
        >
          <div className="flex items-center justify-between gap-3 mb-2">
            <span className="text-sm font-semibold text-white">
              {exportProgress.icon || '📄'} {exportProgress.title || 'Exporting'}
            </span>
            <span className="text-xs text-slate-300">
              {exportProgress.total
                ? `${Math.round((exportProgress.done / exportProgress.total) * 100)}%`
                : ''}
            </span>
          </div>
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-700">
            <div
              className={`h-full rounded-full bg-blue-500 ${exportProgress.total ? 'transition-[width] duration-150' : 'animate-pulse w-1/3'}`}
              style={exportProgress.total
                ? { width: `${Math.max(2, Math.round((exportProgress.done / exportProgress.total) * 100))}%` }
                : undefined}
            />
          </div>
          <p className="mt-2 text-xs text-slate-300">{exportProgress.label}</p>
        </div>
      )}
      <ModeToast message={modeToast.message} isEdit={modeToast.isEdit} visible={modeToast.visible} />
      <HotkeysModal isOpen={isHotkeysOpen} onClose={() => setIsHotkeysOpen(false)} />
      <CreateSectionModal
        isOpen={Boolean(createSectionRequest && createSectionPreview)}
        afterSlide={createSectionPreview?.afterSlide}
        fromSectionTitle={createSectionPreview?.fromSectionTitle}
        trailingSlides={createSectionPreview?.trailingSlides || []}
        onCreate={handleCreateSectionAfterSlide}
        onClose={closeCreateSectionModal}
      />

      <EmptiedSectionPrompt
        isOpen={!!emptiedSectionPrompt}
        sectionTitle={emptiedSectionPrompt?.sectionTitle}
        onKeepBlank={keepEmptiedSection}
        onDeleteSection={() => {
          const sectionId = emptiedSectionPrompt?.sectionId;
          setEmptiedSectionPrompt(null);
          if (sectionId) handleDeleteSection(sectionId);
        }}
        onClose={() => setEmptiedSectionPrompt(null)}
      />
      <SectionReorderModal
        isOpen={isSectionReorderOpen}
        sections={sections}
        onMove={moveSectionByOffset}
        onClose={() => setIsSectionReorderOpen(false)}
        labelFor={(section) => getSectionHeaderLabel(section, sectionNumberMap)}
      />
      <SlideReorderModal
        isOpen={Boolean(slideReorderSectionId)}
        sectionLabel={slideReorderSection
          ? getSectionHeaderLabel(slideReorderSection, sectionNumberMap)
          : ''}
        slides={slideReorderSlides}
        onMove={handleSlideReorderMove}
        onClose={() => setSlideReorderSectionId(null)}
      />

      {/* Left Toolbar Drawer */}
      <ToolbarDrawer
        isOpen={isToolbarDrawerOpen}
        onToggle={() => setIsToolbarDrawerOpen(prev => !prev)}
        elevated={isFullScreenPreview}
        hideToggle={isFullScreenPreview && isFullScreenChromeHidden}
        topOffset={isFullScreenPreview ? '64px' : (isHeaderExpanded ? `calc(${tabNavHeight}px + 114px)` : `calc(${tabNavHeight}px + 56px)`)}
        onAddContent={handleAddContent}
        onApplyPresetLayout={(presetKey) => {
          const targetSectionId = slides[currentSlideIndex]?.section?.id || defaultSection || sections[0]?.id;
          if (!targetSectionId) return;
          handleAddContent(targetSectionId, `preset-${presetKey}`, { source: 'toolbar' });
        }}
        onOpenPresetColumnsModal={handleOpenPresetColumnsModal}
        onOpenInteractiveTools={() => setShowInteractiveTools(true)}
        currentSectionId={slides[currentSlideIndex]?.section?.id || defaultSection || sections[0]?.id}
        isEditMode={isEditMode}
        lightMode={slideAreaLightMode}
      />

      {/* Interactive Content Builder Modal */}
      <InteractiveContentTools
        isVisible={showInteractiveTools}
        onClose={() => setShowInteractiveTools(false)}
        onInsertContent={(content, metadata) => {
          const activeSlide = slides[currentSlideIndex];
          const targetSectionId = activeSlide?.section?.id || defaultSection || sections[0]?.id;
          const targetSlideId = activeSlide?.section?.id === targetSectionId ? activeSlide?.slideId : null;

          if (!targetSectionId) {
            setShowInteractiveTools(false);
            return;
          }

          const newBlock = metadata?.type === 'image_drawover'
            ? {
                id: generateId(),
                type: 'image_drawover',
                content,
                drawoverData: metadata.drawoverData || null,
                backgroundImage: metadata.backgroundImage || null,
                backgroundOpacity: metadata.backgroundOpacity ?? 100,
              }
            : {
                id: generateId(),
                type: 'html',
                content,
                language: 'html',
                mode: 'preview',
                options: { softWrap: false, showLineNumbers: false, allowScripts: false },
              };

          setSections(prevSections =>
            prevSections.map(section => {
              if (section.id !== targetSectionId) return section;
              const blocks = [...section.blocks];

              if (!targetSlideId) {
                blocks.push(newBlock);
                return { ...section, blocks };
              }

              const sectionSlides = splitSectionSlides(section, { includeEmpty: true, includeMeta: true });
              const matchedSlide = sectionSlides.find((slide) => slide.slideId === targetSlideId);
              if (matchedSlide?.blocks?.length) {
                const lastBlockId = matchedSlide.blocks[matchedSlide.blocks.length - 1]?.id;
                const index = blocks.findIndex((block) => block.id === lastBlockId);
                if (index !== -1) {
                  blocks.splice(index + 1, 0, newBlock);
                } else {
                  blocks.push(newBlock);
                }
              } else {
                const dividerIndex = blocks.findIndex((block) => block?.type === 'divider'
                  && block?.content?.isSlideBreak
                  && (block?.content?.slideId === targetSlideId || block?.slideId === targetSlideId));
                if (dividerIndex !== -1) {
                  blocks.splice(dividerIndex + 1, 0, newBlock);
                } else {
                  blocks.push(newBlock);
                }
              }

              return { ...section, blocks };
            })
          );

          showSaveIndicator(metadata?.type === 'image_drawover' ? '🎨 Image draw-over block added' : '🎨 Drawing inserted into slide');
          setShowInteractiveTools(false);
        }}
      />

      <TemplateManager
        isOpen={showTemplateManager}
        onClose={() => setShowTemplateManager(false)}
        getCurrentLessonData={getCurrentTemplateData}
        getDefaultLessonData={getDefaultTemplateData}
        onApplyTemplate={applyLoadedData}
        onDownloadPresentationJson={handleSave}
      />

      {/* A save that did happen. Under the deck, in the middle, where the eye
          already is — not in the corner opposite the button. */}
      <SaveConfirmation
        save={saveConfirmation}
        onDismiss={handleDismissSaveConfirmation}
      />

      {/* A save that did not happen, and the ways out of it. */}
      {saveProblem && (
        <SaveProblemDialog
          problem={saveProblem.info}
          busy={saveProblemBusy}
          onChooseFolder={handleSaveProblemChooseFolder}
          onSaveAs={handleSaveProblemSaveAs}
          onDownload={handleSaveProblemDownload}
          onDismiss={() => setSaveProblem(null)}
        />
      )}

      {/* Save is about to replace a numbered version that already exists. */}
      {overwritePrompt && (
        <OverwriteVersionDialog
          fileName={overwritePrompt.fileName}
          version={overwritePrompt.version}
          where={overwritePrompt.where}
          onConfirm={handleConfirmOverwrite}
          onCancel={handleCancelOverwrite}
        />
      )}

      {/* A deck that would not open. Rendered over the editor, which still has
          the previous deck in it — nothing has been replaced or cleared. */}
      {deckLoadError && (
        <PresentationLoadErrorScreen
          error={deckLoadError}
          variant="modal"
          onRetry={() => { setDeckLoadError(null); handleOpenPresentation(); }}
          onPasteInstead={() => { setDeckLoadError(null); setPasteJsonError(''); setShowJsonInput(true); }}
          onDismiss={() => setDeckLoadError(null)}
        />
      )}

      {/* Paste JSON Modal */}
      {showJsonInput && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl">
            <div className="bg-gray-50 border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Paste JSON</h2>
                <p className="text-sm text-gray-500 mt-1">
                  Paste a deck an assistant wrote and it opens here — no need to save it as a file first.
                  This replaces the deck you have open.
                </p>
              </div>
              <button
                type="button"
                onClick={() => { setShowJsonInput(false); setJsonInput(''); setPasteJsonError(''); }}
                className="p-2 rounded-lg hover:bg-gray-200 text-gray-600"
                aria-label="Close paste JSON modal"
              >
                <X size={18} />
              </button>
            </div>
            <div className="p-6">
              <textarea
                className="w-full h-64 p-3 border border-gray-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={jsonInput}
                onChange={(e) => { setJsonInput(e.target.value); if (pasteJsonError) setPasteJsonError(''); }}
                placeholder='{"format":"boppps_lesson","version":"4.0.0","sections":{...}}'
                aria-label="Deck JSON"
              />
              {pasteJsonError && (
                <p role="alert" className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  {pasteJsonError}
                </p>
              )}
            </div>
            <div className="bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
              <button
                onClick={() => { setShowJsonInput(false); setJsonInput(''); setPasteJsonError(''); }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleJsonPasteLoad}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Apply to deck
              </button>
            </div>
          </div>
        </div>
      )}

      <TransferContentModal
        open={showSelectionModal}
        mode={selectionMode}
        onModeChange={setSelectionMode}
        onClose={() => { setShowSelectionModal(false); resetSelectionState(); }}
        exportType={selectionType === 'slides' ? 'slides' : 'blocks'}
        onExportTypeChange={setSelectionType}
        deckSections={transferDeckSections}
        deckEdges={transferDeckEdges}
        renderThumbnail={renderTransferThumbnail}
        selectedBlockIds={selectedBlockIds}
        onToggleBlock={toggleBlockSelection}
        onSetBlocks={setBlockSelection}
        selectedSlideIds={selectedSlideIds}
        onToggleSlide={toggleSlideSelection}
        onSetSlides={setSlideSelection}
        collapsedSections={collapsedExportSections}
        onToggleSectionCollapse={toggleExportSectionCollapse}
        getBlockPreview={getBlockPreview}
        onExport={handleExportSelection}
        onChooseFile={() => openFileInput(selectionFileInputRef.current)}
        fileInput={(
          <input
            ref={selectionFileInputRef}
            type="file"
            accept={JSON_ACCEPT}
            className={HIDDEN_FILE_INPUT_CLASS}
            onChange={handleSelectionFileChange}
          />
        )}
        importData={selectionImportData}
        importSlides={transferImportSlides}
        importSelectionSlideIds={importSelectionSlideIds}
        onToggleImportSlide={toggleImportSlideSelection}
        onSetImportSlides={setImportSlideSelection}
        targetSectionId={selectionTargetSection || sections[0]?.id || ''}
        onTargetSectionChange={setSelectionTargetSection}
        placementMode={selectionPlacementMode}
        onPlacementModeChange={setSelectionPlacementMode}
        placementSlideId={selectionPlacementSlideId}
        onPlacementSlideChange={setSelectionPlacementSlideId}
        blockTargetSlideId={blockTargetSlideId}
        onBlockTargetChange={setBlockTargetSlideId}
        onImport={applySelectionImport}
      />

      <AutoSaveRecoveryModal
        isOpen={showAutoSaveRecovery}
        onRecover={handleRecoverAutoSave}
        onDiscard={handleDiscardAutoSave}
        timestamp={autoSaveData?.timestamp}
      />

      {/* Template Controls have been moved to the Settings tab in the top navigation */}

      <ControlPanel
        isEditMode={isEditMode}
        onToggleEditMode={handleToggleEditMode}
        onExportPDF={handleExportPDF}
        onExportPPTX={handleExportPPTX}
        onExportHTML={handleExportHTML}
        onExportOptimizedHTML={handleExportOptimizedHTML}
        onExportSlimHTML={handleExportSlimHTML}
        onExportPathHTML={handleExportPathHTML}
        onExportSlimJson={handleSaveSlimJson}
        onSave={handleSave}
        onLoad={handleLoad}
        onAddSection={handleAddSection}
        onAddContent={(type) => handleAddContent(defaultSection, type)}
        week={week}
        onWeekChange={setWeek}
        date={date}
        onDateChange={setDate}
        isOpen={isControlPanelOpen}
        onToggle={() => setIsControlPanelOpen(!isControlPanelOpen)}
        hasUnsavedChanges={hasUnsavedChanges}
        lastSaved={lastSaved}
        sections={sections}
        defaultSection={defaultSection}
        onDefaultSectionChange={setDefaultSection}
        onOpenLogoSettings={() => setShowLogoSettings(true)}
        instructorName={headerData.instructorName}
        instructorEmail={headerData.instructorEmail}
        onHeaderDataChange={handleHeaderDataChange}
        headerData={headerData}
        bodyLayout={bodyLayout}
        onBodyLayoutChange={setBodyLayout}
        bodyBackground={bodyBackground}
        slideBackgrounds={slideBackgrounds}
        onBodyBackgroundChange={setBodyBackground}
        onApplyBackgroundSelection={handleApplyBackgroundSelection}
        slideHeaderMode={slideHeaderMode}
        slideHeaderColor={slideHeaderColor}
        slideHeaderColors={slideHeaderColors}
        onSlideHeaderModeChange={setSlideHeaderMode}
        onSlideHeaderColorChange={setSlideHeaderColor}
        onSlideHeaderSectionColorChange={handleSlideHeaderColorChange}
        slideGlobalCss={slideGlobalCss}
        onSlideGlobalCssChange={setSlideGlobalCss}
        titleCardSettings={titleCardSettings}
        onTitleCardUpdate={handleTitleCardUpdate}
        onTitleCardImageChange={handleTitleCardImageChange}
        endTitleCardSettings={endTitleCardSettings}
        onEndTitleCardUpdate={handleEndTitleCardUpdate}
        onEndTitleCardImageChange={handleEndTitleCardImageChange}
        slideDeckName={slideDeckName}
        onSlideDeckNameChange={setSlideDeckName}
        exportFileName={exportFileName}
        onExportFileNameChange={setExportFileName}
        htmlExportTitle={htmlExportTitle}
        onHtmlExportTitleChange={setHtmlExportTitle}
        exportVersion={exportVersion}
        onDownloadScopedCssExample={handleDownloadScopedCssExample}
        onExportLlmTemplate={handleDownloadLlmTemplate}
        onExportStableJson={handleExportStableJson}
        showAdvancedExports={showAdvancedExports}
        setShowAdvancedExports={setShowAdvancedExports}
        sectionNumberMap={sectionNumberMap}
      />

      <div
        className={`fixed inset-y-0 left-0 ${isFullScreenPreview ? 'z-[10000]' : 'z-30'} no-print pointer-events-none`}
        style={{ top: isFullScreenPreview ? '64px' : (isHeaderExpanded ? `calc(${tabNavHeight}px + 114px)` : `calc(${tabNavHeight}px + 56px)`) }}
        aria-hidden={!isNavDrawerOpen}
      >
        <aside
          ref={navDrawerRef}
          className={`pointer-events-auto relative h-full w-full max-w-[320px] md:w-[300px] shadow-2xl border-r transform transition-transform duration-300 flex flex-col ${lightMode ? 'bg-white border-gray-200' : 'bg-gray-900 border-gray-700'} ${isNavDrawerOpen ? 'translate-x-0' : '-translate-x-full'}`}
          role="complementary"
          aria-label="Sections navigation"
          tabIndex={-1}
        >
          {/* Compact header: title + deck name on one row, then the filter and
              the three controls. The keyboard hint moved into the close
              button's tooltip so the panel spends its height on slides. */}
          <div className={`px-3 py-2 border-b ${lightMode ? 'border-gray-200 bg-white' : 'border-gray-700 bg-gray-900'}`}>
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <h2 className={`text-sm font-semibold leading-tight ${lightMode ? 'text-gray-900' : 'text-white'}`}>Sections</h2>
                <p className={`truncate text-[11px] leading-tight ${lightMode ? 'text-gray-500' : 'text-gray-400'}`} title={drawerSubtitle}>
                  {drawerSubtitle}
                </p>
              </div>
              <button
                type="button"
                onClick={closeNavDrawer}
                className={`shrink-0 p-1.5 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${lightMode ? 'hover:bg-gray-100 text-gray-500' : 'hover:bg-gray-800 text-gray-400'}`}
                aria-label="Close navigation panel"
                title="Close panel  ( [ )"
              >
                <X size={16} />
              </button>
            </div>
            {/* Straight to a slide by its number. A form rather than a bare
                button so Return submits it — nobody types a number and then
                reaches for the mouse. Only in slides mode: the accordion
                lists no slides to jump to. */}
            {bodyLayout === 'slides' && slides.length > 0 && (
              <form
                className="mt-2 flex items-center gap-1.5"
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSlideJump();
                }}
              >
                <label
                  htmlFor="drawer-slide-jump"
                  className={`shrink-0 text-[11px] font-semibold ${lightMode ? 'text-gray-500' : 'text-gray-400'}`}
                >
                  Go to
                </label>
                <input
                  id="drawer-slide-jump"
                  type="text"
                  inputMode="numeric"
                  autoComplete="off"
                  value={drawerSlideJump}
                  // Digits only, so the box can never hold something that is
                  // not a slide number in the first place.
                  onChange={(e) => setDrawerSlideJump(sanitizeSlideJumpInput(e.target.value))}
                  onFocus={(e) => e.target.select()}
                  placeholder={`1–${slides.length}`}
                  aria-label={`Slide number, 1 to ${slides.length}`}
                  aria-invalid={slideJump.isOutOfRange || undefined}
                  className={`min-w-0 flex-1 rounded-lg border py-1.5 px-2 text-xs shadow-sm focus:outline-none focus:ring-2 ${
                    slideJump.isOutOfRange
                      ? 'border-rose-500/60 focus:ring-rose-500'
                      : 'focus:ring-blue-500 ' + (lightMode ? 'border-gray-200' : 'border-gray-700')
                  } ${
                    lightMode
                      ? 'bg-gray-50 text-gray-900 placeholder-gray-400'
                      : 'bg-gray-800 text-gray-200 placeholder-gray-500'
                  }`}
                />
                <button
                  type="submit"
                  disabled={!slideJump.isValid}
                  aria-label={slideJump.isValid ? `Go to slide ${slideJump.number}` : 'Go to slide'}
                  title={
                    slideJump.isOutOfRange
                      ? `This deck has ${slides.length} slide${slides.length === 1 ? '' : 's'}`
                      : slideJump.isValid
                        ? `Go to slide ${slideJump.number}`
                        : 'Type a slide number, then press Return'
                  }
                  className={`inline-flex shrink-0 items-center rounded-lg border p-1.5 transition-colors ${
                    slideJump.isValid
                      ? lightMode
                        ? 'border-blue-200 bg-blue-50 text-blue-600 hover:bg-blue-100'
                        : 'border-blue-500/40 bg-blue-900/40 text-blue-300 hover:bg-blue-900/60'
                      : lightMode
                        ? 'border-gray-200 bg-gray-50 text-gray-300 cursor-not-allowed'
                        : 'border-gray-700 bg-gray-800 text-gray-600 cursor-not-allowed'
                  }`}
                >
                  <Search size={13} />
                </button>
              </form>
            )}
            <div className="mt-2 flex items-center gap-1.5">
              <div className="relative min-w-0 flex-1">
                <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={drawerFilterText}
                  onChange={(e) => setDrawerFilterText(e.target.value)}
                  placeholder="Filter"
                  className={`w-full rounded-lg border py-1.5 pl-8 pr-2 text-xs shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    lightMode
                      ? 'border-gray-200 bg-gray-50 text-gray-900 placeholder-gray-400'
                      : 'border-gray-700 bg-gray-800 text-gray-200 placeholder-gray-500'
                  }`}
                />
              </div>
              <button
                type="button"
                onClick={() => setDrawerIssuesOnly((prev) => !prev)}
                aria-pressed={drawerIssuesOnly}
                title={drawerIssuesOnly ? 'Showing only slides that need content' : 'Show only slides that need content'}
                className={`inline-flex shrink-0 items-center gap-1 rounded-lg border px-2 py-1.5 text-[11px] font-semibold transition-colors ${
                  drawerIssuesOnly
                    ? 'border-rose-500/30 bg-rose-900/30 text-rose-400'
                    : lightMode
                      ? 'border-gray-200 bg-gray-50 text-gray-600 hover:bg-gray-100'
                      : 'border-gray-700 bg-gray-800 text-gray-400 hover:bg-gray-700'
                }`}
              >
                <AlertTriangle size={12} />
                {drawerIssueCount > 0 && <span>{drawerIssueCount}</span>}
              </button>
              <button
                type="button"
                onClick={handleDrawerExpandAll}
                title="Expand all sections"
                aria-label="Expand all sections"
                className={`inline-flex shrink-0 items-center rounded-lg border p-1.5 ${
                  lightMode
                    ? 'border-gray-200 bg-gray-50 text-gray-600 hover:bg-gray-100'
                    : 'border-gray-700 bg-gray-800 text-gray-400 hover:bg-gray-700'
                }`}
              >
                <ChevronDown size={13} />
              </button>
              <button
                type="button"
                onClick={handleDrawerCollapseAll}
                title="Collapse all sections"
                aria-label="Collapse all sections"
                className={`inline-flex shrink-0 items-center rounded-lg border p-1.5 ${
                  lightMode
                    ? 'border-gray-200 bg-gray-50 text-gray-600 hover:bg-gray-100'
                    : 'border-gray-700 bg-gray-800 text-gray-400 hover:bg-gray-700'
                }`}
              >
                <ChevronUp size={13} />
              </button>
              {sections.length > 1 && (
                <button
                  type="button"
                  onClick={() => setIsSectionReorderOpen(true)}
                  title="Reorder sections"
                  aria-label="Reorder sections"
                  className={`inline-flex shrink-0 items-center rounded-lg border p-1.5 ${
                    lightMode
                      ? 'border-gray-200 bg-gray-50 text-gray-600 hover:bg-gray-100'
                      : 'border-gray-700 bg-gray-800 text-gray-400 hover:bg-gray-700'
                  }`}
                >
                  <List size={13} />
                </button>
              )}
            </div>
          </div>

          {/* What is picked out, and the way back. Without this a selection
              made three sections up is invisible the moment it scrolls away,
              and the next drag carries slides the author has forgotten
              about. */}
          {pickedSlideIds.length > 0 && (
            <div className={`flex items-center gap-2 border-b px-3 py-1.5 text-[11px] ${
              lightMode
                ? 'border-violet-200 bg-violet-50 text-violet-800'
                : 'border-violet-900/60 bg-violet-950/40 text-violet-200'
            }`}>
              <span className="font-semibold">
                {pickedSlideIds.length === 1 ? '1 slide picked' : `${pickedSlideIds.length} slides picked`}
              </span>
              <span className={lightMode ? 'text-violet-600' : 'text-violet-400'}>
                drag any one to move them together
              </span>
              <button
                type="button"
                onClick={clearSlidePicks}
                className={`ml-auto shrink-0 rounded-md px-1.5 py-0.5 font-semibold ${
                  lightMode ? 'hover:bg-violet-100' : 'hover:bg-violet-900/60'
                }`}
              >
                Clear
              </button>
            </div>
          )}

          <div
            ref={drawerScrollRef}
            onDragOver={handleDrawerDragOver}
            onScroll={handleDrawerScroll}
            tabIndex={-1}
            className={`flex-1 overflow-y-auto px-2.5 py-2.5 space-y-2 scrollbar-thin focus:outline-none ${lightMode ? 'bg-white' : 'bg-gray-900'}`}
          >
            <ul className="space-y-2">
              {navigationItems.map((item) => {
                const sectionSlides = bodyLayout === 'slides' ? sectionSlidesMap[item.id] || [] : [];
                const contentSlides = sectionSlides.filter((sectionSlide) => !sectionSlide.isSectionTitleCard);
                // How much of this section reaches an exported file. The
                // section's own title card has its own switch a row above and
                // is not counted here.
                const sectionSlideIds = contentSlides
                  .map((sectionSlide) => sectionSlide.slideId)
                  .filter(Boolean);
                const sectionActiveCount = sectionSlideIds
                  .filter((slideId) => slideVisibility?.[slideId] !== false).length;
                const sectionAllActive = sectionSlideIds.length > 0
                  && sectionActiveCount === sectionSlideIds.length;
                const hasSlides = sectionSlides.length > 0;
                const isCollapsed = drawerCollapsedSections.includes(item.id);
                const isActive = activeSectionId === item.id || (item.id === 'title-card' && activeSectionId === 'title-card');
                const targetSlideIndex = hasSlides
                  ? sectionSlides[0]?.globalIndex ?? item.slideIndex
                  : item.slideIndex;
                const isTitleCardNavItem = item.type === 'title-card';
                const titleCardEnabled = titleCardSettings?.enabled;
                const isTitleCardDisabled = isTitleCardNavItem && !titleCardEnabled;
                const isEndTitleNavItem = item.type === 'end-title-card';
                const endTitleEnabled = endTitleCardSettings?.enabled;
                const isEndTitleDisabled = isEndTitleNavItem && !endTitleEnabled;
                const isNavItemDisabled = isTitleCardDisabled || isEndTitleDisabled;
                const itemLabel = isNavItemDisabled ? `${item.label} (Off)` : item.label;
                const titleCardPreviewSettings = isTitleCardNavItem
                  ? titleCardSettings
                  : (isEndTitleNavItem ? endTitleCardSettings : null);
                // A title card holds a deck position like any other slide, and
                // the drawer is where an author counts slides.
                const navItemDeckNumber = isTitleCardNavItem
                  ? deckSlideNumbers.get('title-card')
                  : isEndTitleNavItem
                    ? deckSlideNumbers.get('end-title-card')
                    : null;
                const titleCardThumbStyles = titleCardPreviewSettings
                  ? getTitleCardPreviewStyles(titleCardPreviewSettings, titleCardBackground)
                  : null;
                const normalizedItemLabel = itemLabel.toLowerCase();
                const sectionLabelMatches = !normalizedDrawerFilter || normalizedItemLabel.includes(normalizedDrawerFilter);
                const sectionSlidesWithMeta = sectionSlides.map((sectionSlide) => {
                  const slideData = slideById[sectionSlide.slideId];
                  const primaryBlock = sectionSlide.isSectionTitleCard
                    ? null
                    : (slideData?.blocks || []).find((block) => block?.type && block.type !== 'divider' && !isEmptyBlock(block));
                  const typeLabel = sectionSlide.isSectionTitleCard
                    ? 'TITLE SLIDE'
                    : formatBlockTypeLabel(primaryBlock?.type);
                  const hasIssue = !!drawerIssueMap[sectionSlide.slideId];
                  const matchesFilter = sectionLabelMatches
                    || (!normalizedDrawerFilter
                      ? true
                      : sectionSlide.label.toLowerCase().includes(normalizedDrawerFilter)
                        || typeLabel.toLowerCase().includes(normalizedDrawerFilter));
                  return {
                    ...sectionSlide,
                    typeLabel,
                    hasIssue,
                    matchesFilter
                  };
                });
                const visibleSectionSlides = sectionSlidesWithMeta.filter((sectionSlide) => {
                  if (!sectionSlide.matchesFilter) return false;
                  if (drawerIssuesOnly) return sectionSlide.hasIssue;
                  return true;
                });
                const hasIssueSlides = sectionSlidesWithMeta.some((sectionSlide) => sectionSlide.hasIssue);
                const itemHasIssue = hasIssueSlides || !!drawerIssueMap[item.id];
                const shouldShowItem = (!drawerIssuesOnly || itemHasIssue)
                  && (!normalizedDrawerFilter || sectionLabelMatches || visibleSectionSlides.length > 0);
                if (!shouldShowItem) return null;
                const visibleContentSlides = visibleSectionSlides.filter((sectionSlide) => !sectionSlide.isSectionTitleCard);
                const hasVisibleSlides = visibleSectionSlides.length > 0;
                const hasVisibleMultipleSlides = visibleContentSlides.length > 1;
                const isExpanded = hasVisibleSlides && !isCollapsed && (hasVisibleMultipleSlides
                  ? drawerExpandedSections.includes(item.id)
                  : true);
                const hasSectionTitleCard = sectionSlides.some((sectionSlide) => sectionSlide.isSectionTitleCard);
                const isSectionNavItem = item.type === 'section';
                // Read the toggle straight off section state so the checkbox
                // reflects the author's choice before the deck rebuilds.
                const sectionTitleCardEnabled = isSectionNavItem
                  && Boolean(sections.find((section) => section.id === item.id)?.sectionTitleCard?.enabled);
                const allowSectionDrag = isEditMode && item.type === 'section';
                // Reordering is not gated on edit mode: "New Section" at the
                // foot of this drawer is not either, and a control the author
                // cannot find from preview — where the drawer mostly lives —
                // may as well not exist. The drag handle stays edit-only.
                const allowSectionReorder = item.type === 'section' && sections.length > 1;
                // Position among the real sections, so the move buttons can be
                // disabled at the ends. Title cards are nav items but not
                // sections, so they are absent here and never reorderable.
                const sectionOrderIndex = item.type === 'section'
                  ? sections.findIndex((section) => section.id === item.id)
                  : -1;
                const canMoveSectionUp = allowSectionReorder && sectionOrderIndex > 0;
                const canMoveSectionDown = allowSectionReorder
                  && sectionOrderIndex > -1
                  && sectionOrderIndex < sections.length - 1;
                const isDraggingThisSection = draggingSection?.sectionId === item.id;
                const sectionDropBefore = sectionDropTarget?.sectionId === item.id && sectionDropTarget?.position === 'before';
                const sectionDropAfter = sectionDropTarget?.sectionId === item.id && sectionDropTarget?.position === 'after';
                const headerDropTarget = isEditMode
                  && slideDropTarget?.sectionId === item.id
                  && slideDropTarget?.slideIndex === 0
                  && slideDropTarget?.position === 'before';
                const headerAppendTarget = isEditMode
                  && slideDropTarget?.sectionId === item.id
                  && slideDropTarget?.slideIndex === contentSlides.length
                  && slideDropTarget?.position === 'after';
                const isSectionDropTarget = slideDropTarget?.sectionId === item.id;

                return (
                  <li
                    key={item.id}
                    className={`rounded-xl border border-gray-700 bg-gray-800 shadow-sm ${isSectionDropTarget ? 'ring-2 ring-emerald-300' : ''} ${isNavItemDisabled ? 'opacity-80' : ''} ${isDraggingThisSection ? 'ring-2 ring-blue-300' : ''}`}
                  >
                    {allowSectionDrag && (
                      <div
                        className={`h-2 ${sectionDropBefore ? 'bg-blue-200' : 'bg-transparent'} rounded-t-xl`}
                        onDragOver={handleSectionDragOver(item.id, 'before')}
                        onDrop={handleSectionDrop(item.id, 'before')}
                      />
                    )}
                    {isEditMode && hasSlides && (
                      <div
                        className={`h-2 ${headerDropTarget ? 'bg-emerald-200' : 'bg-transparent'} rounded-t-xl`}
                        onDragOver={handleSlideDragOver(item.id, 0, 'before')}
                        onDrop={handleSlideDrop(item.id, 0, 'before')}
                      />
                    )}
                    <div
                      className={`px-3 py-2.5 ${isActive ? 'bg-blue-900/30' : 'bg-gray-800'}`}
                      onDragOver={isEditMode ? handleSlideDragOver(item.id, contentSlides.length, 'after') : undefined}
                      onDrop={isEditMode ? handleSlideDrop(item.id, Math.max(contentSlides.length - 1, 0), 'after') : undefined}
                    >
                    {/* Controls and the small labels share one row; the title
                        gets a row to itself below. Side by side, the controls
                        left the title about 125px in a 300px drawer, so any
                        real section name either wrapped to four lines or was
                        cut off. Full width roughly doubles what fits. */}
                    <div className="flex items-center gap-1">
                      {allowSectionDrag && (
                        <button
                          type="button"
                          draggable
                          onDragStart={handleSectionDragStart(item.id)}
                          onDragEnd={handleSectionDragEnd}
                          onClick={(e) => e.stopPropagation()}
                          className="mt-1 flex items-center justify-center w-7 h-7 rounded-md text-gray-500 hover:text-blue-400 hover:bg-blue-900/30"
                          aria-label={`Drag section ${item.label}`}
                        >
                          <GripVertical size={14} />
                        </button>
                      )}
                      {/* One line, clipped rather than wrapped: these are
                          secondary labels, and letting them wrap was half of
                          why a header grew — "Needs attention" alone took a
                          line of its own on a narrow drawer. */}
                      <div className="drawer-section-chips min-w-0 flex-1 text-sky-500">
                        <span className="truncate">{item.type === 'section' ? item.id.replace(/-/g, ' ') : item.type?.replace(/-/g, ' ') || 'section'}</span>
                        {hasSlides && (
                          <span className="shrink-0 rounded-full bg-gray-700 px-1.5 font-semibold text-gray-400">
                            {contentSlides.length}
                          </span>
                        )}
                        {itemHasIssue && (
                          <span className="shrink-0 rounded-full bg-rose-900/30 px-1.5 font-semibold text-rose-400">
                            Attention
                          </span>
                        )}
                      </div>

                      {hasVisibleSlides && (
                        <button
                          type="button"
                          onClick={() => handleDrawerSectionToggle(item.id, !isExpanded)}
                          aria-expanded={isExpanded}
                          className="p-2 rounded-md text-gray-400 hover:text-blue-400 hover:bg-blue-900/30"
                          aria-label={`${isExpanded ? 'Collapse' : 'Expand'} slides in ${item.label}`}
                        >
                          {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                        </button>
                      )}

                      {/* The same escape hatch the deck's sections have, one
                          level down: a section with more than a couple of
                          slides is easier to put in order as a list than as a
                          drag inside a 300px column. */}
                      {isSectionNavItem && contentSlides.length > 1 && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setSlideReorderSectionId(item.id);
                          }}
                          className="p-2 rounded-md text-gray-400 hover:text-blue-400 hover:bg-blue-900/30"
                          aria-label={`Reorder slides in ${item.label}`}
                          title="Reorder slides"
                        >
                          <List size={14} />
                        </button>
                      )}

                      {/* Reordering without a drag. Stacked as a spinner pair so
                          two more controls cost one column rather than two in a
                          300px drawer. Disabled rather than hidden at the ends,
                          so the row's controls do not shift as sections move. */}
                      {allowSectionReorder && (
                        <span className="flex flex-col justify-center -my-0.5">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              moveSectionByOffset(item.id, -1);
                            }}
                            disabled={!canMoveSectionUp}
                            className="flex h-4 w-6 items-center justify-center rounded text-gray-500 hover:text-blue-400 hover:bg-blue-900/30 disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-gray-500"
                            aria-label={`Move ${item.label} earlier`}
                            title="Move section up"
                          >
                            <ChevronUp size={13} />
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              moveSectionByOffset(item.id, 1);
                            }}
                            disabled={!canMoveSectionDown}
                            className="flex h-4 w-6 items-center justify-center rounded text-gray-500 hover:text-blue-400 hover:bg-blue-900/30 disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-gray-500"
                            aria-label={`Move ${item.label} later`}
                            title="Move section down"
                          >
                            <ChevronDown size={13} />
                          </button>
                        </span>
                      )}

                      {isEditMode && item.type === 'section' && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            const confirmed = window.confirm(`Delete section "${item.label}"?`);
                            if (confirmed) handleDeleteSection(item.id);
                          }}
                          className="flex items-center justify-center w-8 h-8 rounded-md text-gray-500 hover:text-red-400 hover:bg-red-900/30 border border-transparent"
                          aria-label={`Delete ${item.label}`}
                          title="Delete section"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>

                    {/* The title gets the whole width, on its own line. */}
                    <button
                      type="button"
                      onClick={(e) => handleNavHeaderClick(e, item, visibleSectionSlides, targetSlideIndex, isExpanded)}
                      className={`mt-1 block w-full rounded-lg px-1 py-0.5 text-left transition-colors ${isActive ? 'text-blue-400' : 'text-gray-200'} hover:bg-blue-900/30 ${isNavItemDisabled ? 'cursor-pointer' : ''}`}
                      aria-disabled={isNavItemDisabled}
                    >
                      <span className="flex flex-wrap items-center gap-1.5">
                        {/* Two lines maximum, and a step smaller once the title
                            is long enough to need it, so a wordy section name
                            stops driving the header's height. The full text
                            stays in the tooltip. */}
                        <span
                          className={`drawer-section-title${itemLabel.length > 40 ? ' drawer-section-title--long' : ''}`}
                          title={itemLabel}
                        >
                          {itemLabel}
                        </span>
                        {(isEndTitleNavItem || isTitleCardNavItem) && (
                          <span
                            className={`text-[10px] px-2 py-1 rounded-full font-semibold uppercase tracking-wide ${
                              (isEndTitleNavItem ? endTitleEnabled : titleCardEnabled)
                                ? 'bg-emerald-900/30 text-emerald-400'
                                : 'bg-gray-700 text-gray-400'
                            }`}
                          >
                            {(isEndTitleNavItem ? endTitleEnabled : titleCardEnabled) ? 'Included' : 'Optional'}
                          </span>
                        )}
                        {isEditMode && item.type === 'section' && hasSectionTitleCard && (
                          <span className="text-[10px] px-2 py-1 rounded-full font-semibold uppercase tracking-wide bg-indigo-900/30 text-indigo-400">
                            Title slide
                          </span>
                        )}
                      </span>
                    </button>
                    </div>

                    {allowSectionDrag && (
                      <div
                        className={`h-2 ${sectionDropAfter ? 'bg-blue-200' : 'bg-transparent'}`}
                        onDragOver={handleSectionDragOver(item.id, 'after')}
                        onDrop={handleSectionDrop(item.id, 'after')}
                      />
                    )}

                    {titleCardThumbStyles && (
                      <button
                        type="button"
                        onClick={(e) => handleNavHeaderClick(e, item, visibleSectionSlides, targetSlideIndex, isExpanded)}
                        className="mx-3 mb-2 block w-[calc(100%-1.5rem)] rounded-lg border border-gray-700 bg-gray-900/50 p-2 text-left transition-colors hover:bg-gray-700/60"
                        aria-label={`Open ${itemLabel}`}
                      >
                        <span className="drawer-slide-thumb drawer-slide-thumb--title-card" style={titleCardThumbStyles} aria-hidden="true">
                          <span className="drawer-title-card-thumb__content">
                            <span className="drawer-title-card-thumb__label">{isEndTitleNavItem ? 'End title' : 'Title card'}</span>
                            <span className="drawer-title-card-thumb__title">{itemLabel}</span>
                          </span>
                          {navItemDeckNumber && (
                            <span className="drawer-slide-number drawer-slide-number--on-card">{navItemDeckNumber}</span>
                          )}
                        </span>
                      </button>
                    )}

                    {isTitleCardNavItem && (
                      <div className="flex items-center justify-between px-4 pb-3">
                        <p className="text-xs text-gray-400 pr-2">Toggle the optional title card.</p>
                        <label className="inline-flex items-center gap-2 text-xs text-gray-300 font-medium select-none">
                          <span className="sr-only">Include title card</span>
                          <input
                            type="checkbox"
                            className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                            checked={!!titleCardEnabled}
                            onChange={(e) => handleTitleCardUpdate({ enabled: e.target.checked })}
                            onClick={(e) => e.stopPropagation()}
                          />
                          <span>{titleCardEnabled ? 'On' : 'Off'}</span>
                        </label>
                      </div>
                    )}

                    {isEndTitleNavItem && (
                      <div className="flex items-center justify-between px-4 pb-3">
                        <p className="text-xs text-gray-400 pr-2">Toggle the optional end title card.</p>
                        <label className="inline-flex items-center gap-2 text-xs text-gray-300 font-medium select-none">
                          <span className="sr-only">Include end title card</span>
                          <input
                            type="checkbox"
                            className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                            checked={!!endTitleEnabled}
                            onChange={(e) => handleEndTitleCardUpdate({ enabled: e.target.checked })}
                            onClick={(e) => e.stopPropagation()}
                          />
                          <span>{endTitleEnabled ? 'On' : 'Off'}</span>
                        </label>
                      </div>
                    )}

                    {isSectionNavItem && (
                      <div className="flex items-center justify-between gap-2 px-4 pb-3">
                        <div className="flex min-w-0 items-center gap-2">
                          <p className="truncate text-xs text-gray-400">Section title card</p>
                          {sectionTitleCardEnabled && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                openSectionTitleCardModal(item.id);
                              }}
                              className="shrink-0 rounded-md px-1.5 py-0.5 text-[11px] font-semibold text-blue-400 hover:bg-blue-900/30"
                            >
                              Customize
                            </button>
                          )}
                        </div>
                        <label className="inline-flex shrink-0 items-center gap-2 text-xs text-gray-300 font-medium select-none">
                          <span className="sr-only">{`Include a title card at the start of ${itemLabel}`}</span>
                          <input
                            type="checkbox"
                            className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                            checked={sectionTitleCardEnabled}
                            onChange={(e) => handleToggleSectionTitleCard(item.id, e.target.checked)}
                            onClick={(e) => e.stopPropagation()}
                          />
                          <span>{sectionTitleCardEnabled ? 'On' : 'Off'}</span>
                        </label>
                      </div>
                    )}

                    {/* The whole section's slides, on or off in one go. Sits
                        with the title card's switch because it answers the same
                        question a row lower: what of this section reaches the
                        exported file. */}
                    {isSectionNavItem && sectionSlideIds.length > 0 && (
                      <div className="flex items-center justify-between gap-2 px-4 pb-3">
                        <p className="min-w-0 truncate text-xs text-gray-400">
                          {sectionAllActive
                            ? `Slides in export — all ${sectionSlideIds.length}`
                            : sectionActiveCount === 0
                              ? 'Slides in export — none'
                              : `Slides in export — ${sectionActiveCount} of ${sectionSlideIds.length}`}
                        </p>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            toggleSectionSlidesActive(item.id);
                          }}
                          className={`inline-flex shrink-0 items-center gap-1.5 rounded-lg border px-2 py-1 text-xs font-semibold ${
                            sectionActiveCount === 0
                              ? 'border-amber-800 bg-amber-950/40 text-amber-300 hover:bg-amber-900/40'
                              : 'border-gray-600 bg-gray-700/60 text-gray-200 hover:bg-gray-600'
                          }`}
                          title={sectionActiveCount === 0
                            ? `Put every slide in ${itemLabel} back into the exported files`
                            : `Leave every slide in ${itemLabel} out of the exported files`}
                        >
                          {sectionActiveCount === 0 ? <Eye size={13} /> : <EyeOff size={13} />}
                          {sectionActiveCount === 0 ? 'Activate all' : 'Deactivate all'}
                        </button>
                      </div>
                    )}

                    {isEditMode && hasSlides && !isExpanded && (
                      <div
                        className={`h-2 ${headerAppendTarget ? 'bg-emerald-200' : 'bg-transparent'}`}
                        onDragOver={handleSlideDragOver(item.id, contentSlides.length, 'after')}
                        onDrop={handleSlideDrop(item.id, Math.max(contentSlides.length, 1) - 1, 'after')}
                      />
                    )}

                    {hasVisibleSlides && isExpanded && (
                      <ul className="border-t border-gray-700 bg-gray-900/50 px-2.5 py-2.5 space-y-2">
                        {visibleSectionSlides.map((sectionSlide) => {
                          const isSlideActive = currentSlideIndex === sectionSlide.globalIndex;
                          const slideDeckNumber = deckSlideNumbers.get(sectionSlide.slideId)
                            || (Number.isFinite(sectionSlide.globalIndex) ? sectionSlide.globalIndex + 1 : null);
                          const isHighlighted = recentlyImportedSlideIds.includes(sectionSlide.slideId);
                          const isSectionTitleSlide = sectionSlide.isSectionTitleCard;
                          const thumbHeaderColor = headerColorMap?.[item.id] || slideHeaderColor || '#545f7d';
                          const slideDurationLabel = formatSlideDurationLabel(slideDurations?.[sectionSlide.slideId]);
                          const slideData = slideById[sectionSlide.slideId];
                          const slideBlocks = slideData?.blocks || [];
                          const thumbBackgroundStyles = getBackgroundStyles(
                            slideBackgrounds?.[sectionSlide.slideId] || bodyBackground
                          );
                          const slideMenuId = `${item.id}-${sectionSlide.slideIndex}-${sectionSlide.slideId || 'no-id'}`;
                          const isFirstContentSlide = sectionSlide.slideIndex <= 0;
                          const isLastContentSlide = sectionSlide.slideIndex >= contentSlides.length - 1;
                          const isSlideSelected = Boolean(sectionSlide.slideId)
                            && pickedSlideSet.has(sectionSlide.slideId);
                          // A menu opened on a picked slide acts on every picked
                          // slide, the same way dragging one drags them all.
                          const movesWholePick = isSlideSelected && pickedSlideIds.length > 1;
                          // Not to be confused with isSlideActive above, which is
                          // the slide being edited. This is whether the exported
                          // files will carry it.
                          const isSlideInExport = slideVisibility?.[sectionSlide.slideId] !== false;
                          // What the on/off item will act on, and which way it
                          // will go. A mixed set switches off: leaving some on
                          // is the state you are trying to get out of.
                          const activeTargetIds = movesWholePick ? pickedSlideIds : [sectionSlide.slideId];
                          const activatesTarget = movesWholePick
                            ? !pickedSlideIds.some((id) => slideVisibility?.[id] !== false)
                            : !isSlideInExport;
                          // Every slide travelling with this drag looks like it
                          // is travelling, not only the row under the pointer.
                          const isDraggingThisSlide = Boolean(draggingSlide) && (
                            (draggingSlide.sectionId === item.id
                              && draggingSlide.slideIndex === sectionSlide.slideIndex)
                            || Boolean(sectionSlide.slideId && draggingSlide.refs?.some(
                              (ref) => ref.slideId === sectionSlide.slideId
                            ))
                          );
                          const dropBefore = slideDropTarget
                            && slideDropTarget.sectionId === item.id
                            && slideDropTarget.slideIndex === sectionSlide.slideIndex
                            && slideDropTarget.position === 'before';
                          const dropAfter = slideDropTarget
                            && slideDropTarget.sectionId === item.id
                            && slideDropTarget.slideIndex === sectionSlide.slideIndex
                            && slideDropTarget.position === 'after';

                          return (
                            <li
                              key={`${item.id}-${sectionSlide.slideIndex}`}
                              className="relative"
                              // The row's identity, in the DOM. The list is
                              // otherwise described entirely by position —
                              // labels included — so this is the only way to
                              // say which slide a row is after a move.
                              data-slide-id={sectionSlide.slideId}
                              data-section-id={item.id}
                              ref={isSlideActive ? activeSlideItemRef : null}
                              // Delete removes this slide. On the row, so the
                              // key acts on the slide you are actually on, and
                              // never on one the canvas has selected.
                              onKeyDown={handleSlideRowKeyDown(item.id, sectionSlide, slideBlocks)}
                            >
                              {isEditMode && !isSectionTitleSlide && (
                                <div
                                  className={`h-2 ${dropBefore ? 'bg-emerald-200' : 'bg-transparent'} rounded-lg`}
                                  onDragOver={handleSlideDragOver(item.id, sectionSlide.slideIndex, 'before')}
                                  onDrop={handleSlideDrop(item.id, sectionSlide.slideIndex, 'before')}
                                />
                              )}
                              {/* The slide you are on gets a solid ring, a blue
                                  card and a left accent bar — a faint ring was
                                  too easy to lose in a long outline. */}
                              <div className={`${isSectionTitleSlide ? 'flex items-center gap-2 border-y border-gray-700/80 bg-gray-900/80 px-2 py-1.5' : `relative rounded-xl border shadow-sm overflow-visible ${isSlideActive ? 'border-blue-400 bg-blue-950/40 ring-2 ring-blue-400 ring-offset-2 ring-offset-gray-900' : 'border-gray-700 bg-gray-800'}`}`}>
                                {isSectionTitleSlide ? (
                                  <div className="relative w-full">
                                    <button
                                      type="button"
                                      onClick={(e) => handleNavClick(e, item.id, sectionSlide.slideIndex, sectionSlide.globalIndex)}
                                      className={`drawer-title-card-row w-full rounded-lg border border-gray-700 bg-gray-800 p-2 text-left transition-colors ${isSlideActive ? 'ring-1 ring-blue-500/40' : 'hover:bg-gray-700/70'}`}
                                      title={sectionSlide.label}
                                    >
                                      <span
                                        className="drawer-slide-thumb drawer-slide-thumb--section-title"
                                        style={getTitleCardPreviewStyles(slideData || {}, titleCardBackground)}
                                        aria-hidden="true"
                                      >
                                        <span className="drawer-title-card-thumb__content">
                                          <span className="drawer-title-card-thumb__label">Section title</span>
                                          <span className="drawer-title-card-thumb__title">{sectionSlide.label}</span>
                                        </span>
                                        {slideDeckNumber && (
                                          <span className="drawer-slide-number drawer-slide-number--on-card">
                                            {slideDeckNumber}
                                          </span>
                                        )}
                                      </span>
                                    </button>
                                    <button
                                      type="button"
                                      onClick={(event) => { event.stopPropagation(); openSectionDividerSettings(item, sectionSlide); }}
                                      className="absolute right-2 top-1/2 -translate-y-1/2 rounded-lg border border-gray-600 bg-gray-700/95 p-1.5 text-gray-200 shadow-sm hover:bg-gray-600 hover:text-white"
                                      aria-label={`Settings for ${sectionSlide.label}`}
                                      title="Section divider settings"
                                    >
                                      <Settings size={14} />
                                    </button>
                                  </div>
                                ) : (
                                /* Not a <button>. A button may only contain phrasing
                                   content, and a live thumbnail renders the slide's
                                   real blocks — divs, headings, tables, and in some
                                   cases links and inputs. Nesting those inside a
                                   button is invalid markup and swallows this row's
                                   own click and drag handlers. The row is a plain
                                   container; the overlay button at the end of it is
                                   the click and keyboard target. */
                                <div
                                  draggable={isEditMode && !isSectionTitleSlide}
                                  onDragStart={
                                    isEditMode && !isSectionTitleSlide
                                      ? handleSlideDragStart(item.id, sectionSlide.slideIndex, sectionSlide.slideId)
                                      : undefined
                                  }
                                  onDragEnd={isEditMode && !isSectionTitleSlide ? handleSlideDragEnd : undefined}
                                  onDragOver={
                                    isEditMode && !isSectionTitleSlide
                                      ? handleSlideDragOver(item.id, sectionSlide.slideIndex, 'before')
                                      : undefined
                                  }
                                  onDrop={
                                    isEditMode && !isSectionTitleSlide
                                      ? handleSlideDrop(item.id, sectionSlide.slideIndex, 'before')
                                      : undefined
                                  }
                                  className={`drawer-slide-row relative w-full rounded-xl px-2.5 py-2 text-sm transition-colors ${
                                    isSlideActive ? 'text-blue-200 font-semibold' : 'text-gray-300 hover:bg-gray-700/70'
                                  } ${isHighlighted ? 'ring-2 ring-emerald-400' : ''} ${isSlideSelected ? 'bg-violet-950/50 ring-2 ring-violet-400' : ''} ${isSlideInExport ? '' : 'opacity-60'} ${isDraggingThisSlide ? 'opacity-70 ring-2 ring-emerald-400' : ''}`}
                                  aria-grabbed={isDraggingThisSlide}
                                >
                                  {/* The miniature and the slide's place in the
                                      deck, which sits in its corner: the same
                                      figure the deck counter and the transfer
                                      window print, so "go back to 23" means one
                                      thing. On the picture rather than beside it
                                      because the column beside it belongs to the
                                      slide's name, and there is not much of it. */}
                                  <span className="drawer-slide-row__thumb">
                                    <DrawerSlideThumbnail
                                      blocks={slideBlocks}
                                      headerColor={thumbHeaderColor}
                                      bodyStyle={thumbBackgroundStyles}
                                      designMode={slideDesignMode}
                                    />
                                    {slideDeckNumber && (
                                      <span
                                        className={`drawer-slide-number${isSlideActive ? ' drawer-slide-number--active' : ''}`}
                                        aria-hidden="true"
                                      >
                                        {slideDeckNumber}
                                      </span>
                                    )}
                                  </span>
                                  <span className="drawer-slide-row__meta flex items-start gap-1.5 text-left">
                                    {/* The visible half of picking slides out.
                                        Ctrl and Shift clicking the row does the
                                        same thing faster, but a modifier key no
                                        one is told about is not a feature — this
                                        is what says the outline can hold more
                                        than one slide at a time. Above the row's
                                        overlay button (z-10), which would
                                        otherwise swallow the click. */}
                                    {isEditMode && !isSectionTitleSlide && sectionSlide.slideId && (
                                      <input
                                        type="checkbox"
                                        checked={isSlideSelected}
                                        onChange={() => toggleSlidePick(sectionSlide.slideId)}
                                        onClick={(event) => event.stopPropagation()}
                                        className="relative z-20 mt-0.5 h-3.5 w-3.5 shrink-0 cursor-pointer rounded border-gray-500 bg-gray-700 text-violet-500 accent-violet-500 focus:ring-violet-400"
                                        aria-label={`Pick ${sectionSlide.label} to move with others`}
                                        title="Pick this slide — then drag any picked slide to move them together"
                                      />
                                    )}
                                    {isEditMode && !isSectionTitleSlide && <GripVertical size={13} className="mt-0.5 shrink-0 text-gray-500" />}
                                    <span className="min-w-0 flex-1">
                                      {/* Name first: it is what the list is scanned
                                          for, and the thumbnail beside it already
                                          says what kind of slide this is. */}
                                      <span className="drawer-slide-row__name" title={sectionSlide.label}>
                                        {sectionSlide.label}
                                      </span>
                                      <span className="drawer-slide-row__chips">
                                        <span>{sectionSlide.typeLabel}</span>
                                        {slideDurationLabel && (
                                          <span className="rounded-full border border-gray-600 bg-gray-700 px-1 font-semibold text-gray-400">
                                            {slideDurationLabel}
                                          </span>
                                        )}
                                        {sectionSlide.hasIssue && (
                                          <span className="rounded-full bg-rose-900/30 px-1 font-semibold text-rose-400">
                                            Missing
                                          </span>
                                        )}
                                        {isSlideActive && (
                                          <span className="rounded-full bg-blue-500 px-1 font-bold text-white">
                                            Current
                                          </span>
                                        )}
                                        {isSlideSelected && (
                                          <span className="rounded-full bg-violet-500 px-1 font-bold text-white">
                                            Picked
                                          </span>
                                        )}
                                        {!isSlideInExport && (
                                          <span
                                            className="rounded-full bg-amber-900/40 px-1 font-semibold text-amber-300"
                                            title="Left out of the exported files"
                                          >
                                            Off
                                          </span>
                                        )}
                                      </span>
                                    </span>
                                  </span>
                                  {/* The row's click and keyboard target, laid over
                                      the whole card. Kept last and above the
                                      thumbnail so the slide's own markup can never
                                      intercept a click, and below the actions menu
                                      (z-20), which has to stay reachable. */}
                                  <button
                                    type="button"
                                    onClick={(e) => handleSlideRowClick(e, item.id, sectionSlide)}
                                    // Where focus goes after a Delete takes the
                                    // row it was on with it.
                                    data-slide-row-target="true"
                                    className="absolute inset-0 z-10 h-full w-full rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
                                    aria-label={
                                      isSlideSelected
                                        ? `${sectionSlide.label} — selected. Ctrl-click to deselect`
                                        : `Go to ${sectionSlide.label}`
                                    }
                                    aria-pressed={isSlideSelected || undefined}
                                    title={sectionSlide.label}
                                  />
                                </div>
                                )}
                                {!isSectionTitleSlide && (
                                  /* z-20 keeps this above the row's overlay button
                                     (z-10), which otherwise covers it completely. */
                                  <div
                                    className="drawer-slide-row__actions absolute z-20"
                                  >
                                    <button
                                      ref={drawerSlideMenu?.menuId === slideMenuId ? drawerSlideMenuButtonRef : null}
                                      type="button"
                                      onClick={(event) => openDrawerSlideMenu(event, {
                                        menuId: slideMenuId,
                                        slideId: sectionSlide.slideId,
                                        sectionId: item.id,
                                        slideIndex: sectionSlide.slideIndex
                                      })}
                                      className={`flex h-7 w-7 items-center justify-center rounded-lg border border-gray-600 bg-gray-700/95 shadow-sm ${
                                        'text-gray-200 hover:text-white hover:bg-gray-600'
                                      }`}
                                      aria-haspopup="menu"
                                      aria-expanded={drawerSlideMenu?.menuId === slideMenuId}
                                      aria-label={`Slide actions for ${sectionSlide.label}`}
                                      title="Slide actions"
                                    >
                                      <MoreHorizontal size={14} />
                                    </button>
                                    {drawerSlideMenu?.menuId === slideMenuId && createPortal(
                                      <div
                                        ref={drawerSlideMenuRef}
                                        role="menu"
                                        aria-label={`Slide actions for ${sectionSlide.label}`}
                                        data-drawer-slide-menu-portal
                                        className="drawer-slide-menu-portal fixed w-56 rounded-xl border border-gray-700 bg-gray-800 shadow-lg overflow-hidden"
                                        style={{ ...drawerSlideMenuPosition, zIndex: isFullScreenPreview ? 10020 : 80 }}
                                      >
                                        {drawerSlideMenu.mode === 'move-section' ? (
                                          <div className="py-2">
                                            <button
                                              type="button"
                                              onClick={() => setDrawerSlideMenu((prev) => ({ ...prev, mode: 'root' }))}
                                              className="w-full px-4 py-2 text-left text-xs font-semibold text-gray-400 hover:bg-gray-700"
                                            >
                                              ← Back
                                            </button>
                                            <div className="px-4 pb-2 text-[10px] uppercase tracking-wide text-gray-400">
                                              {movesWholePick
                                                ? `Move ${pickedSlideIds.length} slides to section`
                                                : 'Move to section'}
                                            </div>
                                            {sections.map((section) => {
                                              const sectionLabel = getSectionHeaderLabel(section, sectionNumberMap);
                                              // Its own section is no destination for a single
                                              // slide. For a set gathered from several sections
                                              // it is a real one — that is where they land.
                                              const isCurrent = section.id === item.id && !movesWholePick;
                                              return (
                                                <button
                                                  key={section.id}
                                                  type="button"
                                                  onClick={() => {
                                                    handleMoveSlideToSection(item.id, sectionSlide.slideIndex, sectionSlide.slideId, section.id);
                                                    closeDrawerSlideMenu();
                                                  }}
                                                  disabled={isCurrent}
                                                  className={`w-full px-4 py-2 text-left text-xs font-semibold transition-colors ${
                                                    isCurrent
                                                      ? 'text-gray-600 cursor-not-allowed'
                                                      : 'text-gray-300 hover:bg-gray-700'
                                                  }`}
                                                >
                                                  {sectionLabel}
                                                </button>
                                              );
                                            })}
                                          </div>
                                        ) : (
                                          <div className="py-2">
                                            <button
                                              type="button"
                                              onClick={() => {
                                                handleDuplicateSlide(item.id, sectionSlide.slideIndex, sectionSlide.slideId, sectionSlide.globalIndex);
                                                closeDrawerSlideMenu();
                                              }}
                                              className="w-full px-4 py-2 text-left text-xs font-semibold text-gray-300 hover:bg-gray-700 flex items-center gap-2"
                                            >
                                              <Copy size={14} />
                                              Duplicate
                                            </button>
                                            <button
                                              type="button"
                                              onClick={() => {
                                                handleAddSlideFromDrawer(item.id, sectionSlide.slideIndex, sectionSlide.slideId, sectionSlide.globalIndex);
                                                closeDrawerSlideMenu();
                                              }}
                                              className="w-full px-4 py-2 text-left text-xs font-semibold text-gray-300 hover:bg-gray-700 flex items-center gap-2"
                                            >
                                              <Plus size={14} />
                                              Add slide below
                                            </button>
                                            {/* Beside "Add slide below" because it answers the
                                                neighbouring question: what comes after this
                                                slide. Structural - it splits this section in
                                                two - and not the Section Header slide preset. */}
                                            <button
                                              type="button"
                                              onClick={() => {
                                                openCreateSectionModal({
                                                  sectionId: item.id,
                                                  slideId: sectionSlide.slideId,
                                                  slideIndex: sectionSlide.slideIndex
                                                });
                                                closeDrawerSlideMenu();
                                              }}
                                              className="w-full px-4 py-2 text-left text-xs font-semibold text-gray-300 hover:bg-gray-700 flex items-center gap-2"
                                              title="Start a new section here - the slides below this one move into it"
                                            >
                                              <FolderPlus size={14} />
                                              New section after this slide&hellip;
                                            </button>
                                            <button
                                              type="button"
                                              onClick={() => {
                                                handleMoveSlideUp(item.id, sectionSlide.slideIndex, sectionSlide.slideId, sectionSlide.globalIndex);
                                                closeDrawerSlideMenu();
                                              }}
                                              disabled={isFirstContentSlide}
                                              className={`w-full px-4 py-2 text-left text-xs font-semibold flex items-center gap-2 ${
                                                isFirstContentSlide
                                                  ? 'text-gray-600 cursor-not-allowed'
                                                  : 'text-gray-300 hover:bg-gray-700'
                                              }`}
                                            >
                                              <ArrowUp size={14} />
                                              Move up
                                            </button>
                                            <button
                                              type="button"
                                              onClick={() => {
                                                handleMoveSlideDown(item.id, sectionSlide.slideIndex, sectionSlide.slideId, sectionSlide.globalIndex);
                                                closeDrawerSlideMenu();
                                              }}
                                              disabled={isLastContentSlide}
                                              className={`w-full px-4 py-2 text-left text-xs font-semibold flex items-center gap-2 ${
                                                isLastContentSlide
                                                  ? 'text-gray-600 cursor-not-allowed'
                                                  : 'text-gray-300 hover:bg-gray-700'
                                              }`}
                                            >
                                              <ArrowDown size={14} />
                                              Move down
                                            </button>
                                            <button
                                              type="button"
                                              onClick={() => setDrawerSlideMenu((prev) => ({ ...prev, mode: 'move-section' }))}
                                              className="w-full px-4 py-2 text-left text-xs font-semibold text-gray-300 hover:bg-gray-700 flex items-center gap-2"
                                            >
                                              <ArrowRight size={14} />
                                              {movesWholePick
                                                ? `Move ${pickedSlideIds.length} slides to section...`
                                                : 'Move to section...'}
                                            </button>
                                            <div className="border-t border-gray-700 my-2" />
                                            {/* Off is the flag the slide header's
                                                own Include switch sets: the slide
                                                stays in the deck and the exported
                                                files leave it out. */}
                                            <button
                                              type="button"
                                              onClick={() => {
                                                setSlidesActive(activeTargetIds, activatesTarget);
                                                if (movesWholePick) clearSlidePicks();
                                                closeDrawerSlideMenu();
                                              }}
                                              className="w-full px-4 py-2 text-left text-xs font-semibold text-gray-300 hover:bg-gray-700 flex items-center gap-2"
                                              title={activatesTarget
                                                ? 'Put back into the exported files'
                                                : 'Keep the slide but leave it out of the exported files'}
                                            >
                                              {activatesTarget ? <Eye size={14} /> : <EyeOff size={14} />}
                                              {movesWholePick
                                                ? `${activatesTarget ? 'Activate' : 'Deactivate'} ${pickedSlideIds.length} slides`
                                                : `${activatesTarget ? 'Activate' : 'Deactivate'} slide`}
                                            </button>
                                            <div className="border-t border-gray-700 my-2" />
                                            <button
                                              type="button"
                                              onClick={() => {
                                                const sectionIdForSlide = slideData?.section?.id || item.id;
                                                if (!slideData) {
                                                  console.error('Slide data not found for slideId:', sectionSlide.slideId);
                                                  showSaveIndicator('⚠️ Error: Slide data not found');
                                                  closeDrawerSlideMenu();
                                                  return;
                                                }
                                                const confirmed = window.confirm(`Delete slide "${sectionSlide.label}"?`);
                                                if (confirmed) {
                                                  handleRemoveSlide(sectionIdForSlide, slideBlocks, sectionSlide.slideId);
                                                }
                                                closeDrawerSlideMenu();
                                              }}
                                              className="w-full px-4 py-2 text-left text-xs font-semibold text-red-400 hover:bg-red-900/30 flex items-center gap-2"
                                            >
                                              <Trash2 size={14} />
                                              Delete
                                            </button>
                                          </div>
                                        )}
                                      </div>,
                                      document.body
                                    )}
                                  </div>
                                )}
                              </div>
                              {isEditMode && !isSectionTitleSlide && (
                                <div
                                  className={`h-2 ${dropAfter ? 'bg-emerald-200' : 'bg-transparent'} rounded-lg`}
                                  onDragOver={handleSlideDragOver(item.id, sectionSlide.slideIndex, 'after')}
                                  onDrop={handleSlideDrop(item.id, sectionSlide.slideIndex, 'after')}
                                />
                              )}
                            </li>
                          );
                        })}
                        {isEditMode && (
                          <li className="border-b border-gray-700 last:border-b-0">
                            <div
                              className={`h-3 ${slideDropTarget?.sectionId === item.id && slideDropTarget?.slideIndex === contentSlides.length && slideDropTarget?.position === 'after' ? 'bg-emerald-200' : 'bg-transparent'}`}
                              onDragOver={handleSlideDragOver(item.id, contentSlides.length, 'after')}
                              onDrop={handleSlideDrop(item.id, Math.max(contentSlides.length - 1, 0), 'after')}
                            />
                          </li>
                        )}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ul>

            <div className="rounded-xl border border-gray-700 bg-gray-800 p-3 space-y-2">
              <label className="text-xs font-semibold text-gray-400" htmlFor="new-section-placement">
                Place new section
              </label>
              <select
                id="new-section-placement"
                value={newSectionPlacement}
                onChange={(e) => setNewSectionPlacement(e.target.value)}
                className="w-full rounded-lg border border-gray-600 bg-gray-700 px-3 py-2 text-sm text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="start">At start</option>
                {sections.map((section) => (
                  <option key={section.id} value={`before:${section.id}`}>
                    {`Before ${getSectionHeaderLabel(section, sectionNumberMap)}`}
                  </option>
                ))}
                <option value="end">At end</option>
              </select>
            </div>

            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                handleAddSection();
              }}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 border-dashed border-blue-500/30 bg-blue-900/20 text-blue-400 font-semibold text-sm hover:bg-blue-900/30"
            >
              <Plus size={14} />
              New Section
            </button>

            <div className="space-y-1">
              <label className="text-xs font-semibold text-gray-400" htmlFor="new-section-name">
                New section name
              </label>
              <input
                id="new-section-name"
                type="text"
                value={newSectionName}
                onChange={(e) => setNewSectionName(e.target.value)}
                placeholder="Section name"
                className="w-full rounded-lg border border-gray-600 bg-gray-700 px-3 py-2 text-sm text-gray-200 shadow-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="pt-3 text-xs text-gray-400 space-y-1">
              <p className="font-semibold text-gray-300">How the drawer works</p>
              <p>Filter by keyword or toggle "Issues only" to focus on slides that need content.</p>
              <p>Tap a section to open it and view its slides. Use the arrow on each section to collapse or expand its list.</p>
              <p>Scrolled a long way down? The blue "Top" button in the bottom left corner takes you straight back to the start of the list.</p>
              <p>Reorder sections with the up and down arrows on a section header, or open the list icon above for the whole deck on one screen. In Edit mode you can also drag sections and slides by their handles, and dropping a slide on another section moves it there.</p>
            </div>
          </div>

          {/* Deliberately loud: a small grey chevron among a drawer full of
              small grey chevrons is one more thing to hunt for.

              Bottom LEFT, not right. Anything floating over the list covers
              something, and the right-hand edge of every row is where the
              actions menu and the slide number live — a button that sits on
              top of the "..." menu takes a control away. The left edge is the
              thumbnail's picture: covering a corner of it costs nothing.

              Gone while a row is being dragged, so it cannot swallow a drop
              aimed at the foot of the list. */}
          {showDrawerTopJump && !isDraggingInDrawer && (
            <button
              type="button"
              onClick={handleDrawerScrollToTop}
              title="Back to the top of the list"
              aria-label="Back to the top of the list"
              className="absolute bottom-4 left-3 z-10 inline-flex items-center gap-1 rounded-full border border-blue-300/50 bg-blue-600 pl-2 pr-2.5 py-1.5 text-[11px] font-bold uppercase tracking-wide text-white shadow-lg ring-1 ring-black/10 transition-colors hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-300"
            >
              <ArrowUp size={14} strokeWidth={3} />
              Top
            </button>
          )}
        </aside>
        <button
          type="button"
          onClick={toggleNavDrawer}
          // h-28 leaves room for the vertical label in both states, matching
          // the Tools panel tab on the right edge.
          className={`pointer-events-auto absolute top-10 h-28 w-8 flex-col gap-1 items-center justify-center rounded-r-lg border border-l-0 shadow-lg transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
            isFullScreenPreview && isFullScreenChromeHidden ? 'hidden' : 'flex'
          } ${
            lightMode ? 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50' : 'border-gray-700 bg-gray-800 text-gray-300 hover:bg-gray-700'
          }`}
          style={{ left: isNavDrawerOpen ? '300px' : '0px' }}
          aria-expanded={isNavDrawerOpen}
          aria-label={isNavDrawerOpen ? 'Collapse slide panel' : 'Open slide panel'}
          title={isNavDrawerOpen ? 'Collapse slide panel' : 'Open slide panel'}
          ref={!isNavDrawerOpen ? navDrawerTriggerRef : undefined}
        >
          <ChevronRight size={18} className={isNavDrawerOpen ? 'rotate-180' : undefined} />
          {/* A bare chevron reads as decoration — say what is behind it. */}
          <span
            className={`text-[10px] font-semibold uppercase tracking-wider whitespace-nowrap ${
              lightMode ? 'text-gray-500' : 'text-gray-400'
            }`}
            style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
          >
            Slide panel
          </span>
        </button>
      </div>

      {sectionDividerSettings && (
        <div className="fixed inset-0 z-[140] flex items-center justify-center bg-black/50 p-4" onClick={closeSectionDividerSettings}>
          <div className="w-full max-w-md rounded-2xl border border-gray-700 bg-gray-900 p-5 shadow-2xl" onClick={(event) => event.stopPropagation()}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-sky-400">Section divider settings</p>
                <h3 className="mt-1 text-lg font-semibold text-white">{sectionDividerSettings.sectionLabel}</h3>
              </div>
              <button type="button" onClick={closeSectionDividerSettings} className="rounded-lg p-2 text-gray-400 hover:bg-gray-800 hover:text-white" aria-label="Close section divider settings">
                <X size={16} />
              </button>
            </div>
            <p className="mt-3 text-sm text-gray-300">Use this section divider as a compact, one-line marker in the outline. Slide-specific divider controls can be added here without changing existing navigation behavior.</p>
            <div className="mt-4 rounded-xl border border-gray-700 bg-gray-800 p-3 text-sm text-gray-300">
              <span className="font-semibold text-gray-100">Divider title:</span> {sectionDividerSettings.slideLabel}
            </div>
            <div className="mt-5 flex justify-end">
              <button type="button" onClick={closeSectionDividerSettings} className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700">Done</button>
            </div>
          </div>
        </div>
      )}

      {/* Row 2: Compact Unified Header */}
      {isHeaderExpanded && (
        <div className="presentation-header no-print sticky z-[60]" style={{ top: tabNavHeight }}>
          <div className="max-w-7xl mx-auto px-4 h-[38px] flex items-center gap-4">
            <div className="mode-toggle header-mode-toggle" aria-label="Slide view mode">
              <button type="button" onClick={() => { if (!isEditMode) handleToggleEditMode(); }} className={isEditMode ? 'active' : ''} title="Edit Mode (E)">Edit</button>
              <button type="button" onClick={() => { if (isEditMode) handleToggleEditMode(); }} className={!isEditMode ? 'active' : ''} title="Preview Mode (P)">Preview</button>
              <button type="button" onClick={handleEnterFullScreenPreview} title="Full Screen (F)">Full</button>
            </div>
            <div className="presentation-info">
              {!isEditingDate ? (
                <div
                  className="date-display"
                  onClick={() => setIsEditingDate(true)}
                  title="Click to change date"
                >
                  <Calendar size={14} />
                  <span className="date">{shortDisplayDate}</span>
                </div>
              ) : (
                <input
                  type="date"
                  className="date-input"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  onBlur={() => setIsEditingDate(false)}
                  autoFocus
                />
              )}
              {!isEditingTitle ? (
                <h1
                  className="title cursor-pointer hover:bg-blue-50 rounded px-2 py-1 transition-colors"
                  onClick={() => setIsEditingTitle(true)}
                  title="Click to edit"
                >
                  {safeString(headerData.courseTopic, getCourseTopicForWeek()).replace(/Week \d+/, `Week ${week}`)}
                </h1>
              ) : (
                <input
                  type="text"
                  className="title border-2 border-blue-500 rounded px-2 py-1 focus:outline-none"
                  value={tempTitle}
                  onChange={(e) => setTempTitle(e.target.value)}
                  onBlur={handleTitleSave}
                  onKeyDown={handleTitleKeyDown}
                  autoFocus
                  onFocus={(e) => e.target.select()}
                />
              )}
            </div>

            <div className="instructor-badge">
              <input
                type="text"
                className="instructor-input"
                value={headerData.instructorName}
                onChange={e => handleHeaderDataChange('instructorName', e.target.value)}
                placeholder="Instructor Name"
              />
              {' · '}
              <input
                type="text"
                className="instructor-input"
                value={headerData.instructorEmail}
                onChange={e => handleHeaderDataChange('instructorEmail', e.target.value)}
                placeholder="email@institution.edu"
              />
            </div>

            {/* What happened to this deck last time Save was pressed. It lives
                on this row rather than beside the Save button because this row
                is status and has room, while that one is buttons and had none
                — adding it there pushed Export off the end of the screen. */}
            {lastFileSave && (
              <span
                className={`save-status ${editedSinceSave ? 'save-status--stale' : ''}`}
                title={
                  editedSinceSave
                    ? `Edited since v${lastFileSave.version} was saved to ${lastFileSave.fileName}. ${lastFileSave.where}`
                    : `Saved to ${lastFileSave.fileName} at ${lastFileSave.at.toLocaleTimeString()}. ${lastFileSave.where}`
                }
              >
                {editedSinceSave ? <AlertCircle size={12} /> : <CheckCircle size={12} />}
                {editedSinceSave
                  ? `Edited since v${lastFileSave.version}`
                  : `Saved v${lastFileSave.version} · ${lastFileSave.at.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Row 3: Unified Toolbar */}
      {showToolbar ? (
        <div
          ref={toolbarRef}
          className="toolbar no-print sticky z-50"
          style={{
            top: isHeaderExpanded ? `calc(${tabNavHeight}px + 58px)` : tabNavHeight,
          }}
        >
          <div className="max-w-7xl mx-auto px-4 h-[40px] flex items-center justify-center gap-2.5">
            {showHeaderBar && (
              <button
                type="button"
                onClick={() => setIsAppHeaderCollapsed((prev) => !prev)}
                className="toolbar-btn toolbar-btn--header-toggle"
                aria-expanded={!isAppHeaderCollapsed}
                aria-label={isAppHeaderCollapsed ? 'Expand lesson header' : 'Collapse lesson header'}
                title={isAppHeaderCollapsed ? 'Expand lesson header' : 'Collapse lesson header'}
              >
                {isAppHeaderCollapsed ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
                {/* The toolbar row is already wider than its 1280px container, so
                    the label only appears where there is room for it; below that
                    the button stays icon-sized and the tooltip carries the name. */}
                <span className="hidden min-[1600px]:inline">Header</span>
              </button>
            )}
            {/* Slide-level clipboard and creation controls */}
            <div className="toolbar-group bordered slide-clipboard-toolbar" role="group" aria-label="Slide tools">
              <span className="slide-tools-label" aria-hidden="true">Slide Tools</span>
              <div className="slide-tools-controls">
              <button type="button" className="slide-tool-btn slide-tool-btn--paste" onClick={handlePasteSlide} disabled={!slideClipboard} title={slideClipboard ? 'Paste slide after current slide (Ctrl/Cmd+V)' : 'Paste slide (clipboard empty)'} aria-label="Paste slide">
                <ClipboardPaste size={19} />
              </button>
              <button type="button" className="slide-tool-btn slide-tool-btn--cut" onClick={handleCutSlide} disabled={!slides[currentSlideIndex]?.section?.id} title="Cut current slide (Ctrl/Cmd+X)" aria-label="Cut current slide">
                <Scissors size={19} />
              </button>
              <button type="button" className="slide-tool-btn slide-tool-btn--copy" onClick={handleCopySlide} disabled={!slides[currentSlideIndex]?.section?.id} title="Copy current slide (Ctrl/Cmd+C)" aria-label="Copy current slide">
                <Copy size={19} />
              </button>
              <button type="button" className={`slide-tool-btn slide-tool-btn--format ${formatPainter ? 'active' : ''}`} onClick={handleFormatPainter} disabled={!slides[currentSlideIndex]?.section?.id} title={formatPainter ? 'Format Painter active — select a target slide; click again or press Escape to cancel' : 'Format Painter — copy current slide formatting'} aria-label={formatPainter ? 'Cancel Format Painter' : 'Format Painter'} aria-pressed={Boolean(formatPainter)}>
                <Paintbrush size={19} />
              </button>
              <div className="slide-new-control">
                <button type="button" className="slide-tool-btn slide-tool-btn--new" onClick={handleNewBlankSlide} title="New blank slide after current slide — empty, no text box (Ctrl/Cmd+Shift+N)" aria-label="New blank slide">
                  <Presentation size={19} />
                </button>
                <button
                  ref={newSlideMenuButtonRef}
                  type="button"
                  className="slide-tool-btn slide-tool-btn--chevron"
                  onClick={() => setShowNewSlideMenu((open) => !open)}
                  onKeyDown={(event) => {
                    if (['ArrowDown', 'Enter', ' '].includes(event.key) && !showNewSlideMenu) {
                      event.preventDefault();
                      setShowNewSlideMenu(true);
                      setTimeout(() => newSlideMenuRef.current?.querySelector('[role="menuitem"]')?.focus(), 0);
                    }
                  }}
                  aria-label="New slide options"
                  aria-haspopup="menu"
                  aria-expanded={showNewSlideMenu}
                  title="New slide options"
                >
                  <ChevronDown size={14} />
                </button>
                {showNewSlideMenu && (
                  <div
                    ref={newSlideMenuRef}
                    className="dropdown-menu slide-new-menu"
                    role="menu"
                    aria-label="New slide options"
                    onKeyDown={(event) => {
                      const items = [...event.currentTarget.querySelectorAll('[role="menuitem"]')];
                      const index = items.indexOf(document.activeElement);
                      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                        event.preventDefault();
                        const offset = event.key === 'ArrowDown' ? 1 : -1;
                        items[(index + offset + items.length) % items.length]?.focus();
                      } else if (event.key === 'Home') { event.preventDefault(); items[0]?.focus(); }
                      else if (event.key === 'End') { event.preventDefault(); items[items.length - 1]?.focus(); }
                      else if (event.key === 'Escape') { event.preventDefault(); setShowNewSlideMenu(false); newSlideMenuButtonRef.current?.focus(); }
                    }}
                  >
                    {/* Blank first: it is the one most new slides want, and
                        the one the button beside this menu already does. */}
                    <button type="button" role="menuitem" onClick={handleNewBlankSlide} title="An empty slide — add whichever block you want">Blank Slide</button>
                    <button type="button" role="menuitem" onClick={handleNewTextSlide} title="A slide that starts with a text block">Text Slide</button>
                    <button type="button" role="menuitem" onClick={handleDuplicateCurrentSlide} disabled={!slides[currentSlideIndex]?.section?.id}>Duplicate Current Slide</button>
                    {/* In the menu rather than as a toolbar button of its own:
                        the row is already full and one more icon would wrap it.
                        Same command the outline row's item runs - one workflow,
                        two ways in. */}
                    <button
                      type="button"
                      role="menuitem"
                      onClick={handleCreateSectionFromToolbar}
                      disabled={!slides[currentSlideIndex]?.section?.id}
                      title="Start a new section after the current slide - the slides below it move into it"
                    >
                      New section after this slide&hellip;
                    </button>
                  </div>
                )}
              </div>
              </div>
            </div>

            {/* Group 2: Slide Navigation */}
            <div className="toolbar-group bordered">
              <div className="slide-nav">
                <button
                  className="nav-prev"
                  onClick={() => setCurrentSlideIndex(prev => Math.max(0, prev - 1))}
                  disabled={currentSlideIndex === 0}
                  title="Previous slide"
                >
                  ◀
                </button>
                <span className="slide-counter">
                  {bodyLayout === 'accordion'
                    ? `${openSectionIds.length}/${sections.length}`
                    : `${Math.min(currentSlideIndex + 1, slides.length || 1)} / ${slides.length || 1}`}
                </span>
                <button
                  className="nav-next"
                  onClick={() => setCurrentSlideIndex(prev => Math.min(slides.length - 1, prev + 1))}
                  disabled={currentSlideIndex >= (slides.length - 1)}
                  title="Next slide"
                >
                  ▶
                </button>
              </div>
            </div>

            {/* Zoom Controls */}
            <div className="toolbar-group bordered">
              <div className="zoom-controls">
                <button
                  onClick={handleZoomOut}
                  // "Fit" can land on any value, not just a step on the ladder,
                  // so ask whether a smaller step exists rather than comparing
                  // against the ends of it.
                  disabled={!ZOOM_LEVELS.some((level) => level < slideZoom - 0.001)}
                  title="Zoom out"
                >
                  <Minimize2 size={12} />
                </button>
                <span
                  className="zoom-label"
                  onClick={handleZoomReset}
                  title="Reset zoom to 100%"
                >
                  {Math.round(slideZoom * 100)}%
                </span>
                <button
                  onClick={handleZoomIn}
                  disabled={!ZOOM_LEVELS.some((level) => level > slideZoom + 0.001)}
                  title="Zoom in"
                >
                  <Maximize2 size={12} />
                </button>
                <button
                  className="zoom-fit"
                  onClick={handleZoomFit}
                  title="Fit the whole slide on screen"
                >
                  Fit
                </button>
              </div>
            </div>

            {/* Group 3: Tools & Sections */}
            <button
              className={`toolbar-btn ${isToolbarDrawerOpen ? 'primary' : ''}`}
              onClick={() => setIsToolbarDrawerOpen(prev => !prev)}
              aria-expanded={isToolbarDrawerOpen}
              title="Toggle tools panel"
            >
              <Edit3 size={14} />
              Tools
            </button>

            {/* The shape builder is reached from the Tools drawer's
                "Shape Builder" button, so it does not need a second entry
                point here — the room goes to the save controls instead. */}

            {/* Group 3b: Sections (Right Drawer) */}
            <button
              className="toolbar-btn"
              onClick={toggleNavDrawer}
              aria-expanded={isNavDrawerOpen}
              ref={navDrawerTriggerRef}
            >
              <List size={14} />
              Sections
            </button>

            {/* Group 4: Export Name and Version */}
            <div className="export-name-group">
              <label htmlFor="export-name">Name:</label>
              <input
                type="text"
                id="export-name"
                placeholder="Week1_Module"
                value={exportFileName}
                onChange={(e) => setExportFileName(e.target.value)}
              />
              <div className="version-input-wrapper">
                <span className="version-label">v</span>
                <input
                  type="number"
                  min="1"
                  className="version-input"
                  value={exportVersionInput}
                  onChange={(e) => {
                    setExportVersionInput(e.target.value);
                    setPrefilledVersionInput(false);
                  }}
                  placeholder={`${exportVersion}`}
                  title={`Version number (auto: v${exportVersion})`}
                />
              </div>
            </div>

            {/* Group 5: Primary Actions.
                The two save actions share one dropdown — they differ only in
                whether the version number moves, which is a choice to make at
                the moment of saving rather than two buttons to tell apart. */}
            <div className="toolbar-group">
              {/* A deck arrives two ways — as a saved file, or as JSON in an
                  assistant's reply — and both are "open a deck", so they share
                  one button rather than the paste hiding in the Export menu. */}
              <div className="dropdown">
                <button
                  ref={openMenuButtonRef}
                  className="toolbar-btn open-btn dropdown-trigger"
                  onClick={() => {
                    setShowOpenMenu(prev => !prev);
                    setShowSaveMenu(false);
                    setShowExportMenu(false);
                    setShowHtmlExportMenu(false);
                    setShowSettingsMenu(false);
                  }}
                  aria-expanded={showOpenMenu}
                  title="Open a deck from a file or from pasted JSON"
                >
                  <FolderOpen size={14} />
                  Open ▾
                </button>
                {showOpenMenu && (
                  <div ref={openMenuRef} className="dropdown-menu dropdown-menu-compact">
                    <button onClick={() => { setShowOpenMenu(false); handleOpenPresentation(); }}>
                      📂 Open file <small>.json</small>
                    </button>
                    <button onClick={() => { setShowOpenMenu(false); setPasteJsonError(''); setShowJsonInput(true); }}>
                      📋 Paste JSON <small>text</small>
                    </button>
                  </div>
                )}
              </div>
              <div className="dropdown">
                <button
                  ref={saveMenuButtonRef}
                  className="toolbar-btn success dropdown-trigger"
                  onClick={() => {
                    setShowSaveMenu(prev => !prev);
                    setShowExportMenu(false);
                    setShowHtmlExportMenu(false);
                    setShowSettingsMenu(false);
                  }}
                  aria-expanded={showSaveMenu}
                  title="Save the deck as JSON"
                >
                  <Save size={14} />
                  Save ▾
                </button>
                {showSaveMenu && (
                  <div ref={saveMenuRef} className="dropdown-menu dropdown-menu-compact">
                    <button onClick={() => { handleSave(); setShowSaveMenu(false); }}>
                      💾 Save <small>v{exportVersionInput || exportVersion}</small>
                    </button>
                    <button onClick={() => { handleVersionUpFullJson(); setShowSaveMenu(false); }}>
                      ➕ Save + <small>new version</small>
                    </button>
                    {/* Where the two buttons above write. The line is always
                        shown — including on a browser that cannot be pointed at
                        a folder, where the honest answer is "the Downloads
                        folder" and a missing row just reads as a missing
                        feature. Only the buttons that would work are offered. */}
                    <div className="dropdown-divider" />
                    <div className="dropdown-note" title={saveLocation.locationLabel}>
                      Saving to: <strong>{saveLocation.locationLabel}</strong>
                    </div>
                    {saveLocation.supported ? (
                      <>
                        {saveLocation.canPickDirectory && (
                          <button onClick={() => { setShowSaveMenu(false); handleSetSaveDirectory(); }}>
                            📁 Set Dir <small>choose folder</small>
                          </button>
                        )}
                        <button onClick={() => { setShowSaveMenu(false); handleSaveAs(); }}>
                          🗂️ Save as… <small>pick the file</small>
                        </button>
                        {(saveLocation.hasFile || saveLocation.hasDirectory) && (
                          <button onClick={() => { setShowSaveMenu(false); handleClearSaveLocation(); }}>
                            📍 Clear location <small>ask next time</small>
                          </button>
                        )}
                      </>
                    ) : (
                      <div className="dropdown-note dropdown-note--wrap">
                        This browser cannot save to a chosen folder. Chrome or Edge can.
                      </div>
                    )}
                  </div>
                )}
              </div>
              <button
                className="toolbar-btn primary"
                onClick={handleNewSlideDeck}
                title="Create new slide deck"
              >
                + New
              </button>
              <button
                className="toolbar-btn present-btn"
                onClick={handleEnterPresentMode}
                title="Present full screen — Space or a slide clicker advances"
              >
                <Play size={14} />
                Present
              </button>
            </div>

            {/* Group 6: Export Dropdown */}
            <div className="dropdown">
              <button
                ref={exportMenuButtonRef}
                className="toolbar-btn dropdown-trigger"
                onClick={() => {
                  setShowExportMenu(prev => !prev);
                  setShowHtmlExportMenu(false);
                  setShowSettingsMenu(false);
                }}
              >
                Export ▾
              </button>
              {showExportMenu && (
                <div ref={exportMenuRef} className="dropdown-menu dropdown-menu-right">
                  <div className="dropdown-section">
                    <div className="dropdown-label">JSON</div>
                    <button onClick={() => { handleSave(); setShowExportMenu(false); }}>
                      📄 Full JSON <small>embedded images</small>
                    </button>
                    <button onClick={() => { handleSaveSlimJson(); setShowExportMenu(false); }}>
                      📄 Slim JSON <small>no images</small>
                    </button>
                    {showAdvancedExports && (
                      <button onClick={() => { handleExportStableJson(); setShowExportMenu(false); }}>
                        🤖 LLM Template <small>AI-friendly</small>
                      </button>
                    )}
                  </div>
                  <div className="dropdown-section">
                    <div className="dropdown-label">HTML</div>
                    <button onClick={() => { handleExportHTML(); setShowExportMenu(false); }}>
                      🌐 Full HTML <small>embedded images</small>
                    </button>
                    <button onClick={() => { handleExportPathHTML(); setShowExportMenu(false); }}>
                      🌐 HTML (Server paths) <small>hosted images</small>
                    </button>
                    <button onClick={() => { handleExportSlimHTML(); setShowExportMenu(false); }}>
                      🌐 Slim HTML <small>no images</small>
                    </button>
                  </div>
                  <div className="dropdown-section">
                    <div className="dropdown-label">Slides</div>
                    <button onClick={() => { handleExportPPTX(); setShowExportMenu(false); }}>
                      📊 PowerPoint <small>.pptx</small>
                    </button>
                    <button onClick={() => { handleExportPDFSlides(); setShowExportMenu(false); }}>
                      📄 PDF <small>slide per page</small>
                    </button>
                  </div>
                  <div className="dropdown-section">
                    <div className="dropdown-label">Tools</div>
                    <button onClick={() => { resetSelectionState(); setSelectionMode('export'); setShowSelectionModal(true); setShowExportMenu(false); }}>
                      ↔️ Import/Export Slides
                    </button>
                    {/* Paste JSON used to live here, in the Export menu. It is
                        a way of opening a deck, so it moved to Open ▾. */}
                  </div>
                </div>
              )}
            </div>

            {/* Group 7: Keyboard shortcuts + Settings.
                The shortcut list sits beside the gear rather than among the
                document actions: both are "about the app" rather than about
                the deck, and this is the corner the eye already goes to for
                app-level controls. */}
            <button
              className="icon-btn"
              onClick={() => setIsHotkeysOpen(true)}
              title="Keyboard shortcuts  ( ? )"
              aria-label="Keyboard shortcuts"
            >
              <Keyboard size={14} />
            </button>

            <div className="dropdown">
              <button
                ref={settingsMenuButtonRef}
                className="icon-btn dropdown-trigger"
                onClick={() => {
                  setShowSettingsMenu(prev => !prev);
                  setShowExportMenu(false);
                  setShowHtmlExportMenu(false);
                }}
                title="Settings"
              >
                <Settings size={14} />
              </button>
              {showSettingsMenu && (
                <div ref={settingsMenuRef} className="dropdown-menu dropdown-menu-right">
                  <button onClick={() => { setShowTemplateManager(true); setShowSettingsMenu(false); }}>
                    🧩 Template Manager
                  </button>
                  <button onClick={() => { setShowLogoSettings(true); setShowSettingsMenu(false); }}>
                    🖼 School Logo Settings
                  </button>
                  <button onClick={handleToggleAllSections} style={{ display: bodyLayout === 'accordion' ? 'block' : 'none' }}>
                    {openSectionIds.length > 0 ? '⌃ Collapse All Sections' : '⌄ Expand All Sections'}
                  </button>
                  <div className="dropdown-divider"></div>
                  <button onClick={handleDownloadTemplate}>
                    📥 Download Slide Template
                  </button>
                  <button onClick={handleApplyTemplate}>
                    📄 Apply Template to Slides
                  </button>
                  <button onClick={() => setShowAspectGuides((prev) => !prev)}>
                    📐 16:9 Export Guides {showAspectGuides ? '✓' : ''}
                  </button>
                  <button onClick={() => setSlideDesignMode((prev) => (prev === 'canvas' ? 'flow' : 'canvas'))}>
                    🎨 Slide Designer (16:9) {slideDesignMode === 'canvas' ? '✓' : ''}
                  </button>
                  <button onClick={() => setSlideNumbersEnabled((prev) => !prev)}>
                    🔢 Slide Numbers {slideNumbersEnabled ? '✓' : ''}
                  </button>
                  <button onClick={() => setDeckNameBadgeEnabled((prev) => !prev)}>
                    🏷 Deck Name Badge {deckNameBadgeEnabled ? '✓' : ''}
                  </button>
                  {deckNameBadgeEnabled && (
                    <button onClick={() => setDeckNameOnContentSlides((prev) => !prev)}>
                      &nbsp;&nbsp;↳ On Content Slides {deckNameOnContentSlides ? '✓' : ''}
                    </button>
                  )}
                  <div className="dropdown-divider"></div>
                  <button onClick={() => { setShowToolbar(false); setShowSettingsMenu(false); }}>
                    ⌃ Hide Toolbar
                  </button>
                  <button onClick={() => { toggleCanvasMaximized(); setShowSettingsMenu(false); }}>
                    {isCanvasMaximized ? '🗗 Restore Layout' : '🗖 Maximize Canvas'}
                  </button>
                  <button onClick={() => { onToggleLightMode && onToggleLightMode(); setShowSettingsMenu(false); }}>
                    {lightMode ? <><Moon size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 6 }} /> Dark Mode</> : <><Sun size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 6 }} /> Light Mode</>}
                  </button>
                  <div className="dropdown-divider"></div>
                  <button onClick={() => window.open('/about.html', '_blank')}>
                    ℹ About
                  </button>
                  <button onClick={() => window.open('/faq.html', '_blank')}>
                    ❓ FAQ
                  </button>
                  <div className="dropdown-divider"></div>
                  <span className="dropdown-meta">v{require('../package.json').version}</span>
                </div>
              )}
            </div>
          </div>

        </div>
      ) : (
        <div
          ref={collapsedToolbarRef}
          className="toolbar no-print sticky z-50"
          style={{ top: isHeaderExpanded ? `calc(${tabNavHeight}px + 58px)` : tabNavHeight }}
        >
          <div className="max-w-7xl mx-auto px-4 h-[40px] flex items-center justify-end">
            <button
              type="button"
              onClick={() => setShowToolbar(true)}
              className="toolbar-btn"
            >
              <ChevronDown size={14} />
              Show Toolbar
            </button>
          </div>
        </div>
      )}

      {isCanvasMaximized && (
        <button
          type="button"
          onClick={toggleCanvasMaximized}
          className="canvas-maximize-restore no-print"
          title="Restore toolbar and outline panel"
        >
          <Minimize2 size={16} />
          Restore layout
        </button>
      )}


      <div
        className="slide-content-wrapper"
        style={{
          paddingLeft: isNavDrawerOpen ? '380px' : '0',
          paddingRight: isToolbarDrawerOpen ? '288px' : '0',
          backgroundColor: slideAreaLightMode ? '#ffffff' : '#020617',
          transition: 'padding-left 0.3s ease-in-out, padding-right 0.3s ease-in-out',
        }}
      >
      {/* Zoom viewport.
          This used to be `zoom: slideZoom` on the element below, which scaled
          the wrong way round: that element is width-capped (max-w-7xl, and the
          space between the drawers is narrower than the cap anyway), so the box
          could not grow. CSS zoom then inflated only its px-16 padding, and the
          slide got *smaller* the further you zoomed in.
          A transform scales what is already laid out, so the cap no longer
          interferes and 110% really is 10% bigger. The viewport reserves the
          scaled height, because a transform does not affect layout size, and
          scrolls horizontally once the content outgrows the space. */}
      <div
        ref={slideZoomViewportRef}
        className="slide-zoom-viewport"
        style={slideZoom === 1 ? undefined : {
          height: slideZoomNaturalHeight ? slideZoomNaturalHeight * slideZoom : undefined,
          overflowX: slideZoom > 1 ? 'auto' : undefined,
          overflowY: 'hidden',
        }}
      >
      <div
        ref={slideZoomStageRef}
        className="lesson-template-main max-w-7xl mx-auto px-16 py-12"
        style={{
          transform: slideZoom !== 1 ? `scale(${slideZoom})` : undefined,
          // Above 100% the origin has to be the left edge: scrollable overflow
          // only extends to the right, so a centred origin would push the left
          // side of the slide somewhere the user cannot scroll to.
          transformOrigin: slideZoom === 1
            ? undefined
            : (slideZoom > 1 ? 'top left' : 'top center'),
          backgroundColor: slideAreaLightMode ? '#ffffff' : '#020617',
        }}
      >
        {bodyLayout === 'accordion' ? (
          sections.map(section => (
            <Section
              key={section.id}
              section={section}
              onUpdate={handleSectionUpdate}
              isEditMode={isEditMode}
              onAddContent={(sectionId) => handleAddContent(sectionId)}
              onDeleteSection={handleDeleteSection}
              onBlockEdit={handleBlockEdit}
              isOpen={openSectionIds.includes(section.id)}
              onToggle={() => handleToggleSection(section.id)}
              htmlModes={htmlModes}
              toggleHtmlMode={toggleHtmlMode}
              onAddBlockBelow={handleAddBlockBelow}
              onAddBlockAtTop={handleAddBlockAtTop}
              handleWorksheetJsonImport={handleWorksheetJsonImport}
              handleWorksheetJsonPaste={handleWorksheetJsonPaste}
              onPresetImageRequest={handlePresetImageRequest}
              onPresetVideoRequest={handlePresetVideoRequest}
              sectionNumberMap={sectionNumberMap}
              autoApaCitations={autoApaCitations}
            />
          ))
        ) : (
          <>
            {isFullScreenPreview && createPortal(
              // Presenting fits the slide to the screen, so there is nothing to
              // scroll to — unless the presenter has deliberately zoomed past
              // fit, in which case panning is the point.
              <div
                className={`fixed inset-0 z-[9999] flex flex-col lesson-template-container ${
                  isPresenting && fullScreenZoom <= 1 ? 'overflow-hidden' : 'overflow-auto'
                }`}
                style={{ backgroundColor: slideAreaLightMode ? '#f1f5f9' : '#020617' }}
              >
                {/* With the controls collapsed the slide owns the whole screen;
                    a single low-contrast chevron in the corner brings them
                    back (H does the same). */}
                {isFullScreenChromeHidden && (
                  <button
                    type="button"
                    onClick={() => setIsFullScreenChromeHidden(false)}
                    className="fixed top-1 right-1 z-[60] w-7 h-7 flex items-center justify-center rounded text-white/25 hover:text-white hover:bg-slate-800/90 transition-colors no-print"
                    title={isPresenting ? 'Show presenter controls' : 'Show controls (H)'}
                    aria-label="Show full screen controls"
                  >
                    <ChevronDown size={16} />
                  </button>
                )}
                {/* Slide navigation, centered on the top row so it clears the
                    edit/preview mode banner text on the left and its toggle on
                    the right. */}
                <div className={`fixed top-3 left-1/2 -translate-x-1/2 z-[60] items-center gap-0.5 bg-slate-800/90 rounded-lg px-1 py-1 shadow-lg no-print ${isFullScreenChromeHidden ? 'hidden' : 'flex'}`}>
                  <button
                    type="button"
                    onClick={() => setCurrentSlideIndex((prev) => Math.max(0, prev - 1))}
                    className="w-8 h-8 flex items-center justify-center text-white text-lg leading-none rounded hover:bg-slate-700 disabled:opacity-40"
                    disabled={currentSlideIndex === 0}
                    title="Previous slide"
                    aria-label="Previous slide"
                  >
                    ◀
                  </button>
                  <span className="min-w-[3.5rem] px-2 text-center text-white text-xs font-semibold tabular-nums">
                    {`${Math.min(currentSlideIndex + 1, slides.length || 1)} / ${slides.length || 1}`}
                  </span>
                  <button
                    type="button"
                    onClick={() => setCurrentSlideIndex((prev) => Math.min(slides.length - 1, prev + 1))}
                    className="w-8 h-8 flex items-center justify-center text-white text-lg leading-none rounded hover:bg-slate-700 disabled:opacity-40"
                    disabled={currentSlideIndex >= (slides.length - 1)}
                    title="Next slide"
                    aria-label="Next slide"
                  >
                    ▶
                  </button>
                </div>
                {/* Floating controls: a single compact top row combining the
                    zoom stepper and a small exit button, so it stays out of the
                    way while the slide is full screen. */}
                <div className={`fixed top-3 right-3 z-[60] items-center gap-0.5 bg-slate-800/90 rounded-lg px-1 py-1 shadow-lg no-print ${isFullScreenChromeHidden ? 'hidden' : 'flex'}`}>
                  <button
                    type="button"
                    onClick={handleFullScreenZoomOut}
                    className="w-7 h-7 flex items-center justify-center text-white text-xl leading-none rounded hover:bg-slate-700 disabled:opacity-40"
                    disabled={fullScreenZoom <= 0.25}
                    title="Zoom out"
                    aria-label="Zoom out"
                  >
                    −
                  </button>
                  <button
                    type="button"
                    onClick={handleFullScreenZoomFit}
                    className="min-w-[3rem] px-1.5 h-7 flex items-center justify-center text-white text-xs font-semibold rounded hover:bg-slate-700"
                    title="Fit slide to screen"
                  >
                    {fullScreenZoom === 1
                      ? 'Fit'
                      /* Presenting already scales the slide to fit, so report
                         the scale actually on screen rather than the multiplier. */
                      : `${Math.round(fullScreenZoom * (isPresenting ? presentFitScale : 1) * 100)}%`}
                  </button>
                  <button
                    type="button"
                    onClick={handleFullScreenZoomIn}
                    className="w-7 h-7 flex items-center justify-center text-white text-xl leading-none rounded hover:bg-slate-700 disabled:opacity-40"
                    disabled={fullScreenZoom >= 3}
                    title="Zoom in"
                    aria-label="Zoom in"
                  >
                    +
                  </button>
                  <span className="mx-0.5 h-5 w-px bg-slate-600" aria-hidden="true" />
                  <button
                    type="button"
                    onClick={handleExitFullScreenPreview}
                    className="inline-flex items-center gap-1.5 px-2.5 h-7 text-xs font-semibold text-white rounded hover:bg-slate-700 transition-colors"
                    title={isPresenting ? 'End presentation (Esc)' : 'Exit full screen (Esc)'}
                    aria-label={isPresenting ? 'End presentation' : 'Exit full screen'}
                  >
                    <Minimize2 size={14} />
                    {isPresenting ? 'End' : 'Exit'}
                  </button>
                  <span className="mx-0.5 h-5 w-px bg-slate-600" aria-hidden="true" />
                  <button
                    type="button"
                    onClick={() => setIsFullScreenChromeHidden(true)}
                    className="w-7 h-7 flex items-center justify-center text-white rounded hover:bg-slate-700"
                    title={isPresenting ? 'Hide presenter controls' : 'Hide controls (H) — H brings them back'}
                    aria-label="Hide full screen controls"
                  >
                    <ChevronUp size={16} />
                  </button>
                </div>
                {/* Stage: fit the 16:9 slide to the viewport, then apply any extra zoom.
                    Presenting centres the slide and scales it to fit the frame
                    in both directions; the editing full screen keeps its top
                    alignment, its padding and its scrollbar. */}
                <div className={`flex-1 flex justify-center ${isPresenting ? 'items-center overflow-hidden' : 'items-start p-4'}`}>
                  <div
                    ref={presentStageRef}
                    className="w-full"
                    style={isPresenting ? {
                      maxWidth: 'min(100vw, calc(100vh * 16 / 9))',
                      // A visual scale, not a zoom: the slide keeps the layout
                      // it was authored with instead of reflowing to fit. The
                      // nudge translates before it, so a press moves the slide
                      // by the same distance on screen at any zoom.
                      transform: `translateX(${fullScreenPanX}px) scale(${presentFitScale * fullScreenZoom})`,
                      transformOrigin: 'center center',
                      flex: '0 0 auto'
                    } : {
                      maxWidth: 'min(100%, calc((100vh - 2rem) * 16 / 9))',
                      zoom: fullScreenZoom !== 1 ? fullScreenZoom : undefined,
                      transform: fullScreenPanX ? `translateX(${fullScreenPanX}px)` : undefined
                    }}
                  >
                <SlideDeck
                  slides={slides}
                  currentIndex={currentSlideIndex}
                  headerColorMap={headerColorMap}
                  defaultHeaderColor={slideHeaderColor}
                  onUpdateHeaderColor={handleSlideHeaderColorForSection}
                  onHeaderDataChange={handleHeaderDataChange}
                  slideGlobalCss={effectiveSlideCss}
                  slideVisibility={slideVisibility}
                  onToggleSlideVisibility={handleToggleSlideVisibility}
                  onChangeIndex={setCurrentSlideIndex}
                  headerData={headerData}
                  isEditMode={isEditMode}
                  onToggleEditMode={handleToggleEditMode}
                  onUpdate={handleSectionUpdate}
                  onAddContent={(sectionId) => handleAddContent(sectionId)}
                  onBlockEdit={handleBlockEdit}
                  onAddSlideAfter={handleAddSlideAfter}
                  onAddSectionTitleSlide={handleAddSectionTitleSlide}
                  onRemoveSlide={handleRemoveSlide}
                  onClearSlideBlocks={handleClearSlideBlocks}
                  onCustomizeTitleCard={() => openTitleCardModal('start')}
                  onCustomizeEndTitleCard={() => openTitleCardModal('end')}
                  onCustomizeSectionTitleCard={openSectionTitleCardModal}
                  onTitleCardImageChange={handleTitleCardImageChange}
                  onEndTitleCardImageChange={handleEndTitleCardImageChange}
                  onSectionTitleCardImageChange={handleSectionTitleCardImageChange}
                  onApplySlideBackgroundScope={handleApplySlideBackgroundScope}
                  htmlModes={htmlModes}
                  toggleHtmlMode={toggleHtmlMode}
                  onAddBlockBelow={handleAddBlockBelow}
                  onAddBlockAtTop={handleAddBlockAtTop}
                  handleWorksheetJsonImport={handleWorksheetJsonImport}
                  handleWorksheetJsonPaste={handleWorksheetJsonPaste}
                  onPresetImageRequest={handlePresetImageRequest}
                  onPresetVideoRequest={handlePresetVideoRequest}
                  onCanvasImageDrop={handleCanvasImageDrop}
                  onCanvasImagePaste={handleCanvasImagePaste}
                  slideNames={slideNames}
                  onUpdateSlideName={handleUpdateSlideName}
                  slideSubtitles={slideSubtitles}
                  onUpdateSlideSubtitle={handleUpdateSlideSubtitle}
                  slideDeckName={slideDeckName}
                  onSlideDeckNameChange={setSlideDeckName}
                  bodyBackground={bodyBackground}
                  slideBackgrounds={slideBackgrounds}
                  titleCardBackground={titleCardBackground}
                  autoApaCitations={autoApaCitations}
                  lightMode={slideAreaLightMode}
                  showAspectGuides={showAspectGuides}
                  slideDesignMode={slideDesignMode}
                  deckNameOnContentSlides={deckNameOnContentSlides}
                  deckNameBadgeEnabled={deckNameBadgeEnabled}
                  slideNumbersEnabled={slideNumbersEnabled}
                  isFullScreen
                  hideChrome={isFullScreenChromeHidden}
                  onNudge={handleFullScreenNudge}
                />
                  </div>
                </div>
              </div>,
              document.body
            )}

            {!isFullScreenPreview && (
              <SlideDeck
                slides={slides}
                currentIndex={currentSlideIndex}
                headerColorMap={headerColorMap}
                defaultHeaderColor={slideHeaderColor}
                onUpdateHeaderColor={handleSlideHeaderColorForSection}
                onHeaderDataChange={handleHeaderDataChange}
                slideGlobalCss={effectiveSlideCss}
                slideVisibility={slideVisibility}
                onToggleSlideVisibility={handleToggleSlideVisibility}
                onChangeIndex={setCurrentSlideIndex}
                headerData={headerData}
                isEditMode={isEditMode}
                onToggleEditMode={handleToggleEditMode}
                onUpdate={handleSectionUpdate}
                onAddContent={(sectionId) => handleAddContent(sectionId)}
                onBlockEdit={handleBlockEdit}
                onAddSlideAfter={handleAddSlideAfter}
                onAddSectionTitleSlide={handleAddSectionTitleSlide}
                onRemoveSlide={handleRemoveSlide}
                onClearSlideBlocks={handleClearSlideBlocks}
                onCustomizeTitleCard={() => openTitleCardModal('start')}
                onCustomizeEndTitleCard={() => openTitleCardModal('end')}
                onCustomizeSectionTitleCard={openSectionTitleCardModal}
                onTitleCardImageChange={handleTitleCardImageChange}
                onEndTitleCardImageChange={handleEndTitleCardImageChange}
                onSectionTitleCardImageChange={handleSectionTitleCardImageChange}
                onApplySlideBackgroundScope={handleApplySlideBackgroundScope}
                htmlModes={htmlModes}
                toggleHtmlMode={toggleHtmlMode}
                onAddBlockBelow={handleAddBlockBelow}
                onAddBlockAtTop={handleAddBlockAtTop}
                handleWorksheetJsonImport={handleWorksheetJsonImport}
                handleWorksheetJsonPaste={handleWorksheetJsonPaste}
                onPresetImageRequest={handlePresetImageRequest}
                onPresetVideoRequest={handlePresetVideoRequest}
                onCanvasImageDrop={handleCanvasImageDrop}
                onCanvasImagePaste={handleCanvasImagePaste}
                slideNames={slideNames}
                onUpdateSlideName={handleUpdateSlideName}
                slideSubtitles={slideSubtitles}
                onUpdateSlideSubtitle={handleUpdateSlideSubtitle}
                slideDeckName={slideDeckName}
                onSlideDeckNameChange={setSlideDeckName}
                bodyBackground={bodyBackground}
                slideBackgrounds={slideBackgrounds}
                titleCardBackground={titleCardBackground}
                autoApaCitations={autoApaCitations}
                lightMode={slideAreaLightMode}
                showAspectGuides={showAspectGuides}
                slideDesignMode={slideDesignMode}
                deckNameOnContentSlides={deckNameOnContentSlides}
                deckNameBadgeEnabled={deckNameBadgeEnabled}
                slideNumbersEnabled={slideNumbersEnabled}
              />
            )}

            {!isFullScreenPreview && (
              <PrintSections
                sections={sections}
                onUpdate={handleSectionUpdate}
                onAddContent={(sectionId) => handleAddContent(sectionId)}
                onBlockEdit={handleBlockEdit}
                htmlModes={htmlModes}
                toggleHtmlMode={toggleHtmlMode}
                onAddBlockBelow={handleAddBlockBelow}
                onAddBlockAtTop={handleAddBlockAtTop}
                handleWorksheetJsonImport={handleWorksheetJsonImport}
                handleWorksheetJsonPaste={handleWorksheetJsonPaste}
                onPresetImageRequest={handlePresetImageRequest}
                onPresetVideoRequest={handlePresetVideoRequest}
                sectionNumberMap={sectionNumberMap}
                autoApaCitations={autoApaCitations}
              />
            )}
          </>
        )}
      </div>
      </div>
      </div>

      <ContentModal
        isOpen={isModalOpen}
        contentType={modalContentType}
        onClose={() => setIsModalOpen(false)}
        onSave={handleModalSave}
        initialData={modalInitialData}
        sections={sections}
        onSetSourceInclusion={handleSetSourceInclusion}
        deckName={slideDeckName}
      />

      {jsonPasteModal.open && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] no-print">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-2xl w-full">
            <h2 className="text-lg font-semibold mb-4">Paste Worksheet JSON</h2>
            <p className="text-sm text-gray-600 mb-4">
              Paste the contents of a worksheet .json file below. This will replace the current worksheet for this block.
            </p>
            <textarea
              className="w-full h-64 p-2 border border-gray-300 rounded"
              value={jsonPasteModal.text}
              onChange={(e) => setJsonPasteModal({ ...jsonPasteModal, text: e.target.value })}
              placeholder='{"title":"Worksheet","questions":[]}'
            />
            <div className="mt-4 flex justify-end gap-2">
              <button
                className="px-4 py-2 bg-gray-200 rounded"
                onClick={closeWorksheetJsonPaste}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-blue-600 text-white rounded"
                onClick={applyWorksheetJsonPaste}
              >
                Import
              </button>
            </div>
          </div>
        </div>
      )}

      {isHelpOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] no-print">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full">
            <h2 className="text-lg font-semibold mb-4">Help</h2>
            <p className="text-sm mb-4">
              Download an example worksheet JSON file to use with the import feature.
            </p>
            <a
              href="/worksheet-example.json"
              download
              className="text-blue-600 underline"
            >
              Example worksheet JSON
            </a>
            <div className="mt-4 text-right">
              <button
                onClick={() => setIsHelpOpen(false)}
                className="px-3 py-1 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {showLogoSettings && (
        <SchoolLogoSettings onClose={() => setShowLogoSettings(false)} />
      )}

      {showTitleCardModal && createPortal(
        <div
          data-title-modal-panel
          className="fixed z-[10001] flex flex-col w-[min(94vw,440px)] max-h-[86vh] bg-white rounded-xl shadow-2xl ring-1 ring-black/10 no-print"
          style={titleModalPos
            ? { left: titleModalPos.x, top: titleModalPos.y }
            : { right: 24, top: 84 }}
        >
          <div
            className="flex items-center justify-between gap-3 px-5 py-3 border-b border-gray-200 rounded-t-xl bg-gray-50 cursor-move select-none"
            onPointerDown={startTitleModalDrag}
            title="Drag to move this panel"
          >
            <div className="flex items-center gap-2 min-w-0">
              <GripVertical size={16} className="text-gray-400 flex-shrink-0" />
              <h2 className="text-lg font-bold text-gray-900 truncate">{activeTitleCardHeading}</h2>
            </div>
            <button
              onClick={() => setShowTitleCardModal(false)}
              onPointerDown={(e) => e.stopPropagation()}
              className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
              aria-label="Close"
            >
              <X size={22} />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
              {/* Title Input */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Title
                </label>
                <input
                  type="text"
                  value={activeTitleCardSettings.title}
                  onChange={(e) => activeTitleCardUpdater({ title: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter title card title"
                />
              </div>

              {/* Subtitle Input */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Subtitle (Optional)
                </label>
                <input
                  type="text"
                  value={activeTitleCardSettings.subtitle}
                  onChange={(e) => activeTitleCardUpdater({ subtitle: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter subtitle"
                />
              </div>

              {/* Text Position */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Text Position
                </label>
                <div className="flex gap-3">
                  {['top', 'center', 'bottom'].map((position) => (
                    <button
                      key={position}
                      onClick={() => activeTitleCardUpdater({ titlePosition: position })}
                      className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${
                        activeTitleCardSettings.titlePosition === position
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {position.charAt(0).toUpperCase() + position.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Font Sizes */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Text Sizes
                </label>
                <div className="space-y-3">
                  {[
                    { key: 'titleFontSize', label: 'Title', min: 16, max: 120, fallback: 48 },
                    { key: 'subtitleFontSize', label: 'Subtitle', min: 10, max: 72, fallback: 20 },
                    { key: 'metaFontSize', label: 'Course / instructor', min: 8, max: 48, fallback: 16 }
                  ].map(({ key, label, min, max, fallback }) => {
                    const value = Number(activeTitleCardSettings[key]) || fallback;
                    return (
                      <div key={key} className="flex items-center gap-3">
                        <span className="w-40 text-sm text-gray-600">{label}</span>
                        <input
                          type="range"
                          min={min}
                          max={max}
                          value={value}
                          onChange={(e) => activeTitleCardUpdater({ [key]: parseInt(e.target.value, 10) })}
                          className="flex-1"
                        />
                        <span className="w-14 text-right text-sm tabular-nums text-gray-500">{value}px</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Instructor / course info layout */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Course &amp; Instructor Layout
                </label>
                <div className="flex gap-3">
                  {[{ value: 'stacked', label: 'Stacked' }, { value: 'row', label: 'One row' }].map((option) => (
                    <button
                      key={option.value}
                      onClick={() => activeTitleCardUpdater({ instructorLayout: option.value })}
                      className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${
                        (activeTitleCardSettings.instructorLayout || 'stacked') === option.value
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
                <p className="mt-2 text-xs text-gray-500">Stack the course, name and email vertically, or flow them in one row along the bottom.</p>
              </div>

              {/* Background Color */}
              <ColorSwatchPicker
                label="Background Color"
                value={activeTitleCardSettings.backgroundColor}
                onChange={(color) => activeTitleCardUpdater({ backgroundColor: color })}
              />

              <BackgroundImagePicker
                label="Background Image"
                description="Use a reusable image preset, upload a custom background for this title card, or link one that is already hosted."
                presets={IMAGE_BACKGROUND_PRESET_OPTIONS}
                selectedId={activeTitleCardBackgroundSelection.selectedId}
                selectedLabel={activeTitleCardBackgroundSelection.selectedLabel || 'No background image selected'}
                selectedPreview={activeTitleCardBackgroundSelection.selectedPreview}
                onSelectPreset={activeTitleCardBackgroundSelect}
                onUploadImage={activeTitleCardBackgroundUpload}
                onUseLink={activeTitleCardBackgroundLink}
                onClear={activeTitleCardBackgroundClear}
                uploadLabel="Upload title image"
                replaceLabel="Replace title image"
                clearLabel="Remove title image"
                emptyLabel="No background image selected"
              />

              {/* Text Color */}
              <ColorSwatchPicker
                label="Title Text Color"
                value={activeTitleCardSettings.textColor || '#ffffff'}
                onChange={(color) => activeTitleCardUpdater({ textColor: color })}
                swatches={DEFAULT_TEXT_SWATCHES}
              />

              {/* Lower-right course/instructor text color (independent of title) */}
              <ColorSwatchPicker
                label="Course / Instructor Text Color"
                value={activeTitleCardSettings.metaTextColor || activeTitleCardSettings.textColor || '#ffffff'}
                onChange={(color) => activeTitleCardUpdater({ metaTextColor: color })}
                swatches={DEFAULT_TEXT_SWATCHES}
              />

              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={activeTitleCardSettings.showCourseInfo !== false}
                  onChange={(e) => activeTitleCardUpdater({ showCourseInfo: e.target.checked })}
                />
                Show course/instructor info on the title card
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={activeTitleCardSettings.showLogoOnTitle !== false}
                  onChange={(e) => activeTitleCardUpdater({ showLogoOnTitle: e.target.checked })}
                />
                Show school logo on the title card
              </label>
              {/* Corner logo: a school crest or any other mark, in a top
                  corner of the card, at whatever size suits it. Separate from
                  the checkbox above, which is the deck's own school logo down
                  in the meta row along the bottom. */}
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-3">
                <div>
                  <label className="block text-sm font-semibold text-gray-700">
                    Corner Logo (Optional)
                  </label>
                  <p className="mt-1 text-xs text-gray-500">
                    A school crest or other mark, pinned in a top corner of the card and scaled to the size you choose.
                  </p>
                </div>
                {activeTitleCardSettings.cornerLogo && (
                  <div className="relative inline-block">
                    <img
                      src={activeTitleCardSettings.cornerLogo}
                      alt="Corner logo preview"
                      className="max-w-[180px] rounded-lg border border-gray-300 bg-white p-2"
                      style={{ maxHeight: '110px' }}
                    />
                    <button
                      type="button"
                      onClick={() => activeTitleCardUpdater({ cornerLogo: '' })}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                      aria-label="Remove the corner logo"
                      title="Remove the corner logo"
                    >
                      <X size={14} />
                    </button>
                  </div>
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleTitleCardCornerLogoFile(file);
                    e.target.value = '';
                  }}
                  aria-label="Upload a corner logo"
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                />
                <ImageLinkInput
                  label="Or use a linked logo"
                  onUseLink={(url) => activeTitleCardUpdater({ cornerLogo: url })}
                />
                {logo && (
                  /* The deck already carries a school logo; copying it here
                     saves finding the file again. A copy rather than a link:
                     the corner mark stays what the author put there even if
                     the deck's logo is changed later. */
                  <button
                    type="button"
                    onClick={() => activeTitleCardUpdater({ cornerLogo: logo })}
                    className="w-full rounded-lg border border-blue-200 bg-blue-50 px-4 py-2 text-sm font-semibold text-blue-700 transition-colors hover:bg-blue-100"
                  >
                    Use the school logo from Settings
                  </button>
                )}
                {activeTitleCardSettings.cornerLogo && (
                  <div className="space-y-3">
                    <div>
                      <span className="block text-sm font-semibold text-gray-700 mb-2">Corner</span>
                      <div className="flex gap-3">
                        {[{ value: 'top-left', label: 'Upper left' }, { value: 'top-right', label: 'Upper right' }].map((option) => (
                          <button
                            key={option.value}
                            type="button"
                            onClick={() => activeTitleCardUpdater({ cornerLogoPosition: option.value })}
                            aria-pressed={(activeTitleCardSettings.cornerLogoPosition || 'top-right') === option.value}
                            className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${
                              (activeTitleCardSettings.cornerLogoPosition || 'top-right') === option.value
                                ? 'bg-blue-600 text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                          >
                            {option.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2" htmlFor="title-card-corner-logo-size">
                        Logo Size: {Number(activeTitleCardSettings.cornerLogoSize) || 120}px
                      </label>
                      <input
                        id="title-card-corner-logo-size"
                        type="range"
                        min={TITLE_CARD_CORNER_LOGO_MIN_PX}
                        max={TITLE_CARD_CORNER_LOGO_MAX_PX}
                        value={Number(activeTitleCardSettings.cornerLogoSize) || 120}
                        onChange={(e) => activeTitleCardUpdater({ cornerLogoSize: parseInt(e.target.value, 10) })}
                        className="w-full"
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        Measured across the slide, so the logo keeps its share of the card in every view and in every export.
                      </p>
                    </div>
                  </div>
                )}
              </div>

              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={activeTitleCardSettings.showTextOverlay === true}
                  onChange={(e) => activeTitleCardUpdater({ showTextOverlay: e.target.checked })}
                />
                Add transparent overlay behind text (for background images)
              </label>

              {/* Overlay Opacity Slider - only show when overlay is enabled */}
              {activeTitleCardSettings.showTextOverlay && (
                <div className="pl-6 space-y-2">
                  <label className="block text-sm font-medium text-gray-600">
                    Glass intensity: {Math.round((activeTitleCardSettings.textOverlayOpacity ?? 0.5) * 100)}%
                  </label>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-gray-400">Subtle</span>
                    <input
                      type="range"
                      min="0.1"
                      max="0.9"
                      step="0.05"
                      value={activeTitleCardSettings.textOverlayOpacity ?? 0.5}
                      onChange={(e) => activeTitleCardUpdater({ textOverlayOpacity: parseFloat(e.target.value) })}
                      className="flex-1"
                    />
                    <span className="text-xs text-gray-400">Strong</span>
                  </div>
                </div>
              )}

              {/* Image Upload */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Image (Optional)
                </label>
                <div className="space-y-3">
                  {activeTitleCardSettings.image && (
                    <div className="relative inline-block">
                      <img
                        src={activeTitleCardSettings.image}
                        alt="Title card preview"
                        className="max-w-xs rounded-lg border border-gray-300"
                        style={{ maxHeight: '200px' }}
                      />
                      <button
                        onClick={() => activeTitleCardUpdater({ image: '' })}
                        className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) activeTitleCardImageHandler(file);
                    }}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  />
                  <ImageLinkInput
                    label="Or use a linked image"
                    onUseLink={(url) => activeTitleCardUpdater({ image: url })}
                  />
                </div>
              </div>

              {/* Image Size */}
              {activeTitleCardSettings.image && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Image citation (Optional)
                  </label>
                  <textarea
                    value={activeTitleCardSettings.imageCitation || ''}
                    onChange={(e) => activeTitleCardUpdater({ imageCitation: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 p-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={3}
                    placeholder="Example: Ministry of Education. (2020, August 28). Guide to reopening Ontario’s schools. https://www.ontario.ca/page/guide-reopening-ontarios-schools"
                  />
                </div>
              )}

              {activeTitleCardSettings.image && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Image Size: {activeTitleCardSettings.imageSize}px
                  </label>
                  <input
                    type="range"
                    min="50"
                    max="800"
                    value={activeTitleCardSettings.imageSize}
                    onChange={(e) => activeTitleCardUpdater({ imageSize: parseInt(e.target.value) })}
                    className="w-full"
                  />
                  <div className="mt-3 space-y-2">
                    <div className="flex gap-2">
                      {[{ value: 'above', label: 'Text above image' }, { value: 'below', label: 'Text below image' }].map((option) => (
                        <button
                          key={option.value}
                          onClick={() => activeTitleCardUpdater({ textPlacement: option.value })}
                          className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${
                            (activeTitleCardSettings.textPlacement || 'below') === option.value
                              ? 'bg-blue-600 text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                    <p className="text-xs text-gray-500">Choose where the title and subtitle sit relative to the image.</p>
                  </div>
                </div>
              )}
            </div>

          <div className="flex-shrink-0 flex justify-end gap-3 px-6 py-4 border-t border-gray-200 rounded-b-xl bg-gray-50">
            <button
              onClick={() => setShowTitleCardModal(false)}
              className="px-6 py-2 border border-gray-300 rounded-lg font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => setShowTitleCardModal(false)}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors"
            >
              Save Changes
            </button>
          </div>
        </div>,
        document.body
      )}

      <button
        onClick={() => smoothScrollTo(0, 1000)}
        className="back-to-top-button fixed bottom-8 right-8 w-12 h-12 bg-slate-700 hover:bg-slate-800 text-white rounded-xl flex items-center justify-center shadow-lg transition-all no-print opacity-0 invisible hover:opacity-100 hover:visible"
        style={{
          opacity: typeof window !== 'undefined' && window.pageYOffset > 300 ? 1 : 0,
          visibility: typeof window !== 'undefined' && window.pageYOffset > 300 ? 'visible' : 'hidden'
        }}
      >
        ↑
      </button>
    </div>
  );
};

const App = ({ initialData, onLoadPresentation, showSettingsPage, activeAppTab }) => {
  return (
    <LogoProvider>
      <LectureTemplateSystem initialData={initialData} onLoadPresentation={onLoadPresentation} showSettingsPage={showSettingsPage} activeAppTab={activeAppTab} />
    </LogoProvider>
  );
};

export default App;
